import React, { useState, useRef, useCallback, useEffect } from "react";
import { Stage, Layer, Line, Circle, Text, Group, Image as KonvaImage, Rect, Transformer, Shape } from "react-konva";
import useImage from 'use-image';
import { Html } from 'react-konva-utils';
import { Check, X, Info, Ruler, RotateCcw, CheckCircle, ZoomIn, ZoomOut, ArrowUpRight, ArrowLeft, Eye, Edit, Eraser, Delete, Trash2, RotateCw, Download, Move, Target, Edit2, DoorOpen, Globe, Wind, LayoutGrid, ExternalLink, Save as SaveIcon } from "lucide-react";

import * as martinez from 'martinez-polygon-clipping';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import CustomSweetAlert from '../CustomSweetAlert';
import { Video as VideoGuideIcon } from 'lucide-react';
import { useLeaveConfirm } from './components/ConfirmLeaveDialog';
import VideoGuideModal from './components/VideoGuideModal';
import { VIDEO_GUIDE_DESKTOP_URL, VIDEO_GUIDE_MOBILE_URL, ACTION_REQUIRED_VIDEO_URL, FLOOR_PLAN_MAKER_TUTORIAL_URL, FLOOR_PLAN_MAKER_URL } from './constants/videoGuide';
import SpaceAnalysis, { buildSpaceHtml as buildSpaceItemHtml, isStillDefaultSpaceHtml as isDefaultSpaceItemHtml, buildDefaultSpaceReport } from '../SpaceAnalysis';
import EntranceAnalysis, { buildEntranceHtml, isStillDefaultEntranceHtml, buildDefaultEntranceReport } from '../EntranceAnalysis';
import VastuDevtaGuide, { buildInitialHtml as buildDevtaHtml, isStillDefaultHtml as isDefaultDevtaHtml } from '../VastuDevtaGuide';
import NavgrahShanti, { buildInitialHtml as buildGrahaHtml, isStillDefaultHtml as isDefaultGrahaHtml } from '../NavgrahShanti';
import FengShui, { buildInitialHtml as buildFengShuiHtml, isStillDefaultHtml as isDefaultFengShuiHtml } from '../FengShui';
import Helix, { buildInitialHtml as buildHelixHtml, isStillDefaultHtml as isDefaultHelixHtml } from '../Helix';
import { HELIX_DATA } from '../HelixData';
import { VASTU_DEVTA_GUIDE_DATA } from '../VastuDevtaGuideData';
import { NAVGRAH_SHANTI_DATA } from '../NavgrahShantiData';
import { FENG_SHUI_DATA } from '../FengShuiData';
import { ZONE_ANALYSIS_DATA } from '../ZoneAnalysisData';
import { describeSaveError } from '../storageUsage';
import { loShuText, localDigits, nvLabel } from '../NumeroVastuI18n';
import { pdfToDocxBlob, downloadBlob, createWordTextRecorder } from './utils/wordExport';
import { fitImageToBox } from './components/ReplaceableImage';
import { shrinkImageDataUrl } from './utils/imageShrink';
import ReportFooterModal from './components/ReportFooterModal';
import ZoneAnalysis, { buildInitialHtml as buildZoneAnalysisHtml, isStillDefaultHtml as isDefaultZoneAnalysisHtml } from '../ZoneAnalysis';
import VastuPersonsGateway, { RASHIS, HOUSES, getRashi } from '../VastuPersonsGateway';
import { buildAstroAnalysisHtml, isDefaultAstroHtml } from '../AstroPlanetHouseMeanings';
import { parseNotesBlock } from '../SpaceNotesData';
import WallColorSchemaTable from '../WallColorSchemaTable';
import { WALL_COLOR_ROWS, wallLabel, wallCellTokens } from '../WallColorSchemaData';
import { drawNorthIndianChartPdf, drawSouthIndianChartPdf } from '../NorthIndianChart';
import { VEDIC_VASTU_DIRECTIONS, LAAL_KITAAB_DIRECTIONS, computeAstroVastuDirections } from '../VastuChartVariants';
import CutExtensionGuide from '../CutExtensionGuide';
import { ZONE_LABELS as CE_ZONE_LABELS, CUT_ZONE_TEXT, EXTENSION_ZONE_TEXT, buildZoneHtml, isDefaultZoneHtml } from '../CutExtensionZoneData';
import { KUA_DATA, calcKuaNumber, calcMoolank, calcBhagyank, calcNumerologyMatch, getLuckyColors, getUnluckyColors, buildKuaHtml, isDefaultKuaHtml, kuaDir, kuaElement, kuaGroup, kuaPersonality, kuaLifeArea, kuaColors, kuaLabel } from '../KuaNumberData';
import { computeNavataraChakra, buildActivityMapping, HIT_PLANETS, HIT_DISPLAY_HOUSES, getNakshatra, getNakshatraDeity, getNakshatraBestDirection, GRAHA_PLANT_REMEDY, deityLabel, directionLabel, plantLabel, navataraLabel, activityLabel, compassCodeLabel, laalKitaabLabel, rashiLabel, planetLabel, kundaliTextLabel, ascLabel } from '../KundaliReportsData';
import BirthDateModal from '../BirthDateModal';
import NumeroVastuReport from '../NumeroVastuReport';
import { LANGUAGE_OPTIONS, LANGUAGE_NATIVE_FONT, pickLang } from '../i18n/languageOptions';
import useIsMobile from '../../../../hooks/useIsMobile';
import MobileSheetWrapper from './mobile/MobileSheetWrapper';
import MobileBottomToolbar from './mobile/MobileBottomToolbar';
import { AccountDetailsWithProfile } from '../../../../components/UserProfilePopover';

// ?"??"??"? Extracted constants ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
import { Vedic_Devata_NAMES, Devata_NAMES } from './constants/devata';
import DEVATA_DESCRIPTIONS from './constants/devataDescriptions.json';
import { elementalMapping } from './constants/elementalMapping';
import { ZONE_COLORS, getZoneColors } from './constants/zoneColors';
import { DEFAULT_EXPORT_LAYERS } from './constants/exportLayers';
import {
  predefinedImages,
  img_cover_logo,
  img_cover_page_template,
  img_last_page_template,
} from './constants/predefinedImages';
import {
  area45Directions,
  mainDirections,
  all32Directions,
  all64Directions,
  directions8,
  directions24,
} from './constants/directions';
import { LAYER_NAMES, getLayerName } from './constants/layerNames';
import { HOUSE_PLANNING_NOTES } from './constants/housePlanningNotes';
import { VEDIC_GENERATIVE_IDS } from './constants/vedicGenerativeGeometry';
import { ROOM_TYPES } from './constants/roomTypes';
import { STAGES } from './constants/stages';

// ?"??"??"? Extracted utils ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
import {
  toMartinezPolygon,
  buildSectorWedge,
  clipSectorWithBoundary,
  calculateSectorArea,
} from './utils/geometry';
import { parseDistanceInput } from './utils/measurementHelpers';

// ?"??"??"? Extracted sibling components ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
import ToggleSwitch from './components/ToggleSwitch';
import FloorPlanImage from './components/FloorPlanImage';
import DevataBlocksTable from './components/DevataBlocksTable';
import RatnaSthapnaTable from './components/RatnaSthapnaTable';

// ?"??"??"? Project flow (Phase 2) ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
import SaveProjectModal from './components/SaveProjectModal';
import useEditorState from './hooks/useEditorState';
import useProject from './hooks/useProject';
import projectService from './services/projectService';
import { userAuthAPI } from '../../../../services/api';

// Zone Analysis items are nested per zone; the editor and export both work on the flat list.
const ZONE_ANALYSIS_ITEMS = ZONE_ANALYSIS_DATA.flatMap(z => z.items.map(i => ({ ...i, zoneName: z.zoneName, zoneId: z.zoneId })));
// Auto-save of a report's default (every item on) so it can be enabled and exported without opening Edit first.
const saveDefaultReport = (key, list) => localStorage.setItem(key, JSON.stringify(list.map(d => ({ id: d.id, enabled: true }))));
// Saved reports may be slim ({ id, enabled, richHtml? }): fill names/images from the static data and rebuild default text.
const loadReportList = (key, staticList, buildHtml, lang) => {
  try {
    const s = localStorage.getItem(key);
    if (!s) return [];
    return JSON.parse(s).map(p => {
      const d = { ...(staticList.find(x => x.id === p.id) || {}), ...p };
      if (!d.richHtml) d.richHtml = buildHtml(d, lang);
      return d;
    });
  } catch { return []; }
};

// ToggleSwitch and FloorPlanImage extracted to ./Configure/components/{ToggleSwitch,FloorPlanImage}.js

// Mobile-only top toolbar button style — uniform height + a single theme
// color (teal, matching the app) for every header button/chip on phone
// widths, replacing the mixed red/green/blue/orange badge colors and
// mismatched heights that desktop still uses. Desktop's own inline style
// objects are left completely untouched everywhere this is used; each
// button picks between the two via isMobile. `enabled` only ever changes
// opacity/cursor here (never the hue) so the header stays genuinely
// one-color as requested. `square` drops the horizontal padding for
// icon-only buttons (Back/Download) so they read as equal-sized squares
// next to the icon+label ones.
const MOBILE_TOOLBAR_BTN_H = '32px';
const MOBILE_TOOLBAR_COLOR = '#00A19D';
const mobileToolbarBtnStyle = (enabled = true, square = false) => ({
  height: MOBILE_TOOLBAR_BTN_H,
  minHeight: MOBILE_TOOLBAR_BTN_H,
  width: square ? MOBILE_TOOLBAR_BTN_H : 'auto',
  padding: square ? 0 : '0 10px',
  background: MOBILE_TOOLBAR_COLOR,
  border: 'none',
  color: '#ffffff',
  borderRadius: '6px',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontSize: '0.68rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  marginLeft: 0,
  marginRight: 0,
  opacity: enabled ? 1 : 0.45,
});
// Same treatment for the plain info chips (Tap first point / Drawing.../
// measurement-cancel) sitting in the same row — not <button>s, but they
// need to match height + color for the header to read as one coherent bar.
const mobileToolbarChipStyle = () => ({
  height: MOBILE_TOOLBAR_BTN_H,
  minHeight: MOBILE_TOOLBAR_BTN_H,
  padding: '0 10px',
  background: MOBILE_TOOLBAR_COLOR,
  color: '#ffffff',
  borderRadius: '6px',
  border: 'none',
  fontSize: '0.68rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
});

// Small square logo thumbnail for the Cover Page's Consultant card — fitted
// (contain, no crop/stretch) inside the given box, centered. Shows a dashed
// placeholder box (instead of rendering nothing) when there's no logo yet or
// it failed to load, so the reserved spot is always visible.
const ConsultantLogoImage = ({ src, x, y, size, resolving }) => {
  const [image, status] = useImage(src, 'anonymous');
  if (!src || status !== 'loaded' || !image) {
    const label = resolving ? 'Loading...' : (status === 'failed' ? 'Logo\nfailed' : 'LOGO');
    return (
      <Group listening={false}>
        <Rect x={x} y={y} width={size} height={size} stroke="#cccccc" strokeWidth={1} dash={[4, 3]} cornerRadius={4} />
        <Text
          x={x} y={y} width={size} height={size}
          text={label}
          fontSize={9} fill="#aaaaaa" align="center" verticalAlign="middle"
        />
      </Group>
    );
  }
  // No radius/clipping any more — show the whole logo, "contain" scaled
  // (not cropped to fill a circle) so nothing of the original image is cut off.
  const scale = Math.min(size / image.width, size / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  return (
    <KonvaImage
      image={image}
      x={x + (size - w) / 2}
      y={y + (size - h) / 2}
      width={w}
      height={h}
      listening={false}
    />
  );
};

// Cover page's new full-bleed background artwork (client-supplied template —
// already generated at A4 proportions, so it's drawn edge-to-edge with no
// letterboxing/padding, unlike FloorPlanImage's framed floor-plan images).
const CoverPageBackgroundImage = ({ width, height, src, onReady }) => {
  const [image, status] = useImage(src || img_cover_page_template);
  useEffect(() => { if ((status === 'loaded' || status === 'failed') && onReady) onReady(); }, [status, onReady]);
  if (!image) return null;
  return <KonvaImage image={image} x={0} y={0} width={width} height={height} listening={false} />;
};

// Cover Back's own full-bleed artwork — same idea as CoverPageBackgroundImage above.
const CoverBackBackgroundImage = ({ width, height, src, onReady }) => {
  const [image, status] = useImage(src || img_last_page_template);
  useEffect(() => { if ((status === 'loaded' || status === 'failed') && onReady) onReady(); }, [status, onReady]);
  if (!image) return null;
  return <KonvaImage image={image} x={0} y={0} width={width} height={height} listening={false} />;
};

// Area (px^2) of a list of clipped rings [[{x,y},...],...].
const ringsArea = (rings) => (rings || []).reduce((sum, ring) => {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    a += ring[i].x * ring[j].y - ring[j].x * ring[i].y;
  }
  return sum + Math.abs(a / 2);
}, 0);

// Dynamic sector radius (same rule every clipSectorWithBoundary caller in this
// file uses): far enough past the plan's corners that the wedge always fully
// covers the boundary before clipping.
const triDynamicRadius = (center, boundaryPoints) => {
  const xs = boundaryPoints.map(p => p.x), ys = boundaryPoints.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const corners = [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }];
  return Math.max(...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))) * 1.2;
};

// Re-derives the same dosha/guna area-share slices each of the 4 Tri-Dosh /
// Tri-Guna Konva blocks computes for its own on-canvas pie chart — kept as a
// separate top-level copy (not shared with those blocks) so it can run from
// a plain useEffect and be saved into layerState.triChartData, which is what
// makes the data available for the chart's own PDF page even when this
// layer's editor isn't currently open.
const computeTriAreaShares = (layerId, polygonPoints, compassCenter, rotation, parentRot) => {
  if (!compassCenter || !polygonPoints || polygonPoints.length < 3) return null;
  const R = triDynamicRadius(compassCenter, polygonPoints);
  const sectorArea = (startDeg, endDeg) =>
    ringsArea(clipSectorWithBoundary(compassCenter, R, startDeg + rotation + parentRot, endDeg + rotation + parentRot, polygonPoints));

  if (layerId === 'tri-dosh-Vedic') {
    const quadrants = [
      { label: 'Wet Kapha', start: 270, end: 360 }, { label: 'Pitta', start: 0, end: 90 },
      { label: 'Dry Kapha', start: 90, end: 180 }, { label: 'Vata', start: 180, end: 270 },
    ];
    const acc = {};
    // Kept as 4 separate quadrants (was merged into 3, matching the "Tri-Dosh"
    // name — but the on-canvas overlay right above already draws 4 distinct
    // wedges, so the Area Share chart was under-reporting them). User's
    // explicit call: show all 4 quadrants split out, not the merged 3-dosha
    // convention.
    quadrants.forEach(({ label, start, end }) => {
      acc[label] = (acc[label] || 0) + sectorArea(start, end);
    });
    return { title: 'Tri-Dosh - Area Share', slices: [
      { label: 'Vata', value: acc.Vata || 0, color: '#B0BEC5' },
      { label: 'Pitta', value: acc.Pitta || 0, color: '#64B5F6' },
      { label: 'Wet Kapha', value: acc['Wet Kapha'] || 0, color: '#4CAF50' },
      { label: 'Dry Kapha', value: acc['Dry Kapha'] || 0, color: '#A5D6A7' },
    ] };
  }

  if (layerId === 'tri-guna-Vedic') {
    const sectors = [
      { name: 'RAJAS', start: 240, end: 270 }, { name: 'SATTVA', start: 270, end: 300 },
      { name: 'SATTVA', start: 300, end: 330 }, { name: 'SATTVA', start: 330, end: 360 },
      { name: 'RAJAS', start: 0, end: 30 }, { name: 'RAJAS', start: 30, end: 60 },
      { name: 'RAJAS', start: 60, end: 90 }, { name: 'TAMAS', start: 90, end: 120 },
      { name: 'TAMAS', start: 120, end: 150 }, { name: 'TAMAS', start: 150, end: 180 },
      { name: 'RAJAS', start: 180, end: 210 }, { name: 'RAJAS', start: 210, end: 240 },
    ];
    const acc = {};
    sectors.forEach(({ name, start, end }) => { acc[name] = (acc[name] || 0) + sectorArea(start, end); });
    return { title: 'Tri-Guna - Area Share', slices: [
      { label: 'Sattva', value: acc.SATTVA || 0, color: '#d4b78c' },
      { label: 'Rajas', value: acc.RAJAS || 0, color: '#9bb6e8' },
      { label: 'Tamas', value: acc.TAMAS || 0, color: '#72d26a' },
    ] };
  }

  if (layerId === 'tri-dosh-modern' || layerId === 'tri-guna-modern') {
    const names = layerId === 'tri-dosh-modern' ? ['Rajas', 'Sattva', 'Tamas'] : ['Pitta', 'Vata', 'Kapha'];
    const colors = ['#B83000', '#0D5C99', '#1E6B22'];
    const acc = { first: 0, second: 0, third: 0 };
    for (let i = 0; i < 24; i++) {
      const baseSvgAngle = i * 15 - 90;
      const normalized = ((baseSvgAngle + 90) % 360 + 360) % 360;
      const key = normalized >= 105 && normalized < 225 ? 'first' : normalized >= 225 && normalized < 345 ? 'second' : 'third';
      acc[key] += sectorArea(baseSvgAngle, baseSvgAngle + 15);
    }
    const title = layerId === 'tri-dosh-modern' ? 'Tri-Guna - Area Share' : 'Tri-Dosh - Area Share';
    return { title, slices: ['first', 'second', 'third'].map((k, i) => ({ label: names[i], value: acc[k], color: colors[i] })) };
  }

  return null;
};

// Vertical value labels inside the bar charts (read bottom -> top). They used to be a CSS rotate(-90deg) span,
// whose result depends on how html2canvas / the browser applies transform-origin — it came out upside down in
// some captures. A tiny canvas picture with the text already turned can never come out flipped, in any capture.
const _vLabelCache = new Map();
const verticalLabelImage = (text, color = '#ffffff', fontPx = 7) => {
  const key = text + '|' + color + '|' + fontPx;
  if (_vLabelCache.has(key)) return _vLabelCache.get(key);
  const K = 4; // draw at 4x so it stays sharp in the 2x capture and in print
  const font = 'bold ' + (fontPx * K) + 'px Arial, Helvetica, sans-serif';
  const probe = document.createElement('canvas').getContext('2d');
  probe.font = font;
  const textW = Math.ceil(probe.measureText(text).width) + 2;
  const lineH = Math.ceil(fontPx * 1.25 * K);
  const c = document.createElement('canvas');
  c.width = lineH; c.height = textW;
  const g = c.getContext('2d');
  g.font = font; g.fillStyle = color; g.textBaseline = 'middle'; g.textAlign = 'left';
  g.translate(lineH / 2, textW);   // bottom edge, horizontally centred
  g.rotate(-Math.PI / 2);          // text now runs from the bottom upwards
  g.fillText(text, 1, 0);
  const out = { url: c.toDataURL('image/png'), w: lineH / K, h: textW / K };
  _vLabelCache.set(key, out);
  return out;
};

// html2canvas lays text out with the page's web font but paints it with the
// canvas' own fallback font, so word gaps collapsed and neighbouring words
// overlapped in captured tables/charts ("NortEast", "MAXine"). Capturing in a
// plain system font keeps layout and painting consistent.
const captureSafeFont = (_doc, el) => {
  if (!el) return;
  const stack = 'Arial, Helvetica, sans-serif';
  el.style.fontFamily = stack;
  el.querySelectorAll('*').forEach((n) => { n.style.fontFamily = stack; });
};

// "North East" -> "NE", "South" -> "S", "North North East" -> "NNE". Anything that
// isn't made purely of compass words (already a code like "NNE", "N5", a devta
// name, ...) is returned unchanged.
const DIRECTION_INITIALS = { north: 'N', south: 'S', east: 'E', west: 'W' };
const shortDirection = (name) => {
  if (typeof name !== 'string') return name;
  const words = name.trim().split(/[\s-]+/);
  if (!words.length || !words.every(w => DIRECTION_INITIALS[w.toLowerCase()])) return name;
  return words.map(w => DIRECTION_INITIALS[w.toLowerCase()]).join('');
};

const LayerEditor = ({
  compassColor,
  polygonColor,
  compassOpacity,
  compassLineOpacity: parentCompassLineOpacity = 0.7,
  compassLineWeight: parentCompassLineWeight = 2,
  polygonLineWeight = 2,
  polygonLineOpacity = 0.7,
  layerId, 
  layerName,
  baseImage, 
  uploadedImage,
  canvasDimensions,
  currentConfig,
  onSave: onSaveProp,
  onCancel,
  coverBackgrounds = {},
  onRequestReportsAfterSave,
  polygonPoints,
  isPolygonComplete,
  compassCenter,
  compassRotation: parentCompassRotation,
  measurements,
  isCalibrationSet,
  pixelsPerUnit,
  currentUnit,
  mainDirections = [],
  directions8 = [],
  directions24 = [],
  all32Directions = [],
  findPolygonIntersection = () => null,
  calculateDirectionMeasurements = () => null,
  calculate8DirectionMeasurements = () => null,
  calculate45AreaMeasurements = () => null,
  exportLayers = {},
  getLayerName = (id) => id,
    entranceReportImage,
  spaceReportImage,
  entranceReportData,
  spaceReportData,
  language = 'en',
  projectClientDetails = null,
  coverFrontConfig = null,
  autoSave = false
}) => {

  // Mobile ("Edit Mode") — the desktop toolbar's per-layer-type tool
  // controls (Float/Align, Opacity, Grid color, Measure, Download, etc.)
  // are hidden entirely on phone widths per explicit request; only Save +
  // Cancel remain, icon-only (no label). Desktop keeps every control
  // exactly as before via isMobile-gated JSX further down.
  const isMobile = useIsMobile();
  // On phone widths the full direction names ("North East") overflow and overlap in
  // table headers and chart axes, so they are shown as initials ("NE") there.
  const dirLabel = (d) => (isMobile ? shortDirection(d) : d);

  const A4_WIDTH = 794;
  const A4_HEIGHT = 1123;

  // Mobile canvas auto-fit — same technique as the main Configure canvas
  // (see its mobileFitScale): the A4-sized editing surface (and the report
  // preview blocks, which are close enough to A4 width) is shrunk to fit
  // the phone's viewport via a CSS transform on an ancestor wrapper, never
  // by touching the Stage's own width/height/scaleX/scaleY props — that
  // broke Konva's tap/click coordinate math last time. Fixes the
  // horizontal (and matching vertical) scrollbar that used to appear
  // bottom-right in Edit Mode on phone widths. Desktop is untouched
  // (editorFitScale is always 1 there).
  const [editorViewportWidth, setEditorViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 800);
  useEffect(() => {
    const onResize = () => setEditorViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const editorFitScale = isMobile ? Math.min(1, (editorViewportWidth - 24) / A4_WIDTH) : 1;
  // Zoom for generative layers' edit mode. Same trick as the main canvas: a CSS transform on a
  // wrapper (Konva reads pointer positions correctly through it), never the Stage's own scale,
  // so saving / capturing the layer image is not affected.
  const [editorZoom, setEditorZoom] = useState(1);
  const zoomInnerRef = useRef(null);
  // Konva nodes of the 45-block compass hover tooltip (see handleDevta45Hover).
  const devta45TipGroupRef = useRef(null);
  const devta45TipTextRef = useRef(null);
  const devta45TipTagRef = useRef(null);
  const [zoomInnerH, setZoomInnerH] = useState(A4_HEIGHT);
  useEffect(() => {
    const el = zoomInnerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setZoomInnerH(el.offsetHeight || A4_HEIGHT));
    ro.observe(el);
    return () => ro.disconnect();
  }, [layerId]);

  // Vedic_Devata_NAMES, Devata_NAMES, geometry helpers (toMartinezPolygon,
  // buildSectorWedge, clipSectorWithBoundary) imported from
  // ./Configure/constants/devata and ./Configure/utils/geometry
  const editorStageRef = useRef(null);
  const VedicStageRef = useRef(null);
  const MedicalStageRef = useRef(null);
  const OthersStageRef = useRef(null);
  const HousePlanningStageRef = useRef(null);
  const contentRef = useRef(null);
  const tableContentRef = useRef(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [compassOpacity2, setCompassOpacity2] = useState(0.5);
  
  // Text states
  const [isAddingText, setIsAddingText] = useState(false);
  const [texts, setTexts] = useState([]);
  const [nextTextId, setNextTextId] = useState((currentConfig?.texts?.length || 0) + 1);
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');

  // Measurement states for this layer
  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [layerMeasurements, setLayerMeasurements] = useState([]);
  const [removeMeasurementMode, setRemoveMeasurementMode] = useState(false);
  
  // Directly use main canvas calibration values
  const layerIsCalibrationSet = isCalibrationSet; // From props
  const layerPixelsPerUnit = pixelsPerUnit; // From props
  
  const [showMeasurementDropdown, setShowMeasurementDropdown] = useState(false);
  const [pngPosition, setPngPosition] = useState({ 
    x: currentConfig?.positionX || 0, 
    y: currentConfig?.positionY || 0 
  });
  
  // Layer type
  const [uploadedImageDimensions, setUploadedImageDimensions] = useState(null);
  const imageGroupRef = useRef(null);
  const transformerRef = useRef(null);
    const calculateIntersection = (center, angle) => {
    // Simple fallback calculation
    const radius = Math.min(A4_WIDTH, A4_HEIGHT) * 0.4;
    const rad = (angle * Math.PI) / 180;
    return {
      x: center.x + radius * Math.cos(rad),
      y: center.y + radius * Math.sin(rad)
    };
  };

  const [clientInfo, setClientInfo] = useState({
  name: '',
  phone: '',
  email: '',
  propertyType: '',
  address: '',
  dateOfVisit: ''
});

const [consultantInfo, setConsultantInfo] = useState({
  name: '',
  companyName: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  logo: ''
});

// ── Cover Front / Cover Back: optional custom full-page background ─────────
// Uploaded image replaces the bundled template art; the editable text sits on
// top exactly as before. Saved as layerImages['<layerId>-bg'] (so the backend
// stores it as a file, not in the project JSON) and used by the PDF export.
const [customBg, setCustomBg] = useState(null);
const [customBgDirty, setCustomBgDirty] = useState(false);
// Loader while a background is being set: shown until the new image is really
// on the canvas (upload) or for a fixed 3.5s (reset to the default artwork,
// which loads instantly and would otherwise look like nothing happened).
const [bgBusy, setBgBusy] = useState(null);
const bgBusyRef = useRef({ kind: null, t0: 0, timer: null });
const beginBgBusy = (kind) => {
  const b = bgBusyRef.current;
  if (b.timer) clearTimeout(b.timer);
  b.kind = kind; b.t0 = Date.now();
  setBgBusy(kind);
  // never leave the loader stuck if the image callback does not fire
  b.timer = setTimeout(() => { b.kind = null; setBgBusy(null); }, 12000);
};
const finishBgBusy = useCallback(() => {
  const b = bgBusyRef.current;
  if (!b.kind) return;
  const minMs = b.kind === 'reset' ? 3500 : 900;
  const wait = Math.max(0, minMs - (Date.now() - b.t0));
  if (b.timer) clearTimeout(b.timer);
  b.timer = setTimeout(() => { b.kind = null; setBgBusy(null); }, wait);
}, []);
const onSave = (payload) => onSaveProp(customBgDirty ? { ...payload, customBackground: customBg || '' } : payload);

useEffect(() => {
  if (layerId !== 'cover-page' && layerId !== 'cover-back-page') return undefined;
  const ref = coverBackgrounds && coverBackgrounds[layerId];
  if (!ref) return undefined;
  let cancelled = false;
  projectService.resolveLayerImage(ref).then((res) => { if (!cancelled && res) setCustomBg(res); });
  return () => { cancelled = true; };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [layerId]);

const handleCoverBgUpload = (e) => {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (!file) return;
  if (!file.type || !file.type.startsWith('image/')) { alert('Please choose an image file (JPG / PNG / WEBP).'); return; }
  beginBgBusy('upload');
  const reader = new FileReader();
  reader.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      // A4 at ~150 dpi, "cover" fit so nothing gets stretched; JPEG keeps the saved file small.
      const W = 1240, H = 1754;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
      const k = Math.max(W / img.width, H / img.height);
      const dw = img.width * k, dh = img.height * k;
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      setCustomBg(c.toDataURL('image/jpeg', 0.88));
      setCustomBgDirty(true);
    };
    img.onerror = () => { alert('This image could not be read. Please try another file.'); finishBgBusy(); };
    img.src = reader.result;
  };
  reader.onerror = () => finishBgBusy();
  reader.readAsDataURL(file);
};

const renderCoverBgControls = (inHeader = false) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: inHeader ? 0 : '10px', marginRight: inHeader ? '12px' : 0, flexWrap: 'wrap', flexShrink: 0 }}>
    <label
      title="Replaces the whole page background — click Save to keep it"
      style={inHeader
        ? { background: '#ffffff', color: '#00A19D', borderRadius: '6px', padding: '0 14px', height: '100%', display: 'inline-flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }
        : { background: '#00A19D', color: '#fff', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
      {customBg ? 'Change Background Image' : 'Upload Background Image'}
      <input type="file" accept="image/*" onChange={handleCoverBgUpload} style={{ display: 'none' }} />
    </label>
    {customBg && (
      <button type="button" onClick={() => { beginBgBusy('reset'); setCustomBg(null); setCustomBgDirty(true); }}
        style={inHeader
          ? { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '0 12px', height: '100%', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }
          : { background: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
        Reset to default
      </button>
    )}
  </div>
);

const [editingField, setEditingField] = useState(null); // { type: 'client'/'consultant', field: 'name'/'phone'/'email'/'address'/'companyName'/'website' }
const [editingValue, setEditingValue] = useState('');

// elementalMapping imported from ./Configure/constants/elementalMapping
// calculateSectorArea imported from ./Configure/utils/geometry

// Default per-field consultant info from the logged-in user's own profile
// (Settings > Update Profile) — so every project's cover page starts
// pre-filled instead of blank. Only used as a fallback when the project has
// never saved its own consultant info yet (see useEffect below).
const buildConsultantDefaultsFromProfile = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user_data') || 'null');
    if (!u) return { name: '', companyName: '', phone: '', email: '', website: '', address: '', logo: '' };
    return {
      name: u.name || '',
      companyName: u.company_name || '',
      phone: u.mobile || '',
      email: u.email || '',
      website: u.website_url || '',
      address: u.address || '',
      logo: u.logo || '',
    };
  } catch { return { name: '', companyName: '', phone: '', email: '', website: '', address: '', logo: '' }; }
};

useEffect(() => {
  if (layerId === 'cover-page' || layerId === 'cover-back-page') {
    // Cover Back shows the SAME Owner Name / Consultant fields as Cover Front
    // (per its own on-screen caption), but it is a separate saved layer with
    // no clientInfo/consultantInfo of its own — its currentConfig is always
    // empty for these. Read Cover Front's own saved config instead so Cover
    // Back actually reflects what was typed there, not blank placeholders.
    const sourceConfig = layerId === 'cover-page' ? (currentConfig || {}) : (coverFrontConfig || {});
    // Each field falls back to the Property/Client Details collected on the
    // "Create Project" form (projectClientDetails) whenever this cover page
    // has no value of its own saved yet — same pattern as consultantInfo's
    // profile fallback below. Once the user edits a field here directly, that
    // saved value takes over from then on.
    const savedClient = sourceConfig.clientInfo || {};
    const pickClient = (field) => savedClient[field] || (projectClientDetails && projectClientDetails[field]) || '';
    setClientInfo({
      name: pickClient('name'),
      phone: pickClient('phone'),
      email: pickClient('email'),
      propertyType: pickClient('propertyType'),
      address: pickClient('address'),
      dateOfVisit: pickClient('dateOfVisit'),
    });
    // Consultant info: each field falls back to the profile default whenever it
    // has no real content of its own yet — either never saved, or blank. Once
    // the user actually types something into a field, that takes over.
    // The logo always follows the current profile — there's no per-project
    // logo upload UI.
    const profileDefaults = buildConsultantDefaultsFromProfile();
    const saved = sourceConfig.consultantInfo;
    const pick = (field) => (saved && saved[field]) ? saved[field] : profileDefaults[field];

    // One-time migration: older projects (saved before this card layout existed)
    // dumped everything — "Name : x\nCompany : y\n..." — into the single old
    // `address` field. Detect that stale combined blob and discard it instead
    // of showing it verbatim inside the new, separate Address field.
    const isStaleCombinedBlob = (text) =>
      typeof text === 'string' && /^Name\s*:/.test(text.trim()) && /\nCompany\s*:/.test(text);
    const rawAddress = pick('address');
    const addressValue = isStaleCombinedBlob(rawAddress) ? profileDefaults.address : rawAddress;

    setConsultantInfo({
      name:        pick('name'),
      companyName: pick('companyName'),
      phone:       pick('phone'),
      email:       pick('email'),
      website:     pick('website'),
      address:     addressValue,
      logo: profileDefaults.logo,
    });
  }
}, [layerId, currentConfig, projectClientDetails, coverFrontConfig]);

// Same CDN/CORS workaround as the floor plan image, but for the Cover Page's
// company logo (Consultant card) — resolves to a base64 data: URL so the
// canvas stays exportable.
const [resolvedLogoSrc, setResolvedLogoSrc] = useState('');
const [logoResolving, setLogoResolving] = useState(false);
useEffect(() => {
  if (!consultantInfo.logo) { setResolvedLogoSrc(''); setLogoResolving(false); return; }
  let cancelled = false;
  setLogoResolving(true);
  userAuthAPI.resolveLogoImage(consultantInfo.logo).then((resolved) => {
    if (!cancelled) { setResolvedLogoSrc(resolved); setLogoResolving(false); }
  });
  return () => { cancelled = true; };
}, [consultantInfo.logo]);

// Add handler functions for editing
const handleFieldClick = (type, field) => {
  setEditingField({ type, field });
  setEditingValue(type === 'client' ? clientInfo[field] : consultantInfo[field]);
};

const handleFieldChange = (e) => {
  setEditingValue(e.target.value);
};

const handleFieldBlur = () => {
  if (editingField) {
    if (editingField.type === 'client') {
      setClientInfo(prev => ({
        ...prev,
        [editingField.field]: editingValue
      }));
    } else {
      setConsultantInfo(prev => ({
        ...prev,
        [editingField.field]: editingValue
      }));
    }
    setEditingField(null);
    setEditingValue('');
  }
};

  const handleFieldKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFieldBlur();
    }
  };



  const calculateBarChart16Data = () => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) return [];
    return mainDirections.map(direction => {
      const measurements = calculateDirectionMeasurements(direction.area);
      return {
        direction: direction.area,
        area: measurements && !measurements.calibrationRequired ? measurements.area : '0.00',
        unit: currentUnit
      };
    }).filter(item => parseFloat(item.area) > 0);
  };

  const calculateBarChart32Data = () => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) return [];
    return all32Directions.map(({ area }) => ({
      direction: area,
      area: (Math.random() * 50).toFixed(1),
      unit: currentUnit
    }));
  };

  const calculateBarChart45Data = () => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) return [];
    
    const data = [];
    for (let blockNum = 1; blockNum <= 45; blockNum++) {
      const blockData = calculate45AreaMeasurements(blockNum);
      if (blockData && !blockData.calibrationRequired) {
        data.push({
          blockNumber: blockNum,
          area: blockData.area,
          unit: currentUnit
        });
      } else {
        data.push({
          blockNumber: blockNum,
          area: '0.00',
          unit: currentUnit
        });
      }
    }
    
    return data;
  };
  // ?o. MODERN 45 ??" alag ring boundaries: 25% / 50% / 75% / 100%
  const calculateModern45AreaMeasurements = (blockNumber) => {
    if (!compassCenter || !isPolygonComplete || !isCalibrationSet || !pixelsPerUnit) {
      return {
        blockNumber,
        area: '0.00',
        perim: '0.00',
        unit: currentUnit,
        calibrationRequired: true,
        error: 'Missing requirements'
      };
    }

    let ringInner, ringOuter, directionIndices = [];

    if (blockNumber === 1) {
      ringInner = 0;
      ringOuter = 0.25;
      directionIndices = Array.from({ length: 32 }, (_, i) => i);
    } else if (blockNumber >= 2 && blockNumber <= 5) {
      ringInner = 0.25;
      ringOuter = 0.5;
      const quadrantMap = {
        2: [11, 12, 13, 14, 15, 16, 17, 18],
        3: [19, 20, 21, 22, 23, 24, 25, 26],
        4: [27, 28, 29, 30, 31, 0, 1, 2],
        5: [3, 4, 5, 6, 7, 8, 9, 10]
      };
      directionIndices = quadrantMap[blockNumber];
    } else if (blockNumber >= 6 && blockNumber <= 13) {
      ringInner = 0.5;
      ringOuter = 0.75;
      const ring3Map = {
        6: [1, 2], 7: [3, 4], 8: [9, 10], 9: [11, 12],
        10: [17, 18], 11: [19, 20], 12: [25, 26], 13: [27, 28]
      };
      directionIndices = ring3Map[blockNumber];
    } else if (blockNumber >= 14 && blockNumber <= 45) {
      ringInner = 0.75;
      ringOuter = 1.0;
      directionIndices = [(blockNumber - 14 + 3) % 32];
    } else {
      return null;
    }

    const sectorPoints = [];
    if (ringInner === 0) sectorPoints.push(compassCenter);

    const directions = directionIndices.map(idx => all32Directions[idx]);
    const closingIndex = (directionIndices[directionIndices.length - 1] + 1) % 32;
    const allDirections = [...directions, all32Directions[closingIndex]];

    allDirections.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection && ringInner > 0) {
        sectorPoints.push({
          x: compassCenter.x + (intersection.x - compassCenter.x) * ringInner,
          y: compassCenter.y + (intersection.y - compassCenter.y) * ringInner
        });
      }
    });

    const outerPoints = [];
    allDirections.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection) {
        outerPoints.push({
          x: compassCenter.x + (intersection.x - compassCenter.x) * ringOuter,
          y: compassCenter.y + (intersection.y - compassCenter.y) * ringOuter
        });
      }
    });
    outerPoints.reverse().forEach(p => sectorPoints.push(p));

    if (sectorPoints.length < 3) return null;

    let areaPixels = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      areaPixels += sectorPoints[i].x * sectorPoints[j].y;
      areaPixels -= sectorPoints[j].x * sectorPoints[i].y;
    }
    areaPixels = Math.abs(areaPixels / 2);

    let perimPixels = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      const dx = sectorPoints[j].x - sectorPoints[i].x;
      const dy = sectorPoints[j].y - sectorPoints[i].y;
      perimPixels += Math.sqrt(dx * dx + dy * dy);
    }

    return {
      blockNumber,
      area: (areaPixels / (pixelsPerUnit * pixelsPerUnit)).toFixed(1),
      perim: (perimPixels / pixelsPerUnit).toFixed(1),
      unit: currentUnit,
      calibrationRequired: false,
      sectorPoints
    };
  };

const calculateBarChart452Data = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
 
    return [];
  }
  
  const data = [];
  for (let blockNum = 1; blockNum <= 45; blockNum++) {
    // ?o. FIX: Modern 45 ke liye alag function - correct 25/50/75/100 ring boundaries
    const blockData = calculateModern45AreaMeasurements(blockNum);
    if (blockData && !blockData.calibrationRequired) {
      data.push({
        blockNumber: blockNum,
        area: blockData.area,
        unit: currentUnit
      });
    } else {
      data.push({
        blockNumber: blockNum,
        area: '0.00',
        unit: currentUnit
      });
    }
  }
  
  return data;
};


// ?o. COMPASS-45 RING-BASED GROUPED BAR DATA
const calculateGroupedBarData45 = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return { group4: [], group8: [], group32: [] };
  }

  const all45Data = [];
  for (let blockNum = 1; blockNum <= 45; blockNum++) {
    const blockData = calculate45AreaMeasurements(blockNum);
    all45Data.push({
      blockNumber: blockNum,
      area: blockData && !blockData.calibrationRequired ? blockData.area : '0.00',
      unit: currentUnit
    });
  }

  const group4 = all45Data.slice(1, 5).map(item => ({
    ...item,
    group: 'Ring 2 - 4 Cardinal Blocks (2-5)'
  }));

  const group8 = all45Data.slice(5, 13).map(item => ({
    ...item,
    group: 'Ring 3 - 8 Blocks (6-13)'
  }));

  const group32 = all45Data.slice(13, 45).map(item => ({
    ...item,
    group: 'Ring 4 - 32 Outer Blocks (14-45)'
  }));



  return { group4, group8, group32 };
};

// ?o. COMPASS-45 RING-BASED GROUPED TABLE DATA
const calculateGroupedTableData45 = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return { group4: [], group8: [], group32: [] };
  }

  const all45Data = [];
  for (let blockNum = 1; blockNum <= 45; blockNum++) {
    const blockData = calculate45AreaMeasurements(blockNum);
    all45Data.push({
      blockNumber: blockNum,
      area: blockData && !blockData.calibrationRequired ? blockData.area : '0.00',
      unit: currentUnit
    });
  }

  const group4 = all45Data.slice(1, 5).map(item => ({
    ...item,
    group: 'Ring 2 - 4 Cardinal Blocks (2-5)',
    DevataName: Vedic_Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));

  const group8 = all45Data.slice(5, 13).map(item => ({
    ...item,
    group: 'Ring 3 - 8 Blocks (6-13)',
    DevataName: Vedic_Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));

  const group32 = all45Data.slice(13, 45).map(item => ({
    ...item,
    group: 'Ring 4 - 32 Outer Blocks (14-45)',
    DevataName: Vedic_Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));



  return { group4, group8, group32 };
};
const calculateGroupedBarData = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return {
      group4: [],
      group8: [],
      group32: []
    };
  }
  
  const all45Data = calculateBarChart452Data();
  
  // Group 1: First 4 Bars (Blocks 2-5)
  const group4 = all45Data.slice(1, 5).map(item => ({
    ...item,
    group: 'Group 1 (Blocks 2-5)'
  }));
  
  // Group 2: Next 8 Bars (Blocks 5-13)
  const group8 = all45Data.slice(5, 13).map(item => ({
    ...item,
    group: 'Group 2 (Blocks 5-12)'
  }));
  
  // Group 3: Remaining 32 Bars (Blocks 14-45)
  const group32 = all45Data.slice(13, 45).map(item => ({
    ...item,
    group: 'Group 3 (Blocks 14-45)'
  }));

  
  return {
    group4,
    group8,
    group32
  };
};


const getLayerType = () => {
  if (layerId === 'cover-page' || layerId === 'cover-index') return 'cover-page';
  if (layerId === 'compass-45-Devata-table' || layerId === 'compass-45-2-Devata-table') return 'Devata-table';
  if (
    layerId.includes('Bar-chart') ||
    layerId.includes('compass-45-2-table') ||
    layerId.includes('compass-45-2-chart') ||
    layerId === 'compass-45-table' ||
    layerId === 'compass-45-chart'
  ) return 'Bar-chart';
  // ?o. UPDATED: center-analysis & surya-chandra-bhag moved to generative
  if ((layerId.includes('compass') && !layerId.startsWith('house-')) || layerId === 'grid-layout' || layerId === 'coloured-zone' || layerId === 'elemental-5-zone' || layerId === 'elemental-5-chart' || layerId === 'marmsthan-advance' || layerId === 'center-analysis' || layerId === 'surya-chandra-bhag' || layerId === '4-state-image' || layerId === 'tri-dosh-Vedic' || layerId === 'tri-dosh-modern' || layerId === 'tri-guna-modern' || layerId === 'tri-guna-Vedic'  || layerId === 'body-part-division' || layerId === 'floor-plan' || layerId === 'MANDAL'  || layerId === 'RATNA-STHAPNA' || VEDIC_GENERATIVE_IDS.includes(layerId)) return 'generative';
  return 'png';
};
  const layerType = getLayerType();
  const editorZoomOn = layerType === 'generative';
  const editorZoomed = editorZoomOn && editorZoom !== 1;
  const editorViewScale = editorFitScale * (editorZoomOn ? editorZoom : 1);

  // Same sharpness fix as the main canvas: draw every layer of the edit-mode Stage with more real pixels
  // (2x at 100%, growing with zoom, capped at 3x) so zooming in no longer turns the drawing blurry.
  useEffect(() => {
    const stage = editorStageRef.current;
    if (!stage || typeof stage.getLayers !== 'function') return;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    let target = isMobile ? dpr : Math.max(2, dpr);
    target = Math.max(target, Math.ceil(dpr * Math.max(1, editorViewScale)));
    target = Math.min(target, Math.max(3, dpr));
    stage.getLayers().forEach((layer) => {
      const canvas = layer.getCanvas && layer.getCanvas();
      if (!canvas) return;
      let changed = false;
      if (Math.abs(canvas.getPixelRatio() - target) > 0.01) { canvas.setPixelRatio(target); changed = true; }
      const ctx = layer.getContext && layer.getContext() && layer.getContext()._context;
      if (ctx) { ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'; }
      if (changed) layer.batchDraw();
    });
  });
  // ?o. ADD THESE FUNCTIONS IN LayerEditor.js BEFORE getDefaultStateForLayer
  const calculateBarChart8Data = () => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) return [];
    return directions8.map(direction => {
      const measurements = calculate8DirectionMeasurements(direction.area);
      return {
        direction: direction.area,
        area: measurements && !measurements.calibrationRequired ? measurements.area : '0.00',
        unit: currentUnit
      };
    }).filter(item => parseFloat(item.area) > 0);
  };
  const getDefaultStateForLayer = () => {
      // Cover Page case
    if (layerId === 'cover-page') {
      return {
        showCoverPage: true,
        texts: [],
        layerMeasurements: []
      };
    }
    if (layerId === 'cover-back-page' || layerId === 'cover-index') {
      return { texts: [], layerMeasurements: [] };
    }
    if (layerId === 'compass-45-table') {
      return {
        data: calculateBarChart45Data(),
        showTable: true,
        showBarChart: false,
        tableOpacity: 0.9,
        chartOpacity: 0.9,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
    }

    if (layerId === 'compass-45-chart') {
      return {
        data: calculateBarChart45Data(),
        showTable: false,
        showBarChart: true,
        tableOpacity: 0.9,
        chartOpacity: 0.9,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
    }
    if (layerId === 'compass-45-2-table') {
      return {
        data: calculateBarChart452Data(),
        showTable: true,
        showBarChart: false,
        tableOpacity: 0.9,
        chartOpacity: 0.9,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
    }

    if (layerId === 'compass-45-2-chart') {
      return {
        data: calculateBarChart452Data(),
        showTable: false,
        showBarChart: true,
        tableOpacity: 0.9,
        chartOpacity: 0.9,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
    }
    if (layerId === 'compass-8') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'MANDAL') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'RATNA-STHAPNA') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      if (layerId === 'coloured-zone') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          // ?o. COLORED ZONE SPECIFIC PROPERTIES
          zoneColor: '#00A19D',
          zoneOpacity: 0.5, // ?o. Yeh colored area ke liye
          filledOpacity: 0.5, // ?o. Ya aap filledOpacity use kar sakte ho
          zonePattern: 'solid',
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        }
      }
      if (layerId === 'marmsthan-advance') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          // ?o. COLORED ZONE SPECIFIC PROPERTIES
          zoneColor: '#00A19D',
          zoneOpacity: 0.5, // ?o. Yeh colored area ke liye
          filledOpacity: 0.5, // ?o. Ya aap filledOpacity use kar sakte ho
          zonePattern: 'solid',
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        }
      }
      if (layerId === 'elemental-5-chart') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          // ?o. COLORED ZONE SPECIFIC PROPERTIES
          zoneColor: '#00A19D',
          zoneOpacity: 0.5, // ?o. Yeh colored area ke liye
          filledOpacity: 0.5, // ?o. Ya aap filledOpacity use kar sakte ho
          zonePattern: 'solid',
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        }
      }
      if (layerId === 'elemental-5-zone') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          // ?o. COLORED ZONE SPECIFIC PROPERTIES
          zoneColor: '#00A19D',
          zoneOpacity: 0.5, // ?o. Yeh colored area ke liye
          filledOpacity: 0.5, // ?o. Ya aap filledOpacity use kar sakte ho
          zonePattern: 'solid',
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        }
      }
    if (layerId === 'compass-24') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'surya-chandra-bhag') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === '4-state-image') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'tri-dosh-Vedic') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'tri-guna-modern') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'tri-guna-Vedic') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'tri-dosh-modern') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
    if (layerId === 'body-part-division') {
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }

    if (layerId === 'Bar-chart-8') {
      return {
        data: calculateBarChart8Data(),
        showTable: true,
        showBarChart: true,
        tableOpacity: 0.9,
        chartOpacity: 0.9,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
    }



    switch(layerId) {
      case 'compass-8':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'MANDAL':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'RATNA-STHAPNA':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'compass-24':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'surya-chandra-bhag':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case '4-state-image':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'tri-dosh-Vedic':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'tri-guna-modern':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'tri-guna-Vedic':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'tri-dosh-modern':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'body-part-division':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };

      case 'Bar-chart-8':
        return {
          data: calculateBarChart8Data(),
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'grid-layout':
        return {
          showGrid: true,
          gridOpacity: 0.5,
          gridRatioX: 1,
          gridRatioY: 1,
          gridColor: '#000000',
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'Bar-chart-16':
        return {
          data: calculateBarChart16Data(),
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'Bar-chart-32':
        return {
          data: calculateBarChart32Data(),
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'Bar-chart-45':
        return {
          data: calculateBarChart45Data(),
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'Bar-chart-45-2':
        return {
          data: calculateBarChart452Data(),
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'compass-16':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'coloured-zone':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'marmsthan-advance':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'elemental-5-chart':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'elemental-5-zone':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'compass-32':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: true,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'compass-45':
        return {
          show45Area: true,
          compassOpacity: 0.7,
          compassRotation: parentCompassRotation || 0,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'compass-45-2':
        return {
          show45Area: true,
          compassOpacity: 0.7,
          compassRotation: parentCompassRotation || 0,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'png-image-1':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'nakshatra-vastu':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-Nakshatra-Vastu':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case '32-Entrance-Analysis-Compass':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Khanija-Dhatu':
      case 'house-bank':
      case 'house-clinic':
      case 'house-electronic-shop':
      case 'house-factory':
      case 'house-hotel':
      case 'house-marriage-hall':
      case 'house-multiplex':
      case 'house-office-cabin':
      case 'house-petrol-pump':
      case 'house-retail-outlets':
      case 'house-school-college':
      case 'house-space-analysis-compass':
      case 'house-temple':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      // Vedic Generative Zones: computed live against the actual plot
      // polygon + compass center (same as compass-32 / elemental-5-zone) —
      // NOT a draggable image, so it uses the compass-style default state.
      case 'Vedic-zones-32':
      case 'Vedic-numbers-64':
      case 'Vedic-64-padvinyash':
      case 'Vedic-81-padvinyash':
      case 'Vedic-elemental-zone':
      case 'Vedic-planet-zoning':
        return {
          showCompass: true,
          compassRotation: parentCompassRotation || 0,
          compassOpacity: 0.7,
          compassColor: compassColor,
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
          showCompassLines: true,
          showSectorAreas: true,
          show32Directions: false,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Direction-Diety-Planet':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-Khanij':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-Bhojan':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-Bhojan-aadi':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-chinha':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Devata-chinha-aadi':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Muhurat-vichar':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Seharumkh-achintya-vibhav':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case '16-Directional-Domestic-Vastu':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
     
      case 'modern-Devata-45':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
     
      case 'mahavastu-chakra':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      case 'Vedic-Mahavastu-Chakra':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
     
        case 'shakti-chakra':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'floor-plan-vpm':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'vastu-numbers-81':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'grid-3x3':
        return {
          rotation: 0,
          opacity: 0.7,
          scale: 1,
          floatMode: false,
          positionX: 0,
          positionY: 0,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
        
      case 'center-analysis':
        return {
          showCenterPoint: true,
          centerPoint: compassCenter,
          opacity: 1,
          scale: 1,
          showCenterAnalysisLayer: true,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null,
          compassColor: '#000000',
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
        };
        
      case 'floor-plan':
        return {
          rotation: 0,
          opacity: 1,
          scale: 1,
          showFloorPlan: true,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null,
          compassColor: '#000000',
          compassLineWeight: parentCompassLineWeight,
          compassLineOpacity: parentCompassLineOpacity,
        };
        
      // Add default for any other layer types
      default:
        // Check if it's a PNG layer
        if (layerId.startsWith('png-image-') || layerId.includes('image')) {
          return {
            rotation: 0,
            opacity: 1,
            scale: 1,
            floatMode: false,
            positionX: 0,
            positionY: 0,
            texts: [],
            layerMeasurements: [],
            layerIsCalibrationSet: false,
            layerPixelsPerUnit: null
          };
        }
        
        // Check if it's a Bar-chart layer
        if (layerId.includes('Bar-chart') || layerId.includes('chart')) {
          return {
            data: [],
            showTable: true,
            showBarChart: true,
            tableOpacity: 0.9,
            chartOpacity: 0.9,
            texts: [],
            layerMeasurements: [],
            layerIsCalibrationSet: false,
            layerPixelsPerUnit: null
          };
        }
        
        if (layerId === 'cover-page') {
          return {
            texts: [],
            // ?o. Empty strings, not "Click to edit"
            clientInfo: {
              name: '',
              phone: '',
              email: '',
              propertyType: '',
              address: '',
              dateOfVisit: ''
            },
            consultantInfo: {
              name: '',
              phone: '',
              email: '',
              address: ''
            },
            layerMeasurements: [],
            layerIsCalibrationSet: false,
            layerPixelsPerUnit: null
          };
        }

        if (layerId.includes('compass')) {
          return {
            showCompass: true,
            compassRotation: parentCompassRotation || 0,
            compassOpacity: 0.7,
            compassColor: compassColor,
            compassLineWeight: parentCompassLineWeight,
            compassLineOpacity: parentCompassLineOpacity,
            showCompassLines: true,
            showSectorAreas: true,
            show32Directions: false,
            texts: [],
            layerMeasurements: [],
            layerIsCalibrationSet: false,
            layerPixelsPerUnit: null
          };
        }
        
        // Generic default for any other layer
        return {
          rotation: 0,
          opacity: 1,
          scale: 1,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
    }
  };
// LayerEditor component ke ANDAR, useState declarations ke paas add karo (line ~50)
const [coverLogoImage, setCoverLogoImage] = useState(null);

// useEffect add karo image load karne ke liye (line ~100 ke paas)
useEffect(() => {
  if (layerId === 'cover-page' && !coverLogoImage) {
    const img = new window.Image();
    img.src = img_cover_logo;
    img.onload = () => {
      setCoverLogoImage(img);
    };
  }
}, [layerId, coverLogoImage]);
const CoverPageLogo = ({ x, y, radius, image }) => {
  if (!image) return null;

  const diameter = radius * 2;
  const imageAspect = image.width / image.height;

  let imgWidth, imgHeight;

  const HEIGHT_MULTIPLIER = 1.9; // image height content se badi

  imgHeight = diameter * HEIGHT_MULTIPLIER;
  imgWidth = imgHeight * imageAspect;

  const cornerRadius = 20; // ?Y'^ ALL CORNERS 20px

  return (
    <Group
      x={x - imgWidth / 2}
      y={y - imgHeight / 2}
      clipFunc={(ctx) => {
        const w = imgWidth;
        const h = imgHeight;
        const r = cornerRadius;

        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.lineTo(w - r, 0);
        ctx.quadraticCurveTo(w, 0, w, r);
        ctx.lineTo(w, h - r);
        ctx.quadraticCurveTo(w, h, w - r, h);
        ctx.lineTo(r, h);
        ctx.quadraticCurveTo(0, h, 0, h - r);
        ctx.lineTo(0, r);
        ctx.quadraticCurveTo(0, 0, r, 0);
        ctx.closePath();
      }}
    >
      <KonvaImage
        image={image}
        width={imgWidth}
        height={imgHeight}
        listening={false}
      />
    </Group>
  );
};

 const getInitialState = () => {
  // If we have a saved config, use it as the primary source
  if (currentConfig && Object.keys(currentConfig).length > 0) {
      return {
          // Get defaults first
          ...getDefaultStateForLayer(),
          // Override with saved config values
          ...currentConfig,
          // Ensure texts and measurements are preserved (with proper fallbacks)
          texts: currentConfig.texts || [],
          layerMeasurements: currentConfig.layerMeasurements || [],
          layerIsCalibrationSet: currentConfig.layerIsCalibrationSet !== undefined 
              ? currentConfig.layerIsCalibrationSet : false,
          layerPixelsPerUnit: currentConfig.layerPixelsPerUnit !== undefined 
              ? currentConfig.layerPixelsPerUnit : null,
          // Ensure position is preserved for PNG layers
          positionX: currentConfig.positionX !== undefined ? currentConfig.positionX : 0,
          positionY: currentConfig.positionY !== undefined ? currentConfig.positionY : 0,
          // Preserve float mode
          floatMode: currentConfig.floatMode !== undefined ? currentConfig.floatMode : false,
          // ?o. CRITICAL: Ensure clientInfo and consultantInfo don't get "Click to edit" placeholders
          clientInfo: currentConfig.clientInfo || {
            name: '',
            phone: '',
            email: '',
            propertyType: '',
            address: '',
            dateOfVisit: ''
          },
          consultantInfo: currentConfig.consultantInfo || {
            name: '',
            phone: '',
            email: '',
            address: ''
          }
      };
  }
  // If no saved config, return defaults based on layer type
  return getDefaultStateForLayer();
};

  const [layerState, setLayerState] = useState(getInitialState());

  // Tri-Dosh / Tri-Guna: the pie chart isn't drawn on the canvas any more (it
  // has its own PDF page instead — see addTriChartPage) — this list is only
  // used to know which layer ids should keep layerState.triChartData in sync.
  const TRI_CHART_LAYER_IDS = ['tri-dosh-Vedic', 'tri-guna-Vedic', 'tri-dosh-modern', 'tri-guna-modern'];

  // Keeps layerState.triChartData in sync with the plan's own geometry, so the
  // dedicated PDF page (drawn from the SAVED layer config, independent of
  // whether this editor is even open) always reflects the current polygon/
  // compass — not just whatever the pie chart last happened to render while
  // someone was looking at it on screen.
  useEffect(() => {
    if (!TRI_CHART_LAYER_IDS.includes(layerId) || !isPolygonComplete || !compassCenter) return;
    const rotation = (layerState.compassRotation || 0) - (parentCompassRotation || 0);
    const result = computeTriAreaShares(layerId, polygonPoints, compassCenter, rotation, parentCompassRotation || 0);
    if (!result) return;
    setLayerState(prev => {
      const same = prev.triChartData && JSON.stringify(prev.triChartData.slices) === JSON.stringify(result.slices);
      return same ? prev : { ...prev, triChartData: result };
    });
  }, [layerId, isPolygonComplete, compassCenter, polygonPoints, layerState.compassRotation, parentCompassRotation]);

  useEffect(() => {
    if (currentConfig && layerId && Object.keys(currentConfig).length > 0) {
        
        // Update main layer state - preserve any existing state properties not in currentConfig
        setLayerState(prev => ({
            ...prev,
            ...currentConfig,
            // Ensure nested objects are properly merged
            texts: currentConfig.texts || prev.texts || [],
            layerMeasurements: currentConfig.layerMeasurements || prev.layerMeasurements || [],
            layerIsCalibrationSet: currentConfig.layerIsCalibrationSet !== undefined 
                ? currentConfig.layerIsCalibrationSet : prev.layerIsCalibrationSet,
            layerPixelsPerUnit: currentConfig.layerPixelsPerUnit !== undefined 
                ? currentConfig.layerPixelsPerUnit : prev.layerPixelsPerUnit,
            floatMode: currentConfig.floatMode !== undefined 
                ? currentConfig.floatMode : prev.floatMode
        }));

        // Update individual states
        if (currentConfig.texts && currentConfig.texts.length > 0) {
            setTexts(currentConfig.texts);
            const maxId = Math.max(...currentConfig.texts.map(t => t.id), 0);
            setNextTextId(maxId + 1);
        }

        if (currentConfig.layerMeasurements && currentConfig.layerMeasurements.length > 0) {
            setLayerMeasurements(currentConfig.layerMeasurements);
        }

        // Update PNG position if available
        if (currentConfig.positionX !== undefined && currentConfig.positionY !== undefined) {
            setPngPosition({
                x: currentConfig.positionX,
                y: currentConfig.positionY
            });
        }

    }
  }, [currentConfig, layerId]);

  useEffect(() => {
    if (currentConfig && Object.keys(currentConfig).length > 0) {
        
        // Initialize texts
        if (currentConfig.texts && currentConfig.texts.length > 0) {
            setTexts(currentConfig.texts);
            const maxId = Math.max(...currentConfig.texts.map(t => t.id), 0);
            setNextTextId(maxId + 1);
        }
        
        // Initialize measurements
        if (currentConfig.layerMeasurements && currentConfig.layerMeasurements.length > 0) {
            setLayerMeasurements(currentConfig.layerMeasurements);
        }
        
        // Initialize PNG position
        if (currentConfig.positionX !== undefined && currentConfig.positionY !== undefined) {
            setPngPosition({
                x: currentConfig.positionX,
                y: currentConfig.positionY
            });
        }
    }
  }, []); // Empty dependency array - only run on mount

  // Sync PNG position with compass center on mount/change
  useEffect(() => {
    if (compassCenter && !currentConfig?.positionX && !currentConfig?.positionY) {
      setPngPosition({ x: compassCenter.x, y: compassCenter.y });
      setLayerState(prev => ({
        ...prev,
        positionX: compassCenter.x,
        positionY: compassCenter.y
      }));
    }
  }, [compassCenter]);
useEffect(() => {
  if (layerId === 'compass-45-chart' || layerId === 'compass-45-table') {

    if (layerId === 'compass-45-chart') {
      const groupedData = calculateGroupedBarData45();
      setLayerState(prev => ({
        ...prev,
        groupedData45: groupedData,
        showTable: false,
        showBarChart: true
      }));
    } else {
      const groupedTableData = calculateGroupedTableData45();
      setLayerState(prev => ({
        ...prev,
        groupedTableData45: groupedTableData,
        showTable: true,
        showBarChart: false
      }));
    }
  }
}, [layerId, isPolygonComplete, compassCenter, isCalibrationSet, pixelsPerUnit]);
useEffect(() => {
  if (layerId === 'compass-45-chart' || layerId === 'compass-45-table') {

    if (layerId === 'compass-45-chart') {
      const groupedData = calculateGroupedBarData45();
      setLayerState(prev => ({
        ...prev,
        groupedData45: groupedData,
        groupedData: undefined,
        groupedTableData: undefined,
        showTable: false,
        showBarChart: true
      }));
    } else {
      const groupedTableData = calculateGroupedTableData45();
      setLayerState(prev => ({
        ...prev,
        groupedTableData45: groupedTableData,
        groupedData: undefined,
        groupedTableData: undefined,
        showTable: true,
        showBarChart: false
      }));
    }
  }
}, [layerId, isPolygonComplete, compassCenter, isCalibrationSet, pixelsPerUnit]);
  useEffect(() => {
    if (layerState.floatMode && transformerRef.current && imageGroupRef.current) {
      transformerRef.current.nodes([imageGroupRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [layerState.floatMode]);

  // Get uploaded image dimensions
  useEffect(() => {
    if (uploadedImage) {
      const img = new window.Image();
      img.onload = () => {
        setUploadedImageDimensions({ 
          width: img.width, 
          height: img.height 
        });
      };
      img.src = uploadedImage;
    }
  }, [uploadedImage]);

const getBarColor = (direction) => {
  if (layerId === 'Bar-chart-8') {
    // ?o. NORMALIZE direction name (uppercase + trim spaces)
    const normalizedDir = direction.toUpperCase().trim();
    
    // ?o. COLOR MAPPING for 8 Directions
    const colorMap = {
      'NORTH': '#4da9c8',           // Blue
      'NORTH-EAST': '#4da9c8',      // Blue
      'NORTHEAST': '#4da9c8',       // Blue (alternative spelling)
      
      'EAST': '#28a745',            // Green
      
      'SOUTH-EAST': '#DC3545',      // Red
      'SOUTHEAST': '#DC3545',       // Red (alternative spelling)
      'SOUTH': '#DC3545',           // Red
      
      'SOUTH-WEST': '#FFD700',      // Yellow
      'SOUTHWEST': '#FFD700',       // Yellow (alternative spelling)
      
      'WEST': '#6c757d',            // Gray
      'NORTH-WEST': '#6c757d',      // Gray
      'NORTHWEST': '#6c757d'        // Gray (alternative spelling)
    };
    
    return colorMap[normalizedDir] || '#1f77d4'; // Default blue if not found
  }
  
  if (layerId === 'Bar-chart-16') {
    const normalizedDir = direction.toUpperCase().trim();
    
    if (['N', 'NNE', 'NNW', 'NE'].includes(normalizedDir)) return '#4da9c8';
    if (['ENE', 'E', 'ESE'].includes(normalizedDir)) return '#28a745';
    if (['SE', 'SSE', 'S'].includes(normalizedDir)) return '#DC3545';
    if (['SSW', 'SW'].includes(normalizedDir)) return '#FFD700';
    if (['WSW', 'W', 'NW'].includes(normalizedDir)) return '#6c757d';
    
    return '#6c757d';
  }

  if (layerId === 'Bar-chart-32') {
    // ?o. COLOR MAPPING for 32 Directions (matching image: N=LightBlue, E=Green, S=Red, SW=Yellow, W=Gray)
    const colorMap32 = {
      // North zone ??" Light Blue
      'N5': '#4da9c8', 'N6': '#4da9c8', 'N7': '#4da9c8', 'N8': '#4da9c8',
      'N1': '#4da9c8', 'N2': '#4da9c8', 'N3': '#4da9c8', 'N4': '#4da9c8',
      
      // East zone ??" Green
      'E1': '#28a745', 'E2': '#28a745', 'E3': '#28a745', 'E4': '#28a745',
      'E5': '#28a745', 'E6': '#28a745', 'E7': '#28a745', 'E8': '#28a745',
      
      // South zone ??" Red
      'S1': '#DC3545', 'S2': '#DC3545', 'S3': '#DC3545', 'S4': '#DC3545',
      'S5': '#DC3545', 'S6': '#DC3545',
      
      // South-West zone ??" Yellow
      'S7': '#FFD700', 'S8': '#FFD700',
      
      // West zone ??" Gray
      'W1': '#6c757d', 'W2': '#6c757d', 'W3': '#6c757d', 'W4': '#6c757d',
      'W5': '#6c757d', 'W6': '#6c757d', 'W7': '#6c757d', 'W8': '#6c757d',
    };
    
    return colorMap32[direction] || '#6c757d'; // Default gray if not found
  }
  
  return '#6c757d'; // Default color for other charts
};

  // Handle canvas click
  // ── 45-block compass hover: which box (1-45) is under the cursor, and its Devata name ──
  // Mirrors how "compass-45" (Vedic: rings at 1/3, 1/2, 2/3, lines shifted 5.625°) and "compass-45-2" (Modern: rings at
  // 1/4, 1/2, 3/4, lines shifted 11.25°) draw their boxes. Sector i = the wedge between all32Directions[i] and [i+1].
  const getDevta45BlockAt = (px, py) => {
    if (!compassCenter || !isPolygonComplete || !polygonPoints || polygonPoints.length < 3 || !all32Directions.length) return null;
    const isVedic = layerId === 'compass-45';
    const norm = (ang) => ((ang % 360) + 360) % 360;
    const rot = (layerState.compassRotation || 0) + (isVedic ? 5.625 : 11.25);
    const dx = px - compassCenter.x, dy = py - compassCenter.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.5) return 1;
    const ux = dx / dist, uy = dy / dist;
    // Distance from the centre to the plot boundary along this direction (outermost crossing).
    let edge = 0;
    for (let i = 0; i < polygonPoints.length; i++) {
      const p1 = polygonPoints[i], p2 = polygonPoints[(i + 1) % polygonPoints.length];
      const ex = p2.x - p1.x, ey = p2.y - p1.y;
      const den = ux * ey - uy * ex;
      if (Math.abs(den) < 1e-9) continue;
      const wx = p1.x - compassCenter.x, wy = p1.y - compassCenter.y;
      const t = (wx * ey - wy * ex) / den;
      const s = (wx * uy - wy * ux) / den;
      if (t > 0 && s >= 0 && s <= 1 && t > edge) edge = t;
    }
    if (!edge || dist > edge) return null; // outside the plot
    const f = dist / edge;
    const ring = isVedic ? [1 / 3, 0.5, 2 / 3] : [0.25, 0.5, 0.75];
    if (f < ring[0]) return 1;
    const theta = norm(Math.atan2(dy, dx) * 180 / Math.PI);
    const n = all32Directions.length;
    let sector = -1;
    for (let i = 0; i < n; i++) {
      const a = norm(all32Directions[i].angle + rot);
      const b = norm(all32Directions[(i + 1) % n].angle + rot);
      if (norm(theta - a) < norm(b - a)) { sector = i; break; }
    }
    if (sector < 0) return null;
    const quadrantBlock = sector >= 27 || sector <= 2 ? 2 : sector <= 10 ? 3 : sector <= 18 ? 4 : 5;
    const ring4Block = 14 + ((sector - 3 + 32) % 32);
    if (f < ring[1]) return quadrantBlock;
    if (f < ring[2]) {
      const ring3Block = { 1: 6, 2: 6, 3: 7, 4: 7, 9: 8, 10: 8, 11: 9, 12: 9, 17: 10, 18: 10, 19: 11, 20: 11, 25: 12, 26: 12, 27: 13, 28: 13 }[sector];
      if (ring3Block) return ring3Block;
      // Cardinal wedges have no ring-3 box: Vedic's quadrant box runs on to 2/3, Modern's outer box starts at 1/2.
      return isVedic ? quadrantBlock : ring4Block;
    }
    return ring4Block;
  };
  const hideDevta45Tip = () => {
    const g = devta45TipGroupRef.current;
    if (g && g.visible()) { g.visible(false); g.getLayer() && g.getLayer().batchDraw(); }
  };
  const handleDevta45Hover = (e) => {
    if (layerId !== 'compass-45' && layerId !== 'compass-45-2') return;
    const stage = e.target.getStage();
    const pos = stage && stage.getPointerPosition();
    const g = devta45TipGroupRef.current, txt = devta45TipTextRef.current, tag = devta45TipTagRef.current;
    if (!pos || !g || !txt || !tag) return;
    const block = getDevta45BlockAt(pos.x, pos.y);
    const name = block ? (layerId === 'compass-45' ? Vedic_Devata_NAMES : Devata_NAMES).find(d => d.blockNo === block)?.name : null;
    if (!name) { hideDevta45Tip(); return; }
    const info = DEVATA_DESCRIPTIONS[name];
    let desc = info ? info.description : '';
    if (desc.length > 220) desc = desc.slice(0, 220).replace(/\s+\S*$/, '') + '...';
    const title = name.toLowerCase().replace(/(^|[-\s])([a-z])/g, (m, a, b) => a + b.toUpperCase()) + (info ? ` (${info.tagline})` : '');
    txt.width('auto');
    txt.text(desc ? `${title}: ${desc}` : title);
    if (txt.width() > 300) txt.width(300);
    tag.width(txt.width()); tag.height(txt.height());
    g.x(Math.max(2, Math.min(pos.x + 14, A4_WIDTH - txt.width() - 2)));
    g.y(Math.max(2, pos.y - txt.height() - 10));
    g.visible(true);
    g.getLayer() && g.getLayer().batchDraw();
  };

  const handleCanvasClick = (e) => {
    // Measurement mode
    if (measurementMode) {
      handleMeasurementClick(e);
      return;
    }

    // Add text mode
    if (!isAddingText) return;
    
    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    
    const newText = {
      id: nextTextId,
      x: pointerPosition.x,
      y: pointerPosition.y,
      text: 'Enter text here...',
      fontSize: 14,
      fontFamily: 'Arial',
      fill: '#000000',
      draggable: true,
      fontStyle: 'normal',
      textDecoration: 'none',
      align: 'left'
    };
    
    setTexts([...texts, newText]);
    setEditingTextId(nextTextId); // Immediately start editing
    setEditingTextValue(newText.text); // Set initial value
    setNextTextId(nextTextId + 1);
    setIsAddingText(false);
  };

  const handleMeasurementClick = (e) => {
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    const newPoint = { x: pointerPos.x, y: pointerPos.y, id: Date.now() };

    if (measurementPoints.length === 0) {
      setMeasurementPoints([newPoint]);
    } else if (measurementPoints.length === 1) {
      const firstPoint = measurementPoints[0];
      const distance = Math.sqrt(
        Math.pow(pointerPos.x - firstPoint.x, 2) + 
        Math.pow(pointerPos.y - firstPoint.y, 2)
      );

      if (!isCalibrationSet || !pixelsPerUnit) {
        alert('Measurement calibration not set in main canvas!');
        setMeasurementPoints([]);
        return;
      }

      const realDistance = distance / pixelsPerUnit;
      
      const newMeasurement = {
        id: Date.now(),
        points: [firstPoint, newPoint],
        pixelDistance: distance,
        realDistance: realDistance,
        unit: currentUnit,
        isCalibration: false
      };
      
      setLayerMeasurements(prev => [...prev, newMeasurement]);
      setMeasurementPoints([]);
      // Was missing — measurementMode stayed true after finishing a measurement,
      // so the very next click on the canvas silently started ANOTHER measurement
      // instead of requiring the user to click "Get Measurement" again.
      setMeasurementMode(false);
    }
  };

  const startGetMeasurement = () => {
    if (!isCalibrationSet || !pixelsPerUnit) {
      alert("Please set measurement calibration in main canvas first!");
      return;
    }
    
    setMeasurementMode(true);
    setMeasurementPoints([]);
    setShowMeasurementDropdown(false);
  };


// ?o. NEW: Handle Measurement Click for Removal
const handleMeasurementClickForRemoval = (measurementId) => {
  if (!removeMeasurementMode) return;
  
  setLayerMeasurements(prev => prev.filter(m => m.id !== measurementId));
  setRemoveMeasurementMode(false);
  alert('Measurement removed successfully');
};

// ?o. NEW: Reset All Measurements
const handleResetMeasurements = () => {
  if (layerMeasurements.length === 0) {
    alert('No measurements to reset');
    return;
  }
  
  if (window.confirm('Are you sure you want to reset all measurements?')) {
    setLayerMeasurements([]);
    setMeasurementPoints([]);
    setMeasurementMode(false);
    setRemoveMeasurementMode(false);
    alert('All measurements have been reset');
  }
};

// ?o. NEW: Download Current Layer as Image
const handleDownloadLayer = async () => {
  try {
    // Bar-chart layers render their table/chart as plain HTML (tableContentRef /
    // contentRef), not as Konva shapes — the Konva-stage snapshot below would
    // capture a blank/incomplete image for them. Use the same html2canvas
    // capture that Save already uses for these instead.
    if (isBarChart) {
      const captures = await captureBarChart();
      if (!captures.length) {
        alert('Nothing to download yet — enable Table and/or Chart first.');
        return;
      }
      captures.forEach(({ type, dataURL }) => {
        const link = document.createElement('a');
        link.download = `${layerId}-${type}-${Date.now()}.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
      alert('Layer downloaded successfully!');
      return;
    }

    if (!editorStageRef.current) return;
    const uri = editorStageRef.current.toDataURL({
      mimeType: 'image/png',
      quality: 1,
      pixelRatio: 2
    });

    const link = document.createElement('a');
    link.download = `${layerId}-${Date.now()}.png`;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    alert('Layer downloaded successfully!');
  } catch (error) {
    console.error('Download error:', error);
    alert('Failed to download layer');
  }
};
  const cancelMeasurement = () => {
    setMeasurementMode(false);
    setMeasurementPoints([]);
  };

  // Text handlers
  const handleTextClick = (textId) => {
    const text = texts.find(t => t.id === textId);
    if (text) {
      setEditingTextId(textId);
      setEditingTextValue(text.text);
    }
  };

  const handleTextChange = (e) => {
    setEditingTextValue(e.target.value);
  };

  const handleTextEditEnd = () => {
    if (editingTextId !== null) {
      setTexts(texts.map(text => 
        text.id === editingTextId 
          ? { ...text, text: editingTextValue }
          : text
      ));
      setEditingTextId(null);
      setEditingTextValue('');
    }
  };

  // Text formatting handlers
  const handleTextFormat = (textId, property, value) => {
    setTexts(texts.map(text => 
      text.id === textId 
        ? { ...text, [property]: value }
        : text
    ));
  };

  const toggleBold = (textId) => {
    const text = texts.find(t => t.id === textId);
    if (text) {
      const newStyle = text.fontStyle === 'bold' ? 'normal' : 'bold';
      handleTextFormat(textId, 'fontStyle', newStyle);
    }
  };

  const toggleItalic = (textId) => {
    const text = texts.find(t => t.id === textId);
    if (text) {
      const newStyle = text.fontStyle === 'italic' ? 'normal' : 'italic';
      handleTextFormat(textId, 'fontStyle', newStyle);
    }
  };

  const handleTextDragEnd = (textId, e) => {
    setTexts(texts.map(text => 
      text.id === textId 
        ? { ...text, x: e.target.x(), y: e.target.y() }
        : text
    ));
  };

  const handleTextRightClick = (e, textId) => {
    e.evt.preventDefault();
    if (window.confirm('Delete this text?')) {
      setTexts(texts.filter(text => text.id !== textId));
    }
  };

  const handleAlignToCenter = () => {
    if (compassCenter) {
      const cx = compassCenter.x;
      const cy = compassCenter.y;
      setPngPosition({ x: cx, y: cy });
      setLayerState(prev => ({
        ...prev,
        positionX: cx,
        positionY: cy
      }));
      if (imageGroupRef.current) {
        imageGroupRef.current.position({ x: cx, y: cy });
        imageGroupRef.current.getLayer()?.batchDraw();
      }
    }
  };

  const handlePNGDragEnd = (e) => {
    
    if (layerState.floatMode) {
      const group = e.target;
      const newX = group.x();
      const newY = group.y();
      
      
      setPngPosition({ x: newX, y: newY });
      setLayerState(prev => ({
        ...prev,
        positionX: newX,
        positionY: newY
      }));
    }
  };
const captureLayerImage = useCallback(async () => {
  try {
    // ?o. COVER PAGE SPECIAL HANDLING - Capture both pages
    if (layerId === 'cover-page' || layerId === 'cover-index') {
      const images = [];
      
      // Capture Modern Vastu Page (Page 1) — the Cover Front page itself;
      // Cover Index has no front page of its own, only the index pages below.
      if (layerId === 'cover-page' && editorStageRef.current) {
        const stage = editorStageRef.current;
        const textNodes = stage.find('Text');
        const originalVisibility = [];
        
        textNodes.forEach((node, index) => {
          originalVisibility[index] = node.visible();
          const text = node.text();
          if (text === 'Click to edit' || text.includes('Click to edit')) {
            node.visible(false);
          }
        });
        
        stage.batchDraw();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const modernPageDataURL = stage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        
        textNodes.forEach((node, index) => {
          node.visible(originalVisibility[index]);
        });
        
        stage.batchDraw();
        
        images.push({ 
          type: 'modern', 
          dataURL: modernPageDataURL, 
          group: 'Modern Vastu Page' 
        });
        
      }
      
      // Capture Vedic Vastu Page (Page 2) ??" via VedicStageRef
      if (VedicStageRef.current) {
        const VedicStage = VedicStageRef.current;
        const VedicPageDataURL = VedicStage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        images.push({
          type: 'Vedic',
          dataURL: VedicPageDataURL,
          group: 'Vedic Vastu Page'
        });
      } else {
      }

      // Capture Medical Vastu Page (Page 3) ??" via MedicalStageRef
      if (MedicalStageRef.current) {
        const MedicalStage = MedicalStageRef.current;
        const MedicalPageDataURL = MedicalStage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        images.push({
          type: 'medical',
          dataURL: MedicalPageDataURL,
          group: 'Medical Vastu Page'
        });
      } else {
      }

      // Capture Others Page (Page 4) ??" via OthersStageRef
      if (OthersStageRef.current) {
        const OthersStage = OthersStageRef.current;
        const OthersPageDataURL = OthersStage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        images.push({
          type: 'others',
          dataURL: OthersPageDataURL,
          group: 'Others Page'
        });
      } else {
      }

      // Capture Planning Guide Page (Page 6) ??" via HousePlanningStageRef
      if (HousePlanningStageRef.current) {
        const HousePlanningStage = HousePlanningStageRef.current;
        const HousePlanningPageDataURL = HousePlanningStage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        images.push({
          type: 'housePlanning',
          dataURL: HousePlanningPageDataURL,
          group: 'Planning Guide Page'
        });
      } else {
      }


      if (images.length > 0) {
        return images;
      }
      return images[0]?.dataURL || null;
    }

    // ?o. FLOOR PLAN (basic capture)
    if (layerId === 'floor-plan' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. GRID LAYOUT
    if (layerId === 'grid-layout' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. CENTER ANALYSIS
    if (layerId === 'center-analysis' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. ALL PNG/CHAKRA LAYERS WITH FLOAT MODE FIX
    if ([
      'mahavastu-chakra',
      'Vedic-Mahavastu-Chakra',
      'shakti-chakra',
      'modern-Devata-45',
      'vastu-numbers-64',
      'vastu-numbers-81',
      'vastu-numbers-81-numbers',
      'vastu-numbers-81-numbers-Vedic',
      'marmsthan-analysis',
      'grid-3x3'
    ].includes(layerId) && editorStageRef.current) {
      const stage = editorStageRef.current;
      const originalFloatMode = layerState.floatMode;
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: false }));
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const dataURL = stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: true }));
        await new Promise(resolve => setTimeout(resolve, 50));
        if (transformerRef.current && imageGroupRef.current) {
          transformerRef.current.nodes([imageGroupRef.current]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
      }
      
      return dataURL;
    }

    // ?o. FLOOR PLAN WITH VPM
    if (layerId === 'floor-plan-vpm' && editorStageRef.current) {
      const stage = editorStageRef.current;
      const originalFloatMode = layerState.floatMode;
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: false }));
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const dataURL = stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: true }));
        await new Promise(resolve => setTimeout(resolve, 50));
        if (transformerRef.current && imageGroupRef.current) {
          transformerRef.current.nodes([imageGroupRef.current]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
      }
      
      return dataURL;
    }

    // ?o. 8 ZONES ANALYSIS
    if (layerId === 'compass-8' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }
    if (layerId === 'MANDAL' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }
    if (layerId === 'RATNA-STHAPNA' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. 8 ZONES BAR CHART
    if (layerId === 'Bar-chart-8') {
      return captureBarChart();
    }

    // ?o. 16 ZONES ANALYSIS
    if (layerId === 'compass-16' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. 16 ZONES BAR CHART
    if (layerId === 'Bar-chart-16') {
      return captureBarChart();
    }

    // ?o. 24 ZONES ANALYSIS
    if (layerId === 'compass-24' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }
    if (layerId === 'surya-chandra-bhag' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }
    if (layerId === '4-state-image' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }
    
    // ?o. MEDICAL VASTU LAYERS ??" editorStageRef se capture
    const MEDICAL_VASTU_IDS = ['tri-dosh-Vedic', 'tri-dosh-modern', 'tri-guna-Vedic', 'tri-guna-modern', 'body-part-division'];
    if (MEDICAL_VASTU_IDS.includes(layerId)) {
      if (editorStageRef.current) {
        const stage = editorStageRef.current;
        const dataURL = stage.toDataURL({
          pixelRatio: 2,
          quality: 1,
          mimeType: 'image/png',
          width: A4_WIDTH,
          height: A4_HEIGHT
        });
        return dataURL;
      } else {
        // ?o. FALLBACK: editorStageRef null hai ??" canvas element se capture karo
        const konvaCanvas = document.querySelector('canvas');
        if (konvaCanvas) {
          const dataURL = konvaCanvas.toDataURL('image/png');
          return dataURL;
        }
        console.error(`?O No canvas found for "${layerId}"`);
        return null;
      }
    }
    // ?o. 32 MAHAVASTU ZONES
    if (layerId === 'compass-32' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. 32 MAHAVASTU BAR CHART
    if (layerId === 'Bar-chart-32') {
      return captureBarChart();
    }

    // ?o. MODERN 45 Devata (WITH NAME)
    if (layerId === 'compass-45-2' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. COMPASS-45 TABLE
    if (layerId === 'compass-45-table') {
      return captureBarChart();
    }

    // ?o. COMPASS-45 CHART
    if (layerId === 'compass-45-chart' && contentRef.current) {
      // ?o. FIX: width/height remove karo ??" element ka natural size use hoga, scale 3 for high quality
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false,
        onclone: captureSafeFont
      });

      return [{ type: 'full', dataURL: canvas.toDataURL('image/png'), group: 'Complete View' }];
    }

    // ?o. COMPASS-45-2 TABLE
    if (layerId === 'compass-45-2-table') {
      return captureBarChart();
    }

    // ?o. MODERN 45 Devata BAR CHART
    if (layerId === 'compass-45-2-chart' && contentRef.current) {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        width: A4_WIDTH,
        height: A4_HEIGHT,
        logging: false,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false,
        onclone: captureSafeFont
      });
      
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = A4_WIDTH;
      finalCanvas.height = A4_HEIGHT;
      const ctx = finalCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, A4_WIDTH, A4_HEIGHT);
      ctx.drawImage(canvas, 0, 0, A4_WIDTH, A4_HEIGHT);
      
      return [{ type: 'full', dataURL: finalCanvas.toDataURL('image/png'), group: 'Complete View' }];
    }

    // ?o. COLOURED ZONE
    if (layerId === 'coloured-zone' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. MODERN 5 ELEMENTAL ZONE
    if (layerId === 'elemental-5-zone' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. MODERN 5 ELEMENTAL PIE CHART
    if (layerId === 'elemental-5-chart' && editorStageRef.current) {
      const stage = editorStageRef.current;
      // ?o. FIX: width/height remove ??" full stage capture karo, crop nahi
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png'
      });
    }

    // ?o. ADVANCE MARMSTHAN ANALYSIS
    if (layerId === 'marmsthan-advance' && editorStageRef.current) {
      const stage = editorStageRef.current;
      return stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
    }

    // ?o. PNG IMAGES (with float mode handling)
    if ((layerId === 'png-image-1' || layerId === 'png-image-2') && editorStageRef.current) {
      const stage = editorStageRef.current;
      const originalFloatMode = layerState.floatMode;
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: false }));
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const dataURL = stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: true }));
        await new Promise(resolve => setTimeout(resolve, 50));
        if (transformerRef.current && imageGroupRef.current) {
          transformerRef.current.nodes([imageGroupRef.current]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
      }
      
      return dataURL;
    }

    // ?o. Vedic LAYERS (with float mode handling)
    if (editorStageRef.current && !['cover-page'].includes(layerId)) {
      const stage = editorStageRef.current;
      const originalFloatMode = layerState.floatMode;
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: false }));
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const dataURL = stage.toDataURL({
        pixelRatio: 2,
        quality: 1,
        mimeType: 'image/png',
        width: A4_WIDTH,
        height: A4_HEIGHT
      });
      
      if (originalFloatMode) {
        setLayerState(prev => ({ ...prev, floatMode: true }));
        await new Promise(resolve => setTimeout(resolve, 50));
        if (transformerRef.current && imageGroupRef.current) {
          transformerRef.current.nodes([imageGroupRef.current]);
          const layer = transformerRef.current.getLayer();
          if (layer) layer.batchDraw();
        }
      }
      
      return dataURL;
    }

    return null;
  } catch (error) {
    console.error('Error capturing layer image:', error);
    return null;
  }
}, [layerId, layerType, layerState.showTable, layerState.showBarChart, layerState.floatMode, A4_WIDTH, A4_HEIGHT]);

const handleSaveLayer = async () => {
  try {
    const imageData = await captureLayerImage();
    
    if (!imageData) {
      console.error(`?O captureLayerImage returned null/undefined for "${layerId}"`);
      alert('Error capturing layer image. Please try again.');
      if (autoSave) onCancel();
      return;
    }

    // Filter client/consultant info
    const saveClientInfo = {};
    const saveConsultantInfo = {};
    
    Object.keys(clientInfo).forEach(key => {
      const value = clientInfo[key];
      if (value && value.trim() !== '' && value !== 'Click to edit') {
        saveClientInfo[key] = value;
      } else {
        saveClientInfo[key] = '';
      }
    });
    
    Object.keys(consultantInfo).forEach(key => {
      const value = consultantInfo[key];
      if (value && value.trim() !== '' && value !== 'Click to edit') {
        saveConsultantInfo[key] = value;
      } else {
        saveConsultantInfo[key] = '';
      }
    });

    // ?o. Handle cover page with multiple images
    if (layerId === 'cover-page' || layerId === 'cover-index') {
      
      // If imageData is already an array (from captureLayerImage)
      if (Array.isArray(imageData)) {
        onSave({
          imageData: imageData[0]?.dataURL,
          images: imageData,
          config: {
            ...layerState,
            texts,
            clientInfo: saveClientInfo,
            consultantInfo: saveConsultantInfo,
            layerMeasurements,
            layerIsCalibrationSet,
            layerPixelsPerUnit,
            positionX: pngPosition.x,
            positionY: pngPosition.y
          },
          timestamp: new Date().toISOString()
        });
      } 
      // If it's a single image (fallback)
      else if (imageData) {
        onSave({
          imageData: imageData,
          config: {
            ...layerState,
            texts,
            clientInfo: saveClientInfo,
            consultantInfo: saveConsultantInfo,
            layerMeasurements,
            layerIsCalibrationSet,
            layerPixelsPerUnit,
            positionX: pngPosition.x,
            positionY: pngPosition.y
          },
          timestamp: new Date().toISOString()
        });
      }
      
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
      return;
    }

    // ?o. Handle compass-45-chart
    if (layerId === 'compass-45-chart' && Array.isArray(imageData)) {

      onSave({
        imageData: imageData[0]?.dataURL,
        images: imageData,
        config: {
          ...layerState,
          texts,
          clientInfo: saveClientInfo,
          consultantInfo: saveConsultantInfo,
          layerMeasurements,
          layerIsCalibrationSet,
          layerPixelsPerUnit,
          positionX: pngPosition.x,
          positionY: pngPosition.y,
          groupedData45: layerState.groupedData45
        },
        timestamp: new Date().toISOString()
      });
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
      return;
    }
    
    // ?o. Handle compass-45-2-chart
    if (layerId === 'compass-45-2-chart' && Array.isArray(imageData)) {
      
      onSave({
        imageData: imageData[0]?.dataURL,
        images: imageData,
        config: { 
          ...layerState, 
          texts,
          clientInfo: saveClientInfo,
          consultantInfo: saveConsultantInfo,
          layerMeasurements,
          layerIsCalibrationSet,
          layerPixelsPerUnit,
          positionX: pngPosition.x,
          positionY: pngPosition.y,
          groupedData: layerState.groupedData
        },
        timestamp: new Date().toISOString()
      });
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
      return;
    } 
    // ?o. Handle Bar chart with multiple captures
    else if (layerType === 'Bar-chart' && Array.isArray(imageData)) {
      onSave({
        imageData: imageData,
        images: imageData,
        config: { 
          ...layerState, 
          texts,
          clientInfo: saveClientInfo,
          consultantInfo: saveConsultantInfo,
          layerMeasurements,
          layerIsCalibrationSet,
          layerPixelsPerUnit,
          positionX: pngPosition.x,
          positionY: pngPosition.y
        },
        timestamp: new Date().toISOString()
      });
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
      return;
    }
    // ?o. Normal single image save
    else {
      onSave({
        imageData: imageData,
        config: { 
          ...layerState, 
          texts,
          clientInfo: saveClientInfo,
          consultantInfo: saveConsultantInfo,
          layerMeasurements,
          layerIsCalibrationSet,
          layerPixelsPerUnit,
          positionX: pngPosition.x,
          positionY: pngPosition.y
        },
        timestamp: new Date().toISOString()
      });
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
      return;
    }
    
  } catch (error) {
    console.error('Error saving layer:', error);
    alert('Error saving layer. Please try again.');
    if (autoSave) onCancel();
  }
};
  // Auto-capture: once the hidden editor has drawn itself, run the very same Save the button runs (it closes the editor).
  const saveLayerRef = useRef(null);
  saveLayerRef.current = handleSaveLayer;
  const autoSavedRef = useRef(false);
  useEffect(() => {
    if (!autoSave || autoSavedRef.current) return undefined;
    const t = setTimeout(() => {
      autoSavedRef.current = true;
      onRequestReportsAfterSave && onRequestReportsAfterSave();
      saveLayerRef.current && saveLayerRef.current();
    }, 2500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSave]);

  const toggleFloatMode = () => {
    setLayerState(prev => ({
      ...prev,
      floatMode: !prev.floatMode
    }));
  };
  
  const handleCancel = () => {
    onCancel();
  };

  // Cancel asks "leave or save?" first (see ConfirmLeaveDialog).
  const leaveConfirm = useLeaveConfirm({ onSave: () => handleSaveLayer(), onLeave: () => handleCancel() });


  const captureBarChart = async () => {
  const captures = [];
  
  // ?o. FIX: width/height remove karo ??" element ka natural size capture hoga, crop nahi
  if (layerState.showTable && tableContentRef.current) {
    const canvas = await html2canvas(tableContentRef.current, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: false,
      onclone: captureSafeFont
    });
    captures.push({ type: 'table', dataURL: canvas.toDataURL('image/png') });
  }
  
  if (layerState.showBarChart && contentRef.current) {
    const canvas = await html2canvas(contentRef.current, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: false,
      onclone: captureSafeFont
    });
    captures.push({ type: 'chart', dataURL: canvas.toDataURL('image/png') });
  }
  
  return captures;
};
// DevataBlocksTable extracted to ./Configure/components/DevataBlocksTable
// RatnaSthapnaTable extracted to ./Configure/components/RatnaSthapnaTable
// Bar chart data - with safe fallback
const data = Array.isArray(layerState.data) ? layerState.data : [];
const dataCount = data.length;
const areaValues = data.length > 0 ? data.map(item => parseFloat(item.area) || 0) : [];
const minValue = areaValues.length > 0 ? Math.min(...areaValues) : 0;
const maxValue = areaValues.length > 0 ? Math.max(...areaValues) : 0;
const totalArea = areaValues.reduce((sum, val) => sum + val, 0);
const avgValue = dataCount > 0 ? (totalArea / dataCount).toFixed(1) : 0;
const maxLine = areaValues.length > 0 ? (maxValue + parseFloat(avgValue)) / 2 : 0;
const minLine = areaValues.length > 0 ? (minValue + parseFloat(avgValue)) / 2 : 0;

// Auto-convert large mm² values to cm² for display only (1 cm² = 100 mm²)
const _needsConvert = currentUnit === 'mm' && maxValue > 9999;
const displayScale = _needsConvert ? 100 : 1;
const displayUnit = _needsConvert ? 'cm' : currentUnit;
const fmt = (v) => {
  const n = (typeof v === 'string' ? parseFloat(v) : v) / displayScale;
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
};

// Calculate maxDisplayValue safely
const maxDisplayValue = areaValues.length > 0 ? Math.max(...areaValues, maxLine) : 1;

// Nice round Y-axis scale based on data
const _niceRawStep = maxDisplayValue / 5;
const _niceMag = Math.pow(10, Math.floor(Math.log10(_niceRawStep || 1)));
const _niceNorm = _niceRawStep / _niceMag;
const _niceStep = _niceNorm <= 1 ? _niceMag : _niceNorm <= 2 ? 2 * _niceMag : _niceNorm <= 5 ? 5 * _niceMag : 10 * _niceMag;
// +1 extra step of headroom above the tallest bar so it never touches/crowds the
// chart's top border (a bare ceil() can land within ~2% of the max value).
const niceMaxDisplay = Math.ceil((maxDisplayValue || _niceStep) / _niceStep) * _niceStep + _niceStep;
const niceDisplayTicks = Array.from({ length: Math.round(niceMaxDisplay / _niceStep) + 1 }, (_, i) => i * _niceStep);

  const tableWidth = (layerId === 'Bar-chart-45' || layerId === 'Bar-chart-45-2' || layerId === 'compass-45-2-table' || layerId === 'compass-45-2-chart') ? 900 : 810;
  const chartWidth = (layerId === 'Bar-chart-45' || layerId === 'Bar-chart-45-2' || layerId === 'compass-45-2-table' || layerId === 'compass-45-2-chart') ? 700 : 600;
  const chartHeight = 350;
  // ?o. Bar-chart-16 ke liye special width calculation
// ?o. Different width for different chart types
const BarMaxWidth = (() => {
  if (layerId === 'Bar-chart-16') {
    return Math.max(6, Math.floor((chartWidth - 100) / dataCount * 0.85));
  }
  if (layerId === 'Bar-chart-32') {
    return Math.max(6, Math.floor((chartWidth - 120) / dataCount * 1.125));
  }
  if (layerId === 'Bar-chart-45' || layerId === 'Bar-chart-45-2') {
    return Math.max(4, Math.floor((chartWidth - 80) / dataCount * 0.90));
  }
  return dataCount > 0 ? Math.max(8, Math.floor((chartWidth - 40) / dataCount * 0.97)) : 8;
})();
  const tableCellWidth = dataCount > 0 ? Math.max(8, Math.floor((tableWidth - 140) / dataCount)) : 8;

  const isBarChart = layerType === 'Bar-chart';
  const isGrid = layerId === 'grid-layout';
  // const isCompass = layerId.includes('compass') && !layerId.includes('compass-45-2-table') && !layerId.includes('compass-45-2-chart') ||
  //                layerId === 'coloured-zone' || layerId === 'elemental-5-zone' || layerId === 'elemental-5-chart' || layerId  === 'marmsthan-advance'  || layerId  === 'floor-plan';
  const isCompass = (layerId.includes('compass') &&
  !layerId.startsWith('house-') && // ?o. FIX: "house-space-analysis-compass" (Planning Guide) was matching this
                                    // substring check by accident, rendering a 2nd, bare compass-editor Stage
                                    // alongside the correct Planning Guide image block — 2 pages in edit mode.
  !layerId.includes('compass-45-2-table') &&
  !layerId.includes('compass-45-2-chart') &&
  !layerId.includes('compass-45-table') &&
  !layerId.includes('compass-45-chart')) ||
  layerId === 'coloured-zone' || 
  layerId === 'elemental-5-zone' || 
  layerId === 'elemental-5-chart' || 
  layerId === 'marmsthan-advance' || 
  layerId === 'floor-plan' ||
  // ?o. FIX: center-analysis removed from isCompass ??" it has its own dedicated render block
  layerId === 'surya-chandra-bhag' || // ?o. FIX: surya-chandra-bhag ko compass edit mode mein include kiya
  layerId === '4-state-image' || // ?o. FIX: 4-state-image ko compass edit mode mein include kiya
  layerId === 'tri-dosh-Vedic' || // ?o. FIX: tri-dosh-Vedic ko compass edit mode mein include kiya
  layerId === 'tri-guna-modern' || // ?o. FIX: tri-dosh-Vedic ko compass edit mode mein include kiya
  layerId === 'tri-guna-Vedic' || // ?o. FIX: tri-dosh-Vedic ko compass edit mode mein include kiya
  layerId === 'tri-dosh-modern' || // ?o. FIX: tri-dosh-Vedic ko compass edit mode mein include kiya
  layerId === 'body-part-division' || // ?o. FIX: body-part-division ko compass edit mode mein include kiya
  layerId === 'MANDAL' || // ?o. FIX: MANDAL ko compass edit mode mein include kiya
  layerId === 'RATNA-STHAPNA' || // ?o. FIX: MANDAL ko compass edit mode mein include kiya
  VEDIC_GENERATIVE_IDS.includes(layerId); // Vedic Generative Zones: drawn live against the plot polygon, same as compass layers
  const isPNG = layerType === 'png';
// Transformer attachment effect for PNG layers
useEffect(() => {
  if (layerType === 'png' && layerState.floatMode && transformerRef.current && imageGroupRef.current) {
    // Attach transformer to the image group
    transformerRef.current.nodes([imageGroupRef.current]);
    transformerRef.current.getLayer()?.batchDraw();
  } else if (layerType === 'png' && !layerState.floatMode && transformerRef.current) {
    // Detach transformer when float mode is OFF
    transformerRef.current.nodes([]);
    transformerRef.current.getLayer()?.batchDraw();
  }
}, [layerType, layerState.floatMode]);
  // Table rendering

  const calculateGroupedTableData = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return {
      group4: [],
      group8: [],
      group32: []
    };
  }
  
  const all45Data = calculateBarChart452Data();
  
  // Group 1: First 4 Bars (Blocks 2-5)
  const group4 = all45Data.slice(1, 5).map(item => ({
    ...item,
    group: 'Group 1 (Blocks 2-5)',
    DevataName: Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));
  
  // Group 2: Next 8 Bars (Blocks 5-13) - Actually Blocks 6-13
  const group8 = all45Data.slice(5, 13).map(item => ({
    ...item,
    group: 'Group 2 (Blocks 6-13)',
    DevataName: Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));
  
  // Group 3: Remaining 32 Bars (Blocks 14-45)
  const group32 = all45Data.slice(13, 45).map(item => ({
    ...item,
    group: 'Group 3 (Blocks 14-45)',
    DevataName: Devata_NAMES.find(d => d.blockNo === item.blockNumber)?.name || 'N/A'
  }));
  
  return {
    group4,
    group8,
    group32
  };
};

useEffect(() => {
  if (layerId !== 'compass-45-2-chart' && layerId !== 'compass-45-2-table') return;
  
  
  if (layerId === 'compass-45-2-chart') {
    const groupedData = calculateGroupedBarData();
    setLayerState(prev => ({
      ...prev,
      groupedData: groupedData,
      groupedData45: undefined,
      groupedTableData45: undefined,
      showTable: false,
      showBarChart: true
    }));
  } else {
    const groupedTableData = calculateGroupedTableData();
    setLayerState(prev => ({
      ...prev,
      groupedTableData: groupedTableData,
      groupedData45: undefined,
      groupedTableData45: undefined,
      showTable: true,
      showBarChart: false
    }));
  }
}, [layerId, isPolygonComplete, compassCenter, isCalibrationSet, pixelsPerUnit]);
  const render32Table = () => {
    const chunk1 = data.slice(0, 11);
    const chunk2 = data.slice(11, 22);
    const chunk3 = data.slice(22, 32);
    
    const renderChunk = (chunkData, title) => (
      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#000', fontSize: '13px' }}>{title}</h4>
      <table style={{
        borderCollapse: 'collapse',
        border: '2px solid #333',
        background: '#fff',
        width: '100%',
        tableLayout: 'fixed',
        maxWidth: '760px'
      }}>
      <tbody>
            <tr style={{ background: '#dc3545', color: '#fff' }}>
              {chunkData.map((item, idx) => (
                <th key={idx} style={{ padding: '6px 2px', textAlign: 'left', fontSize: '12px', fontWeight: 800, border: '1px solid #ECEEF2', width: `${tableCellWidth}px` }}>
                  {dirLabel(item.direction)}
                </th>
              ))}
              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', border: '1px solid #ECEEF2', width: '100px' }}>DIRECTION</th>
            </tr>
            <tr>
              {chunkData.map((item, idx) => (
                <td key={idx} style={{ color:"black", padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', border: '1px solid #ECEEF2', background: idx % 2 === 0 ? '#f8f9fa' : '#e9ecef', fontWeight: 'bold', width: `${tableCellWidth}px` }}>
                  {fmt(item.area)}
                </td>
              ))}
              <td style={{color:"black", padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#f8f9fa', border: '1px solid #ECEEF2' }}>AREA{_needsConvert ? ' (cm²)' : ''}</td>
            </tr>
            <tr>
              {chunkData.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', border: '1px solid #ECEEF2', background: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', width: `${tableCellWidth}px` }}>
                  {fmt(avgValue)}
                </td>
              ))}
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e8f5e9', border: '1px solid #ECEEF2', color: '#2e7d32' }}>AVG AREA</td>
            </tr>
            <tr>
              {chunkData.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', border: '1px solid #ECEEF2', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', width: `${tableCellWidth}px` }}>
                  {fmt(maxLine)}
                </td>
              ))}
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e3f2fd', border: '1px solid #ECEEF2', color: '#1565c0' }}>MAX LINE</td>
            </tr>
            <tr>
              {chunkData.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', border: '1px solid #ECEEF2', background: '#fff3cd', color: '#856404', fontWeight: 'bold', width: `${tableCellWidth}px` }}>
                  {fmt(minLine)}
                </td>
              ))}
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#fff3cd', border: '1px solid #ECEEF2', color: '#856404' }}>MIN LINE</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
    
    return (
      <div style={{ marginBottom: '20px', opacity: layerState.tableOpacity }}>
        {renderChunk(chunk1, 'Directions 1-11')}
        {renderChunk(chunk2, 'Directions 12-22')}
        {renderChunk(chunk3, 'Directions 23-32')}
      </div>
    );
  };

const renderBottomTable = () => {
  if (layerId === 'Bar-chart-8') {
    // ?o. Bar-chart-8 ?.?? ??? ?.??- table structure
    return (
      <div style={{ 
        overflowX: 'auto', 
        opacity: layerState.tableOpacity, 
        marginTop: '0',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <table style={{ 
          borderCollapse: 'collapse', 
          border: '2px solid #333', 
          background: '#fff', 
          width: '100%',
          tableLayout: 'fixed',
          maxWidth: '760px'
        }}>
          <thead>
            <tr style={{ background: '#dc3545', color: '#fff' }}>
              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', border: '1px solid #ECEEF2', width: '100px' }}>DIRECTION</th>
              {data.map((item, idx) => (
                <th key={idx} style={{ padding: '6px 2px', textAlign: 'left', fontSize: '12px', fontWeight: 800, border: '1px solid #ECEEF2', width: `${tableCellWidth}px` }}>
                  {dirLabel(item.direction)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ color:'black',padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#f8f9fa', border: '1px solid #ECEEF2', width: '100px' }}>AREA</td>
              {data.map((item, idx) => (
                <td key={idx} style={{ color: 'red', padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: idx % 2 === 0 ? '#f8f9fa' : '#e9ecef', fontWeight: 'bold' }}>
                  {item.area}
                </td>
              ))}
            </tr>
            <tr>
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e8f5e9', border: '1px solid #ECEEF2', color: '#2e7d32' }}>AVG AREA</td>
              {data.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }}>
                  {avgValue}
                </td>
              ))}
            </tr>
            <tr>
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e3f2fd', border: '1px solid #ECEEF2', color: '#1565c0' }}>MAX LINE</td>
              {data.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}>
                  {maxLine.toFixed(1)}
                </td>
              ))}
            </tr>
            <tr>
              <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#fff3cd', border: '1px solid #ECEEF2', color: '#856404' }}>MIN LINE</td>
              {data.map((_, idx) => (
                <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#fff3cd', color: '#856404', fontWeight: 'bold' }}>
                  {minLine.toFixed(1)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // ?o. Bar-chart-16 ?.?? ??? original structure
  return (
    <div style={{ 
      overflowX: 'auto', 
      opacity: layerState.tableOpacity, 
      marginTop: '0',
      maxWidth: '760px',
      margin: '0 auto'
    }}>
      <table style={{ 
        borderCollapse: 'collapse', 
        border: '2px solid #333', 
        background: '#fff', 
        width: '100%',
        tableLayout: 'fixed',
        maxWidth: '760px'
      }}>
        <tbody>
          <tr>
            {data.map((item, idx) => (
              <td key={idx} style={{ color: 'red', padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: idx % 2 === 0 ? '#f8f9fa' : '#e9ecef', fontWeight: 'bold' }}>
                {item.area}
              </td>
            ))}
            <td style={{ color:'black', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#f8f9fa', border: '1px solid #ECEEF2', width: '100px' }}>AREA</td>
          </tr>
          <tr>
            {data.map((_, idx) => (
              <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }}>
                {avgValue}
              </td>
            ))}
            <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e8f5e9', border: '1px solid #ECEEF2', color: '#2e7d32' }}>AVG AREA</td>
          </tr>
          <tr>
            {data.map((_, idx) => (
              <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}>
                {maxLine.toFixed(1)}
              </td>
            ))}
            <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e3f2fd', border: '1px solid #ECEEF2', color: '#1565c0' }}>MAX LINE</td>
          </tr>
          <tr>
            {data.map((_, idx) => (
              <td key={idx} style={{ padding: '6px 2px', textAlign: 'center', fontSize: '8px', lineHeight: 1.15, whiteSpace: 'normal', wordBreak: 'break-word', width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#fff3cd', color: '#856404', fontWeight: 'bold' }}>
                {minLine.toFixed(1)}
              </td>
            ))}
            <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#fff3cd', border: '1px solid #ECEEF2', color: '#856404' }}>MIN LINE</td>
          </tr>
          <tr style={{ background: '#dc3545', color: '#fff' }}>
            {data.map((item, idx) => (
              <th key={idx} style={{ padding: '6px 2px', textAlign: 'left', fontSize: '12px', fontWeight: 800, width: `${tableCellWidth}px`, border: '1px solid #ECEEF2', background: '#dc3545', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {dirLabel(item.direction)}
              </th>
            ))}
            <th style={{ padding: '8px 6px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>DIRECTION</th>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

  // Reset function when component unmounts or when editing is done
  useEffect(() => {
    return () => {
      setTexts([]);
      setLayerMeasurements([]);
      setMeasurementMode(false);
      setMeasurementPoints([]);
    };
  }, [layerId]);


  return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#ffffff',
            // Bumped above the mobile bottom toolbar (10100)/sheets (10200+)
            // — LayerEditor's own bottom edge used to sit behind the
            // floating toolbar pill on phone widths.
            zIndex: 10500,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // Auto-capture (report toggled ON without ever opening Edit): same editor, same Save, just not shown.
            ...(autoSave ? { opacity: 0, pointerEvents: 'none' } : {})
          }}>
            {/* TOOLBAR HEADER — SaaS-style refined */}
            <div style={{
              background: '#00A19D',
              borderTop: '3px solid #007F7B',
              borderBottom: '1px solid rgba(255,255,255,0.15)',
              padding: '0',
              display: 'flex',
              alignItems: 'stretch',
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(0,30,40,0.18)',
              height: '60px',
              fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif"
            }}>

              {/* Left - Title Section (breadcrumb-style) */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '0 22px',
                borderRight: '1px solid rgba(255,255,255,0.18)',
                minWidth: '280px',
                background: 'rgba(255,255,255,0.06)'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Edit size={16} color="#ffffff" />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <div style={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '10px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: 1
                  }}>
                    Editing Layer
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: 600,
                    lineHeight: '1.3',
                    marginTop: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {layerName}
                  </div>
                </div>
              </div>

              {/* Center - Tool Controls — hidden entirely on mobile per
                  explicit request; only Save/Cancel remain (see Right -
                  Action Buttons below), icon-only there. Desktop keeps
                  every per-layer-type tool control unchanged. */}
              {!isMobile && (
              <div style={{
                flex: 1,
                display: 'flex',
                gap: '0',
                alignItems: 'stretch',
                padding: '8px 12px',
                overflowX: 'auto',
                overflowY: 'hidden'
              }}>
                {/* Cover Front / Cover Back: background upload lives in the header */}
                {(layerId === 'cover-page' || layerId === 'cover-back-page') && renderCoverBgControls(true)}

                {/* COVER PAGE - INFO MESSAGE */}
                {(layerId === 'cover-page' || layerId === 'cover-index' || layerId === 'cover-back-page') ? (
                  <div style={{
                    padding: '0 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 500,
                    gap: '10px',
                    width: '100%',
                    height: '100%'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.22)',
                      borderRadius: '8px',
                      letterSpacing: '0.01em'
                    }}>
                      <Info size={14} />
                      <span>{layerId === 'cover-index' ? 'Index pages list every enabled report · Text tool is auto-enabled' : layerId === 'cover-back-page' ? 'Text comes from Cover Front fields · only the background can be changed here' : 'Click on any field to edit · Text tool is auto-enabled'}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* PNG-SPECIFIC TOOLS (Float + Align) */}
                    {isPNG && (
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center',
                        padding: '0 12px',
                        height: '100%',
                        borderRight: '1px solid rgba(255,255,255,0.15)'
                      }}>
                        {/* Float Mode Toggle */}
                        <div
                          onClick={toggleFloatMode}
                          title={layerState.floatMode ? 'Float mode active — click to lock' : 'Enable float mode'}
                          style={{
                            background: layerState.floatMode ? '#FFFFFF' : 'rgba(255,255,255,0.10)',
                            color: layerState.floatMode ? '#00857E' : '#FFFFFF',
                            border: '1px solid ' + (layerState.floatMode ? '#FFFFFF' : 'rgba(255,255,255,0.22)'),
                            height: '36px',
                            padding: '0 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 160ms ease',
                            letterSpacing: '0.01em'
                          }}
                          onMouseEnter={(e) => {
                            if (!layerState.floatMode) e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
                          }}
                          onMouseLeave={(e) => {
                            if (!layerState.floatMode) e.currentTarget.style.background = 'rgba(255,255,255,0.10)';
                          }}
                        >
                          <Move size={14} />
                          <span>{layerState.floatMode ? 'Float On' : 'Float Off'}</span>
                        </div>

                        {/* Align to Center Button */}
                        <div
                          onClick={handleAlignToCenter}
                          title={compassCenter ? 'Align image to compass center' : 'Set compass center first'}
                          style={{
                            background: 'rgba(255,255,255,0.10)',
                            color: '#FFFFFF',
                            border: '1px solid rgba(255,255,255,0.22)',
                            height: '36px',
                            padding: '0 12px',
                            borderRadius: '8px',
                            cursor: compassCenter ? 'pointer' : 'not-allowed',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 160ms ease',
                            letterSpacing: '0.01em',
                            opacity: compassCenter ? 1 : 0.5
                          }}
                          onMouseEnter={(e) => {
                            if (compassCenter) e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
                          }}
                          onMouseLeave={(e) => {
                            if (compassCenter) e.currentTarget.style.background = 'rgba(255,255,255,0.10)';
                          }}
                        >
                          <Target size={14} />
                          <span>Align Center</span>
                        </div>
                      </div>
                    )}
                    
                    {/* PNG LAYER CONTROLS */}
                    {layerType === 'png' && (
                      <>
                       
                        {/* Opacity Control */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '2px',
                          minWidth: '100px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Opacity
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input 
                              type="range" 
                              min="0" 
                              max="1" 
                              step="0.1" 
                              value={layerState.opacity || 0.5}
                              onChange={(e) => setLayerState(prev => ({ ...prev, opacity: Number(e.target.value) }))}
                              style={{ width: '60px', accentColor: '#ffffff', height: '4px' }} 
                            />
                            <span style={{ color: '#ffffff', fontSize: '10px', minWidth: '30px', fontWeight: 600 }}>
                              {Math.round((layerState.opacity || 0.5) * 100)}%
                            </span>
                          </div>
                        </div>
                        {/* ?o. NEW: Download Layer Button for PNG */}
                        <div 
                          onClick={handleDownloadLayer}
                          style={{ 
                            background: '#28a745',
                            color: '#ffffff',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            minWidth: '70px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderRight: '1px solid #ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#218838';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#28a745';
                          }}
                        >
                          <Download size={16} />
                          <span style={{ fontSize: '9px' }}>Download</span>
                        </div>
                         <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          borderRight: '1px solid #ffffff'
                        }}>
                          {/* Get Measurement Button */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (layerIsCalibrationSet) {
                                startGetMeasurement();
                              } else {
                                alert('Please set measurement calibration first!');
                              }
                            }}
                            style={{
                              background: '#00A19D',
                              color: layerIsCalibrationSet ? '#ffffff' : '#ffffff',
                              padding: '6px 12px',
                              cursor: layerIsCalibrationSet ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                              height: '100%',
                              textTransform: 'uppercase',
                              borderRight: '1px solid #ffffff',
                              opacity: layerIsCalibrationSet ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (layerIsCalibrationSet && !measurementMode) {
                                e.currentTarget.style.background = '#00857E';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (layerIsCalibrationSet && !measurementMode) {
                                e.currentTarget.style.background = '#00A19D';
                              }
                            }}
                          >
                            Get
                            <span style={{ fontSize: '9px' }}>Measure</span>
                          </div>

                          {/* ?o. NEW: Reset Measurements Button */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetMeasurements();
                            }}
                            style={{
                              background: '#00A19D',
                              color: layerMeasurements.length > 0 ? '#ffffff' : '#ffffff',
                              padding: '6px 12px',
                              cursor: layerMeasurements.length > 0 ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                              height: '100%',
                              textTransform: 'uppercase',
                              borderRight: measurementMode || removeMeasurementMode ? '1px solid #ffffff' : 'none',
                              opacity: layerMeasurements.length > 0 ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (layerMeasurements.length > 0) {
                                e.currentTarget.style.background = '#ffc107';
                                e.currentTarget.style.color = '#000000';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (layerMeasurements.length > 0) {
                                e.currentTarget.style.background = '#00A19D';
                                e.currentTarget.style.color = '#ffffff';
                              }
                            }}
                          >
                            ??? Reset
                            <span style={{ fontSize: '9px' }}>All</span>
                          </div>

                          {/* Cancel Button - Only show when in measurement or remove mode */}
                          {(measurementMode || removeMeasurementMode) && (
                            <div
                              onClick={() => {
                                setMeasurementMode(false);
                                setRemoveMeasurementMode(false);
                                setMeasurementPoints([]);
                              }}
                              style={{
                                background: '#dc3545',
                                color: '#ffffff',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease',
                                height: '100%',
                                textTransform: 'uppercase'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#c82333';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#dc3545';
                              }}
                            >
                              <X size={12} />
                              <span style={{ fontSize: '9px' }}>Cancel</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                    {/* GRID LAYER CONTROLS */}
                    {isGrid && (
                      <>
                        {/* Grid Color */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '4px',
                          minWidth: '80px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Color
                          </div>
                          <input 
                            type="color" 
                            value={layerState.gridColor || '#00A19D'} 
                            onChange={(e) => setLayerState(prev => ({ ...prev, gridColor: e.target.value }))} 
                            style={{ width: '40px', height: '24px', cursor: 'pointer', borderRadius: '3px', border: '1px solid #DCE0E8' }} 
                          />
                        </div>
                        
                        {/* Grid Opacity */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '2px',
                          minWidth: '100px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Opacity
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input 
                              type="range" 
                              min="0.1" 
                              max="1" 
                              step="0.1" 
                              value={layerState.gridOpacity || 0.5} 
                              onChange={(e) => setLayerState(prev => ({ ...prev, gridOpacity: Number(e.target.value) }))} 
                              style={{ width: '60px', accentColor: '#ffffff', height: '4px' }} 
                            />
                            <span style={{ color: '#ffffff', fontSize: '10px', minWidth: '30px', fontWeight: 600 }}>
                              {Math.round((layerState.gridOpacity || 0.5) * 100)}%
                            </span>
                          </div>
                        </div>
                        <div 
                          onClick={handleDownloadLayer}
                          style={{ 
                            background: '#28a745',
                            color: '#ffffff',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            minWidth: '70px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderRight: '1px solid #ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#218838';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#28a745';
                          }}
                        >
                          <Download size={16} />
                          <span style={{ fontSize: '9px' }}>Download</span>
                        </div>
                      </>
                    )}
                    
                    {isCompass && (
                      <>

                        {/* Color Control */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '4px',
                          minWidth: '80px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Color
                          </div>
                          <input 
                            type="color" 
                            value={layerState.compassColor || '#000000'} 
                            onChange={(e) => setLayerState(prev => ({ ...prev, compassColor: e.target.value }))} 
                            style={{ width: '40px', height: '24px', cursor: 'pointer', borderRadius: '3px', border: '1px solid #DCE0E8' }} 
                          />
                        </div>
                        
                        {/* Line Weight Control */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '2px',
                          minWidth: '100px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Line Weight
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input 
                              type="range" 
                              min="1" 
                              max="10" 
                              value={layerState.compassLineWeight || 2} 
                              onChange={(e) => setLayerState(prev => ({ ...prev, compassLineWeight: Number(e.target.value) }))} 
                              style={{ width: '60px', accentColor: '#ffffff', height: '4px' }} 
                            />
                            <span style={{ color: '#ffffff', fontSize: '10px', minWidth: '30px', fontWeight: 600 }}>
                              {layerState.compassLineWeight || 2}px
                            </span>
                          </div>
                        </div>

                        {/* Line Opacity Control */}
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '2px',
                          minWidth: '110px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Line Opacity
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input 
                              type="range" 
                              min="0.05" 
                              max="1" 
                              step="0.05" 
                              value={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1} 
                              onChange={(e) => setLayerState(prev => ({ ...prev, compassLineOpacity: Number(e.target.value) }))} 
                              style={{ width: '60px', accentColor: '#ffffff', height: '4px' }} 
                            />
                            <span style={{ color: '#ffffff', fontSize: '10px', minWidth: '30px', fontWeight: 600 }}>
                              {Math.round((layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1) * 100)}%
                            </span>
                          </div>
                        </div>
                      
                        {/* ?o. NEW: Download Layer Button for Compass/Generative Layers */}
                        <div 
                          onClick={handleDownloadLayer}
                          style={{ 
                            background: '#28a745',
                            color: '#ffffff',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            minWidth: '70px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderRight: '1px solid #ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#218838';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#28a745';
                          }}
                        >
                          <Download size={16} />
                          <span style={{ fontSize: '9px' }}>Download</span>
                        </div>
                                                {/* Measurement Buttons - Always Visible */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          borderRight: '1px solid #ffffff'
                        }}>
                          {/* Get Measurement Button */}
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (layerIsCalibrationSet) {
                                startGetMeasurement();
                              } else {
                                alert('Please set measurement calibration first!');
                              }
                            }}
                            style={{ 
                              background: '#00A19D',
                              color: layerIsCalibrationSet ? '#ffffff' : '#ffffff',
                              padding: '6px 12px',
                              cursor: layerIsCalibrationSet ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                              height: '100%',
                              textTransform: 'uppercase',
                              borderRight: '1px solid #ffffff',
                              opacity: layerIsCalibrationSet ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (layerIsCalibrationSet && !measurementMode) {
                                e.currentTarget.style.background = '#00857E';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (layerIsCalibrationSet && !measurementMode) {
                                e.currentTarget.style.background = '#00A19D';
                              }
                            }}
                          >
                            Get
                            <span style={{ fontSize: '9px' }}>Measure</span>
                          </div>

                          {/* ?o. NEW: Reset Measurements Button */}
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetMeasurements();
                            }}
                            style={{ 
                              background: '#00A19D',
                              color: layerMeasurements.length > 0 ? '#ffffff' : '#ffffff',
                              padding: '6px 12px',
                              cursor: layerMeasurements.length > 0 ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                              height: '100%',
                              textTransform: 'uppercase',
                              borderRight: measurementMode || removeMeasurementMode ? '1px solid #ffffff' : 'none',
                              opacity: layerMeasurements.length > 0 ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (layerMeasurements.length > 0) {
                                e.currentTarget.style.background = '#ffc107';
                                e.currentTarget.style.color = '#000000';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (layerMeasurements.length > 0) {
                                e.currentTarget.style.background = '#00A19D';
                                e.currentTarget.style.color = '#ffffff';
                              }
                            }}
                          >
                            ??? Reset
                            <span style={{ fontSize: '9px' }}>All</span>
                          </div>

                          {/* Cancel Button - Only show when in measurement or remove mode */}
                          {(measurementMode || removeMeasurementMode) && (
                            <div 
                              onClick={() => {
                                setMeasurementMode(false);
                                setRemoveMeasurementMode(false);
                                setMeasurementPoints([]);
                              }}
                              style={{ 
                                background: '#dc3545',
                                color: '#ffffff',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease',
                                height: '100%',
                                textTransform: 'uppercase'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#c82333';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#dc3545';
                              }}
                            >
                              <X size={12} />
                              <span style={{ fontSize: '9px' }}>Cancel</span>
                            </div>
                          )}
                        </div>

                      </>
                    )}

                    {(isCompass && (layerId === 'coloured-zone' || layerId === 'elemental-5-zone' || layerId === 'elemental-5-chart' || layerId === 'marmsthan-advance')) && (
                      <>
                        <div style={{
                          padding: '6px 14px',
                          borderRight: '1px solid #ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '2px',
                          minWidth: '100px'
                        }}>
                          <div style={{ 
                            color: '#ffffff', 
                            fontSize: '9px', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600
                          }}>
                            Filled Opacity
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input 
                              type="range" 
                              min="0.1" 
                              max="1" 
                              step="0.1" 
                              value={compassOpacity2|| 0.5} 
                              onChange={(e) => setCompassOpacity2(Number(e.target.value))} 
                              style={{ width: '60px', accentColor: '#ffffff', height: '4px' }} 
                            />
                            <span style={{ color: '#ffffff', fontSize: '10px', minWidth: '30px', fontWeight: 600 }}>
                              {Math.round((compassOpacity2 || 0.5) * 100)}%
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* BAR CHART CONTROLS */}
                    {isBarChart && (
                      <>
                        <div 
                          onClick={handleDownloadLayer}
                          style={{ 
                            background: '#28a745',
                            color: '#ffffff',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            minWidth: '70px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderRight: '1px solid #ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#218838';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#28a745';
                          }}
                        >
                          <Download size={16} />
                          <span style={{ fontSize: '9px' }}>Download</span>
                        </div>
                      </>
                    )}

                    {/* Add Text Tool */}
                    <div 
                      onClick={() => setIsAddingText(!isAddingText)}
                      style={{ 
                        background: isAddingText ? '#00A19D' : '#00A19D',
                        color: '#ffffff',
                        padding: '6px 14px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        minWidth: '70px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderRight: '1px solid #ffffff'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAddingText) {
                          e.currentTarget.style.background = '#00857E';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAddingText) {
                          e.currentTarget.style.background = '#00A19D';
                        }
                      }}
                    >
                      {isAddingText ? <Check size={16} /> : <Edit size={16} />}
                      <span style={{ fontSize: '9px' }}>{isAddingText ? 'Click' : 'Text'}</span>
                    </div>
                  </>
                )}
              </div>
              )}

              {/* Right - Action Buttons (refined) */}
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                borderLeft: '1px solid rgba(255,255,255,0.18)',
                padding: '0 16px',
                background: 'rgba(255,255,255,0.06)',
                // Mobile: the Center Tool Controls div (which used to be
                // the flex:1 spacer pushing this to the far right) renders
                // nothing at all now, so without a spacer of its own these
                // buttons just sat right after the title — near the middle
                // of a phone screen instead of flush right. Desktop still
                // has that flex:1 spacer, so it doesn't need this.
                marginLeft: isMobile ? 'auto' : undefined,
              }}>
                {/* Report Section shortcut — mobile only. Saves the layer
                    being edited (same action as the checkmark below) and
                    then opens the Report Section sheet once edit mode
                    actually closes (see pendingReportsOpen in Configure).
                    Placed before Cancel per explicit request; shown as a
                    text label instead of an icon per explicit request too —
                    a bare icon here wasn't self-explanatory next to the
                    plain Cancel/Save icons. */}
                {isMobile && (
                  <button
                    onClick={() => { onRequestReportsAfterSave && onRequestReportsAfterSave(); handleSaveLayer(); }}
                    title="Save and Next Report"
                    aria-label="Save and Next Report"
                    style={{
                      height: 34, borderRadius: '8px', border: 'none',
                      padding: '0 12px',
                      background: '#ffffff', color: '#00A19D',
                      fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    Save and Next Report
                  </button>
                )}
                <button
                  onClick={leaveConfirm.requestClose}
                  title="Cancel"
                  style={isMobile ? {
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: '#ffffff',
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    flexShrink: 0,
                  } : {
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: '#ffffff',
                    padding: '0 16px',
                    height: '36px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'background 160ms ease, border-color 160ms ease',
                    letterSpacing: '0.02em',
                    borderRadius: '8px',
                    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
                  }}
                  onMouseEnter={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.background = 'rgba(255,107,92,0.18)';
                    e.currentTarget.style.borderColor = '#FF6B5C';
                  }}
                  onMouseLeave={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
                  }}
                >
                  <X size={14} />
                  {!isMobile && 'Cancel'}
                </button>
                {leaveConfirm.dialog}

                <button
                  onClick={handleSaveLayer}
                  title="Save Layer"
                  style={isMobile ? {
                    background: '#ffffff',
                    border: '1px solid #ffffff',
                    color: '#00857E',
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    flexShrink: 0,
                  } : {
                    background: '#ffffff',
                    border: '1px solid #ffffff',
                    color: '#00857E',
                    padding: '0 18px',
                    height: '36px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'background 160ms ease, transform 120ms ease, box-shadow 160ms ease',
                    letterSpacing: '0.02em',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
                  }}
                  onMouseEnter={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.background = '#F0FBFA';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.22)';
                  }}
                  onMouseLeave={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.18)';
                  }}
                >
                  <CheckCircle size={14} />
                  {!isMobile && 'Save Layer'}
                </button>
              </div>
            </div>

                {/* Main Display Area */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
                  {editorZoomOn && (
                    <div style={{
  position: 'absolute', top: 10, right: 18, zIndex: 30, display: 'flex', alignItems: 'center', gap: 4,
  background: 'rgba(255,255,255,0.94)', border: '1px solid #ECEEF2', borderRadius: 8, padding: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
}} data-zoom-controls="edit">
                      <button type="button" onClick={() => setEditorZoom(z => Math.max(Math.round((z - 0.25) * 100) / 100, 0.5))} disabled={editorZoom <= 0.5} title="Zoom Out" style={{
  width: 28, height: 28, background: '#00A19D', border: '1px solid #00A19D', color: '#ffffff',
  borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: editorZoom <= 0.5 ? 0.5 : 1
}}><ZoomOut size={16} /></button>
                      <button type="button" onClick={() => setEditorZoom(1)} title="Reset zoom to 100%" style={{
  minWidth: 52, height: 28, background: '#ffffff', border: '1px solid #00A19D', color: '#00A19D',
  borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, padding: '0 6px'
}}>{Math.round(editorZoom * 100)}%</button>
                      <button type="button" onClick={() => setEditorZoom(z => Math.min(Math.round((z + 0.25) * 100) / 100, 3))} disabled={editorZoom >= 3} title="Zoom In" style={{
  width: 28, height: 28, background: '#00A19D', border: '1px solid #00A19D', color: '#ffffff',
  borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: editorZoom >= 3 ? 0.5 : 1
}}><ZoomIn size={16} /></button>
                    </div>
                  )}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: editorZoom > 1 && editorZoomOn ? 'flex-start' : 'center',
                    justifyContent: editorZoom > 1 && editorZoomOn ? 'flex-start' : 'center',
                    padding: '20px',
                    overflow: 'auto',
                    background: '#ffffff',
                    cursor: isAddingText || measurementMode ? 'crosshair' : 'default',
                    gap: '20px',
                    flexWrap: 'wrap'
                  }}>

                    {/* Mobile fit-scale sizer — fixed to the *scaled* pixel
                        size so the flex/overflow:auto parent above never
                        has to allocate room for the full unscaled A4 canvas
                        (that's what was causing the bottom-right scrollbar).
                        The inner div keeps the true A4 size and is visually
                        shrunk with a CSS transform, which Konva's own
                        pointer-position math already accounts for — same
                        approach as the main canvas. Desktop: both divs are
                        inert (no width/transform override; default browser
                        behavior for a naked wrapping <div> === none). */}
                    <div style={editorZoomed ? {
                      width: `${Math.round(A4_WIDTH * editorViewScale)}px`,
                      height: `${Math.round(zoomInnerH * editorViewScale)}px`,
                      flexShrink: 0,
                      margin: 'auto',
                    } : isMobile ? {
                      width: `${Math.round(A4_WIDTH * editorFitScale)}px`,
                      // Cover Front stacks 6 A4 preview pages (4 gaps of 20px) — its
                      // scaled height must cover all of them, not just one page.
                      height: `${Math.round((layerId === 'cover-index' ? A4_HEIGHT * 5 + 20 * 3 : A4_HEIGHT) * editorFitScale)}px`,
                    } : undefined}>
                    <div ref={zoomInnerRef} style={(isMobile || editorZoomed) ? {
                      width: `${A4_WIDTH}px`,
                      transform: `scale(${editorViewScale})`,
                      transformOrigin: 'top left',
                    } : undefined}>

                    {/* ?o. COMPASS-45 CHART */}
                    {layerId === 'compass-45-chart' && layerState.groupedData45 && (
                      <div
                        ref={contentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          width: '840px',
                          position: 'relative',
                          padding: '20px',
                          overflowX: 'hidden'
                        }}
                      >
                        {/* Group 1: 4 Vedic Devata */}
                        <div style={{ marginBottom: '40px' }}>
                          <h3 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '14px', fontWeight: 'bold', color: 'black' }}>
                            4 Vedic Devata
                          </h3>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', justifyContent: 'center', border: '1px solid #ddd', flexWrap: 'wrap' }}>
                            {[{ color: '#4da9c8', label: 'AREA', box: true }, { color: '#d32f2f', label: 'AVG LINE' }, { color: '#7cb342', label: 'MAX LINE' }, { color: '#512da8', label: 'MIN LINE' }].map(({ color, label, box }) => (
                              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '10px', height: box ? '10px' : '2px', background: color, border: box ? '1px solid #333' : 'none' }} />
                                <span style={{ fontSize: '9px', fontWeight: 'bold', color }}>{label}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ border: '2px solid #333', borderRadius: '4px', padding: '15px 15px 60px 50px', background: '#fafafa', position: 'relative', display: 'flex' }}>
                            <div style={{ position: 'absolute', left: '42px', top: '15px', bottom: '60px', width: '1px', background: '#333', opacity: 0.5 }} />
                            <div style={{ position: 'absolute', left: '15px', top: '15px', bottom: '60px', width: '30px', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: '8px' }}>
                              {(() => {
                                const g4 = layerState.groupedData45.group4;
                                const maxV = Math.max(...g4.map(i => parseFloat(i.area)));
                                return Array.from({ length: 5 }, (_, i) => (
                                  <div key={i} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666' }}>{((maxV / 4) * i).toFixed(1)}</div>
                                ));
                              })()}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', height: '180px', paddingBottom: '0px', position: 'relative', justifyContent: 'center', flex: 1 }}>
                              {(() => {
                                const g4 = layerState.groupedData45.group4;
                                const maxV = Math.max(...g4.map(i => parseFloat(i.area)));
                                const minV = Math.min(...g4.map(i => parseFloat(i.area)));
                                const avgV = g4.reduce((s, i) => s + parseFloat(i.area), 0) / g4.length;
                                const maxLine = (maxV + avgV) / 2;
                                const minLine = (minV + avgV) / 2;
                                const chartH = 180;
                                return (
                                  <>
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${chartH}px`, pointerEvents: 'none' }}>
                                      {[{ top: chartH * (1 - maxLine / maxV), color: '#7cb342' }, { top: chartH * (1 - avgV / maxV), color: '#d32f2f' }, { top: chartH * (1 - minLine / maxV), color: '#512da8' }].map(({ top, color }, i) => (
                                        <div key={i} style={{ position: 'absolute', top: `${top}px`, left: 0, right: 0, height: '2px', background: color, opacity: 0.7 }} />
                                      ))}
                                    </div>
                                    {g4.map((item, idx) => {
                                      const BarH = (parseFloat(item.area) / maxV) * chartH;
                                      const DevataName = Vedic_Devata_NAMES.find(d => d.blockNo === idx + 2)?.name || 'N/A';
                                      return (
                                        <div key={idx} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '28px', position: 'relative' }}>
                                          <div style={{ width: '28px', height: `${BarH}px`, background: '#4da9c8', border: '0.5px solid #333', opacity: 0.85, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '1px', fontSize: '7px', color: '#fff', fontWeight: 'bold' }}>
                                            {BarH > 15 && fmt(item.area)}
                                          </div>
                                          <div style={{ position: 'absolute', bottom: '-45px', left: '50%', transform: 'translateX(-50%) rotate(90deg)', transformOrigin: 'center', fontSize: '7px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>
                                            {DevataName} ({idx + 2})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Group 2: 8 Vedic Devata */}
                        <div style={{ marginBottom: '40px' }}>
                          <h3 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '14px', fontWeight: 'bold', color: 'black' }}>
                            8 Vedic Devata
                          </h3>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', justifyContent: 'center', border: '1px solid #ddd', flexWrap: 'wrap' }}>
                            {[{ color: '#28a745', label: 'AREA', box: true }, { color: '#d32f2f', label: 'AVG LINE' }, { color: '#7cb342', label: 'MAX LINE' }, { color: '#512da8', label: 'MIN LINE' }].map(({ color, label, box }) => (
                              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '10px', height: box ? '10px' : '2px', background: color, border: box ? '1px solid #333' : 'none' }} />
                                <span style={{ fontSize: '9px', fontWeight: 'bold', color }}>{label}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ border: '2px solid #333', borderRadius: '4px', padding: '15px 15px 60px 50px', background: '#fafafa', position: 'relative', display: 'flex' }}>
                            <div style={{ position: 'absolute', left: '42px', top: '15px', bottom: '60px', width: '1px', background: '#333', opacity: 0.5 }} />
                            <div style={{ position: 'absolute', left: '15px', top: '15px', bottom: '60px', width: '30px', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: '8px' }}>
                              {(() => {
                                const g8 = layerState.groupedData45.group8;
                                const maxV = Math.max(...g8.map(i => parseFloat(i.area)));
                                return Array.from({ length: 5 }, (_, i) => (
                                  <div key={i} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666' }}>{((maxV / 4) * i).toFixed(1)}</div>
                                ));
                              })()}
                            </div>
                            <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-end', height: '180px', justifyContent: 'center', flex: 1 }}>
                              {(() => {
                                const g8 = layerState.groupedData45.group8;
                                const maxV = Math.max(...g8.map(i => parseFloat(i.area)));
                                const minV = Math.min(...g8.map(i => parseFloat(i.area)));
                                const avgV = g8.reduce((s, i) => s + parseFloat(i.area), 0) / g8.length;
                                const maxLine = (maxV + avgV) / 2;
                                const minLine = (minV + avgV) / 2;
                                const chartH = 180;
                                return (
                                  <>
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${chartH}px`, pointerEvents: 'none' }}>
                                      {[{ top: chartH * (1 - maxLine / maxV), color: '#7cb342' }, { top: chartH * (1 - avgV / maxV), color: '#d32f2f' }, { top: chartH * (1 - minLine / maxV), color: '#512da8' }].map(({ top, color }, i) => (
                                        <div key={i} style={{ position: 'absolute', top: `${top}px`, left: 0, right: 0, height: '2px', background: color, opacity: 0.7 }} />
                                      ))}
                                    </div>
                                    {g8.map((item, idx) => {
                                      const BarH = (parseFloat(item.area) / maxV) * chartH;
                                      const blockNumber = idx + 6;
                                      const DevataName = Vedic_Devata_NAMES.find(d => d.blockNo === blockNumber)?.name || 'N/A';
                                      return (
                                        <div key={idx} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '22px', position: 'relative' }}>
                                          <div style={{ width: '22px', height: `${BarH}px`, background: '#28a745', border: '0.5px solid #333', opacity: 0.85, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '1px', fontSize: '7px', color: '#fff', fontWeight: 'bold' }}>
                                            {BarH > 15 && fmt(item.area)}
                                          </div>
                                          <div style={{ position: 'absolute', bottom: '-45px', left: '50%', transform: 'translateX(-50%) rotate(90deg)', transformOrigin: 'center', fontSize: '7px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>
                                            {DevataName} ({blockNumber})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Group 3: 32 Vedic Devata */}
                        <div style={{ marginBottom: '20px' }}>
                          <h3 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '14px', fontWeight: 'bold', color: 'black' }}>
                            32 Vedic Devata
                          </h3>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', justifyContent: 'center', border: '1px solid #ddd', flexWrap: 'wrap' }}>
                            {[{ color: '#00A19D', label: 'AREA', box: true }, { color: '#d32f2f', label: 'AVG LINE' }, { color: '#7cb342', label: 'MAX LINE' }, { color: '#512da8', label: 'MIN LINE' }].map(({ color, label, box }) => (
                              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '10px', height: box ? '10px' : '2px', background: color, border: box ? '1px solid #333' : 'none' }} />
                                <span style={{ fontSize: '9px', fontWeight: 'bold', color }}>{label}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ border: '2px solid #333', borderRadius: '4px', padding: '15px 15px 60px 50px', background: '#fafafa', position: 'relative', display: 'flex' }}>
                            <div style={{ position: 'absolute', left: '42px', top: '15px', bottom: '60px', width: '1px', background: '#333', opacity: 0.5 }} />
                            <div style={{ position: 'absolute', left: '15px', top: '15px', bottom: '60px', width: '30px', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: '8px' }}>
                              {(() => {
                                const g32 = layerState.groupedData45.group32;
                                const maxV = Math.max(...g32.map(i => parseFloat(i.area)));
                                return Array.from({ length: 5 }, (_, i) => (
                                  <div key={i} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666' }}>{((maxV / 4) * i).toFixed(1)}</div>
                                ));
                              })()}
                            </div>
                            <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-end', height: '180px', justifyContent: 'center', overflowX: 'visible', flex: 1 }}>
                              {(() => {
                                const g32 = layerState.groupedData45.group32;
                                const maxV = Math.max(...g32.map(i => parseFloat(i.area)));
                                const minV = Math.min(...g32.map(i => parseFloat(i.area)));
                                const avgV = g32.reduce((s, i) => s + parseFloat(i.area), 0) / g32.length;
                                const maxLine = (maxV + avgV) / 2;
                                const minLine = (minV + avgV) / 2;
                                const chartH = 180;
                                return (
                                  <>
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${chartH}px`, pointerEvents: 'none' }}>
                                      {[{ top: chartH * (1 - maxLine / maxV), color: '#7cb342' }, { top: chartH * (1 - avgV / maxV), color: '#d32f2f' }, { top: chartH * (1 - minLine / maxV), color: '#512da8' }].map(({ top, color }, i) => (
                                        <div key={i} style={{ position: 'absolute', top: `${top}px`, left: 0, right: 0, height: '2px', background: color, opacity: 0.7 }} />
                                      ))}
                                    </div>
                                    {g32.map((item, idx) => {
                                      const BarH = (parseFloat(item.area) / maxV) * chartH;
                                      const blockNumber = idx + 14;
                                      const DevataName = Vedic_Devata_NAMES.find(d => d.blockNo === blockNumber)?.name || 'N/A';
                                      return (
                                        <div key={idx} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '16px', position: 'relative' }}>
                                          <div style={{ width: '14px', height: `${BarH}px`, background: '#00A19D', border: '0.5px solid #333', opacity: 0.85, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '1px', fontSize: '6px', color: '#fff', fontWeight: 'bold' }}>
                                            {BarH > 20 && fmt(item.area)}
                                          </div>
                                          <div style={{ position: 'absolute', bottom: '-45px', left: '50%', transform: 'translateX(-50%) rotate(90deg)', transformOrigin: 'center', fontSize: '6px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>
                                            {DevataName} ({blockNumber})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {layerId === 'compass-45-2-chart' && layerState.groupedData && (
                      <div 
                        ref={contentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          width: '840px',
                          position: 'relative',
                          padding: '20px',
                          overflowX: 'hidden'
                        }}
                      >
                        {/* Group 1: 4 Modern Devata */}
                        <div style={{ marginBottom: '40px' }}>
                          {/* Group Title */}
                          <h3 style={{
                            textAlign: 'center',
                            marginBottom: '15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black'
                          }}>
                            4 Modern Devata
                          </h3>

                          {/* Legend Box inside Chart */}
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '10px',
                            padding: '8px',
                            background: '#f8f9fa',
                            borderRadius: '4px',
                            justifyContent: 'center',
                            border: '1px solid #ddd',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '10px', background: '#4da9c8', border: '1px solid #ECEEF2' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#4da9c8' }}>AREA</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#d32f2f' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#d32f2f' }}>AVG LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#7cb342' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#7cb342' }}>MAX LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#512da8' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#512da8' }}>MIN LINE</span>
                            </div>
                          </div>

                          {/* Chart Box */}
                          <div style={{
                            border: '2px solid #333',
                            borderRadius: '4px',
                            padding: '15px 15px 60px 50px',
                            background: '#fafafa',
                            position: 'relative',
                            display: 'flex'
                          }}>
                            {/* Y-Axis Line */}
                            <div style={{
                              position: 'absolute',
                              left: '42px',
                              top: '15px',
                              bottom: '60px',
                              width: '1px',
                              background: '#333',
                              opacity: 0.5
                            }}></div>

                            {/* Y-Axis Scale */}
                            <div style={{
                              position: 'absolute',
                              left: '15px',
                              top: '15px',
                              bottom: '60px',
                              width: '30px',
                              display: 'flex',
                              flexDirection: 'column-reverse',
                              justifyContent: 'space-between',
                              alignItems: 'flex-end',
                              paddingRight: '8px'
                            }}>
                              {(() => {
                                const group4Data = layerState.groupedData.group4;
                                const maxVal = Math.max(...group4Data.map(i => parseFloat(i.area)));
                                const rs = maxVal / 5, mg = Math.pow(10, Math.floor(Math.log10(rs || 1))), nr = rs / mg;
                                const ns = nr <= 1 ? mg : nr <= 2 ? 2*mg : nr <= 5 ? 5*mg : 10*mg;
                                const niceMax = Math.ceil((maxVal || ns) / ns) * ns;
                                const ticks = Array.from({ length: Math.round(niceMax / ns) + 1 }, (_, i) => i * ns);
                                return ticks.map((v, idx) => (
                                  <div key={`scale-g1-${idx}`} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666', textAlign: 'right' }}>
                                    {Number.isInteger(v) ? v : v.toFixed(1)}
                                  </div>
                                ));
                              })()}
                            </div>

                            {/* Chart Content */}
                            <div style={{
                              display: 'flex',
                              gap: '6px',
                              alignItems: 'flex-end',
                              height: '180px',
                              paddingBottom: '0px',
                              position: 'relative',
                              justifyContent: 'center',
                              flex: 1
                            }}>
                              {(() => {
                                  const group4Data = layerState.groupedData.group4;
                                  const maxVal = Math.max(...group4Data.map(i => parseFloat(i.area)));
                                  const minVal = Math.min(...group4Data.map(i => parseFloat(i.area)));
                                  const avgVal = group4Data.reduce((sum, i) => sum + parseFloat(i.area), 0) / group4Data.length;
                                  const maxLine = (maxVal + avgVal) / 2;
                                  const minLine = (minVal + avgVal) / 2;
                                  const _rs4 = maxVal/5, _mg4 = Math.pow(10,Math.floor(Math.log10(_rs4||1))), _nr4 = _rs4/_mg4;
                                  const _ns4 = _nr4<=1?_mg4:_nr4<=2?2*_mg4:_nr4<=5?5*_mg4:10*_mg4;
                                  const niceMax4 = Math.ceil((maxVal||_ns4)/_ns4)*_ns4;

                                const chartHeightVal = 180;

                                return (
                                  <>
                                    {/* Reference Lines */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: `${chartHeightVal}px`,
                                      pointerEvents: 'none'
                                    }}>
                                      {/* Max Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - maxLine / niceMax4)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#7cb342',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Avg Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - avgVal / niceMax4)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#d32f2f',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Min Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - minLine / niceMax4)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#512da8',
                                        opacity: 0.7
                                      }}></div>
                                    </div>

                                    {group4Data.map((item, idx) => {
                                      const BarHeight = (parseFloat(item.area) / niceMax4) * chartHeightVal;
                                      const blockNumber = idx + 2;
                                      const DevataName = Devata_NAMES.find(d => d.blockNo === blockNumber)?.name || 'N/A';
                                      
                                      return (
                                        <div key={`g1-${idx}`} style={{ 
                                          flex: '0 0 auto',
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          alignItems: 'center',
                                          width: '28px',
                                          position: 'relative'
                                        }}>
                                          <div style={{
                                            width: '28px',
                                            height: `${BarHeight}px`,
                                            background: '#4da9c8',
                                            border: '0.5px solid #333',
                                            opacity: 0.85,
                                            position: 'relative',
                                            fontSize: '7px',
                                            color: '#fff',
                                            fontWeight: 'bold'
                                          }}>
                                            {/* Value reads bottom → top from the bar's base; a bar too
                                                short to hold it draws it just above the bar (dark)
                                                instead of hiding it, so the smallest zone still shows. */}
                                            {(() => {
                                              const lbl = fmt(item.area);
                                              const fits = BarHeight >= String(lbl).length * 4.3 + 6;
                                              return (
                                                (() => {
                                                  const vl = verticalLabelImage(String(lbl), fits ? '#ffffff' : '#111111', 7);
                                                  return (
                                                    <img alt="" src={vl.url} style={{ position: 'absolute', left: `calc(50% - ${vl.w / 2}px)`, bottom: fits ? '2px' : 'calc(100% + 3px)', width: vl.w, height: vl.h, pointerEvents: 'none' }} />
                                                  );
                                                  })()
                                              );
                                            })()}
                                          </div>
                                          
                                          {/* Devata Name - Rotated 310 Degrees */}
                                          <div style={{
                                            position: 'absolute',
                                            bottom: '-45px',
                                            left: '50%',
                                            transform: 'translateX(-50%) rotate(90deg)',
                                            transformOrigin: 'center',
                                            fontSize: '7px',
                                            fontWeight: '500',
                                            color: '#333',
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            {DevataName} ({blockNumber})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Group 2: 8 Modern Devata */}
                        <div style={{ marginBottom: '40px' }}>
                          {/* Group Title */}
                          <h3 style={{
                            textAlign: 'center',
                            marginBottom: '15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black'
                          }}>
                            8 Modern Devata
                          </h3>

                          {/* Legend Box inside Chart */}
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '10px',
                            padding: '8px',
                            background: '#f8f9fa',
                            borderRadius: '4px',
                            justifyContent: 'center',
                            border: '1px solid #ddd',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '10px', background: '#28a745', border: '1px solid #ECEEF2' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#28a745' }}>AREA</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#d32f2f' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#d32f2f' }}>AVG LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#7cb342' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#7cb342' }}>MAX LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#512da8' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#512da8' }}>MIN LINE</span>
                            </div>
                          </div>

                          {/* Chart Box */}
                          <div style={{
                            border: '2px solid #333',
                            borderRadius: '4px',
                            padding: '15px 15px 60px 50px',
                            background: '#fafafa',
                            position: 'relative',
                            display: 'flex'
                          }}>
                            {/* Y-Axis Line */}
                            <div style={{
                              position: 'absolute',
                              left: '42px',
                              top: '15px',
                              bottom: '60px',
                              width: '1px',
                              background: '#333',
                              opacity: 0.5
                            }}></div>

                            {/* Y-Axis Scale */}
                            <div style={{
                              position: 'absolute',
                              left: '15px',
                              top: '15px',
                              bottom: '60px',
                              width: '30px',
                              display: 'flex',
                              flexDirection: 'column-reverse',
                              justifyContent: 'space-between',
                              alignItems: 'flex-end',
                              paddingRight: '8px'
                            }}>
                              {(() => {
                                const group8Data = layerState.groupedData.group8;
                                const maxVal = Math.max(...group8Data.map(i => parseFloat(i.area)));
                                const rs = maxVal / 5, mg = Math.pow(10, Math.floor(Math.log10(rs || 1))), nr = rs / mg;
                                const ns = nr <= 1 ? mg : nr <= 2 ? 2*mg : nr <= 5 ? 5*mg : 10*mg;
                                const niceMax = Math.ceil((maxVal || ns) / ns) * ns;
                                const ticks = Array.from({ length: Math.round(niceMax / ns) + 1 }, (_, i) => i * ns);
                                return ticks.map((v, idx) => (
                                  <div key={`scale-g2-${idx}`} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666', textAlign: 'right' }}>
                                    {Number.isInteger(v) ? v : v.toFixed(1)}
                                  </div>
                                ));
                              })()}
                            </div>

                            {/* Chart Content */}
                            <div style={{
                              display: 'flex',
                              gap: '5px',
                              alignItems: 'flex-end',
                              height: '180px',
                              paddingBottom: '0px',
                              justifyContent: 'center',
                              flex: 1
                            }}>
                              {(() => {
                                const group8Data = layerState.groupedData.group8;
                                const maxVal = Math.max(...group8Data.map(i => parseFloat(i.area)));
                                const minVal = Math.min(...group8Data.map(i => parseFloat(i.area)));
                                const avgVal = group8Data.reduce((sum, i) => sum + parseFloat(i.area), 0) / group8Data.length;
                                const maxLine = (maxVal + avgVal) / 2;
                                const minLine = (minVal + avgVal) / 2;
                                const _rs8 = maxVal/5, _mg8 = Math.pow(10,Math.floor(Math.log10(_rs8||1))), _nr8 = _rs8/_mg8;
                                const _ns8 = _nr8<=1?_mg8:_nr8<=2?2*_mg8:_nr8<=5?5*_mg8:10*_mg8;
                                const niceMax8 = Math.ceil((maxVal||_ns8)/_ns8)*_ns8;

                                const chartHeightVal = 180;

                                return (
                                  <>
                                    {/* Reference Lines */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: `${chartHeightVal}px`,
                                      pointerEvents: 'none'
                                    }}>
                                      {/* Max Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - maxLine / niceMax8)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#7cb342',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Avg Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - avgVal / niceMax8)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#d32f2f',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Min Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - minLine / niceMax8)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#512da8',
                                        opacity: 0.7
                                      }}></div>
                                    </div>

                                    {group8Data.map((item, idx) => {
                                      const BarHeight = (parseFloat(item.area) / niceMax8) * chartHeightVal;
                                      const blockNumber = idx + 6;
                                      const DevataName = Devata_NAMES.find(d => d.blockNo === blockNumber)?.name || 'N/A';
                                      
                                      return (
                                        <div key={`g2-${idx}`} style={{ 
                                          flex: '0 0 auto',
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          alignItems: 'center',
                                          width: '22px',
                                          position: 'relative'
                                        }}>
                                          <div style={{
                                            width: '22px',
                                            height: `${BarHeight}px`,
                                            background: '#28a745',
                                            border: '0.5px solid #333',
                                            opacity: 0.85,
                                            position: 'relative',
                                            fontSize: '7px',
                                            color: '#fff',
                                            fontWeight: 'bold'
                                          }}>
                                            {(() => {
                                              const lbl = fmt(item.area);
                                              const fits = BarHeight >= String(lbl).length * 4.3 + 6;
                                              return (
                                                (() => {
                                                  const vl = verticalLabelImage(String(lbl), fits ? '#ffffff' : '#111111', 7);
                                                  return (
                                                    <img alt="" src={vl.url} style={{ position: 'absolute', left: `calc(50% - ${vl.w / 2}px)`, bottom: fits ? '2px' : 'calc(100% + 3px)', width: vl.w, height: vl.h, pointerEvents: 'none' }} />
                                                  );
                                                  })()
                                              );
                                            })()}
                                          </div>
                                          
                                          {/* Devata Name - Rotated 310 Degrees */}
                                          <div style={{
                                            position: 'absolute',
                                            bottom: '-45px',
                                            left: '50%',
                                            transform: 'translateX(-50%) rotate(90deg)',
                                            transformOrigin: 'center',
                                            fontSize: '7px',
                                            fontWeight: '500',
                                            color: '#333',
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            {DevataName} ({blockNumber})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Group 3: 32 Modern Devata */}
                        <div style={{ marginBottom: '20px' }}>
                          {/* Group Title */}
                          <h3 style={{
                            textAlign: 'center',
                            marginBottom: '15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black'
                          }}>
                            32 Modern Devata
                          </h3>

                          {/* Legend Box inside Chart */}
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '10px',
                            padding: '8px',
                            background: '#f8f9fa',
                            borderRadius: '4px',
                            justifyContent: 'center',
                            border: '1px solid #ddd',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '10px', background: '#00A19D', border: '1px solid #ECEEF2' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#00A19D' }}>AREA</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#d32f2f' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#d32f2f' }}>AVG LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#7cb342' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#7cb342' }}>MAX LINE</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ width: '10px', height: '2px', background: '#512da8' }}></div>
                              <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#512da8' }}>MIN LINE</span>
                            </div>
                          </div>

                          {/* Chart Box */}
                          <div style={{
                            border: '2px solid #333',
                            borderRadius: '4px',
                            padding: '15px 15px 60px 50px',
                            background: '#fafafa',
                            position: 'relative',
                            display: 'flex'
                          }}>
                            {/* Y-Axis Line */}
                            <div style={{
                              position: 'absolute',
                              left: '42px',
                              top: '15px',
                              bottom: '60px',
                              width: '1px',
                              background: '#333',
                              opacity: 0.5
                            }}></div>

                            {/* Y-Axis Scale */}
                            <div style={{
                              position: 'absolute',
                              left: '15px',
                              top: '15px',
                              bottom: '60px',
                              width: '30px',
                              display: 'flex',
                              flexDirection: 'column-reverse',
                              justifyContent: 'space-between',
                              alignItems: 'flex-end',
                              paddingRight: '8px'
                            }}>
                              {(() => {
                                const group32Data = layerState.groupedData.group32;
                                const maxVal = Math.max(...group32Data.map(i => parseFloat(i.area)));
                                const rs = maxVal / 5, mg = Math.pow(10, Math.floor(Math.log10(rs || 1))), nr = rs / mg;
                                const ns = nr <= 1 ? mg : nr <= 2 ? 2*mg : nr <= 5 ? 5*mg : 10*mg;
                                const niceMax = Math.ceil((maxVal || ns) / ns) * ns;
                                const ticks = Array.from({ length: Math.round(niceMax / ns) + 1 }, (_, i) => i * ns);
                                return ticks.map((v, idx) => (
                                  <div key={`scale-g3-${idx}`} style={{ fontSize: '8px', fontWeight: 'bold', color: '#666', textAlign: 'right' }}>
                                    {Number.isInteger(v) ? v : v.toFixed(1)}
                                  </div>
                                ));
                              })()}
                            </div>

                            {/* Chart Content */}
                            <div style={{
                              display: 'flex',
                              gap: '5px',
                              alignItems: 'flex-end',
                              height: '180px',
                              paddingBottom: '0px',
                              justifyContent: 'center',
                              overflowX: 'visible',
                              flex: 1
                            }}>
                              {(() => {
                                const group32Data = layerState.groupedData.group32;
                                const maxVal = Math.max(...group32Data.map(i => parseFloat(i.area)));
                                const minVal = Math.min(...group32Data.map(i => parseFloat(i.area)));
                                const avgVal = group32Data.reduce((sum, i) => sum + parseFloat(i.area), 0) / group32Data.length;
                                const maxLine = (maxVal + avgVal) / 2;
                                const minLine = (minVal + avgVal) / 2;
                                const _rs32 = maxVal/5, _mg32 = Math.pow(10,Math.floor(Math.log10(_rs32||1))), _nr32 = _rs32/_mg32;
                                const _ns32 = _nr32<=1?_mg32:_nr32<=2?2*_mg32:_nr32<=5?5*_mg32:10*_mg32;
                                const niceMax32 = Math.ceil((maxVal||_ns32)/_ns32)*_ns32;

                                const chartHeightVal = 180;

                                return (
                                  <>
                                    {/* Reference Lines */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: `${chartHeightVal}px`,
                                      pointerEvents: 'none'
                                    }}>
                                      {/* Max Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - maxLine / niceMax32)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#7cb342',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Avg Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - avgVal / niceMax32)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#d32f2f',
                                        opacity: 0.7
                                      }}></div>
                                      {/* Min Line */}
                                      <div style={{
                                        position: 'absolute',
                                        top: `${chartHeightVal * (1 - minLine / niceMax32)}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#512da8',
                                        opacity: 0.7
                                      }}></div>
                                    </div>

                                    {group32Data.map((item, idx) => {
                                      const BarHeight = (parseFloat(item.area) / niceMax32) * chartHeightVal;
                                      const blockNumber = idx + 14;
                                      const DevataName = Devata_NAMES.find(d => d.blockNo === blockNumber)?.name || 'N/A';

                                      return (
                                        <div
                                          key={`g3-${idx}`}
                                          style={{
                                            flex: '0 0 auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            width: '16px',
                                            position: 'relative'
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: '16px',
                                              height: `${BarHeight}px`,
                                              background: '#00A19D',
                                              border: '0.5px solid #333',
                                              opacity: 0.85,
                                              position: 'relative',
                                              fontSize: '6px',
                                              color: '#fff',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {(() => {
                                              const lbl = fmt(item.area);
                                              const fits = BarHeight >= String(lbl).length * 3.7 + 6;
                                              return (
                                                (() => {
                                                  const vl = verticalLabelImage(String(lbl), fits ? '#ffffff' : '#111111', 6);
                                                  return (
                                                    <img alt="" src={vl.url} style={{ position: 'absolute', left: `calc(50% - ${vl.w / 2}px)`, bottom: fits ? '2px' : 'calc(100% + 3px)', width: vl.w, height: vl.h, pointerEvents: 'none' }} />
                                                  );
                                                  })()
                                              );
                                            })()}
                                          </div>

                                          {/* Devata Name - Rotated 310 Degrees */}
                                          <div
                                            style={{
                                              position: 'absolute',
                                              bottom: '-45px',
                                              left: '50%',
                                              transform: 'translateX(-50%) rotate(90deg)',
                                              transformOrigin: 'center',
                                              fontSize: '6px',
                                              fontWeight: '500',
                                              color: '#333',
                                              textAlign: 'right',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {DevataName} ({blockNumber})
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {layerId === 'compass-45-2-table' && layerState.groupedTableData && (
                      <div
                        ref={tableContentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          padding: '30px',
                          width: isMobile ? 'auto' : 'fit-content',
                          maxWidth: isMobile ? 'none' : '95vw',
                          position: 'relative'
                        }}
                      >
                        {(() => {
                          const { group4, group8, group32 } = layerState.groupedTableData;

                          const getStats = (items) => {
                            const areas = items.map(i => parseFloat(i.area) || 0);
                            const avg = areas.length > 0 ? areas.reduce((s, v) => s + v, 0) / areas.length : 0;
                            const max = areas.length > 0 ? Math.max(...areas) : 0;  // ?o. 0 hataya
                            const min = areas.length > 0 ? Math.min(...areas) : 0;  // ?o. 0 hataya
                            return { avg, max, min, maxLine: (max + avg) / 2, minLine: (min + avg) / 2 };
                          };

                          const StatsHeader = ({ max, min, avg }) => (
                            <div style={{ display: 'flex', gap: '6px', maxWidth: '760px', margin: '0 auto 8px auto' }}>
                              {[
                                { label: 'MAX', value: max.toFixed(1), bg: '#ffebee', color: '#dc3545' },
                                { label: 'MIN', value: min.toFixed(1), bg: '#e8f5e9', color: '#28a745' },
                                { label: 'AVG', value: avg.toFixed(1), bg: '#f3e5f5', color: '#6f42c1' },
                              ].map(({ label, value, bg, color }) => (
                                <div key={label} style={{ flex: 1, background: bg, border: `1px solid ${color}`, borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '8px', fontWeight: 'bold', color, textTransform: 'uppercase' }}>{label}</div>
                                  <div style={{ fontSize: '11px', fontWeight: 'bold', color }}>{value}</div>
                                </div>
                              ))}
                            </div>
                          );

                          const renderTableGroup = (items, title) => {
                            const { avg, max, min, maxLine, minLine } = getStats(items);
                            const isSmall = items.length <= 8;
                            // Groups this big (the 32-devata group) can't fit one column per
                            // devata within a readable width — the header names overlap. Show
                            // those as a vertical table (one row per devata) instead.
                            const isVertical = items.length > 16;
                            return (
                              <div style={{ marginBottom: '30px' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#000', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>{title}</h4>

                                {/* ?o. Stats header - same 3 colors */}
                                <div style={{ display: 'flex', gap: '6px', maxWidth: '760px', margin: '0 auto 8px auto' }}>
                                  <div style={{ flex: 1, background: '#ffebee', border: '1px solid #dc3545', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#dc3545', textTransform: 'uppercase' }}>MAX Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#dc3545' }}>{max.toFixed(1)}</div>
                                  </div>
                                  <div style={{ flex: 1, background: '#e8f5e9', border: '1px solid #28a745', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#28a745', textTransform: 'uppercase' }}>MIN Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#28a745' }}>{min.toFixed(1)}</div>
                                  </div>
                                  <div style={{ flex: 1, background: '#e3f2fd', border: '1px solid #1565c0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#1565c0', textTransform: 'uppercase' }}>AVG Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1565c0' }}>{avg.toFixed(1)}</div>
                                  </div>
                                </div>

                                {isVertical ? (
                                  <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    <table style={{ borderCollapse: 'collapse', border: '2px solid #333', background: '#fff', width: '100%' }}>
                                      <thead>
                                        <tr style={{ background: '#00A19D', color: '#fff' }}>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>DEVATA NAME</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>AREA</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>AVG AREA</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>MAX LINE</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>MIN LINE</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.map((item, idx) => (
                                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#f8f9fa' : '#ffffff' }}>
                                            <td style={{ padding: '5px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#000' }}>{item.DevataName}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#000' }}>{item.area}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#1565c0', background: '#e3f2fd' }}>{avg.toFixed(1)}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#dc3545', background: '#ffebee' }}>{maxLine.toFixed(1)}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#28a745', background: '#e8f5e9' }}>{minLine.toFixed(1)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                <div style={{ overflowX: 'auto', maxWidth: '760px', margin: '0 auto' }}>
                                  <table style={{ borderCollapse: 'collapse', border: '2px solid #333', background: '#fff', width: '100%', tableLayout: 'fixed' }}>
                                    <tbody>
                                      {/* ?o. Header row - teal */}
                                      <tr style={{ background: '#00A19D', color: '#fff' }}>
                                        {items.map((item, idx) => (
                                          <th key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'left', fontSize: isSmall ? '10px' : '8px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>
                                            {item.DevataName}
                                          </th>
                                        ))}
                                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', border: '1px solid #ECEEF2', width: '100px' }}>DEVATA</th>
                                      </tr>

                                      {/* ?o. AREA row - plain white/gray */}
                                      <tr>
                                        {items.map((item, idx) => (
                                          <td key={idx} style={{ color: '#000', padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: idx % 2 === 0 ? '#f8f9fa' : '#ffffff', fontWeight: 'bold' }}>
                                            {item.area}
                                          </td>
                                        ))}
                                        <td style={{ color: '#000', padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#f8f9fa', border: '1px solid #ECEEF2' }}>AREA</td>
                                      </tr>

                                      {/* ?o. AVG row - blue */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}>
                                            {avg.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e3f2fd', border: '1px solid #ECEEF2', color: '#1565c0' }}>AVG AREA</td>
                                      </tr>

                                      {/* ?o. MAX LINE row - red */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#ffebee', color: '#dc3545', fontWeight: 'bold' }}>
                                            {maxLine.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#ffebee', border: '1px solid #ECEEF2', color: '#dc3545' }}>MAX LINE</td>
                                      </tr>

                                      {/* ?o. MIN LINE row - green */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#e8f5e9', color: '#28a745', fontWeight: 'bold' }}>
                                            {minLine.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e8f5e9', border: '1px solid #ECEEF2', color: '#28a745' }}>MIN LINE</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                )}
                              </div>
                            );
                          };

                          return (
                            <div style={{ opacity: layerState.tableOpacity }}>
                              {renderTableGroup(group4, '4 Modern Devata')}
                              {renderTableGroup(group8, '8 Modern Devata')}
                              {renderTableGroup(group32, '32 Modern Devata')}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {layerId === 'compass-45-table' && isBarChart && layerState.showTable && layerState.groupedTableData45 && (
                      <div
                        ref={tableContentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          padding: '30px',
                          width: isMobile ? 'auto' : 'fit-content',
                          maxWidth: isMobile ? 'none' : '95vw',
                          position: 'relative'
                        }}
                      >
                        {(() => {
                          const { group4, group8, group32 } = layerState.groupedTableData45;

                          const getStats = (items) => {
                            const areas = items.map(i => parseFloat(i.area) || 0);
                            const avg = areas.length > 0 ? areas.reduce((s, v) => s + v, 0) / areas.length : 0;
                            const max = areas.length > 0 ? Math.max(...areas) : 0;  // ?o. 0 hataya
                            const min = areas.length > 0 ? Math.min(...areas) : 0;  // ?o. 0 hataya
                            return { avg, max, min, maxLine: (max + avg) / 2, minLine: (min + avg) / 2 };
                          };
                          const StatsHeader = ({ max, min, avg }) => (
                            <div style={{ display: 'flex', gap: '6px', maxWidth: '760px', margin: '0 auto 8px auto' }}>
                              {[
                                { label: 'MAX', value: max.toFixed(1), bg: '#ffebee', color: '#dc3545' },
                                { label: 'MIN', value: min.toFixed(1), bg: '#e8f5e9', color: '#28a745' },
                                { label: 'AVG', value: avg.toFixed(1), bg: '#f3e5f5', color: '#6f42c1' },
                              ].map(({ label, value, bg, color }) => (
                                <div key={label} style={{ flex: 1, background: bg, border: `1px solid ${color}`, borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '8px', fontWeight: 'bold', color, textTransform: 'uppercase' }}>{label}</div>
                                  <div style={{ fontSize: '11px', fontWeight: 'bold', color }}>{value}</div>
                                </div>
                              ))}
                            </div>
                          );

                          const renderTableGroup = (items, title) => {
                            const { avg, max, min, maxLine, minLine } = getStats(items);
                            const isSmall = items.length <= 8;
                            // Groups this big (the 32-devata group) can't fit one column per
                            // devata within a readable width — the header names overlap. Show
                            // those as a vertical table (one row per devata) instead.
                            const isVertical = items.length > 16;
                            return (
                              <div style={{ marginBottom: '30px' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#000', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>{title}</h4>

                                {/* ?o. Stats header - same 3 colors */}
                                <div style={{ display: 'flex', gap: '6px', maxWidth: '760px', margin: '0 auto 8px auto' }}>
                                  <div style={{ flex: 1, background: '#ffebee', border: '1px solid #dc3545', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#dc3545', textTransform: 'uppercase' }}>MAX Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#dc3545' }}>{max.toFixed(1)}</div>
                                  </div>
                                  <div style={{ flex: 1, background: '#e8f5e9', border: '1px solid #28a745', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#28a745', textTransform: 'uppercase' }}>MIN Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#28a745' }}>{min.toFixed(1)}</div>
                                  </div>
                                  <div style={{ flex: 1, background: '#e3f2fd', border: '1px solid #1565c0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#1565c0', textTransform: 'uppercase' }}>AVG Value</div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1565c0' }}>{avg.toFixed(1)}</div>
                                  </div>
                                </div>

                                {isVertical ? (
                                  <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    <table style={{ borderCollapse: 'collapse', border: '2px solid #333', background: '#fff', width: '100%' }}>
                                      <thead>
                                        <tr style={{ background: '#00A19D', color: '#fff' }}>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>DEVATA NAME</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>AREA</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>AVG AREA</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>MAX LINE</th>
                                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>MIN LINE</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.map((item, idx) => (
                                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#f8f9fa' : '#ffffff' }}>
                                            <td style={{ padding: '5px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#000' }}>{item.DevataName}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#000' }}>{item.area}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#1565c0', background: '#e3f2fd' }}>{avg.toFixed(1)}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#dc3545', background: '#ffebee' }}>{maxLine.toFixed(1)}</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', border: '1px solid #ECEEF2', color: '#28a745', background: '#e8f5e9' }}>{minLine.toFixed(1)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                <div style={{ overflowX: 'auto', maxWidth: '760px', margin: '0 auto' }}>
                                  <table style={{ borderCollapse: 'collapse', border: '2px solid #333', background: '#fff', width: '100%', tableLayout: 'fixed' }}>
                                    <tbody>
                                      {/* ?o. Header row - teal */}
                                      <tr style={{ background: '#00A19D', color: '#fff' }}>
                                        {items.map((item, idx) => (
                                          <th key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'left', fontSize: isSmall ? '10px' : '8px', fontWeight: 'bold', border: '1px solid #ECEEF2' }}>
                                            {item.DevataName}
                                          </th>
                                        ))}
                                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', border: '1px solid #ECEEF2', width: '100px' }}>DEVATA</th>
                                      </tr>

                                      {/* ?o. AREA row - plain white/gray */}
                                      <tr>
                                        {items.map((item, idx) => (
                                          <td key={idx} style={{ color: '#000', padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: idx % 2 === 0 ? '#f8f9fa' : '#ffffff', fontWeight: 'bold' }}>
                                            {item.area}
                                          </td>
                                        ))}
                                        <td style={{ color: '#000', padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#f8f9fa', border: '1px solid #ECEEF2' }}>AREA</td>
                                      </tr>

                                      {/* ?o. AVG row - blue */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}>
                                            {avg.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e3f2fd', border: '1px solid #ECEEF2', color: '#1565c0' }}>AVG AREA</td>
                                      </tr>

                                      {/* ?o. MAX LINE row - red */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#ffebee', color: '#dc3545', fontWeight: 'bold' }}>
                                            {maxLine.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#ffebee', border: '1px solid #ECEEF2', color: '#dc3545' }}>MAX LINE</td>
                                      </tr>

                                      {/* ?o. MIN LINE row - green */}
                                      <tr>
                                        {items.map((_, idx) => (
                                          <td key={idx} style={{ padding: isSmall ? '6px 2px' : '4px 1px', textAlign: 'center', fontSize: isSmall ? '9px' : '8px', border: '1px solid #ECEEF2', background: '#e8f5e9', color: '#28a745', fontWeight: 'bold' }}>
                                            {minLine.toFixed(1)}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', background: '#e8f5e9', border: '1px solid #ECEEF2', color: '#28a745' }}>MIN LINE</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                )}
                              </div>
                            );
                          };

                          return (
                            <div style={{ opacity: layerState.tableOpacity }}>
                              {renderTableGroup(group4, '4 Vedic Devata')}
                              {renderTableGroup(group8, '8 Vedic Devata')}
                              {renderTableGroup(group32, '32 Vedic Devata')}
                            </div>
                          );
                        })()}
                      </div>
                    )}



                    {isBarChart && data.length > 0 && layerState.showTable && 
                      layerId !== 'compass-45-chart' && layerId !== 'compass-45-table' && layerId !== 'compass-45-2-chart' && layerId !== 'compass-45-2-table' && (
                      <div 
                        ref={tableContentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          padding: '30px',
                          width: isMobile ? 'auto' : 'fit-content',
                          maxWidth: isMobile ? 'none' : '95vw',
                          position: 'relative'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: '20px',
                          marginBottom: '15px',
                          padding: '10px',
                          background: '#ffffff',
                          borderRadius: '4px',
                          justifyContent: 'center',
                        }}>
                         {layerState.groupedTableData ? (
                          <></>
                          ) : (
                            // ?o. 8, 16, 32 compass ??" overall stats
                            <>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#dc3545' }}>MAX line</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#dc3545' }}>{fmt(maxLine)}</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#28a745' }}>MIN line</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>{fmt(minLine)}</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6f42c1' }}>AVG AREA{_needsConvert ? ' (cm²)' : ''}</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#6f42c1' }}>{fmt(avgValue)}</div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* {(layerId === 'Bar-chart-45' || layerId === 'Bar-chart-45-2' || layerId === 'compass-45-2-table' || layerId === 'compass-45-2-chart') && render45Table()} */}
                        {layerId === 'Bar-chart-32' && render32Table()}
                        {/* {layerId === 'Bar-chart-16' && renderBottomTable()} */}
                        {(layerId === 'Bar-chart-16' || layerId === 'Bar-chart-8') && renderBottomTable()}
                      </div>
                    )}

                    {/* BAR CHART CONTENT */}
                    {isBarChart && data.length > 0 && layerState.showBarChart && layerId !== 'compass-45-2-chart' && layerId !== 'compass-45-chart' && (
                      <div
                        ref={contentRef}
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          padding: '30px',
                          width: isMobile ? 'auto' : 'fit-content',
                          maxWidth: isMobile ? 'none' : '95vw',
                          position: 'relative'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: '20px',
                          marginBottom: '15px',
                          padding: '10px',
                          background: '#f8f9fa',
                          borderRadius: '4px',
                          justifyContent: 'center',
                          border: '2px solid #333'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#dc3545' }}>MAX line</div>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#dc3545' }}>{fmt(maxLine)}</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#28a745' }}>MIN line</div>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>{fmt(minLine)}</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6f42c1' }}>AVG AREA{_needsConvert ? ' (cm²)' : ''}</div>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#6f42c1' }}>{fmt(avgValue)}</div>
                          </div>
                        </div>

                        <div style={{
                          position: 'relative',
                          width: chartWidth,
                          height: chartHeight,
                          border: '2px solid #333',
                          background: '#f8f9fa',
                          padding: '0px 0px 35px 42px',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          gap: layerId === 'Bar-chart-16' ? '4px' : '0px',
                          marginBottom: '0'
                        }}>
                          {/* Left Y-axis meter */}
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 50,
                            bottom: 35,
                            width: 40,
                            borderRight: '2px solid #333',
                            zIndex: 2,
                          }}>
                            {[...niceDisplayTicks].reverse().map((tick, i) => (
                              <div key={i} style={{
                                position: 'absolute',
                                right: 0,
                                top: `${(1 - tick / niceMaxDisplay) * 100}%`,
                                transform: 'translateY(-50%)',
                                fontSize: '9px',
                                color: '#333',
                                paddingRight: '4px',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold',
                              }}>
                                {fmt(tick)}
                                <span style={{
                                  display: 'inline-block',
                                  width: 4,
                                  height: 1,
                                  background: '#333',
                                  marginLeft: 2,
                                  verticalAlign: 'middle',
                                }} />
                              </div>
                            ))}
                          </div>
                          {/* Horizontal scale grid lines behind bars */}
                          {niceDisplayTicks.map((tick, i) => (
                            <div key={`grid-${i}`} style={{
                              position: 'absolute',
                              left: 42,
                              right: 0,
                              height: '1px',
                              borderTop: '1px dashed #bbb',
                              top: `${50 + ((chartHeight - 85) * (1 - tick / niceMaxDisplay))}px`,
                              zIndex: 0,
                              pointerEvents: 'none'
                            }} />
                          ))}
                          <div style={{
                            position: 'absolute',
                            left: 42,
                            right: 0,
                            height: '2px',
                            background: '#4da9c8',
                            top: `${50 + ((chartHeight - 85) * (1 - maxLine / niceMaxDisplay))}px`,
                            opacity: 0.6,
                            zIndex: 1
                          }} />
                          <div style={{
                            position: 'absolute',
                            left: 42,
                            right: 0,
                            height: '2px',
                            background: '#00A19D',
                            top: `${50 + ((chartHeight - 85) * (1 - minLine / niceMaxDisplay))}px`,
                            opacity: 0.6,
                            zIndex: 1
                          }} />
                          <div style={{
                            position: 'absolute',
                            left: 42,
                            right: 0,
                            height: '2px',
                            background: '#28a745',
                            top: `${50 + ((chartHeight - 85) * (1 - parseFloat(avgValue) / niceMaxDisplay))}px`,
                            opacity: 0.6,
                            zIndex: 1
                          }} />

                          {data.map((item, idx) => {
                            const BarValue = parseFloat(item.area) || 0;
                            const BarHeight = (BarValue / niceMaxDisplay) * (chartHeight - 85);
                            const BarColor = getBarColor(item.direction);
                            // ~4.3px per glyph at 7px bold + a little padding. When the bar is
                            // too short to hold its own value, the value is drawn just above
                            // the bar (dark) instead of being hidden, so the smallest zone
                            // still shows its area.
                            const barLabel = fmt(BarValue);
                            const labelFits = BarHeight >= String(barLabel).length * 4.3 + 6;

                            return (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  flex: `0 0 ${BarMaxWidth}px`,
                                  position: 'relative',
                                  zIndex: 2
                                }}
                              >
                                <div
                                  style={{
                                    width: '100%',
                                    height: BarHeight,
                                    background: BarColor,
                                    border: '1px solid #ECEEF2',
                                    opacity: layerState.chartOpacity,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '7px',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    boxSizing: 'border-box',
                                    overflow: 'visible',
                                    position: 'relative'
                                  }}
                                >
                                  {/* Reads bottom → top, anchored to the bottom of the bar
                                      (rotate(-90deg) around the bottom-left corner — plain
                                      transform, so it also survives the html2canvas capture,
                                      unlike writing-mode). */}
                                  {(() => {
                                    const vl = verticalLabelImage(String(barLabel), labelFits ? '#ffffff' : '#111111', 7);
                                    return (
                                      <img alt="" src={vl.url} style={{ position: 'absolute', left: `calc(50% - ${vl.w / 2}px)`, bottom: labelFits ? '3px' : 'calc(100% + 3px)', width: vl.w, height: vl.h, pointerEvents: 'none' }} />
                                    );
                                  })()}
                                </div>
                                <div style={{
                                  position: 'absolute',
                                  bottom: '-32px',
                                  fontSize: '9px',
                                  textAlign: 'center',
                                  width: '100%',
                                  color: '#000',
                                  lineHeight: '1.1',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {(layerId === 'Bar-chart-45' || layerId === 'Bar-chart-45-2' || layerId === 'compass-45-2-table' || layerId === 'compass-45-2-chart') ? `B${item.blockNumber}` : dirLabel(item.direction)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {isGrid && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',  // ?o. CHANGED from '700px'
                        maxWidth: isMobile ? 'none' : '95vw',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        margin: '0 auto'  // ?o. ADDED for centering
                      }}>
                        <h3 style={{ 
                          textAlign: 'center', 
                          marginTop: 0, 
                          marginBottom: '20px', 
                          color: '#000',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {layerName}
                        </h3>

                        {/* ?o. A4 dimensions - CENTERED */}
                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            display: 'block',  // ?o. ADDED
                            margin: '0 auto'   // ?o. ADDED for centering
                          }}
                          onClick={handleCanvasClick}
                        >
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                           <Layer>
                            {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                            </Layer>

                          {/* ?o. Grid Layer */}
                          {layerState.showGrid && isCalibrationSet && pixelsPerUnit && (() => {
                            const calibration = measurements.find(m => m.isCalibration);
                            if (!calibration) return null;
                            
                            const baseBoxCount = Math.round(calibration.realDistance);
                            const baseBoxSize = calibration.pixelDistance / baseBoxCount;
                            const boxSizeX = baseBoxSize * (layerState.gridRatioX || 1);
                            const boxSizeY = baseBoxSize * (layerState.gridRatioY || 1);
                            
                            const numBoxesX = Math.ceil(A4_WIDTH / boxSizeX) + 1;
                            const numBoxesY = Math.ceil(A4_HEIGHT / boxSizeY) + 1;
                            
                            const gridLineColor = layerState.gridColor || '#00A19D';
                            const gridLineOpacity = layerState.gridOpacity || 0.5;
                            
                            return (
                              <Layer>
                                {/* Vertical Lines */}
                                {Array.from({ length: numBoxesX }).map((_, i) => {
                                  const x = i * boxSizeX;
                                  if (x <= A4_WIDTH) {
                                    return (
                                      <Line
                                        key={`v-${i}`}
                                        points={[x, 0, x, A4_HEIGHT]}
                                        stroke={gridLineColor}
                                        strokeWidth={1}
                                        opacity={gridLineOpacity}
                                        listening={false}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                                
                                {/* Horizontal Lines */}
                                {Array.from({ length: numBoxesY }).map((_, i) => {
                                  const y = i * boxSizeY;
                                  if (y <= A4_HEIGHT) {
                                    return (
                                      <Line
                                        key={`h-${i}`}
                                        points={[0, y, A4_WIDTH, y]}
                                        stroke={gridLineColor}
                                        strokeWidth={1}
                                        opacity={gridLineOpacity}
                                        listening={false}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                              </Layer>
                            );
                          })()}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={Math.min(Math.max(point.x, 0), A4_WIDTH)}
                                  y={Math.min(Math.max(point.y, 0), A4_HEIGHT)}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const boundedP1 = { 
                                  x: Math.min(Math.max(p1.x, 0), A4_WIDTH), 
                                  y: Math.min(Math.max(p1.y, 0), A4_HEIGHT) 
                                };
                                const boundedP2 = { 
                                  x: Math.min(Math.max(p2.x, 0), A4_WIDTH), 
                                  y: Math.min(Math.max(p2.y, 0), A4_HEIGHT) 
                                };
                                
                                const midX = (boundedP1.x + boundedP2.x) / 2;
                                const midY = (boundedP1.y + boundedP2.y) / 2;
                                
                                const angleRad = Math.atan2(boundedP2.y - boundedP1.y, boundedP2.x - boundedP1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = Math.min(Math.max(midX + Math.cos(angleRad + Math.PI/2) * labelOffset, 20), A4_WIDTH - 20);
                                const labelY = Math.min(Math.max(midY + Math.sin(angleRad + Math.PI/2) * labelOffset, 20), A4_HEIGHT - 20);
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group key={measurement.id}>
                                    <Line
                                      points={[boundedP1.x, boundedP1.y, boundedP2.x, boundedP2.y]}
                                      stroke={layerState.compassColor || "#000000"}
                                      strokeWidth={layerState.compassLineWeight || 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill="#000000"
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                        width={80}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: Math.min(Math.max(text.y - 50, 0), A4_HEIGHT - 100) + 'px',
                                      left: Math.min(Math.max(text.x, 0), A4_WIDTH - 100) + 'px',
                                      zIndex: 10000,
                                      maxWidth: `${A4_WIDTH - 80}px`
                                    }
                                  }}
                                >
                                  {/* Formatting ToolBar */}
                                  <div style={{
                                    background: '#00A19D',
                                    border: '1px solid #ECEEF2',
                                    borderRadius: '4px',
                                    padding: '8px',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                    maxWidth: `${A4_WIDTH - 80}px`,
                                    flexWrap: 'wrap'
                                  }}>
                                    {/* Bold Button */}
                                    <button
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => toggleBold(text.id)}
                                      style={{
                                        background: text.fontStyle === 'bold' ? '#00A19D' : '#444',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}
                                      title="Bold"
                                    >
                                      B
                                    </button>

                                    {/* Italic Button */}
                                    <button
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => toggleItalic(text.id)}
                                      style={{
                                        background: text.fontStyle === 'italic' ? '#00A19D' : '#444',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontStyle: 'italic',
                                        fontSize: '12px'
                                      }}
                                      title="Italic"
                                    >
                                      I
                                    </button>

                                    {/* Color Picker */}
                                    <input
                                      onMouseDown={(e) => e.preventDefault()}
                                      type="color"
                                      value={text.fill}
                                      onChange={(e) => handleTextFormat(text.id, 'fill', e.target.value)}
                                      style={{
                                        width: '30px',
                                        height: '24px',
                                        border: '1px solid #00A19D',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                      }}
                                      title="Text Color"
                                    />

                                    {/* Close/Done Button */}
                                    <button
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={handleTextEditEnd}
                                      style={{
                                        background: '#28a745',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '4px 12px',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginLeft: 'auto'
                                      }}
                                    >
                                      Done
                                    </button>
                                  </div>

                                  {/* Textarea */}
                                  <textarea
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      fontStyle: text.fontStyle || 'normal',
                                      fontWeight: text.fontStyle === 'bold' ? 'bold' : 'normal',
                                      color: text.fill,
                                      border: '1px solid #ECEEF2',
                                      padding: '8px',
                                      background: '#fff',
                                      borderRadius: '4px',
                                      minWidth: '200px',
                                      maxWidth: `${A4_WIDTH - 100}px`,
                                      minHeight: '60px',
                                      resize: 'both',
                                      outline: 'none',
                                      boxShadow: '0 4px 12px rgba(255, 107, 92, 0.3)',
                                      boxSizing: 'border-box'
                                    }}
                                    placeholder="Enter text..."
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={Math.min(Math.max(text.x, 0), A4_WIDTH)}
                                  y={Math.min(Math.max(text.y, 0), A4_HEIGHT)}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fontStyle={text.fontStyle || 'normal'}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => {
                                    const newX = Math.min(Math.max(e.target.x(), 0), A4_WIDTH);
                                    const newY = Math.min(Math.max(e.target.y(), 0), A4_HEIGHT);
                                    e.target.x(newX);
                                    e.target.y(newY);
                                    handleTextDragEnd(text.id, e);
                                  }}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'pointer';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                  width={A4_WIDTH - 20}
                                  wrap="word"
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}

                    {isCompass && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        // Mobile: 'fit-content' + a vw-based maxWidth was
                        // fighting the mobile fit-scale wrapper above — vw
                        // is measured against the real viewport regardless
                        // of the ancestor's CSS transform, so this card kept
                        // clamping itself far narrower than its own 794px
                        // Stage child, which just overflowed past it
                        // uncontained (no overflow:hidden here) — the
                        // mismatched "narrow card, wide floor plan poking
                        // out" seen on phone. The outer wrapper already
                        // fits everything to the viewport, so on mobile this
                        // card just sizes naturally to its child instead.
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff'
                          }}
                          onClick={handleCanvasClick}
                          onMouseMove={handleDevta45Hover}
                          onMouseLeave={hideDevta45Tip}
                        >
                          <Layer>
                            {!['elemental-5-chart'].includes(layerId) && isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}

                            {!['elemental-5-chart'].includes(layerId) && (
                              <FloorPlanImage 
                                src={baseImage} 
                                canvasWidth={A4_WIDTH}
                                canvasHeight={A4_HEIGHT}
                                rotation={0}
                                opacity={1}
                                scale={1}
                              />
                            )}
                          </Layer>

                          {layerId === 'compass-8' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const boundaryAngles = [
                                  22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5,
                                ];

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. CLIPPED SECTORS - sirf jab compassRotation === 0 */}
                                    {!hasParentRotation && boundaryAngles.map((angle, index) => {
                                      const nextAngle = boundaryAngles[(index + 1) % boundaryAngles.length];
                                      const startAngleDeg = angle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                      const endAngleDeg = nextAngle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);

                                        return (
                                          <Line
                                            key={`compass-8-sector-${index}-ring-${ringIndex}`}
                                            points={flatPoints}
                                            closed
                                            fill="transparent"
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?o. DIRECTION LINES - sirf jab compassRotation > 0 */}
                                    {hasParentRotation && layerState.showCompassLines && (() => {
                                      return boundaryAngles.map((angle, index) => {
                                        const adjustedAngle = angle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                        const intersection = findPolygonIntersection ?
                                          findPolygonIntersection(compassCenter, adjustedAngle) :
                                          calculateIntersection(compassCenter, adjustedAngle);
                                        if (!intersection) return null;

                                        return (
                                          <Line
                                            key={`compass-boundary-${index}`}
                                            points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })()}

                                    {/* ?o. Area Labels */}
                                    {layerState.showSectorAreas && (() => {
                                      const sectorAreas = [
                                        { centerAngle: 0, area: 'North' },
                                        { centerAngle: 45, area: 'North-East' },
                                        { centerAngle: 90, area: 'East' },
                                        { centerAngle: 135, area: 'South-East' },
                                        { centerAngle: 180, area: 'South' },
                                        { centerAngle: 225, area: 'South-West' },
                                        { centerAngle: 270, area: 'West' },
                                        { centerAngle: 315, area: 'North-West' },
                                      ];

                                      return sectorAreas.map(({ centerAngle, area }, index) => {
                                        // centerAngle (0, 45, 90...) is already the midpoint of its 45°
                                        // sector (boundary lines sit at the odd 22.5° multiples on either
                                        // side) — adding another +22.5° here pushed the label off-centre
                                        // onto a boundary line instead of the middle of the zone.
                                        const adjustedAngle = centerAngle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);

                                        const intersection = findPolygonIntersection ?
                                          findPolygonIntersection(compassCenter, adjustedAngle) :
                                          calculateIntersection(compassCenter, adjustedAngle);

                                        if (!intersection) return null;

                                        const dirX = intersection.x - compassCenter.x;
                                        const dirY = intersection.y - compassCenter.y;
                                        const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (distance < 5) return null;

                                        const labelDistance = distance * 1.15;
                                        const labelX = compassCenter.x + (dirX / distance) * labelDistance;
                                        const labelY = compassCenter.y + (dirY / distance) * labelDistance;

                                        const getLabelColor = (areaName) => {
                                          switch (areaName) {
                                            case 'North': case 'North-East': return '#4da9c8';
                                            case 'East': return '#28a745';
                                            case 'South-East': case 'South': return '#DC3545';
                                            case 'South-West': return '#FFD700';
                                            case 'West': case 'North-West': return '#6c757d';
                                            default: return '#00A19D';
                                          }
                                        };

                                        const textWidth = area.length * 8;

                                        return (
                                          <Text
                                            key={`label-8-${area}-${index}`}
                                            x={labelX}
                                            y={labelY}
                                            text={area}
                                            fontSize={14}
                                            fontStyle="bold"
                                            fill={getLabelColor(area)}
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={textWidth / 2}
                                            offsetY={7}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })()}

                                    {/* ?o. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={layerState.compassColor || '#000000'}
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}
                          {layerId === 'MANDAL' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const boundaryAngles = [
                                  22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5,
                                ];

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. CLIPPED SECTORS - sirf jab compassRotation === 0 */}
                                    {!hasParentRotation && boundaryAngles.map((angle, index) => {
                                      const nextAngle = boundaryAngles[(index + 1) % boundaryAngles.length];
                                      const startAngleDeg = angle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                      const endAngleDeg = nextAngle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);

                                        return (
                                          <Line
                                            key={`compass-8-sector-${index}-ring-${ringIndex}`}
                                            points={flatPoints}
                                            closed
                                            fill="transparent"
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?o. DIRECTION LINES - sirf jab compassRotation > 0 */}
                                    {hasParentRotation && layerState.showCompassLines && (() => {
                                      return boundaryAngles.map((angle, index) => {
                                        const adjustedAngle = angle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                        const intersection = findPolygonIntersection ?
                                          findPolygonIntersection(compassCenter, adjustedAngle) :
                                          calculateIntersection(compassCenter, adjustedAngle);
                                        if (!intersection) return null;

                                        return (
                                          <Line
                                            key={`compass-boundary-${index}`}
                                            points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })()}

                                    {/* ?o. Area Labels */}
                                    {layerState.showSectorAreas && (() => {
                                      const sectorAreas = [
                                        { centerAngle: 0, area: 'North', mandal: 'SOMA' },
                                        { centerAngle: 45, area: 'North-East', mandal: 'JAL' },
                                        { centerAngle: 90, area: 'East', mandal: 'SURYA' },
                                        { centerAngle: 135, area: 'South-East', mandal: 'AGNI' },
                                        { centerAngle: 180, area: 'South', mandal: 'YAMA' },
                                        { centerAngle: 225, area: 'South-West', mandal: 'AKASH' },
                                        { centerAngle: 270, area: 'West', mandal: 'VARUN' },
                                        { centerAngle: 315, area: 'North-West', mandal: 'VAYU' },
                                      ];

                                      return sectorAreas.map(({ centerAngle, area, mandal }, index) => {
                                        const adjustedAngle = centerAngle - 90 - (parentCompassRotation || 0) + (layerState.compassRotation || 0);

                                        const intersection = findPolygonIntersection ?
                                          findPolygonIntersection(compassCenter, adjustedAngle) :
                                          calculateIntersection(compassCenter, adjustedAngle);

                                        if (!intersection) return null;

                                        const dirX = intersection.x - compassCenter.x;
                                        const dirY = intersection.y - compassCenter.y;
                                        const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (distance < 5) return null;

                                        // Direction label ??" bahar (1.15x)
                                        const dirLabelDistance = distance * 1.15;
                                        const dirLabelX = compassCenter.x + (dirX / distance) * dirLabelDistance;
                                        const dirLabelY = compassCenter.y + (dirY / distance) * dirLabelDistance;

                                        // Mandal label ??" andar (0.5x)
                                        const mandalLabelDistance = distance * 0.5;
                                        const mandalLabelX = compassCenter.x + (dirX / distance) * mandalLabelDistance;
                                        const mandalLabelY = compassCenter.y + (dirY / distance) * mandalLabelDistance;

                                        const getLabelColor = (areaName) => {
                                          switch (areaName) {
                                            case 'North': case 'North-East': return '#4da9c8';
                                            case 'East': return '#28a745';
                                            case 'South-East': case 'South': return '#DC3545';
                                            case 'South-West': return '#FFD700';
                                            case 'West': case 'North-West': return '#6c757d';
                                            default: return '#00A19D';
                                          }
                                        };

                                        const labelColor = getLabelColor(area);
                                        const dirTextWidth = area.length * 8;
                                        const mandalTextWidth = mandal.length * 8;

                                        return (
                                          <React.Fragment key={`label-mandal-${area}-${index}`}>
                                            {/* Direction label ??" boundary ke bahar */}
                                            <Text
                                              x={dirLabelX}
                                              y={dirLabelY}
                                              text={area}
                                              fontSize={12}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={dirTextWidth / 2}
                                              offsetY={6}
                                              listening={false}
                                            />
                                            {/* Mandal label ??" sector ke andar */}
                                            <Text
                                              x={mandalLabelX}
                                              y={mandalLabelY}
                                              text={mandal}
                                              fontSize={12}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={mandalTextWidth / 2}
                                              offsetY={6}
                                              listening={false}
                                            />
                                          </React.Fragment>
                                        );
                                      });
                                    })()}

                                    {/* ?o. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={layerState.compassColor || '#000000'}
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}
                          {layerId === 'RATNA-STHAPNA' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const boundaryAngles = [22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5];
                                const sectorCenterAngles = [0, 45, 90, 135, 180, 225, 270, 315];

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;
                                const compassOpacity = layerState.compassOpacity || 0.8;
                                const compassLineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;
                                const compassColor = layerState.compassColor || '#000000';
                                const compassLineWeight = layerState.compassLineWeight || 2;
                                const compassRotation = layerState.compassRotation || 0;

                                // ?o. Boundary pe intersection point nikalo
                                const getBoundaryPoint = (angleDeg) => {
                                  const adjusted = angleDeg - 90 - (parentCompassRotation || 0) + compassRotation;
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, adjusted)
                                    : calculateIntersection(compassCenter, adjusted);
                                };

                                // ?o. Ring point ??" boundary direction se ratio pe
                                const getRingPoint = (angleDeg, ratio) => {
                                  const bp = getBoundaryPoint(angleDeg);
                                  if (!bp) return null;
                                  return {
                                    x: compassCenter.x + (bp.x - compassCenter.x) * ratio,
                                    y: compassCenter.y + (bp.y - compassCenter.y) * ratio,
                                  };
                                };

                                // ?o. Pentagon shape draw karne ka function (5-sided)
                                const getPentagonPoints = (cx, cy, size) => {
                                  const pts = [];
                                  for (let i = 0; i < 5; i++) {
                                    const angle = (i * 72 - 90) * (Math.PI / 180);
                                    pts.push(cx + size * Math.cos(angle));
                                    pts.push(cy + size * Math.sin(angle));
                                  }
                                  return pts;
                                };

                                // ?o. Star shape draw karne ka function (5-pointed)
                                const getStarPoints = (cx, cy, outerR, innerR) => {
                                  const pts = [];
                                  for (let i = 0; i < 10; i++) {
                                    const angle = (i * 36 - 90) * (Math.PI / 180);
                                    const r = i % 2 === 0 ? outerR : innerR;
                                    pts.push(cx + r * Math.cos(angle));
                                    pts.push(cy + r * Math.sin(angle));
                                  }
                                  return pts;
                                };

                                // ?o. Image se match ??" 8 directions ke numbers
                                // Boundary (100%) pe: corners = 7,9,3,5 (top-left, top-right, bottom-right, bottom-left)
                                //                     cardinals = 8,2,4,6 (top, right, bottom, left)
                                // Image match: NW=7, N=8, NE=9, E=2, SE=3, S=4, SW=5, W=6
                                const boundarySymbols = [
                                  { angle: 315, num: 7, isCorner: true  }, // NW ??" top-left
                                  { angle: 0,   num: 8, isCorner: false }, // N  ??" top
                                  { angle: 45,  num: 9, isCorner: true  }, // NE ??" top-right
                                  { angle: 90,  num: 2, isCorner: false }, // E  ??" right  (image mein E=2)
                                  { angle: 135, num: 3, isCorner: true  }, // SE ??" bottom-right
                                  { angle: 180, num: 4, isCorner: false }, // S  ??" bottom
                                  { angle: 225, num: 5, isCorner: true  }, // SW ??" bottom-left
                                  { angle: 270, num: 6, isCorner: false }, // W  ??" left
                                ];

                                // ?o. 2nd ring (50%) pe: N,E,S,W pe pentagon ??" numbers 8,9,2 etc image se
                                // Image match: N=8, E=3, S=6 (inner ring mein), W=4 ??" ye inner ring numbers hain
                                // Inner ring (50%) cardinals pe: N=8, E=3, S=6, W=4 (image se)
                                // Actually image mein 2nd ring pe: 9,3,7,4,2,6,8 dikhte hain ??" sab 8 directions pe
                                // Simple: 50% ring pe 8 directions pe pentagon with numbers 2-9
                                const ring2Symbols = [
                                  { angle: 315, num: 7 }, // NW
                                  { angle: 0,   num: 8 }, // N
                                  { angle: 45,  num: 9 }, // NE
                                  { angle: 90,  num: 3 }, // E
                                  { angle: 135, num: 5 }, // SE (image se match)
                                  { angle: 180, num: 6 }, // S
                                  { angle: 225, num: 2 }, // SW
                                  { angle: 270, num: 4 }, // W
                                ];

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" 100% */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. INNER RINGS ??" 25%, 50%, 75% ??" boundary parallel (compass-45 technique) */}
                                    {[0.25, 0.50, 0.75].map((ratio, ratioIndex) => {
                                      const steps = 360;
                                      const ringPoints = [];
                                      for (let i = 0; i < steps; i++) {
                                        const angleDeg = (i / steps) * 360;
                                        const pt = getRingPoint(angleDeg, ratio);
                                        if (pt) ringPoints.push(pt);
                                      }
                                      if (ringPoints.length < 3) return null;
                                      return (
                                        <Line
                                          key={`ratna-inner-ring-${ratioIndex}`}
                                          points={ringPoints.flatMap(p => [p.x, p.y])}
                                          closed={true}
                                          stroke={compassColor}
                                          strokeWidth={compassLineWeight * 0.75}
                                          fill="transparent"
                                          opacity={compassLineOpacity * 0.85}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. DIRECTION LINES ??" center se boundary tak, sector center angles pe */}
                                    {layerState.showCompassLines && sectorCenterAngles.map((centerAngle, index) => {
                                      const bp = getBoundaryPoint(centerAngle);
                                      if (!bp) return null;
                                      return (
                                        <Line
                                          key={`ratna-direction-line-${index}`}
                                          points={[compassCenter.x, compassCenter.y, bp.x, bp.y]}
                                          stroke={compassColor}
                                          strokeWidth={compassLineWeight}
                                          opacity={compassLineOpacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. BOUNDARY (100%) SYMBOLS ??" Star ??" 3rd aur 4th ring ke BEECH (87.5%) */}
                                    {boundarySymbols.map(({ angle, num, isCorner }) => {
                                      const pt = getRingPoint(angle, 0.875);  // ??? 75% aur 100% ke beech
                                      if (!pt) return null;

                                    const starPts = getStarPoints(pt.x, pt.y, 16, 7);

                                      return (
                                        <React.Fragment key={`ratna-boundary-sym-${angle}`}>
                                          <Line
                                            points={starPts}
                                            closed={true}
                                            fill="#1a3c8f"
                                            stroke="#1a3c8f"
                                            strokeWidth={0.5}
                                            opacity={1}
                                            listening={false}
                                          />
                                          <Text
                                            x={pt.x}
                                            y={pt.y}
                                            text={`${num}`}
                                            fontSize={9}
                                            fontStyle="bold"
                                            fill="#FFFFFF"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={4}
                                            offsetY={5}
                                            opacity={1}
                                            listening={false}
                                          />
                                        </React.Fragment>
                                      );
                                    })}

                                    {/* ?o. 2nd RING (50%) SYMBOLS ??" Pentagon ??" 1st aur 2nd ring ke BEECH (37.5%) */}
                                    {ring2Symbols.map(({ angle, num }) => {
                                      const pt = getRingPoint(angle, 0.375);  // ??? 25% aur 50% ke beech
                                      if (!pt) return null;

                                    const pentPts = getPentagonPoints(pt.x, pt.y, 14);  // 9 ??' 14

                                      return (
                                        <React.Fragment key={`ratna-ring2-sym-${angle}`}>
                                          <Line
                                            points={pentPts}
                                            closed={true}
                                            fill="#c8922a"
                                            stroke="#a07020"
                                            strokeWidth={0.8}
                                            opacity={1}
                                            listening={false}
                                          />
                                          <Text
                                            x={pt.x}
                                            y={pt.y}
                                            text={`${num}`}
                                            fontSize={8}
                                            fontStyle="bold"
                                            fill="#FFFFFF"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={3.5}
                                            offsetY={4.5}
                                            opacity={1}
                                            listening={false}
                                          />
                                        </React.Fragment>
                                      );
                                    })}

                                    {/* ?o. DIRECTION LABELS ??" boundary ke bahar */}
                                    {layerState.showSectorAreas && (() => {
                                      const sectorAreas = [
                                        { centerAngle: 0,   area: 'North' },
                                        { centerAngle: 45,  area: 'North-East' },
                                        { centerAngle: 90,  area: 'East' },
                                        { centerAngle: 135, area: 'South-East' },
                                        { centerAngle: 180, area: 'South' },
                                        { centerAngle: 225, area: 'South-West' },
                                        { centerAngle: 270, area: 'West' },
                                        { centerAngle: 315, area: 'North-West' },
                                      ];

                                      return sectorAreas.map(({ centerAngle, area }, index) => {
                                        // Offset the label by half a sector (22.5°) so it sits in the
                                        // middle of the area between two direction lines, instead of
                                        // directly on top of one.
                                        const bp = getBoundaryPoint(centerAngle + 22.5);
                                        if (!bp) return null;

                                        const dirX = bp.x - compassCenter.x;
                                        const dirY = bp.y - compassCenter.y;
                                        const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (distance < 5) return null;

                                        const dirLabelDistance = distance * 1.18;
                                        const dirLabelX = compassCenter.x + (dirX / distance) * dirLabelDistance;
                                        const dirLabelY = compassCenter.y + (dirY / distance) * dirLabelDistance;

                                        const getLabelColor = (areaName) => {
                                          switch (areaName) {
                                            case 'North': case 'North-East': return '#4da9c8';
                                            case 'East': return '#28a745';
                                            case 'South-East': case 'South': return '#DC3545';
                                            case 'South-West': return '#FFD700';
                                            case 'West': case 'North-West': return '#6c757d';
                                            default: return '#00A19D';
                                          }
                                        };

                                        const labelColor = getLabelColor(area);
                                        const dirTextWidth = area.length * 8;

                                        return (
                                          <Text
                                            key={`ratna-dir-label-${area}-${index}`}
                                            x={dirLabelX}
                                            y={dirLabelY}
                                            text={area}
                                            fontSize={12}
                                            fontStyle="bold"
                                            fill={labelColor}
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={dirTextWidth / 2}
                                            offsetY={6}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })()}

                                    {/* ?o. CENTER POINT */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={compassColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={compassLineWeight}
                                      opacity={compassLineOpacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          
                          {layerId === 'compass-24' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. CLIPPED SECTORS - sirf jab compassRotation === 0 */}
                                    {!hasParentRotation && directions24.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % directions24.length;
                                      const nextAngle = directions24[nextIndex].angle;
                                      const startAngleDeg = angle - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                      const endAngleDeg = nextAngle - (parentCompassRotation || 0) + (layerState.compassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);
                                        return (
                                          <Line
                                            key={`compass-24-sector-${index}-ring-${ringIndex}`}
                                            points={flatPoints}
                                            closed
                                            fill="transparent"
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?o. DIRECTION LINES - sirf jab compassRotation > 0 */}
                                    {hasParentRotation && layerState.showCompassLines && directions24.map(({ angle, area }, index) => {
                                      const adjustedAngle = angle - (parentCompassRotation || 0) + (layerState.compassRotation || 0);
                                      const intersection = findPolygonIntersection ?
                                        findPolygonIntersection(compassCenter, adjustedAngle) :
                                        calculateIntersection(compassCenter, adjustedAngle);
                                      if (!intersection) return null;
                                      return (
                                        <Line
                                          key={`compass-24-${angle}`}
                                          points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                          stroke={layerState.compassColor || '#000000'}
                                          strokeWidth={layerState.compassLineWeight || 2}
                                          opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Numeric Labels */}
                                    {layerState.showSectorAreas && directions24.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % directions24.length;
                                      const nextAngle = directions24[nextIndex].angle;
                                      let middleAngle = nextAngle < angle ? (angle + nextAngle + 360) / 2 : (angle + nextAngle) / 2;
                                      if (middleAngle >= 360) middleAngle -= 360;
                                      const labelAngle = middleAngle;
                                      const intersection = findPolygonIntersection ?
                                        findPolygonIntersection(compassCenter, labelAngle) :
                                        calculateIntersection(compassCenter, labelAngle);
                                      if (!intersection) return null;
                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;
                                      const labelDistance = distance * 1.08;
                                      const labelX = compassCenter.x + (dirX / distance) * labelDistance;
                                      const labelY = compassCenter.y + (dirY / distance) * labelDistance;
                                      const labelNumber = index + 1;
                                      return (
                                        <Text
                                          key={`label-24-${index}`}
                                          x={labelX}
                                          y={labelY}
                                          text={labelNumber.toString()}
                                          fontSize={16}
                                          fontStyle="bold"
                                          fill="#000000"
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={8}
                                          offsetY={8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={layerState.compassColor || '#000000'}
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === '4-state-image' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const rotation = (layerState.compassRotation || 0) - (parentCompassRotation || 0);

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity = layerState.compassOpacity || 0.8;
                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;

                                const directions16 = [
                                  { label: 'E',   angle: 0     },
                                  { label: 'ESE', angle: 22.5  },
                                  { label: 'SE',  angle: 45    },
                                  { label: 'SSE', angle: 67.5  },
                                  { label: 'S',   angle: 90    },
                                  { label: 'SSW', angle: 112.5 },
                                  { label: 'SW',  angle: 135   },
                                  { label: 'WSW', angle: 157.5 },
                                  { label: 'W',   angle: 180   },
                                  { label: 'WNW', angle: 202.5 },
                                  { label: 'NW',  angle: 225   },
                                  { label: 'NNW', angle: 247.5 },
                                  { label: 'N',   angle: 270   },
                                  { label: 'NNE', angle: 292.5 },
                                  { label: 'NE',  angle: 315   },
                                  { label: 'ENE', angle: 337.5 },
                                ];

                                const lines4 = [
                                  { angle: 0   },
                                  { angle: 90  },
                                  { angle: 180 },
                                  { angle: 270 },
                                ];

                                const quadrantLabels = [
                                  { label: 'COLD', angleStart: 180, angleEnd: 270 },
                                  { label: 'WET',  angleStart: 270, angleEnd: 360 },
                                  { label: 'DRY',  angleStart: 90,  angleEnd: 180 },
                                  { label: 'HOT',  angleStart: 0,   angleEnd: 90  },
                                ];

                                return (
                                  <>
                                    {/* 1. Boundary Line */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* 2. 4 Direction Lines */}
                                    {lines4.map(({ angle }) => {
                                      const point = getIntersection(angle + rotation);
                                      if (!point) return null;
                                      return (
                                        <Line
                                          key={`line-${angle}`}
                                          points={[compassCenter.x, compassCenter.y, point.x, point.y]}
                                          stroke={strokeColor}
                                          strokeWidth={strokeWidth}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 3. 16 Direction Labels */}
                                    {directions16.map(({ label, angle }) => {
                                      const adjustedAngle = angle + rotation;
                                      const intersection = getIntersection(adjustedAngle);
                                      if (!intersection) return null;

                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelDistance = distance * 1.1;
                                      const labelX = compassCenter.x + (dirX / distance) * labelDistance;
                                      const labelY = compassCenter.y + (dirY / distance) * labelDistance;
                                      const offsetX = label.length === 3 ? 14 : 10;

                                      return (
                                        <Text
                                          key={`dir-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={offsetX}
                                          offsetY={8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 4. Quadrant Labels ??" exact center of each box */}
                                    {quadrantLabels.map(({ label, angleStart, angleEnd }) => {
                                      const midAngle = (angleStart + angleEnd) / 2 + rotation;

                                      const p1   = getIntersection(angleStart + rotation);
                                      const p2   = getIntersection(angleEnd + rotation);
                                      const pMid = getIntersection(midAngle);

                                      if (!p1 || !p2 || !pMid) return null;

                                      const centerX = (compassCenter.x + p1.x + pMid.x + p2.x) / 4;
                                      const centerY = (compassCenter.y + p1.y + pMid.y + p2.y) / 4;

                                      return (
                                        <Text
                                          key={`quadrant-${label}`}
                                          x={centerX}
                                          y={centerY}
                                          text={label}
                                          fontSize={13}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={20}
                                          offsetY={8}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 5. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'surya-chandra-bhag' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const rotation = (layerState.compassRotation || 0) - (parentCompassRotation || 0);

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity = layerState.compassOpacity || 0.8;
                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;

                                const directions16 = [
                                  { label: 'E',   angle: 0     },
                                  { label: 'ESE', angle: 22.5  },
                                  { label: 'SE',  angle: 45    },
                                  { label: 'SSE', angle: 67.5  },
                                  { label: 'S',   angle: 90    },
                                  { label: 'SSW', angle: 112.5 },
                                  { label: 'SW',  angle: 135   },
                                  { label: 'WSW', angle: 157.5 },
                                  { label: 'W',   angle: 180   },
                                  { label: 'WNW', angle: 202.5 },
                                  { label: 'NW',  angle: 225   },
                                  { label: 'NNW', angle: 247.5 },
                                  { label: 'N',   angle: 270   },
                                  { label: 'NNE', angle: 292.5 },
                                  { label: 'NE',  angle: 315   },
                                  { label: 'ENE', angle: 337.5 },
                                ];

                                const suryaAngle   = 225 + rotation;
                                const chandraAngle = 45  + rotation;

                                const suryaPoint   = getIntersection(suryaAngle);
                                const chandraPoint = getIntersection(chandraAngle);

                                // Sample the full arc of each half (boundary corners + intermediate
                                // angles) and average with center for a true visual centroid.
                                // Surya half: NW(225) -> N(270) -> NE(315) -> E(0) -> SE(45)
                                // Chandra half: SE(45) -> S(90) -> SW(135) -> W(180) -> NW(225)
                                const computeHalfCentroid = (arcAngles) => {
                                  const pts = arcAngles
                                    .map(a => getIntersection(a + rotation))
                                    .filter(Boolean);
                                  if (pts.length === 0) return { x: null, y: null };
                                  const all = [{ x: compassCenter.x, y: compassCenter.y }, ...pts];
                                  const sumX = all.reduce((s, p) => s + p.x, 0);
                                  const sumY = all.reduce((s, p) => s + p.y, 0);
                                  return { x: sumX / all.length, y: sumY / all.length };
                                };

                                const suryaCentroid = computeHalfCentroid(
                                  [225, 247.5, 270, 292.5, 315, 337.5, 0, 22.5, 45]
                                );
                                const suryaLabelX = suryaCentroid.x;
                                const suryaLabelY = suryaCentroid.y;

                                const chandraCentroid = computeHalfCentroid(
                                  [45, 67.5, 90, 112.5, 135, 157.5, 180, 202.5, 225]
                                );
                                const chandraLabelX = chandraCentroid.x;
                                const chandraLabelY = chandraCentroid.y;

                                return (
                                  <>
                                    {/* 1. Boundary Line */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* 2. Surya Line ??" NW */}
                                    {suryaPoint && (
                                      <Line
                                        points={[compassCenter.x, compassCenter.y, suryaPoint.x, suryaPoint.y]}
                                        stroke={strokeColor}
                                        strokeWidth={strokeWidth}
                                        opacity={opacity}
                                        listening={false}
                                      />
                                    )}

                                    {/* 3. Chandra Line ??" SE */}
                                    {chandraPoint && (
                                      <Line
                                        points={[compassCenter.x, compassCenter.y, chandraPoint.x, chandraPoint.y]}
                                        stroke={strokeColor}
                                        strokeWidth={strokeWidth}
                                        opacity={opacity}
                                        listening={false}
                                      />
                                    )}

                                    {/* 4. 16 Direction Labels */}
                                    {directions16.map(({ label, angle }) => {
                                      const adjustedAngle = angle + rotation;
                                      const intersection = getIntersection(adjustedAngle);
                                      if (!intersection) return null;

                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelDistance = distance * 1.1;
                                      const labelX = compassCenter.x + (dirX / distance) * labelDistance;
                                      const labelY = compassCenter.y + (dirY / distance) * labelDistance;
                                      const offsetX = label.length === 3 ? 14 : 10;

                                      return (
                                        <Text
                                          key={`dir-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={offsetX}
                                          offsetY={8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 5. Surya Bhag Label ??" NE part ke andar center pe */}
                                    {suryaLabelX !== null && suryaLabelY !== null && (
                                      <Text
                                        x={suryaLabelX}
                                        y={suryaLabelY}
                                        text="Surya Bhag"
                                        fontSize={13}
                                        fontStyle="bold"
                                        fill={strokeColor}
                                        align="center"
                                        verticalAlign="middle"
                                        offsetX={36}
                                        offsetY={7}
                                        opacity={opacity}
                                        listening={false}
                                      />
                                    )}

                                    {/* 6. Chandra Label ??" SE part ke andar center pe */}
                                    {chandraLabelX !== null && chandraLabelY !== null && (
                                      <Text
                                        x={chandraLabelX}
                                        y={chandraLabelY}
                                        text="Chandra Bhag"
                                        fontSize={13}
                                        fontStyle="bold"
                                        fill={strokeColor}
                                        align="center"
                                        verticalAlign="middle"
                                        offsetX={44}
                                        offsetY={7}
                                        opacity={opacity}
                                        listening={false}
                                      />
                                    )}

                                    {/* 7. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}
                          {layerId === 'compass-16' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. CLIPPED SECTORS - sirf jab compassRotation === 0 */}
                                    {!hasParentRotation && mainDirections.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % mainDirections.length;
                                      const nextAngle = mainDirections[nextIndex].angle;
                                      const startAngleDeg = angle;
                                      const endAngleDeg = nextAngle;

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);
                                        return (
                                          <Line
                                            key={`compass-16-sector-${index}-ring-${ringIndex}`}
                                            points={flatPoints}
                                            closed
                                            fill="transparent"
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?o. DIRECTION LINES - sirf jab compassRotation > 0 */}
                                    {hasParentRotation && layerState.showCompassLines && mainDirections.map(({ angle, area }) => {
                                      const intersection = findPolygonIntersection(compassCenter, angle);
                                      if (!intersection) return null;
                                      return (
                                        <Line
                                          key={`compass-16-${angle}`}
                                          points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                          stroke={layerState.compassColor || '#000000'}
                                          strokeWidth={layerState.compassLineWeight || 2}
                                          opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Area Labels */}
                                    {layerState.showSectorAreas && mainDirections.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % mainDirections.length;
                                      const nextAngle = mainDirections[nextIndex].angle;
                                      let middleAngle = nextAngle < angle ? (angle + nextAngle + 360) / 2 : (angle + nextAngle) / 2;
                                      if (middleAngle >= 360) middleAngle -= 360;
                                      const labelAngle = middleAngle;
                                      const intersection = findPolygonIntersection(compassCenter, labelAngle);
                                      if (!intersection) return null;
                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;
                                      const labelX = compassCenter.x + (dirX / distance) * (distance * 0.7);
                                      const labelY = compassCenter.y + (dirY / distance) * (distance * 0.7);
                                      return (
                                        <Text
                                          key={`label-16-${area}`}
                                          x={labelX}
                                          y={labelY}
                                          text={area}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={layerState.compassColor || '#000000'}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={12}
                                          offsetY={5}
                                          opacity={layerState.compassOpacity || 0.8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={4}
                                      fill={layerState.compassColor || '#000000'}
                                      opacity={layerState.compassOpacity || 0.8}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'compass-32' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?o. CLIPPED SECTORS - sirf jab compassRotation === 0 */}
                                    {!hasParentRotation && all32Directions.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const nextAngle = all32Directions[nextIndex].angle;
                                      const startAngleDeg = angle;
                                      const endAngleDeg = nextAngle;

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);
                                        return (
                                          <Line
                                            key={`compass-32-sector-${index}-ring-${ringIndex}`}
                                            points={flatPoints}
                                            closed
                                            fill="transparent"
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            stroke={layerState.compassColor || '#000000'}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?o. DIRECTION LINES - sirf jab compassRotation > 0 */}
                                    {hasParentRotation && layerState.showCompassLines && all32Directions.map(({ angle, area }) => {
                                      const intersection = findPolygonIntersection(compassCenter, angle);
                                      if (!intersection) return null;
                                      return (
                                        <Line
                                          key={`compass-32-${angle}`}
                                          points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                          stroke={layerState.compassColor || '#000000'}
                                          strokeWidth={layerState.compassLineWeight || 2}
                                          opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Area Labels */}
                                    {layerState.showSectorAreas && all32Directions.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const nextAngle = all32Directions[nextIndex].angle;
                                      let middleAngle = nextAngle < angle ? (angle + nextAngle + 360) / 2 : (angle + nextAngle) / 2;
                                      if (middleAngle >= 360) middleAngle -= 360;
                                      const labelAngle = middleAngle;
                                      const intersection = findPolygonIntersection(compassCenter, labelAngle);
                                      if (!intersection) return null;
                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;
                                      const labelX = compassCenter.x + (dirX / distance) * (distance * 0.7);
                                      const labelY = compassCenter.y + (dirY / distance) * (distance * 0.7);
                                      return (
                                        <Text
                                          key={`label-32-${area}`}
                                          x={labelX}
                                          y={labelY}
                                          text={area}
                                          fontSize={10}
                                          fontStyle="bold"
                                          fill={layerState.compassColor || '#000000'}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={10}
                                          offsetY={4}
                                          opacity={layerState.compassOpacity || 0.8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?o. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={4}
                                      fill={layerState.compassColor || '#000000'}
                                      opacity={layerState.compassOpacity || 0.8}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {(layerId === 'compass-45') && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const clipLineToBoundaryMartinez = (pointA, pointB) => {
                                  if (!pointA || !pointB) return null;
                                  const dx = pointB.x - pointA.x;
                                  const dy = pointB.y - pointA.y;
                                  const len = Math.sqrt(dx * dx + dy * dy);
                                  if (len < 0.001) return null;
                                  const scale = 10;
                                  const extA = { x: pointA.x - dx * scale, y: pointA.y - dy * scale };
                                  const extB = { x: pointB.x + dx * scale, y: pointB.y + dy * scale };
                                  const offset = 1.0;
                                  const nx = (-dy / len) * offset;
                                  const ny = (dx / len) * offset;
                                  const linePolygon = [[[
                                    [extA.x + nx, extA.y + ny],
                                    [extB.x + nx, extB.y + ny],
                                    [extB.x - nx, extB.y - ny],
                                    [extA.x - nx, extA.y - ny],
                                    [extA.x + nx, extA.y + ny],
                                  ]]];
                                  const boundaryRing = polygonPoints.map(p => [p.x, p.y]);
                                  if (
                                    boundaryRing[0][0] !== boundaryRing[boundaryRing.length - 1][0] ||
                                    boundaryRing[0][1] !== boundaryRing[boundaryRing.length - 1][1]
                                  ) boundaryRing.push(boundaryRing[0]);
                                  const boundaryPolygon = [[boundaryRing]];
                                  try {
                                    const clipped = martinez.intersection(boundaryPolygon, linePolygon);
                                    if (!clipped || clipped.length === 0) return null;
                                    const ux = dx / len;
                                    const uy = dy / len;
                                    const rings = clipped.map(polygon => {
                                      const pts = [];
                                      polygon.forEach(ring => ring.forEach(([x, y]) => pts.push({ x, y })));
                                      if (pts.length < 2) return null;
                                      const proj = pts
                                        .map(p => ({ ...p, t: (p.x - pointA.x) * ux + (p.y - pointA.y) * uy }))
                                        .sort((a, b) => a.t - b.t);
                                      return {
                                        start: { x: proj[0].x, y: proj[0].y },
                                        end:   { x: proj[proj.length - 1].x, y: proj[proj.length - 1].y },
                                        tStart: proj[0].t,
                                        tEnd:   proj[proj.length - 1].t,
                                      };
                                    }).filter(Boolean);
                                    if (rings.length === 0) return null;
                                    rings.sort((a, b) => b.tEnd - a.tEnd);
                                    const lastRing  = rings[0];
                                    const firstRing = rings[rings.length - 1];
                                    return {
                                      lineStart: firstRing.start,
                                      lineEnd:   lastRing.end,
                                      start:     lastRing.start,
                                      end:       lastRing.end,
                                    };
                                  } catch (e) {
                                    return null;
                                  }
                                };

                                // Radial lines must sit exactly where the "32 Zones Vedic" lines sit: each all32Directions
                                // angle pushed clockwise by half a zone (11.25/2 = 5.625°). It used to be 16 - 11.25 = 4.75°,
                                // which left every line 0.875° off the 32 Zones Vedic ones.
                                const totalRotation = (layerState.compassRotation || 0) + 5.625;

                                // Same line look as "32 Zones Vedic": the layer's own compass colour / weight / opacity (falling back to the
                                // main canvas values for layers saved before these were stored) and the same 0.6x stroke weight.
                                const v45Color = layerState.compassColor || compassColor || "#000000";
                                const v45Weight = Math.max(0.5, (layerState.compassLineWeight || parentCompassLineWeight || 2) * 0.6);
                                const v45Opacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : parentCompassLineOpacity;

                                const getClippedBoundaryPt = (rawAngle) => {
                                  const rotatedAngle = rawAngle + totalRotation;
                                  const angleRad = (rotatedAngle * Math.PI) / 180;
                                  const farPoint = {
                                    x: compassCenter.x + Math.cos(angleRad) * 99999,
                                    y: compassCenter.y + Math.sin(angleRad) * 99999,
                                  };
                                  const lc = clipLineToBoundaryMartinez(compassCenter, farPoint);
                                  return lc ? lc.lineEnd : null;
                                };

                                const getRingPoint = (rawAngle, fraction) => {
                                  const bp = getClippedBoundaryPt(rawAngle);
                                  if (!bp) return null;
                                  return {
                                    x: compassCenter.x + (bp.x - compassCenter.x) * fraction,
                                    y: compassCenter.y + (bp.y - compassCenter.y) * fraction,
                                  };
                                };

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* RING 1: Center Block (Block 1) ??" 33.33% boundary */}
                                    <Line
                                      points={all32Directions.flatMap(({ angle }) => {
                                        const pt = getRingPoint(angle, 0.3333);
                                        return pt ? [pt.x, pt.y] : [];
                                      })}
                                      closed={true}
                                      fill="transparent"
                                      stroke={v45Color}
                                      strokeWidth={v45Weight}
                                      opacity={v45Opacity}
                                      listening={false}
                                    />

                                    {/* BLOCK 1 LABEL */}
                                    <Text
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      text="1"
                                      fontSize={20}
                                      fontStyle="bold"
                                      fill="#000000"
                                      align="center"
                                      verticalAlign="middle"
                                      offsetX={0}
                                      offsetY={0}
                                      padding={8}
                                      opacity={1}
                                      listening={false}
                                    />

                                    {/* RING 2: 4 Blocks (Blocks 2-5) */}
                                    {/* Block 2 = North, Block 3 = East, Block 4 = South, Block 5 = West */}
                                    {(() => {
                                      // ?o. midAngle directly set karo ??" N=-90, E=0, S=90, W=180
                                      // lineArea = divider line kahan draw hogi (N8/E8/S8/W8 pe)
                                      const ring2Data = [
                                        { lineArea: 'N8', midAngleDeg: -90,  blockNum: 2 }, // North
                                        { lineArea: 'E8', midAngleDeg:   0,  blockNum: 3 }, // East
                                        { lineArea: 'S8', midAngleDeg:  90,  blockNum: 4 }, // South
                                        { lineArea: 'W8', midAngleDeg: 180,  blockNum: 5 }, // West
                                      ];

                                      return ring2Data.map(({ lineArea, midAngleDeg, blockNum }) => {
                                        const midFraction = (0.3333 + 0.5) / 2; // 0.4166

                                        // ?o. Block number center point ??" directly use midAngleDeg
                                        const bpMid = getClippedBoundaryPt(midAngleDeg);
                                        if (!bpMid) return null;

                                        const finalX = compassCenter.x + (bpMid.x - compassCenter.x) * midFraction;
                                        const finalY = compassCenter.y + (bpMid.y - compassCenter.y) * midFraction;

                                        // ?o. Divider line ??" N8/E8/S8/W8 pe
                                        const origDir = all32Directions.find(d => d.area === lineArea);
                                        if (!origDir) return null;

                                        const bpCurrent = getClippedBoundaryPt(origDir.angle);
                                        if (!bpCurrent) return null;

                                        const innerCurrent = {
                                          x: compassCenter.x + (bpCurrent.x - compassCenter.x) * 0.3333,
                                          y: compassCenter.y + (bpCurrent.y - compassCenter.y) * 0.3333,
                                        };

                                        return (
                                          <Group key={`ring2-block-${blockNum}`}>
                                            <Line
                                              points={[innerCurrent.x, innerCurrent.y, bpCurrent.x, bpCurrent.y]}
                                              stroke={v45Color}
                                              strokeWidth={v45Weight}
                                              opacity={v45Opacity}
                                              listening={false}
                                            />
                                            <Text
                                              x={finalX}
                                              y={finalY}
                                              text={`${blockNum}`}
                                              fontSize={16}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={6}
                                              offsetY={8}
                                              opacity={1}
                                              listening={false}
                                            />
                                          </Group>
                                        );
                                      });
                                    })()}

                                    {/* RING 3: 8 Boxes (Blocks 6-13) */}
                                    {(() => {
                                      const ring3Boxes = [
                                        { startLabel: 'N6', endLabel: 'N8', num: 6  },
                                        { startLabel: 'N8', endLabel: 'E2', num: 7  },
                                        { startLabel: 'E6', endLabel: 'E8', num: 8  },
                                        { startLabel: 'E8', endLabel: 'S2', num: 9  },
                                        { startLabel: 'S6', endLabel: 'S8', num: 10 },
                                        { startLabel: 'S8', endLabel: 'W2', num: 11 },
                                        { startLabel: 'W6', endLabel: 'W8', num: 12 },
                                        { startLabel: 'W8', endLabel: 'N2', num: 13 },
                                      ];

                                      return ring3Boxes.map(({ startLabel, endLabel, num }) => {
                                        const startDir = all32Directions.find(d => d.label === startLabel);
                                        const endDir   = all32Directions.find(d => d.label === endLabel);
                                        if (!startDir || !endDir) return null;

                                        const bpStart = getClippedBoundaryPt(startDir.angle);
                                        const bpEnd   = getClippedBoundaryPt(endDir.angle);
                                        if (!bpStart || !bpEnd) return null;

                                        const innerPoint1 = {
                                          x: compassCenter.x + (bpStart.x - compassCenter.x) * 0.5,
                                          y: compassCenter.y + (bpStart.y - compassCenter.y) * 0.5,
                                        };
                                        const outerPoint1 = {
                                          x: compassCenter.x + (bpStart.x - compassCenter.x) * 0.6666,
                                          y: compassCenter.y + (bpStart.y - compassCenter.y) * 0.6666,
                                        };
                                        const innerPoint2 = {
                                          x: compassCenter.x + (bpEnd.x - compassCenter.x) * 0.5,
                                          y: compassCenter.y + (bpEnd.y - compassCenter.y) * 0.5,
                                        };
                                        const outerPoint2 = {
                                          x: compassCenter.x + (bpEnd.x - compassCenter.x) * 0.6666,
                                          y: compassCenter.y + (bpEnd.y - compassCenter.y) * 0.6666,
                                        };

                                        const midAngle = (startDir.angle + endDir.angle) / 2;
                                        const bpMid = getClippedBoundaryPt(midAngle);
                                        let boxCenterX, boxCenterY;
                                        if (bpMid) {
                                          boxCenterX = compassCenter.x + (bpMid.x - compassCenter.x) * 0.575;
                                          boxCenterY = compassCenter.y + (bpMid.y - compassCenter.y) * 0.575;
                                        } else {
                                          boxCenterX = (innerPoint1.x + innerPoint2.x + outerPoint1.x + outerPoint2.x) / 4;
                                          boxCenterY = (innerPoint1.y + innerPoint2.y + outerPoint1.y + outerPoint2.y) / 4;
                                        }

                                        const isRightSideBox = [7, 9, 11, 13].includes(num);

                                        return (
                                          <Group key={`ring3-block-${num}`}>
                                            <Line
                                              points={[innerPoint1.x, innerPoint1.y, outerPoint1.x, outerPoint1.y]}
                                              stroke={v45Color}
                                              strokeWidth={v45Weight}
                                              opacity={v45Opacity}
                                              listening={false}
                                            />
                                            {isRightSideBox && (
                                              <Line
                                                points={[innerPoint2.x, innerPoint2.y, outerPoint2.x, outerPoint2.y]}
                                                stroke={v45Color}
                                                strokeWidth={v45Weight}
                                                opacity={v45Opacity}
                                                listening={false}
                                              />
                                            )}
                                            <Text
                                              x={boxCenterX}
                                              y={boxCenterY}
                                              text={`${num}`}
                                              fontSize={14}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={7}
                                              offsetY={7}
                                              padding={5}
                                              opacity={1}
                                              listening={false}
                                            />
                                          </Group>
                                        );
                                      });
                                    })()}

                                    {/* EXTRA LINES ??" N8, E8, S8, W8 */}
                                    {['N8', 'E8', 'S8', 'W8'].map((dirName) => {
                                      const dirData = all32Directions.find(d => d.label === dirName);
                                      if (!dirData) return null;
                                      const bp = getClippedBoundaryPt(dirData.angle);
                                      if (!bp) return null;

                                      const innerPt = {
                                        x: compassCenter.x + (bp.x - compassCenter.x) * 0.5,
                                        y: compassCenter.y + (bp.y - compassCenter.y) * 0.5,
                                      };
                                      const outerPt = {
                                        x: compassCenter.x + (bp.x - compassCenter.x) * 0.6666,
                                        y: compassCenter.y + (bp.y - compassCenter.y) * 0.6666,
                                      };

                                      return (
                                        <Line
                                          key={`ring3-cardinal-line-${dirName}`}
                                          points={[innerPt.x, innerPt.y, outerPt.x, outerPt.y]}
                                          stroke={v45Color}
                                          strokeWidth={v45Weight}
                                          opacity={v45Opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* RING 4: 32 Blocks (Blocks 14-45) */}
                                    {all32Directions.map(({ angle, label }, index) => {
                                      const bp = getClippedBoundaryPt(angle);
                                      if (!bp) return null;

                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const nextBp    = getClippedBoundaryPt(all32Directions[nextIndex].angle);
                                      if (!nextBp) return null;

                                      const innerPoint1 = {
                                        x: compassCenter.x + (bp.x - compassCenter.x) * 0.6666,
                                        y: compassCenter.y + (bp.y - compassCenter.y) * 0.6666,
                                      };
                                      const outerPoint1 = { x: bp.x, y: bp.y };
                                      const innerPoint2 = {
                                        x: compassCenter.x + (nextBp.x - compassCenter.x) * 0.6666,
                                        y: compassCenter.y + (nextBp.y - compassCenter.y) * 0.6666,
                                      };

                                      const boxCenterX = (innerPoint1.x + innerPoint2.x + outerPoint1.x + nextBp.x) / 4;
                                      const boxCenterY = (innerPoint1.y + innerPoint2.y + outerPoint1.y + nextBp.y) / 4;
                                      const blockNumber = 14 + ((index - 3 + 32) % 32);

                                      return (
                                        <Group key={`ring4-block-${angle}`}>
                                          <Line  f 
                                            points={[innerPoint1.x, innerPoint1.y, outerPoint1.x, outerPoint1.y]}
                                            stroke={v45Color}
                                            strokeWidth={v45Weight}
                                            opacity={v45Opacity}
                                            listening={false}
                                          />
                                          <Text
                                            x={boxCenterX}
                                            y={boxCenterY}
                                            text={`${blockNumber}`}
                                            fontSize={12}
                                            fontStyle="bold"
                                            fill="#000000"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={6}
                                            offsetY={6}
                                            padding={4}
                                            opacity={1}
                                            listening={false}
                                          />
                                        </Group>
                                      );
                                    })}

                                    {/* RING 2 BOUNDARY (50%) */}
                                    {all32Directions.map(({ angle, label }, index) => {
                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const nextLabel = all32Directions[nextIndex].label;

                                      const pt     = getRingPoint(angle, 0.5);
                                      const nextPt = getRingPoint(all32Directions[nextIndex].angle, 0.5);
                                      if (!pt || !nextPt) return null;

                                      const hiddenLabels = ['2', '3', '4', '5', '6'];
                                      const currentHide =
                                        (label.startsWith('N') && hiddenLabels.includes(label.substring(1))) ||
                                        (label.startsWith('E') && hiddenLabels.includes(label.substring(1))) ||
                                        (label.startsWith('S') && hiddenLabels.includes(label.substring(1))) ||
                                        (label.startsWith('W') && hiddenLabels.includes(label.substring(1)));
                                      const nextHide =
                                        (nextLabel.startsWith('N') && hiddenLabels.includes(nextLabel.substring(1))) ||
                                        (nextLabel.startsWith('E') && hiddenLabels.includes(nextLabel.substring(1))) ||
                                        (nextLabel.startsWith('S') && hiddenLabels.includes(nextLabel.substring(1))) ||
                                        (nextLabel.startsWith('W') && hiddenLabels.includes(nextLabel.substring(1)));

                                      if (currentHide && nextHide) return null;

                                      return (
                                        <Line
                                          key={`ring2-boundary-${angle}`}
                                          points={[pt.x, pt.y, nextPt.x, nextPt.y]}
                                          stroke={v45Color}
                                          strokeWidth={v45Weight}
                                          opacity={v45Opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* RING 3 BOUNDARY (66.66%) */}
                                    {all32Directions.map(({ angle }, index) => {
                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const pt     = getRingPoint(angle, 0.6666);
                                      const nextPt = getRingPoint(all32Directions[nextIndex].angle, 0.6666);
                                      if (!pt || !nextPt) return null;

                                      return (
                                        <Line
                                          key={`ring3-boundary-${angle}`}
                                          points={[pt.x, pt.y, nextPt.x, nextPt.y]}
                                          stroke={v45Color}
                                          strokeWidth={v45Weight}
                                          opacity={v45Opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* DIRECTION LABELS - between two direction lines, outside boundary */}
                                    {all32Directions.map(({ angle, label }, index) => {
                                      const nextIndex = (index + 1) % all32Directions.length;
                                      const nextAngle = all32Directions[nextIndex].angle;

                                    const prevIndex = (index - 1 + all32Directions.length) % all32Directions.length;
                                    const prevAngle = all32Directions[prevIndex].angle;

                                    let midAngle = (prevAngle + angle) / 2;
                                    if (Math.abs(angle - prevAngle) > 180) {
                                      midAngle = ((prevAngle + angle + 360) / 2) % 360;
                                    }
                                      const bp = getClippedBoundaryPt(midAngle);
                                      if (!bp) return null;

                                      const dirX = bp.x - compassCenter.x;
                                      const dirY = bp.y - compassCenter.y;
                                      const length = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (length < 0.001) return null;

                                      const normDirX = dirX / length;
                                      const normDirY = dirY / length;

                                      const labelX = bp.x + normDirX * 18;
                                      const labelY = bp.y + normDirY * 18;
                                      const rotation = midAngle + totalRotation + 90;

                                      return (
                                        <Text
                                          key={`main-label-${angle}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={10}
                                          fontStyle="bold"
                                          fill="#000000"
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={10}
                                          offsetY={5}
                                          rotation={rotation}
                                          opacity={1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* CENTER POINT */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={4}
                                      fill="#8B0000"
                                      opacity={1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {(layerId === 'compass-45-2') && compassCenter && isPolygonComplete && (
                            <>
                              <Layer>
                                {(() => {
                                  const clipLineToBoundaryMartinez = (pointA, pointB) => {
                                    if (!pointA || !pointB) return null;
                                    const dx = pointB.x - pointA.x;
                                    const dy = pointB.y - pointA.y;
                                    const len = Math.sqrt(dx * dx + dy * dy);
                                    if (len < 0.001) return null;
                                    const scale = 10;
                                    const extA = { x: pointA.x - dx * scale, y: pointA.y - dy * scale };
                                    const extB = { x: pointB.x + dx * scale, y: pointB.y + dy * scale };
                                    const offset = 1.0;
                                    const nx = (-dy / len) * offset;
                                    const ny = (dx / len) * offset;
                                    const linePolygon = [[[
                                      [extA.x + nx, extA.y + ny],
                                      [extB.x + nx, extB.y + ny],
                                      [extB.x - nx, extB.y - ny],
                                      [extA.x - nx, extA.y - ny],
                                      [extA.x + nx, extA.y + ny],
                                    ]]];
                                    const boundaryRing = polygonPoints.map(p => [p.x, p.y]);
                                    if (
                                      boundaryRing[0][0] !== boundaryRing[boundaryRing.length - 1][0] ||
                                      boundaryRing[0][1] !== boundaryRing[boundaryRing.length - 1][1]
                                    ) boundaryRing.push(boundaryRing[0]);
                                    const boundaryPolygon = [[boundaryRing]];
                                    try {
                                      const clipped = martinez.intersection(boundaryPolygon, linePolygon);
                                      if (!clipped || clipped.length === 0) return null;
                                      const ux = dx / len;
                                      const uy = dy / len;
                                      const rings = clipped.map(polygon => {
                                        const pts = [];
                                        polygon.forEach(ring => ring.forEach(([x, y]) => pts.push({ x, y })));
                                        if (pts.length < 2) return null;
                                        const proj = pts
                                          .map(p => ({ ...p, t: (p.x - pointA.x) * ux + (p.y - pointA.y) * uy }))
                                          .sort((a, b) => a.t - b.t);
                                        return {
                                          start: { x: proj[0].x, y: proj[0].y },
                                          end: { x: proj[proj.length - 1].x, y: proj[proj.length - 1].y },
                                          tStart: proj[0].t,
                                          tEnd: proj[proj.length - 1].t,
                                        };
                                      }).filter(Boolean);
                                      if (rings.length === 0) return null;
                                      rings.sort((a, b) => b.tEnd - a.tEnd);
                                      const lastRing = rings[0];
                                      const firstRing = rings[rings.length - 1];
                                      return {
                                        lineStart: firstRing.start,
                                        lineEnd: lastRing.end,
                                        start: lastRing.start,
                                        end: lastRing.end,
                                      };
                                    } catch (e) {
                                      return null;
                                    }
                                  };

                                  const getInsideSegmentPoints = (startPt, endPt) => {
                                    if (!startPt || !endPt) return [];
                                    const dx = endPt.x - startPt.x;
                                    const dy = endPt.y - startPt.y;
                                    const totalLen = Math.sqrt(dx * dx + dy * dy);
                                    if (totalLen < 0.001) return [];
                                    const ux = dx / totalLen;
                                    const uy = dy / totalLen;
                                    const tValues = [0, totalLen];
                                    for (let i = 0; i < polygonPoints.length; i++) {
                                      const p1 = polygonPoints[i];
                                      const p2 = polygonPoints[(i + 1) % polygonPoints.length];
                                      const ex = p2.x - p1.x;
                                      const ey = p2.y - p1.y;
                                      const denom = ux * ey - uy * ex;
                                      if (Math.abs(denom) < 1e-10) continue;
                                      const t = ((p1.x - startPt.x) * ey - (p1.y - startPt.y) * ex) / denom;
                                      const s = ((p1.x - startPt.x) * uy - (p1.y - startPt.y) * ux) / denom;
                                      if (t > 1e-8 && t < totalLen - 1e-8 && s >= 0 && s <= 1) tValues.push(t);
                                    }
                                    tValues.sort((a, b) => a - b);
                                    const unique = [tValues[0]];
                                    for (let i = 1; i < tValues.length; i++) {
                                      if (tValues[i] - unique[unique.length - 1] > 1e-6) unique.push(tValues[i]);
                                    }
                                    const result = [];
                                    for (let i = 0; i < unique.length - 1; i++) {
                                      const t1 = unique[i];
                                      const t2 = unique[i + 1];
                                      const mx = startPt.x + ux * ((t1 + t2) / 2);
                                      const my = startPt.y + uy * ((t1 + t2) / 2);
                                      let inside = false;
                                      for (let j = 0, k = polygonPoints.length - 1; j < polygonPoints.length; k = j++) {
                                        const xi = polygonPoints[j].x, yi = polygonPoints[j].y;
                                        const xj = polygonPoints[k].x, yj = polygonPoints[k].y;
                                        if (((yi > my) !== (yj > my)) && (mx < (xj - xi) * (my - yi) / (yj - yi) + xi)) {
                                          inside = !inside;
                                        }
                                      }
                                      if (inside) result.push({
                                        x1: startPt.x + ux * t1, y1: startPt.y + uy * t1,
                                        x2: startPt.x + ux * t2, y2: startPt.y + uy * t2,
                                      });
                                    }
                                    return result;
                                  };

                                  const totalRotation = (layerState.compassRotation || 0);

                                  const getClippedBoundaryPt = (rawAngle) => {
                                    const rotatedAngle = rawAngle + totalRotation;
                                    const angleRad = (rotatedAngle * Math.PI) / 180;
                                    const farPoint = {
                                      x: compassCenter.x + Math.cos(angleRad) * 99999,
                                      y: compassCenter.y + Math.sin(angleRad) * 99999,
                                    };
                                    const lc = clipLineToBoundaryMartinez(compassCenter, farPoint);
                                    return lc ? lc.lineEnd : null;
                                  };

                                  const getRingPoint = (angle, fraction) => {
                                    const bp = getClippedBoundaryPt(angle);
                                    if (!bp) return null;
                                    return {
                                      x: compassCenter.x + (bp.x - compassCenter.x) * fraction,
                                      y: compassCenter.y + (bp.y - compassCenter.y) * fraction,
                                    };
                                  };

                                  const getFullLineToBoundary = (startPt, endPt) => {
                                    if (!startPt || !endPt) return null;
                                    return { x1: startPt.x, y1: startPt.y, x2: endPt.x, y2: endPt.y };
                                  };

                                  const getBoxCenter = (startAngle, endAngle, innerFraction, outerFraction) => {
                                    let mid = (startAngle + endAngle) / 2;
                                    if (Math.abs(startAngle - endAngle) > 180) {
                                      mid = ((startAngle + endAngle + 360) / 2) % 360;
                                    }
                                    const midFraction = (innerFraction + outerFraction) / 2;
                                    return getRingPoint(mid, midFraction);
                                  };

                                  const rotatedDirections = all32Directions.map(dir => ({
                                    ...dir,
                                    angle: (dir.angle + 11.25) % 360
                                  }));

                                  return (
                                    <>
                                      {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                      <Line
                                        points={polygonPoints.flatMap(p => [p.x, p.y])}
                                        closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                        fill="transparent"
                                        listening={false}
                                      />

                                      {/* DIRECTION LINES */}
                                      {rotatedDirections.map(({ angle, label }) => {
                                        const bp = getClippedBoundaryPt(angle);
                                        if (!bp) return null;

                                        const isCardinal          = ['N8', 'E8', 'S8', 'W8'].includes(label);
                                        const isSubCardinal       = ['N7', 'E7', 'S7', 'W7'].includes(label);
                                        const isSecondaryCardinal = ['N1', 'E1', 'S1', 'W1'].includes(label);

                                        let startFraction;
                                        if (isCardinal)                                startFraction = 0.25;
                                        else if (isSubCardinal || isSecondaryCardinal) startFraction = 0.75;
                                        else                                           startFraction = 0.5;

                                        const startPt = {
                                          x: compassCenter.x + (bp.x - compassCenter.x) * startFraction,
                                          y: compassCenter.y + (bp.y - compassCenter.y) * startFraction,
                                        };

                                        const innerSegments = getInsideSegmentPoints(startPt, bp);
                                        const fullLine = getFullLineToBoundary(startPt, bp);

                                        return (
                                          <React.Fragment key={`dir-${label}`}>
                                            {innerSegments.map((seg, idx) => (
                                              <Line
                                                key={`direction-line-${label}-${idx}`}
                                                points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                                stroke={layerState.compassColor || "#000000"}
                                                strokeWidth={layerState.compassLineWeight || 2}
                                                opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                                listening={false}
                                              />
                                            ))}
                                            {fullLine && (
                                              <Line
                                                key={`direction-line-${label}-outer`}
                                                points={[fullLine.x1, fullLine.y1, fullLine.x2, fullLine.y2]}
                                                stroke={layerState.compassColor || "#000000"}
                                                strokeWidth={layerState.compassLineWeight || 2}
                                                opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                                listening={false}
                                              />
                                            )}
                                          </React.Fragment>
                                        );
                                      })}

                                      {/* RING 1 BOUNDARY — 25% (outline only, no fill — otherwise it hides the floor plan) */}
                                      <Line
                                        points={rotatedDirections.flatMap(({ angle }) => {
                                          const pt = getRingPoint(angle, 0.25);
                                          return pt ? [pt.x, pt.y] : [];
                                        })}
                                        closed={true}
                                        fill="transparent"
                                        stroke={layerState.compassColor || "#000000"}
                                        strokeWidth={layerState.compassLineWeight || 2}
                                        opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                        listening={false}
                                      />

                                      {/* BLOCK 1 NUMBER ??" center */}
                                      <Text
                                        x={compassCenter.x}
                                        y={compassCenter.y}
                                        text="1"
                                        fontSize={24}
                                        fontStyle="bold"
                                        fill="#000000"
                                        align="center"
                                        verticalAlign="middle"
                                        offsetX={6}
                                        offsetY={12}
                                        opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                        listening={false}
                                      />

                                      {/* RING 2 BOUNDARY ??" 50% */}
                                      <Line
                                        points={rotatedDirections.flatMap(({ angle }) => {
                                          const pt = getRingPoint(angle, 0.5);
                                          return pt ? [pt.x, pt.y] : [];
                                        })}
                                        stroke={layerState.compassColor || "#000000"}
                                        strokeWidth={layerState.compassLineWeight || 2}
                                        opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                        closed={true}
                                        listening={false}
                                      />

                                      {/* RING 2 BLOCK NUMBERS (2-5) */}
                                      {(() => {
                                        const ring2Data = [
                                          { startLabel: 'N8', endLabel: 'E8', num: 2 },
                                          { startLabel: 'E8', endLabel: 'S8', num: 3 },
                                          { startLabel: 'S8', endLabel: 'W8', num: 4 },
                                          { startLabel: 'W8', endLabel: 'N8', num: 5 }
                                        ];

                                        const shiftedRing2Data = [...ring2Data];
                                        for (let i = 0; i < 3; i++) {
                                          const last = shiftedRing2Data.pop();
                                          shiftedRing2Data.unshift(last);
                                        }

                                        return ring2Data.map(({ startLabel, endLabel }, index) => {
                                          const startDir = rotatedDirections.find(d => d.area === startLabel);
                                          const endDir   = rotatedDirections.find(d => d.area === endLabel);
                                          if (!startDir || !endDir) return null;

                                          const center = getBoxCenter(startDir.angle, endDir.angle, 0.25, 0.5);
                                          if (!center) return null;

                                          const shiftedNum = shiftedRing2Data[index].num;

                                          return (
                                            <Text
                                              key={`ring2-block-${shiftedNum}`}
                                              x={center.x}
                                              y={center.y}
                                              text={`${shiftedNum}`}
                                              fontSize={18}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={6}
                                              offsetY={9}
                                              opacity={layerState.compassOpacity || 0.8}
                                              listening={false}
                                            />
                                          );
                                        });
                                      })()}

                                      {/* RING 3 BOUNDARY ??" 75% */}
                                      {rotatedDirections.map(({ angle, label }, index) => {
                                        const nextIndex = (index + 1) % rotatedDirections.length;
                                        const nextDir   = rotatedDirections[nextIndex];

                                        const pt     = getRingPoint(angle, 0.75);
                                        const nextPt = getRingPoint(nextDir.angle, 0.75);
                                        if (!pt || !nextPt) return null;

                                        const hideRanges = [
                                          ['N2', 'N3', 'N4', 'N5', 'N6'],
                                          ['E2', 'E3', 'E4', 'E5', 'E6'],
                                          ['S2', 'S3', 'S4', 'S5', 'S6'],
                                          ['W2', 'W3', 'W4', 'W5', 'W6']
                                        ];

                                        const shouldHide = hideRanges.some(range =>
                                          range.includes(label) && range.includes(nextDir.label)
                                        );
                                        if (shouldHide) return null;

                                        return (
                                          <Line
                                            key={`ring3-boundary-${angle}`}
                                            points={[pt.x, pt.y, nextPt.x, nextPt.y]}
                                            stroke={layerState.compassColor || "#000000"}
                                            strokeWidth={layerState.compassLineWeight || 2}
                                            opacity={layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1}
                                            listening={false}
                                          />
                                        );
                                      })}

                                      {/* RING 3 BLOCK NUMBERS (6-13) */}
                                      {(() => {
                                        const ring3Data = [
                                          { startLabel: 'N6', endLabel: 'N8', num: 6  },
                                          { startLabel: 'N8', endLabel: 'E2', num: 7  },
                                          { startLabel: 'E6', endLabel: 'E8', num: 8  },
                                          { startLabel: 'E8', endLabel: 'S2', num: 9  },
                                          { startLabel: 'S6', endLabel: 'S8', num: 10 },
                                          { startLabel: 'S8', endLabel: 'W2', num: 11 },
                                          { startLabel: 'W6', endLabel: 'W8', num: 12 },
                                          { startLabel: 'W8', endLabel: 'N2', num: 13 }
                                        ];

                                        return ring3Data.map(({ startLabel, endLabel, num }) => {
                                          const startDir = rotatedDirections.find(d => d.label === startLabel);
                                          const endDir   = rotatedDirections.find(d => d.label === endLabel);
                                          if (!startDir || !endDir) return null;

                                          const center = getBoxCenter(startDir.angle, endDir.angle, 0.5, 0.75);
                                          if (!center) return null;

                                          return (
                                            <Text
                                              key={`ring3-block-${num}`}
                                              x={center.x}
                                              y={center.y}
                                              text={`${num}`}
                                              fontSize={16}
                                              fontStyle="bold"
                                              fill="#000000"
                                              align="center"
                                              verticalAlign="middle"
                                              offsetX={5}
                                              offsetY={8}
                                              opacity={layerState.compassOpacity || 0.8}
                                              listening={false}
                                            />
                                          );
                                        });
                                      })()}

                                      {/* RING 4 BLOCK NUMBERS (14-45) */}
                                      {rotatedDirections.map(({ angle }, index) => {
                                        const nextIndex = (index + 1) % rotatedDirections.length;
                                        const nextAngle = rotatedDirections[nextIndex].angle;

                                        const center = getBoxCenter(angle, nextAngle, 0.75, 1.0);
                                        if (!center) return null;

                                        const shiftedBlockNumber = 14 + ((index - 3 + 32) % 32);

                                        return (
                                          <Text
                                            key={`ring4-block-${shiftedBlockNumber}`}
                                            x={center.x}
                                            y={center.y}
                                            text={`${shiftedBlockNumber}`}
                                            fontSize={13}
                                            fontStyle="bold"
                                            fill="#000000"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={6}
                                            offsetY={6.5}
                                            opacity={layerState.compassOpacity || 0.8}
                                            listening={false}
                                          />
                                        );
                                      })}

                                      {/* DIRECTION LABELS - between two direction lines, outside boundary */}
                                      {rotatedDirections.map(({ angle, label }, index) => {
                                        // ?o. PREV index use karo - current aur previous ke beech
                                        const prevIndex = (index - 1 + rotatedDirections.length) % rotatedDirections.length;
                                        const prevAngle = rotatedDirections[prevIndex].angle;

                                        // ?o. Current aur previous direction line ka mid-angle
                                        let midAngle = (prevAngle + angle) / 2;
                                        
                                        // Wrap-around handle karo
                                        if (Math.abs(angle - prevAngle) > 180) {
                                          midAngle = ((prevAngle + angle + 360) / 2) % 360;
                                        }

                                        // ?o. Mid-angle pe boundary point nikalo
                                        const bp = getClippedBoundaryPt(midAngle);
                                        if (!bp) return null;

                                        const dirX = bp.x - compassCenter.x;
                                        const dirY = bp.y - compassCenter.y;
                                        const length = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (length < 0.001) return null;

                                        const normDirX = dirX / length;
                                        const normDirY = dirY / length;

                                        const labelX = bp.x + normDirX * 18;
                                        const labelY = bp.y + normDirY * 18;

                                        return (
                                          <Text
                                            key={`main-label-${angle}`}
                                            x={labelX}
                                            y={labelY}
                                            text={label}
                                            fontSize={10}
                                            fontStyle="bold"
                                            fill="#000000"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={10}
                                            offsetY={5}
                                            opacity={layerState.compassOpacity || 0.8}
                                            listening={false}
                                          />
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </Layer>
                            </>
                          )}

                          {layerId === 'marmsthan-advance' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const all32Directions = [
                                  { angle: -90,     label: 'N5',  area: 'N5',  num: 1  },
                                  { angle: -78.75,  label: 'N6',  area: 'N6',  num: 2  },
                                  { angle: -67.5,   label: 'N7',  area: 'N7',  num: 3  },
                                  { angle: -56.25,  label: 'N8',  area: 'N8',  num: 4  },
                                  { angle: -45,     label: 'E1',  area: 'E1',  num: 5  },
                                  { angle: -33.75,  label: 'E2',  area: 'E2',  num: 6  },
                                  { angle: -22.5,   label: 'E3',  area: 'E3',  num: 7  },
                                  { angle: -11.25,  label: 'E4',  area: 'E4',  num: 8  },
                                  { angle: 0,       label: 'E5',  area: 'E5',  num: 9  },
                                  { angle: 11.25,   label: 'E6',  area: 'E6',  num: 10 },
                                  { angle: 22.5,    label: 'E7',  area: 'E7',  num: 11 },
                                  { angle: 33.75,   label: 'E8',  area: 'E8',  num: 12 },
                                  { angle: 45,      label: 'S1',  area: 'S1',  num: 13 },
                                  { angle: 56.25,   label: 'S2',  area: 'S2',  num: 14 },
                                  { angle: 67.5,    label: 'S3',  area: 'S3',  num: 15 },
                                  { angle: 78.75,   label: 'S4',  area: 'S4',  num: 16 },
                                  { angle: 90,      label: 'S5',  area: 'S5',  num: 17 },
                                  { angle: 101.25,  label: 'S6',  area: 'S6',  num: 18 },
                                  { angle: 112.5,   label: 'S7',  area: 'S7',  num: 19 },
                                  { angle: 123.75,  label: 'S8',  area: 'S8',  num: 20 },
                                  { angle: 135,     label: 'W1',  area: 'W1',  num: 21 },
                                  { angle: 146.25,  label: 'W2',  area: 'W2',  num: 22 },
                                  { angle: 157.5,   label: 'W3',  area: 'W3',  num: 23 },
                                  { angle: 168.75,  label: 'W4',  area: 'W4',  num: 24 },
                                  { angle: 180,     label: 'W5',  area: 'W5',  num: 25 },
                                  { angle: -168.75, label: 'W6',  area: 'W6',  num: 26 },
                                  { angle: -157.5,  label: 'W7',  area: 'W7',  num: 27 },
                                  { angle: -146.25, label: 'W8',  area: 'W8',  num: 28 },
                                  { angle: -135,    label: 'N1',  area: 'N1',  num: 29 },
                                  { angle: -123.75, label: 'N2',  area: 'N2',  num: 30 },
                                  { angle: -112.5,  label: 'N3',  area: 'N3',  num: 31 },
                                  { angle: -101.25, label: 'N4',  area: 'N4',  num: 32 },
                                ];

                                const clipLineToBoundaryMartinez = (pointA, pointB) => {
                                  if (!pointA || !pointB) return null;
                                  const dx = pointB.x - pointA.x;
                                  const dy = pointB.y - pointA.y;
                                  const len = Math.sqrt(dx * dx + dy * dy);
                                  if (len < 0.001) return null;
                                  const scale = 10;
                                  const extA = { x: pointA.x - dx * scale, y: pointA.y - dy * scale };
                                  const extB = { x: pointB.x + dx * scale, y: pointB.y + dy * scale };
                                  const offset = 1.0;
                                  const nx = (-dy / len) * offset;
                                  const ny = (dx / len) * offset;
                                  const linePolygon = [[[
                                    [extA.x + nx, extA.y + ny],
                                    [extB.x + nx, extB.y + ny],
                                    [extB.x - nx, extB.y - ny],
                                    [extA.x - nx, extA.y - ny],
                                    [extA.x + nx, extA.y + ny],
                                  ]]];
                                  const boundaryRing = polygonPoints.map(p => [p.x, p.y]);
                                  if (
                                    boundaryRing[0][0] !== boundaryRing[boundaryRing.length - 1][0] ||
                                    boundaryRing[0][1] !== boundaryRing[boundaryRing.length - 1][1]
                                  ) boundaryRing.push(boundaryRing[0]);
                                  const boundaryPolygon = [[boundaryRing]];
                                  try {
                                    const clipped = martinez.intersection(boundaryPolygon, linePolygon);
                                    if (!clipped || clipped.length === 0) return null;
                                    const ux = dx / len;
                                    const uy = dy / len;
                                    const rings = clipped.map(polygon => {
                                      const pts = [];
                                      polygon.forEach(ring => ring.forEach(([x, y]) => pts.push({ x, y })));
                                      if (pts.length < 2) return null;
                                      const proj = pts
                                        .map(p => ({ ...p, t: (p.x - pointA.x) * ux + (p.y - pointA.y) * uy }))
                                        .sort((a, b) => a.t - b.t);
                                      return {
                                        start: { x: proj[0].x, y: proj[0].y },
                                        end: { x: proj[proj.length - 1].x, y: proj[proj.length - 1].y },
                                        tStart: proj[0].t,
                                        tEnd: proj[proj.length - 1].t,
                                      };
                                    }).filter(Boolean);
                                    if (rings.length === 0) return null;
                                    rings.sort((a, b) => b.tEnd - a.tEnd);
                                    const lastRing = rings[0];
                                    const firstRing = rings[rings.length - 1];
                                    return {
                                      lineStart: firstRing.start,
                                      lineEnd: lastRing.end,
                                      start: lastRing.start,
                                      end: lastRing.end,
                                    };
                                  } catch (e) {
                                    return null;
                                  }
                                };

                                const totalRotation = (layerState.compassRotation || 0);

                                const getBoundaryPoint = (rawAngle) => {
                                  const rotatedAngle = rawAngle + totalRotation;
                                  const angleRad = (rotatedAngle * Math.PI) / 180;
                                  const lc = clipLineToBoundaryMartinez(compassCenter, {
                                    x: compassCenter.x + Math.cos(angleRad) * 99999,
                                    y: compassCenter.y + Math.sin(angleRad) * 99999,
                                  });
                                  return lc ? lc.lineEnd : null;
                                };

                                const getFullLine = (startPt, endPt) => {
                                  if (!startPt || !endPt) return null;
                                  return { x1: startPt.x, y1: startPt.y, x2: endPt.x, y2: endPt.y };
                                };

                                const getInsideSegmentPoints = (startPt, endPt) => {
                                  if (!startPt || !endPt) return [];
                                  const dx = endPt.x - startPt.x;
                                  const dy = endPt.y - startPt.y;
                                  const totalLen = Math.sqrt(dx * dx + dy * dy);
                                  if (totalLen < 0.001) return [];
                                  const ux = dx / totalLen;
                                  const uy = dy / totalLen;
                                  const tValues = [0, totalLen];
                                  for (let i = 0; i < polygonPoints.length; i++) {
                                    const p1 = polygonPoints[i];
                                    const p2 = polygonPoints[(i + 1) % polygonPoints.length];
                                    const ex = p2.x - p1.x;
                                    const ey = p2.y - p1.y;
                                    const denom = ux * ey - uy * ex;
                                    if (Math.abs(denom) < 1e-10) continue;
                                    const t = ((p1.x - startPt.x) * ey - (p1.y - startPt.y) * ex) / denom;
                                    const s = ((p1.x - startPt.x) * uy - (p1.y - startPt.y) * ux) / denom;
                                    if (t > 1e-8 && t < totalLen - 1e-8 && s >= 0 && s <= 1) tValues.push(t);
                                  }
                                  tValues.sort((a, b) => a - b);
                                  const unique = [tValues[0]];
                                  for (let i = 1; i < tValues.length; i++) {
                                    if (tValues[i] - unique[unique.length - 1] > 1e-6) unique.push(tValues[i]);
                                  }
                                  const result = [];
                                  for (let i = 0; i < unique.length - 1; i++) {
                                    const t1 = unique[i];
                                    const t2 = unique[i + 1];
                                    const mx = startPt.x + ux * ((t1 + t2) / 2);
                                    const my = startPt.y + uy * ((t1 + t2) / 2);
                                    let inside = false;
                                    for (let j = 0, k = polygonPoints.length - 1; j < polygonPoints.length; k = j++) {
                                      const xi = polygonPoints[j].x, yi = polygonPoints[j].y;
                                      const xj = polygonPoints[k].x, yj = polygonPoints[k].y;
                                      if (((yi > my) !== (yj > my)) && (mx < (xj - xi) * (my - yi) / (yj - yi) + xi)) {
                                        inside = !inside;
                                      }
                                    }
                                    if (inside) result.push({
                                      x1: startPt.x + ux * t1, y1: startPt.y + uy * t1,
                                      x2: startPt.x + ux * t2, y2: startPt.y + uy * t2,
                                    });
                                  }
                                  return result;
                                };

                                const findLineIntersectionFn = (p1, p2, p3, p4) => {
                                  if (!p1 || !p2 || !p3 || !p4) return null;
                                  const denom = ((p1.x - p2.x) * (p3.y - p4.y)) - ((p1.y - p2.y) * (p3.x - p4.x));
                                  if (Math.abs(denom) < 0.0001) return null;
                                  const px = (((p1.x * p2.y - p1.y * p2.x) * (p3.x - p4.x)) - ((p1.x - p2.x) * (p3.x * p4.y - p3.y * p4.x))) / denom;
                                  const py = (((p1.x * p2.y - p1.y * p2.x) * (p3.y - p4.y)) - ((p1.y - p2.y) * (p3.x * p4.y - p3.y * p4.x))) / denom;
                                  return { x: px, y: py };
                                };

                                const divideFullLineInsideOnly = (startPt, endPt, numParts) => {
                                  if (!startPt || !endPt || numParts < 2) return [];
                                  const dx = endPt.x - startPt.x;
                                  const dy = endPt.y - startPt.y;
                                  const totalLen = Math.sqrt(dx * dx + dy * dy);
                                  if (totalLen < 0.001) return [];
                                  const ux = dx / totalLen;
                                  const uy = dy / totalLen;
                                  const partLen = totalLen / numParts;
                                  const result = [];
                                  for (let i = 1; i < numParts; i++) {
                                    const t = partLen * i;
                                    result.push({ x: startPt.x + ux * t, y: startPt.y + uy * t });
                                  }
                                  return result;
                                };

                                const divideFromCenterFullLength = (centerPt, endPt, numParts) => {
                                  if (!centerPt || !endPt || numParts < 2) return [];
                                  const dx = endPt.x - centerPt.x;
                                  const dy = endPt.y - centerPt.y;
                                  const totalLen = Math.sqrt(dx * dx + dy * dy);
                                  if (totalLen < 0.001) return [];
                                  const ux = dx / totalLen;
                                  const uy = dy / totalLen;
                                  const EXTEND = 99999;
                                  const farPt = { x: centerPt.x + ux * EXTEND, y: centerPt.y + uy * EXTEND };
                                  const clipped = clipLineToBoundaryMartinez(centerPt, farPt);
                                  if (!clipped) return [];
                                  const bx = clipped.lineEnd.x - centerPt.x;
                                  const by = clipped.lineEnd.y - centerPt.y;
                                  const boundaryLen = Math.sqrt(bx * bx + by * by);
                                  if (boundaryLen < 0.001) return [];
                                  const partLen = boundaryLen / numParts;
                                  const result = [];
                                  for (let i = 1; i < numParts; i++) {
                                    const t = partLen * i;
                                    result.push({ x: centerPt.x + ux * t, y: centerPt.y + uy * t });
                                  }
                                  return result;
                                };

                                const n2 = getBoundaryPoint(-123.75);
                                const n7 = getBoundaryPoint(-56.25);
                                const e2 = getBoundaryPoint(-33.75);
                                const e7 = getBoundaryPoint(33.75);
                                const s2 = getBoundaryPoint(56.25);
                                const s7 = getBoundaryPoint(123.75);
                                const w2 = getBoundaryPoint(146.25);
                                const w7 = getBoundaryPoint(-146.25);

                                const cross1A = getBoundaryPoint(-135);
                                const cross1B = getBoundaryPoint(45);
                                const cross1Clipped = (cross1A && cross1B) ? clipLineToBoundaryMartinez(cross1A, cross1B) : null;
                                const cross1Segments = cross1Clipped ? getInsideSegmentPoints(cross1Clipped.lineStart, cross1Clipped.lineEnd) : [];
                                const cross1Full = cross1A && cross1B ? getFullLine(cross1A, cross1B) : null;

                                const cross2A = getBoundaryPoint(-45);
                                const cross2B = getBoundaryPoint(135);
                                const cross2Clipped = (cross2A && cross2B) ? clipLineToBoundaryMartinez(cross2A, cross2B) : null;
                                const cross2Segments = cross2Clipped ? getInsideSegmentPoints(cross2Clipped.lineStart, cross2Clipped.lineEnd) : [];
                                const cross2Full = cross2A && cross2B ? getFullLine(cross2A, cross2B) : null;

                                const n2e7Clipped = (n2 && e7) ? clipLineToBoundaryMartinez(n2, e7) : null;
                                const s2w7Clipped = (s2 && w7) ? clipLineToBoundaryMartinez(s2, w7) : null;
                                const w2n7Clipped = (w2 && n7) ? clipLineToBoundaryMartinez(w2, n7) : null;
                                const e2s7Clipped = (e2 && s7) ? clipLineToBoundaryMartinez(e2, s7) : null;

                                const n2e7Segments = n2e7Clipped ? getInsideSegmentPoints(n2e7Clipped.lineStart, n2e7Clipped.lineEnd) : [];
                                const s2w7Segments = s2w7Clipped ? getInsideSegmentPoints(s2w7Clipped.lineStart, s2w7Clipped.lineEnd) : [];
                                const w2n7Segments = w2n7Clipped ? getInsideSegmentPoints(w2n7Clipped.lineStart, w2n7Clipped.lineEnd) : [];
                                const e2s7Segments = e2s7Clipped ? getInsideSegmentPoints(e2s7Clipped.lineStart, e2s7Clipped.lineEnd) : [];

                                const n2e7Full = (n2 && e7) ? getFullLine(n2, e7) : null;
                                const s2w7Full = (s2 && w7) ? getFullLine(s2, w7) : null;
                                const w2n7Full = (w2 && n7) ? getFullLine(w2, n7) : null;
                                const e2s7Full = (e2 && s7) ? getFullLine(e2, s7) : null;

                                const crossCenter = (cross1Clipped && cross2Clipped)
                                  ? findLineIntersectionFn(
                                    cross1Clipped.lineStart, cross1Clipped.lineEnd,
                                    cross2Clipped.lineStart, cross2Clipped.lineEnd
                                  ) : null;

                                const cross1Side1Divs = (crossCenter && cross1Full) ? divideFullLineInsideOnly(crossCenter, { x: cross1Full.x1, y: cross1Full.y1 }, 5) : [];
                                const cross1Side2Divs = (crossCenter && cross1Full) ? divideFullLineInsideOnly(crossCenter, { x: cross1Full.x2, y: cross1Full.y2 }, 5) : [];
                                const cross2Side1Divs = (crossCenter && cross2Full) ? divideFullLineInsideOnly(crossCenter, { x: cross2Full.x1, y: cross2Full.y1 }, 5) : [];
                                const cross2Side2Divs = (crossCenter && cross2Full) ? divideFullLineInsideOnly(crossCenter, { x: cross2Full.x2, y: cross2Full.y2 }, 5) : [];

                                const n2_n7_center = (n2e7Clipped && w2n7Clipped)
                                  ? findLineIntersectionFn(n2e7Clipped.lineStart, n2e7Clipped.lineEnd, w2n7Clipped.lineStart, w2n7Clipped.lineEnd) : null;
                                const s2_s7_center = (s2w7Clipped && e2s7Clipped)
                                  ? findLineIntersectionFn(s2w7Clipped.lineStart, s2w7Clipped.lineEnd, e2s7Clipped.lineStart, e2s7Clipped.lineEnd) : null;
                                const w2_w7_center = (w2n7Clipped && s2w7Clipped)
                                  ? findLineIntersectionFn(w2n7Clipped.lineStart, w2n7Clipped.lineEnd, s2w7Clipped.lineStart, s2w7Clipped.lineEnd) : null;
                                const e2_e7_center = (e2s7Clipped && n2e7Clipped)
                                  ? findLineIntersectionFn(e2s7Clipped.lineStart, e2s7Clipped.lineEnd, n2e7Clipped.lineStart, n2e7Clipped.lineEnd) : null;

                                const n2IntersectionToN2Divs = (n2_n7_center && n2e7Full) ? divideFromCenterFullLength(n2_n7_center, { x: n2e7Full.x1, y: n2e7Full.y1 }, 3) : [];
                                const n2IntersectionToN7Divs = (n2_n7_center && w2n7Full) ? divideFromCenterFullLength(n2_n7_center, { x: w2n7Full.x2, y: w2n7Full.y2 }, 3) : [];
                                const e2IntersectionToE2Divs = (e2_e7_center && e2s7Full) ? divideFromCenterFullLength(e2_e7_center, { x: e2s7Full.x1, y: e2s7Full.y1 }, 3) : [];
                                const e2IntersectionToE7Divs = (e2_e7_center && n2e7Full) ? divideFromCenterFullLength(e2_e7_center, { x: n2e7Full.x2, y: n2e7Full.y2 }, 3) : [];
                                const s2IntersectionToS2Divs = (s2_s7_center && s2w7Full) ? divideFromCenterFullLength(s2_s7_center, { x: s2w7Full.x1, y: s2w7Full.y1 }, 3) : [];
                                const s2IntersectionToS7Divs = (s2_s7_center && e2s7Full) ? divideFromCenterFullLength(s2_s7_center, { x: e2s7Full.x2, y: e2s7Full.y2 }, 3) : [];
                                const w2IntersectionToW2Divs = (w2_w7_center && w2n7Full) ? divideFromCenterFullLength(w2_w7_center, { x: w2n7Full.x1, y: w2n7Full.y1 }, 3) : [];
                                const w2IntersectionToW7Divs = (w2_w7_center && s2w7Full) ? divideFromCenterFullLength(w2_w7_center, { x: s2w7Full.x2, y: s2w7Full.y2 }, 3) : [];

                                const opacity = layerState.compassOpacity || 0.8;
                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;

                                const DivCircles = ({ points, prefix }) =>
                                  points.map((point, index) => (
                                    <Circle
                                      key={`${prefix}-${index}`}
                                      x={point.x}
                                      y={point.y}
                                      radius={6}
                                      fill="#DC3545"
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  ));

                                return (
                                  <>
                                    {/* ?o. POLYGON BOUNDARY - static, always visible */}
                                    <Group>
                                      <Line
                                        points={polygonPoints.flatMap(p => [p.x, p.y])}
                                        closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                        listening={false}
                                      />
                                    </Group>

                                    <Group>
                                      {cross1Full && (
                                        <Line points={[cross1Full.x1, cross1Full.y1, cross1Full.x2, cross1Full.y2]}
                                          stroke="#DC3545" strokeWidth={2} opacity={opacity} listening={false} />
                                      )}
                                      {cross1Segments.map((seg, idx) => (
                                        <Line key={`cross1-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={2} opacity={opacity} listening={false} />
                                      ))}
                                      <DivCircles points={cross1Side1Divs} prefix="cross1-s1" />
                                      <DivCircles points={cross1Side2Divs} prefix="cross1-s2" />

                                      {cross2Full && (
                                        <Line points={[cross2Full.x1, cross2Full.y1, cross2Full.x2, cross2Full.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      )}
                                      {cross2Segments.map((seg, idx) => (
                                        <Line key={`cross2-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      ))}
                                      <DivCircles points={cross2Side1Divs} prefix="cross2-s1" />
                                      <DivCircles points={cross2Side2Divs} prefix="cross2-s2" />

                                      {n2e7Full && (
                                        <Line points={[n2e7Full.x1, n2e7Full.y1, n2e7Full.x2, n2e7Full.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      )}
                                      {n2e7Segments.map((seg, idx) => (
                                        <Line key={`n2e7-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      ))}

                                      {s2w7Full && (
                                        <Line points={[s2w7Full.x1, s2w7Full.y1, s2w7Full.x2, s2w7Full.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      )}
                                      {s2w7Segments.map((seg, idx) => (
                                        <Line key={`s2w7-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      ))}

                                      {w2n7Full && (
                                        <Line points={[w2n7Full.x1, w2n7Full.y1, w2n7Full.x2, w2n7Full.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      )}
                                      {w2n7Segments.map((seg, idx) => (
                                        <Line key={`w2n7-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      ))}

                                      {e2s7Full && (
                                        <Line points={[e2s7Full.x1, e2s7Full.y1, e2s7Full.x2, e2s7Full.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      )}
                                      {e2s7Segments.map((seg, idx) => (
                                        <Line key={`e2s7-seg-${idx}`} points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                          stroke="#DC3545" strokeWidth={3} opacity={opacity} listening={false} />
                                      ))}

                                      <DivCircles points={n2IntersectionToN2Divs} prefix="n2-to-n2" />
                                      <DivCircles points={n2IntersectionToN7Divs} prefix="n2-to-n7" />
                                      <DivCircles points={e2IntersectionToE2Divs} prefix="e2-to-e2" />
                                      <DivCircles points={e2IntersectionToE7Divs} prefix="e2-to-e7" />
                                      <DivCircles points={s2IntersectionToS2Divs} prefix="s2-to-s2" />
                                      <DivCircles points={s2IntersectionToS7Divs} prefix="s2-to-s7" />
                                      <DivCircles points={w2IntersectionToW2Divs} prefix="w2-to-w2" />
                                      <DivCircles points={w2IntersectionToW7Divs} prefix="w2-to-w7" />

                                      {all32Directions.map(({ angle, label }) => {
                                        const boundaryPt = getBoundaryPoint(angle);
                                        if (!boundaryPt) return null;
                                        return (
                                          <Circle
                                            key={`boundary-circle-${label}`}
                                            x={boundaryPt.x}
                                            y={boundaryPt.y}
                                            radius={2.5}
                                            fill="#DC3545"
                                            opacity={opacity}
                                            listening={false}
                                          />
                                        );
                                      })}

                                      {all32Directions.map(({ angle, label }) => {
                                        const boundaryPt = getBoundaryPoint(angle + 5.5);
                                        if (!boundaryPt) return null;
                                        const dirX = boundaryPt.x - compassCenter.x;
                                        const dirY = boundaryPt.y - compassCenter.y;
                                        const dist = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (dist < 0.001) return null;
                                        const labelOffset = 18;
                                        const labelAngleRad = Math.atan2(dirY, dirX) + (10 * Math.PI / 180);
                                        const labelX = boundaryPt.x + Math.cos(labelAngleRad) * labelOffset;
                                        const labelY = boundaryPt.y + Math.sin(labelAngleRad) * labelOffset;
                                        return (
                                          <Text
                                            key={`boundary-label-${label}`}
                                            x={labelX}
                                            y={labelY}
                                            text={label}
                                            fontSize={9}
                                            fontStyle="bold"
                                            fill="#DC3545"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={8}
                                            offsetY={5}
                                            opacity={opacity}
                                            listening={false}
                                          />
                                        );
                                      })}
                                    </Group>
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'elemental-5-chart' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const BarChart16Data = calculateBarChart16Data();

                                const getElementFromDirection = (directionName) => {
                                  const dir = directionName.toUpperCase().trim();
                                  if (['SE', 'SSE', 'SOUTH', 'S'].includes(dir)) return 'FIRE';
                                  if (['SSW', 'SW'].includes(dir)) return 'EARTH';
                                  if (['WSW', 'WEST', 'W', 'WNW', 'NW'].includes(dir)) return 'SPACE';  // ?o. METAL ??' SPACE
                                  if (['NNW', 'NORTH', 'N', 'NNE', 'NE'].includes(dir)) return 'WATER';
                                  if (['ENE', 'EAST', 'E', 'ESE'].includes(dir)) return 'AIR';
                                  return null;
                                };

                                const elementAreas = {};

                                BarChart16Data.forEach((item) => {
                                  const element = getElementFromDirection(item.direction);
                                  if (!element) return;

                                  const actualArea = parseFloat(item.area) || 0;

                                  if (!elementAreas[element]) {
                                    elementAreas[element] = {
                                      element,
                                      totalArea: 0,
                                      color: (() => {
                                        switch(element) {
                                          case 'FIRE':  return '#FF0000';
                                          case 'EARTH': return '#FFD700';
                                          case 'SPACE': return '#808080';  // ?o. METAL ??' SPACE, white ??' gray
                                          case 'WATER': return '#0066FF';
                                          case 'AIR':   return '#00AA55';
                                          default:      return '#999999';
                                        }
                                      })(),
                                      directions: []
                                    };
                                  }

                                  elementAreas[element].totalArea += actualArea;
                                  elementAreas[element].directions.push({
                                    direction: item.direction,
                                    area: actualArea
                                  });
                                });

                                const elementsArray = Object.values(elementAreas).filter(e => e.totalArea > 0);
                                const totalArea = elementsArray.reduce((sum, e) => sum + e.totalArea, 0);

                                elementsArray.forEach(item => {
                                  item.percentage = ((item.totalArea / totalArea) * 100).toFixed(1);
                                });

                                elementsArray.sort((a, b) => b.percentage - a.percentage);

                                // ?o. FIX: agar calibration nahi hai ya data empty hai toh 5 elements equal distribution se dikhao
                                const allElements = [
                                  { name: 'FIRE',  color: '#FF0000' },
                                  { name: 'EARTH', color: '#FFD700' },
                                  { name: 'SPACE', color: '#808080' },
                                  { name: 'WATER', color: '#0066FF' },
                                  { name: 'AIR',   color: '#00AA55' }
                                ];

                                const finalElementsArray = elementsArray.length > 0 ? elementsArray : allElements.map(e => ({
                                  element: e.name,
                                  totalArea: 20,
                                  color: e.color,
                                  directions: [],
                                  percentage: '20.0'
                                }));

                                const canvasWidth = A4_WIDTH;
                                const canvasHeight = A4_HEIGHT;
                                const pieRadius = 220;
                                const pieX = canvasWidth / 2;
                                const pieY = canvasHeight / 2 + 20;
                                let currentAngle = 0; // kept for compat

                                return (
                                  <Group>
                                    <Group>
                                      {allElements.map((element, index) => {
                                        const spacing = 600 / 5;
                                        const elementX = pieX - 300 + (spacing * index) + (spacing / 2);
                                        const elementData = finalElementsArray.find(e => e.element === element.name);
                                        const hasData = !!elementData;
                                        const isSpace = element.name === 'SPACE';  // ?o. isMetal ??' isSpace

                                        return (
                                          <Group key={`zone-line-${element.name}`}>
                                            <Circle
                                              x={elementX}
                                              y={30}
                                              radius={12}
                                              fill={element.color}
                                              stroke={isSpace ? '#555555' : '#FFFFFF'}   // ?o.
                                              strokeWidth={isSpace ? 2 : 2}            // ?o.
                                              opacity={hasData ? 1 : 0.3}
                                              listening={false}
                                            />
                                            <Text
                                              x={elementX}
                                              y={45}
                                              text={element.name}
                                              fontSize={16}
                                              fontStyle="bold"
                                              fill={hasData ? '#333333' : '#999999'}
                                              align="center"
                                              verticalAlign="top"
                                              opacity={hasData ? 1 : 0.5}
                                              listening={false}
                                            />
                                            {hasData && (
                                              <Text
                                                x={elementX}
                                                y={65}
                                                text={`${elementData.percentage}%`}
                                                fontSize={14}
                                                fontStyle="bold"
                                                fill="#333333"
                                                align="center"
                                                verticalAlign="top"
                                                listening={false}
                                              />
                                            )}
                                          </Group>
                                        );
                                      })}
                                    </Group>

                                    
                                    {(() => {
                                      const totalAreaFinal = finalElementsArray.reduce((sum, e) => sum + e.totalArea, 0);
                                      let angleCounter = 0;
                                      return finalElementsArray.map((item, index) => {
                                      if (item.totalArea === 0) return null;

                                      const sectorAngle = (item.totalArea / totalAreaFinal) * 360;
                                      const sectorAngleRad = (sectorAngle * Math.PI) / 180;
                                      const startAngleRad = (angleCounter * Math.PI) / 180;
                                      const endAngleRad = startAngleRad + sectorAngleRad;

                                      const startX = pieX + pieRadius * Math.cos(startAngleRad);
                                      const startY = pieY + pieRadius * Math.sin(startAngleRad);

                                      const labelAngle = startAngleRad + sectorAngleRad / 2;
                                      const labelDistance = pieRadius * 0.65;
                                      const labelX = pieX + labelDistance * Math.cos(labelAngle);
                                      const labelY = pieY + labelDistance * Math.sin(labelAngle);

                                      angleCounter += sectorAngle;

                                      const isLargeEnough = sectorAngle > 15;
                                      const isSpace = item.element === 'SPACE';  // ?o. isMetal ??' isSpace

                                      return (
                                        <Group key={`pie-${item.element}`}>
                                          <Shape
                                            sceneFunc={(context, shape) => {
                                              context.beginPath();
                                              context.moveTo(pieX, pieY);
                                              context.lineTo(startX, startY);
                                              context.arc(pieX, pieY, pieRadius, startAngleRad, endAngleRad, false);
                                              context.lineTo(pieX, pieY);
                                              context.closePath();
                                              context.fillStyle = item.color;
                                              context.globalAlpha = 0.85;
                                              context.fill();
                                              context.strokeStyle = isSpace ? '#555555' : (layerState.compassColor || '#000000');  // ?o.
                                              context.lineWidth = isSpace ? 2 : (layerState.compassLineWeight || 2);              // ?o.
                                              context.globalAlpha = 1;
                                              context.stroke();
                                            }}
                                            listening={false}
                                          />
                                          {isLargeEnough && (
                                            <Text
                                              x={labelX}
                                              y={labelY}
                                              text={`${item.percentage}%`}
                                              fontSize={16}
                                              fontStyle="bold"
                                              fill={isSpace ? '#ffffff' : '#000000'}  // ?o. gray sector pe white text
                                              align="center"
                                              verticalAlign="middle"
                                              listening={false}
                                            />
                                          )}
                                        </Group>
                                      );
                                    });
                                      })()}
                                  </Group>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'coloured-zone' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const getDirectionColor = (direction) => {
                                  const dir = direction.toUpperCase().trim();
                                  if (['SSE', 'SOUTH', 'S', 'SW'].includes(dir)) return '#FF0000';
                                  if (['SE', 'SSW'].includes(dir)) return '#FFFF00';
                                  if (['ENE', 'EAST', 'E', 'ESE'].includes(dir)) return '#00FF00';
                                  if (['NE', 'NNE', 'NNW', 'NW'].includes(dir)) return '#0000FF';
                                  if (['NORTH', 'N', 'WSW', 'WEST', 'W', 'WNW'].includes(dir)) return '#FFFFFF';
                                  return '#00A19D';
                                };

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {mainDirections.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % mainDirections.length;
                                      const startAngleDeg = angle + (layerState.compassRotation || 0);
                                      const endAngleDeg = mainDirections[nextIndex].angle + (layerState.compassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      const sectorColor = getDirectionColor(area);

                                      return clippedRings.map((ring, ringIndex) => (
                                        <Line
                                          key={`colored-sector-${area}-ring-${ringIndex}`}
                                          points={ring.flatMap(p => [p.x, p.y])}
                                          closed
                                          fill={sectorColor}
                                          opacity={compassOpacity2 || 0.3}
                                          stroke={layerState.compassColor || '#000000'}
                                          strokeWidth={layerState.compassLineWeight || 2}
                                          listening={false}
                                        />
                                      ));
                                    })}

                                    {layerState.showSectorAreas &&
                                      mainDirections.map(({ angle, area }, index) => {
                                        const normalizeAngle = (a) => {
                                          while (a < 0) a += 360;
                                          while (a >= 360) a -= 360;
                                          return a;
                                        };

                                        const nextIndex = (index + 1) % mainDirections.length;
                                        const nextAngle = mainDirections[nextIndex].angle;

                                        let middleAngle = nextAngle < angle
                                          ? (angle + nextAngle + 360) / 2
                                          : (angle + nextAngle) / 2;

                                        middleAngle = normalizeAngle(middleAngle);

                                        const adjustedAngle = normalizeAngle(
                                          middleAngle - (parentCompassRotation || 0) + (layerState.compassRotation || 0)
                                        );

                                        const intersection = findPolygonIntersection(compassCenter, adjustedAngle);
                                        if (!intersection) return null;

                                        const dirX = intersection.x - compassCenter.x;
                                        const dirY = intersection.y - compassCenter.y;
                                        const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (distance < 5) return null;

                                        const outsideMultiplier = 1.18;
                                        const labelX = compassCenter.x + (dirX / distance) * (distance * outsideMultiplier);
                                        const labelY = compassCenter.y + (dirY / distance) * (distance * outsideMultiplier);

                                        return (
                                          <Text
                                            key={`label-${area}`}
                                            x={labelX}
                                            y={labelY}
                                            text={area}
                                            fontSize={15}
                                            fontStyle="bold"
                                            fill="#4da9c8"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={area.length * 4}
                                            offsetY={7}
                                            listening={false}
                                          />
                                        );
                                      })}

                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill="#000000"
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'elemental-5-zone' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };
                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.5;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);

                                    let result = null;
                                    try { result = martinez.intersection(sectorPoly, boundaryPoly); } catch(e) { result = null; }

                                    // ?o. FALLBACK: if martinez fails/empty, manually clip sector points inside boundary
                                    if (!result || result.length === 0) {
                                      const toRad = (deg) => (deg * Math.PI) / 180;
                                      let start = toRad(startAngleDeg);
                                      let end = toRad(endAngleDeg);
                                      if (end <= start) end += Math.PI * 2;
                                      const steps = 64;
                                      const step = (end - start) / steps;
                                      const pointInPolygon = (px, py, poly) => {
                                        let inside = false;
                                        for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
                                          const xi = poly[i].x, yi = poly[i].y;
                                          const xj = poly[j].x, yj = poly[j].y;
                                          if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
                                            inside = !inside;
                                          }
                                        }
                                        return inside;
                                      };
                                      const fallbackPoints = [center];
                                      for (let i = 0; i <= steps; i++) {
                                        const angle = start + i * step;
                                        const px = center.x + dynamicRadius * Math.cos(angle);
                                        const py = center.y + dynamicRadius * Math.sin(angle);
                                        if (pointInPolygon(px, py, boundaryPoints)) {
                                          fallbackPoints.push({ x: px, y: py });
                                        } else {
                                          const angleDeg = (angle * 180) / Math.PI;
                                          const edgePt = findPolygonIntersection ? findPolygonIntersection(center, angleDeg) : null;
                                          if (edgePt && pointInPolygon(edgePt.x, edgePt.y, boundaryPoints)) {
                                            fallbackPoints.push(edgePt);
                                          }
                                        }
                                      }
                                      if (fallbackPoints.length >= 3) return [fallbackPoints];
                                      return [];
                                    }

                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const getDirectionColor = (direction) => {
                                  const dir = direction.toUpperCase().trim();
                                  if (['SE', 'SSE', 'SOUTH', 'S'].includes(dir)) return '#FF0000';
                                  if (['SSW', 'SW'].includes(dir)) return '#FFD700';
                                  if (['WSW', 'WEST', 'W', 'WNW', 'NW'].includes(dir)) return '#808080';
                                  if (['NNW', 'NORTH', 'N', 'NNE', 'NE'].includes(dir)) return '#0066FF';
                                  if (['ENE', 'EAST', 'E', 'ESE'].includes(dir)) return '#00AA55';
                                  return '#ffffff';
                                };

                                return (
                                  <>
                                    {/* ?o. BOUNDARY LINE ??" ALWAYS VISIBLE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {mainDirections.map(({ angle, area }, index) => {
                                      const nextIndex = (index + 1) % mainDirections.length;
                                      const startAngleDeg = angle + (layerState.compassRotation || 0);
                                      const endAngleDeg = mainDirections[nextIndex].angle + (layerState.compassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );

                                      if (!clippedRings || clippedRings.length === 0) return null;

                                      const sectorColor = getDirectionColor(area);

                                      return clippedRings.map((ring, ringIndex) => (
                                        <Line
                                          key={`elemental-sector-${area}-ring-${ringIndex}`}
                                          points={ring.flatMap(p => [p.x, p.y])}
                                          closed
                                          fill={sectorColor}
                                          opacity={compassOpacity2 || 0.3}
                                          stroke={layerState.compassColor || '#000000'}
                                          strokeWidth={layerState.compassLineWeight || 2}
                                          listening={false}
                                        />
                                      ));
                                    })}

                                    {layerState.showSectorAreas &&
                                      mainDirections.map(({ angle, area }, index) => {
                                        const normalizeAngle = (a) => {
                                          while (a < 0) a += 360;
                                          while (a >= 360) a -= 360;
                                          return a;
                                        };

                                        const nextIndex = (index + 1) % mainDirections.length;
                                        const nextAngle = mainDirections[nextIndex].angle;

                                        let middleAngle = nextAngle < angle
                                          ? (angle + nextAngle + 360) / 2
                                          : (angle + nextAngle) / 2;

                                        middleAngle = normalizeAngle(middleAngle);

                                        const adjustedAngle = normalizeAngle(
                                          middleAngle - (parentCompassRotation || 0) + (layerState.compassRotation || 0)
                                        );

                                        const intersection = findPolygonIntersection(compassCenter, adjustedAngle);
                                        if (!intersection) return null;

                                        const dirX = intersection.x - compassCenter.x;
                                        const dirY = intersection.y - compassCenter.y;
                                        const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                        if (distance < 5) return null;

                                        const outsideMultiplier = 1.18;
                                        const labelX = compassCenter.x + (dirX / distance) * (distance * outsideMultiplier);
                                        const labelY = compassCenter.y + (dirY / distance) * (distance * outsideMultiplier);
                                        const labelColor = getDirectionColor(area);

                                        return (
                                          <Text
                                            key={`elemental-label-${area}`}
                                            x={labelX}
                                            y={labelY}
                                            text={area}
                                            fontSize={15}
                                            fontStyle="bold"
                                            fill={labelColor}
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={area.length * 4}
                                            offsetY={7}
                                            listening={false}
                                          />
                                        );
                                      })}

                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill="#000000"
                                      stroke="#FFFFFF"
                                      strokeWidth={layerState.compassLineWeight || 2}
                                      opacity={1}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}
                          {/* ── Vedic Generative Zones: 32 Zones / 64 Grid / 64 & 81 Padvinyash /
                              Elemental Zone / Planet Zoning — all computed LIVE against the actual
                              plot polygon + compass center (same technique as compass-32 /
                              elemental-5-zone above), not a fixed square image. ── */}
                          {VEDIC_GENERATIVE_IDS.includes(layerId) && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const cleanRings = (result) => {
                                  if (!result || result.length === 0) return [];
                                  const allRings = [];
                                  for (const polygon of result) {
                                    for (const ring of polygon) {
                                      if (!ring || ring.length < 3) continue;
                                      const cleaned = (
                                        ring[0][0] === ring[ring.length - 1][0] &&
                                        ring[0][1] === ring[ring.length - 1][1]
                                      ) ? ring.slice(0, -1) : ring;
                                      if (cleaned.length >= 3) allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                    }
                                  }
                                  return allRings;
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    let result = null;
                                    try { result = martinez.intersection(sectorPoly, boundaryPoly); } catch (e) { result = null; }
                                    return cleanRings(result);
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const clipPolygonWithBoundary = (points, boundaryPoints) => {
                                  try {
                                    const result = martinez.intersection(toMartinezPolygon(points), toMartinezPolygon(boundaryPoints));
                                    return cleanRings(result);
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const scalePolygonTowardCenter = (points, center, factor) =>
                                  points.map(p => ({ x: center.x + (p.x - center.x) * factor, y: center.y + (p.y - center.y) * factor }));

                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;
                                const fillOpacity = layerState.compassOpacity !== undefined ? layerState.compassOpacity : 0.5;
                                const rotation = layerState.compassRotation || 0;
                                const lineColor = layerState.compassColor || '#000000';
                                const lineWeight = layerState.compassLineWeight || 2;

                                const boundaryLine = (
                                  <Line
                                    points={polygonPoints.flatMap(p => [p.x, p.y])}
                                    closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                    fill="transparent"
                                    listening={false}
                                  />
                                );

                                const centerDot = (
                                  <Circle x={compassCenter.x} y={compassCenter.y} radius={5} fill="#000000" stroke="#FFFFFF" strokeWidth={lineWeight} listening={false} />
                                );

                                // ---------- 32 Zones Vedic / 64 Grid: radial lines + labels ----------
                                if (layerId === 'Vedic-zones-32' || layerId === 'Vedic-numbers-64') {
                                  const dirs = layerId === 'Vedic-zones-32' ? all32Directions : all64Directions;
                                  const isFine = layerId === 'Vedic-numbers-64';
                                  // 64 Grid was already correct at -2.8125°, unchanged.
                                  // 32 Zones Vedic: all32Directions stores each zone at its
                                  // *boundary* angle (N5 = -90 = due North = N4|N5 line), so the
                                  // radial ray must be pushed clockwise by half a zone (11.25/2 =
                                  // 5.625°) to sit at the zone's centre — e.g. N5's ray at -84.375°,
                                  // right through the middle of the N5 slice.
                                  const vedicOffset = layerId === 'Vedic-zones-32' ? 5.625 : -2.8125;
                                  return (
                                    <>
                                      {boundaryLine}
                                      {dirs.map(({ angle }, index) => {
                                        // NOTE: findPolygonIntersection() already adds the real compass
                                        // calibration rotation internally — do NOT add `rotation` again here,
                                        // that was causing a double-rotation bug (North fixed at the wrong spot).
                                        const intersection = findPolygonIntersection(compassCenter, angle + vedicOffset);
                                        if (!intersection) return null;
                                        return (
                                          <Line
                                            key={`vz-ray-${index}`}
                                            points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                            stroke={lineColor}
                                            strokeWidth={Math.max(0.5, lineWeight * (isFine ? 0.4 : 0.6))}
                                            opacity={lineOpacity}
                                            listening={false}
                                          />
                                        );
                                      })}
                                      {layerState.showSectorAreas && dirs.map(({ angle }, index) => {
                                        const nextAngle = dirs[(index + 1) % dirs.length].angle;
                                        let mid = nextAngle < angle ? (angle + nextAngle + 360) / 2 : (angle + nextAngle) / 2;
                                        const intersection = findPolygonIntersection(compassCenter, mid + vedicOffset);
                                        if (!intersection) return null;
                                        const dirX = intersection.x - compassCenter.x, dirY = intersection.y - compassCenter.y;
                                        const dist = Math.hypot(dirX, dirY);
                                        if (dist < 5) return null;
                                        const placementRatio = isFine ? 0.85 : 1.1;
                                        const lx = compassCenter.x + (dirX / dist) * (dist * placementRatio);
                                        const ly = compassCenter.y + (dirY / dist) * (dist * placementRatio);
                                        const label = dirs[(index + 1) % dirs.length].label;
                                        return (
                                          <Text
                                            key={`vz-label-${index}`}
                                            x={lx} y={ly} text={label}
                                            fontSize={isFine ? 14 : 14}
                                            fontStyle="bold"
                                            fill={lineColor}
                                            align="center" verticalAlign="middle"
                                            offsetX={label.length * (isFine ? 4 : 4)} offsetY={7}
                                            listening={false}
                                          />
                                        );
                                      })}
                                      {centerDot}
                                    </>
                                  );
                                }

                                // ---------- Elemental Zone (Vedic): 4 colour quadrants + Brahmasthan ----------
                                if (layerId === 'Vedic-elemental-zone') {
                                  const ELEMENTS = [
                                    { name: 'AIR', color: '#8fbf82', from: 25, to: 29 },
                                    { name: 'WATER', color: '#7fa8d9', from: 1, to: 5 },
                                    { name: 'FIRE', color: '#e07a6b', from: 9, to: 13 },
                                    { name: 'EARTH', color: '#e8d977', from: 17, to: 21 },
                                  ];
                                  const brahmasthan = scalePolygonTowardCenter(polygonPoints, compassCenter, 1 / 5);
                                  return (
                                    <>
                                      {boundaryLine}
                                      {ELEMENTS.map((el) => {
                                        // clipSectorWithBoundary does NOT go through findPolygonIntersection,
                                        // so it does not auto-apply compass calibration — add it manually here.
                                        const a1 = all32Directions[el.from].angle + rotation;
                                        const a2 = all32Directions[el.to].angle + rotation;
                                        const rings = clipSectorWithBoundary(compassCenter, a1, a2, polygonPoints);
                                        return rings.map((ring, ri) => (
                                          <Line
                                            key={`elem-${el.name}-${ri}`}
                                            points={ring.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={el.color}
                                            opacity={fillOpacity}
                                            stroke={lineColor}
                                            strokeWidth={lineWeight}
                                            listening={false}
                                          />
                                        ));
                                      })}
                                      {ELEMENTS.map((el) => {
                                        // findPolygonIntersection() already adds compass calibration internally —
                                        // do not add `rotation` here too (that caused the label/colour mismatch).
                                        const mid = (all32Directions[el.from].angle + all32Directions[el.to].angle) / 2;
                                        const intersection = findPolygonIntersection(compassCenter, mid);
                                        if (!intersection) return null;
                                        const dirX = intersection.x - compassCenter.x, dirY = intersection.y - compassCenter.y;
                                        const dist = Math.hypot(dirX, dirY);
                                        const lx = compassCenter.x + (dirX / dist) * (dist * 0.65);
                                        const ly = compassCenter.y + (dirY / dist) * (dist * 0.65);
                                        return (
                                          <Text
                                            key={`elem-label-${el.name}`}
                                            x={lx} y={ly} text={el.name}
                                            fontSize={26} fontStyle="bold" fill="#222222"
                                            align="center" verticalAlign="middle"
                                            offsetX={el.name.length * 8} offsetY={13}
                                            listening={false}
                                          />
                                        );
                                      })}
                                      <Line points={brahmasthan.flatMap(p => [p.x, p.y])} closed fill="#ffffff" opacity={1} stroke={lineColor} strokeWidth={lineWeight} listening={false} />
                                      {(() => {
                                        // Brahmasthan is the plan's own polygon scaled to 1/5 toward its centre, so its
                                        // width varies with the plan's shape — a fixed 15.4px font (sized for a roughly
                                        // square box) ran past a narrower box's edges. Shrinking to fit the box's actual
                                        // width (never bigger than the original 15.4) keeps "Brahmasthan" inside it.
                                        const bxs = brahmasthan.map(p => p.x);
                                        const boxW = Math.max(...bxs) - Math.min(...bxs);
                                        const label = 'Space';
                                        const fontSize = Math.max(6, Math.min(15.4, (boxW - 10) / (label.length * 0.62)));
                                        return (
                                          <Text
                                            x={compassCenter.x} y={compassCenter.y}
                                            text={label} fontSize={fontSize} fontStyle="bold" fill="#333333"
                                            align="center" verticalAlign="middle"
                                            offsetX={fontSize * label.length * 0.2645} offsetY={fontSize * 0.545}
                                            listening={false}
                                          />
                                        );
                                      })()}
                                    </>
                                  );
                                }

                                // ---------- 64 / 81 Padvinyash: 4 concentric plot-shaped bands ----------
                                if (layerId === 'Vedic-64-padvinyash' || layerId === 'Vedic-81-padvinyash') {
                                  const colored = layerId === 'Vedic-81-padvinyash';
                                  const factors = [1, 0.75, 0.5, 0.25];
                                  const colors = colored ? ['#f4a3a3', '#a8d9a8', '#f7e79a', '#a9c3ea'] : ['#d9d9d9', '#d9d9d9', '#d9d9d9', '#d9d9d9'];
                                  const bands = factors.map((f, i) => ({
                                    points: scalePolygonTowardCenter(polygonPoints, compassCenter, f),
                                    fill: colors[i],
                                  }));
                                  return (
                                    <>
                                      {/* fill-only pass so "Opacity" only affects the colour fill */}
                                      {bands.map((b, i) => (
                                        <Line
                                          key={`pad-band-fill-${i}`}
                                          points={b.points.flatMap(p => [p.x, p.y])}
                                          closed
                                          fill={b.fill}
                                          opacity={fillOpacity}
                                          listening={false}
                                        />
                                      ))}
                                      {/* stroke-only pass so "Line Opacity" independently affects the boundary lines */}
                                      {bands.map((b, i) => (
                                        <Line
                                          key={`pad-band-stroke-${i}`}
                                          points={b.points.flatMap(p => [p.x, p.y])}
                                          closed
                                          fill="transparent"
                                          stroke={lineColor}
                                          strokeWidth={i === 0 ? lineWeight * 1.3 : lineWeight * 0.8}
                                          opacity={lineOpacity}
                                          listening={false}
                                        />
                                      ))}
                                      {centerDot}
                                    </>
                                  );
                                }

                                // ---------- Vedic Planet Zoning: 3x3 grid over the plot ----------
                                if (layerId === 'Vedic-planet-zoning') {
                                  const PLANET_GRID = [
                                    ['Moon (2)', 'Mercury (5)', 'Jupiter (3)'],
                                    ['Saturn (8)', '', 'Sun (1)'],
                                    ['Rahu Ketu (4)', 'Mars (9)', 'Venus (6)'],
                                  ];
                                  // Rotate the bounding box by the compass calibration BEFORE gridding it, so
                                  // the 3x3 grid follows true North instead of always sitting axis-aligned
                                  // to the canvas.
                                  const rotRad = (rotation * Math.PI) / 180;
                                  const cosR = Math.cos(rotRad), sinR = Math.sin(rotRad);
                                  const rotatePt = (p) => ({
                                    x: compassCenter.x + (p.x - compassCenter.x) * cosR - (p.y - compassCenter.y) * sinR,
                                    y: compassCenter.y + (p.x - compassCenter.x) * sinR + (p.y - compassCenter.y) * cosR,
                                  });
                                  const unrotatePt = (p) => ({
                                    x: compassCenter.x + (p.x - compassCenter.x) * cosR + (p.y - compassCenter.y) * sinR,
                                    y: compassCenter.y - (p.x - compassCenter.x) * sinR + (p.y - compassCenter.y) * cosR,
                                  });
                                  // Work out the bounding box in UN-rotated (calibration-aligned) space so the
                                  // grid is centred on true North/East, then rotate every point back for display.
                                  const alignedPts = polygonPoints.map(unrotatePt);
                                  const xs = alignedPts.map(p => p.x), ys = alignedPts.map(p => p.y);
                                  const minX = Math.min(...xs), maxX = Math.max(...xs);
                                  const minY = Math.min(...ys), maxY = Math.max(...ys);
                                  const cw = (maxX - minX) / 3, ch = (maxY - minY) / 3;
                                  // Area-weighted centroid so the label lands inside whatever shape the
                                  // cell actually clips down to, not the un-clipped rectangle's centre
                                  // (which can sit outside the plot for anything but a perfect rectangle).
                                  const ringArea = (pts) => {
                                    let a = 0;
                                    for (let k = 0; k < pts.length; k++) {
                                      const p1 = pts[k], p2 = pts[(k + 1) % pts.length];
                                      a += p1.x * p2.y - p2.x * p1.y;
                                    }
                                    return Math.abs(a) / 2;
                                  };
                                  const ringCentroid = (pts) => {
                                    let cx6 = 0, cy6 = 0, a6 = 0;
                                    for (let k = 0; k < pts.length; k++) {
                                      const p1 = pts[k], p2 = pts[(k + 1) % pts.length];
                                      const cross = p1.x * p2.y - p2.x * p1.y;
                                      a6 += cross;
                                      cx6 += (p1.x + p2.x) * cross;
                                      cy6 += (p1.y + p2.y) * cross;
                                    }
                                    if (Math.abs(a6) < 1e-6) return { x: pts[0].x, y: pts[0].y };
                                    return { x: cx6 / (3 * a6), y: cy6 / (3 * a6) };
                                  };

                                  const cells = [];
                                  for (let row = 0; row < 3; row++) {
                                    for (let col = 0; col < 3; col++) {
                                      const cx = minX + col * cw, cy = minY + row * ch;
                                      const rectPts = [
                                        { x: cx, y: cy }, { x: cx + cw, y: cy },
                                        { x: cx + cw, y: cy + ch }, { x: cx, y: cy + ch },
                                      ].map(rotatePt);
                                      const fallbackCenter = rotatePt({ x: cx + cw / 2, y: cy + ch / 2 });

                                      const clippedRings = clipPolygonWithBoundary(rectPts, polygonPoints);
                                      let labelPt = fallbackCenter;
                                      if (clippedRings.length > 0) {
                                        const biggest = clippedRings.reduce((best, r) =>
                                          ringArea(r) > ringArea(best) ? r : best, clippedRings[0]);
                                        labelPt = ringCentroid(biggest);
                                      }

                                      cells.push({ rectPts, clippedRings, cx: labelPt.x, cy: labelPt.y, text: PLANET_GRID[row][col] });
                                    }
                                  }
                                  return (
                                    <>
                                      {boundaryLine}
                                      {cells.map((c, i) => {
                                        return c.clippedRings.map((ring, ri) => (
                                          <Line
                                            key={`pz-cell-${i}-${ri}`}
                                            points={ring.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={c.text ? 'transparent' : '#F5F7FA'}
                                            opacity={fillOpacity}
                                            stroke={lineColor}
                                            strokeWidth={lineWeight * 0.7}
                                            listening={false}
                                          />
                                        ));
                                      })}
                                      {cells.filter(c => c.text).map((c, i) => {
                                        // Button-style chip: white background, black border, black text.
                                        const chipW = c.text.length * 6.2 + 16;
                                        const chipH = 20;
                                        return (
                                          <React.Fragment key={`pz-label-${i}`}>
                                            <Rect
                                              x={c.cx - chipW / 2} y={c.cy - chipH / 2}
                                              width={chipW} height={chipH}
                                              fill="#ffffff" stroke="#000000" strokeWidth={1.2}
                                              cornerRadius={5}
                                              listening={false}
                                            />
                                            <Text
                                              x={c.cx} y={c.cy} text={c.text}
                                              fontSize={9.5} fontStyle="bold" fill="#000000"
                                              align="center" verticalAlign="middle"
                                              offsetX={c.text.length * 2.6} offsetY={5}
                                              listening={false}
                                            />
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                }

                                return null;
                              })()}
                            </Layer>
                          )}
                          {layerId === 'floor-plan' && isPolygonComplete && polygonPoints.length >= 3 && (() => {
                            const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;
                            return (
                              <Layer>
                                {/* ?o. BOUNDARY LINE ??" compassColor + compassLineWeight se controlled */}
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                  fill="transparent"
                                  listening={false}
                                />
                              </Layer>
                            );
                          })()}

                          {layerId === 'tri-dosh-Vedic' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const rotation = (layerState.compassRotation || 0) - (parentCompassRotation || 0);

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity = layerState.compassOpacity || 0.8;
                                const lineOpacity = layerState.compassLineOpacity !== undefined ? layerState.compassLineOpacity : 1;

                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (
                                    ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]
                                  ) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const directions16 = [
                                  { label: 'E',   angle: 0     },
                                  { label: 'ESE', angle: 22.5  },
                                  { label: 'SE',  angle: 45    },
                                  { label: 'SSE', angle: 67.5  },
                                  { label: 'S',   angle: 90    },
                                  { label: 'SSW', angle: 112.5 },
                                  { label: 'SW',  angle: 135   },
                                  { label: 'WSW', angle: 157.5 },
                                  { label: 'W',   angle: 180   },
                                  { label: 'WNW', angle: 202.5 },
                                  { label: 'NW',  angle: 225   },
                                  { label: 'NNW', angle: 247.5 },
                                  { label: 'N',   angle: 270   },
                                  { label: 'NNE', angle: 292.5 },
                                  { label: 'NE',  angle: 315   },
                                  { label: 'ENE', angle: 337.5 },
                                ];

                                const lines4 = [
                                  { angle: 0   },
                                  { angle: 90  },
                                  { angle: 180 },
                                  { angle: 270 },
                                ];

                                const quadrantLabels = [
                                  { label: 'Wet Kapha', angleStart: 270, angleEnd: 360, fillColor: '#4CAF50' },
                                  { label: 'Pitta',     angleStart: 0,   angleEnd: 90,  fillColor: '#64B5F6' },
                                  { label: 'Dry Kapha', angleStart: 90,  angleEnd: 180, fillColor: '#4CAF50' },
                                  { label: 'Vata',      angleStart: 180, angleEnd: 270, fillColor: '#FFFFFF' },
                                ];
const doshaAcc = {};

                                return (
                                  <>
                                    {/* 1. ?o. FILLS ??" rotation + compassRotation */}
                                    {quadrantLabels.map(({ label, angleStart, angleEnd, fillColor }) => {
                                      const startAngle = angleStart + rotation + (parentCompassRotation || 0); // ?o.
                                      const endAngle   = angleEnd   + rotation + (parentCompassRotation || 0); // ?o.

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter, startAngle, endAngle, polygonPoints
                                      );
                                      if (!clippedRings || clippedRings.length === 0) return null;
{ const _d = label.includes('Kapha') ? 'Kapha' : label; doshaAcc[_d] = (doshaAcc[_d] || 0) + ringsArea(clippedRings); }

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        return (
                                          <Line
                                            key={`fill-${label}-ring-${ringIndex}`}
                                            points={ringPoints.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={fillColor}
                                            opacity={opacity}
                                            stroke="transparent"
                                            strokeWidth={0}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* 2. BOUNDARY LINE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* 3. 4 DIRECTION LINES ??" rotation only */}
                                    {lines4.map(({ angle }) => {
                                      const point = getIntersection(angle + rotation);
                                      if (!point) return null;
                                      return (
                                        <Line
                                          key={`line-${angle}`}
                                          points={[compassCenter.x, compassCenter.y, point.x, point.y]}
                                          stroke={strokeColor}
                                          strokeWidth={strokeWidth}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 4. 16 DIRECTION LABELS ??" rotation only */}
                                    {directions16.map(({ label, angle }) => {
                                      const adjustedAngle = angle + rotation;
                                      const intersection  = getIntersection(adjustedAngle);
                                      if (!intersection) return null;

                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelX  = compassCenter.x + (dirX / distance) * (distance * 1.1);
                                      const labelY  = compassCenter.y + (dirY / distance) * (distance * 1.1);
                                      const offsetX = label.length === 3 ? 14 : 10;

                                      return (
                                        <Text
                                          key={`dir-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={offsetX}
                                          offsetY={8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 5. QUADRANT LABELS ??" rotation only */}
                                    {quadrantLabels.map(({ label, angleStart, angleEnd }) => {
                                      const normalizedEnd = angleEnd === 360 ? 0 : angleEnd;
                                      const midAngle = angleEnd === 360
                                        ? (angleStart + 360) / 2
                                        : (angleStart + angleEnd) / 2;

                                      const p1   = getIntersection(angleStart    + rotation);
                                      const p2   = getIntersection(normalizedEnd + rotation);
                                      const pMid = getIntersection(midAngle      + rotation);

                                      if (!p1 || !p2 || !pMid) return null;

                                      const centerX = (compassCenter.x + p1.x + pMid.x + p2.x) / 4;
                                      const centerY = (compassCenter.y + p1.y + pMid.y + p2.y) / 4;
                                      const labelOffsetX = label.length > 5 ? 35 : 20;
                                      const labelFill = '#000000';

                                      return (
                                        <Text
                                          key={`quadrant-${label}`}
                                          x={centerX}
                                          y={centerY}
                                          text={label}
                                          fontSize={14}
                                          fontStyle="bold"
                                          fill={labelFill}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={labelOffsetX}
                                          offsetY={8}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 6. CENTER POINT */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}  
 
                          {layerId === 'tri-guna-Vedic' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const rotation = (layerState.compassRotation || 0) - (parentCompassRotation || 0);

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity     = layerState.compassOpacity || 0.8;

                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (
                                    ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]
                                  ) ring.push(ring[0]);
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end   = toRad(endAngleDeg);
                                  while (end <= start) end += Math.PI * 2;
                                  const points = [];
                                  points.push([center.x, center.y]);
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    points.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  points.push([center.x, center.y]);
                                  return [points];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result       = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3)
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const staticSectors = [
                                  { label: '-RAJAS(50%)',  fill: '#d3f3f3', labelCol: '#000000', angleStart: 240, angleEnd: 270 },
                                  { label: '+SATTVA(50%)',fill: '#f5e3d1', labelCol: '#000000', angleStart: 270, angleEnd: 300 },
                                  { label: 'SATTVA(100%)',fill: '#d4b78c', labelCol: '#000000', angleStart: 300, angleEnd: 330 },
                                  { label: '+SATTVA(50%)',fill: '#f5e3d1', labelCol: '#000000', angleStart: 330, angleEnd: 360 },
                                  { label: '-RAJAS(50%)', fill: '#d3f3f3', labelCol: '#000000', angleStart:   0, angleEnd:  30 },
                                  { label: 'RAJAS(100%)', fill: '#9bb6e8', labelCol: '#000000', angleStart:  30, angleEnd:  60 },
                                  { label: '+RAJAS(50%)', fill: '#d3f3f3', labelCol: '#000000', angleStart:  60, angleEnd:  90 },
                                  { label: '-TAMAS(50%)', fill: '#d8f5c7', labelCol: '#000000', angleStart:  90, angleEnd: 120 },
                                  { label: 'TAMAS(100%)', fill: '#72d26a', labelCol: '#000000', angleStart: 120, angleEnd: 150 },
                                  { label: '+TAMAS(50%)', fill: '#d8f5c7', labelCol: '#000000', angleStart: 150, angleEnd: 180 },
                                  { label: '+RAJAS(50%)', fill: '#d3f3f3', labelCol: '#000000', angleStart: 180, angleEnd: 210 },
                                  { label: '+RAJAS(100%)',fill: '#9bb6e8', labelCol: '#000000', angleStart: 210, angleEnd: 240 },
                                ];
const gunaAcc = {};

                                const directions16 = [
                                  { label: 'E',   angle:   0   },
                                  { label: 'ESE', angle:  22.5 },
                                  { label: 'SE',  angle:  45   },
                                  { label: 'SSE', angle:  67.5 },
                                  { label: 'S',   angle:  90   },
                                  { label: 'SSW', angle: 112.5 },
                                  { label: 'SW',  angle: 135   },
                                  { label: 'WSW', angle: 157.5 },
                                  { label: 'W',   angle: 180   },
                                  { label: 'WNW', angle: 202.5 },
                                  { label: 'NW',  angle: 225   },
                                  { label: 'NNW', angle: 247.5 },
                                  { label: 'N',   angle: 270   },
                                  { label: 'NNE', angle: 292.5 },
                                  { label: 'NE',  angle: 315   },
                                  { label: 'ENE', angle: 337.5 },
                                ];

                                return (
                                  <>
                                    {/* 1. ?o. SECTOR FILLS ??" rotation + compassRotation */}
                                    {staticSectors.map(({ label, fill, angleStart, angleEnd }, si) => {
                                      if (!polygonPoints || polygonPoints.length < 3) return null;

                                      const startAngle = angleStart + rotation + (parentCompassRotation || 0);
                                      const endAngle   = angleEnd   + rotation + (parentCompassRotation || 0);

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter, startAngle, endAngle, polygonPoints
                                      );
                                      if (!clippedRings || clippedRings.length === 0) return null;
{ const _g = label.includes('SATTVA') ? 'Sattva' : label.includes('RAJAS') ? 'Rajas' : 'Tamas'; gunaAcc[_g] = (gunaAcc[_g] || 0) + ringsArea(clippedRings); }

                                      return clippedRings.map((ringPoints, ri) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        return (
                                          <Line
                                            key={`fill-${si}-${ri}`}
                                            points={ringPoints.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={fill}
                                            opacity={opacity}
                                            stroke="transparent"
                                            strokeWidth={0}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* 2. BOUNDARY LINE */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* 3. DIVISION LINES ??" rotation only */}
                                    {staticSectors.map(({ angleStart }, si) => {
                                      const pt = getIntersection(angleStart + rotation);
                                      if (!pt) return null;
                                      return (
                                        <Line
                                          key={`div-line-${si}`}
                                          points={[compassCenter.x, compassCenter.y, pt.x, pt.y]}
                                          stroke={strokeColor}
                                          strokeWidth={strokeWidth}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 4. 16 DIRECTION LABELS ??" rotation only */}
                                    {directions16.map(({ label, angle }) => {
                                      const adjustedAngle = angle + rotation;
                                      const intersection  = getIntersection(adjustedAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelX  = compassCenter.x + (dirX / distance) * (distance * 1.1);
                                      const labelY  = compassCenter.y + (dirY / distance) * (distance * 1.1);
                                      const offsetX = label.length === 3 ? 14 : 10;

                                      return (
                                        <Text
                                          key={`dir-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={offsetX}
                                          offsetY={8}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 5. SECTOR NAME LABELS ??" rotation only */}
                                    {staticSectors.map(({ label, labelCol, angleStart, angleEnd }, si) => {
                                      const midAngle = (angleStart + angleEnd) / 2 + rotation;
                                      const inter    = getIntersection(midAngle);
                                      if (!inter) return null;

                                      const dirX = inter.x - compassCenter.x;
                                      const dirY = inter.y - compassCenter.y;
                                      const dist = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (dist < 5) return null;

                                      const labelX = compassCenter.x + (dirX / dist) * (dist * 0.72);
                                      const labelY = compassCenter.y + (dirY / dist) * (dist * 0.72);

                                      return (
                                        <Text
                                          key={`sector-lbl-${si}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={9}
                                          fontStyle="bold"
                                          fill={labelCol}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={label.length * 2.6}
                                          offsetY={5}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 6. CENTER POINT */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          
                          {layerId === 'tri-dosh-modern' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (
                                    ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]
                                  ) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end   = toRad(endAngleDeg);
                                  while (end <= start) end += Math.PI * 2;
                                  const pts  = [];
                                  const step = (end - start) / steps;
                                  pts.push([center.x, center.y]);
                                  for (let i = 0; i <= steps; i++) {
                                    const a = start + i * step;
                                    pts.push([center.x + radiusOuter * Math.cos(a), center.y + radiusOuter * Math.sin(a)]);
                                  }
                                  pts.push([center.x, center.y]);
                                  return [pts];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result       = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const rotation  = (layerState.compassRotation || 0) - (parentCompassRotation || 0);
                                const parentRot = parentCompassRotation || 0;

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor     || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity     = layerState.compassOpacity   || 0.8;

                                const getTriGunaFill = (baseSvgAngle) => {
                                  const normalized = ((baseSvgAngle + 90) % 360 + 360) % 360;
                                  if (normalized >= 105 && normalized < 225) return '#B83000'; // RAJAS  ??" dark red-orange
                                  if (normalized >= 225 && normalized < 345) return '#0D5C99'; // SATTVA ??" dark sky blue
                                  return '#1E6B22';                                             // TAMAS  ??" dark green
                                };

                                const FILL_OPACITY = 0.45;

                                const dir16 = [
                                  'N','NNE','NE','ENE',
                                  'E','ESE','SE','SSE',
                                  'S','SSW','SW','WSW',
                                  'W','WNW','NW','NNW',
                                ];

                                const sectors = Array.from({ length: 24 }, (_, i) => ({
                                  index:        i + 1,
                                  baseSvgAngle: i * 15 - 90,
                                }));
const zoneAcc = {};

                                // ?o. Helper to get boundary point at specific angle (for partition lines)
                                const getBoundaryPointAtAngle = (angleDeg) => {
                                  const adjusted = angleDeg + rotation + parentRot;
                                  return getIntersection(adjusted);
                                };

                                // ?o. Partition lines at zone boundaries (RAJAS, SATTVA, TAMAS divisions)
                                const partitionAngles = [
                                  { angle: 105, name: 'RAJAS-SATTVA' },  // RAJAS to SATTVA boundary
                                  { angle: 225, name: 'SATTVA-TAMAS' },  // SATTVA to TAMAS boundary
                                  { angle: 345, name: 'TAMAS-RAJAS' },   // TAMAS to RAJAS boundary (wrap)
                                ];

                                return (
                                  <>
                                    {/* ?"??"? 1. SECTOR FILLS ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {sectors.map(({ index, baseSvgAngle }) => {
                                      const startAngleDeg = baseSvgAngle + rotation + parentRot;
                                      const endAngleDeg   = baseSvgAngle + 15 + rotation + parentRot;

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );
                                      if (!clippedRings || clippedRings.length === 0) return null;
{ const _f = getTriGunaFill(baseSvgAngle); const _z = _f === '#B83000' ? 'first' : _f === '#0D5C99' ? 'second' : 'third'; zoneAcc[_z] = (zoneAcc[_z] || 0) + ringsArea(clippedRings); }

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        return (
                                          <Line
                                            key={`tds-fill-${index}-${ringIndex}`}
                                            points={ringPoints.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={getTriGunaFill(baseSvgAngle)}
                                            opacity={FILL_OPACITY}
                                            stroke="transparent"
                                            strokeWidth={0}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?"??"? 2. BOUNDARY LINE ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?"??"? 6. OUTER 16 DIRECTION LABELS ??" rotation only ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {dir16.map((label, i) => {
                                      const svgAngle     = i * 22.5 - 90 + rotation;
                                      const intersection = getIntersection(svgAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      return (
                                        <Text
                                          key={`tds-dirlabel-${i}`}
                                          x={compassCenter.x + (dirX / distance) * (distance * 1.1)}
                                          y={compassCenter.y + (dirY / distance) * (distance * 1.1)}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={label.length * 3.5}
                                          offsetY={6}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 7. ZONE LABELS (RAJAS / SATTVA / TAMAS) ??" rotation only ?"??"??"??"??"??"? */}
                                    {[
                                      { name: 'RAJAS',  svgCenter: 80, color: '#8B1A00' },
                                      { name: 'SATTVA', svgCenter: 200, color: '#083D6E' },
                                      { name: 'TAMAS',  svgCenter: 300,  color: '#144D17' },
                                    ].map(({ name, svgCenter, color }) => {
                                      const svgAngle     = svgCenter + rotation;
                                      const intersection = getIntersection(svgAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      return (
                                        <Text
                                          key={`tds-zone-${name}`}
                                          x={compassCenter.x + (dirX / distance) * (distance * 0.55)}
                                          y={compassCenter.y + (dirY / distance) * (distance * 0.55)}
                                          text={name}
                                          fontSize={14}
                                          fontStyle="bold"
                                          fill={color}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={name.length * 4}
                                          offsetY={7}
                                          opacity={1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 8. CENTER POINT ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}

                          {layerId === 'tri-guna-modern' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (
                                    ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]
                                  ) {
                                    ring.push(ring[0]);
                                  }
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end   = toRad(endAngleDeg);
                                  while (end <= start) end += Math.PI * 2;
                                  const pts  = [];
                                  const step = (end - start) / steps;
                                  pts.push([center.x, center.y]);
                                  for (let i = 0; i <= steps; i++) {
                                    const a = start + i * step;
                                    pts.push([center.x + radiusOuter * Math.cos(a), center.y + radiusOuter * Math.sin(a)]);
                                  }
                                  pts.push([center.x, center.y]);
                                  return [pts];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const corners = [
                                      { x: minX, y: minY }, { x: maxX, y: minY },
                                      { x: maxX, y: maxY }, { x: minX, y: maxY },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result       = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3) {
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                        }
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const rotation  = (layerState.compassRotation || 0) - (parentCompassRotation || 0);
                                const parentRot = parentCompassRotation || 0;

                                const getIntersection = (angleDeg) => {
                                  return findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);
                                };

                                const strokeColor = layerState.compassColor     || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity     = layerState.compassOpacity   || 0.8;

                                // ?o. KEY FIX: baseSvgAngle se color ??" rotation se nahi
                                // Color sector ke SAATH rotate hota hai kyunki position rotate ho rahi hai
                                // Agar color bhi rotate karo toh har rotation pe color badalta rahega ??" WRONG
                                const getTriGunaFill = (baseSvgAngle) => {
                                  const normalized = ((baseSvgAngle + 90) % 360 + 360) % 360;
                                  if (normalized >= 105 && normalized < 225) return '#B83000'; // RAJAS  ??" dark red-orange
                                  if (normalized >= 225 && normalized < 345) return '#0D5C99'; // SATTVA ??" dark sky blue
                                  return '#1E6B22';                                             // TAMAS  ??" dark green
                                };

                                const FILL_OPACITY = 0.45;

                                const dir16 = [
                                  'N','NNE','NE','ENE',
                                  'E','ESE','SE','SSE',
                                  'S','SSW','SW','WSW',
                                  'W','WNW','NW','NNW',
                                ];

                                const sectors = Array.from({ length: 24 }, (_, i) => ({
                                  index:        i + 1,
                                  baseSvgAngle: i * 15 - 90,
                                }));
const zoneAcc = {};

                                return (
                                  <>
                                    {/* ?"??"? 1. SECTOR FILLS ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {sectors.map(({ index, baseSvgAngle }) => {
                                      const startAngleDeg = baseSvgAngle + rotation + parentRot;
                                      const endAngleDeg   = baseSvgAngle + 15 + rotation + parentRot;

                                      const clippedRings = clipSectorWithBoundary(
                                        compassCenter,
                                        startAngleDeg,
                                        endAngleDeg,
                                        polygonPoints
                                      );
                                      if (!clippedRings || clippedRings.length === 0) return null;
{ const _f = getTriGunaFill(baseSvgAngle); const _z = _f === '#B83000' ? 'first' : _f === '#0D5C99' ? 'second' : 'third'; zoneAcc[_z] = (zoneAcc[_z] || 0) + ringsArea(clippedRings); }

                                      return clippedRings.map((ringPoints, ringIndex) => {
                                        if (!ringPoints || ringPoints.length < 3) return null;
                                        return (
                                          <Line
                                            key={`tds-fill-${index}-${ringIndex}`}
                                            points={ringPoints.flatMap(p => [p.x, p.y])}
                                            closed
                                            fill={getTriGunaFill(baseSvgAngle)} // ?o. sirf baseSvgAngle ??" rotation nahi
                                            opacity={FILL_OPACITY}
                                            stroke="transparent"
                                            strokeWidth={0}
                                            listening={false}
                                          />
                                        );
                                      });
                                    })}

                                    {/* ?"??"? 2. BOUNDARY LINE ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* ?"??"? 3. DIRECTION LINES ??" rotation only ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {false && layerState.showCompassLines && sectors.map(({ index, baseSvgAngle }) => {
                                      const adjustedAngle = baseSvgAngle + rotation;
                                      const intersection  = getIntersection(adjustedAngle);
                                      if (!intersection) return null;
                                      return (
                                        <Line
                                          key={`tds-dirline-${index}`}
                                          points={[
                                            compassCenter.x, compassCenter.y,
                                            intersection.x,  intersection.y,
                                          ]}
                                          stroke={strokeColor}
                                          strokeWidth={strokeWidth}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 4. INNER 24 NUMBER LABELS ??" HIDDEN ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {false && layerState.showSectorAreas && sectors.map(({ index, baseSvgAngle }) => {
                                      const midAngle     = baseSvgAngle + 7.5 + rotation;
                                      const intersection = getIntersection(midAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      return (
                                        <Text
                                          key={`tds-num-${index}`}
                                          x={compassCenter.x + (dirX / distance) * (distance * 0.6)}
                                          y={compassCenter.y + (dirY / distance) * (distance * 0.6)}
                                          text={String(index)}
                                          fontSize={14}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={8}
                                          offsetY={8}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 5. OUTER 16 DIRECTION LABELS ??" rotation only ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    {dir16.map((label, i) => {
                                      const svgAngle     = i * 22.5 - 90 + rotation;
                                      const intersection = getIntersection(svgAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      return (
                                        <Text
                                          key={`tds-dirlabel-${i}`}
                                          x={compassCenter.x + (dirX / distance) * (distance * 1.1)}
                                          y={compassCenter.y + (dirY / distance) * (distance * 1.1)}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={label.length * 3.5}
                                          offsetY={6}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 6. ZONE LABELS (RAJAS / SATTVA / TAMAS) ??" rotation only ?"??"??"??"??"??"? */}
                                    {[
                                      { name: 'PITTA',  svgCenter: 80, color: '#8B1A00' },
                                      { name: 'VATA',   svgCenter: 200, color: '#083D6E' },
                                      { name: 'KAPHA',  svgCenter: 300, color: '#144D17' }, // ?o. -30 se 300 kiya
                                    ].map(({ name, svgCenter, color }) => {
                                      const svgAngle     = svgCenter + rotation;
                                      const intersection = getIntersection(svgAngle);
                                      if (!intersection) return null;

                                      const dirX     = intersection.x - compassCenter.x;
                                      const dirY     = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      return (
                                        <Text
                                          key={`tds-zone-${name}`}
                                          x={compassCenter.x + (dirX / distance) * (distance * 0.55)}
                                          y={compassCenter.y + (dirY / distance) * (distance * 0.55)}
                                          text={name}
                                          fontSize={14}
                                          fontStyle="bold"
                                          fill={color}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={name.length * 4}
                                          offsetY={7}
                                          opacity={1}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* ?"??"? 7. CENTER POINT ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"? */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}





                          {layerId === 'body-part-division' && layerState.showCompass && compassCenter && isPolygonComplete && (
                            <Layer>
                              {(() => {
                                const toMartinezPolygon = (points) => {
                                  let ring = points.map(p => [p.x, p.y]);
                                  let area = 0;
                                  for (let i = 0; i < ring.length; i++) {
                                    const j = (i + 1) % ring.length;
                                    area += ring[i][0] * ring[j][1];
                                    area -= ring[j][0] * ring[i][1];
                                  }
                                  if (area > 0) ring = ring.reverse();
                                  if (
                                    ring[0][0] !== ring[ring.length - 1][0] ||
                                    ring[0][1] !== ring[ring.length - 1][1]
                                  ) ring.push(ring[0]);
                                  return [ring];
                                };

                                const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
                                  const toRad = (deg) => (deg * Math.PI) / 180;
                                  let start = toRad(startAngleDeg);
                                  let end = toRad(endAngleDeg);
                                  if (end <= start) end += Math.PI * 2;
                                  const pts = [[center.x, center.y]];
                                  const step = (end - start) / steps;
                                  for (let i = 0; i <= steps; i++) {
                                    const angle = start + i * step;
                                    pts.push([
                                      center.x + radiusOuter * Math.cos(angle),
                                      center.y + radiusOuter * Math.sin(angle),
                                    ]);
                                  }
                                  pts.push([center.x, center.y]);
                                  return [pts];
                                };

                                const clipSectorWithBoundary = (center, startAngleDeg, endAngleDeg, boundaryPoints) => {
                                  try {
                                    if (!boundaryPoints || boundaryPoints.length < 3) return [];
                                    const xs = boundaryPoints.map(p => p.x);
                                    const ys = boundaryPoints.map(p => p.y);
                                    const corners = [
                                      { x: Math.min(...xs), y: Math.min(...ys) },
                                      { x: Math.max(...xs), y: Math.min(...ys) },
                                      { x: Math.max(...xs), y: Math.max(...ys) },
                                      { x: Math.min(...xs), y: Math.max(...ys) },
                                    ];
                                    const dynamicRadius = Math.max(
                                      ...corners.map(c => Math.hypot(c.x - center.x, c.y - center.y))
                                    ) * 1.2;
                                    const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
                                    const boundaryPoly = toMartinezPolygon(boundaryPoints);
                                    const result = martinez.intersection(sectorPoly, boundaryPoly);
                                    if (!result || result.length === 0) return [];
                                    const allRings = [];
                                    for (const polygon of result) {
                                      for (const ring of polygon) {
                                        if (!ring || ring.length < 3) continue;
                                        const cleaned = (
                                          ring[0][0] === ring[ring.length - 1][0] &&
                                          ring[0][1] === ring[ring.length - 1][1]
                                        ) ? ring.slice(0, -1) : ring;
                                        if (cleaned.length >= 3)
                                          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
                                      }
                                    }
                                    return allRings;
                                  } catch (e) {
                                    return [];
                                  }
                                };

                                const rotation    = (layerState.compassRotation || 0) - (parentCompassRotation || 0);
                                const strokeColor = layerState.compassColor || '#000000';
                                const strokeWidth = layerState.compassLineWeight || 2;
                                const opacity     = layerState.compassOpacity || 0.8;

                                const ZONE_START = 270;
                                const ZONE_SIZE  = 15;

                                const zones24 = Array.from({ length: 24 }, (_, i) => ({
                                  zoneNumber: i + 1,
                                  startAngle: ZONE_START + i * ZONE_SIZE,
                                  endAngle:   ZONE_START + (i + 1) * ZONE_SIZE,
                                  midAngle:   ZONE_START + i * ZONE_SIZE + ZONE_SIZE / 2,
                                }));

                                // Body parts — each spans 2 zones = 30° sector
                                const bodyParts = [
                                  { label: 'Kidney',       zones: [2,  3]  },
                                  { label: 'Pericardium',  zones: [4,  5]  },
                                  { label: 'Circulation',  zones: [6,  7]  },
                                  { label: 'Liver',        zones: [8,  9]  },
                                  { label: 'Gall Bladder', zones: [10, 11] },
                                  { label: 'Spleen',       zones: [12, 13] },
                                  { label: 'Stomach',      zones: [14, 15] },
                                  { label: 'Heart',        zones: [16, 17] },
                                  { label: 'Lung',         zones: [18, 19] },
                                  { label: 'Colon',        zones: [20, 21] },
                                  { label: 'Intestine',    zones: [22, 23] },
                                  { label: 'Bladder',      zones: [24, 1]  },
                                ];

                                // Visible boundary angles — only at body part START boundaries
                                // Each body part starts at: zone[0].startAngle
                                // Hidden lines: zone[0].endAngle (= zone[1].startAngle) — internal divider
                                // Visible lines at angles: 270, 300, 330, 0, 30, 60, 90, 120, 150, 180, 210, 240
                                // i.e. every 30° starting from 270°
                                const visibleLineAngles = new Set(
                                  bodyParts.map(({ zones }) => {
                                    const z1 = zones24.find(z => z.zoneNumber === zones[0]);
                                    return z1 ? ((z1.startAngle) % 360) : null;
                                  }).filter(Boolean)
                                );

                                const directions16 = [
                                  { label: 'E',   angle: 0     },
                                  { label: 'ESE', angle: 22.5  },
                                  { label: 'SE',  angle: 45    },
                                  { label: 'SSE', angle: 67.5  },
                                  { label: 'S',   angle: 90    },
                                  { label: 'SSW', angle: 112.5 },
                                  { label: 'SW',  angle: 135   },
                                  { label: 'WSW', angle: 157.5 },
                                  { label: 'W',   angle: 180   },
                                  { label: 'WNW', angle: 202.5 },
                                  { label: 'NW',  angle: 225   },
                                  { label: 'NNW', angle: 247.5 },
                                  { label: 'N',   angle: 270   },
                                  { label: 'NNE', angle: 292.5 },
                                  { label: 'NE',  angle: 315   },
                                  { label: 'ENE', angle: 337.5 },
                                ];

                                const getIntersection = (angleDeg) =>
                                  findPolygonIntersection
                                    ? findPolygonIntersection(compassCenter, angleDeg)
                                    : calculateIntersection(compassCenter, angleDeg);

                                const hasParentRotation = (parentCompassRotation || 0) !== 0;

                                return (
                                  <>
                                    {/* 1. Only VISIBLE body-part boundary lines (every 30°, skip internal 15° dividers) */}
                                    {!hasParentRotation
                                      ? // Clipped sector — draw full 30° body part sectors
                                        bodyParts.map(({ label, zones }) => {
                                          const isWrapped = zones[1] < zones[0];
                                          const z1 = zones24.find(z => z.zoneNumber === zones[0]);
                                          const z2 = zones24.find(z => z.zoneNumber === zones[1]);
                                          if (!z1 || !z2) return null;

                                          const adjStart = z1.startAngle + rotation;
                                          const adjEnd   = isWrapped
                                            ? z2.endAngle + 360 + rotation
                                            : z2.endAngle + rotation;

                                          const clippedRings = clipSectorWithBoundary(
                                            compassCenter, adjStart, adjEnd, polygonPoints
                                          );
                                          if (!clippedRings || clippedRings.length === 0) return null;

                                          return clippedRings.map((ringPoints, ringIndex) => {
                                            if (!ringPoints || ringPoints.length < 3) return null;
                                            return (
                                              <Line
                                                key={`body-sector-${label}-ring-${ringIndex}`}
                                                points={ringPoints.flatMap(p => [p.x, p.y])}
                                                closed
                                                fill="transparent"
                                                stroke={strokeColor}
                                                strokeWidth={strokeWidth}
                                                opacity={opacity}
                                                listening={false}
                                              />
                                            );
                                          });
                                        })
                                      : // hasParentRotation ??" draw only visible lines from center
                                        zones24
                                          .filter(({ startAngle }) => {
                                            const normalized = ((startAngle % 360) + 360) % 360;
                                            return visibleLineAngles.has(normalized);
                                          })
                                          .map(({ zoneNumber, startAngle }) => {
                                            const point = getIntersection(startAngle + rotation);
                                            if (!point) return null;
                                            return (
                                              <Line
                                                key={`visible-line-${zoneNumber}`}
                                                points={[compassCenter.x, compassCenter.y, point.x, point.y]}
                                                stroke={strokeColor}
                                                strokeWidth={strokeWidth}
                                                opacity={opacity}
                                                listening={false}
                                              />
                                            );
                                          })
                                    }

                                    {/* 2. Boundary Line */}
                                    <Line
                                      points={polygonPoints.flatMap(p => [p.x, p.y])}
                                      closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                      fill="transparent"
                                      listening={false}
                                    />

                                    {/* 4. ?o. Body Part Labels ??" exact center of 2-zone span, fontSize 20 */}
                                    {bodyParts.map(({ label, zones }) => {
                                      const isWrapped = zones[1] < zones[0];
                                      const z1 = zones24.find(z => z.zoneNumber === zones[0]);
                                      const z2 = zones24.find(z => z.zoneNumber === zones[1]);
                                      if (!z1 || !z2) return null;

                                      const bodyStart = z1.startAngle;
                                      const bodyEnd   = isWrapped ? z2.endAngle + 360 : z2.endAngle;
                                      const bodyMid   = (bodyStart + bodyEnd) / 2 + rotation;

                                      const intersection = getIntersection(bodyMid);
                                      if (!intersection) return null;

                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelDist = distance * 0.72;
                                      const labelX = compassCenter.x + (dirX / distance) * labelDist;
                                      const labelY = compassCenter.y + (dirY / distance) * labelDist;

                                      return (
                                        <Text
                                          key={`body-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={label.length * 3.5}
                                          offsetY={7}
                                          opacity={opacity}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 5. ?o. 16 Direction Labels ??" outside boundary */}
                                    {directions16.map(({ label, angle }) => {
                                      const adjustedAngle = angle + rotation;
                                      const intersection  = getIntersection(adjustedAngle);
                                      if (!intersection) return null;

                                      const dirX = intersection.x - compassCenter.x;
                                      const dirY = intersection.y - compassCenter.y;
                                      const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                                      if (distance < 5) return null;

                                      const labelDist = distance * 1.1;
                                      const labelX = compassCenter.x + (dirX / distance) * labelDist;
                                      const labelY = compassCenter.y + (dirY / distance) * labelDist;
                                      const offsetX = label.length === 3 ? 14 : 10;

                                      return (
                                        <Text
                                          key={`dir-label-${label}`}
                                          x={labelX}
                                          y={labelY}
                                          text={label}
                                          fontSize={12}
                                          fontStyle="bold"
                                          fill={strokeColor}
                                          align="center"
                                          verticalAlign="middle"
                                          offsetX={offsetX}
                                          offsetY={8}
                                          listening={false}
                                        />
                                      );
                                    })}

                                    {/* 6. Center Point */}
                                    <Circle
                                      x={compassCenter.x}
                                      y={compassCenter.y}
                                      radius={6}
                                      fill={strokeColor}
                                      stroke="#FFFFFF"
                                      strokeWidth={strokeWidth}
                                      opacity={opacity}
                                      listening={false}
                                    />
                                  </>
                                );
                              })()}
                            </Layer>
                          )}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;

                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;

                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;

                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;

                                return (
                                  <Group key={measurement.id}>
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={layerState.compassColor || '#000000'}
                                      strokeWidth={layerState.compassLineWeight || 2}
                                    />
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill="#000000"
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px'
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                          {/* Hover tooltip for the 45-block compasses: Devata name of the box under the cursor (updated straight on the Konva nodes, no React re-render). */}
                          {(layerId === 'compass-45' || layerId === 'compass-45-2') && (
                            <Layer listening={false}>
                              <Group ref={devta45TipGroupRef} visible={false}>
                                <Rect ref={devta45TipTagRef} fill="#1A1F36" cornerRadius={5} opacity={0.95} shadowColor="#000000" shadowBlur={6} shadowOpacity={0.3} />
                                <Text ref={devta45TipTextRef} text="" fontSize={13} lineHeight={1.3} fill="#FFFFFF" padding={7} />
                              </Group>
                            </Layer>
                          )}
                        </Stage>
                        {/* Coloured Zone: wall/curtain colour reference table, shown under the
                            zone canvas (outside the Stage, so it isn't part of the captured
                            image — the PDF draws it on its own page right after this one). */}
                        {layerId === 'coloured-zone' && <WallColorSchemaTable language={language} width={A4_WIDTH} />}
                      </div>
                    )}


                    {(layerId === 'compass-45-2' || layerId === 'compass-45-2-chart' || layerId === 'compass-45-chart' || layerId === 'compass-45' || layerId === 'compass-45-table' || layerId === 'compass-45-2-table') && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          45 Devata Block Name
                        </h3>
                        <DevataBlocksTable layerId={"compass-45-2"} />
                      </div>
                    )}
                    {(layerId === 'RATNA-STHAPNA') && (
                        <div style={{
                          background: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          padding: '20px',
                          width: isMobile ? 'auto' : 'fit-content',
                          maxWidth: isMobile ? 'none' : '95vw'
                        }}>
                          <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                            Ratna Sthapna Table
                          </h3>
                          <RatnaSthapnaTable layerId={"RATNA-STHAPNA"} />
                        </div>
                      )}
                      {/* PNG-1 LAYER (Shakti Chakra) */}
                    {layerId === 'modern-Devata-45' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>

                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                   
                    
                      {/* PNG-1 LAYER (Shakti Chakra) */}
                    {layerId === 'mahavastu-chakra' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Vedic-Mahavastu-Chakra' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {/* PNG-1 LAYER (Shakti Chakra) */}
                    {layerId === 'shakti-chakra' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {/* PNG-2 LAYER (Mahavastu Chakra) */}
                    {layerId === 'png-image-2' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Vedic-devata-Numbers-with-Names-81' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Disha-Gandha' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-Nighat' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Nighantu' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {/* PNG-2 LAYER (Mahavastu Chakra) */}
                    {layerId === 'vastu-numbers-64' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'vastu-numbers-81-numbers' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'vastu-numbers-81-numbers-Vedic' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {/* PNG-2 LAYER (Mahavastu Chakra) */}
                    {layerId === 'marmsthan-analysis' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'floor-plan-vpm' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'vastu-numbers-81' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'grid-3x3' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER WITH NATURAL BEHAVIOR */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-center',
                                    'top-right',
                                    'middle-right',
                                    'bottom-right',
                                    'bottom-center',
                                    'bottom-left',
                                    'middle-left'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'center-analysis' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          Center Analysis
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* Base Image Layer */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                            {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                            </Layer>
                          {/* Center Analysis Overlay Layer */}
                          {compassCenter && (
                            <Layer>
                              {/* Red Center Point */}
                              <Circle
                                x={compassCenter.x}
                                y={compassCenter.y}
                                radius={4}
                                fill={layerState.compassColor || '#FF0000'}
                                stroke="#FFFFFF"
                                strokeWidth={1}
                              />
                              
                              {/* Crosshair Lines */}
                              <Line
                                points={[compassCenter.x - 30, compassCenter.y, compassCenter.x + 30, compassCenter.y]}
                                stroke={layerState.compassColor || '#FF0000'}
                                strokeWidth={layerState.compassLineWeight || 2}
                                dash={[5, 3]}
                                listening={false}
                              />
                              <Line
                                points={[compassCenter.x, compassCenter.y - 30, compassCenter.x, compassCenter.y + 30]}
                                stroke={layerState.compassColor || '#FF0000'}
                                strokeWidth={layerState.compassLineWeight || 2}
                                dash={[5, 3]}
                                listening={false}
                              />
                            </Layer>
                          )}

                          {/* ?o. Measurement Layer ??" center-analysis */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`ca-measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={layerState.compassLineWeight || 2}
                                />
                              ))}
                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                const labelX = midX + Math.cos(angleRad + Math.PI / 2) * 8;
                                const labelY = midY + Math.sin(angleRad + Math.PI / 2) * 8;
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                return (
                                  <Group key={measurement.id}>
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={layerState.compassColor || '#000000'}
                                      strokeWidth={layerState.compassLineWeight || 2}
                                    />
                                    <Group x={labelX} y={labelY} rotation={readableAngle}>
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill="#000000"
                                        padding={2}
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px'
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}

                   
                    {layerId === 'Khanija-Dhatu' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {[
                      'house-bank', 'house-clinic', 'house-electronic-shop', 'house-factory',
                      'house-hotel', 'house-marriage-hall', 'house-multiplex', 'house-office-cabin',
                      'house-petrol-pump', 'house-retail-outlets', 'house-school-college',
                      'house-space-analysis-compass', 'house-temple'
                    ].includes(layerId) && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{
                            border: '2px solid #333',
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage
                              src={baseImage}
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;

                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;

                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;

                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;

                                return (
                                  <Group
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />

                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                        {HOUSE_PLANNING_NOTES[layerId] && (
                          <div style={{
                            marginTop: '16px',
                            padding: '16px 20px',
                            background: '#F5F7FA',
                            borderRadius: '8px',
                            maxWidth: A4_WIDTH,
                          }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1A1F36', marginBottom: '8px' }}>
                              Vastu Guidelines (auto-included with this report's export)
                            </div>
                            <ol style={{ margin: 0, paddingLeft: '20px', color: '#333', fontSize: '0.82rem', lineHeight: 1.6 }}>
                              {HOUSE_PLANNING_NOTES[layerId].map((line, i) => (
                                <li key={i} style={{ marginBottom: '4px' }}>{line}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    )}
                    {layerId === 'Direction-Diety-Planet' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-Khanij' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-Bhojan' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-Bhojan-aadi' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-chinha' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-chinha-aadi' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Muhurat-vichar' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Seharumkh-achintya-vibhav' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === '16-Directional-Domestic-Vastu' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'png-image-1' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'nakshatra-vastu' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === 'Devata-Nakshatra-Vastu' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}
                    {layerId === '32-Entrance-Analysis-Compass' && (
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        padding: '20px',
                        width: isMobile ? 'auto' : 'fit-content',
                        maxWidth: isMobile ? 'none' : '95vw'
                      }}>
                        <h3 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', color: '#000' }}>
                          {layerName}
                        </h3>

                        <Stage
                          ref={editorStageRef}
                          width={A4_WIDTH}
                          height={A4_HEIGHT}
                          style={{ 
                            border: '2px solid #333', 
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: isAddingText || measurementMode ? 'crosshair' : 'default'
                          }}
                          onClick={handleCanvasClick}
                        >
                          {/* ?o. BASE IMAGE LAYER - Original floor plan */}
                          <Layer>
                            <FloorPlanImage 
                              src={baseImage} 
                              canvasWidth={A4_WIDTH}
                              canvasHeight={A4_HEIGHT}
                              rotation={0}
                              opacity={1}
                              scale={1}
                            />
                          </Layer>
                          <Layer>
                             {isPolygonComplete && polygonPoints.length >= 3 && (
                              <Group opacity={1}>
                                <Line
                                  points={polygonPoints.flatMap(p => [p.x, p.y])}
                                  closed stroke={polygonColor} strokeWidth={polygonLineWeight} opacity={polygonLineOpacity}
                                />
                              </Group>
                            )}
                          </Layer>
                          {/* PNG IMAGE LAYER with TRANSFORMER */}
                          {uploadedImage && (
                            <Layer>
                              <Group
                                ref={imageGroupRef}
                                draggable={layerState.floatMode}
                                x={pngPosition.x}
                                y={pngPosition.y}
                                rotation={(layerState.rotation || 0) + (parentCompassRotation || 0)}
                                scaleX={layerState.scale || 1}
                                scaleY={layerState.scale || 1}
                                onDragEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = e.target;
                                    const newX = node.x();
                                    const newY = node.y();
                                    setPngPosition({ x: newX, y: newY });
                                    setLayerState(prev => ({
                                      ...prev,
                                      positionX: newX,
                                      positionY: newY
                                    }));
                                  }
                                }}
                                onTransformEnd={(e) => {
                                  if (layerState.floatMode) {
                                    const node = imageGroupRef.current;
                                    const scaleX = node.scaleX();
                                    const scaleY = node.scaleY();
                                    const rotation = node.rotation();

                                    setLayerState(prev => ({
                                      ...prev,
                                      scale: Math.max(scaleX, scaleY),
                                      rotation: rotation - (parentCompassRotation || 0),
                                      positionX: node.x(),
                                      positionY: node.y()
                                    }));

                                    setPngPosition({ x: node.x(), y: node.y() });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (layerState.floatMode) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                }}
                              >
                                <Group
                                  offsetX={500 / 2}
                                  offsetY={500 / 2}
                                >
                                  {/* Background draggable area */}
                                  <Rect
                                    x={0}
                                    y={0}
                                    width={500}
                                    height={500}
                                    fill="rgba(255, 0, 0, 0.01)"
                                    listening={layerState.floatMode}
                                  />

                                  {/* PNG image zoomed to 1.4x */}
                                  <FloorPlanImage
                                    src={uploadedImage}
                                    canvasWidth={500}
                                    canvasHeight={500}
                                    rotation={0}
                                    opacity={layerState.opacity || 1}
                                    scale={1.4}
                                    showCenterPoint={false}
                                    centerPoint={null}
                                  />
                                </Group>
                              </Group>

                              {/* ?o. TRANSFORMER */}
                              {layerState.floatMode && (
                                <Transformer
                                  ref={transformerRef}
                                  borderStroke="#00A19D"
                                  borderStrokeWidth={3}
                                  borderDash={[8, 3]}
                                  anchorStroke="#00A19D"
                                  anchorFill="#FFFFFF"
                                  anchorSize={12}
                                  anchorCornerRadius={6}
                                  anchorStrokeWidth={3}
                                  enabledAnchors={[
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right'
                                  ]}
                                  rotateEnabled={true}
                                  rotateAnchorOffset={40}
                                  keepRatio={false}
                                  boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 100 || newBox.height < 100) {
                                      return oldBox;
                                    }
                                    return newBox;
                                  }}
                                />
                              )}
                            </Layer>
                          )}

                          {/* PNG Center Point Marker */}
                          {uploadedImage && layerState.floatMode && (
                            <Layer>
                              <Group>
                                <Circle
                                  x={pngPosition.x}
                                  y={pngPosition.y}
                                  radius={6}
                                  fill="#00FF00"
                                  stroke="#FFFFFF"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x - 10, pngPosition.y,
                                    pngPosition.x + 10, pngPosition.y
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                                <Line
                                  points={[
                                    pngPosition.x, pngPosition.y - 10,
                                    pngPosition.x, pngPosition.y + 10
                                  ]}
                                  stroke="#00FF00"
                                  strokeWidth={2}
                                  listening={false}
                                />
                              </Group>
                            </Layer>
                          )}

                          {/* Measurement Layer */}
                          {(measurementMode || layerMeasurements.length > 0) && (
                            <Layer>
                              {measurementPoints.map((point, index) => (
                                <Circle
                                  key={`measure-point-${index}`}
                                  x={point.x}
                                  y={point.y}
                                  radius={6}
                                  fill="#dc3545"
                                  stroke="#ffffff"
                                  strokeWidth={2}
                                />
                              ))}

                              {layerMeasurements.map((measurement) => {
                                const [p1, p2] = measurement.points;
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                                const angleDeg = (angleRad * 180) / Math.PI;
                                
                                const labelOffset = 8;
                                const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                                const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                                
                                let readableAngle = angleDeg;
                                if (readableAngle > 90) readableAngle -= 180;
                                if (readableAngle < -90) readableAngle += 180;
                                
                                return (
                                  <Group 
                                    key={measurement.id}
                                    onClick={() => handleMeasurementClickForRemoval(measurement.id)}
                                    onMouseEnter={(e) => {
                                      if (removeMeasurementMode) {
                                        const container = e.target.getStage().container();
                                        container.style.cursor = 'pointer';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const container = e.target.getStage().container();
                                      container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                    }}
                                  >
                                    <Line
                                      points={[p1.x, p1.y, p2.x, p2.y]}
                                      stroke={removeMeasurementMode ? "#DC3545" : "#000000"}
                                      strokeWidth={removeMeasurementMode ? 3 : 2}
                                    />
                                    
                                    <Group
                                      x={labelX}
                                      y={labelY}
                                      rotation={readableAngle}
                                    >
                                      <Text
                                        text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                        fontSize={10}
                                        fontStyle="bold"
                                        fill={removeMeasurementMode ? "#DC3545" : "#000000"}
                                        padding={2}
                                        background="#ffffff"
                                        align="center"
                                        offsetX={5}
                                        offsetY={4}
                                      />
                                    </Group>
                                  </Group>
                                );
                              })}
                            </Layer>
                          )}

                          {/* Text Layer */}
                          <Layer>
                            {texts.map(text => (
                              text.id === editingTextId ? (
                                <Html
                                  key={text.id}
                                  divProps={{
                                    style: {
                                      position: 'absolute',
                                      top: text.y + 'px',
                                      left: text.x + 'px',
                                      zIndex: 10000
                                    }
                                  }}
                                >
                                  <input
                                    autoFocus
                                    value={editingTextValue}
                                    onChange={handleTextChange}
                                    onBlur={handleTextEditEnd}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleTextEditEnd();
                                      }
                                    }}
                                    style={{
                                      fontSize: text.fontSize + 'px',
                                      fontFamily: text.fontFamily,
                                      color: text.fill,
                                      border: '1px solid #00A19D',
                                      padding: '2px',
                                      background: '#fff'
                                    }}
                                  />
                                </Html>
                              ) : (
                                <Text
                                  key={text.id}
                                  x={text.x}
                                  y={text.y}
                                  text={text.text}
                                  fontSize={text.fontSize}
                                  fontFamily={text.fontFamily}
                                  fill={text.fill}
                                  draggable={text.draggable}
                                  onClick={() => handleTextClick(text.id)}
                                  onDragEnd={(e) => handleTextDragEnd(text.id, e)}
                                  onContextMenu={(e) => handleTextRightClick(e, text.id)}
                                  onMouseEnter={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = 'move';
                                  }}
                                  onMouseLeave={(e) => {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = isAddingText || measurementMode ? 'crosshair' : 'default';
                                  }}
                                />
                              )
                            ))}
                          </Layer>
                        </Stage>
                      </div>
                    )}

{(layerId === 'cover-page' || layerId === 'cover-index') && (() => {
  // This view stacks 6 full A4 preview pages (Cover, Modern Vastu, Vedic
  // Vastu, Medical Vastu, Others, House Planning Guide) at their native
  // 794x1123 size — on mobile that's ~6800px of unscaled canvas, so the
  // Cover Page's own click-to-edit fields (Property/Client + Consultant
  // Details) ended up far wider than the phone screen and effectively
  // untappable ("client info not editable"). Same mobile fit-scale sizer
  // already used elsewhere in this file (see editorFitScale above) —
  // Konva's own pointer-position math accounts for the CSS transform, so
  // every onClick/tap on the fields below keeps working unchanged; only the
  // visual size shrinks to fit. Height is 4 gaps of 20px (pages 1-4 have a
  // marginBottom, the last 2 don't) plus 6 full-height pages.
  // NOTE: no scaling wrappers of its own any more — this whole block already
  // sits inside the editor's outer mobile fit-scale sizer (see editorFitScale
  // near the top of the canvas area), and stacking a second scale() on top of
  // it squared the factor (0.44 x 0.44 ≈ 0.19), which is why the cover page
  // showed up at roughly a quarter of the screen. The outer sizer's height is
  // widened for this layer so all 6 stacked pages still fit inside it.
  return (
  <div>
  <div>
  <div style={{
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    padding: '0',
    position: 'relative',
    overflow: 'visible'
  }}>
    {layerId === 'cover-page' && bgBusy && (
      <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'rgba(255,255,255,0.78)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', borderRadius: '8px' }}>
        <style>{'@keyframes cvbgspin { to { transform: rotate(360deg); } }'}</style>
        <div style={{ width: 44, height: 44, border: '4px solid #cfe9e8', borderTopColor: '#00A19D', borderRadius: '50%', animation: 'cvbgspin 0.8s linear infinite' }} />
        <div style={{ color: '#0f172a', fontSize: '0.9rem', fontWeight: 600 }}>{bgBusy === 'reset' ? 'Setting default background…' : 'Setting background image…'}</div>
      </div>
    )}
    {/* ========== PAGE 1: BRAND COVER PAGE (B&W) — Cover Front only ========== */}
    {layerId === 'cover-page' && isMobile && renderCoverBgControls(false)}
    {layerId === 'cover-page' && (
    <Stage
      ref={editorStageRef}
      width={A4_WIDTH}
      height={A4_HEIGHT}
      style={{
        border: 'none',
        borderRadius: '4px',
        background: '#ffffff',
        cursor: isAddingText ? 'crosshair' : 'default',
        marginBottom: '20px'
      }}
      onClick={handleCanvasClick}
    >
      {/* Client-supplied cover page artwork — replaces the old black/white
          branded design. Sits full-bleed since the image is already at A4
          proportions (see cover-page-template.png). */}
      <Layer>
        <CoverPageBackgroundImage width={A4_WIDTH} height={A4_HEIGHT} src={customBg || undefined} onReady={finishBgBusy} />
      </Layer>

      {/* Editable Fields — click-to-edit values placed over the template's
          own "Property / Client Details" and "Consultant Details" labels. */}
      <Layer>
        {(() => {
          // Positions below were measured directly off cover-page-template.png
          // (pixel-row detection of each printed label, not eyeballed) and
          // converted to this canvas's 794x1123 scale, so every value's TOP
          // edge — not some offset guess — lines up with its own label row.
          const VALUE_X = 320;
          const VALUE_W = 400;
          const FONT_SIZE = 15;
          const FIELD_H   = 19; // must stay ≤ ~20px — that's how tight the template's own label rows are

          const renderValueLine = (type, field, topY, placeholder) => {
            const info = type === 'consultant' ? consultantInfo : clientInfo;
            const isEditing = editingField?.type === type && editingField?.field === field;
            if (isEditing) {
              return (
                <Html key={`${type}-${field}-e`} divProps={{ style: { position: 'absolute', top: `${topY}px`, left: `${VALUE_X}px`, zIndex: 10000 } }}>
                  <input
                    autoFocus
                    value={editingValue}
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    onKeyDown={handleFieldKeyPress}
                    placeholder={placeholder}
                    style={{ fontSize: `${FONT_SIZE}px`, height: `${FIELD_H}px`, border: '1px solid #00A19D', borderRadius: '4px', padding: '0 5px', width: `${VALUE_W}px`, outline: 'none', fontFamily: 'Arial, Helvetica', background: '#fff', boxSizing: 'border-box' }}
                  />
                </Html>
              );
            }
            return (
              <React.Fragment key={`${type}-${field}`}>
                <Rect x={VALUE_X} y={topY} width={VALUE_W} height={FIELD_H} fill="transparent" onClick={() => handleFieldClick(type, field)} listening={true} />
                <Text
                  x={VALUE_X} y={topY}
                  text={info[field] || placeholder}
                  fontSize={FONT_SIZE}
                  fontFamily="Arial, Helvetica"
                  fill="#000000"
                  fontStyle={info[field] ? 'normal' : 'italic'}
                  width={VALUE_W}
                  wrap="none"
                  ellipsis={true}
                  onClick={() => handleFieldClick(type, field)}
                  listening={true}
                />
              </React.Fragment>
            );
          };

          return (
            <>
              {/* Property / Client Details */}
              {renderValueLine('client', 'name', 667, 'Enter owner name')}
              {renderValueLine('client', 'phone', 690, 'Enter contact number')}
              {renderValueLine('client', 'email', 712, 'Enter email')}
              {renderValueLine('client', 'propertyType', 737, 'Enter property type')}
              {renderValueLine('client', 'address', 759, 'Enter property address')}
              {renderValueLine('client', 'dateOfVisit', 779, 'Enter date of visit')}

              {/* Consultant Details */}
              {renderValueLine('consultant', 'name', 836, 'Enter consultant name')}
              {renderValueLine('consultant', 'phone', 859, 'Enter contact number')}
              {renderValueLine('consultant', 'email', 881, 'Enter email')}
              {renderValueLine('consultant', 'website', 904, 'Enter website')}
            </>
          );
        })()}
      </Layer>

      {/* Derived overlays — not separately editable; they mirror data already
          captured above (Owner Name → greeting, consultant logo/company name
          → the template's logo circle and "Powered by" line). Positions were
          measured the same pixel-row way as the editable fields above. */}
      <Layer>
        {/* 5px up, 6px right from where it originally sat (349,948) */}
        <ConsultantLogoImage src={resolvedLogoSrc} resolving={logoResolving} x={355} y={943} size={90} />
        <Text
          x={0} y={1075}
          text={consultantInfo.companyName || 'VastuAnalyzer'}
          fontSize={13}
          fontFamily="Arial, Helvetica"
          fill="#3a2b1a"
          width={A4_WIDTH}
          align="center"
          listening={false}
        />
        {/* "Respected [Name]" greeting lives on Cover Back only, not here. */}
      </Layer>

      {/* User Added Texts */}
      <Layer>
        {texts.map(text => (
          text.id === editingTextId ? (
            <Html key={text.id} divProps={{ style: { position: 'absolute', top: (text.y - 50) + 'px', left: text.x + 'px', zIndex: 10000 } }}>
              <div style={{ background: '#333', border: '1px solid #999', borderRadius: '4px', padding: '8px', display: 'flex', gap: '4px', alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => toggleBold(text.id)} style={{ background: text.fontStyle === 'bold' ? '#000' : '#444', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}>B</button>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => toggleItalic(text.id)} style={{ background: text.fontStyle === 'italic' ? '#000' : '#444', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '3px', cursor: 'pointer', fontStyle: 'italic' }}>I</button>
                <input onMouseDown={(e) => e.preventDefault()} type="color" value={text.fill} onChange={(e) => handleTextFormat(text.id, 'fill', e.target.value)} style={{ width: '30px', height: '24px', border: '1px solid #999', borderRadius: '3px', cursor: 'pointer' }} />
                <button onMouseDown={(e) => e.preventDefault()} onClick={handleTextEditEnd} style={{ background: '#333', color: '#fff', border: '1px solid #999', padding: '4px 12px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', marginLeft: 'auto' }}>Done</button>
              </div>
              <textarea autoFocus value={editingTextValue} onChange={handleTextChange} style={{ fontSize: text.fontSize + 'px', fontFamily: text.fontFamily, fontStyle: text.fontStyle || 'normal', fontWeight: text.fontStyle === 'bold' ? 'bold' : 'normal', color: text.fill, border: '1px solid #999', padding: '8px', background: '#fff', borderRadius: '4px', minWidth: '200px', minHeight: '40px', resize: 'both', outline: 'none' }} />
            </Html>
          ) : (
            <Text key={text.id} x={text.x} y={text.y} text={text.text} fontSize={text.fontSize} fontFamily={text.fontFamily} fontStyle={text.fontStyle || 'normal'} fill={text.fill} draggable={text.draggable} onClick={() => handleTextClick(text.id)} onDragEnd={(e) => handleTextDragEnd(text.id, e)} onContextMenu={(e) => handleTextRightClick(e, text.id)} onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'pointer'; }} onMouseLeave={(e) => { e.target.getStage().container().style.cursor = isAddingText ? 'crosshair' : 'default'; }} />
          )
        ))}
      </Layer>
    </Stage>
    )}

    {/* ========== INDEX PAGES — Cover Index only ==========
        One continuous list: when a section's reports end, the next section's
        heading follows on the same page; a new page only starts when the
        page is full (mirrors addCombinedIndexPages in the PDF export). */}
    {layerId === 'cover-index' && (() => {
      const modernIds = ['floor-plan','grid-layout','center-analysis','mahavastu-chakra','shakti-chakra','floor-plan-vpm','compass-8','Bar-chart-8-table','Bar-chart-8-chart','compass-16','Bar-chart-16-table','Bar-chart-16-chart','compass-24','surya-chandra-bhag','4-state-image','compass-32','Bar-chart-32-table','Bar-chart-32-chart','compass-45-2','compass-45-2-table','compass-45-2-chart','coloured-zone','elemental-5-zone','elemental-5-chart','marmsthan-analysis','marmsthan-advance','vastu-numbers-81-numbers','vastu-numbers-64','grid-3x3','entrance-analysis-report','space-analysis-report','32-Entrance-Analysis-Compass','cut-zone-report','extension-zone-report'];
      const vedicIds = ['Vedic-Mahavastu-Chakra','16-Directional-Domestic-Vastu','Khanija-Dhatu','Vedic-devata-Numbers-with-Names-81','vastu-numbers-81-numbers-Vedic','Direction-Diety-Planet','Devata-Khanij','Devata-Bhojan','Devata-Bhojan-aadi','Devata-chinha','Devata-chinha-aadi','Muhurat-vichar','Seharumkh-achintya-vibhav','Disha-Gandha','Devata-Nighat','Nighantu','compass-45','compass-45-table','compass-45-chart','4-state-image','surya-chandra-bhag', ...VEDIC_GENERATIVE_IDS];
      const medicalIds = ['tri-dosh-Vedic','tri-dosh-modern','tri-guna-Vedic','tri-guna-modern','body-part-division'];
      const othersIds = ['MANDAL','RATNA-STHAPNA','nakshatra-vastu','Devata-Nakshatra-Vastu','helix-report','numero-vastu-report','astro-vastu-report'];
      const housePlanningIds = ['house-bank','house-clinic','house-electronic-shop','house-factory','house-hotel','house-marriage-hall','house-multiplex','house-office-cabin','house-petrol-pump','house-retail-outlets','house-school-college','house-space-analysis-compass','house-temple'];
      const MM = A4_HEIGHT / 297;
      const START_Y = 34 * MM, BOTTOM_Y = 275 * MM, ROW_H = 10 * MM, HEAD_H = 14 * MM, GAP = 6 * MM;
      const cats = [
        { title: 'Modern Vastu Report', ids: modernIds },
        { title: 'Vedic Vastu Report', ids: vedicIds },
        { title: 'Medical Vastu Report', ids: medicalIds },
        { title: 'Others Report', ids: othersIds },
        { title: 'Planning Guide Report', ids: housePlanningIds },
      ]
        .map(c => ({ title: c.title, names: c.ids.filter(id => exportLayers[id]?.enabled).map(id => getLayerName(id)) }))
        .filter(c => c.names.length > 0);

      const pages = [[]];
      let y = START_Y;
      const newPage = () => { pages.push([]); y = START_Y; };
      cats.forEach((cat, ci) => {
        if (y + HEAD_H + ROW_H > BOTTOM_Y) newPage(); else if (ci > 0) y += GAP;
        pages[pages.length - 1].push({ kind: 'head', text: cat.title, y });
        y += HEAD_H;
        cat.names.forEach((name, idx) => {
          if (y + ROW_H > BOTTOM_Y) {
            newPage();
            pages[pages.length - 1].push({ kind: 'head', text: cat.title, y });
            y += HEAD_H;
          }
          pages[pages.length - 1].push({ kind: 'row', text: (idx + 1) + '. ' + name, y });
          y += ROW_H;
        });
      });

      return pages.map((items, pi) => (
        <Stage key={'index-page-' + pi} ref={pi === 0 ? VedicStageRef : undefined} width={A4_WIDTH} height={A4_HEIGHT} style={{ border: 'none', borderRadius: '4px', background: '#ffffff', marginBottom: pi < pages.length - 1 ? '20px' : '0px' }}>
          <Layer>
            <Rect x={0} y={0} width={A4_WIDTH} height={A4_HEIGHT} stroke="#a85e25" strokeWidth={2.5} listening={false} />
            <Rect x={6} y={6} width={A4_WIDTH - 12} height={A4_HEIGHT - 12} stroke="#e0b994" strokeWidth={0.8} listening={false} />
            {[[0,0,1,0],[0,0,0,1],[1,0,-1,0],[1,0,0,1],[0,1,1,0],[0,1,0,-1],[1,1,-1,0],[1,1,0,-1]].map(([ox,oy,dx,dy],i) => (
              <Line key={i} points={[ox*A4_WIDTH, oy*A4_HEIGHT, ox*A4_WIDTH+dx*40, oy*A4_HEIGHT+dy*40]} stroke="#a85e25" strokeWidth={2.5} listening={false} />
            ))}
            <Text x={0} y={44} width={A4_WIDTH} align="center" text="REPORT INDEX" fontSize={26} fontStyle="bold" fontFamily="Arial, Helvetica" fill="#7c421c" listening={false} />
            <Line points={[A4_WIDTH / 2 - 90, 88, A4_WIDTH / 2 - 20, 88]} stroke="#a85e25" strokeWidth={1} listening={false} />
            <Line points={[A4_WIDTH / 2 + 20, 88, A4_WIDTH / 2 + 90, 88]} stroke="#a85e25" strokeWidth={1} listening={false} />
            <Circle x={A4_WIDTH / 2 - 20} y={88} radius={2} fill="#a85e25" listening={false} />
            <Circle x={A4_WIDTH / 2 + 20} y={88} radius={2} fill="#a85e25" listening={false} />
            <Text x={0} y={76} width={A4_WIDTH} align="center" text="ॐ" fontSize={22} fontFamily="Arial, Helvetica" fill="#a85e25" listening={false} />
            {items.map((it, i) => (
              it.kind === 'head' ? (
                <Text key={i} x={70} y={it.y - 4 * MM} text={it.text} fontSize={19} fontStyle="bold" fontFamily="Arial, Helvetica" fill="#7c421c" listening={false} />
              ) : (
                <Text key={i} x={90} y={it.y - 4 * MM} text={it.text} fontSize={17} fontFamily="Arial, Helvetica" fill="#333333" listening={false} />
              )
            ))}
          </Layer>
        </Stage>
      ));
    })()}
  </div>
  </div>
  </div>
  );
})()}

{layerId === 'cover-back-page' && (
  <div style={{
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    padding: '0',
    position: 'relative',
    overflow: 'visible'
  }}>
    {layerId === 'cover-back-page' && bgBusy && (
      <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'rgba(255,255,255,0.78)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', borderRadius: '8px' }}>
        <style>{'@keyframes cvbgspin { to { transform: rotate(360deg); } }'}</style>
        <div style={{ width: 44, height: 44, border: '4px solid #cfe9e8', borderTopColor: '#00A19D', borderRadius: '50%', animation: 'cvbgspin 0.8s linear infinite' }} />
        <div style={{ color: '#0f172a', fontSize: '0.9rem', fontWeight: 600 }}>{bgBusy === 'reset' ? 'Setting default background…' : 'Setting background image…'}</div>
      </div>
    )}
    {isMobile && renderCoverBgControls(false)}
    {/* Read-only preview — its text comes from the Cover Front fields
        (Owner Name, Consultant email/phone/website/company name), not a
        separate set of inputs, so there's nothing new to click-to-edit here. */}
    <Stage
      ref={editorStageRef}
      width={A4_WIDTH}
      height={A4_HEIGHT}
      style={{ border: 'none', borderRadius: '4px', background: '#ffffff', marginBottom: '10px' }}
    >
      <Layer>
        <CoverBackBackgroundImage width={A4_WIDTH} height={A4_HEIGHT} src={customBg || undefined} onReady={finishBgBusy} />
      </Layer>
      <Layer>
        {clientInfo.name && (
          <Text x={0} y={323} text={`Respected ${clientInfo.name}`} fontSize={34} fontStyle="bold" fontFamily="Arial, Helvetica" fill="#5b321d" width={A4_WIDTH} align="center" listening={false} />
        )}
        {/* Email/Website/Contact — re-measured directly off the template
            (pixel-row detection of each label's actual baseline: img
            y=1290/1340/1390 → canvas y=826/858/890), then converted from
            baseline to Konva's top-of-text positioning (−~15px for a 19px
            font). The earlier wider spacing had drifted away from where the
            labels actually sit — this restores exact label alignment. */}
        {/* All 3 moved right 15px (339→354) from their previous x */}
        <Text x={354} y={816} text={consultantInfo.email || 'Enter email'} fontSize={19} fontFamily="Arial, Helvetica" fill="#000000" fontStyle={consultantInfo.email ? 'normal' : 'italic'} listening={false} />
        <Text x={354} y={852} text={consultantInfo.website || 'Enter website'} fontSize={19} fontFamily="Arial, Helvetica" fill="#000000" fontStyle={consultantInfo.website ? 'normal' : 'italic'} listening={false} />
        <Text x={354} y={884} text={consultantInfo.phone || 'Enter contact number'} fontSize={19} fontFamily="Arial, Helvetica" fill="#000000" fontStyle={consultantInfo.phone ? 'normal' : 'italic'} listening={false} />
        {/* Company name — theme accent colour, bold, nudged back up a bit
            from the template's "Powered by" line (baseline ≈ y946). */}
        <Text x={0} y={970} text={consultantInfo.companyName || 'VastuAnalyzer'} fontSize={20} fontStyle="bold" fontFamily="Arial, Helvetica" fill="#a85e25" width={A4_WIDTH} align="center" listening={false} />
      </Layer>
    </Stage>
    <div style={{ padding: '2px 14px 14px', fontSize: '12px', color: '#888', textAlign: 'center' }}>
      This page's text comes from Cover Front's Owner Name and Consultant fields — edit those there to update it here too.
    </div>
  </div>
)}
                    </div>
                    </div>
                </div>
              </div>
            </div>
       );
};


const Configure = ({ finalImage, imageData, onNext, projectId, initialProjectName, initialProjectDescription, initialProjectClientDetails, onFloorPlanLoad, onProjectLoad, saveRef, onDirtyChange, onBackToProjects }) => {
  // getZoneColors imported from ./Configure/constants/zoneColors
  const A4_WIDTH = 794;
  const A4_HEIGHT = 1123;

  // ── Mobile layout ──────────────────────────────────────────────────────
  // Desktop keeps its fixed 3-column chrome (left "Layout Stages" sidebar +
  // canvas + right "Export & Reports" sidebar) completely untouched — see
  // MobileSheetWrapper's own comment for how. On phone widths, those two
  // sidebars render as slide-up bottom sheets instead, opened from the new
  // floating bottom toolbar; `mobileSheet` tracks which one (if any) is
  // currently open (null | 'stages' | 'measurement' | 'reports').
  const isMobile = useIsMobile();
  const [mobileSheet, setMobileSheet] = useState(null);
  // Compass "Change <X>" mode (mobile only, explicit user request): tapping
  // any of the 5 compass control buttons inside the Compass sheet closes
  // that sheet entirely and drops a slim control bar over the bottom of the
  // screen instead — no sheet, no backdrop, canvas fully visible above it —
  // so dragging e.g. the rotation slider shows the compass/floor-plan
  // actually turning live. null | 'rotation' | 'lineColor' | 'lineWeight' |
  // 'lineOpacity' | 'opacity'.
  const [mobileCompassControl, setMobileCompassControl] = useState(null);
  // Same idea, for the Polygon sheet's Color/Line Weight/Line Opacity/
  // Opacity controls (per explicit request: every mobile sheet slider gets
  // this same "float over the visible canvas" treatment, not just Compass).
  // null | 'lineColor' | 'lineWeight' | 'lineOpacity' | 'opacity'.
  const [mobilePolygonControl, setMobilePolygonControl] = useState(null);

  // Tracks window.innerWidth reactively (own resize listener, updates
  // immediately) — used below to size the canvas on mobile. Deliberately
  // NOT reusing the existing `canvasDimensions` state for this: that one
  // defaults to a stale 800px until its own effect measures the real
  // container after mount, so the very first mobile render (and any render
  // before that effect catches up) computed almost no shrink at all — the
  // canvas briefly rendered near its full 794px desktop size regardless of
  // the actual phone width. window.innerWidth is available immediately on
  // first render, and on mobile there's no sidebar competing for width (both
  // are bottom sheets, closed by default), so it's a reliable stand-in for
  // "how wide the canvas area actually is" without that lag.
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 800
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [openSpaceAnalysisModal, setOpenSpaceAnalysisModal] = useState(false);
  const [openEntranceAnalysisModal, setOpenEntranceAnalysisModal] = useState(false);
  const [openVastuDevtaGuideModal, setOpenVastuDevtaGuideModal] = useState(false);
  const [openNavgrahShantiModal, setOpenNavgrahShantiModal]     = useState(false);
  const [openFengShuiModal, setOpenFengShuiModal]               = useState(false);
  const [openHelixModal, setOpenHelixModal]                     = useState(false);
  const [openZoneAnalysisModal, setOpenZoneAnalysisModal]       = useState(false);
  const [openVastuGatewayModal, setOpenVastuGatewayModal]       = useState(false);
  const [vastuGatewayView,     setVastuGatewayView]             = useState('persons');
  const [openCutExtensionModal, setOpenCutExtensionModal]       = useState(false);
  const [cutExtensionMode,     setCutExtensionMode]             = useState('cut');
  const [openBirthDateModal,   setOpenBirthDateModal]           = useState(false);
  const [videoGuideOpen, setVideoGuideOpen] = useState(false);
  const [videoGuideKind, setVideoGuideKind] = useState('desktop'); // 'desktop' | 'mobile' | 'action-required' | 'floor-plan-maker'
  const [openNumeroReport,     setOpenNumeroReport]             = useState(false);
  const [numeroRemedyData,     setNumeroRemedyData]             = useState({});
  const [entranceReportData, setEntranceReportData] = useState([]);
  const [spaceReportData, setSpaceReportData] = useState([]);
  // `finalImage` is the canonical floor plan reference (raw storage URL for a loaded
  // project, or a data: URL fresh off an upload) — it's what gets sent back on save, so
  // it must never be overwritten with a resolved copy. `baseImage` is a display-only
  // resolution of it: for a remote /api/floor-plan-image/ URL it's swapped for a base64
  // data: URL fetched via resolveFloorPlanImage, because the CDN in front of the API
  // strips CORS headers on image/* responses, which breaks the canvas's crossOrigin
  // image load and makes layer/export capture throw a "tainted canvas" error. data:
  // URLs are always same-origin, so this keeps the canvas exportable regardless.
  const [baseImage, setBaseImage] = useState(finalImage);
  useEffect(() => {
    let cancelled = false;
    projectService.resolveFloorPlanImage(finalImage).then((resolved) => {
      if (!cancelled) setBaseImage(resolved);
    });
    return () => { cancelled = true; };
  }, [finalImage]);
  const [openStages, setOpenStages] = useState(new Set(["unit"]));
  const [canvasScale, setCanvasScale] = useState(1);
  const [selectedPolygonPoints, setSelectedPolygonPoints] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [currentUnit, setCurrentUnit] = useState("ft");
  const [magnifierPosition, setMagnifierPosition] = useState(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 800, height: 600 });
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  // Mobile canvas auto-fit. IMPORTANT: the Stage's own width/height/
  // scaleX/scaleY props stay 100% identical on mobile and desktop (see
  // where <Stage> is rendered) — Konva derives its click/tap pointer-
  // position math directly from those props (confirmed by reading Konva's
  // own source, Stage.js#_getContentPosition/#setPointersPositions), so
  // changing them per-device was actively wrong: it made
  // getPointerPosition() return coordinates in a *different* space than
  // every polygon/room/entrance point in this file is authored and stored
  // in (0..A4_WIDTH), and the resulting error grew the further from the
  // stage's origin you clicked — exactly matching what testing on a real
  // phone surfaced. The fix is a pure CSS transform: scale() on an
  // ancestor wrapper around the (otherwise-untouched) Stage, which is
  // Konva's own documented mechanism for a responsive canvas — its content
  // element's post-transform getBoundingClientRect() vs. pre-transform
  // clientWidth gives it the right ratio automatically. mobileFitScale
  // below is only used for that CSS transform now (and the viewport
  // "clipping box" sizes around it) — it never touches the Stage itself.
  // viewportWidth (immediate on first render) is used rather than the
  // existing canvasDimensions measurement, which defaults to a stale
  // 800px until its own effect measures the real container after mount.
  const mobileFitScale = isMobile
    ? Math.min(1, (viewportWidth - 16) / A4_WIDTH)
    : 1;
  const mobileStageWidth  = Math.round(A4_WIDTH * mobileFitScale);
  const mobileStageHeight = Math.round(A4_HEIGHT * mobileFitScale);

  const [showPerpendicularLines, setShowPerpendicularLines] = useState(true);

  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [isPolygonComplete, setIsPolygonComplete] = useState(false);
  const [selectedPolygonPoint, setSelectedPolygonPoint] = useState(null);
  const [isDraggingPolygonPoint, setIsDraggingPolygonPoint] = useState(false);
  const [showPolygonLayer, setShowPolygonLayer] = useState(false);
  const [showPolygonFill, setShowPolygonFill] = useState(false);
  const [showPolygonPoints, setShowPolygonPoints] = useState(false);
  const [showPolygonLines, setShowPolygonLines] = useState(true);
  const [showCenterPoint, setShowCenterPoint] = useState(false);
  
  const [polygonColor, setPolygonColor] = useState("#000000");
  const [polygonLineWeight, setPolygonLineWeight] = useState(2);
  const [polygonLineOpacity, setPolygonLineOpacity] = useState(0.7);
  const [polygonOpacity, setPolygonOpacity] = useState(0.7);

  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  // ?o. CLOSE MODALS WHEN STAGE CLOSES
useEffect(() => {
  if (!openStages.has('space-analysis')) {
    setOpenSpaceAnalysisModal(false);
  }
}, [openStages]);

useEffect(() => {
  if (!openStages.has('entrance-analysis')) {
    setOpenEntranceAnalysisModal(false);
  }
}, [openStages]);

const [exportLayers, setExportLayers] = useState(DEFAULT_EXPORT_LAYERS);
  const [layerImages, setLayerImages] = useState({});
  const [savedLayerConfigs, setSavedLayerConfigs] = useState({});
  const [isCalibrationSet, setIsCalibrationSet] = useState(false);
  const [showMeasurementDropdown, setShowMeasurementDropdown] = useState(false);
  const [pixelsPerUnit, setPixelsPerUnit] = useState(null);

  // ── Report language selector (top toolbar, next to Download Canvas) ──────
  // UI-only for now — the dropdown just remembers the pick. Wiring it to
  // actually translate remedies content is a separate, upcoming task; see
  // handleLanguageChange below for where that hook goes in.
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  // Chart/image reports toggled ON without ever being saved: the layer id whose editor is being opened, saved and closed by itself.
  const [autoCaptureLayer, setAutoCaptureLayer] = useState(null);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    setShowLanguageDropdown(false);
    // TODO(remedies-language): trigger remedies-content translation here once that work starts.
  };

  const [compassCenter, setCompassCenter] = useState(null);
  const [compassRotation, setCompassRotation] = useState(0);
  const [showCompass, setShowCompass] = useState(false);
  const [compassOpacity, setCompassOpacity] = useState(0.7);
  const [compassLineOpacity, setCompassLineOpacity] = useState(0.7);
  const [showCompassLines, setShowCompassLines] = useState(true);
  const [showSectorAreas, setShowSectorAreas] = useState(true);
  const [compassLineWeight, setCompassLineWeight] = useState(2);
  const [compassColor, setCompassColor] = useState("#000000");

  // NEW STATES - Already added ?o"
  const [compassLines, setCompassLines] = useState([]);
  const [angleInputs, setAngleInputs] = useState([]);

  // Click-to-edit popup for the on-canvas angle boxes — editing one box's
  // value here back-solves compassRotation so every other angle box (which
  // all read off that same compassRotation state) updates in lockstep,
  // exactly like dragging the rotation slider does.
  const [editingAngleBox, setEditingAngleBox] = useState(null); // { id, value } | null

  // Reverse of the calculateDisplayedAngle() used at the compass-lines render
  // site: displayedAngle = (angleFromNorth - compassRotation) mod 360, and
  // angleFromNorth (the point's fixed screen-space bearing) doesn't change
  // when compassRotation changes, so solving for compassRotation here and
  // applying it recalculates every angle box at once, not just this one.
  const saveEditingAngleBox = () => {
    if (!editingAngleBox) return;
    const line = compassLines.find(l => l.id === editingAngleBox.id);
    if (!line || !line.intersection || !compassCenter) { setEditingAngleBox(null); return; }
    const numValue = ((parseFloat(editingAngleBox.value) || 0) % 360 + 360) % 360;
    const dx = line.intersection.x - compassCenter.x;
    const dy = line.intersection.y - compassCenter.y;
    const rawAngle = Math.atan2(dy, dx);
    const angleFromNorth = (rawAngle * 180 / Math.PI + 90 + 360) % 360;

    // Mirrors calculateDisplayedAngle() at the render site EXACTLY, including
    // its "-1 when rotation > 90" rounding-correction hack — without this,
    // solving the plain (angleFromNorth - rotation) equation lands 1° off
    // for every box once rotation crosses 90°, which was the bug reported.
    const forwardDisplay = (rotation) => {
      let a = Math.round(((angleFromNorth - rotation) % 360 + 360) % 360);
      if (rotation > 90) a = (a - 1 + 360) % 360;
      return a;
    };

    let newRotation = ((angleFromNorth - numValue) % 360 + 360) % 360;
    if (forwardDisplay(newRotation) !== numValue) {
      const alt = [1, -1, 2, -2].map(d => ((newRotation + d) % 360 + 360) % 360)
        .find(c => forwardDisplay(c) === numValue);
      if (alt !== undefined) newRotation = alt;
    }

    setCompassRotation(newRotation);
    setEditingAngleBox(null);
  };

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [showGrid, setShowGrid] = useState(false);
  const [showGridConfig, setShowGridConfig] = useState(false);
  const [gridOpacity, setGridOpacity] = useState(0.7);
  const [gridRatioX, setGridRatioX] = useState(1);
  const [gridRatioY, setGridRatioY] = useState(1);

  const [showMeasurementConfig, setShowMeasurementConfig] = useState(false);
  const [measurementLineColor, setMeasurementLineColor] = useState('#000000');
  const [measurementLineWeight, setMeasurementLineWeight] = useState(2);
  const [measurementLabelColor, setMeasurementLabelColor] = useState('#000000');
  // Add after existing measurement states
  const [showDirectionInfo, setShowDirectionInfo] = useState(false);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [directionHoverColor, setDirectionHoverColor] = useState('#00A19D');
  const [directionHoverOpacity, setDirectionHoverOpacity] = useState(0.3);
  const [hoveredDirection, setHoveredDirection] = useState(null);

  const [selectedMeasurementForDelete, setSelectedMeasurementForDelete] = useState(null);
  const [selectedPointForDelete, setSelectedPointForDelete] = useState(null);
  const [show32Directions, setShow32Directions] = useState(false);
  // NEW STATES FOR LAYER EDITING - Add after line 1050
  const [editingLayer, setEditingLayer] = useState(null);
  // Mobile-only "Report Section" shortcut icon in Edit Mode's header (next
  // to Cancel/Save): saves the layer being edited, same as the existing
  // checkmark Save button, then opens the Report Section sheet once edit
  // mode actually closes. handleSaveLayerEdit (passed to LayerEditor as
  // onSave) is what calls setEditingLayer(null) once the save completes —
  // this flag + effect just watches for that instead of threading a
  // callback through LayerEditor's own large, per-layer-type save logic.
  const [pendingReportsOpen, setPendingReportsOpen] = useState(false);
  useEffect(() => {
    if (!editingLayer && pendingReportsOpen) {
      setMobileSheet('reports');
      setPendingReportsOpen(false);
    }
  }, [editingLayer, pendingReportsOpen]);
  // predefinedImages imported from ./Configure/constants/predefinedImages
  const [uploadedImages] = useState(predefinedImages);
  // Add after existing states
  const [BarChart16Data, setBarChart16Data] = useState(null);
  const [BarChart32Data, setBarChart32Data] = useState(null);
  const [BarChart45Data, setBarChart45Data] = useState(null);

  // Every sidebar category starts collapsed on both desktop and mobile —
  // the user opens whichever section they need. (Previously "Vastu" and
  // "Modern Vastu" auto-expanded on desktop, out of step with the rest.)
  const [vastuOpen, setVastuOpen] = useState(false);
  const [modernVastuOpen, setModernVastuOpen] = useState(false);
  const [VedicVastuOpen, setVedicVastuOpen] = useState(false);
  const [medicalVastuOpen, setMedicalVastuOpen] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const [housePlanningOpen, setHousePlanningOpen] = useState(false);
  const [astroNumeroOpen, setAstroNumeroOpen] = useState(false);
  // Cut Zones + Extension Zones were loose rows inside "Modern Vastu"; now
  // grouped under their own collapsible "Cut & Extension" parent.
  const [cutExtensionOpen, setCutExtensionOpen] = useState(false);

  const [tempLayerConfig, setTempLayerConfig] = useState(null);

      // Add after existing states (around line 1050)
  const [entrancePoints, setEntrancePoints] = useState([]);
  const [isSettingEntrance, setIsSettingEntrance] = useState(false);
  const [entranceData, setEntranceData] = useState(null);

  const [roomLayers, setRoomLayers] = useState([]);
  const [currentRoomPolygon, setCurrentRoomPolygon] = useState({
    points: [],
    isDrawing: false,
    isComplete: false,
    roomType: null,
    color: null
  });
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);


  const [show45Area, setShow45Area] = useState(false);
  // Line 1047 ke baad add karo:

// Cover Page States — seeded from initialProjectClientDetails (Property/Client
// Details collected on the "New Project" form) when this Configure instance
// mounts fresh for a not-yet-created project; loadProject()'s hydrateEditorState
// overwrites this with the saved project's own value once an existing project loads.
  const [clientDetails, setClientDetails] = useState(() => ({
    name: '',
    phone: '',
    email: '',
    propertyType: '',
    address: '',
    dateOfVisit: '',
    ...(initialProjectClientDetails || {}),
  }));

  const [consultantDetails, setConsultantDetails] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [selected45Block, setSelected45Block] = useState(null);
  // Footer line on every report page. '' = fall back to the user's profile (company name + website).
  const [reportFooterText, setReportFooterText] = useState('');
  const [footerModalOpen, setFooterModalOpen] = useState(false);
  // What the footer says when the user has not typed their own: company name | website from Update Profile.
  const profileFooterDefault = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user_data') || 'null');
      if (!u) return '';
      return [u.company_name, u.website_url].map((x) => (x || '').trim()).filter(Boolean).join('  |  ');
    } catch { return ''; }
  })();

  // area45Directions imported from ./Configure/constants/directions

  const [previewLayer, setPreviewLayer] = useState(null);

 // Add this state definition near your other useState declarations (around line 1050)
  const [alertState, setAlertState] = useState({
    show: false,
    title: '',
    message: '',
    type: 'warning'
  });

  // Update the showAlert and hideAlert functions:
  const showAlert = (title, message, type = 'warning') => {
    setAlertState({
      show: true,
      title,
      message,
      type
    });
  };

  const hideAlert = () => {
    setAlertState(prev => ({ ...prev, show: false }));
  };

  // ?"??"??"? Project flow state (Phase 2) ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [saveModalMode, setSaveModalMode] = useState('create'); // 'create' | 'edit'

  // Full-screen "Saving project…" animation — capped to 3s per explicit
  // design decision, separate from project.loading itself (which can stay
  // true longer, e.g. while report images are still syncing in the
  // background — that real-progress case has its own header banner further
  // down, which is NOT capped and keeps running until actually done). This
  // overlay is purely the decorative "saving" animation and closes at 3s
  // regardless of whether the underlying save call is still in flight, or
  // sooner if the save already finished by then.
  const [showSavingOverlay, setShowSavingOverlay] = useState(false);
  useEffect(() => {
    if (!isSavingProject) { setShowSavingOverlay(false); return; }
    setShowSavingOverlay(true);
    const t = setTimeout(() => setShowSavingOverlay(false), 3000);
    return () => clearTimeout(t);
  }, [isSavingProject]);

  // Bundle the editor state slices that round-trip through the project payload.
  // Setters use standard naming so useEditorState.applySetters() can dispatch by key.
  const editorStateSlices = {
    currentUnit, pixelsPerUnit, isCalibrationSet,
    polygonPoints, polygonColor, polygonLineWeight, polygonLineOpacity, polygonOpacity,
    showPolygonLayer, showPolygonFill, showPolygonPoints, showPolygonLines, showCenterPoint,
    measurements, measurementLineColor, measurementLineWeight, measurementLabelColor,
    compassCenter, compassRotation, compassOpacity, compassLineOpacity, compassLineWeight,
    compassColor, showCompass, showCompassLines, showSectorAreas, show32Directions,
    compassLines, angleInputs,
    showGrid, gridOpacity, gridRatioX, gridRatioY,
    roomLayers,
    entrancePoints, entranceData,
    exportLayers, savedLayerConfigs, layerImages,
    clientDetails, consultantDetails, selected45Block, reportFooterText,
    numeroRemedyData,
  };
  const editorStateSetters = {
    setCurrentUnit, setPixelsPerUnit, setIsCalibrationSet,
    setPolygonPoints, setPolygonColor, setPolygonLineWeight, setPolygonLineOpacity, setPolygonOpacity,
    setShowPolygonLayer, setShowPolygonFill, setShowPolygonPoints, setShowPolygonLines, setShowCenterPoint,
    setIsPolygonComplete,
    setMeasurements, setMeasurementLineColor, setMeasurementLineWeight, setMeasurementLabelColor,
    setCompassCenter, setCompassRotation, setCompassOpacity, setCompassLineOpacity, setCompassLineWeight,
    setCompassColor, setShowCompass, setShowCompassLines, setShowSectorAreas, setShow32Directions,
    setCompassLines, setAngleInputs,
    setShowGrid, setGridOpacity, setGridRatioX, setGridRatioY,
    setRoomLayers,
    setEntrancePoints, setEntranceData,
    setExportLayers, setSavedLayerConfigs, setLayerImages,
    setClientDetails, setConsultantDetails, setSelected45Block, setReportFooterText,
    setNumeroRemedyData,
  };
  const { snapshot: editorSnapshot, applySetters } = useEditorState(editorStateSlices, editorStateSetters);
  const project = useProject({ snapshot: editorSnapshot, applySetters, externalProjectId: projectId });

  // Numero Vastu / Kua Number Analysis data used to live under flat, un-scoped
  // localStorage keys shared by every one of a user's projects — scoping them
  // by project id here (matching VastuPersonsGateway.jsx's own fix) keeps the
  // sidebar toggle, "DATA" badge, and combined PDF export all reading the
  // right project's people instead of whichever project last saved.
  const activeProjectIdForStorage = project.currentProject?.id || projectId;
  const numeroVastuStorageKey = `numero_vastu_data_${activeProjectIdForStorage || 'new'}`;
  const numeroVastuKuaStorageKey = `numero_vastu_kua_data_${activeProjectIdForStorage || 'new'}`;
  const vastuPersonsStorageKey = `vastu_persons_data_${activeProjectIdForStorage || 'new'}`;
  const astroVastuStorageKey = `astro_vastu_data_${activeProjectIdForStorage || 'new'}`;
  const astroChartStyleStorageKey = `astro_vastu_chart_styles_${activeProjectIdForStorage || 'new'}`;
  const cutZoneStorageKey = `cut_zone_data_${activeProjectIdForStorage || 'new'}`;
  const extensionZoneStorageKey = `extension_zone_data_${activeProjectIdForStorage || 'new'}`;
  // These 6 were flat/unscoped keys shared across every one of a user's
  // projects (same class of bug as numero/astro above) — now project-scoped,
  // matching the sub-components' own now-scoped keys.
  const spaceAnalysisStorageKey = `space_analysis_report_data_${activeProjectIdForStorage || 'new'}`;
  const entranceAnalysisStorageKey = `entrance_analysis_report_data_${activeProjectIdForStorage || 'new'}`;
  const vastuDevtaGuideStorageKey = `vastu_devta_guide_data_${activeProjectIdForStorage || 'new'}`;
  const navgrahShantiStorageKey = `navgrah_shanti_data_${activeProjectIdForStorage || 'new'}`;
  const fengShuiStorageKey = `feng_shui_data_${activeProjectIdForStorage || 'new'}`;
  const helixStorageKey = `helix_data_${activeProjectIdForStorage || 'new'}`;
  const zoneAnalysisStorageKey = `zone_analysis_data_${activeProjectIdForStorage || 'new'}`;

  // Auto-load existing project when projectId prop is provided (user opened from ProjectsPage).
  // Restores floor plan image and notifies Editor of the project name.
  useEffect(() => {
    if (projectId) {
      project.loadProject(projectId).then((loaded) => {
        if (loaded?.floorPlanRef && onFloorPlanLoad) {
          // Pass the raw floorPlanRef through unchanged — this is the value that gets sent
          // back to saveProject/createProject, so it must stay the canonical storage URL
          // (not a resolved data: URL, or every save would re-upload the image as new).
          // The canvas-facing `baseImage` below resolves a display copy separately.
          onFloorPlanLoad(loaded.floorPlanRef, null);
        }
        if (loaded?.name && onProjectLoad) {
          onProjectLoad(loaded.name);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const openSaveAsNew = () => { setSaveModalMode('create'); setShowSaveModal(true); };
  const openSaveChanges = () => { setSaveModalMode('edit'); setShowSaveModal(true); };
  const performSave = async (mode, name, newClientDetails) => {
    setIsSavingProject(true);
    try {
      if (mode === 'edit' && project.currentProject?.id) {
        await project.saveProject(name, finalImage);
      } else {
        // Property/Client Details collected on the "Create Project" form —
        // update the live editor state too (so the Cover Page reflects it
        // immediately within this same session) and hand it to createProject
        // directly for the actual save (avoids a stale-closure race with the
        // setClientDetails update above).
        if (newClientDetails) setClientDetails(prev => ({ ...prev, ...newClientDetails }));
        await project.createProject(name, finalImage, newClientDetails);
      }
      setShowSaveModal(false);
      // Fire any pending navigation callback (e.g. "Save & Leave" from UnsavedChangesModal)
      if (afterSaveCallbackRef.current) {
        const cb = afterSaveCallbackRef.current;
        afterSaveCallbackRef.current = null;
        cb();
      }
    } catch (e) {
      // Report-image chunks can now fail the save outright (see
      // saveLayerImagesInChunks) instead of silently vanishing — surface it
      // so the user knows to retry, and leave the Save modal open (don't
      // fire the "leave" callback) so nothing navigates away on a failed save.
      showAlert(
        'Save Incomplete',
        e.message || 'Something went wrong while saving. Please try again.',
        'warning'
      );
    } finally {
      setIsSavingProject(false);
    }
  };
  const handleSaveSubmit = ({ name, clientDetails: newClientDetails }) => performSave(saveModalMode, name, newClientDetails);

  // Save straight away when the project already has a name (typed when the
  // project was created / already saved) — the naming modal only opens when
  // there genuinely is no name yet. Returns true if it saved directly.
  const saveDirect = () => {
    if (isSavingProject || project.loading) return true;
    // Existing project still loading — nothing to save yet (and must not create a duplicate).
    if (projectId && !project.currentProject?.id) return true;
    if (project.currentProject?.id && project.currentProject?.name) {
      performSave('edit', project.currentProject.name);
      return true;
    }
    if (!project.currentProject?.id && initialProjectName) {
      performSave('create', initialProjectName, initialProjectClientDetails);
      return true;
    }
    return false;
  };
  const triggerSave = () => {
    if (saveDirect()) return;
    if (project.currentProject?.id) openSaveChanges(); else openSaveAsNew();
  };

  // afterSaveCallback — called once after a successful save (used by Editor to proceed blocked navigation)
  const afterSaveCallbackRef = useRef(null);

  // Expose save trigger to parent (Editor topbar) via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        save: () => triggerSave(),
        // Editor's Esc → Projects shortcut stays out of the way while any
        // modal / editor / inline edit is open.
        hasOpenOverlay: () => !!(
          editingLayer || showSaveModal || alertState.show || mobileSheet || videoGuideOpen
          || openSpaceAnalysisModal || openEntranceAnalysisModal || openVastuDevtaGuideModal
          || openNavgrahShantiModal || openFengShuiModal || openHelixModal || openZoneAnalysisModal
          || openVastuGatewayModal || openCutExtensionModal || openBirthDateModal || openNumeroReport
          || editingAngleBox || measurementMode || isGeneratingPDF
        ),
        // Export (PDF / Word) — triggered from the Editor top bar's Export popover.
        exportPdf: () => handleExportPDF(),
        exportWord: () => handleExportWord(),
        isExporting: isGeneratingPDF,
        isLoading: project.loading,
        isDirty: project.isDirty,
        projectName: project.currentProject?.name ?? null,
        setAfterSaveCallback: (cb) => { afterSaveCallbackRef.current = cb; },
      };
    }
  });

  // Esc dismisses the alert popup.
  useEffect(() => {
    if (!alertState.show) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); hideAlert(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertState.show]);

  // Notify Editor whenever dirty state flips so it can show/hide the "Unsaved" badge
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(project.isDirty);
  }, [project.isDirty, onDirtyChange]);

  // Warn (native browser prompt) if the user tries to close/reload the tab
  // while report-layer images are still uploading after a save (see
  // syncLayerImages in useProject.js). That upload is now awaited by
  // saveProject/createProject — the Save modal's spinner stays open and
  // "Save" isn't reported as done until every image has actually landed in
  // the DB — but a user can still force-close the tab while that await is
  // in flight, which is exactly the scenario a client explicitly reported:
  // a report saved once should stay saved until they close it themselves.
  // This is the last-resort backstop for that.
  useEffect(() => {
    if (!project.layerImagesSyncProgress) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [project.layerImagesSyncProgress]);

// Shared "Action Required" body for every report that needs Boundary + Measurement first.
// Reports drawn on the boundary + compass but not using the measurement scale — "Set Measurement" is NOT required for them.
const BOUNDARY_COMPASS_ONLY_LAYERS = ["4-state-image","surya-chandra-bhag","Vedic-zones-32","Vedic-numbers-64","Vedic-64-padvinyash","Vedic-81-padvinyash","Vedic-elemental-zone","Vedic-planet-zoning","compass-45","body-part-division","compass-8","compass-16","compass-24","compass-32","compass-45-2"];
const getBoundaryCompassAlertMessage = () => (
  <div style={{ textAlign: 'left' }}>
    <div style={{ fontWeight: 700, marginBottom: 8 }}>Please follow the steps below to complete this action:</div>
    <ol style={{ margin: 0, paddingLeft: 20 }}>
      <li style={{ marginBottom: 6 }}>Set the <strong>Boundary</strong> (Set Pin / Boundary).</li>
      <li>Go to <strong>Apply Compass</strong> and place the compass centre.</li>
    </ol>
  </div>
);

const getPolygonSetupAlertMessage = () => (
  <div style={{ textAlign: 'left' }}>
    <div style={{ fontWeight: 700, marginBottom: 8 }}>Please follow the steps below to complete this action:</div>
    <ol style={{ margin: 0, paddingLeft: 20 }}>
      <li style={{ marginBottom: 6 }}>Set the <strong>Boundary</strong>.</li>
      <li>
        Go to the <strong>Measurement</strong> tab and click <strong>Set Measurement</strong> to scale the layout.
        <div style={{ marginTop: 6, fontSize: 13 }}>
          (Please watch the demo video guide for assistance —{' '}
          <a
            href="#video-guide"
            onClick={(e) => { e.preventDefault(); hideAlert(); setVideoGuideKind('action-required'); setVideoGuideOpen(true); }}
            style={{ color: '#FF6B5C', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
          >watch now</a>.)
        </div>
      </li>
    </ol>
  </div>
);

// "Please open the “X” report in View and Edit mode, save the layer first, and then enable it."
const reportNotSavedText = (name) =>
  `Please open the “${String(name).toUpperCase()}” report in View and Edit mode, save the layer first, and then enable it.`;
const reportNotSavedMessage = (layerId) => {
  const isBarBase = layerId.includes('Bar-chart') && !layerId.includes('-table') && !layerId.includes('-chart');
  if (!isBarBase && (layerId.includes('-table') || layerId.includes('-chart'))) {
    const baseId = layerId.replace('-table', '').replace('-chart', '');
    return reportNotSavedText(getLayerName(baseId) + (layerId.includes('-table') ? ' - Table' : ' - Chart'));
  }
  return reportNotSavedText(getLayerName(layerId));
};

// Open the layer's editor invisibly and run its normal Save, so a chart report can be enabled without opening Edit first.
const startAutoCapture = (layerId) => {
  const editId = (layerId.includes('Bar-chart') && /-(table|chart)$/.test(layerId)) ? layerId.replace(/-(table|chart)$/, '') : layerId;
  setAutoCaptureLayer(editId);
  handleEditLayer(editId);
};

const handleToggleLayer = (layerId, enabled) => {
  // Define which layers require validation
  const layersRequiringValidation = [
    'Bar-chart-8', 'Bar-chart-16', 'Bar-chart-32', 'Bar-chart-45', 
    'tri-dosh-Vedic','tri-guna-modern','tri-guna-Vedic','tri-dosh-modern',
    'compass-45-2-table', 'compass-45-2-chart',  
    'center-analysis', 'mahavastu-chakra', 'Vedic-Mahavastu-Chakra','shakti-chakra',
    'zones-8-analysis', 'zones-8-chart',
    'zones-16-analysis', 'zones-16-chart', 'zones-24-analysis',
    'zones-32-mahavastu', 'zones-32-chart', 'modern-Devata-45',
    'modern-Devata-45-chart', 'coloured-zone', 'elemental-5-zone',
    'elemental-5-chart', 'marmsthan-analysis', 'marmsthan-advance',
    'vastu-numbers-81','vastu-numbers-81-numbers','vastu-numbers-81-numbers-Vedic', 'vastu-numbers-64', 'grid-3x3', 
  'MANDAL',
    'RATNA-STHAPNA',
  ];

  // Boundary + compass only (no measurement) for the reports listed in BOUNDARY_COMPASS_ONLY_LAYERS
  if (BOUNDARY_COMPASS_ONLY_LAYERS.includes(layerId) && (!isPolygonComplete || !compassCenter)) {
    showAlert('Action Required', getBoundaryCompassAlertMessage(), 'warning');
    return;
  }

  // ?o. Check validation only for layers that require it (polygon/compass)
  if (layersRequiringValidation.includes(layerId)) {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet) {
      showAlert(
        'Action Required', 
        getPolygonSetupAlertMessage(),
        'warning'
      );
      return;
    }
  }

  // ?o. Toggle ON tabhi allow karo jab report save ho chuki ho (layerImages mein exist kare)
  if (enabled) {
    // Bar-chart layers ke liye table/chart dono check karo
    const isBarChartBase = layerId.includes('Bar-chart') && !layerId.includes('-table') && !layerId.includes('-chart');
    
    if (isBarChartBase) {
      const tableId = `${layerId}-table`;
      const chartId = `${layerId}-chart`;
      if (!layerImages[tableId] && !layerImages[chartId]) {
        startAutoCapture(layerId);
          return;
      }
    } else if (layerId.includes('-table') || layerId.includes('-chart')) {
      // compass-45-2-table, compass-45-2-chart etc ??" direct key check
      if (!layerImages[layerId]) {
        startAutoCapture(layerId);
          return;
      }
    } else if (!layerImages[layerId]) {
      // cover-page aur special reports ko skip karo ??" unka alag logic hai
      const skipSaveCheck = [
        'cover-page',
        'entrance-analysis-report',
        'space-analysis-report',
        'vastu-devta-guide-report',
        'navgrah-shanti-report',
        'feng-shui-report',
        'helix-report',
        'zone-analysis-report',
        'numero-vastu-report',
        'astro-vastu-report',
        'cut-zone-report',
        'extension-zone-report',
      ];
      // ?o. Medical Vastu generative layers ??" agar save ho chuki hain toh toggle allow karo
      const medicalVastuLayers = [
        'tri-dosh-Vedic', 'tri-dosh-modern', 'tri-guna-Vedic', 'tri-guna-modern', 'body-part-division'
      ];
      // ?o. DEBUG: Log toggle check state
      if (!skipSaveCheck.includes(layerId) && !medicalVastuLayers.includes(layerId)) {
        startAutoCapture(layerId);
          return;
      }
      // ?o. Medical Vastu: agar pehle save nahi hua toh alert do
      if (medicalVastuLayers.includes(layerId) && !layerImages[layerId]) {
        startAutoCapture(layerId);
          return;
      }
    }
  }

  // For Bar chart main layers, toggle both table and chart components
  if (layerId.includes('Bar-chart') && !layerId.includes('-table') && !layerId.includes('-chart')) {
    setExportLayers(prev => ({
      ...prev,
      [`${layerId}-table`]: { ...prev[`${layerId}-table`], enabled },
      [`${layerId}-chart`]: { ...prev[`${layerId}-chart`], enabled }
    }));
  } else {
    setExportLayers(prev => ({
      ...prev,
      [layerId]: { ...prev[layerId], enabled }
    }));
  }
};
  // Download Canvas as Image
  const handleDownloadCanvas = () => {
    if (!stageRef.current) return;
    
    try {
      const uri = stageRef.current.toDataURL({
        pixelRatio: 3, // High quality
        mimeType: 'image/png'
      });
      
      const link = document.createElement('a');
      link.download = `floor-plan-${Date.now()}.png`;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      setShowSuccessMessage(true);
      setSuccessMessage('Canvas downloaded successfully!');
      setTimeout(() => setShowSuccessMessage(false), 2000);
    } catch (error) {
      console.error('Error downloading canvas:', error);
      alert('Failed to download canvas. Please try again.');
    }
  };

const handleEditLayer = (layerId) => {
  // Mobile: Edit Mode opens as its own full-screen overlay (LayerEditor),
  // but the Report Section sheet sits on a HIGHER z-index than it, so it
  // stayed stuck on top, hiding Edit Mode behind it even though it had
  // opened. Close the sheet whenever an Edit button inside it is tapped —
  // desktop has no such sheet (isMobile false), so this is a no-op there.
  if (isMobile) setMobileSheet(null);
  let currentConfig = {};
  let canEdit = true;
  let errorMessage = '';

  const previouslySavedConfig = savedLayerConfigs[layerId];

  if (previouslySavedConfig) {
    const mergedConfig = {
      ...previouslySavedConfig,
      ...(previouslySavedConfig.compassRotation !== undefined && { compassRotation: compassRotation }),
    };
    setTempLayerConfig(mergedConfig);
    setEditingLayer(layerId);
    return;
  }

  const modernVastuLayers = [
    'center-analysis',
    'mahavastu-chakra',
    'Vedic-Mahavastu-Chakra',
    'shakti-chakra',
    'compass-8',
    'Bar-chart-8',
    'compass-16',
    'Bar-chart-16',
    'compass-24',
    'compass-32',
    'Bar-chart-32',
    'compass-45',
    'compass-45-table',
    'compass-45-chart',
    'compass-45-2',
    'compass-45-2-table',
    'compass-45-2-chart',
    'coloured-zone',
    'elemental-5-zone',
    'elemental-5-chart',
    'marmsthan-analysis',
    'marmsthan-advance',
    'vastu-numbers-81-numbers',
    'vastu-numbers-81-numbers-Vedic',
    'vastu-numbers-64',
    'grid-3x3',
    'MANDAL',
    'RATNA-STHAPNA',
  ];

  if (BOUNDARY_COMPASS_ONLY_LAYERS.includes(layerId)) {
    if (!isPolygonComplete || !compassCenter) {
      canEdit = false;
      errorMessage = getBoundaryCompassAlertMessage();
    }
  } else if (modernVastuLayers.includes(layerId)) {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet) {
      canEdit = false;
      errorMessage = getPolygonSetupAlertMessage();
    }
  }

  switch (layerId) {
    case 'cover-page':
      currentConfig = {
        showCoverPage: true,
        texts: [],
        layerMeasurements: []
      };
      break;

    case 'floor-plan':
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        showFloorPlan: true,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'grid-layout':
      currentConfig = {
        showGrid: true,
        gridOpacity: gridOpacity,
        gridRatioX: gridRatioX,
        gridRatioY: gridRatioY,
        gridColor: '#000000',
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'center-analysis':
      if (canEdit) {
        currentConfig = {
          showCenterPoint: true,
          centerPoint: compassCenter,
          opacity: 1,
          scale: 1,
          showCenterAnalysisLayer: true,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    case 'mahavastu-chakra':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'Vedic-Mahavastu-Chakra':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'shakti-chakra':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'floor-plan-vpm':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'compass-8':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'MANDAL':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'RATNA-STHAPNA':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'Bar-chart-8':
      if (canEdit) {
        const chart8Data = calculateBarChart8Data();
        currentConfig = {
          data: chart8Data,
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    case 'compass-16':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'Bar-chart-16':
      if (canEdit) {
        const chart16Data = calculateBarChart16Data();
        currentConfig = {
          data: chart16Data,
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    case 'compass-24':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'surya-chandra-bhag':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case '4-state-image':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'tri-dosh-Vedic':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'tri-guna-modern':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'tri-guna-Vedic':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'tri-dosh-modern':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;
    case 'body-part-division':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'compass-32':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: true,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'Bar-chart-32':
      if (canEdit) {
        const chart32Data = calculateBarChart32Data();
        currentConfig = {
          data: chart32Data,
          showTable: true,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    // ?o. Vedic 45 Devata (WITH NAME)
    case 'compass-45':
      currentConfig = {
        show45Area: show45Area,
        compassOpacity: compassOpacity,
        compassRotation: compassRotation,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    // ?o. Vedic 45 Devata TABLE
    case 'compass-45-table':
      if (canEdit) {
        const chart45TableData = calculateBarChart45Data();
        currentConfig = {
          data: chart45TableData,
          showTable: true,
          showBarChart: false,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    // ?o. Vedic 45 Devata BAR CHART
    case 'compass-45-chart':
      if (canEdit) {
        const chart45ChartData = calculateBarChart45Data();
        currentConfig = {
          data: chart45ChartData,
          showTable: false,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    // ?o. MODERN 45 Devata (WITH NAME)
    case 'compass-45-2':
      currentConfig = {
        show45Area: show45Area,
        compassOpacity: compassOpacity,
        compassRotation: compassRotation,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    // ?o. MODERN 45 Devata TABLE
    case 'compass-45-2-table':
      if (canEdit) {
        const chart452TableData = calculateBarChart452Data();
        currentConfig = {
          data: chart452TableData,
          showTable: true,
          showBarChart: false,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    // ?o. MODERN 45 Devata BAR CHART
    case 'compass-45-2-chart':
      if (canEdit) {
        const chart452ChartData = calculateBarChart452Data();
        currentConfig = {
          data: chart452ChartData,
          showTable: false,
          showBarChart: true,
          tableOpacity: 0.9,
          chartOpacity: 0.9,
          compassRotation: compassRotation,
          texts: [],
          layerMeasurements: [],
          layerIsCalibrationSet: false,
          layerPixelsPerUnit: null
        };
      }
      break;

    case 'coloured-zone':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'elemental-5-zone':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'elemental-5-chart':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'marmsthan-analysis':
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'marmsthan-advance':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'vastu-numbers-81-numbers':
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'vastu-numbers-81-numbers-Vedic':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'vastu-numbers-64':
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'grid-3x3':
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'png-image-1':
    case 'nakshatra-vastu':
    case 'Devata-Nakshatra-Vastu':
    case '32-Entrance-Analysis-Compass':
    case 'Khanija-Dhatu':
    case '16-Directional-Domestic-Vastu':
    case 'Direction-Diety-Planet':
    case 'Devata-Khanij':
    case 'Devata-Bhojan':
    case 'Devata-Bhojan-aadi':
    case 'Devata-chinha':
    case 'Devata-chinha-aadi':
    case 'Muhurat-vichar':
    case 'Seharumkh-achintya-vibhav':
    case 'house-bank':
    case 'house-clinic':
    case 'house-electronic-shop':
    case 'house-factory':
    case 'house-hotel':
    case 'house-marriage-hall':
    case 'house-multiplex':
    case 'house-office-cabin':
    case 'house-petrol-pump':
    case 'house-retail-outlets':
    case 'house-school-college':
    case 'house-space-analysis-compass':
    case 'house-temple':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    // Vedic Generative Zones — computed live against the plot polygon +
    // compass center (same as compass-32), not a draggable image.
    case 'Vedic-zones-32':
    case 'Vedic-numbers-64':
    case 'Vedic-64-padvinyash':
    case 'Vedic-81-padvinyash':
    case 'Vedic-elemental-zone':
    case 'Vedic-planet-zoning':
      currentConfig = {
        showCompass: true,
        compassRotation: compassRotation,
        compassOpacity: compassOpacity,
        compassColor: compassColor,
        compassLineWeight: compassLineWeight,
        compassLineOpacity: compassLineOpacity,
        showCompassLines: showCompassLines,
        showSectorAreas: showSectorAreas,
        show32Directions: false,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    case 'png-image-2':
    case 'Vedic-devata-Numbers-with-Names-81':
    case 'Disha-Gandha':
    case 'Devata-Nighat':
    case 'Nighantu':
      currentConfig = {
        rotation: 0,
        opacity: 0.7,
        scale: 1,
        floatMode: false,
        positionX: compassCenter ? compassCenter.x : 0,
        positionY: compassCenter ? compassCenter.y : 0,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
      break;

    default:
      currentConfig = {
        rotation: 0,
        opacity: 1,
        scale: 1,
        texts: [],
        layerMeasurements: [],
        layerIsCalibrationSet: false,
        layerPixelsPerUnit: null
      };
  }

  if (!canEdit) {
    showAlert('Action Required', errorMessage, 'warning');
    return;
  }

  setTempLayerConfig(currentConfig);
  setEditingLayer(layerId);
};
// toMartinezPolygon, buildSectorWedge, clipSectorWithBoundary imported from ./Configure/utils/geometry
const handleSaveLayerEdit = (saveData) => {
    if (saveData.customBackground !== undefined && (editingLayer === 'cover-page' || editingLayer === 'cover-back-page')) {
        const bgKey = `${editingLayer}-bg`;
        setLayerImages(prev => {
            const next = { ...prev };
            if (saveData.customBackground) next[bgKey] = saveData.customBackground; else delete next[bgKey];
            return next;
        });
    }
    const completeConfig = {
        ...saveData.config,
        texts: saveData.config.texts || [],
        layerMeasurements: saveData.config.layerMeasurements || [],
        layerIsCalibrationSet: saveData.config.layerIsCalibrationSet !== undefined 
            ? saveData.config.layerIsCalibrationSet : false,
        layerPixelsPerUnit: saveData.config.layerPixelsPerUnit !== undefined 
            ? saveData.config.layerPixelsPerUnit : null,
        positionX: saveData.config.positionX !== undefined ? saveData.config.positionX : 0,
        positionY: saveData.config.positionY !== undefined ? saveData.config.positionY : 0,
        floatMode: saveData.config.floatMode !== undefined ? saveData.config.floatMode : false
    };
    
    setSavedLayerConfigs(prev => ({
        ...prev,
        [editingLayer]: completeConfig
    }));
    
    setTempLayerConfig(null);
    
    // ?o. Special handling for compass-45-chart ??" array return karta hai, single dataURL store karo
    if (editingLayer === 'compass-45-chart' && Array.isArray(saveData.images) && saveData.images.length > 0) {
        setLayerImages(prev => ({
            ...prev,
            [editingLayer]: saveData.images[0]?.dataURL
        }));
        setExportLayers(prev => ({
            ...prev,
            [editingLayer]: { ...prev[editingLayer], enabled: true }
        }));
    // ?o. Special handling for compass-45-2-chart multi-page data
    } else if (editingLayer === 'compass-45-2-chart' && saveData.imageData && typeof saveData.imageData === 'object' && saveData.imageData.page1) {
        setLayerImages(prev => ({
            ...prev,
            [`${editingLayer}-page1`]: saveData.imageData.page1,
            [`${editingLayer}-page2`]: saveData.imageData.page2,
            [`${editingLayer}-page3`]: saveData.imageData.page3,
            [editingLayer]: saveData.imageData.page1
        }));
    } else if ((editingLayer === 'cover-page' || editingLayer === 'cover-index') && Array.isArray(saveData.images) && saveData.images.length > 0) {
        // ?o. Cover page: save full array ??" dono pages PDF mein jayenge
        setLayerImages(prev => ({
            ...prev,
            [editingLayer]: saveData.images
        }));
    } else if ((editingLayer.includes('Bar-chart') || editingLayer === 'compass-45-2-table' || editingLayer === 'compass-45-table') && (Array.isArray(saveData.imageData) || Array.isArray(saveData.images))) {
        // Store each component separately for Bar charts
        const imageArray = Array.isArray(saveData.imageData) ? saveData.imageData : saveData.images;

        if (imageArray.length > 0) {
            // ?o. Single setLayerImages call ??" React batching problem avoid
            const newImages = {};
            imageArray.forEach(imageItem => {
                const componentId = `${editingLayer}-${imageItem.type}`;
                newImages[componentId] = imageItem.dataURL;
            });
            // ?o. Base layerId mein bhi store karo (table prefer, fallback chart)
            const tableItem = imageArray.find(item => item.type === 'table');
            const chartItem = imageArray.find(item => item.type === 'chart');
            newImages[editingLayer] = tableItem?.dataURL || chartItem?.dataURL;

            setLayerImages(prev => ({ ...prev, ...newImages }));

            // ?o. Single setExportLayers call ??" all components + base layer enable
            const newExportState = {};
            imageArray.forEach(imageItem => {
                const componentId = `${editingLayer}-${imageItem.type}`;
                newExportState[componentId] = { enabled: true };
            });
            // Bar-chart base toggle enable karo
            if (editingLayer.includes('Bar-chart')) {
                newExportState[editingLayer] = { enabled: true };
            }
            setExportLayers(prev => {
                const updated = { ...prev };
                Object.keys(newExportState).forEach(k => {
                    updated[k] = { ...prev[k], ...newExportState[k] };
                });
                return updated;
            });
        }

        // ?o. compass-45-table aur compass-45-2-table ke liye bhi base key save karo
        if (editingLayer === 'compass-45-table' || editingLayer === 'compass-45-2-table') {
            const tableItem = imageArray.find(item => item.type === 'table');
            if (tableItem) {
                setLayerImages(prev => ({
                    ...prev,
                    [editingLayer]: tableItem.dataURL
                }));
            }
        }
    } else {
        // Standard single image handling (includes Medical Vastu generative layers)
        const imageToStore = saveData.imageData;
        
        if (imageToStore) {
            setLayerImages(prev => {
                const updated = { ...prev, [editingLayer]: imageToStore };
                return updated;
            });
        } else {
            console.error(`?O No imageData for layer "${editingLayer}" ??" layerImages NOT updated`);
        }
    }
    
    const config = saveData.config;
    switch(editingLayer) {
        case 'cover-page':
            // No special state to save, texts are already handled
            break;
        case 'grid-layout':
            setGridOpacity(config.gridOpacity);
            setGridRatioX(config.gridRatioX);
            setGridRatioY(config.gridRatioY);
            break;
        case 'compass-16':
        case 'marmsthan-advance':
        case 'coloured-zone':
        case 'elemental-5-chart':
        case 'elemental-5-zone':
        case 'compass-32':
            setCompassRotation(config.compassRotation);
            setCompassOpacity(config.compassOpacity);
            setCompassColor(config.compassColor);
            setCompassLineWeight(config.compassLineWeight);
            if (config.compassLineOpacity !== undefined) setCompassLineOpacity(config.compassLineOpacity);
            setShowCompassLines(config.showCompassLines);
            setShowSectorAreas(config.showSectorAreas);
            if (editingLayer === 'compass-32') {
                setShow32Directions(true);
            }
            break;
        case 'compass-45':
        case 'compass-45-2':
        case 'compass-45-2-table':
        case 'compass-45-2-chart':
            setShow45Area(config.show45Area);
            setCompassOpacity(config.compassOpacity);
            setCompassRotation(config.compassRotation || 0);
            break;
        case 'Bar-chart-16':
        case 'Bar-chart-32':
        case 'Bar-chart-45':
            break;
        default:
            break;
    }
    
    // ?o. Enable export layers (skip for multi-component Bar charts except compass-45-2-chart)
    if (!editingLayer.includes('Bar-chart') && editingLayer !== 'compass-45-2-table' && editingLayer !== 'compass-45-2-chart') {
        setExportLayers(prev => ({
            ...prev,
            [editingLayer]: { ...prev[editingLayer], enabled: true }
        }));
    } else if (editingLayer === 'compass-45-2-chart' || editingLayer === 'compass-45-2-table') {
        // ?o. Enable compass-45-2-chart / compass-45-2-table for export
        setExportLayers(prev => ({
            ...prev,
            [editingLayer]: { ...prev[editingLayer], enabled: true }
        }));
    }

// Exit edit mode
    setEditingLayer(null);
    setShow32Directions(false); // ?o. FIX: main canvas pe 16 directions hi dikhein

    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
};
const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
// "pdf" | "word" — which download the shared export routine below should finish with.
// Read once, at the very start of handleExportPDF, so the PDF button always behaves exactly as before.
const exportOutputRef = useRef('pdf');
const [exportKind, setExportKind] = useState('pdf');
const handleExportWord = async () => {
  if (isGeneratingPDF) return;
  exportOutputRef.current = 'word';
  setExportKind('word');
  try {
    await handleExportPDF();
  } finally {
    exportOutputRef.current = 'pdf';
    setExportKind('pdf');
  }
};


const handleExportPDF = async () => {
  const outputKind = exportOutputRef.current;
  const exportStartedAt = performance.now();
  try {
  // Consultant's logo (saved on the cover-page layer, or falling back to the
  // profile default). jsPDF's addImage needs actual image bytes, not a URL —
  // resolveLogoImage already converts the common /api/user-logo/ case to a
  // data: URI, but silently falls back to the bare URL on any other path
  // (a different host, or that one API call failing), and a bare URL was
  // being passed straight to the "only accept data:" check below, so the
  // logo just never appeared. Same fix as the live editor's ConsultantLogoImage
  // (crossOrigin="anonymous"): force one more conversion attempt via canvas
  // before giving up. No cropping — the whole logo is shown, uncropped, no radius.
  let resolvedLogoForPdf = '';
  try {
    const coverCfgForLogo = savedLayerConfigs['cover-page'] || {};
    const logoUrlForPdf = coverCfgForLogo.consultantInfo?.logo;
    if (logoUrlForPdf) {
      const rawLogo = await userAuthAPI.resolveLogoImage(logoUrlForPdf);
      if (rawLogo && rawLogo.startsWith('data:')) {
        resolvedLogoForPdf = rawLogo;
      } else if (rawLogo) {
        resolvedLogoForPdf = await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              canvas.getContext('2d').drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            } catch (e) {
              resolve(''); // still tainted even with crossOrigin — no CORS headers on this host, give up cleanly
            }
          };
          img.onerror = () => resolve('');
          img.src = rawLogo;
        });
      }
    }
  } catch (e) { /* no logo available — cover/closing pages just won't show one */ }

  // Cover/closing page background artwork — bundled webpack assets (URLs,
  // not raw bytes), so jsPDF's addImage needs them as data: URLs. Both source
  // files are already compressed, size-tuned JPEGs (~320-330KB), so this just
  // reads their existing bytes straight to base64 — no canvas re-encode,
  // which would decode+recompress a second time for no benefit.
  const resolveBundledImageToDataUrl = (assetUrl) => new Promise((resolve, reject) => {
    fetch(assetUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
  let resolvedCoverTemplateForPdf = '';
  try { resolvedCoverTemplateForPdf = await resolveBundledImageToDataUrl(img_cover_page_template); }
  catch (e) { /* cover page will just render without the background artwork */ }
  let resolvedClosingTemplateForPdf = '';
  try { resolvedClosingTemplateForPdf = await resolveBundledImageToDataUrl(img_last_page_template); }
  catch (e) { /* closing page will just render without the background artwork */ }
  // Custom full-page backgrounds uploaded in Cover Front / Cover Back edit mode.
  try {
    const customFront = await projectService.resolveLayerImage(layerImages['cover-page-bg']);
    if (customFront) resolvedCoverTemplateForPdf = customFront;
    const customBack = await projectService.resolveLayerImage(layerImages['cover-back-page-bg']);
    if (customBack) resolvedClosingTemplateForPdf = customBack;
  } catch (e) { /* keep the bundled templates */ }
  // Full-page cover / closing artwork: keep it sharp (~200 dpi) but not at the size of a poster.
  try {
    if (resolvedCoverTemplateForPdf) resolvedCoverTemplateForPdf = await shrinkImageDataUrl(resolvedCoverTemplateForPdf, { quality: 0.9, maxSide: 2400 });
    if (resolvedClosingTemplateForPdf) resolvedClosingTemplateForPdf = await shrinkImageDataUrl(resolvedClosingTemplateForPdf, { quality: 0.9, maxSide: 2400 });
  } catch (e) { /* keep them as they are */ }
  const pdfImgFormat = (d) => (/^data:image\/png/i.test(d) ? 'PNG' : 'JPEG');

  const PDF_LAYER_ORDER = [
    'cover-page',

    // ?"??"? Modern Vastu Section (same as sidebar) ?"??"?
    'floor-plan',
    'grid-layout',
    'center-analysis',
    'mahavastu-chakra',
    'shakti-chakra',
    'floor-plan-vpm',
    'compass-8',
    'Bar-chart-8-table',
    'Bar-chart-8-chart',
    'compass-16',
    'Bar-chart-16-table',
    'Bar-chart-16-chart',
    'compass-24',
    'compass-32',
    'Bar-chart-32-table',
    'Bar-chart-32-chart',
    'compass-45-2',
    'compass-45-2-table',
    'compass-45-2-chart',
    'coloured-zone',
    'elemental-5-zone',
    'elemental-5-chart',
    'marmsthan-analysis',
    'marmsthan-advance',
    'vastu-numbers-81-numbers',
    'vastu-numbers-64',
    'grid-3x3',
    'entrance-analysis-report',
    'space-analysis-report',
    'vastu-devta-guide-report',
    // These 7 were toggle-able in the sidebar and drew fine on their own once
    // the export actually ran (each has its own dedicated block below), but
    // were never added to this list — so the "enabledLayers.length === 0"
    // gate above always missed them, and toggling ONLY one of these on (e.g.
    // just Astro Vastu) meant the PDF button was blocked with a false
    // "Please select at least one layer to export" alert.
    'navgrah-shanti-report',
    'feng-shui-report',
    'helix-report',
    'zone-analysis-report',
    'numero-vastu-report',
    'astro-vastu-report',
    'cut-zone-report',
    'extension-zone-report',
    'png-image-1',
    'png-image-2',
    '32-Entrance-Analysis-Compass',

    // ?"??"? Vedic Vastu Section (same as sidebar) ?"??"?
    'Vedic-Mahavastu-Chakra',
    '16-Directional-Domestic-Vastu',
    'Khanija-Dhatu',
    'Vedic-devata-Numbers-with-Names-81',
    'vastu-numbers-81-numbers-Vedic',
    'Direction-Diety-Planet',
    'Devata-Khanij',
    'Devata-Bhojan',
    'Devata-Bhojan-aadi',
    'Devata-chinha',
    'Devata-chinha-aadi',
    'Muhurat-vichar',
    'Seharumkh-achintya-vibhav',
    'Disha-Gandha',
    'Devata-Nighat',
    'Nighantu',
    'compass-45',
    'compass-45-table',
    'compass-45-chart',
    '4-state-image',
    'surya-chandra-bhag',
    'Vedic-zones-32',
    'Vedic-numbers-64',
    'Vedic-64-padvinyash',
    'Vedic-81-padvinyash',
    'Vedic-elemental-zone',
    'Vedic-planet-zoning',

    // ?"??"? Medical Vastu Section (same as sidebar) ?"??"?
    'tri-dosh-Vedic',
    'tri-dosh-modern',
    'tri-guna-Vedic',
    'tri-guna-modern',
    'body-part-division',

    // ?"??"? Others Section (same as sidebar) ?"??"?
    'MANDAL',
    'RATNA-STHAPNA',
    'nakshatra-vastu',
    'Devata-Nakshatra-Vastu',

    // ?"??"? Planning Guide Section (same as sidebar) ?"??"?
    'house-bank',
    'house-clinic',
    'house-electronic-shop',
    'house-factory',
    'house-hotel',
    'house-marriage-hall',
    'house-multiplex',
    'house-office-cabin',
    'house-petrol-pump',
    'house-retail-outlets',
    'house-school-college',
    'house-space-analysis-compass',
    'house-temple',
  ];

    const enabledLayers = PDF_LAYER_ORDER.filter(id => exportLayers[id]?.enabled);
    const medicalVastuLayers = ['tri-dosh-Vedic', 'tri-dosh-modern', 'tri-guna-Vedic', 'tri-guna-modern', 'body-part-division'];


    if (enabledLayers.length === 0) {
      alert('Please select at least one layer to export');
      return;
    }

    const nonReportLayers = enabledLayers.filter(layerId =>
      !['entrance-analysis-report', 'space-analysis-report', 'vastu-devta-guide-report', 'navgrah-shanti-report', 'feng-shui-report', 'helix-report', 'zone-analysis-report',
        'numero-vastu-report', 'astro-vastu-report', 'cut-zone-report', 'extension-zone-report', 'cover-page'].includes(layerId)
    );

    // ?o. UPDATED: RATNA-STHAPNA table count bhi include kiya
    const DevataTablesCount =
      (exportLayers['compass-45']?.enabled ? 1 : 0) +
      (exportLayers['compass-45-2']?.enabled ? 1 : 0) +
      (exportLayers['RATNA-STHAPNA']?.enabled ? 1 : 0);

    const Vedic_LAYER_IDS_FOR_COUNT = [
      'Vedic-Mahavastu-Chakra', '16-Directional-Domestic-Vastu', 'Khanija-Dhatu',
      'Vedic-devata-Numbers-with-Names-81', 'vastu-numbers-81-numbers-Vedic',
      'Direction-Diety-Planet', 'Devata-Khanij', 'Devata-Bhojan', 'Devata-Bhojan-aadi',
      'Devata-chinha', 'Devata-chinha-aadi', 'Muhurat-vichar', 'Seharumkh-achintya-vibhav',
      'Disha-Gandha', 'Devata-Nighat', 'Nighantu', 'compass-45', 'compass-45-table',
      'compass-45-chart', '4-state-image', 'surya-chandra-bhag',
      ...VEDIC_GENERATIVE_IDS,
    ];
    const MODERN_LAYER_IDS_FOR_COUNT = [
      'floor-plan', 'grid-layout', 'center-analysis', 'mahavastu-chakra', 'shakti-chakra',
      'floor-plan-vpm', 'compass-8', 'Bar-chart-8-table', 'Bar-chart-8-chart',
      'compass-16', 'Bar-chart-16-table', 'Bar-chart-16-chart',
      'compass-24', 'surya-chandra-bhag', '4-state-image',
      'compass-32', 'Bar-chart-32-table', 'Bar-chart-32-chart',
      'compass-45-2', 'compass-45-2-table', 'compass-45-2-chart',
      'coloured-zone', 'elemental-5-zone', 'elemental-5-chart',
      'marmsthan-analysis', 'marmsthan-advance',
      'vastu-numbers-81-numbers', 'vastu-numbers-64', 'grid-3x3',
      'entrance-analysis-report', 'space-analysis-report', 'vastu-devta-guide-report',
      'navgrah-shanti-report', 'feng-shui-report', 'zone-analysis-report',
      '32-Entrance-Analysis-Compass', 'cut-zone-report', 'extension-zone-report',
    ];
    const hasVedicEnabledForCount = Vedic_LAYER_IDS_FOR_COUNT.some(id => exportLayers[id]?.enabled);
    const hasModernEnabledForCount = MODERN_LAYER_IDS_FOR_COUNT.some(id => exportLayers[id]?.enabled);

    const MEDICAL_IDS_COUNT = ['tri-dosh-Vedic', 'tri-dosh-modern', 'tri-guna-Vedic', 'tri-guna-modern', 'body-part-division'];
    const OTHERS_IDS_COUNT = ['MANDAL', 'RATNA-STHAPNA', 'nakshatra-vastu', 'Devata-Nakshatra-Vastu', 'helix-report', 'numero-vastu-report', 'astro-vastu-report'];
    const HOUSE_PLANNING_IDS_COUNT = ['house-bank', 'house-clinic', 'house-electronic-shop', 'house-factory', 'house-hotel', 'house-marriage-hall', 'house-multiplex', 'house-office-cabin', 'house-petrol-pump', 'house-retail-outlets', 'house-school-college', 'house-space-analysis-compass', 'house-temple'];
    const hasMedicalEnabledForCount = MEDICAL_IDS_COUNT.some(id => exportLayers[id]?.enabled);
    const hasOthersEnabledForCount = OTHERS_IDS_COUNT.some(id => exportLayers[id]?.enabled);
    const hasHousePlanningEnabledForCount = HOUSE_PLANNING_IDS_COUNT.some(id => exportLayers[id]?.enabled);

    // Cover Index is its own toggle. Projects saved before it existed have no
    // 'cover-index' key, so it follows Cover Front until the user flips it.
    const coverIndexEnabled = (exportLayers['cover-index']?.enabled ?? exportLayers['cover-page']?.enabled) || false;
    // Report Index layout (mm) — shared by addCombinedIndexPages and the estimate below.
    const INDEX_START_Y = 34, INDEX_BOTTOM_Y = 275, INDEX_ROW_H = 10, INDEX_HEAD_H = 14, INDEX_GAP = 6;
    const indexCategories = [];
    const pushIndexCategory = (name, ids) => {
      const names = ids.filter(id => exportLayers[id]?.enabled).map(id => getLayerName(id));
      if (names.length) indexCategories.push({ name, names });
    };
    pushIndexCategory('Modern Vastu Report', MODERN_LAYER_IDS_FOR_COUNT);
    pushIndexCategory('Vedic Vastu Report', Vedic_LAYER_IDS_FOR_COUNT);
    pushIndexCategory('Medical Vastu Report', MEDICAL_IDS_COUNT);
    pushIndexCategory('Others Report', OTHERS_IDS_COUNT);
    pushIndexCategory('Planning Guide Report', HOUSE_PLANNING_IDS_COUNT);
    const countIndexPages = (cats) => {
      if (!cats.length) return 0;
      let pages = 1, y = INDEX_START_Y;
      cats.forEach((cat, ci) => {
        if (y + INDEX_HEAD_H + INDEX_ROW_H > INDEX_BOTTOM_Y) { pages++; y = INDEX_START_Y; }
        else if (ci > 0) y += INDEX_GAP;
        y += INDEX_HEAD_H;
        cat.names.forEach(() => {
          if (y + INDEX_ROW_H > INDEX_BOTTOM_Y) { pages++; y = INDEX_START_Y + INDEX_HEAD_H; }
          y += INDEX_ROW_H;
        });
      });
      return pages;
    };
    const coverPageImagesCount = (exportLayers['cover-page']?.enabled ? 1 : 0)
      + (coverIndexEnabled ? countIndexPages(indexCategories) : 0);

    const devtaGuideRawData = (() => { try { const s = localStorage.getItem(vastuDevtaGuideStorageKey); return s ? JSON.parse(s) : []; } catch { return []; } })();
    const devtaGuideEnabledCount = exportLayers['vastu-devta-guide-report']?.enabled ? (devtaGuideRawData.filter(d => d.enabled).length || 0) : 0;

    const navgrahRawData = (() => { try { const s = localStorage.getItem(navgrahShantiStorageKey); return s ? JSON.parse(s) : []; } catch { return []; } })();
    const navgrahEnabledCount = exportLayers['navgrah-shanti-report']?.enabled ? (navgrahRawData.filter(d => d.enabled).length || 0) : 0;

    const fengShuiRawData = exportLayers['feng-shui-report']?.enabled ? loadReportList(fengShuiStorageKey, FENG_SHUI_DATA, buildFengShuiHtml, selectedLanguage) : [];
    // Pre-calculate pages using packing simulation (200px=53mm image, dynamic symbols per page)
    const fengShuiEnabledCount = (() => {
      if (!exportLayers['feng-shui-report']?.enabled) return 0;
      const areas = fengShuiRawData.filter(d => d.enabled);
      if (!areas.length) return 0;
      const PAGE_AVAIL = 246;   // mm available per page (297 - 26top - 22bottom - 3sub)
      const IMG_MM     = 53;    // 200px ≈ 53mm
      const LINE_H     = 6;
      const TITLE_H    = 13;    // name + underline + gaps
      const SYM_GAP    = 10;    // separator between symbols on same page
      const CHARS_LINE = 60;    // rough chars per line at 8.5pt across 182mm
      let curY = 0, pages = 1;
      for (const area of areas) {
        const plain = (area.richHtml || area.description || '').replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
        const descH = Math.max(1, Math.ceil(plain.length / CHARS_LINE)) * LINE_H + 4;
        const symH  = TITLE_H + IMG_MM + 5 + descH;
        if (curY > 0 && curY + SYM_GAP + symH > PAGE_AVAIL) { pages++; curY = 0; }
        else if (curY > 0) curY += SYM_GAP;
        curY += symH;
      }
      return pages;
    })();

    // Helix page count — mirrors the flow in the Helix PDF block (name + images
    // kept together, text flows line-by-line), estimated from text length.
    const helixEnabledCount = (() => {
      if (!exportLayers['helix-report']?.enabled) return 0;
      let list = [];
      try { const s = localStorage.getItem(helixStorageKey); list = s ? JSON.parse(s) : []; } catch { list = []; }
      const on = list.filter(d => d.enabled && HELIX_DATA.some(h => h.id === d.id));
      if (!on.length) return 0;
      const PAGE_AVAIL = 246, IMG_H = 52, GAP = 10, LINE_H = 7.5, CHARS_LINE = 70;
      let curY = 0, pages = 1;
      for (const it of on) {
        const base = HELIX_DATA.find(h => h.id === it.id) || {};
        const plain = (it.richHtml || '').replace(/<\/(p|li|h[1-3])>/gi, '\n').replace(/<[^>]+>/g, '').trim();
        const textH = plain.split('\n').reduce((a, ln) => a + Math.max(1, Math.ceil(ln.length / CHARS_LINE)) * LINE_H + 2, 0);
        const headH = 14 + ((base.images || []).length ? IMG_H + 7 : 0);
        if (curY > 0 && curY + GAP + headH + 20 > PAGE_AVAIL) { pages++; curY = 0; } else if (curY > 0) curY += GAP;
        curY += headH;
        let remain = textH;
        while (curY + remain > PAGE_AVAIL) { remain -= (PAGE_AVAIL - curY); pages++; curY = 0; }
        curY += remain;
      }
      return pages;
    })();

    // Pre-calculate entrance analysis page count using packing simulation
    const entranceAnalysisEnabledCount = (() => {
      if (!exportLayers['entrance-analysis-report']?.enabled) return 0;
      try {
        const raw = localStorage.getItem(entranceAnalysisStorageKey);
        const items = raw ? JSON.parse(raw).filter(d => d.enabled !== false) : [];
        if (!items.length) return 0;
        const PAGE_AVAIL = 246;
        const LINE_H     = 6;
        const CHARS_LINE = 60;
        const EITEM_GAP  = 10;
        let curY = 0, pages = 1;
        let lastEntrName = null;
        for (const item of items) {
          const entrName = item.customName || `Entrance ${item.entranceNumber || ''}`;
          const showLabel = entrName !== lastEntrName;
          const plain = (isStillDefaultEntranceHtml(item, item.richHtml) ? buildEntranceHtml(item, selectedLanguage) : item.richHtml).replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
          const sectH = Math.max(1, Math.ceil(plain.length / CHARS_LINE)) * LINE_H + 4;
          const dirH  = item.direction ? 9 : 0;
          const itemH = (showLabel ? 14 : 0) + dirH + sectH;
          if (curY > 0 && curY + EITEM_GAP + itemH > PAGE_AVAIL) { pages++; curY = 0; lastEntrName = null; }
          else if (curY > 0) curY += EITEM_GAP;
          if (curY === 0 && showLabel) { curY += 14; lastEntrName = entrName; }
          else if (showLabel) { curY += 14; lastEntrName = entrName; }
          if (item.direction) curY += 9;
          curY += sectH;
        }
        return pages;
      } catch { return 1; }
    })();

    // Pre-calculate space analysis page count using packing simulation
    const spaceAnalysisEnabledCount = (() => {
      if (!exportLayers['space-analysis-report']?.enabled) return 0;
      try {
        const raw = localStorage.getItem(spaceAnalysisStorageKey);
        const items = raw ? JSON.parse(raw) : [];
        if (!items.length) return 0;
        const PAGE_AVAIL = 246;
        const LINE_H     = 4.5;
        const CHARS_LINE = 65;
        const ITEM_GAP   = 4;
        const GH         = 10; // group header height
        let curY = 0, pages = 1;
        let lastGroup = null;
        for (const item of items) {
          const groupKey = item.customName || item.placementName || '';
          if (groupKey !== lastGroup) {
            if (curY > 0 && curY + ITEM_GAP + GH > PAGE_AVAIL) { pages++; curY = 0; }
            else if (curY > 0) curY += ITEM_GAP;
            curY += GH;
            lastGroup = groupKey;
          }
          // (A slim-saved item has no stored text while it is still the default — rebuild it for the count.)
          const plain = (item.richHtml || buildSpaceItemHtml(item, selectedLanguage) || '').replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
          // Skip first h2 (space name) from count estimate — approx 80% of text is content
          const contentLen = Math.max(0, plain.length - (groupKey.length + 2));
          const sectH = Math.max(1, Math.ceil(contentLen / CHARS_LINE)) * LINE_H + (item.zone ? LINE_H : 0) + 6;
          if (curY > 0 && curY + ITEM_GAP + sectH > PAGE_AVAIL) { pages++; curY = 0; lastGroup = null; }
          else if (curY > 0) curY += ITEM_GAP;
          curY += sectH;
        }
        return pages;
      } catch { return 1; }
    })();

    const zoneAnalysisRawData = exportLayers['zone-analysis-report']?.enabled ? loadReportList(zoneAnalysisStorageKey, ZONE_ANALYSIS_ITEMS, buildZoneAnalysisHtml, selectedLanguage) : [];
    // Pre-calculate pages using packing simulation (same logic as Feng Shui)
    const zoneAnalysisEnabledCount = (() => {
      if (!exportLayers['zone-analysis-report']?.enabled) return 0;
      const items = zoneAnalysisRawData.filter(d => d.enabled);
      if (!items.length) return 0;
      // Mirrors the export's continuous-flow layout: header block (zone label +
      // name + image) stays together, text then flows line-by-line across pages.
      const PAGE_AVAIL = 246;
      const IMG_MM     = 40;  // 150px ≈ 40mm
      const LINE_H     = 9.6;
      const ITEM_GAP   = 10;
      const CHARS_LINE = 65;
      let used = 0, pages = 1, lastZone = null;
      const flow = (h) => {
        let rem = h;
        while (used + rem > PAGE_AVAIL) { rem -= (PAGE_AVAIL - used); pages++; used = 0; }
        used += rem;
      };
      for (const item of items) {
        const html = item.richHtml || item.description || '';
        const plain = html.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
        const sectionCount = Math.max(1, (html.match(/<h2/gi) || []).length);
        const headerH = (item.zoneName !== lastZone ? 14 : 0) + 9 + IMG_MM + 5;
        lastZone = item.zoneName;
        if (used > 0) {
          if (used + ITEM_GAP + headerH > PAGE_AVAIL) { pages++; used = 0; }
          else used += ITEM_GAP;
        }
        used += headerH;
        const textLines = Math.ceil(plain.length / CHARS_LINE) + sectionCount * 0.5;
        flow(textLines * LINE_H + sectionCount * 11);
      }
      return pages;
    })();

    // Numero Vastu — one page per person + 1 combined strength page
    const numeroVastuEnabledCount = (() => {
      if (!exportLayers['numero-vastu-report']?.enabled) return 0;
      try {
        const raw = localStorage.getItem(numeroVastuStorageKey) || localStorage.getItem(vastuPersonsStorageKey);
        const persons = raw ? JSON.parse(raw) : [];
        const filled = persons.filter(p => p.name || p.dob).length;
        return filled > 0 ? filled + 1 : 0; // +1 for combined page
      } catch { return 0; }
    })();

    // Each Tri-Dosh/Tri-Guna layer adds one extra page for its own pie chart
    // (see addTriChartPage) — only counted when that layer is enabled AND
    // already has chart data saved (matches addTriChartPage's own "nothing to
    // draw yet" skip, so the "Page X of Y" count stays accurate either way).
    const triChartPagesCount = ['tri-dosh-Vedic', 'tri-guna-Vedic', 'tri-dosh-modern', 'tri-guna-modern']
      .filter(id => exportLayers[id]?.enabled && (savedLayerConfigs[id]?.triChartData?.slices || []).some(s => (s.value || 0) > 0))
      .length;

    const totalPages = nonReportLayers.length +
      DevataTablesCount +
      entranceAnalysisEnabledCount +
      spaceAnalysisEnabledCount +
      numeroVastuEnabledCount +
      devtaGuideEnabledCount +
      navgrahEnabledCount +
      fengShuiEnabledCount +
      helixEnabledCount +
      zoneAnalysisEnabledCount +
      coverPageImagesCount +
      triChartPagesCount;

    const estimatedDuration = totalPages * 500;

    setIsGeneratingPDF(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    let entranceReportData = [];
    let spaceReportData = [];
    try {
      const entranceRaw = localStorage.getItem(entranceAnalysisStorageKey);
      const spaceRaw = localStorage.getItem(spaceAnalysisStorageKey);

      if (entranceRaw) entranceReportData = JSON.parse(entranceRaw);
      if (spaceRaw) spaceReportData = JSON.parse(spaceRaw);

    } catch (parseError) {
    }


    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    // jsPDF 3 throws "Type of text must be string or Array" for a numeric text argument, which silently aborted
    // whole reports (e.g. the Numero grid numbers). Turn numbers into text for EVERY pdf.text() call.
    const _textAsIs = pdf.text.bind(pdf);
    pdf.text = function textNumbersToStrings(t, ...rest) { return _textAsIs(typeof t === 'number' ? String(t) : t, ...rest); };
    // Pictures that were shrunk to JPEG are still declared 'PNG' at many addImage call sites: the declared format follows the data.
    const _addImageAsIs = pdf.addImage.bind(pdf);
    pdf.addImage = function formatFollowsData(data, fmt, ...rest) {
      if (typeof data === 'string' && /^data:image\/jpe?g/i.test(data)) fmt = 'JPEG';
      return _addImageAsIs(data, fmt, ...rest);
    };
    // Word export: every pdf.text() is recorded (and not drawn) so it can go into the .docx as real, editable
    // text on top of the page picture. The normal PDF export leaves this null and draws as always.
    const wordRecorder = outputKind === 'word' ? createWordTextRecorder(pdf) : null;
    const a4Width = 210;
    const a4Height = 297;
    let pageNumber = 1;
    let isFirstPage = true;

    // ── Indic-script-safe text rendering ───────────────────────────────
    // jsPDF's built-in fonts have no Indic glyphs and no shaping engine
    // (matras/conjuncts render out of order — e.g. a mantra prints as garbage
    // symbols), so any line containing an Indic-script character is
    // rasterised on an offscreen canvas — using the browser's own text
    // shaping — and embedded as an image. Pure-Latin text still goes through
    // pdf.text() as before (crisp vector, no size cost). Shared across every
    // report section in this export (Devta Guide, Navgrah Shanti, Cut &
    // Extension Zones, etc).
    //
    // BUG FIX: this only matched the Devanagari block (Hindi/Marathi), so
    // Gujarati/Bengali/Kannada/Tamil/Telugu text fell through to jsPDF's
    // Latin-only 'helvetica' font — which has no glyphs for those scripts —
    // and printed as garbage symbols (client-reported: Cut Zones export in
    // Gujarati showed unreadable characters). Now covers all 6 non-Latin
    // scripts this app supports a language for (Devanagari for hi/mr,
    // Gujarati, Bengali, Tamil, Kannada, Telugu).
    const DEVANAGARI_RE = /[ऀ-ॿ઀-૿ঀ-৿஀-௿ఀ-౿ಀ-೿]/;
    const PX_PER_MM = 96 / 25.4;
    const measureCtxPdf = document.createElement('canvas').getContext('2d');
    // fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' (same vocabulary as jsPDF.setFont)
    // "Nirmala UI" (Windows' pan-Indic UI font) covers all 6 scripts below on
    // its own, but naming each Noto Sans script variant explicitly too keeps
    // this working on Mac/Linux, where "Nirmala UI" doesn't exist and a bare
    // "Noto Sans" typically only covers Latin/Cyrillic/Greek — the browser
    // picks whichever named family is actually installed for that character.
    const hindiFontCssPdf = (fontSizePt, fontStyle) => {
      const isBold   = fontStyle === 'bold' || fontStyle === 'bolditalic';
      const isItalic = fontStyle === 'italic' || fontStyle === 'bolditalic';
      return `${isItalic ? 'italic ' : ''}${isBold ? '700' : '400'} ${(fontSizePt * 96 / 72).toFixed(2)}px "Noto Sans", "Noto Sans Devanagari", "Noto Sans Gujarati", "Noto Sans Bengali", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", "Nirmala UI", Arial, sans-serif`;
    };

    const wrapMixedTextPdf = (text, fontSizePt, fontStyle, maxWidthMM) => {
      measureCtxPdf.font = hindiFontCssPdf(fontSizePt, fontStyle);
      const maxWidthPx = maxWidthMM * PX_PER_MM;
      const words = text.split(' ');
      const lines = [];
      let cur = '';
      words.forEach(word => {
        const test = cur ? cur + ' ' + word : word;
        if (cur && measureCtxPdf.measureText(test).width > maxWidthPx) {
          lines.push(cur);
          cur = word;
        } else {
          cur = test;
        }
      });
      if (cur) lines.push(cur);
      return lines;
    };

    // Wraps text to maxWidthMM: canvas metrics for Devanagari lines, jsPDF's
    // own splitTextToSize (unchanged behaviour) for plain Latin text.
    const wrapAnyPdf = (pdfInstance, text, fontSizePt, fontStyle, maxWidthMM) => {
      if (DEVANAGARI_RE.test(text)) return wrapMixedTextPdf(text, fontSizePt, fontStyle, maxWidthMM);
      pdfInstance.setFont('helvetica', fontStyle || 'normal');
      pdfInstance.setFontSize(fontSizePt);
      return pdfInstance.splitTextToSize(text, maxWidthMM);
    };

    // Draws one line at (x, yBaseline) in mm, matching pdf.text()'s baseline
    // positioning, transparently switching to an image for Devanagari lines.
    // align: 'left' (default) or 'center' — matches jsPDF's text() align option.
    const drawLineSmartPdf = (pdfInstance, line, x, yBaseline, fontSizePt, fontStyle, rgb, align) => {
      line = String(line ?? '');   // jsPDF throws on a numeric text argument — always hand it a string
      if (!DEVANAGARI_RE.test(line)) {
        pdfInstance.setFont('helvetica', fontStyle || 'normal');
        pdfInstance.setFontSize(fontSizePt);
        pdfInstance.setTextColor(...rgb);
        pdfInstance.text(line, x, yBaseline, align === 'center' ? { align: 'center' } : undefined);
        return;
      }
      const fontCss = hindiFontCssPdf(fontSizePt, fontStyle);
      measureCtxPdf.font = fontCss;
      const scale = 3; // supersample for crisp print output
      const widthPx  = Math.ceil(measureCtxPdf.measureText(line).width) + 4;
      const heightPx = Math.ceil(fontSizePt * 96 / 72 * 1.4);
      const canvas = document.createElement('canvas');
      canvas.width = widthPx * scale;
      canvas.height = heightPx * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.font = fontCss;
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = `rgb(${rgb.join(',')})`;
      ctx.fillText(line, 2, heightPx * 0.75);
      const widthMM  = widthPx  / PX_PER_MM;
      const heightMM = heightPx / PX_PER_MM;
      if (align === 'center') x -= widthMM / 2;
      pdfInstance.addImage(canvas.toDataURL('image/png'), 'PNG', x, yBaseline - heightMM * 0.75, widthMM, heightMM, undefined, 'FAST');
    };

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // HELPER: Footer
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // Shared decorative palette — matches the ornate border/heading theme
    // requested for every exported page (cover page excepted): warm brown
    // border with a light tint hairline, used by both addPageFrame (top)
    // and addFooter (bottom) so the two ends of the page match.
    const REPORT_THEME_ACCENT = [168, 94, 37];   // #a85e25
    const REPORT_THEME_ACCENT_LIGHT = [224, 185, 148]; // #e0b994
    const REPORT_THEME_TITLE = [124, 66, 28];    // #7c421c
    const REPORT_THEME_TINT = [248, 236, 224];   // #f8ece0 — soft highlight fill
    const REPORT_THEME_QUOTE = [90, 53, 31];     // #5a351f

    const addFooter = (pdfInstance, pageNum, totalPagesParam, layerName) => {
      // frameBottom keeps the footer text a couple mm clear of the border
      // (which now sits right at the page edge, a4Height). Spaced out
      // (quote / divider / page number each a few mm apart) instead of
      // packed tight — plenty of clearance above every report section's
      // own bottom margin.
      const frameBottom = a4Height - 2;

      // Footer line: the user's own text if they set one, else their profile details (company | website),
      // else the old default quote. Shrunk / trimmed so it always fits between the page margins.
      let footerLine = (reportFooterText || '').trim() || profileFooterDefault || 'May Vastu bring peace, happiness, prosperity always';
      let footerSize = 8;
      const footerMaxW = a4Width - 30;
      pdfInstance.setFont('helvetica', 'italic');
      pdfInstance.setFontSize(footerSize);
      while (footerSize > 6 && pdfInstance.getTextWidth(footerLine) > footerMaxW) { footerSize -= 0.5; pdfInstance.setFontSize(footerSize); }
      if (pdfInstance.getTextWidth(footerLine) > footerMaxW) {
        while (footerLine.length > 4 && pdfInstance.getTextWidth(footerLine + '…') > footerMaxW) footerLine = footerLine.slice(0, -1);
        footerLine += '…';
      }
      drawLineSmartPdf(pdfInstance, footerLine, a4Width / 2, frameBottom - 9, footerSize, 'italic', REPORT_THEME_QUOTE, 'center');

      // Small divider: line — dot — line, centred
      const dividerY = frameBottom - 5;
      pdfInstance.setDrawColor(...REPORT_THEME_ACCENT);
      pdfInstance.setLineWidth(0.25);
      pdfInstance.line(a4Width / 2 - 20, dividerY, a4Width / 2 - 3, dividerY);
      pdfInstance.line(a4Width / 2 + 3, dividerY, a4Width / 2 + 20, dividerY);
      pdfInstance.setFillColor(...REPORT_THEME_ACCENT);
      pdfInstance.circle(a4Width / 2, dividerY, 0.8, 'F');

      // Page number
      pdfInstance.setFont('helvetica', 'normal');
      pdfInstance.setFontSize(6);
      pdfInstance.setTextColor(...REPORT_THEME_TITLE);
      pdfInstance.text(`Page ${pageNum}`, a4Width / 2, frameBottom - 1, { align: 'center' });
    };

    const getUserPoweredBy = () => {
      try {
        const u = JSON.parse(localStorage.getItem('user_data') || 'null');
        return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
      } catch { return 'Powered by VastuAnalyzer.com'; }
    };

    // Standard type scale for every piece of text in the exported PDF, per
    // the "Title / Subtitle / Description" spec: 25 / 20 / 16 pt. Title is
    // always in the report theme colour; Subtitle and Description are
    // always black. Any other kind of text picks whichever of these three
    // sizes matches its role instead of inventing a new size.
    const TYPE_TITLE_SIZE = 25;
    const TYPE_SUBTITLE_SIZE = 20;
    const TYPE_DESCRIPTION_SIZE = 16;
    const TYPE_BLACK = [0, 0, 0];

    // subtitleText is optional — most simple image-layer reports only need a
    // Title, but reports built from repeated HTML sections (Entrance
    // Analysis, Navgrah Shanti, Feng Shui, Zone Analysis, ...) pass one.
    // Returns the Y (mm) below which it's safe for that page's own body
    // content to start — reports with a subtitle get a taller header, so
    // they must use this return value as their PAGE_TOP instead of a
    // hardcoded constant.
    const addPageFrame = (pdfInstance, headingText, subtitleText) => {
      // ── Ornate frame: accent-coloured outer border + light inner hairline.
      // Outer border sits exactly at the page corner (0,0) — no gap between
      // the page edge and the border any more. ──
      pdfInstance.setDrawColor(...REPORT_THEME_ACCENT);
      pdfInstance.setLineWidth(1);
      pdfInstance.rect(0, 0, a4Width, a4Height, 'S');
      pdfInstance.setDrawColor(...REPORT_THEME_ACCENT_LIGHT);
      pdfInstance.setLineWidth(0.2);
      pdfInstance.rect(2, 2, a4Width - 4, a4Height - 4, 'S');

      // ── Corner brackets (L-shape, one per corner) ──
      const armLen = 12;
      pdfInstance.setDrawColor(...REPORT_THEME_ACCENT);
      pdfInstance.setLineWidth(0.7);
      pdfInstance.line(0, 0, armLen, 0);
      pdfInstance.line(0, 0, 0, armLen);
      pdfInstance.line(a4Width, 0, a4Width - armLen, 0);
      pdfInstance.line(a4Width, 0, a4Width, armLen);
      pdfInstance.line(0, a4Height, armLen, a4Height);
      pdfInstance.line(0, a4Height, 0, a4Height - armLen);
      pdfInstance.line(a4Width, a4Height, a4Width - armLen, a4Height);
      pdfInstance.line(a4Width, a4Height, a4Width, a4Height - armLen);

      // ── Title — centred, uppercase, report name in the accent colour.
      // Auto-shrinks so long report names still fit inside the frame.
      // Devanagari-safe: if the title/subtitle ever carries Hindi text (a
      // future translated layer name, etc.), route it through
      // drawLineSmartPdf instead of a raw pdfInstance.text() call — every
      // caller today passes plain-English names, but this closes off that
      // whole class of "table-cell mojibake" bug from ever recurring here. ──
      const headingUpper = String(headingText || '').toUpperCase();
      let titleSize = TYPE_TITLE_SIZE;
      const maxTitleW = a4Width - 40;
      if (DEVANAGARI_RE.test(headingUpper)) {
        while (titleSize > 12) {
          measureCtxPdf.font = hindiFontCssPdf(titleSize, 'bold');
          if (measureCtxPdf.measureText(headingUpper).width / PX_PER_MM <= maxTitleW) break;
          titleSize -= 1;
        }
      } else {
        pdfInstance.setFont('helvetica', 'bold');
        pdfInstance.setFontSize(titleSize);
        while (pdfInstance.getTextWidth(headingUpper) > maxTitleW && titleSize > 12) {
          titleSize -= 1;
          pdfInstance.setFontSize(titleSize);
        }
      }
      const titleY = 11;
      drawLineSmartPdf(pdfInstance, headingUpper, a4Width / 2, titleY, titleSize, 'bold', REPORT_THEME_TITLE, 'center');

      // ── Subtitle (optional) — centred, black, sits right under the title. ──
      let afterTitleY = titleY + 6; // no-subtitle case: tight gap before the divider
      if (subtitleText) {
        const subtitleY = titleY + 9;
        drawLineSmartPdf(pdfInstance, String(subtitleText), a4Width / 2, subtitleY, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
        afterTitleY = subtitleY + 8;
      }

      // ── Divider: line — dot — Om — dot — line (Om sized 2x, so it needs
      // a little extra baseline offset below the line/dots to stay centred). ──
      const dividerY = afterTitleY;
      pdfInstance.setDrawColor(...REPORT_THEME_ACCENT);
      pdfInstance.setLineWidth(0.25);
      pdfInstance.line(a4Width / 2 - 26, dividerY, a4Width / 2 - 6, dividerY);
      pdfInstance.line(a4Width / 2 + 6, dividerY, a4Width / 2 + 26, dividerY);
      pdfInstance.setFillColor(...REPORT_THEME_ACCENT);
      pdfInstance.circle(a4Width / 2 - 6, dividerY, 0.6, 'F');
      pdfInstance.circle(a4Width / 2 + 6, dividerY, 0.6, 'F');
      drawLineSmartPdf(pdfInstance, 'ॐ', a4Width / 2, dividerY + 2, 16, 'normal', REPORT_THEME_ACCENT, 'center');

      // Safe Y for this page's own body content to start beneath.
      return dividerY + 6;
    };

    const addMainCoverPage = (pdfInstance) => {
      // Read client/consultant data from the saved cover-page layer config
      const coverConfig = savedLayerConfigs['cover-page'] || {};
      const cliInfo  = coverConfig.clientInfo     || {};
      const conInfo  = coverConfig.consultantInfo || {};

      // Full-bleed background artwork (client-supplied template, already at
      // A4 proportions — see cover-page-template.png / resolvedCoverTemplateForPdf
      // resolved up front at the top of handleExportPDF). Replaces the old
      // black/white branded design entirely.
      if (resolvedCoverTemplateForPdf) {
        try {
          pdfInstance.addImage(resolvedCoverTemplateForPdf, pdfImgFormat(resolvedCoverTemplateForPdf), 0, 0, a4Width, a4Height);
        } catch (e) {
          // Malformed/unsupported image data — page stays blank rather than crashing the export.
        }
      }

      // ── Field values, positioned over the template's own printed labels
      // ("Owner Name:", "Contact Number:", etc.). Y values were measured
      // directly off cover-page-template.png (pixel-row detection of each
      // label's baseline in the source image, converted to mm) — same
      // source of truth the live editor's positions come from. ──
      pdfInstance.setFont('helvetica', 'normal');
      pdfInstance.setFontSize(12.7);
      pdfInstance.setTextColor(0, 0, 0);
      const VALUE_X = 85;
      const drawValue = (val, y) => {
        if (!val) return;
        pdfInstance.text(String(val), VALUE_X, y);
      };

      // All 6 nudged ~0.8mm (3 canvas-px equivalent total) further down
      drawValue(cliInfo.name, 180.2);
      drawValue(cliInfo.phone, 186.0);
      drawValue(cliInfo.email, 192.0);
      drawValue(cliInfo.propertyType, 198.5);
      drawValue(cliInfo.address, 204.3);
      drawValue(cliInfo.dateOfVisit, 209.8);

      // Consultant Details' 4 fields nudged ~1.06mm (4 canvas-px equivalent) further down
      drawValue(conInfo.name, 224.9);
      drawValue(conInfo.phone, 230.9);
      drawValue(conInfo.email, 236.7);
      drawValue(conInfo.website, 242.8);

      // ── Consultant logo, over the template's own circular emblem — 5px up,
      // 6px right (canvas-equivalent) from its original centred position. ──
      if (resolvedLogoForPdf && resolvedLogoForPdf.startsWith('data:')) {
        try {
          const logoSize = 21;
          pdfInstance.addImage(resolvedLogoForPdf, 'PNG', a4Width / 2 - logoSize / 2 + 1.6, 250.7, logoSize, logoSize, undefined, 'FAST');
        } catch (e) {
          // Malformed/unsupported image data — the template's own emblem stays visible underneath.
        }
      }

      // ── Company name, under "Powered by" ──
      pdfInstance.setFont('helvetica', 'normal');
      pdfInstance.setFontSize(9);
      pdfInstance.setTextColor(58, 43, 26);
      pdfInstance.text(conInfo.companyName || 'VastuAnalyzer', a4Width / 2, 285.4, { align: 'center' });

      // No "Respected [Owner Name]" greeting here — that lives on Cover Back only.
    };

    // Closing "With Best Wishes" page — added as the very last page of the
    // export (see the addClosingPage() call right before pdf.save()). Reuses
    // the same client/consultant data captured on the cover page rather than
    // its own separate editable UI, since it's the same underlying values.
    const addClosingPage = (pdfInstance) => {
      const coverConfig = savedLayerConfigs['cover-page'] || {};
      const cliInfo  = coverConfig.clientInfo     || {};
      const conInfo  = coverConfig.consultantInfo || {};

      pdfInstance.addPage();

      if (resolvedClosingTemplateForPdf) {
        try {
          pdfInstance.addImage(resolvedClosingTemplateForPdf, pdfImgFormat(resolvedClosingTemplateForPdf), 0, 0, a4Width, a4Height);
        } catch (e) {
          // Malformed/unsupported image data — page stays blank rather than crashing the export.
        }
      }

      // ── "Respected [Owner Name]", in the gap between "WITH BEST WISHES" and "Thank you..." ──
      if (cliInfo.name) {
        pdfInstance.setFont('helvetica', 'bold');
        pdfInstance.setFontSize(24);
        pdfInstance.setTextColor(91, 50, 29);
        pdfInstance.text(`Respected ${cliInfo.name}`, a4Width / 2, 88.1, { align: 'center' });
      }

      // ── Field values, over "Email:" / "Website:" / "Contact:" — X and Y
      // measured directly off the template image's actual pixels (scanned
      // each label's dark-pixel bounding box, 1240×1754px @ 5.9048 px/mm),
      // not eyeballed. "Email:" is a shorter word than "Website:"/"Contact:",
      // so it needs its own (smaller) value X — a single shared X was
      // sitting close after the two long labels but leaving a visibly
      // wider gap after the short one. ──
      pdfInstance.setFont('helvetica', 'normal');
      pdfInstance.setFontSize(16);
      pdfInstance.setTextColor(0, 0, 0);
      const drawValue = (val, x, y) => {
        if (!val) return;
        pdfInstance.text(String(val), x, y);
      };
      drawValue(conInfo.email,   85.0, 219.4);
      drawValue(conInfo.website, 92.5, 229.0);
      drawValue(conInfo.phone,   92.0, 238.3);

      // ── Company name, under "Powered by" — theme accent colour and bold,
      // nudged back up a bit from the template's "Powered by" baseline
      // (≈250.3mm). ──
      pdfInstance.setFont('helvetica', 'bold');
      pdfInstance.setFontSize(15);
      pdfInstance.setTextColor(168, 94, 37);
      pdfInstance.text(conInfo.companyName || 'VastuAnalyzer', a4Width / 2, 259.6, { align: 'center' });

      // No addFooter() here — like the cover page, this is a fully custom
      // image page with its own "Powered by" already baked in; the generic
      // quote/divider footer would just clutter on top of it.
      pageNumber++;
    };

    // Category divider pages ("Modern Vastu Report", "Vedic Vastu Report",
    // etc. — the numbered list of what's included in that section) used to
    // draw their own plain black double-border + left-aligned title, out of
    // step with the ornate theme every other report page now uses. Reuse
    // the same addPageFrame/addFooter every other page gets instead.
    // A category with many enabled reports used to list every one of them on
    // this single page regardless of how far down it ran — the list kept
    // going, straight through the page's own footer (its quote/divider/page
    // number, drawn at a fixed spot near the bottom, doesn't move out of the
    // way for it). Now the list breaks to a fresh page (repeating the same
    // title/frame) once the next line would reach the footer's own zone.
    // Single running "Report Index": a section's heading follows right after the
    // previous section's last row on the same page, and a new page starts only
    // when the page is full (a section that spills over repeats its heading
    // with "(contd.)"). Layout constants are shared with the page-count
    // estimate (countIndexPages) so "Page X of Y" stays accurate.
    const addCombinedIndexPages = (pdfInstance, categories) => {
      const TITLE = 'Report Index';
      const setFont = (bold) => {
        pdfInstance.setFontSize(TYPE_DESCRIPTION_SIZE);
        pdfInstance.setFont('helvetica', bold ? 'bold' : 'normal');
        pdfInstance.setTextColor(...TYPE_BLACK);
      };
      let listY = addPageFrame(pdfInstance, TITLE) + 11;
      const newPage = () => {
        addFooter(pdfInstance, pageNumber, totalPages, TITLE);
        pageNumber++;
        pdfInstance.addPage();
        listY = addPageFrame(pdfInstance, TITLE) + 11;
      };
      const drawHeading = (text) => {
        setFont(true);
        pdfInstance.text(text, 20, listY);
        pdfInstance.setDrawColor(226, 232, 240);
        pdfInstance.setLineWidth(0.3);
        pdfInstance.line(20, listY + 2, a4Width - 20, listY + 2);
        listY += INDEX_HEAD_H;
      };
      categories.forEach((cat, ci) => {
        if (listY + INDEX_HEAD_H + INDEX_ROW_H > a4Height - 22) newPage();
        else if (ci > 0) listY += INDEX_GAP;
        drawHeading(cat.name);
        cat.names.forEach((name, idx) => {
          if (listY + INDEX_ROW_H > a4Height - 22) {
            newPage();
            drawHeading(cat.name);
          }
          setFont(false);
          pdfInstance.text((idx + 1) + '. ' + name, 20, listY);
          listY += INDEX_ROW_H;
        });
      });
      addFooter(pdfInstance, pageNumber, totalPages, TITLE);
      pageNumber++;
    };

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // HELPER: Devata Table (compass-45 / compass-45-2)
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    const addDevataTable = (compassType) => {
      pdf.addPage();
      addPageFrame(pdf, 'Devata Names');

      const Vedic_NAMES_PDF = [
        { srNo: 1, name: 'BRAHMA' }, { srNo: 2, name: 'BHUDHAR' }, { srNo: 3, name: 'ARYAMA' },
        { srNo: 4, name: 'VIVISWAN' }, { srNo: 5, name: 'MITRA' }, { srNo: 6, name: 'AAPAHA' },
        { srNo: 7, name: 'AAPAHA-VATSA' }, { srNo: 8, name: 'SAVITA' }, { srNo: 9, name: 'SAVITRA' },
        { srNo: 10, name: 'INDRA' }, { srNo: 11, name: 'JAYA' }, { srNo: 12, name: 'RUDRA' },
        { srNo: 13, name: 'RAJYA-KSHMA' }, { srNo: 14, name: 'SHIKHI' }, { srNo: 15, name: 'PARJANYA' },
        { srNo: 16, name: 'JAYANTI' }, { srNo: 17, name: 'MAHENDRI' }, { srNo: 18, name: 'SURYA' },
        { srNo: 19, name: 'SATYA' }, { srNo: 20, name: 'BHRISHA' }, { srNo: 21, name: 'ANTRIKSH' },
        { srNo: 22, name: 'ANIL' }, { srNo: 23, name: 'PUSHA' }, { srNo: 24, name: 'VITASTA' },
        { srNo: 25, name: 'GRIHSPATYA' }, { srNo: 26, name: 'YAMA' }, { srNo: 27, name: 'GANDHARVA' },
        { srNo: 28, name: 'BHRINGRAJ' }, { srNo: 29, name: 'MRIGAH' }, { srNo: 30, name: 'PITRA' },
        { srNo: 31, name: 'DAUWARIK' }, { srNo: 32, name: 'SUGREEV' }, { srNo: 33, name: 'PUUSHPDANT' },
        { srNo: 34, name: 'VARUN' }, { srNo: 35, name: 'ASUR' }, { srNo: 36, name: 'SHOSHA' },
        { srNo: 37, name: 'PAPYA-KSHMA' }, { srNo: 38, name: 'ROGA' }, { srNo: 39, name: 'AHIR' },
        { srNo: 40, name: 'MUKHYA' }, { srNo: 41, name: 'BHALLAT' }, { srNo: 42, name: 'SOMA' },
        { srNo: 43, name: 'BHUJAG' }, { srNo: 44, name: 'ADITI' }, { srNo: 45, name: 'DITI' },
      ];
      const MODERN_NAMES_PDF = [...Vedic_NAMES_PDF];
      const DevataNamesForPDF = compassType === 'Compass-45' ? Vedic_NAMES_PDF : MODERN_NAMES_PDF;
      const col1Data = DevataNamesForPDF.slice(0, 15);
      const col2Data = DevataNamesForPDF.slice(15, 30);
      const col3Data = DevataNamesForPDF.slice(30, 45);
      const tableData = [];
      for (let i = 0; i < 15; i++) {
        tableData.push([
          col1Data[i].srNo.toString(), col1Data[i].name,
          col2Data[i]?.srNo.toString() || '', col2Data[i]?.name || '',
          col3Data[i]?.srNo.toString() || '', col3Data[i]?.name || '',
        ]);
      }
      autoTable(pdf, {
        startY: 22,
        head: [['SR', 'Devata NAME', 'SR', 'Devata NAME', 'SR', 'Devata NAME']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2, textColor: [50, 50, 50], fillColor: [255, 255, 255], lineColor: [200, 200, 200], lineWidth: 0.5, overflow: 'linebreak', valign: 'middle', halign: 'left' },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9, halign: 'center', valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' }, 1: { cellWidth: 48, halign: 'left' },
          2: { cellWidth: 12, halign: 'center' }, 3: { cellWidth: 48, halign: 'left' },
          4: { cellWidth: 12, halign: 'center' }, 5: { cellWidth: 48, halign: 'left' },
        },
        margin: { left: 10, right: 10, top: 22, bottom: 22 },
        didDrawPage: () => {
          addFooter(pdf, pageNumber, totalPages, `Devata Names - ${compassType === 'Compass-45' ? 'Vedic' : 'Modern'}`);
        },
      });
      pageNumber++;
    };

    // Planning Guide: every report page (e.g. 'house-bank') automatically
    // gets its Vastu-guidelines text page added right after it — there is no
    // separate toggle for this, it always travels with the report it belongs to.
    const addHousePlanningNotesPage = (baseLayerId) => {
      const guidelines = HOUSE_PLANNING_NOTES[baseLayerId];
      if (!guidelines || guidelines.length === 0) return;

      pdf.addPage();
      const title = getLayerName(baseLayerId) + ' — Notes';
      let curY = addPageFrame(pdf, title) + 3;

      // Body list — Description tier, always black.
      const MARGIN = 14, CONTENT_WIDTH = a4Width - MARGIN * 2, LINE_H = 8.5;
      pdf.setFontSize(TYPE_DESCRIPTION_SIZE);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...TYPE_BLACK);

      guidelines.forEach((text, idx) => {
        const lines = pdf.splitTextToSize(`${idx + 1}. ${text}`, CONTENT_WIDTH);
        if (curY + lines.length * LINE_H > a4Height - 22) {
          addFooter(pdf, pageNumber, totalPages, title);
          pageNumber++;
          pdf.addPage();
          curY = addPageFrame(pdf, title) + 3;
          pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...TYPE_BLACK);
        }
        lines.forEach((line) => {
          pdf.text(line, MARGIN, curY);
          curY += LINE_H;
        });
        curY += 2;
      });

      addFooter(pdf, pageNumber, totalPages, title);
      pageNumber++;
    };


    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // HELPER: PDF Pentagon shape
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    const drawPDFPentagon = (pdfInstance, cx, cy, r, color) => {
      const pts = [];
      for (let i = 0; i < 5; i++) {
        const angle = (i * 72 - 90) * (Math.PI / 180);
        pts.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      }
      pdfInstance.setFillColor(...color);
      pdfInstance.setDrawColor(...color);
      pdfInstance.lines(
        pts.slice(1).map((p, i) => [p.x - pts[i].x, p.y - pts[i].y]),
        pts[0].x, pts[0].y, [1, 1], 'FD', true
      );
    };

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // HELPER: PDF Star shape
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    const drawPDFStar = (pdfInstance, cx, cy, outerR, innerR, color) => {
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const angle = (i * 36 - 90) * (Math.PI / 180);
        const r = i % 2 === 0 ? outerR : innerR;
        pts.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      }
      pdfInstance.setFillColor(...color);
      pdfInstance.setDrawColor(...color);
      pdfInstance.lines(
        pts.slice(1).map((p, i) => [p.x - pts[i].x, p.y - pts[i].y]),
        pts[0].x, pts[0].y, [1, 1], 'FD', true
      );
    };

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // HELPER: Ratna Sthapna Table (Modified - Width reduced by 100px)
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    const addRatnaTable = () => {
      pdf.addPage();
      addPageFrame(pdf, 'Ratna Sthapna Table');

      // ?o. Ratna data ??" image se exactly match
      // mayaSymbol: 'pentagon' = golden, 'star' = blue
      const ratnaData = [
        { srNo: 1, maanasaram: 'Ruby',             mayamatham: 'Ruby',            aparajita: 'Ruby',                    mayaSymbol: 'pentagon' },
        { srNo: 2, maanasaram: 'Diamond',           mayamatham: 'Red coral',       aparajita: 'Crystal Quartz/Diamond',  mayaSymbol: 'star'    },
        { srNo: 3, maanasaram: 'Red coral',         mayamatham: 'Yellow Sapphire', aparajita: "Cat's Eye",               mayaSymbol: 'star'    },
        { srNo: 4, maanasaram: 'Blue sapphire',     mayamatham: "Cat's Eye",       aparajita: 'Pearl',                   mayaSymbol: 'star'    },
        { srNo: 5, maanasaram: 'Yellow Sapphire',   mayamatham: 'Diamond',         aparajita: 'Blue sapphire',           mayaSymbol: 'star'    },
        { srNo: 6, maanasaram: 'Green Emerald',     mayamatham: 'Blue sapphire',   aparajita: 'Blue sapphire',           mayaSymbol: 'star'    },
        { srNo: 7, maanasaram: 'Hessonite Gernet',  mayamatham: 'Blue sapphire',   aparajita: 'Yellow Sapphire',         mayaSymbol: 'star'    },
        { srNo: 8, maanasaram: 'Pearl',             mayamatham: 'Green Emerald',   aparajita: 'Hessonite Gernet',        mayaSymbol: 'star'    },
        { srNo: 9, maanasaram: "Cat's Eye",         mayamatham: 'Pearl',           aparajita: 'Red coral',               mayaSymbol: 'star'    },
      ];

      const tableBody = ratnaData.map(row => [
        row.srNo.toString(),
        row.maanasaram,
        row.mayamatham,
        row.aparajita,
      ]);

      autoTable(pdf, {
        startY: 22,
        head: [['SR.N', 'Maanasaram Granth', 'Mayamatham Granth', 'Aparajitaprccha Granth']],
        body: tableBody,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [30, 30, 80],
          fillColor: [255, 255, 255],
          lineColor: [100, 130, 200],
          lineWidth: 0.5,
          overflow: 'linebreak',
          valign: 'middle',
          halign: 'center',
          fontStyle: 'bold',
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [30, 30, 100],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          valign: 'middle',
          lineColor: [100, 130, 200],
          lineWidth: 0.5,
        },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 15,  halign: 'center' },
          1: { cellWidth: 51,  halign: 'left'   },
          2: { cellWidth: 51,  halign: 'left'   },
          3: { cellWidth: 62,  halign: 'left'   }, // Reduced width (was 65, now 52 - approximately 100px reduction)
        },
        margin: { left: 10, right: 10, top: 22, bottom: 22 },

        // ?o. Symbol draw karo har row mein cell ke left side pe, content ke baad
        didDrawCell: (data) => {
          if (data.section !== 'body') return;
          const row = ratnaData[data.row.index];
          if (!row) return;

          // Symbol position - cell ke right edge ke paas (content ke baad)
          const cx = data.cell.x + data.cell.width - 8;
          const cy = data.cell.y + data.cell.height / 2;
          const symSize = 3.5;

          // Col 1 = Maanasaram ??" pentagon (golden) - content ke baad
          if (data.column.index === 1) {
            drawPDFPentagon(pdf, cx, cy, symSize, [198, 146, 42]);
          }
          // Col 2 = Mayamatham ??" row 1 pentagon, baaki star (blue) - content ke baad
          if (data.column.index === 2) {
            if (row.mayaSymbol === 'star') {
              drawPDFStar(pdf, cx, cy, symSize, symSize * 0.45, [26, 60, 143]);
            } else {
              drawPDFPentagon(pdf, cx, cy, symSize, [198, 146, 42]);
            }
          }
          // Col 3 = Aparajita ??" pentagon (golden) - content ke baad
          if (data.column.index === 3) {
            drawPDFPentagon(pdf, cx, cy, symSize, [198, 146, 42]);
          }
        },

        didDrawPage: () => {
          addFooter(pdf, pageNumber, totalPages, 'Ratna Sthapna Table');
        },
      });

      pageNumber++;
    };

    // Wall Color Schema / Curtain Colors — reference table drawn on its own
    // page right after the Coloured Zone page (same data as the editor's
    // WallColorSchemaTable, all 8 languages). Each colour name is drawn in its
    // own colour, wrapped word-by-word inside the cell.
    const addColouredZoneColorTable = () => {
      const lang = selectedLanguage;
      const title = wallLabel('title', lang);
      pdf.addPage();
      let y = addPageFrame(pdf, title) + 4;

      const TX = 10, TW = a4Width - 20;
      const base = [28, 46, 58, 58];
      const scale = TW / base.reduce((a, b) => a + b, 0);
      const cw = base.map(c => c * scale);
      const FS = 10, LH = 5.2, PADY = 3.2;
      const hexToRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
      const DARK = [60, 60, 60];
      const measure = (text, bold) => {
        if (DEVANAGARI_RE.test(text)) {
          measureCtxPdf.font = hindiFontCssPdf(FS, bold ? 'bold' : 'normal');
          return measureCtxPdf.measureText(text).width / PX_PER_MM;
        }
        pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(FS);
        return pdf.getTextWidth(text);
      };

      // tokens -> wrapped lines of coloured pieces. A colour name that doesn't fit
      // moves to the next line; its " / " separator stays on the previous line.
      const layoutCell = (cell, maxW) => {
        const tokens = wallCellTokens(cell, lang);
        const lines = [[]]; let x = 0, pendingSep = '';
        tokens.forEach((t) => {
          if (t.hex === null) { pendingSep = t.text; return; }
          const nameW = measure(t.text, true);
          const sepW = pendingSep ? measure(pendingSep, false) : 0;
          const cur = lines[lines.length - 1];
          if (cur.length && x + sepW + nameW > maxW) {
            if (pendingSep.trim() === '/') cur.push({ text: ' /', hex: null, w: measure(' /', false), bold: false });
            lines.push([]); x = 0; pendingSep = '';
          }
          const line = lines[lines.length - 1];
          if (pendingSep) { line.push({ text: pendingSep, hex: null, w: sepW, bold: false }); x += sepW; }
          line.push({ text: t.text, hex: t.hex, w: nameW, bold: true }); x += nameW;
          pendingSep = '';
        });
        return lines;
      };

      // header row
      const heads = [wallLabel('colorZone', lang), wallLabel('favorable', lang), wallLabel('anti', lang), wallLabel('possible', lang)];
      pdf.setFillColor(128, 0, 0); pdf.rect(TX, y, TW, 9, 'F');
      let hx = TX;
      heads.forEach((h, i) => { drawLineSmartPdf(pdf, h, hx + cw[i] / 2, y + 6.2, FS, 'bold', [255, 255, 255], 'center'); hx += cw[i]; });
      y += 9;

      WALL_COLOR_ROWS.forEach((row, ri) => {
        const cells = [row.favorable, row.anti, row.possible].map((cell, ci) => layoutCell(cell, cw[ci + 1] - 4));
        const rowH = Math.max(13, Math.max(...cells.map(l => l.length)) * LH + PADY * 2);
        pdf.setFillColor(...(ri % 2 === 0 ? [255, 255, 255] : [249, 250, 251]));
        pdf.rect(TX, y, TW, rowH, 'F');
        pdf.setDrawColor(200, 200, 205); pdf.setLineWidth(0.15);
        let cx = TX;
        cw.forEach((w) => { pdf.rect(cx, y, w, rowH, 'S'); cx += w; });

        // zone label (bold, in its own colour), centred vertically
        const zoneName = wallCellTokens([[row.zone]], lang)[0];
        const zw = measure(zoneName.text, true);
        drawLineSmartPdf(pdf, zoneName.text, TX + (cw[0] - zw) / 2, y + rowH / 2 + 1.6, FS + 1, 'bold', hexToRgb(zoneName.hex));

        cx = TX + cw[0];
        cells.forEach((lines, ci) => {
          const totalH = lines.length * LH;
          const top = y + (rowH - totalH) / 2;
          lines.forEach((line, li) => {
            const lineW = line.reduce((s, p) => s + p.w, 0);
            let px = cx + (cw[ci + 1] - lineW) / 2;
            line.forEach((p) => {
              drawLineSmartPdf(pdf, p.text, px, top + LH * li + 3.7, FS, p.bold ? 'bold' : 'normal', p.hex ? hexToRgb(p.hex) : DARK);
              px += p.w;
            });
          });
          cx += cw[ci + 1];
        });
        y += rowH;
      });

      // notes under the table — Description tier, black
      y += 6;
      [wallLabel('note1', lang), wallLabel('note2', lang)].forEach((text) => {
        wrapAnyPdf(pdf, text, TYPE_DESCRIPTION_SIZE, 'normal', TW - 4).forEach((ln) => {
          if (y + 8 > a4Height - 22) {
            addFooter(pdf, pageNumber, totalPages, title);
            pageNumber++;
            pdf.addPage();
            y = addPageFrame(pdf, title) + 4;
          }
          drawLineSmartPdf(pdf, ln, TX + 2, y + 6, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
          y += 8;
        });
        y += 3;
      });

      addFooter(pdf, pageNumber, totalPages, title);
      pageNumber++;
    };

    // Tri-Dosh / Tri-Guna pie chart — its own page, right after that layer's
    // own image page. Drawn as a vector pie (not a captured screenshot) from
    // layerState.triChartData, which the editor keeps in sync with the
    // current plan/compass and which is saved with the layer (see the
    // useEffect in LayerEditor) — so this works even if that layer's editor
    // was never reopened after the plan last changed.
    const addTriChartPage = (layerId) => {
      const chart = savedLayerConfigs[layerId]?.triChartData;
      const slices = (chart?.slices || []).filter(s => (s.value || 0) > 0);
      const total = slices.reduce((s, sl) => s + sl.value, 0);
      if (!chart || !slices.length || !(total > 0)) return;

      const title = chart.title || 'Area Share';
      pdf.addPage();
      let y = addPageFrame(pdf, title) + 10;

      const hexToRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
      const cx = a4Width / 2, cy = y + 55, R = 45;
      let startDeg = -90;
      const wedgeLabels = [];
      slices.forEach((sl) => {
        const sweep = (360 * sl.value) / total;
        const endDeg = startDeg + sweep;
        const steps = Math.max(2, Math.round(sweep / 3));
        const pts = [[cx, cy]];
        for (let i = 0; i <= steps; i++) {
          const deg = startDeg + (sweep * i) / steps;
          const rad = (deg * Math.PI) / 180;
          pts.push([cx + R * Math.cos(rad), cy + R * Math.sin(rad)]);
        }
        pdf.setFillColor(...hexToRgb(sl.color));
        pdf.setDrawColor(255, 255, 255);
        pdf.setLineWidth(0.4);
        pdf.lines(
          pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]),
          pts[0][0], pts[0][1], [1, 1], 'FD', true
        );
        // Percentage label at the wedge's mid-angle, halfway out to the rim — only
        // when the slice is wide enough that the text won't spill past its own
        // wedge into its neighbours (a sliver slice keeps its % in the legend only).
        if (sweep >= 22) {
          const midRad = ((startDeg + endDeg) / 2 * Math.PI) / 180;
          wedgeLabels.push({ x: cx + R * 0.62 * Math.cos(midRad), y: cy + R * 0.62 * Math.sin(midRad), text: `${((100 * sl.value) / total).toFixed(1)}%` });
        }
        startDeg = endDeg;
      });
      wedgeLabels.forEach((lb) => {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
        const w = pdf.getTextWidth(lb.text);
        pdf.setTextColor(255, 255, 255);
        pdf.text(lb.text, lb.x - w / 2, lb.y + 1.5);
      });

      y = cy + R + 20;
      const LEGEND_ROW = 10;
      slices.forEach((sl) => {
        pdf.setFillColor(...hexToRgb(sl.color));
        pdf.rect(a4Width / 2 - 55, y - 4.5, 6, 6, 'F');
        drawLineSmartPdf(pdf, sl.label, a4Width / 2 - 45, y + 1, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
        drawLineSmartPdf(pdf, `${((100 * sl.value) / total).toFixed(1)}%`, a4Width / 2 + 45, y + 1, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK, 'right');
        y += LEGEND_ROW;
      });

      addFooter(pdf, pageNumber, totalPages, title);
      pageNumber++;
    };

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // COVER PAGE
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    if (exportLayers['cover-page']?.enabled) {
      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;
      addMainCoverPage(pdf);
      pageNumber++;
    }

    // COVER INDEX — the per-category index pages that follow Cover Front.
    if (coverIndexEnabled && indexCategories.length > 0) {
      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;
      addCombinedIndexPages(pdf, indexCategories);
    }

    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    // ALL OTHER LAYERS
    // ?"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"??"?
    for (const layerId of nonReportLayers) {
      if (layerId === 'cover-page') continue;

      let displayName = getLayerName(layerId);
      if (layerId === 'Bar-chart-8-table')    displayName = '8 Zones Table';
      else if (layerId === 'Bar-chart-8-chart')   displayName = '8 Zones Bar Chart';
      else if (layerId === 'Bar-chart-16-table')  displayName = '16 Zones Tables';
      else if (layerId === 'Bar-chart-16-chart')  displayName = '16 Zones Bar Chart';
      else if (layerId === 'Bar-chart-32-table')  displayName = '32 Mahavastu Table';
      else if (layerId === 'Bar-chart-32-chart')  displayName = '32 Mahavastu Bar Chart';
      else if (layerId === 'elemental-5-chart')   displayName = 'Modern 5 Elemental Pie Chart';
      else if (layerId === 'elemental-5-zone')    displayName = 'Modern 5 Elemental Zone';
      else if (layerId.includes('-table') || layerId.includes('-chart')) {
        const baseId = layerId.replace('-table', '').replace('-chart', '');
        const componentType = layerId.includes('-table') ? ' - Table' : ' - Chart';
        displayName = getLayerName(baseId) + componentType;
      }

      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;

      const safeContentY = addPageFrame(pdf, displayName);

      // Vedic Generative Zones (32 Zones / 64 Grid / Padvinyash / Elemental /
      // Planet Zoning): these are rasterized once into uploadedImages[] and
      // edited exactly like Planning Guide reports, so they fall straight
      // through to the standard layerImages[] rendering below — no special case.

      // layerImages[layerId] may be an offloaded file URL (see LayerImageStorage
      // on the backend) rather than a raw base64 string — resolve it to base64
      // first since jsPDF's addImage needs actual bytes, and the CDN in front of
      // the API strips CORS headers off any URL whose path ends in an image
      // extension (same reasoning as resolveFloorPlanImage).
      // Big PNG layer pictures are re-saved as a high-quality, slightly lower-resolution JPEG (see utils/imageShrink.js):
      // same look on paper, a fraction of the file size — in the PDF and in the Word file.
      const layerImage = await shrinkImageDataUrl(await projectService.resolveLayerImage(layerImages[layerId]));

      if (layerImage) {
        try {
          const yPosition = safeContentY;
          const maxWidth = a4Width - 20;
          const maxHeight = a4Height - yPosition - 16;

          await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const actualRatio = img.naturalWidth / img.naturalHeight;
              let imgWidth, imgHeight;
              if (actualRatio > maxWidth / maxHeight) {
                imgWidth = maxWidth; imgHeight = maxWidth / actualRatio;
              } else {
                imgHeight = maxHeight; imgWidth = maxHeight * actualRatio;
              }
              const xPosition = (a4Width - imgWidth) / 2;
              pdf.addImage(layerImage, 'PNG', xPosition, yPosition, imgWidth, imgHeight, undefined, 'FAST');
              addFooter(pdf, pageNumber, totalPages, displayName);
              pageNumber++;
              resolve();
            };
            img.onerror = () => {
              const fallbackRatio = 210 / 297;
              let imgWidth = maxWidth;
              let imgHeight = maxWidth / fallbackRatio;
              if (imgHeight > maxHeight) { imgHeight = maxHeight; imgWidth = maxHeight * fallbackRatio; }
              const xPosition = (a4Width - imgWidth) / 2;
              pdf.addImage(layerImage, 'PNG', xPosition, yPosition, imgWidth, imgHeight, undefined, 'FAST');
              addFooter(pdf, pageNumber, totalPages, displayName);
              pageNumber++;
              resolve();
            };
            img.src = layerImage;
          });
        } catch (imageError) {
        }
      } else {
        addFooter(pdf, pageNumber, totalPages, displayName);
        pageNumber++;
      }

      // ?o. compass-45 / compass-45-2 ke baad Devata table
      const layerToTableMap = {
        'compass-45':   'Compass-45',
        'compass-45-2': 'Compass-45-2',
      };
      if (layerToTableMap[layerId]) {
        try {
          addDevataTable(layerToTableMap[layerId]);
        } catch (e) {
        }
      }

      // Coloured Zone ke baad Wall Color Schema / Curtain Colors table
      if (layerId === 'coloured-zone') {
        try {
          addColouredZoneColorTable();
        } catch (e) {
        }
      }

      // Tri-Dosh / Tri-Guna ke baad uska pie-chart page — right below the layer's own page.
      if (['tri-dosh-Vedic', 'tri-guna-Vedic', 'tri-dosh-modern', 'tri-guna-modern'].includes(layerId)) {
        try {
          addTriChartPage(layerId);
        } catch (e) {
        }
      }

      // ?o. RATNA-STHAPNA ke baad Ratna Sthapna table
      if (layerId === 'RATNA-STHAPNA') {
        try {
          addRatnaTable();
        } catch (e) {
        }
      }

      // Planning Guide: auto-attach this report's Vastu-guidelines page
      // right after its image page — no separate toggle for it.
      if (HOUSE_PLANNING_NOTES[layerId]) {
        try {
          addHousePlanningNotesPage(layerId);
        } catch (e) {
        }
      }
    }

    // ── ENTRANCE ANALYSIS REPORT — dynamic packing, heading/description format ──
    if (exportLayers['entrance-analysis-report']?.enabled) {
      try {
        const entrRaw = localStorage.getItem(entranceAnalysisStorageKey);
        const entrList = entrRaw ? JSON.parse(entrRaw) : [];
        const entrEnabled = entrList.filter(d => d.enabled !== false);
        if (entrEnabled.length > 0) {
          const ECX          = 12;
          const ECW          = a4Width - 24;          // 186mm
          const ELINE_H      = 9.6; // scaled up for 16pt Description text (was tuned for 10pt)
          const EITEM_GAP    = 10;
          const EPAGE_BOTTOM = a4Height - 22;

          /* ── Parse richHtml into [{heading, text}] sections ── */
          const parseEntrSections = (html) => {
            if (!html) return [];
            const sections = [];
            const parts = html.split(/<h2[^>]*>/i);
            parts.forEach(part => {
              if (!part.trim()) return;
              const headingMatch = part.match(/^(.*?)<\/h2>/i);
              if (headingMatch) {
                const heading = headingMatch[1].replace(/<[^>]+>/g, '').trim();
                const rest = part.replace(/^.*?<\/h2>/i, '');
                const text = rest
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ').replace(/<\/li>/gi, ' ')
                  .replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
                if (heading || text) sections.push({ heading, text });
              } else {
                const text = part
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ')
                  .replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
                if (text) sections.push({ heading: '', text });
              }
            });
            return sections;
          };

          const startNewEntrPage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Entrance Analysis Report', 'Vastu Entrance Remedies');
          };

          let entrCurY = 0;
          let lastEntrName = null;

          // Continuous flow: break to a new page only when the next line
          // wouldn't fit above the footer zone (EPAGE_BOTTOM), so nothing can
          // ever run into the footer and the next item starts right where the
          // previous one ended.
          const ensureEntrSpace = (h) => {
            if (entrCurY + h > EPAGE_BOTTOM) {
              addFooter(pdf, pageNumber, totalPages, 'Entrance Analysis Report');
              pageNumber++;
              entrCurY = startNewEntrPage();
            }
          };

          for (const item of entrEnabled) {
            const entrName = item.customName || `Entrance ${item.entranceNumber || ''}`;
            const sections = parseEntrSections(isStillDefaultEntranceHtml(item, item.richHtml) ? buildEntranceHtml(item, selectedLanguage) : item.richHtml);
            const sectionsH = sections.length > 0 ? (sections[0].heading ? 9 : 0) + ELINE_H : 0; // just enough to keep the name label with its first lines

            // entrance name label (if changed) + sections. Used to also add
            // a separate "Direction: W5" sub-label here — duplicated the
            // same item.zone/item.direction value the sections loop below
            // already renders as its own "Zone" heading (buildEntranceHtml
            // adds a Zone field from the exact same source), same bug as
            // the Space Analysis report had. Removed; the Zone heading
            // below is drawn bigger/bolder instead so it still reads as
            // the item's primary heading.
            const showEntrLabel = entrName !== lastEntrName;
            const itemH = (showEntrLabel ? 16 : 0) + sectionsH; // 16 must match the entrCurY increment after drawing the entrance-name label below

            if (entrCurY === 0) {
              entrCurY = startNewEntrPage();
            } else if (entrCurY + EITEM_GAP + itemH > EPAGE_BOTTOM) {
              addFooter(pdf, pageNumber, totalPages, 'Entrance Analysis Report');
              pageNumber++;
              entrCurY = startNewEntrPage();
            } else {
              // separator line between items
              pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
              pdf.line(ECX + 20, entrCurY + EITEM_GAP / 2, ECX + ECW - 20, entrCurY + EITEM_GAP / 2);
              entrCurY += EITEM_GAP;
            }

            // ── Entrance name label (when entrance changes) — Subtitle tier ──
            if (showEntrLabel) {
              drawLineSmartPdf(pdf, entrName, ECX + 2, entrCurY + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
              entrCurY += 16;
              lastEntrName = entrName;
            }

            // ── Sections: Zone / Energy Category / Entrance Effect / Remedies / Notes
            // — headings and body text both Description tier (16pt/black), bold
            // vs normal weight is the only thing distinguishing them now. ──
            sections.forEach(sec => {
              if (sec.heading) {
                ensureEntrSpace(9 + (sec.text ? ELINE_H : 0));
                drawLineSmartPdf(pdf, sec.heading, ECX + 2, entrCurY + 5, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                entrCurY += 9;
              }
              if (sec.text) {
                const lines = wrapAnyPdf(pdf, sec.text, TYPE_DESCRIPTION_SIZE, 'normal', ECW - 4);
                lines.forEach((ln) => {
                  ensureEntrSpace(ELINE_H);
                  drawLineSmartPdf(pdf, ln, ECX + 2, entrCurY + 4, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  entrCurY += ELINE_H;
                });
                entrCurY += 3;
              }
            });
          }

          // close last page
          if (entrCurY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Entrance Analysis Report');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage();
        pdf.setFontSize(14); pdf.text('Entrance Analysis Report', 20, 20);
        pdf.setFontSize(10); pdf.text('Error generating report. Please try again.', 20, 30);
        addFooter(pdf, pageNumber, totalPages, 'Entrance Analysis Report');
        pageNumber++;
      }
    }

    // ── SPACE ANALYSIS REPORT — dynamic packing, heading/description format ──
    if (exportLayers['space-analysis-report']?.enabled) {
      try {
        const spaceRaw = localStorage.getItem(spaceAnalysisStorageKey);
        const spaceItems = spaceRaw ? JSON.parse(spaceRaw) : [];
        // Self-heal stale/wrong-language saves at export time (remedies AND the
        // Additional Notes) — the editor only re-translates while it is open,
        // so switching language and exporting without reopening it used to
        // leave the old language in the PDF. Hand-edited text is left alone.
        spaceItems.forEach((it) => {
          if (isDefaultSpaceItemHtml(it, it.richHtml)) it.richHtml = buildSpaceItemHtml(it, selectedLanguage);
        });
        if (spaceItems.length > 0) {
          const SCX       = 12;
          const SCW       = a4Width - 24;
          const SLINE_H   = 8.5; // scaled up for 16pt Description text (was tuned for 8.5pt)
          const SITEM_GAP = 4;
          const SGH       = 14;   // group header bar height — scaled up for the 20pt Subtitle heading
          const SPAGE_BOT = a4Height - 22;

          /* ── Parse richHtml → [{heading, text}] ── */
          const parseSpaceSect = (html) => {
            if (!html) return [];
            const parts = html.split(/<h2[^>]*>/i);
            const out = [];
            parts.forEach((part, pi) => {
              if (!part.trim()) return;
              const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
              if (m) {
                const heading = m[1].replace(/<[^>]+>/g, '').trim();
                const text = m[2]
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ').replace(/<\/li>/gi, ' ')
                  .replace(/<[^>]+>/g, '').replace(/&amp;/g,'&').replace(/&lt;/g,'<')
                  .replace(/&gt;/g,'>').replace(/&nbsp;/g,' ').replace(/\s{2,}/g,' ').trim();
                if (pi > 0 && (heading || text)) out.push({ heading, text }); // skip first (space name)
              } else {
                const text = part.replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/\s{2,}/g,' ').trim();
                if (text && pi > 0) out.push({ heading:'', text });
              }
            });
            return out;
          };

          const startNewSpacePage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Space Analysis Report', 'Space Analysis  |  Vastu Placement Remedies');
          };

          /* ── Group items by customName ── */
          const sGroups = {}, sOrder = [];
          spaceItems.filter(item => item.kind !== 'notes').forEach(item => {
            const k = item.customName || item.placementName || 'Space';
            if (!sGroups[k]) { sGroups[k] = []; sOrder.push(k); }
            sGroups[k].push(item);
          });

          let sCurY = 0;
          let firstSGroup = true;

          // "Additional Notes" (enabled ones only) print once per space type,
          // right after the LAST instance of that space (e.g. after Toilet #1
          // AND #2), before the next space's remedies start.
          const spaceNoteItems = spaceItems.filter(item => item.kind === 'notes' && item.notesEnabled);
          const spaceNotesDone = new Set();
          const drawSpaceNotes = (placementName) => {
            if (spaceNotesDone.has(placementName)) return;
            spaceNotesDone.add(placementName);
            const list = spaceNoteItems
              .filter(n => n.placementName === placementName)
              .map(n => ({ n, block: parseNotesBlock(n.richHtml) }))
              .filter(x => x.block.paras.length);
            const spaceBreak = () => {
              addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
              pageNumber++;
              sCurY = startNewSpacePage();
            };
            list.forEach(({ n, block }) => {
              const title = list.length > 1
                ? `${block.heading || 'Notes'} — ${n.customName || n.placementName}`
                : (block.heading || 'Notes');
              if (sCurY === 0) sCurY = startNewSpacePage();
              else if (sCurY + SITEM_GAP + SLINE_H * 2 > SPAGE_BOT) spaceBreak();
              else sCurY += SITEM_GAP;
              drawLineSmartPdf(pdf, title, SCX + 2, sCurY + SLINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.35);
              pdf.line(SCX, sCurY + SLINE_H + 1.5, SCX + SCW, sCurY + SLINE_H + 1.5);
              sCurY += SLINE_H + 3;
              block.paras.forEach((p) => {
                wrapAnyPdf(pdf, p, TYPE_DESCRIPTION_SIZE, 'normal', SCW - 10).forEach((ln) => {
                  if (sCurY + SLINE_H > SPAGE_BOT) spaceBreak();
                  drawLineSmartPdf(pdf, ln, SCX + 5, sCurY + SLINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  sCurY += SLINE_H;
                });
                sCurY += 1.5;
              });
            });
          };
          let prevSpacePlacement = null;

          sOrder.forEach(groupName => {
            const gitems = sGroups[groupName];

            // Moving on to a different space type — finish the previous one
            // with its notes first.
            const placementNow = gitems[0].placementName;
            if (prevSpacePlacement !== null && prevSpacePlacement !== placementNow) drawSpaceNotes(prevSpacePlacement);
            prevSpacePlacement = placementNow;

            /* ── Ensure there's room for at least the group header ── */
            if (sCurY === 0) {
              sCurY = startNewSpacePage();
            } else if (!firstSGroup && sCurY + SITEM_GAP + SGH > SPAGE_BOT) {
              addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
              pageNumber++;
              sCurY = startNewSpacePage();
            } else if (!firstSGroup) {
              sCurY += SITEM_GAP;
            }
            firstSGroup = false;

            /* ── Group heading — no fill, bold black text + black underline. Subtitle tier. ── */
            drawLineSmartPdf(pdf, groupName, SCX + 2, sCurY + 10, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
            pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.6);
            pdf.line(SCX, sCurY + SGH, SCX + SCW, sCurY + SGH);
            sCurY += SGH;

            /* ── Items within group ── */
            gitems.forEach((item, idx) => {
              const sections = parseSpaceSect(item.richHtml || '');
              // Only the first heading + first line have to stay together — the
              // rest flows line by line, so the next item starts right where the
              // previous one ended instead of jumping to a fresh page (gap).
              const itemH = SLINE_H * 2 + 1;

              if (idx > 0) {
                if (sCurY + SITEM_GAP + itemH > SPAGE_BOT) {
                  addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
                  pageNumber++;
                  sCurY = startNewSpacePage();
                } else {
                  pdf.setDrawColor(210, 218, 224); pdf.setLineWidth(0.2);
                  pdf.line(SCX + 10, sCurY + SITEM_GAP / 2, SCX + SCW - 10, sCurY + SITEM_GAP / 2);
                  sCurY += SITEM_GAP;
                }
              } else {
                sCurY += 2;
              }

              /* Content sections — "Zone" used to also get its own separate
                 teal sub-label above this loop (Zone: NW), duplicating the
                 "Zone" heading this loop already draws from the same
                 item.zone/item.direction value. Removed that duplicate;
                 the Zone heading here is now drawn bigger/bolder than the
                 other field headings (Type/Impact/Remedies) instead, so it
                 still reads as the item's primary heading. */
              sections.forEach(sec => {
                if (sec.heading) {
                  if (sCurY + SLINE_H + 1 > SPAGE_BOT) {
                    addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
                    pageNumber++;
                    sCurY = startNewSpacePage();
                  }
                  drawLineSmartPdf(pdf, sec.heading, SCX + 2, sCurY + SLINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                  sCurY += SLINE_H + 1;
                }
                if (sec.text) {
                  const lines = wrapAnyPdf(pdf, sec.text, TYPE_DESCRIPTION_SIZE, 'normal', SCW - 10);
                  lines.forEach(ln => {
                    if (sCurY + SLINE_H > SPAGE_BOT) {
                      addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
                      pageNumber++;
                      sCurY = startNewSpacePage();
                    }
                    drawLineSmartPdf(pdf, ln, SCX + 5, sCurY + SLINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                    sCurY += SLINE_H;
                  });
                  sCurY += 1.5;
                }
              });
            });
          });
          if (prevSpacePlacement !== null) drawSpaceNotes(prevSpacePlacement);

          /* Close last page */
          if (sCurY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage();
        pdf.setFontSize(14); pdf.text('Space Analysis Report', 20, 20);
        pdf.setFontSize(10); pdf.text('Error generating report. Please try again.', 20, 30);
        addFooter(pdf, pageNumber, totalPages, 'Space Analysis Report');
        pageNumber++;
      }
    }

    // ─────────────────────────────────────────────────────────────
    // VASTU DEVTA GUIDE REPORT  (one A4 page per enabled devta)
    // ─────────────────────────────────────────────────────────────
    if (exportLayers['vastu-devta-guide-report']?.enabled) {
      try {
        const devtaList = loadReportList(vastuDevtaGuideStorageKey, VASTU_DEVTA_GUIDE_DATA, buildDevtaHtml, selectedLanguage);
        const enabledDevtas = devtaList.filter(d => d.enabled);
        // Self-heal stale/wrong-language saves at export time — same fix as above.
        enabledDevtas.forEach((d) => {
          if (isDefaultDevtaHtml(d, d.richHtml)) d.richHtml = buildDevtaHtml(d, selectedLanguage);
        });

        if (enabledDevtas.length > 0) {
          // Content area (inside the border drawn by addPageFrame):
          //   x: 10 → 200   (width 190mm)
          //   y: 20 → 279   (after title+divider at y=18)
          const CX = 12;          // content left
          const CW = a4Width - 24; // content width  (186mm)
          const TEAL    = [0, 161, 157];
          const TEAL_LT = [0, 175, 170];
          const WHITE   = [255, 255, 255];
          const BLACK   = [0, 0, 0];
          const DARK    = [30, 30, 30];
          const GRAY    = [100, 100, 100];
          const LGRAY   = [230, 230, 230];
          const CHAKRA_RGB = {
            Root: [200,60,60], Sacral: [200,100,30], Solar: [190,150,30],
            Heart: [50,150,90], Throat: [40,110,180], 'Third Eye': [100,60,180], Crown: [120,80,200]
          };

          // Pre-load devta images as data URLs
          const devtaImageCache = {};
          await Promise.all(enabledDevtas.map(async (devta) => {
            // The user replaced this devta's picture in the editor — use it (fitted to the PDF image slot).
            if (devta.customImage) { devtaImageCache[devta.id] = await fitImageToBox(devta.customImage, 60, 52); return; }
            try {
              const resp = await fetch(`/devta-images/devta_${devta.id}.png`);
              if (!resp.ok) return;
              const blob = await resp.blob();
              devtaImageCache[devta.id] = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch { /* image not available — skip */ }
          }));

          // Continuous flow (was one forced page per devta, leaving most of
          // each page blank): the next devta starts right below the previous
          // one with a fixed gap + separator, and a new page is only opened
          // when a devta's header block (name + image + first heading) no
          // longer fits above the footer zone.
          let curY = 0;                    // 0 = no page open yet
          const DEVTA_GAP = 10;            // gap between two devtas sharing a page
          const IMG_W = 60;
          const IMG_H = 52;
          const startDevtaPage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Vastu Devta Guide', 'Devta Energies & Remedial Practices');
          };

          for (const devta of enabledDevtas) {
            const imgData = devtaImageCache[devta.id];
            const headerH = 14 + (imgData ? IMG_H + 7 : 0) + 13 + 10; // name + image + first heading + a line
            if (curY === 0) {
              curY = startDevtaPage();
            } else if (curY + DEVTA_GAP + headerH > a4Height - 22) {
              addFooter(pdf, pageNumber, totalPages, 'Vastu Devta Guide');
              pageNumber++;
              curY = startDevtaPage();
            } else {
              pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
              pdf.line(CX + 20, curY + DEVTA_GAP / 2, CX + CW - 20, curY + DEVTA_GAP / 2);
              curY += DEVTA_GAP;
            }

            // ── Devta name — Subtitle tier (it used to live in the page-header
            // subtitle slot, which only works with one devta per page) ──
            drawLineSmartPdf(pdf, devta.name || 'Vastu Devta', CX + 2, curY + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
            curY += 14;

            // ── Devta Image — centred, properly sized ──
            if (imgData) {
              try {
                const IMG_X = CX + (CW - IMG_W) / 2;
                // Subtle shadow rect (offset 0.8mm)
                pdf.setFillColor(220, 220, 220);
                pdf.rect(IMG_X + 0.8, curY + 0.8, IMG_W, IMG_H, 'F');
                // White background behind image
                pdf.setFillColor(255, 255, 255);
                pdf.rect(IMG_X, curY, IMG_W, IMG_H, 'F');
                pdf.addImage(imgData, 'PNG', IMG_X, curY, IMG_W, IMG_H, undefined, 'FAST');
                // Border
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.35);
                pdf.rect(IMG_X, curY, IMG_W, IMG_H, 'S');
                curY += IMG_H + 7;
              } catch { /* image not available */ }
            }

            // ── Helpers: strip HTML → plain text ──
            const stripHtml = (html) => {
              if (!html) return '';
              return html
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/p>/gi, '\n')
                .replace(/<\/h[1-6]>/gi, '\n')
                .replace(/<\/li>/gi, '\n')
                .replace(/<[^>]+>/g, '')
                .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
                .replace(/\n{2,}/g, '\n').trim();
            };

            // Parse HTML into structured nodes [{tag, text, bold, italic}]
            const parseHtmlNodes = (html) => {
              if (!html || html === '<p><br></p>') return [];
              const nodes = [];
              const blockRe = /<(h[1-3]|p|li)((?:\s[^>]*)?)?>([\s\S]*?)<\/\1>/gi;
              let m;
              while ((m = blockRe.exec(html)) !== null) {
                const tag   = m[1].toLowerCase();
                const inner = m[3];
                const text  = stripHtml(inner).trim();
                if (!text) continue;
                const bold   = /<strong|<b>/i.test(inner);
                const italic = /<em|<i>/i.test(inner);
                nodes.push({ tag, text, bold, italic });
              }
              if (nodes.length === 0) {
                const plain = stripHtml(html).trim();
                if (plain) nodes.push({ tag: 'p', text: plain, bold: false, italic: false });
              }
              return nodes;
            };

            const PAGE_BOTTOM = a4Height - 22;
            // Close the CURRENT page with its own footer before starting the
            // next one (this used to stamp the footer on the NEW page instead,
            // so continuation pages ended up with two footers on top of each
            // other and the page that overflowed had none). A small
            // "<name> (contd.)" line keeps the devta identifiable now that the
            // page header no longer carries its name.
            const addNewPage = () => {
              addFooter(pdf, pageNumber, totalPages, 'Vastu Devta Guide');
              pageNumber++;
              pdf.addPage();
              curY = addPageFrame(pdf, 'Vastu Devta Guide', 'Devta Energies & Remedial Practices');
              drawLineSmartPdf(pdf, `${devta.name || 'Vastu Devta'}`, CX + 2, curY + 7, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              curY += 11;
            };

            // Get HTML: prefer richHtml, fallback to old fields
            let htmlSource = devta.richHtml;
            if (!htmlSource || htmlSource === '<p><br></p>' || htmlSource === '<p></p>') {
              const parts = [];
              if (devta.sacredMantra)      parts.push(`<h1>SACRED ATTRIBUTES</h1><h2>Sacred Mantra</h2><p>${devta.sacredMantra}</p>`);
              if (devta.attributes)        parts.push(`<h2>Key Attributes</h2><p>${devta.attributes}</p>`);
              if (devta.divineEssence)     parts.push(`<h1>DIVINE ESSENCE</h1><p>${devta.divineEssence}</p>`);
              if (devta.remedialPractices) parts.push(`<h1>REMEDIAL PRACTICES</h1><p>${devta.remedialPractices}</p>`);
              htmlSource = parts.join('');
            }

            const htmlNodes = parseHtmlNodes(htmlSource);

            for (const node of htmlNodes) {
              const { tag, text, bold, italic } = node;

              if (tag === 'h1') {
                // ── H1: this devta's major section divider — Subtitle tier,
                // always black + bottom border line (matches editor style) ──
                const H = 13;
                if (curY + H > PAGE_BOTTOM) addNewPage();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 9, TYPE_SUBTITLE_SIZE, italic ? 'bolditalic' : 'bold', TYPE_BLACK);
                // Bottom divider line matching editor border-bottom
                pdf.setDrawColor(226, 232, 240);       // #e2e8f0
                pdf.setLineWidth(0.4);
                pdf.line(CX, curY + H, CX + CW, curY + H);
                curY += H + 2;

              } else if (tag === 'h2') {
                // ── H2: Description tier, bold, always black ──
                const H = 10;
                if (curY + H > PAGE_BOTTOM) addNewPage();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 7.5, TYPE_DESCRIPTION_SIZE, bold && italic ? 'bolditalic' : italic ? 'italic' : 'bold', TYPE_BLACK);
                curY += H + 1;

              } else if (tag === 'h3') {
                // ── H3: Description tier, bold, always black ──
                const H = 9;
                if (curY + H > PAGE_BOTTOM) addNewPage();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 7, TYPE_DESCRIPTION_SIZE, bold && italic ? 'bolditalic' : italic ? 'italic' : 'bold', TYPE_BLACK);
                curY += H + 1;

              } else {
                // ── P / LI: Description tier, always black ──
                const prefix  = (tag === 'li') ? '• ' : '';
                const pStyle  = bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal';
                const wrLines = wrapAnyPdf(pdf, prefix + text, TYPE_DESCRIPTION_SIZE, pStyle, CW - 8);
                const lineH   = 7.5;
                // Line-by-line flow: only break when the next line won't fit
                // above the footer zone, so long paragraphs continue on the
                // next page instead of running into the footer (or leaving a gap).
                wrLines.forEach((ln) => {
                  if (curY + lineH > PAGE_BOTTOM) addNewPage();
                  drawLineSmartPdf(pdf, ln, CX + 4, curY + 7, TYPE_DESCRIPTION_SIZE, pStyle, TYPE_BLACK);
                  curY += lineH;
                });
                curY += 2;
              }
            }
          }

          // Close the last open page with its footer.
          if (curY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Vastu Devta Guide');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage();
        addPageFrame(pdf, 'Vastu Devta Guide Report');
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Error generating Vastu Devta Guide. Please try again.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Vastu Devta Guide');
        pageNumber++;
      }
    }

    // ── NAVGRAH SHANTI REPORT ─────────────────────────────────────────
    if (exportLayers['navgrah-shanti-report']?.enabled) {
      try {
        const navgrahList = loadReportList(navgrahShantiStorageKey, NAVGRAH_SHANTI_DATA, buildGrahaHtml, selectedLanguage);
        const enabledGrahas = navgrahList.filter(d => d.enabled);
        // Self-heal stale/wrong-language saves at export time — same fix as
        // Kua/Astro/Cut & Extension above: NavgrahShanti's own re-translate
        // effect only fires while that editor happens to be open when the
        // language dropdown changes.
        enabledGrahas.forEach((d) => {
          if (isDefaultGrahaHtml(d, d.richHtml)) d.richHtml = buildGrahaHtml(d, selectedLanguage);
        });
        if (enabledGrahas.length > 0) {
          const CX = 12; const CW = a4Width - 24;
          const PURPLE = [124, 58, 237]; const LGRAY2 = [230, 230, 230];
          const GRAY2  = [100, 100, 100];
          for (const graha of enabledGrahas) {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            let curY = addPageFrame(pdf, graha.name || 'Navgrah Shanti', 'Navgrah Shanti  |  9 Planetary Energies & Vastu Alignment');
            const stripHtml2 = (html) => {
              if (!html) return '';
              return html.replace(/<br\s*\/?>/gi,'\n').replace(/<\/p>/gi,'\n').replace(/<\/h[1-6]>/gi,'\n').replace(/<\/li>/gi,'\n')
                .replace(/<[^>]+>/g,'').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&nbsp;/g,' ').replace(/\n{2,}/g,'\n').trim();
            };
            const parseNodes2 = (html) => {
              if (!html || html === '<p><br></p>') return [];
              const nodes = []; const re = /<(h[1-3]|p|li)((?:\s[^>]*)?)?>([\s\S]*?)<\/\1>/gi; let m;
              while ((m = re.exec(html)) !== null) {
                const tag = m[1].toLowerCase(); const inner = m[3]; const text = stripHtml2(inner).trim();
                if (!text) continue;
                nodes.push({ tag, text, bold: /<strong|<b>/i.test(inner), italic: /<em|<i>/i.test(inner) });
              }
              if (nodes.length === 0) { const plain = stripHtml2(html).trim(); if (plain) nodes.push({ tag:'p', text:plain, bold:false, italic:false }); }
              return nodes;
            };
            const PB2 = a4Height - 22;
            // Footer goes on the page being CLOSED (it used to be stamped on the
            // new page, doubling up with that page's own footer).
            const newPage2 = () => { addFooter(pdf, pageNumber, totalPages, 'Navgrah Shanti'); pageNumber++; pdf.addPage(); addPageFrame(pdf, graha.name); curY = 26; };
            let htmlSrc = graha.richHtml;
            if (!htmlSrc || htmlSrc === '<p><br></p>') {
              htmlSrc = `<h1>PLANETARY PROFILE</h1>${graha.direction?`<h2>Direction</h2><p>${graha.direction}</p>`:''}${graha.divineEssence?`<h1>DIVINE ESSENCE</h1><p>${graha.divineEssence}</p>`:''}${graha.remedialPractices?`<h1>REMEDIAL PRACTICES</h1><p>${graha.remedialPractices}</p>`:''}`;
            }
            // h1 = Subtitle tier (20pt/black); h2/h3 = Description tier, bold
            // (16pt/black); paragraphs/list items = Description tier, normal.
            for (const node of parseNodes2(htmlSrc)) {
              const { tag, text, bold, italic } = node;
              if (tag === 'h1') {
                const H = 17; if (curY + H > PB2) newPage2();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 12, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
                pdf.setDrawColor(...LGRAY2); pdf.setLineWidth(0.4); pdf.line(CX, curY + H, CX + CW, curY + H);
                curY += H + 2;
              } else if (tag === 'h2') {
                const H = 12; if (curY + H > PB2) newPage2();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 9, TYPE_DESCRIPTION_SIZE, italic?'bolditalic':'bold', TYPE_BLACK);
                curY += H + 1;
              } else if (tag === 'h3') {
                const H = 11; if (curY + H > PB2) newPage2();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 9, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                curY += H + 1;
              } else {
                const prefix = tag === 'li' ? '• ' : '';
                const pStyle2 = bold&&italic?'bolditalic':bold?'bold':italic?'italic':'normal';
                const wrLines = wrapAnyPdf(pdf, prefix + text, TYPE_DESCRIPTION_SIZE, pStyle2, CW - 8);
                const lineH = 8.5;
                wrLines.forEach((ln) => {
                  if (curY + lineH > PB2) newPage2();
                  drawLineSmartPdf(pdf, ln, CX + 4, curY + 7.5, TYPE_DESCRIPTION_SIZE, pStyle2, TYPE_BLACK);
                  curY += lineH;
                });
                curY += 2;
              }
            }
            addFooter(pdf, pageNumber, totalPages, 'Navgrah Shanti');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, 'Navgrah Shanti Report');
        pdf.setFontSize(10); pdf.setTextColor(0,0,0); pdf.text('Error generating Navgrah Shanti.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Navgrah Shanti'); pageNumber++;
      }
    }

    // ── FENG SHUI REPORT — dynamic packing, 200px(53mm) centred image ──
    if (exportLayers['feng-shui-report']?.enabled) {
      try {
        const fengList = loadReportList(fengShuiStorageKey, FENG_SHUI_DATA, buildFengShuiHtml, selectedLanguage);
        const enabledAreas = fengList.filter(d => d.enabled);
        // Self-heal stale/wrong-language saves at export time — same fix as above.
        enabledAreas.forEach((d) => {
          if (isDefaultFengShuiHtml(d, d.richHtml)) d.richHtml = buildFengShuiHtml(d, selectedLanguage);
        });
        if (enabledAreas.length > 0) {
          const CX          = 12;
          const CW          = a4Width - 24;          // 186mm
          const IMG_SIZE    = 53;                    // 200px ≈ 53mm square
          const IMG_X       = (a4Width - IMG_SIZE) / 2;  // centred horizontally
          const LINE_H      = 9; // scaled up for 16pt Description text (was tuned for 11pt)
          const SYM_GAP     = 10;                   // space between 2 symbols on same page
          const PAGE_BOTTOM = a4Height - 22;

          /* strip HTML → plain text */
          const stripHtml3 = (html) => {
            if (!html) return '';
            return html
              .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ').replace(/<\/li>/gi, ' ')
              .replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
          };

          /* pre-load images */
          const fengImgCache = {};
          await Promise.all(enabledAreas.map(async (area) => {
            if (area.customImage) { fengImgCache[area.id] = await fitImageToBox(area.customImage, 1, 1); return; }
            try {
              const resp = await fetch(`/feng-sui/fengsui${area.id}.png`);
              if (!resp.ok) return;
              const blob = await resp.blob();
              fengImgCache[area.id] = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch { /* skip */ }
          }));

          /* helpers */
          const startNewFengPage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Feng Shui', 'Feng Shui  |  Lucky Symbols & Protective Energies');
          };

          const drawImage = (imgData, cy) => {
            if (imgData) {
              try {
                pdf.setFillColor(255, 255, 255);
                pdf.rect(IMG_X, cy, IMG_SIZE, IMG_SIZE, 'F');
                pdf.addImage(imgData, 'PNG', IMG_X, cy, IMG_SIZE, IMG_SIZE, undefined, 'FAST');
                pdf.setDrawColor(210, 216, 224); pdf.setLineWidth(0.25);
                pdf.rect(IMG_X, cy, IMG_SIZE, IMG_SIZE);
              } catch { drawPlaceholder(cy); }
            } else { drawPlaceholder(cy); }
          };

          const drawPlaceholder = (cy) => {
            pdf.setFillColor(248, 250, 252); pdf.setDrawColor(210, 216, 224); pdf.setLineWidth(0.25);
            pdf.roundedRect(IMG_X, cy, IMG_SIZE, IMG_SIZE, 2, 2, 'FD');
            pdf.setTextColor(190, 196, 210); pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
            pdf.text('[ Image ]', a4Width / 2, cy + IMG_SIZE / 2 + 1, { align: 'center' });
          };

          let curY = 0;  // 0 = need a new page
          let currentPageSymbols = 0;

          for (const area of enabledAreas) {
            const descText  = stripHtml3(area.richHtml || area.description || '');
            const descLines = descText ? wrapAnyPdf(pdf, descText, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4) : [];
            // Only title + image must stay together (an image can't split);
            // the description flows line-by-line across pages below.
            const symH      = 9 + 4 + IMG_SIZE + 5 + (descLines.length > 0 ? LINE_H : 0);

            if (curY === 0) {
              // start first / fresh page
              curY = startNewFengPage();
              currentPageSymbols = 0;
            } else if (curY + SYM_GAP + symH > PAGE_BOTTOM) {
              // doesn't fit — finish this page and start new
              addFooter(pdf, pageNumber, totalPages, 'Feng Shui');
              pageNumber++;
              curY = startNewFengPage();
              currentPageSymbols = 0;
            } else {
              // fits on current page — draw separator line
              pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
              pdf.line(CX + 20, curY + SYM_GAP / 2, CX + CW - 20, curY + SYM_GAP / 2);
              curY += SYM_GAP;
            }

            // ── Symbol name — Subtitle tier (black, left-aligned) ──
            drawLineSmartPdf(pdf, area.name || '', CX + 2, curY + 7, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
            curY += 9;

            // ── Image (200px = 53mm, centred) ──
            drawImage(fengImgCache[area.id], curY);
            curY += IMG_SIZE + 5;

            // ── Description — Description tier (black, left-aligned) ──
            if (descLines.length > 0) {
              const descLinesFinal = wrapAnyPdf(pdf, descText, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4);
              descLinesFinal.forEach((ln) => {
                if (curY + LINE_H > PAGE_BOTTOM) {
                  addFooter(pdf, pageNumber, totalPages, 'Feng Shui');
                  pageNumber++;
                  curY = startNewFengPage();
                }
                drawLineSmartPdf(pdf, ln, CX + 2, curY + 5, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                curY += LINE_H;
              });
              curY += 2;
            }

            currentPageSymbols++;
          }

          // close last page
          if (curY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Feng Shui');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, 'Feng Shui Report');
        pdf.setFontSize(10); pdf.setTextColor(0,0,0); pdf.text('Error generating Feng Shui report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Feng Shui'); pageNumber++;
      }
    }

    // ── HELIX REPORT — continuous flow: name + image(s) + rich-text body ──
    if (exportLayers['helix-report']?.enabled) {
      try {
        const helixRaw = localStorage.getItem(helixStorageKey);
        const helixSaved = helixRaw ? JSON.parse(helixRaw) : [];
        // Saved data is slim ({ id, enabled, richHtml }) — names/images come from HELIX_DATA.
        const enabledHelix = helixSaved
          .filter(d => d.enabled && HELIX_DATA.some(h => h.id === d.id))
          .map(d => ({ ...HELIX_DATA.find(h => h.id === d.id), ...d }));
        enabledHelix.forEach((d) => {
          if (isDefaultHelixHtml(d, d.richHtml)) d.richHtml = buildHelixHtml(d);
          // Pictures the user replaced in the editor take the place of the originals (same position).
          if (d.customImages) d.images = (d.images || []).map((s, i) => d.customImages[i] || s);
        });

        if (enabledHelix.length > 0) {
          const CX = 12;
          const CW = a4Width - 24;
          const PAGE_BOTTOM = a4Height - 22;
          const HELIX_GAP = 10;
          const MAX_IMG_H = 52;

          // Images are re-encoded through a canvas so .webp and .jpg both
          // embed cleanly (jsPDF cannot read webp) and the file stays small.
          const helixImgCache = {};
          const loadHelixImg = (src) => new Promise((resolve) => {
            const im = new Image();
            im.onload = () => {
              try {
                const c = document.createElement('canvas');
                c.width = im.naturalWidth; c.height = im.naturalHeight;
                const ctx = c.getContext('2d');
                ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
                ctx.drawImage(im, 0, 0);
                resolve({ data: c.toDataURL('image/jpeg', 0.9), w: c.width, h: c.height });
              } catch { resolve(null); }
            };
            im.onerror = () => resolve(null);
            im.src = src;
          });
          const allHelixSrcs = Array.from(new Set(enabledHelix.flatMap(d => d.images || [])));
          await Promise.all(allHelixSrcs.map(async (src) => { helixImgCache[src] = await loadHelixImg(src); }));

          const stripHelixHtml = (html) => {
            if (!html) return '';
            return html
              .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<\/h[1-6]>/gi, '\n').replace(/<\/li>/gi, '\n')
              .replace(/<[^>]+>/g, '')
              .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
              .replace(/\n{2,}/g, '\n').trim();
          };
          const parseHelixNodes = (html) => {
            if (!html || html === '<p><br></p>') return [];
            const nodes = [];
            const blockRe = /<(h[1-3]|p|li)((?:\s[^>]*)?)?>([\s\S]*?)<\/\1>/gi;
            let m;
            while ((m = blockRe.exec(html)) !== null) {
              const inner = m[3];
              const text = stripHelixHtml(inner).trim();
              if (!text) continue;
              nodes.push({ tag: m[1].toLowerCase(), text, bold: /<strong|<b>/i.test(inner), italic: /<em|<i>/i.test(inner) });
            }
            if (nodes.length === 0) {
              const plain = stripHelixHtml(html).trim();
              if (plain) nodes.push({ tag: 'p', text: plain, bold: false, italic: false });
            }
            return nodes;
          };

          let curY = 0; // 0 = no page open yet
          const startHelixPage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Helix, Spring and Stud', 'Metal Helix, Frames, Springs & Studs');
          };

          for (const it of enabledHelix) {
            const imgs = (it.images || []).map(s => helixImgCache[s]).filter(Boolean);
            const maxW = imgs.length > 1 ? 62 : 70;
            const sized = imgs.map(im => {
              const r = Math.min(maxW / im.w, MAX_IMG_H / im.h);
              return { ...im, dw: im.w * r, dh: im.h * r };
            });
            const rowH = sized.length ? Math.max(...sized.map(s => s.dh)) : 0;
            const headerH = 14 + (rowH ? rowH + 7 : 0) + 13 + 10;

            if (curY === 0) {
              curY = startHelixPage();
            } else if (curY + HELIX_GAP + headerH > PAGE_BOTTOM) {
              addFooter(pdf, pageNumber, totalPages, 'Helix, Spring and Stud');
              pageNumber++;
              curY = startHelixPage();
            } else {
              pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
              pdf.line(CX + 20, curY + HELIX_GAP / 2, CX + CW - 20, curY + HELIX_GAP / 2);
              curY += HELIX_GAP;
            }

            drawLineSmartPdf(pdf, it.name || 'Helix', CX + 2, curY + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
            curY += 14;

            if (sized.length) {
              const IMG_GAP = 6;
              const totalW = sized.reduce((a, s) => a + s.dw, 0) + IMG_GAP * (sized.length - 1);
              let x = CX + (CW - totalW) / 2;
              sized.forEach((s) => {
                const y = curY + (rowH - s.dh) / 2;
                try {
                  pdf.setFillColor(255, 255, 255);
                  pdf.rect(x, y, s.dw, s.dh, 'F');
                  pdf.addImage(s.data, 'JPEG', x, y, s.dw, s.dh, undefined, 'FAST');
                  pdf.setDrawColor(200, 200, 200); pdf.setLineWidth(0.35);
                  pdf.rect(x, y, s.dw, s.dh, 'S');
                } catch { /* image not embeddable — skip */ }
                x += s.dw + IMG_GAP;
              });
              curY += rowH + 7;
            }

            const addNewHelixPage = () => {
              addFooter(pdf, pageNumber, totalPages, 'Helix, Spring and Stud');
              pageNumber++;
              pdf.addPage();
              curY = addPageFrame(pdf, 'Helix, Spring and Stud', 'Metal Helix, Frames, Springs & Studs');
              drawLineSmartPdf(pdf, `${it.name || 'Helix'}`, CX + 2, curY + 7, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              curY += 11;
            };

            for (const node of parseHelixNodes(it.richHtml)) {
              const { tag, text, bold, italic } = node;
              if (tag === 'h1') {
                const H = 13;
                if (curY + H > PAGE_BOTTOM) addNewHelixPage();
                drawLineSmartPdf(pdf, text, CX + 2, curY + 9, TYPE_SUBTITLE_SIZE, italic ? 'bolditalic' : 'bold', TYPE_BLACK);
                pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.4);
                pdf.line(CX, curY + H, CX + CW, curY + H);
                curY += H + 2;
              } else if (tag === 'h2' || tag === 'h3') {
                const H = tag === 'h2' ? 10 : 9;
                if (curY + H > PAGE_BOTTOM) addNewHelixPage();
                drawLineSmartPdf(pdf, text, CX + 2, curY + (tag === 'h2' ? 7.5 : 7), TYPE_DESCRIPTION_SIZE, bold && italic ? 'bolditalic' : italic ? 'italic' : 'bold', TYPE_BLACK);
                curY += H + 1;
              } else {
                const prefix = tag === 'li' ? '• ' : '';
                const pStyle = bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal';
                const wrLines = wrapAnyPdf(pdf, prefix + text, TYPE_DESCRIPTION_SIZE, pStyle, CW - 8);
                const lineH = 7.5;
                wrLines.forEach((ln) => {
                  if (curY + lineH > PAGE_BOTTOM) addNewHelixPage();
                  drawLineSmartPdf(pdf, ln, CX + 4, curY + 7, TYPE_DESCRIPTION_SIZE, pStyle, TYPE_BLACK);
                  curY += lineH;
                });
                curY += 2;
              }
            }
          }

          if (curY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Helix, Spring and Stud');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, 'Helix, Spring and Stud');
        pdf.setFontSize(10); pdf.setTextColor(0, 0, 0); pdf.text('Error generating Helix, Spring and Stud report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Helix, Spring and Stud'); pageNumber++;
      }
    }

    // ── ZONE ANALYSIS REPORT — dynamic packing, 200px(53mm) centred image ──
    if (exportLayers['zone-analysis-report']?.enabled) {
      try {
        const zoneList = loadReportList(zoneAnalysisStorageKey, ZONE_ANALYSIS_ITEMS, buildZoneAnalysisHtml, selectedLanguage);
        const enabledItems = zoneList.filter(d => d.enabled);
        // Self-heal stale/wrong-language saves at export time — same fix as above.
        enabledItems.forEach((d) => {
          if (isDefaultZoneAnalysisHtml(d, d.richHtml)) d.richHtml = buildZoneAnalysisHtml(d, selectedLanguage);
        });
        if (enabledItems.length > 0) {
          const CX          = 12;
          const CW          = a4Width - 24;          // 186mm
          const IMG_SIZE    = 40;                    // 150px ≈ 40mm
          const IMG_X       = (a4Width - IMG_SIZE) / 2;  // centred horizontally
          const LINE_H      = 9.6; // scaled up for 16pt Description text (was tuned for 10pt)
          const ITEM_GAP    = 10;                   // space between 2 items on same page
          const PAGE_BOTTOM = a4Height - 22;

          /* strip HTML → plain text */
          const stripHtmlZA = (html) => {
            if (!html) return '';
            return html
              .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ').replace(/<\/li>/gi, ' ')
              .replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
          };

          /* pre-load images */
          const zoneImgCache = {};
          await Promise.all(enabledItems.map(async (item) => {
            if (item.customImage) { zoneImgCache[item.id] = await fitImageToBox(item.customImage, 1, 1); return; }
            try {
              const resp = await fetch(`/zone-analysis/zone${item.id}.png`);
              if (!resp.ok) return;
              const blob = await resp.blob();
              zoneImgCache[item.id] = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch { /* skip */ }
          }));

          /* helpers */
          const startNewZonePage = () => {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            return addPageFrame(pdf, 'Zone Analysis', 'Zone Analysis  |  Vastu Zones & Recommended Symbols');
          };

          const drawZoneImage = (imgData, cy) => {
            if (imgData) {
              try {
                pdf.setFillColor(255, 255, 255);
                pdf.rect(IMG_X, cy, IMG_SIZE, IMG_SIZE, 'F');
                pdf.addImage(imgData, 'PNG', IMG_X, cy, IMG_SIZE, IMG_SIZE, undefined, 'FAST');
                pdf.setDrawColor(210, 216, 224); pdf.setLineWidth(0.25);
                pdf.rect(IMG_X, cy, IMG_SIZE, IMG_SIZE);
              } catch { drawZonePlaceholder(cy); }
            } else { drawZonePlaceholder(cy); }
          };

          const drawZonePlaceholder = (cy) => {
            pdf.setFillColor(248, 250, 252); pdf.setDrawColor(210, 216, 224); pdf.setLineWidth(0.25);
            pdf.roundedRect(IMG_X, cy, IMG_SIZE, IMG_SIZE, 2, 2, 'FD');
            pdf.setTextColor(190, 196, 210); pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
            pdf.text('[ Image ]', a4Width / 2, cy + IMG_SIZE / 2 + 1, { align: 'center' });
          };

          /* ── Parse richHtml into [{heading, text}] sections ── */
          const parseZoneSections = (html) => {
            if (!html) return [];
            const sections = [];
            // Split on <h2> tags
            const parts = html.split(/<h2[^>]*>/i);
            parts.forEach(part => {
              if (!part.trim()) return;
              const headingMatch = part.match(/^(.*?)<\/h2>/i);
              if (headingMatch) {
                const heading = headingMatch[1].replace(/<[^>]+>/g, '').trim();
                const rest = part.replace(/^.*?<\/h2>/i, '');
                const text = rest
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ').replace(/<\/li>/gi, ' ')
                  .replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
                if (heading || text) sections.push({ heading, text });
              } else {
                // plain text before first h2
                const text = part
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ')
                  .replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
                if (text) sections.push({ heading: '', text });
              }
            });
            return sections;
          };

          let curY = 0;  // 0 = need a new page
          let lastZoneName = null;

          // Continuous flow: text is placed line-by-line and only breaks to a
          // new page when the NEXT LINE doesn't fit, so the following item
          // starts right where the previous one ended instead of being pushed
          // wholesale to the next page (which left big blank gaps).
          const ensureSpace = (h) => {
            if (curY + h > PAGE_BOTTOM) {
              addFooter(pdf, pageNumber, totalPages, 'Zone Analysis');
              pageNumber++;
              curY = startNewZonePage();
            }
          };

          for (const item of enabledItems) {
            const sections = parseZoneSections(item.richHtml || '');
            // Only the header block (zone label + name + image) must stay together —
            // an image can't be split across pages. The text below it flows freely.
            const showZoneLabel = item.zoneName !== lastZoneName;
            const itemH = (showZoneLabel ? 14 : 0) + 9 + IMG_SIZE + 5;

            if (curY === 0) {
              curY = startNewZonePage();
            } else if (curY + ITEM_GAP + itemH > PAGE_BOTTOM) {
              addFooter(pdf, pageNumber, totalPages, 'Zone Analysis');
              pageNumber++;
              curY = startNewZonePage();
            } else {
              // separator line
              pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
              pdf.line(CX + 20, curY + ITEM_GAP / 2, CX + CW - 20, curY + ITEM_GAP / 2);
              curY += ITEM_GAP;
            }

            // ── Zone label (when zone changes) — Subtitle tier ──
            if (item.zoneName !== lastZoneName) {
              drawLineSmartPdf(pdf, item.zoneName || '', CX + 2, curY + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
              curY += 14;
              lastZoneName = item.zoneName;
            }

            // ── Item name — Description tier, bold ──
            drawLineSmartPdf(pdf, item.name || '', CX + 2, curY + 7, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
            curY += 9;

            // ── Image (200px = 53mm, centred) ──
            drawZoneImage(zoneImgCache[item.id], curY);
            curY += IMG_SIZE + 5;

            // ── Purpose / Description / Special Note sections — Description tier ──
            sections.forEach(sec => {
              if (sec.heading) {
                // keep a heading together with at least the first line of its text
                ensureSpace(8 + (sec.text ? LINE_H : 0));
                drawLineSmartPdf(pdf, sec.heading, CX + 2, curY + 5, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                curY += 8;
              }
              if (sec.text) {
                const lines = wrapAnyPdf(pdf, sec.text, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4);
                lines.forEach((ln) => {
                  ensureSpace(LINE_H);
                  drawLineSmartPdf(pdf, ln, CX + 2, curY + 4, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  curY += LINE_H;
                });
                curY += 3;
              }
            });
          }

          // close last page
          if (curY > 0) {
            addFooter(pdf, pageNumber, totalPages, 'Zone Analysis');
            pageNumber++;
          }
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, 'Zone Analysis Report');
        pdf.setFontSize(10); pdf.setTextColor(0,0,0); pdf.text('Error generating Zone Analysis report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Zone Analysis'); pageNumber++;
      }
    }

    // ── NUMERO VASTU REPORT — one page per person, Lo Shu Grid ──────────────
    if (exportLayers['numero-vastu-report']?.enabled) {
      try {
        const nvRaw = localStorage.getItem(numeroVastuStorageKey) || localStorage.getItem(vastuPersonsStorageKey);
        const nvPersons = nvRaw ? JSON.parse(nvRaw).filter(p => p.name || p.dob) : [];
        if (nvPersons.length > 0) {
          const NV_CW = 54, NV_CH = 55, NV_GAP = 3.5;
          const NV_GRID_W = 3 * NV_CW + 2 * NV_GAP;
          const NV_GRID_X = (a4Width - NV_GRID_W) / 2;

          // Not just the raw DOB digits — Moolank (from the day) and Bhagyank
          // (from the full DOB) are ALSO placed into the grid. A number that
          // only comes from Moolank/Bhagyank still counts as present (e.g.
          // DOB 16-10-2007 has no bare 8, but Bhagyank reduces to 8, so 8 is
          // NOT one of the missing numbers) — same fix as VastuPersonsGateway
          // and NumeroVastuReport's own calcLoShu.
          const calcNvLoShu = (dob) => {
            if (!dob) return { present:[], missing:[1,2,3,4,5,6,7,8,9] };
            const digits = dob.replace(/\D/g,'').split('').map(Number).filter(n => n >= 1 && n <= 9);
            const moolank = calcMoolank(dob);
            const bhagyank = calcBhagyank(dob);
            const allDigits = [...digits, ...(moolank ? [moolank] : []), ...(bhagyank ? [bhagyank] : [])];
            const present = [...new Set(allDigits)].sort((a,b) => a-b);
            return { present, missing:[1,2,3,4,5,6,7,8,9].filter(n => !present.includes(n)) };
          };

          const NV_BOX_DATA = {
            1:{colorRgb:[59,130,246], direction:'NORTH',      planet:'Sun',     element:'Water',      use:'Career & Success'},
            2:{colorRgb:[239,68,68],  direction:'SOUTH WEST', planet:'Moon',    element:'Earth',      use:'Marriage & Relationship'},
            3:{colorRgb:[34,197,94],  direction:'EAST',       planet:'Jupiter', element:'Hard Wood',  use:'Health & Family'},
            4:{colorRgb:[139,92,246], direction:'SOUTH EAST', planet:'Rahu',    element:'Soft Wood',  use:'Money & Property'},
            5:{colorRgb:[202,138,4],  direction:'Center',     planet:'Mercury', element:'Earth',      use:'Energy & Stability'},
            6:{colorRgb:[30,58,95],   direction:'NORTH WEST', planet:'Venus',   element:'Hard Metal', use:'Friends & Beginnings'},
            7:{colorRgb:[13,148,136], direction:'WEST',       planet:'Ketu',    element:'Soft Metal', use:'Children & Creativity'},
            8:{colorRgb:[234,88,12],  direction:'NORTH EAST', planet:'Saturn',  element:'Earth',      use:'Knowledge & Intuition'},
            9:{colorRgb:[107,114,128],direction:'SOUTH',      planet:'Mars',    element:'Fire',       use:'Fame & Prosperity'},
          };
          const NV_GRID = [[4,9,2],[3,5,7],[8,1,6]];

          const drawNvCell = (num, bx, by, isMissing) => {
            const d = NV_BOX_DATA[num];
            const [r,g,b] = d.colorRgb;
            pdf.setFillColor(r,g,b);
            pdf.roundedRect(bx, by, NV_CW, NV_CH, 3, 3, 'F');
            const WHITE_RGB = [255,255,255];
            drawLineSmartPdf(pdf, localDigits(num, selectedLanguage), bx + NV_CW/2, by + 14, 24, 'bold', WHITE_RGB, 'center');
            drawLineSmartPdf(pdf, `${nvLabel('direction', selectedLanguage)}: ${loShuText('direction', d.direction, selectedLanguage)}`, bx + NV_CW/2, by + 23, 7, 'bold', WHITE_RGB, 'center');
            drawLineSmartPdf(pdf, `${nvLabel('planet', selectedLanguage)}: ${loShuText('planet', d.planet, selectedLanguage)}`,   bx + NV_CW/2, by + 30, 6.5, 'normal', WHITE_RGB, 'center');
            drawLineSmartPdf(pdf, `${nvLabel('element', selectedLanguage)}: ${loShuText('element', d.element, selectedLanguage)}`, bx + NV_CW/2, by + 36, 6.5, 'normal', WHITE_RGB, 'center');
            wrapAnyPdf(pdf, `${nvLabel('use', selectedLanguage)}: ${loShuText('use', d.use, selectedLanguage)}`, 6.5, 'bold', NV_CW - 8)
              .forEach((l, i) => drawLineSmartPdf(pdf, l, bx + NV_CW/2, by + 43 + i*4, 6.5, 'bold', WHITE_RGB, 'center'));
            if (isMissing) {
              // 50% opacity so the box's own number/text stays readable under the cross
              pdf.saveGraphicsState();
              pdf.setGState(new pdf.GState({ opacity: 0.5, 'stroke-opacity': 0.5 }));
              pdf.setDrawColor(215,25,25); pdf.setLineWidth(2.5);
              pdf.line(bx+5, by+5, bx+NV_CW-5, by+NV_CH-5);
              pdf.line(bx+NV_CW-5, by+5, bx+5, by+NV_CH-5);
              pdf.restoreGraphicsState();
              pdf.setLineWidth(0.35);
            }
          };

          for (const person of nvPersons) {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            addPageFrame(pdf, nvLabel('reportTitle', selectedLanguage));
            let curY = 27;

            // Person's own name is this page's item-identity — Subtitle tier.
            drawLineSmartPdf(pdf, person.name || 'Person', a4Width/2, curY, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
            curY += 9;

            const dp = [];
            if (person.gender) dp.push(`${nvLabel('gender', selectedLanguage)}: ${person.gender}`);
            if (person.dob) { const d = new Date(person.dob); dp.push(`${nvLabel('dob', selectedLanguage)}: ${!isNaN(d) ? d.toLocaleDateString('en-GB') : person.dob}`); }
            if (person.place) dp.push(`${nvLabel('place', selectedLanguage)}: ${person.place}`);
            if (dp.length) {
              drawLineSmartPdf(pdf, dp.join('   ·   '), a4Width/2, curY, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK, 'center');
              curY += 8;
            }
            if (person.details) {
              const ls = wrapAnyPdf(pdf, person.details, TYPE_DESCRIPTION_SIZE, 'italic', a4Width - 40);
              ls.forEach((l,i) => drawLineSmartPdf(pdf, l, a4Width/2, curY + i*7, TYPE_DESCRIPTION_SIZE, 'italic', TYPE_BLACK, 'center'));
              curY += ls.length * 7 + 1;
            }
            curY += 3;
            pdf.setDrawColor(220,220,220); pdf.setLineWidth(0.2);
            pdf.line(15, curY, a4Width-15, curY);
            curY += 8;

            const { present, missing } = calcNvLoShu(person.dob);
            const NV_GRID_Y = curY;
            NV_GRID.forEach((row, ri) => {
              row.forEach((num, ci) => {
                drawNvCell(num, NV_GRID_X + ci*(NV_CW+NV_GAP), NV_GRID_Y + ri*(NV_CH+NV_GAP), missing.includes(num));
              });
            });

            // Present/Missing summary — Description tier, always black (the
            // grid cells above already show strong/missing via the diagonal
            // cross mark, so this text no longer needs its own red/green).
            // Value column X is measured off the actual label width at the
            // new (bigger) font — a hardcoded offset from the old smaller
            // font was leaving the label and value overlapping.
            const summY = NV_GRID_Y + 3*NV_CH + 2*NV_GAP + 12;
            if (selectedLanguage === 'en') {
              pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica','bold');
              const nvSummValX = NV_GRID_X + Math.max(pdf.getTextWidth('Present Numbers:'), pdf.getTextWidth('Missing Numbers:')) + 4;
              pdf.setTextColor(...TYPE_BLACK); pdf.text('Present Numbers:', NV_GRID_X, summY);
              pdf.setFont('helvetica','normal'); pdf.text(present.length ? present.join('  ,  ') : 'None', nvSummValX, summY);
              pdf.setFont('helvetica','bold'); pdf.text('Missing Numbers:', NV_GRID_X, summY+9);
              pdf.setFont('helvetica','normal'); pdf.text(missing.length ? missing.join('  ,  ') : 'None', nvSummValX, summY+9);
            } else {
              // Non-Latin text can't be measured with the built-in font, so label and numbers share one line.
              const nvList = (arr) => (arr.length ? arr.map(n => localDigits(n, selectedLanguage)).join(', ') : nvLabel('none', selectedLanguage));
              drawLineSmartPdf(pdf, `${nvLabel('presentNumbers', selectedLanguage)}: ${nvList(present)}`, NV_GRID_X, summY, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              drawLineSmartPdf(pdf, `${nvLabel('missingNumbers', selectedLanguage)}: ${nvList(missing)}`, NV_GRID_X, summY+9, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
            }

            addFooter(pdf, pageNumber, totalPages, nvLabel('reportTitle', selectedLanguage));
            pageNumber++;
          }

          // ── Combined Strength page ────────────────────────────────────
          if (nvPersons.length > 0) {
            pdf.addPage();
            isFirstPage = false;
            addPageFrame(pdf, nvLabel('strengthTitle', selectedLanguage));

            const nvTotal = nvPersons.length;
            let cy = 25;

            pdf.setDrawColor(...REPORT_THEME_ACCENT); pdf.setLineWidth(0.4);
            pdf.line(15, cy, a4Width-15, cy);
            cy += 8;

            const NT_X = 12;
            const NT_ROW = 13, NT_HDR = 11;
            const NCW = { num:12, dir:30, pln:20, elm:22, use:38, str:64 };
            const NT_W = Object.values(NCW).reduce((s,v)=>s+v,0);

            // Header
            pdf.setFillColor(...REPORT_THEME_ACCENT);
            pdf.rect(NT_X, cy, NT_W, NT_HDR, 'F');
            let nhx = NT_X;
            [['#',NCW.num],[nvLabel('direction', selectedLanguage),NCW.dir],[nvLabel('planet', selectedLanguage),NCW.pln],[nvLabel('element', selectedLanguage),NCW.elm],
             [nvLabel('use', selectedLanguage),NCW.use],[nvLabel('strengthPct', selectedLanguage),NCW.str]].forEach(([lbl,w]) => {
              drawLineSmartPdf(pdf, lbl, nhx+w/2, cy+7.5, 7.5, 'bold', [255,255,255], 'center'); nhx += w;
            });
            cy += NT_HDR;

            [1,2,3,4,5,6,7,8,9].forEach((num, idx) => {
              const nd = NV_BOX_DATA[num];
              // Must go through calcNvLoShu (Moolank + Bhagyank included), NOT
              // raw DOB digits — otherwise this table shows a number as 0% /
              // "missing" while the per-person grid above shows it present,
              // and edit-mode's Strength Analysis disagrees with the export.
              const cnt = nvPersons.filter(p => calcNvLoShu(p.dob).present.includes(num)).length;
              const str = nvTotal > 0 ? (cnt/nvTotal)*100 : 0;
              const dc  = str >= 66 ? [22,163,74] : str >= 33 ? [202,138,4] : [220,38,38];

              pdf.setFillColor(...(idx%2===0?[255,255,255]:[245,250,250]));
              pdf.rect(NT_X, cy, NT_W, NT_ROW, 'F');
              pdf.setDrawColor(220,220,220); pdf.setLineWidth(0.15);
              pdf.rect(NT_X, cy, NT_W, NT_ROW, 'S');

              let rx = NT_X;
              const midY = cy + NT_ROW/2, textY = midY + 1.5;

              const [nr,ng,nb] = nd.colorRgb;
              pdf.setFillColor(nr,ng,nb);
              pdf.roundedRect(rx+1.5, cy+1.5, NCW.num-3, NT_ROW-3, 2, 2, 'F');
              drawLineSmartPdf(pdf, localDigits(num, selectedLanguage), rx+NCW.num/2, textY, 9.5, 'bold', [255,255,255], 'center'); rx += NCW.num;

              const NV_ROW_RGB = [30,30,30];
              drawLineSmartPdf(pdf, loShuText('direction', nd.direction, selectedLanguage), rx+2, textY, 7.5, 'normal', NV_ROW_RGB); rx += NCW.dir;
              drawLineSmartPdf(pdf, loShuText('planet', nd.planet, selectedLanguage),       rx+2, textY, 7.5, 'normal', NV_ROW_RGB); rx += NCW.pln;
              drawLineSmartPdf(pdf, loShuText('element', nd.element, selectedLanguage),     rx+2, textY, 7.5, 'normal', NV_ROW_RGB); rx += NCW.elm;
              wrapAnyPdf(pdf, loShuText('use', nd.use, selectedLanguage), 7.5, 'normal', NCW.use-4).slice(0,2)
                .forEach((l,li) => drawLineSmartPdf(pdf, l, rx+2, cy+4+li*4.5, 7.5, 'normal', NV_ROW_RGB));
              rx += NCW.use;

              // dot + bar + %
              const dotX=rx+4, barX=dotX+8, barW=30, barH=4, barY2=midY-barH/2;
              pdf.setFillColor(...dc);
              pdf.roundedRect(dotX-2.5, midY-2.5, 5, 5, 2.5, 2.5, 'F');
              pdf.setFillColor(215,215,215);
              pdf.roundedRect(barX, barY2, barW, barH, 1.5, 1.5, 'F');
              if (str>0) { pdf.setFillColor(...dc); pdf.roundedRect(barX, barY2, Math.max(1,(str/100)*barW), barH, 1.5, 1.5, 'F'); }
              pdf.setTextColor(0,0,0); pdf.setFontSize(8.5); pdf.setFont('helvetica','bold');
              pdf.text(`${str.toFixed(1)}%`, barX+barW+3, textY);
              cy += NT_ROW;
            });

            cy += 8;
            const allS = [1,2,3,4,5,6,7,8,9].filter(n => nvPersons.every(p => calcNvLoShu(p.dob).present.includes(n)));
            const allM = [1,2,3,4,5,6,7,8,9].filter(n => nvPersons.every(p => calcNvLoShu(p.dob).missing.includes(n)));
            // Body text below the table — Description tier, always black.
            if (allS.length) { pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica','bold'); pdf.setTextColor(...TYPE_BLACK); wrapAnyPdf(pdf, `${nvLabel('strongAll', selectedLanguage)}: ${allS.map(n=>`${localDigits(n, selectedLanguage)}–${loShuText('direction', NV_BOX_DATA[n].direction, selectedLanguage)}`).join(', ')}`, TYPE_DESCRIPTION_SIZE, 'bold', a4Width - 2*NT_X).forEach(l => { drawLineSmartPdf(pdf, l, NT_X, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); cy+=8; }); }
            if (allM.length) { pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica','bold'); pdf.setTextColor(...TYPE_BLACK); wrapAnyPdf(pdf, `${nvLabel('missingAll', selectedLanguage)}: ${allM.map(n=>`${localDigits(n, selectedLanguage)}–${loShuText('direction', NV_BOX_DATA[n].direction, selectedLanguage)}`).join(', ')}`, TYPE_DESCRIPTION_SIZE, 'bold', a4Width - 2*NT_X).forEach(l => { drawLineSmartPdf(pdf, l, NT_X, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); cy+=8; }); }

            addFooter(pdf, pageNumber, totalPages, nvLabel('reportTitle', selectedLanguage));
            pageNumber++;
          }

          // ── Remedy pages from editor ──────────────────────────────
          if (numeroRemedyData && Object.keys(numeroRemedyData).length > 0) {
            // Used to flatten every tag (h2 title / p Direction+Planet / h3
            // Mind-Body-Soul headings / li bullet items) into plain
            // newline-separated text, all drawn at the exact same small
            // font with no bold/spacing distinction — headings read
            // identically to bullet items, so the whole block looked
            // cramped/run-together. Replaced with a real block parser
            // (below) that keeps each tag's identity so headings and the
            // Direction/Planet line can be drawn bold/bigger with a gap
            // before them, same idea as parseSpaceSect/parseEntrSections
            // above for the other richHtml-based reports.
            const nvParseBlocks = (html) => {
              if (!html) return [];
              const blocks = [];
              const re = /<(h2|h3|li|p)[^>]*>([\s\S]*?)<\/\1>/gi;
              let m;
              while ((m = re.exec(html)) !== null) {
                const tag = m[1].toLowerCase();
                const text = m[2]
                  .replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '')
                  .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
                  .replace(/\s{2,}/g, ' ').trim();
                if (text) blocks.push({ tag, text });
              }
              return blocks;
            };

            const nvPerPerson = nvPersons.length > 0 ? 100 / nvPersons.length : 0;
            const weakNums = [1,2,3,4,5,6,7,8,9].filter(num => {
              // calcNvLoShu (Moolank + Bhagyank), not raw digits — keeps the
              // "which Missing-Number remedy pages appear" decision in sync
              // with the grid + strength table above and with edit mode.
              const cnt = nvPersons.filter(p => calcNvLoShu(p.dob).present.includes(num)).length;
              return (cnt * nvPerPerson) < 50;
            });

            const remedyNums = weakNums.filter(n => numeroRemedyData[n]);
            if (remedyNums.length > 0) {
              pdf.addPage();
              isFirstPage = false;
              let ry = addPageFrame(pdf, 'Numero Vastu — Remedies', 'For Zones with Strength below 50%') + 4;

              // ry here is a text BASELINE, so "h" = the space still needed
              // below it. Checked per line (not per block) so a long remedy
              // paragraph continues on the next page instead of running into
              // the footer zone.
              const nvEnsureRoom = (h) => {
                if (ry + h > a4Height - 22) {
                  addFooter(pdf, pageNumber, totalPages, nvLabel('reportTitle', selectedLanguage)); pageNumber++;
                  pdf.addPage(); ry = addPageFrame(pdf, 'Numero Vastu — Remedies') + 4;
                }
              };

              for (const num of remedyNums) {
                const nd = NV_BOX_DATA[num];
                const blocks = nvParseBlocks(numeroRemedyData[num]);
                if (!blocks.length) continue;

                nvEnsureRoom(19 + 8); // badge + at least the first line under it

                // Coloured badge — Subtitle tier, slightly trimmed down from
                // the full 20pt bar (was reading too thick/tall). White text
                // stays white here (it's on a solid colour fill, for
                // contrast, not decoration).
                const [nr,ng,nb] = REPORT_THEME_ACCENT; // remedies strip: report theme primary (grid + strength badges keep their own colours)
                pdf.setFillColor(nr,ng,nb);
                // Slimmer bar (was 13mm / 17pt) + a wider gap after it so the
                // "Direction: … Planet: …" line below no longer tucks under the
                // coloured strip. Text baseline re-centred for the 10mm height.
                pdf.roundedRect(14, ry, a4Width-28, 10, 2, 2, 'F');
                drawLineSmartPdf(pdf, `${num} — Missing Number  |  ${nd.direction}  |  ${nd.planet}`, 18, ry+6.9, 15, 'bold', [255,255,255]);
                ry += 19;

                // First block is the "N — Missing Number" <h2> title — the
                // colored badge above already shows that, so skip it here
                // (matches parseSpaceSect's "skip the first heading" rule).
                // Everything below is Description tier (16pt/black).
                let skippedTitle = false;
                for (const b of blocks) {
                  if (b.tag === 'h2' && !skippedTitle) { skippedTitle = true; continue; }
                  if (b.tag === 'h3') {
                    // Mind/Body/Soul Remedies sub-heading — bold, with a
                    // small gap above so it doesn't run into the previous
                    // section's last bullet line.
                    ry += 2;
                    wrapAnyPdf(pdf, b.text, TYPE_DESCRIPTION_SIZE, 'bold', a4Width - 36).forEach((wl) => { nvEnsureRoom(8 + 7); drawLineSmartPdf(pdf, wl, 18, ry, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); ry += 8; });
                  } else if (b.tag === 'p') {
                    // The "Direction: West   Planet: Ketu" line — bold.
                    wrapAnyPdf(pdf, b.text, TYPE_DESCRIPTION_SIZE, 'bold', a4Width - 40).forEach((wl) => { nvEnsureRoom(7.5); drawLineSmartPdf(pdf, wl, 18, ry, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); ry += 7.5; });
                    ry += 1;
                  } else {
                    wrapAnyPdf(pdf, `• ${b.text}`, TYPE_DESCRIPTION_SIZE, 'normal', a4Width - 44).forEach((wl) => { nvEnsureRoom(7); drawLineSmartPdf(pdf, wl, 21, ry, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK); ry += 7; });
                  }
                }
                ry += 6;
              }

              addFooter(pdf, pageNumber, totalPages, nvLabel('reportTitle', selectedLanguage)); pageNumber++;
            }
          }
        }
      } catch(e) {
        pdf.addPage(); addPageFrame(pdf, nvLabel('reportTitle', selectedLanguage));
        pdf.setFontSize(10); pdf.setTextColor(0,0,0); pdf.text('Error generating Numero Vastu report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, nvLabel('reportTitle', selectedLanguage)); pageNumber++;
      }

      // ── KUA NUMBER ANALYSIS — one page per person, richHtml sections ────────
      // This was missing from the combined export entirely (only available via
      // VastuPersonsGateway's own standalone "Download Kua PDF" button) — the
      // "Numero Vastu Report" toggle only ever drew the Lo Shu Grid above.
      try {
        const kuaRaw = localStorage.getItem(numeroVastuKuaStorageKey);
        const savedKua = kuaRaw ? JSON.parse(kuaRaw).filter(k => k.personName || k.richHtml) : [];
        // Re-read persons here — nvPersons above is scoped to the Lo Shu
        // Grid's own try block, out of reach down here (this is a separate
        // try/catch so one section's errors don't take out the other's pages).
        const kuaNvRaw = localStorage.getItem(numeroVastuStorageKey) || localStorage.getItem(vastuPersonsStorageKey);
        const nvPersons = kuaNvRaw ? JSON.parse(kuaNvRaw).filter(p => p.name || p.dob) : [];
        // The Kua modal SHOWS analysis even when the user never pressed "Save
        // Kua Data" — it computes buildKuaHtml(p) live per person. Previously
        // the export only used the saved-override array, so a user who viewed
        // (but didn't explicitly save) Kua got NO Kua pages in the PDF even
        // though edit mode showed them. Now: generate one item per saved
        // person, and let a matching saved override (same person, same Kua
        // number, with edited richHtml) win — exactly like enterKuaMode.
        const kuaSavedById = {};
        savedKua.forEach(k => { kuaSavedById[k.id] = k; });
        const kuaItems = nvPersons.length > 0
          ? nvPersons.map((p, i) => {
              const freshKua = calcKuaNumber(p.dob, p.gender);
              const s = kuaSavedById[p.id];
              // Reuse a saved override only if the user hand-edited it; an
              // untouched default (any language) is rebuilt in the currently
              // selected language so the PDF follows the dropdown.
              if (s && s.richHtml && s.kuaNumber === freshKua && !isDefaultKuaHtml(p, s.richHtml)) return s;
              return { id: p.id, personName: p.name || `Person ${i + 1}`, kuaNumber: freshKua, richHtml: buildKuaHtml(p, selectedLanguage) };
            })
          : savedKua;
        if (kuaItems.length > 0) {
          const parseKuaSections = (html) => {
            if (!html) return [];
            const parts = html.split(/<h2[^>]*>/i).filter(Boolean);
            return parts.slice(1).map(part => {
              const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
              if (!m) return null;
              const heading = m[1].replace(/<[^>]+>/g, '').trim();
              const pTags = m[2].match(/<p[^>]*>([\s\S]*?)<\/p>/gi) || [];
              const lines = pTags.map(p => p.replace(/<strong>/gi,'').replace(/<\/strong>/gi,'').replace(/<[^>]+>/g,'').replace(/&amp;/g,'&').replace(/&nbsp;/g,' ').replace(/\s+/g,' ').trim()).filter(Boolean);
              if (!lines.length) { const t = m[2].replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/\s+/g,' ').trim(); if (t) lines.push(t); }
              return heading || lines.length ? { heading, lines } : null;
            }).filter(Boolean);
          };

          // Description-tier (16pt) line height, scaled up from the old
          // 8.5pt body's 5.5mm the same way every other report's LINE_H was
          // rescaled when its font size grew.
          const KUA_LINE_H = 8.5, KUA_CX = 12, KUA_CW = a4Width - 24;
          const KUA_PAGE_BOT = a4Height - 22;

          for (const item of kuaItems) {
            pdf.addPage(); isFirstPage = false;
            addPageFrame(pdf, 'Kua Number Analysis');
            let cy = 25;

            // Person's own name is this page's item-identity — Subtitle tier.
            // (No summary line under it — Kua Number / Group / Element /
            // Personality all repeat verbatim in the PROFILE section right
            // below, so the one-liner was pure duplication.)
            drawLineSmartPdf(pdf, item.personName || 'Person', KUA_CX, cy + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
            cy += 14;
            pdf.setDrawColor(0,0,0); pdf.setLineWidth(0.4);
            pdf.line(KUA_CX, cy, a4Width - KUA_CX, cy);
            cy += 6;

            const kuaSections = parseKuaSections(item.richHtml || '');
            kuaSections.forEach(sec => {
              if (cy + 20 > KUA_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis'); cy = 25; }

              // Section heading — Description tier, bold, black.
              drawLineSmartPdf(pdf, sec.heading.toUpperCase(), KUA_CX, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              cy += KUA_LINE_H + 1;
              pdf.setDrawColor(0,0,0); pdf.setLineWidth(0.35);
              pdf.line(KUA_CX, cy, KUA_CX + KUA_CW, cy);
              cy += 4;

              sec.lines.forEach(line => {
                if (cy + KUA_LINE_H + 1 > KUA_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis'); cy = 25; }
                const ci = line.indexOf(':');
                if (ci > 0) {
                  const label = line.substring(0, ci).trim(), value = line.substring(ci + 1).trim();
                  const lblStr = label + ':  ';
                  pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica','bold');
                  const lblW = DEVANAGARI_RE.test(lblStr)
                    ? (() => { measureCtxPdf.font = hindiFontCssPdf(TYPE_DESCRIPTION_SIZE, 'bold'); return measureCtxPdf.measureText(lblStr).width / PX_PER_MM; })()
                    : pdf.getTextWidth(lblStr);
                  drawLineSmartPdf(pdf, lblStr, KUA_CX + 4, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                  // When the label ("Relationships/Romance, Marriage & Family
                  // Harmony (Nien Yen):") is nearly as wide as the whole
                  // content column, there's no room left to set the value
                  // beside it — it used to wrap ONE letter per line and run
                  // vertically off the right edge, bleeding across pages.
                  // Only keep the value inline when ≥40mm is free; otherwise
                  // drop it onto its own indented line(s) below.
                  const inlineAvail = KUA_CW - 8 - lblW;
                  if (inlineAvail >= 40) {
                    const valLines = wrapAnyPdf(pdf, value, TYPE_DESCRIPTION_SIZE, 'normal', inlineAvail);
                    drawLineSmartPdf(pdf, valLines[0], KUA_CX + 4 + lblW, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                    cy += KUA_LINE_H;
                    for (let i = 1; i < valLines.length; i++) {
                      if (cy + KUA_LINE_H > KUA_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis'); cy = 25; }
                      drawLineSmartPdf(pdf, valLines[i], KUA_CX + 4 + lblW + 2, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                      cy += KUA_LINE_H;
                    }
                  } else {
                    cy += KUA_LINE_H;
                    wrapAnyPdf(pdf, value, TYPE_DESCRIPTION_SIZE, 'normal', KUA_CW - 12).forEach(vl => {
                      if (cy + KUA_LINE_H > KUA_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis'); cy = 25; }
                      drawLineSmartPdf(pdf, vl, KUA_CX + 8, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                      cy += KUA_LINE_H;
                    });
                  }
                  cy += 1;
                } else {
                  const ls = wrapAnyPdf(pdf, line, TYPE_DESCRIPTION_SIZE, 'normal', KUA_CW - 8);
                  ls.forEach(l => {
                    if (cy + KUA_LINE_H > KUA_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis'); cy = 25; }
                    drawLineSmartPdf(pdf, l, KUA_CX + 4, cy + KUA_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                    cy += KUA_LINE_H;
                  });
                  cy += 1;
                }
              });
              cy += 5;
            });

            addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis');
            pageNumber++;
          }

          // ── Comparison Table — all persons side by side, one page ──────
          // Was on-screen only (KuaComparisonTable in VastuPersonsGateway.jsx)
          // until now — same Profile/Numerology/Direction rows, computed the
          // same way, just laid out as columns instead of separate pages.
          if (nvPersons.length > 0) {
            const kL = (k) => kuaLabel(k, selectedLanguage);
            const cmpRows = nvPersons.map(p => {
              const kua = calcKuaNumber(p.dob, p.gender);
              const moolank = calcMoolank(p.dob);
              const bhagyank = calcBhagyank(p.dob);
              const kd = kua ? KUA_DATA[kua] : null;
              const match = calcNumerologyMatch(moolank, bhagyank);
              return {
                name: p.name || 'Person', kua, kd, moolank, bhagyank, match,
                luckyColors: kuaColors(getLuckyColors(moolank, bhagyank), selectedLanguage),
                unluckyColors: kuaColors(getUnluckyColors(moolank, bhagyank), selectedLanguage),
              };
            });
            pdf.addPage(); isFirstPage = false;
            addPageFrame(pdf, 'Kua Number Analysis Comparison');
            let cmY = 25;
            const CMP_X = 10, CMP_LABEL_W = 62;
            const CMP_COL_W = Math.min(58, (a4Width - 20 - CMP_LABEL_W) / cmpRows.length);
            const CMP_ROW_H = 6.2, CMP_BOT = a4Height - 22;

            const cmpRow = (label, getVal, bold) => {
              if (cmY + CMP_ROW_H > CMP_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis Comparison'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis Comparison'); cmY = 25; }
              pdf.setFillColor(248, 250, 250); pdf.rect(CMP_X, cmY, CMP_LABEL_W, CMP_ROW_H, 'F');
              // Cell borders use the theme teal (same as the section bars), label cell included.
              pdf.setDrawColor(...REPORT_THEME_ACCENT); pdf.setLineWidth(0.15); pdf.rect(CMP_X, cmY, CMP_LABEL_W, CMP_ROW_H, 'S');
              const lblLines = wrapAnyPdf(pdf, label, 6.8, 'bold', CMP_LABEL_W - 4);
              drawLineSmartPdf(pdf, lblLines[0], CMP_X + 2, cmY + CMP_ROW_H / 2 + 1.3, 6.8, 'bold', [70, 70, 70]);
              cmpRows.forEach((r, i) => {
                const cx = CMP_X + CMP_LABEL_W + i * CMP_COL_W;
                pdf.setDrawColor(...REPORT_THEME_ACCENT); pdf.setLineWidth(0.15); pdf.rect(cx, cmY, CMP_COL_W, CMP_ROW_H, 'S');
                const val = String(getVal(r) ?? '—');
                const vLines = wrapAnyPdf(pdf, val, 6.8, bold ? 'bold' : 'normal', CMP_COL_W - 3);
                drawLineSmartPdf(pdf, vLines[0], cx + 1.5, cmY + CMP_ROW_H / 2 + 1.3, 6.8, bold ? 'bold' : 'normal', [20, 20, 20]);
              });
              cmY += CMP_ROW_H;
            };
            const cmpSection = (label) => {
              if (cmY + 7 > CMP_BOT) { addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis Comparison'); pageNumber++; pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis Comparison'); cmY = 25; }
              pdf.setFillColor(...REPORT_THEME_ACCENT); pdf.rect(CMP_X, cmY, a4Width - 2 * CMP_X, 6, 'F');
              drawLineSmartPdf(pdf, label.toUpperCase(), CMP_X + 2, cmY + 4.3, 7.5, 'bold', [255, 255, 255]);
              cmY += 6;
            };

            // Header row — person names
            pdf.setFillColor(0, 0, 0); pdf.rect(CMP_X, cmY, a4Width - 2 * CMP_X, 7, 'F');
            drawLineSmartPdf(pdf, 'ATTRIBUTE', CMP_X + 2, cmY + 5, 7, 'bold', [255, 255, 255]);
            cmpRows.forEach((r, i) => {
              const cx = CMP_X + CMP_LABEL_W + i * CMP_COL_W;
              const nLines = wrapAnyPdf(pdf, r.name, 7, 'bold', CMP_COL_W - 3);
              drawLineSmartPdf(pdf, nLines[0], cx + 1.5, cmY + 5, 7, 'bold', [255, 255, 255]);
            });
            cmY += 7;

            cmpSection(kL('profile'));
            cmpRow(kL('kuaNumber'), r => r.kua, true);
            cmpRow(kL('group'), r => r.kd ? kuaGroup(r.kd.group, selectedLanguage) : null);
            cmpRow(kL('element'), r => r.kd ? kuaElement(r.kd.element, selectedLanguage) : null);
            cmpRow(kL('personality'), r => r.kd ? kuaPersonality(r.kua, selectedLanguage) : null);
            cmpRow(kL('lifeArea'), r => r.kd ? kuaLifeArea(r.kua, selectedLanguage) : null);

            cmpSection(kL('numerology'));
            cmpRow(kL('moolank'), r => r.moolank, true);
            cmpRow(kL('bhagyank'), r => r.bhagyank, true);
            cmpRow(kL('luckyNumbers'), r => r.match?.lucky.join(', '));
            cmpRow(kL('neutralNumbers'), r => r.match?.neutral.join(', '));
            cmpRow(kL('unluckyNumbers'), r => r.match?.unlucky.join(', '));
            cmpRow(kL('luckyColors'), r => r.luckyColors);
            cmpRow(kL('unluckyColors'), r => r.unluckyColors);

            cmpSection(kL('favourableDirection'));
            cmpRow(kL('shengChi'), r => r.kd ? kuaDir(r.kd.favorable.shengChi, selectedLanguage) : null);
            cmpRow(kL('tienYi'), r => r.kd ? kuaDir(r.kd.favorable.tianYi, selectedLanguage) : null);
            cmpRow(kL('nienYen'), r => r.kd ? kuaDir(r.kd.favorable.nienYen, selectedLanguage) : null);
            cmpRow(kL('fuWei'), r => r.kd ? kuaDir(r.kd.favorable.fuWei, selectedLanguage) : null);

            cmpSection(kL('challengingDirection'));
            cmpRow(kL('hoHai'), r => r.kd ? kuaDir(r.kd.challenging.hoHai, selectedLanguage) : null);
            cmpRow(kL('wuGui'), r => r.kd ? kuaDir(r.kd.challenging.wuGui, selectedLanguage) : null);
            cmpRow(kL('liuSha'), r => r.kd ? kuaDir(r.kd.challenging.liuSha, selectedLanguage) : null);
            cmpRow(kL('jueMing'), r => r.kd ? kuaDir(r.kd.challenging.jueMing, selectedLanguage) : null);

            addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis Comparison');
            pageNumber++;
          }
        }
      } catch(e) {
        pdf.addPage(); addPageFrame(pdf, 'Kua Number Analysis');
        pdf.setFontSize(10); pdf.setTextColor(0,0,0); pdf.text('Error generating Kua Number Analysis report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Kua Number Analysis'); pageNumber++;
      }
    }

    // ── ASTRO VASTU REPORT — D1 chart, one page per person ──────────────────
    // This was previously entirely missing from the combined export — the
    // "Astro Vastu" sidebar toggle existed but nothing ever read it during
    // export, and it wasn't even registered as a default export layer.
    if (exportLayers['astro-vastu-report']?.enabled) {
      try {
        const personsRaw = localStorage.getItem(vastuPersonsStorageKey);
        const astroPersons = personsRaw ? JSON.parse(personsRaw).filter(p => p.name || p.dob) : [];
        const astroRaw = localStorage.getItem(astroVastuStorageKey);
        const astroDataMap = astroRaw ? JSON.parse(astroRaw) : {};
        // Which kundli style(s) the user ticked in the Astro Vastu editor (both by default).
        let avShowNorth = true, avShowSouth = true;
        try {
          const st = JSON.parse(localStorage.getItem(astroChartStyleStorageKey) || 'null');
          if (st && (st.north || st.south)) { avShowNorth = !!st.north; avShowSouth = !!st.south; }
        } catch (e) { /* keep both */ }

        if (astroPersons.length > 0) {
          const AV_TW = a4Width - 24, AV_TX = 12, AV_RH = 10;
          const AV_COL = [AV_TW * 0.08, AV_TW * 0.14, AV_TW * 0.28, AV_TW * 0.50];

          astroPersons.forEach((person, pi) => {
            pdf.addPage(); isFirstPage = false;
            addPageFrame(pdf, 'Astro Report');
            let cy = 25;
            const ad = astroDataMap[person.id] || { lagnaRashi: 0, houses: {} };
            // Self-heal stale/wrong-language Analysis Notes at export time —
            // VastuPersonsGateway's own re-translate effect only fires while
            // that gateway happens to be open when the language dropdown
            // changes. Generate/save the chart once, close the gateway,
            // switch language, then export from here (without reopening
            // Astro) and the saved notes stayed in whatever language they
            // were last built in — same class of bug fixed for Kua just
            // above and for Cut/Extension Zones (see drawCutExtensionReport).
            if (ad.lagnaRashi && isDefaultAstroHtml(ad.lagnaRashi, ad.houses || {}, getRashi, ad.richHtml)) {
              ad.richHtml = buildAstroAnalysisHtml(ad.lagnaRashi, ad.houses || {}, getRashi, selectedLanguage);
            }

            // Person's own name is this page's item-identity — Subtitle tier.
            // +16 (was +9) leaves a clear ≥20px breathing gap between the
            // 20pt name and the Gender · DOB · Place line right under it —
            // they were nearly touching before.
            drawLineSmartPdf(pdf, person.name || `Person ${pi + 1}`, a4Width / 2, cy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
            cy += 16;

            const parts = [];
            if (person.gender) parts.push(`Gender: ${person.gender}`);
            if (person.dob) { const dp = new Date(person.dob); parts.push(`DOB: ${!isNaN(dp) ? dp.toLocaleDateString('en-GB') : person.dob}`); }
            if (person.place) parts.push(`Place: ${person.place}`);
            if (parts.length) {
              pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...TYPE_BLACK);
              pdf.text(parts.join('   ·   '), a4Width / 2, cy, { align: 'center' });
              cy += 8;
            }
            cy += 3;
            pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.2);
            pdf.line(15, cy, a4Width - 15, cy);
            cy += 5;

            if (ad.lagnaRashi) {
              const lr = RASHIS[ad.lagnaRashi - 1];
              // drawLineSmartPdf, not raw pdf.text — rashiLabel can now
              // return a non-Latin script name once a non-English report
              // language is selected, and jsPDF's built-in font renders
              // those as mojibake.
              drawLineSmartPdf(pdf, `Lagna (Ascendant): ${lr.name} (${rashiLabel(lr.en, selectedLanguage)})`, AV_TX, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              cy += 8;

              // Moon Sign (Rasi) + birth Nakshatra, and the birth details the chart was calculated for —
              // the same two lines the live Astro editor shows above the chart.
              const moonHouseAv = Object.keys(ad.houses || {}).find((h) => (ad.houses[h] || []).includes('Moon'));
              const moonRashiAv = moonHouseAv ? getRashi(ad.lagnaRashi, Number(moonHouseAv)) : null;
              if (moonRashiAv) {
                const nakAv = ad.moonNakshatraNumber ? getNakshatra(ad.moonNakshatraNumber) : null;
                drawLineSmartPdf(pdf, `Moon Sign (Rasi): ${moonRashiAv.name} (${rashiLabel(moonRashiAv.en, selectedLanguage)})${nakAv ? `   ·   Nakshatra: ${nakAv.name}` : ''}`, AV_TX, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                cy += 7;
              }
              if (ad.calcMeta) {
                const cm = ad.calcMeta;
                const tzAv = Number(cm.timezone);
                drawLineSmartPdf(pdf, `Calculated for: ${cm.dob} · ${cm.tob} · ${String(cm.place || '').split(',').slice(0, 3).join(',')} (${Number(cm.latitude).toFixed(2)}°, ${Number(cm.longitude).toFixed(2)}° · UTC${tzAv >= 0 ? '+' : ''}${cm.timezone})`, AV_TX, cy, 7.5, 'normal', [100, 116, 139]);
                cy += 6;
              }
            }

            // 4 charts, 2×2 — D1 plus its 3 relabeled variants (same geometry,
            // same computed Ascendant/planets; only the per-house label
            // differs — see VastuChartVariants.js). Was drawing only the
            // plain D1 chart here — this combined/full-project export path
            // is separate from the Astro modal's own standalone "Download
            // PDF" button, so it needed the same 4-chart update independently.
            if (avShowNorth) {
              const AV_MINI = 85, AV_GAP = 10;
              const avGridW = AV_MINI * 2 + AV_GAP, avGridX = (a4Width - avGridW) / 2;
              const avAstroVastuLabels = computeAstroVastuDirections(ad.ascendantNormDegree || 0);
              // Same fixed direction/wall vocabulary translated as the live
              // D1Chart (see D1Chart's own comment in VastuPersonsGateway.jsx)
              // — the Ascendant/planet placements themselves still come
              // straight from the API untranslated.
              const avTranslateLabels = (houseLabels, translator) => {
                if (!houseLabels) return null;
                const out = {};
                Object.keys(houseLabels).forEach((h) => { out[h] = translator(houseLabels[h], selectedLanguage); });
                return out;
              };
              // Text size bumped to 1.5x current, matching the live-UI
              // D1Chart fix (explicit follow-up request to make the PDF
              // export match too).
              const avChartVariants = [
                { title: 'Chart D1',                 houseLabels: null,                                                          fontSize: 8.25 },
                { title: 'Vedic Vastu Chart',         houseLabels: avTranslateLabels(VEDIC_VASTU_DIRECTIONS, compassCodeLabel),   fontSize: 7.5 },
                { title: 'AstroVastu Chart',          houseLabels: avTranslateLabels(avAstroVastuLabels, compassCodeLabel),       fontSize: 6.5 },
                { title: 'Laal Kitaab Makaan Chart',  houseLabels: avTranslateLabels(LAAL_KITAAB_DIRECTIONS, laalKitaabLabel),    fontSize: 6.5 },
              ];
              avChartVariants.forEach((v, i) => {
                const cx = avGridX + (i % 2) * (AV_MINI + AV_GAP);
                const cyOff = cy + Math.floor(i / 2) * (AV_MINI + 10);
                drawNorthIndianChartPdf(pdf, { x: cx, y: cyOff, size: AV_MINI, lagnaRashi: ad.lagnaRashi, houses: ad.houses || {}, getRashi, houseLabels: v.houseLabels, labelFontSize: v.fontSize,
                  // drawLineSmartPdf here takes `pdf` as its own first arg
                  // (unlike VastuPersonsGateway.jsx's closure-bound
                  // drawLineSmart) — wrap it to match the drawText(text,
                  // x, y, fontSizePt, fontStyle, rgb, align) shape
                  // drawNorthIndianChartPdf expects.
                  drawText: (text, tx, ty, fs, st, rgb, align) => drawLineSmartPdf(pdf, text, tx, ty, fs, st, rgb, align) });
                // Chart caption — bumped bigger + bold, was unreadably small.
                pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); pdf.setTextColor(30, 30, 30);
                pdf.text(v.title, cx + AV_MINI / 2, cyOff + AV_MINI + 6, { align: 'center' });
              });
              cy += (AV_MINI + 10) * 2 + 4;
            }

            // The 4-chart grid fills the page — the reference table below it
            // used to overrun the footer and get clipped mid-row. Give the
            // table its own fresh page.
            // (When North Indian is switched off there is no chart grid on this page, so the table simply continues here.)
            if (avShowNorth) {
              addFooter(pdf, pageNumber, totalPages, 'Astro Report');
              pageNumber++;
              pdf.addPage();
              addPageFrame(pdf, 'Astro Report');
              cy = 25;
              drawLineSmartPdf(pdf, `${person.name || `Person ${pi + 1}`} — Houses, Rashis & Planets`, a4Width / 2, cy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
              cy += 16;
            }

            // Compact reference table below the chart — same data, easy to scan/print.
            pdf.setFillColor(0, 0, 0); pdf.rect(AV_TX, cy, AV_TW, 7, 'F');
            pdf.setTextColor(255, 255, 255); pdf.setFontSize(7); pdf.setFont('helvetica', 'bold');
            let hx = AV_TX;
            ['#', 'House', 'Rashi', 'Planets / Grahas'].forEach((h, i) => {
              pdf.text(h, hx + AV_COL[i] / 2, cy + 5, { align: 'center' });
              hx += AV_COL[i];
            });
            cy += 7;

            HOUSES.forEach((h, i) => {
              const rashi = getRashi(ad.lagnaRashi, h.num);
              const planets = ad.houses?.[h.num] || [];
              const rh = 7.2;
              pdf.setFillColor(...(i % 2 === 0 ? [255, 255, 255] : [248, 250, 252]));
              pdf.rect(AV_TX, cy, AV_TW, rh, 'F');
              pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.1); pdf.rect(AV_TX, cy, AV_TW, rh, 'S');
              let rx = AV_TX;
              const textY = cy + rh / 2 + 1.3;

              pdf.setTextColor(...REPORT_THEME_ACCENT); pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold');
              pdf.text(String(h.num), rx + AV_COL[0] / 2, textY, { align: 'center' });
              rx += AV_COL[0];
              pdf.setTextColor(30, 30, 30); pdf.setFont('helvetica', 'bold');
              pdf.text(h.name, rx + 2, textY);
              rx += AV_COL[1];
              // drawLineSmartPdf — same reason as the Lagna line above.
              if (rashi) drawLineSmartPdf(pdf, `${rashi.name} (${rashiLabel(rashi.en, selectedLanguage)})`, rx + 2, textY, 7.5, 'normal', [80, 30, 150]);
              else drawLineSmartPdf(pdf, '—', rx + 2, textY, 7.5, 'normal', [80, 30, 150]);
              rx += AV_COL[2];
              pdf.setTextColor(30, 30, 30);
              const pStr = planets.join(', ') || '—';
              const pLines = pdf.splitTextToSize(pStr, AV_COL[3] - 4);
              pdf.text(pLines[0], rx + 2, textY);
              cy += rh;
            });

            addFooter(pdf, pageNumber, totalPages, 'Astro Report');
            pageNumber++;

            // Same 4 charts again, on their own page, drawn South Indian
            // style — fixed Rashi cells with house numbers moving around
            // the Ascendant instead (mirrors VastuPersonsGateway.jsx's
            // standalone "Download PDF"; this combined export never had
            // South Indian charts until this explicit follow-up request).
            if (avShowSouth) {
              pdf.addPage(); isFirstPage = false;
              addPageFrame(pdf, 'Astro Report');
              cy = 25;
              drawLineSmartPdf(pdf, `${person.name || `Person ${pi + 1}`} — South Indian Chart`, a4Width / 2, cy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
              cy += 16;

              const SV_MINI = 85, SV_GAP = 10;
              const svGridW = SV_MINI * 2 + SV_GAP, svGridX = (a4Width - svGridW) / 2;
              const svAstroVastuLabels = computeAstroVastuDirections(ad.ascendantNormDegree || 0);
              // Own copy of the North Indian block's avTranslateLabels — that
              // one is scoped inside its own `{}` block above and isn't
              // visible here (same scope pitfall already hit once in
              // VastuPersonsGateway.jsx's downloadAstroPDF).
              const svTranslateLabels = (houseLabels, translator) => {
                if (!houseLabels) return null;
                const out = {};
                Object.keys(houseLabels).forEach((h) => { out[h] = translator(houseLabels[h], selectedLanguage); });
                return out;
              };
              const svChartVariants = [
                { title: 'Chart D1',                 houseLabels: null,                                                          fontSize: 8.25 },
                { title: 'Vedic Vastu Chart',         houseLabels: svTranslateLabels(VEDIC_VASTU_DIRECTIONS, compassCodeLabel),   fontSize: 7.5 },
                { title: 'AstroVastu Chart',          houseLabels: svTranslateLabels(svAstroVastuLabels, compassCodeLabel),       fontSize: 6.5 },
                { title: 'Laal Kitaab Makaan Chart',  houseLabels: svTranslateLabels(LAAL_KITAAB_DIRECTIONS, laalKitaabLabel),    fontSize: 6.5 },
              ];
              svChartVariants.forEach((v, i) => {
                const cx = svGridX + (i % 2) * (SV_MINI + SV_GAP);
                const cyOff = cy + Math.floor(i / 2) * (SV_MINI + 10);
                drawSouthIndianChartPdf(pdf, { x: cx, y: cyOff, size: SV_MINI, lagnaRashi: ad.lagnaRashi, houses: ad.houses || {}, houseLabels: v.houseLabels, labelFontSize: v.fontSize,
                  drawText: (text, tx, ty, fs, st, rgb, align) => drawLineSmartPdf(pdf, text, tx, ty, fs, st, rgb, align) });
                pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); pdf.setTextColor(30, 30, 30);
                pdf.text(v.title, cx + SV_MINI / 2, cyOff + SV_MINI + 6, { align: 'center' });
              });
              cy += (SV_MINI + 10) * 2 + 4;

              addFooter(pdf, pageNumber, totalPages, 'Astro Report');
              pageNumber++;
            }

            // ── Analysis Notes — was missing from this export entirely; the
            // chart + reference table drew fine but the actual written
            // interpretation (auto-drafted or hand-edited) never made it in.
            const parseAstroNoteSections = (html) => {
              if (!html) return [];
              const parts = html.split(/<h2[^>]*>/i).filter(Boolean);
              return parts.slice(1).map(part => {
                const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
                if (!m) return null;
                const heading = m[1].replace(/<[^>]+>/g, '').trim();
                const pTags = m[2].match(/<p[^>]*>([\s\S]*?)<\/p>/gi) || [];
                const lines = pTags.map(p => p.replace(/<strong>/gi, '').replace(/<\/strong>/gi, '').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
                if (!lines.length) { const t = m[2].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim(); if (t) lines.push(t); }
                return heading || lines.length ? { heading, lines } : null;
              }).filter(Boolean);
            };
            const noteSections = parseAstroNoteSections(ad.richHtml || '');
            if (noteSections.length) {
              const AN_LINE_H = 8.5, AN_CX = 12, AN_CW = a4Width - 24;
              const AN_PAGE_BOT = a4Height - 22;
              const analysisNotesTitlePdf = `Astro Vastu — ${kundaliTextLabel('Analysis Notes', selectedLanguage)}`;
              pdf.addPage(); isFirstPage = false;
              addPageFrame(pdf, analysisNotesTitlePdf);
              let ncy = 25;
              // This page's own item-identity line — Subtitle tier.
              drawLineSmartPdf(pdf, `${person.name || `Person ${pi + 1}`} — ${kundaliTextLabel('Analysis Notes', selectedLanguage)}`, AN_CX, ncy + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
              ncy += 20;

              noteSections.forEach(sec => {
                if (ncy + 20 > AN_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, analysisNotesTitlePdf); pageNumber++; pdf.addPage(); addPageFrame(pdf, analysisNotesTitlePdf); ncy = 25; }
                drawLineSmartPdf(pdf, sec.heading.toUpperCase(), AN_CX, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                ncy += AN_LINE_H + 1;
                pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.35);
                pdf.line(AN_CX, ncy, AN_CX + AN_CW, ncy);
                ncy += 4;

                sec.lines.forEach(line => {
                  if (ncy + AN_LINE_H + 1 > AN_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, analysisNotesTitlePdf); pageNumber++; pdf.addPage(); addPageFrame(pdf, analysisNotesTitlePdf); ncy = 25; }
                  const ci = line.indexOf(':');
                  if (ci > 0) {
                    const label = line.substring(0, ci).trim(), value = line.substring(ci + 1).trim();
                    const lblStr = label + ':  ';
                    pdf.setFontSize(TYPE_DESCRIPTION_SIZE); pdf.setFont('helvetica', 'bold');
                    const lblW = DEVANAGARI_RE.test(lblStr)
                      ? (() => { measureCtxPdf.font = hindiFontCssPdf(TYPE_DESCRIPTION_SIZE, 'bold'); return measureCtxPdf.measureText(lblStr).width / PX_PER_MM; })()
                      : pdf.getTextWidth(lblStr);
                    drawLineSmartPdf(pdf, lblStr, AN_CX + 4, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                    const valLines = wrapAnyPdf(pdf, value, TYPE_DESCRIPTION_SIZE, 'normal', AN_CW - 8 - lblW);
                    drawLineSmartPdf(pdf, valLines[0], AN_CX + 4 + lblW, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                    ncy += AN_LINE_H;
                    for (let i = 1; i < valLines.length; i++) {
                      if (ncy + AN_LINE_H > AN_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, analysisNotesTitlePdf); pageNumber++; pdf.addPage(); addPageFrame(pdf, analysisNotesTitlePdf); ncy = 25; }
                      drawLineSmartPdf(pdf, valLines[i], AN_CX + 4 + lblW + 2, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                      ncy += AN_LINE_H;
                    }
                    ncy += 1;
                  } else {
                    const ls = wrapAnyPdf(pdf, line, TYPE_DESCRIPTION_SIZE, 'normal', AN_CW - 8);
                    ls.forEach(l => {
                      if (ncy + AN_LINE_H > AN_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, analysisNotesTitlePdf); pageNumber++; pdf.addPage(); addPageFrame(pdf, analysisNotesTitlePdf); ncy = 25; }
                      drawLineSmartPdf(pdf, l, AN_CX + 4, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                      ncy += AN_LINE_H;
                    });
                    ncy += 1;
                  }
                });
                ncy += 5;
              });

              addFooter(pdf, pageNumber, totalPages, analysisNotesTitlePdf);
              pageNumber++;
            }

            // Kundali report sections — Vimsottari Table, Navatara Chakra,
            // House/Office Activity Mapping, General Suggestions. Same gap
            // as Analysis Notes above: this combined-export path is
            // independent of the Astro modal's own standalone "Download
            // PDF" button, so it needed these added here separately too.
            const formatDashaDatePdf = (iso) => {
              if (!iso) return '—';
              const d = new Date(iso);
              if (Number.isNaN(d.getTime())) return '—';
              return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
            };
            // Draws one OR several tables on a shared page. The Kundali tables
            // are short (≤ ~10 rows each), so giving each its own page left
            // most of every page blank — related tables are now merged onto
            // one page, and only spill to a continuation page (header row
            // repeated) if they genuinely don't fit above the footer zone.
            const drawKundaliTablesPdf = (tables, pageTitle) => {
              const pgTitle = pageTitle || tables.map(t => t.title).join(' & ');
              const ROW_PAGE_BOT = a4Height - 22;
              const personLabel = person.name || `Person ${pi + 1}`;
              pdf.addPage(); isFirstPage = false;
              addPageFrame(pdf, pgTitle);
              let ty = 25;
              const newContinuationPage = () => {
                addFooter(pdf, pageNumber, totalPages, pgTitle);
                pageNumber++;
                pdf.addPage();
                addPageFrame(pdf, pgTitle);
                ty = 25;
              };
              tables.forEach((t, ti) => {
                const { title, headers, colWidths, rows } = t;
                const tableW = colWidths.reduce((a, b) => a + b, 0);
                const TX2 = (a4Width - tableW) / 2;
                if (ti > 0) ty += 18; // gap between two tables sharing a page
                // keep this table's title + header + first row together
                if (ty + 14 + 7 + 7 > ROW_PAGE_BOT) newContinuationPage();
                // This table's own item-identity line — Subtitle tier.
                drawLineSmartPdf(pdf, `${personLabel} — ${title}`, 14, ty + 4, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'left');
                ty += 14;
                const drawHeaderRow = () => {
                  pdf.setFillColor(0, 0, 0); pdf.rect(TX2, ty, tableW, 7, 'F');
                  let hx = TX2;
                  // Left-align headers to sit exactly above their column's body
                  // cells (body cells are drawn at rx + 2, left-aligned).
                  headers.forEach((h, i) => { drawLineSmartPdf(pdf, h, hx + 2, ty + 5, 7, 'bold', [255, 255, 255]); hx += colWidths[i]; });
                  ty += 7;
                };
                drawHeaderRow();
                rows.forEach((row, ri) => {
                  if (ty + 7 > ROW_PAGE_BOT) { newContinuationPage(); drawHeaderRow(); }
                  // A table can name one row to highlight (e.g. the remedy planet recommended for this person).
                  const isHiRow = t.highlightRow === ri;
                  pdf.setFillColor(...(isHiRow ? [...REPORT_THEME_TINT] : ri % 2 === 0 ? [255, 255, 255] : [248, 250, 252]));
                  pdf.rect(TX2, ty, tableW, 7, 'F');
                  if (isHiRow) { pdf.setDrawColor(...REPORT_THEME_ACCENT); pdf.setLineWidth(0.4); } else { pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.1); }
                  pdf.rect(TX2, ty, tableW, 7, 'S');
                  let rx = TX2;
                  row.forEach((cell, ci) => {
                    // A cell can be { text, bg:[r,g,b] } — Hit Theory cells that have a hit
                    // get that hit's colour (the same one its code is shown in on screen) as
                    // a 70%-opacity background, drawn as a blend with white.
                    const hasBg = cell && typeof cell === 'object' && Array.isArray(cell.bg);
                    const cellVal = hasBg ? cell.text : cell;
                    if (hasBg) {
                      const blend = cell.bg.map(c => Math.round(255 * 0.3 + c * 0.7));
                      pdf.setFillColor(...blend);
                      pdf.rect(rx, ty, colWidths[ci], 7, 'F');
                      pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.1);
                      pdf.rect(rx, ty, colWidths[ci], 7, 'S');
                    }
                    const cellStyle = (ci === 0 || hasBg || isHiRow) ? 'bold' : 'normal';
                    const lines = wrapAnyPdf(pdf, String(cellVal ?? ''), 9.1, cellStyle, colWidths[ci] - 4);
                    drawLineSmartPdf(pdf, lines[0] || '', rx + 2, ty + 4.7, 9.1, cellStyle, hasBg ? [255, 255, 255] : isHiRow ? [...REPORT_THEME_TITLE] : [30, 30, 30]);
                    rx += colWidths[ci];
                  });
                  ty += 7;
                });
              });
              addFooter(pdf, pageNumber, totalPages, pgTitle);
              pageNumber++;
            };

            // Page 1: Vimsottari Table + Navatara Chakra together.
            const vimsottariTable = ad.vimshottari?.mahadashas?.length ? {
              title: kundaliTextLabel('Vimsottari Table', selectedLanguage), headers: [kundaliTextLabel('Planet', selectedLanguage), kundaliTextLabel('Start', selectedLanguage), kundaliTextLabel('End', selectedLanguage)], colWidths: [40, 70, 70],
              rows: ad.vimshottari.mahadashas.map(md => [planetLabel(md.lord, selectedLanguage), formatDashaDatePdf(md.start), formatDashaDatePdf(md.end)]),
            } : null;
            const navataraTable = ad.moonNakshatraNumber ? {
              title: kundaliTextLabel('Navatara Chakra', selectedLanguage), headers: [kundaliTextLabel('S.No.', selectedLanguage), kundaliTextLabel('Navatara', selectedLanguage), kundaliTextLabel('Lord', selectedLanguage), kundaliTextLabel('Nakshatra 1', selectedLanguage), kundaliTextLabel('Nakshatra 2', selectedLanguage), kundaliTextLabel('Nakshatra 3', selectedLanguage)],
              colWidths: [14, 40, 20, 34, 34, 34],
              rows: computeNavataraChakra(ad.moonNakshatraNumber).map(r => [r.number, navataraLabel(r.key, selectedLanguage), planetLabel(r.lord, selectedLanguage), r.nakshatras[0], r.nakshatras[1], r.nakshatras[2]]),
            } : null;
            const dashaAndNavatara = [vimsottariTable, navataraTable].filter(Boolean);
            if (dashaAndNavatara.length) drawKundaliTablesPdf(dashaAndNavatara, kundaliTextLabel('Vimsottari & Navatara Chakra', selectedLanguage));

            if (ad.moonNakshatraNumber) {
              // Page 2: Birth Nakshatra / Ruling Deity + Best Direction +
              // Astro-Vastu Plant Remedy — same classical lookups as the
              // equivalent Kundali sections in VastuPersonsGateway.jsx's own
              // standalone Astro PDF, all on ONE page.
              const birthNakPdf = getNakshatra(ad.moonNakshatraNumber);
              const nakshatraTables = [
                { title: kundaliTextLabel('Ruling Deity', selectedLanguage), headers: [kundaliTextLabel('Birth Nakshatra', selectedLanguage), kundaliTextLabel('Ruling Deity', selectedLanguage)], colWidths: [90, 90],
                  rows: [[birthNakPdf.name, deityLabel(getNakshatraDeity(ad.moonNakshatraNumber), selectedLanguage)]] },
                { title: kundaliTextLabel('Best Direction Table', selectedLanguage), headers: [kundaliTextLabel('Birth Nakshatra', selectedLanguage), kundaliTextLabel('Best Direction', selectedLanguage)], colWidths: [90, 90],
                  rows: [[birthNakPdf.name, directionLabel(getNakshatraBestDirection(ad.moonNakshatraNumber), selectedLanguage)]] },
              ];
              if (GRAHA_PLANT_REMEDY[birthNakPdf.lord]) {
                nakshatraTables.push({
                  title: kundaliTextLabel('Astro-Vastu Plant Remedy', selectedLanguage), headers: [kundaliTextLabel('Planet', selectedLanguage), kundaliTextLabel('Remedy Plant / Tree', selectedLanguage), kundaliTextLabel('Vastu Direction', selectedLanguage)], colWidths: [30, 90, 60],
                  rows: Object.entries(GRAHA_PLANT_REMEDY).map(([planet, r]) =>
                    [planetLabel(planet, selectedLanguage) + (planet === birthNakPdf.lord ? ' *' : ''), plantLabel(planet, selectedLanguage), directionLabel(r.direction, selectedLanguage)]),
                  highlightRow: Object.keys(GRAHA_PLANT_REMEDY).indexOf(birthNakPdf.lord),
                });
              }
              drawKundaliTablesPdf(nakshatraTables, kundaliTextLabel('Nakshatra, Deity & Plant Remedy', selectedLanguage));
            }

            // Hit Theory — same directed-angle grids as HitTheorySection /
            // VastuPersonsGateway.jsx's own standalone Astro PDF; Planet to
            // Planet + Planet to House now share one page.
            if (ad.hitTheory?.planetToPlanet && ad.hitTheory?.planetToHouse) {
              // Same colours as the on-screen Hit Theory grid's hit codes (HIT_CELL_FG).
              const HIT_BG_RGB = { positive: [21, 128, 61], negative: [185, 28, 28], neutral: [3, 105, 161] };
              const cellTextPdf = (v) => {
                if (!v) return '-';
                const text = `${v.displayAngle}°${v.hit ? ' ' + v.hit.code : ''}`;
                return v.hit && HIT_BG_RGB[v.hit.category] ? { text, bg: HIT_BG_RGB[v.hit.category] } : text;
              };
              drawKundaliTablesPdf([
                { title: kundaliTextLabel('Hit Theory — Planet to Planet', selectedLanguage), headers: [kundaliTextLabel('Planet', selectedLanguage), ...HIT_PLANETS.map(p => planetLabel(p, selectedLanguage))], colWidths: [24, ...HIT_PLANETS.map(() => 18)],
                  rows: HIT_PLANETS.map((source) => [planetLabel(source, selectedLanguage), ...HIT_PLANETS.map((target) => cellTextPdf(ad.hitTheory.planetToPlanet[source]?.[target]))]) },
                { title: kundaliTextLabel('Hit Theory — Planet to House', selectedLanguage), headers: [kundaliTextLabel('Planet', selectedLanguage), ...HIT_DISPLAY_HOUSES.map(h => `H${h}`)], colWidths: [24, ...HIT_DISPLAY_HOUSES.map(() => 13)],
                  rows: HIT_PLANETS.map((planet) => [planetLabel(planet, selectedLanguage), ...HIT_DISPLAY_HOUSES.map((h) => cellTextPdf(ad.hitTheory.planetToHouse[planet]?.[h]))]) },
              ], kundaliTextLabel('Hit Theory', selectedLanguage));
            }

            // General House Activity Mapping + Office/Shop/Factory Layout
            // Mapping share one page.
            if (ad.ashtakavarga?.bav) {
              const mapHeadersPdf = [kundaliTextLabel('Planet', selectedLanguage), kundaliTextLabel('Activity', selectedLanguage), directionLabel('East', selectedLanguage), directionLabel('West', selectedLanguage), directionLabel('North', selectedLanguage), directionLabel('South', selectedLanguage)];
              const mapColsPdf = [26, 74, 20, 20, 20, 20];
              // The ASC row's own activity text isn't a key in
              // ACTIVITY_LABELS — it has its own dedicated translator,
              // ascLabel(mode, lang) — same fix as the live editor's
              // ActivityMappingSection / VastuPersonsGateway.jsx's PDF.
              const mapRowsPdf = (kind) => buildActivityMapping(ad.ashtakavarga, ad.lagnaRashi, kind)
                .map(r => [planetLabel(r.planet, selectedLanguage) + (r.isApproximated ? ' *' : ''), r.planet === 'ASC' ? ascLabel(kind, selectedLanguage) : r.activities.map(a => activityLabel(a, selectedLanguage)).join(', '), r.east, r.west, r.north, r.south]);
              drawKundaliTablesPdf([
                { title: kundaliTextLabel('General House Activity Mapping', selectedLanguage), headers: mapHeadersPdf, colWidths: mapColsPdf, rows: mapRowsPdf('house') },
                { title: kundaliTextLabel('Office/Shop/Factory Layout Mapping', selectedLanguage), headers: mapHeadersPdf, colWidths: mapColsPdf, rows: mapRowsPdf('office') },
              ], kundaliTextLabel('House & Office Activity Mapping', selectedLanguage));
            }

            const suggestionsTextPdf = (ad.suggestionsHtml || '')
              .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
            if (suggestionsTextPdf) {
              pdf.addPage(); isFirstPage = false;
              const suggestionsTitlePdf = kundaliTextLabel('General Suggestions & Remedies', selectedLanguage);
              addPageFrame(pdf, suggestionsTitlePdf);
              let sy = 25;
              // This page's own item-identity line — Subtitle tier.
              drawLineSmartPdf(pdf, `${person.name || `Person ${pi + 1}`} — ${suggestionsTitlePdf}`, 14, sy + 4, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'left');
              sy += 15;
              const SUG_CX = 12, SUG_CW = a4Width - 24, SUG_PAGE_BOT = a4Height - 22;
              suggestionsTextPdf.split('\n').filter(l => l.trim()).forEach(line => {
                wrapAnyPdf(pdf, line.trim(), TYPE_DESCRIPTION_SIZE, 'normal', SUG_CW).forEach(l => {
                  if (sy + 8 > SUG_PAGE_BOT) { addFooter(pdf, pageNumber, totalPages, suggestionsTitlePdf); pageNumber++; pdf.addPage(); addPageFrame(pdf, suggestionsTitlePdf); sy = 25; }
                  drawLineSmartPdf(pdf, l, SUG_CX, sy + 7, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  sy += 8;
                });
              });
              addFooter(pdf, pageNumber, totalPages, suggestionsTitlePdf);
              pageNumber++;
            }
          });
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, 'Astro Report');
        pdf.setFontSize(10); pdf.setTextColor(0, 0, 0); pdf.text('Error generating Astro Vastu report.', 14, 30);
        addFooter(pdf, pageNumber, totalPages, 'Astro Report'); pageNumber++;
      }
    }

    // ── CUT ZONES / EXTENSION ZONES — auto-detected from the 16-Zone Bar
    // Chart's own min/max reference lines; each flagged zone gets its own
    // page with the (editable) Symptoms + Remedies text, no image.
    const drawCutExtensionReport = (mode) => {
      const layerKey = mode === 'cut' ? 'cut-zone-report' : 'extension-zone-report';
      if (!exportLayers[layerKey]?.enabled) return;
      const title = mode === 'cut' ? 'Cut Zones' : 'Extension Zones';
      // Same brown-gold used for every report page's own border/corner-brackets/
      // divider (REPORT_THEME_ACCENT) — Cut and Extension used to carry their
      // own orange/blue instead, which stood out from every other report page.
      const ACCENT = REPORT_THEME_ACCENT;
      const ACCENT_TINT = REPORT_THEME_ACCENT_LIGHT;
      try {
        const storageKey = `${mode === 'cut' ? 'cut_zone_data' : 'extension_zone_data'}_${activeProjectIdForStorage || 'new'}`;
        const raw = localStorage.getItem(storageKey);
        const zoneItems = raw ? JSON.parse(raw).filter(z => z.enabled !== false) : [];
        if (zoneItems.length === 0) return;

        // Self-heal stale/wrong-language saves at export time — the saved
        // richHtml only gets re-translated while the Cut/Extension Zone
        // Guide editor for THIS mode happens to be open when the language
        // dropdown changes (its own useEffect does that). If the user only
        // reopened Cut Zones (not Extension Zones, or vice versa) after
        // switching language, the other mode's saved data stays in whatever
        // language it was last generated in — reported as "Cut Zones show
        // Gujarati (garbled — see the font-detection fix above) but
        // Extension Zones still show plain English". Regenerating any
        // still-default (never hand-edited) zone here, in the CURRENTLY
        // selected language, right before drawing means the export is
        // always correct regardless of which editor was last opened.
        const zoneTextTable = mode === 'cut' ? CUT_ZONE_TEXT : EXTENSION_ZONE_TEXT;
        zoneItems.forEach((z) => {
          const zoneLabelForBuild = CE_ZONE_LABELS[z.zone] || z.zone;
          const txt = zoneTextTable[z.zone] || { symptom: '—', remedy: '—' };
          if (isDefaultZoneHtml(zoneLabelForBuild, mode, txt, z.richHtml)) {
            z.richHtml = buildZoneHtml(zoneLabelForBuild, mode, txt, selectedLanguage);
          }
        });

        const CX = 12, CW = a4Width - 24, LINE_H = 9.6;
        const PAGE_TOP = 24;
        const PAGE_BOT = a4Height - 22;
        const ITEM_GAP = 10;

        // Simple HTML -> [{heading, bodyText}] blocks (same parsing as before,
        // just pulled out so we can measure a zone's height before drawing it).
        const parseZoneBlocks = (html) => {
          const blocks = (html || '').split(/<h[23][^>]*>/i).filter(Boolean);
          const result = [];
          blocks.forEach((block, bi) => {
            if (bi === 0 && !/^</.test(html)) return; // stray leading text before first heading, skip
            const m = block.match(/^(.*?)<\/h[23]>([\s\S]*)/i);
            if (!m) return;
            const heading = m[1].replace(/<[^>]+>/g, '').trim();
            const bodyText = m[2].replace(/<\/p>|<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').trim();
            if (!heading && !bodyText) return;
            result.push({ heading, bodyText });
          });
          // The saved richHtml starts with a lone "<DIR> — EXTENSION ZONE" /
          // "<DIR> — CUT ZONE" heading (no text under it) that just repeats the
          // title block this page already prints — drop it so the zone name
          // doesn't appear twice. Real sections (Symptoms / Remedies) always
          // carry body text, so a heading-only first block is safe to remove.
          if (result.length && result[0].heading && !result[0].bodyText) result.shift();
          return result;
        };

        // Predict the rendered height of one zone (title block + all its
        // blocks) so 2 (sometimes more, sometimes 1) zones can be packed
        // onto a page instead of always starting a fresh page per zone —
        // that's what was leaving pages half-empty before.
        const calcZoneH = (blocks) => {
          let h = 21; // fixed title block: name/type line (Subtitle tier) + underline + area pill
          blocks.forEach(({ heading, bodyText }) => {
            if (heading) h += 3 + 8;
            if (bodyText) {
              bodyText.split('\n').filter(Boolean).forEach((line) => {
                h += wrapAnyPdf(pdf, line, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4).length * LINE_H;
              });
            }
            h += 5;
          });
          return h;
        };

        const startNewPage = () => {
          pdf.addPage(); isFirstPage = false;
          addPageFrame(pdf, title);
          return PAGE_TOP;
        };

        let curY = 0;

        zoneItems.forEach((z) => {
          const blocks = parseZoneBlocks(z.richHtml || '');
          // Only the title block + first heading and line must fit to start a
          // zone on the current page — the body below already breaks line by
          // line, so the next zone begins right where the previous one ended
          // instead of the whole zone jumping to a new page (blank gap).
          const zoneH = Math.min(calcZoneH(blocks), 21 + 11 + LINE_H);

          if (curY === 0) {
            curY = startNewPage();
          } else if (curY + ITEM_GAP + zoneH > PAGE_BOT) {
            addFooter(pdf, pageNumber, totalPages, title);
            pageNumber++;
            curY = startNewPage();
          } else {
            // separator line between zones sharing the same page
            pdf.setDrawColor(220, 224, 228); pdf.setLineWidth(0.25);
            pdf.line(CX + 20, curY + ITEM_GAP / 2, CX + CW - 20, curY + ITEM_GAP / 2);
            curY += ITEM_GAP;
          }

          let cy = curY;

          // ── Title block: this zone's own item-identity — Subtitle tier,
          // always black (the accent colour lives on the pill/underline
          // decoration below instead, same as every other report's badges).
          const zoneLabel = z.label || z.zone;
          const suffix = ` — ${title.slice(0, -1)}`;
          drawLineSmartPdf(pdf, zoneLabel + suffix, CX, cy + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
          cy += 11;

          // Small accent-coloured underline beneath the title (short, not full width)
          pdf.setFillColor(...ACCENT);
          pdf.rect(CX, cy, 26, 1, 'F');
          cy += 6;

          // Zone-area pill, right-aligned on the same row as the accent line's baseline
          const pillText = `ZONE AREA: ${z.area}${z.unit || ''}`;
          pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold');
          const pillW = pdf.getTextWidth(pillText) + 8;
          pdf.setFillColor(...ACCENT_TINT);
          pdf.roundedRect(a4Width - CX - pillW, cy - 9, pillW, 5.5, 1.2, 1.2, 'F');
          pdf.setTextColor(...ACCENT);
          pdf.text(pillText, a4Width - CX - pillW + 4, cy - 5.4);
          cy += 4;

          blocks.forEach(({ heading, bodyText }) => {
            if (cy + 14 > PAGE_BOT) {
              addFooter(pdf, pageNumber, totalPages, title);
              pageNumber++;
              cy = startNewPage();
            }
            if (heading) {
              cy += 3;
              // Small accent square (decoration only) + section label —
              // Description tier, bold, always black.
              pdf.setFillColor(...ACCENT);
              pdf.rect(CX, cy - 2.6, 2.6, 2.6, 'F');
              drawLineSmartPdf(pdf, heading.toUpperCase(), CX + 4.5, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              cy += 8;
            }
            bodyText.split('\n').filter(Boolean).forEach((line) => {
              const wrapped = wrapAnyPdf(pdf, line, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4);
              wrapped.forEach((wl) => {
                if (cy + LINE_H > PAGE_BOT) {
                  addFooter(pdf, pageNumber, totalPages, title);
                  pageNumber++;
                  cy = startNewPage();
                }
                drawLineSmartPdf(pdf, wl, CX, cy + LINE_H - 2, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                cy += LINE_H;
              });
            });
            cy += 5;
          });

          curY = cy;
        });

        // close last page
        if (curY > 0) {
          addFooter(pdf, pageNumber, totalPages, title);
          pageNumber++;
        }
      } catch (e) {
        pdf.addPage(); addPageFrame(pdf, title);
        pdf.setFontSize(10); pdf.setTextColor(0, 0, 0); pdf.text(`Error generating ${title} report.`, 14, 30);
        addFooter(pdf, pageNumber, totalPages, title); pageNumber++;
      }
    };
    drawCutExtensionReport('cut');
    drawCutExtensionReport('extension');

    // "With Best Wishes" closing page — the very last page of the export,
    // controlled by its own "Cover Back" toggle (independent of Cover Front).
    if (exportLayers['cover-back-page']?.enabled) {
      addClosingPage(pdf);
    }

    // Save the PDF
    const _pdfName = (project.currentProject?.name || 'Vastu_Report').replace(/[^a-zA-Z0-9_ -]/g, '').trim().replace(/\s+/g, '_');
    if (outputKind === 'word') {
      // Same report: page graphics as the background + every text as real, editable Word text — see utils/wordExport.js.
      const reportBuiltAt = performance.now();
      const wordTimings = {};
      const docxBlob = await pdfToDocxBlob(pdf.output('arraybuffer'), { title: `${_pdfName} Vastu Report`, texts: wordRecorder ? wordRecorder.ops : [], images: wordRecorder ? wordRecorder.images : [], timings: wordTimings });
      // Where the time went (open the browser console to read it): drawing the report, then the Word-specific steps.
      console.info(`[Word export] ${pdf.getNumberOfPages()} pages, ${wordRecorder ? wordRecorder.ops.length : 0} texts, ${wordRecorder ? wordRecorder.images.length : 0} pictures | ` +
        `report drawn: ${Math.round(reportBuiltAt - exportStartedAt)} ms | page backgrounds: ${wordTimings.backgroundsMs} ms | pictures: ${wordTimings.picturesMs} ms | ` +
        `packing .docx: ${wordTimings.packMs} ms | file ${(docxBlob.size / 1048576).toFixed(1)} MB | total ${Math.round(performance.now() - exportStartedAt)} ms`);
      await downloadBlob(docxBlob, `${_pdfName}_Vastu_Report.docx`);
    } else {
      pdf.save(`${_pdfName}_Vastu_Report.pdf`);
    }
    userAuthAPI.recordExport(); // bumps this user's export_count on the backend — best-effort, never blocks the download
    setIsGeneratingPDF(false);

  } catch (error) {
    setIsGeneratingPDF(false);
    // Log the real cause (this catch used to swallow it, so "Failed to export
    // PDF" gave nobody anything to go on) and show it after the generic line.
    console.error('PDF export failed:', error);
    const why = error && error.message ? `\n\n(${error.message})` : '';
    alert(`Failed to export ${outputKind === 'word' ? 'the Word file' : 'PDF'}. Please try again.${why}`);
  }
};
// getLayerName imported from ./Configure/constants/layerNames
useEffect(() => {
    // handleEditLayer refused (missing prerequisite) or the editor has closed: drop the flag so a later manual Edit is not auto-saved.
    if (autoCaptureLayer && !editingLayer) setAutoCaptureLayer(null);
  }, [autoCaptureLayer, editingLayer]);

const handleCancelLayerEdit = () => {
    setEditingLayer(null);
    setShow32Directions(false); // ?o. FIX: main canvas pe 16 directions hi dikhein
    setTempLayerConfig(null);
    // Note: savedLayerConfigs is NOT cleared here, preserving previous saves
};
  const roomTypes = ROOM_TYPES;

  const [isDeleteMode, setIsDeleteMode] = useState(false);
    const deleteModePulseAnimation = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
    `;
  const stages = STAGES;
  // Which single stage's bottom sheet is open on mobile (mobileSheet looks
  // like 'stage:<id>' — see MobileBottomToolbar's onOpenStage) — null when
  // no stage sheet is open, or when a non-stage sheet ('measurement' /
  // 'reports') is open instead. Each stage now gets its own dedicated
  // sheet on mobile (not one shared sheet listing all 6, which is what
  // desktop's accordion sidebar shows) — see the mobileFilteredStages note.
  const activeMobileStageId = (isMobile && mobileSheet && mobileSheet.startsWith('stage:'))
    ? mobileSheet.slice('stage:'.length)
    : null;
  const activeMobileStage = activeMobileStageId ? stages.find(s => s.id === activeMobileStageId) : null;
  // mainDirections, all32Directions, directions8, directions24 imported from ./Configure/constants/directions


  const calculate45AreaMeasurements = (blockNumber) => {
    // Check all required conditions
    if (!compassCenter || !isPolygonComplete || !isCalibrationSet || !pixelsPerUnit) {
      return {
        blockNumber,
        area: '0.00',
        perim: '0.00',
        unit: currentUnit,
        calibrationRequired: true,
        error: 'Missing requirements'
      };
    }

    let ringInner, ringOuter, directionIndices = [];
    
    if (blockNumber === 1) {
      // Center block (0% to 33.33%)
      ringInner = 0;
      ringOuter = 0.3333;
      directionIndices = Array.from({ length: 32 }, (_, i) => i);
    } else if (blockNumber >= 2 && blockNumber <= 5) {
      // Ring 2 blocks (33.33% to 50%)
      ringInner = 0.3333;
      ringOuter = 0.5;
      
      const quadrantMap = {
        2: [27, 28, 29, 30, 31, 0, 1, 2],     // W8→N7, closing at N8(3)
        3: [3, 4, 5, 6, 7, 8, 9, 10],         // N8→E7, closing at E8(11)
        4: [11, 12, 13, 14, 15, 16, 17, 18],  // E8→S7, closing at S8(19)
        5: [19, 20, 21, 22, 23, 24, 25, 26]   // S8→W7, closing at W8(27)
      };
      directionIndices = quadrantMap[blockNumber];
    } else if (blockNumber >= 6 && blockNumber <= 13) {
      // Ring 3 blocks (50% to 66.66%)
      ringInner = 0.5;
      ringOuter = 0.6666;

      const ring3Map = {
        6: [1, 2], 7: [3, 4], 8: [9, 10], 9: [11, 12],
        10: [17, 18], 11: [19, 20], 12: [25, 26], 13: [27, 28]
      };
      directionIndices = ring3Map[blockNumber];
    } else if (blockNumber >= 14 && blockNumber <= 45) {
      // Ring 4 blocks (66.66% to 100%)
      // ?o. FIX: +3 offset ??" Block 14 = N8 (index 3), Block 15 = E1 (index 4)
      ringInner = 0.6666;
      ringOuter = 1.0;
      directionIndices = [(blockNumber - 14 + 3) % 32]; // Single direction
    } else {
      return null;
    }

    // Build sector points
    const sectorPoints = [];
    
    // Add center only for innermost ring
    if (ringInner === 0) {
      sectorPoints.push(compassCenter);
    }
    
    // Get directions
    const directions = directionIndices.map(idx => all32Directions[idx]);
    
    // Add closing direction
    const closingIndex = (directionIndices[directionIndices.length - 1] + 1) % 32;
    const allDirections = [...directions, all32Directions[closingIndex]];
    
    // Calculate inner ring points
    allDirections.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection && ringInner > 0) {
        const innerX = compassCenter.x + (intersection.x - compassCenter.x) * ringInner;
        const innerY = compassCenter.y + (intersection.y - compassCenter.y) * ringInner;
        sectorPoints.push({ x: innerX, y: innerY });
      }
    });
    
    // Calculate outer ring points (reversed)
    const outerPoints = [];
    allDirections.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection) {
        const outerX = compassCenter.x + (intersection.x - compassCenter.x) * ringOuter;
        const outerY = compassCenter.y + (intersection.y - compassCenter.y) * ringOuter;
        outerPoints.push({ x: outerX, y: outerY });
      }
    });
    
    // Add outer points in reverse
    outerPoints.reverse().forEach(p => sectorPoints.push(p));
    
    if (sectorPoints.length < 3) {
      return null;
    }
    
    // Calculate area using shoelace formula (in pixels)
    let areaPixels = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      areaPixels += sectorPoints[i].x * sectorPoints[j].y;
      areaPixels -= sectorPoints[j].x * sectorPoints[i].y;
    }
    areaPixels = Math.abs(areaPixels / 2);
    
    // Calculate perim (in pixels)
    let perimPixels = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      const dx = sectorPoints[j].x - sectorPoints[i].x;
      const dy = sectorPoints[j].y - sectorPoints[i].y;
      perimPixels += Math.sqrt(dx * dx + dy * dy);
    }
    
    // Convert to real units
    const areaInUnits = areaPixels / (pixelsPerUnit * pixelsPerUnit);
    const perimInUnits = perimPixels / pixelsPerUnit;
    
    const result = {
      blockNumber,
      area: areaInUnits.toFixed(1),
      perim: perimInUnits.toFixed(1),
      unit: currentUnit,
      calibrationRequired: false,
      sectorPoints
    };
    return result;
  };

// LayerEditor component ????,
  const calculateBarChart16Data = () => {
    
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
      return [];
    }
    
    const data = [];
    
    // Calculate each direction
    for (let i = 0; i < mainDirections.length; i++) {
      const direction = mainDirections[i];
      const measurements = calculateDirectionMeasurements(direction.area);
      
      if (measurements && !measurements.calibrationRequired) {
        data.push({
          direction: direction.area,
          area: measurements.area,
          unit: currentUnit
        });
      } else {
        data.push({
          direction: direction.area,
          area: '0.00', 
          unit: currentUnit
        });
      }
    }
    
    return data.filter(item => parseFloat(item.area) > 0);
  };
// ?o. ADD THIS FUNCTION AFTER calculateBarChart16Data
const calculateBarChart8Data = () => {
  
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return [];
  }
  
  const data = [];
  
  // Calculate each direction
  for (let i = 0; i < directions8.length; i++) {
    const direction = directions8[i];
    const measurements = calculate8DirectionMeasurements(direction.area);
    
    if (measurements && !measurements.calibrationRequired) {
      data.push({
        direction: direction.area,
        area: measurements.area,
        unit: currentUnit
      });
    } else {
      data.push({
        direction: direction.area,
        area: '0.00', 
        unit: currentUnit
      });
    }
  }
  
  return data.filter(item => parseFloat(item.area) > 0);
};
  const calculateBarChart32Data = () => {

    
    if (!isPolygonComplete) {
      return null;
    }
    
    if (!compassCenter) {
      return null;
    }
    
    if (!isCalibrationSet) {
      return null;
    }
    
    if (!pixelsPerUnit) {
      return null;
    }
    

    // Dedicated function for 32 directions calculation
    const calculate32DirectionArea = (direction) => {
      const directionData = all32Directions.find(d => d.area === direction);
      
      if (!directionData) {
        return null;
      }

      const currentIndex = all32Directions.findIndex(d => d.area === direction);
      const nextIndex = (currentIndex + 1) % all32Directions.length;
      
      const currentAngle = directionData.angle;
      const nextAngle = all32Directions[nextIndex].angle;
      
      // Calculate middle angle
      let middleAngle = nextAngle < currentAngle 
        ? (currentAngle + nextAngle + 360) / 2 
        : (currentAngle + nextAngle) / 2;
      if (middleAngle >= 360) middleAngle -= 360;

      // Get intersections
      const mainIntersection = findPolygonIntersection(compassCenter, middleAngle);
      const leftIntersection = findPolygonIntersection(compassCenter, currentAngle);
      const rightIntersection = findPolygonIntersection(compassCenter, nextAngle);

      if (!mainIntersection || !leftIntersection || !rightIntersection) {
        return null;
      }

      // Build sector polygon correctly
      const sectorPoints = [compassCenter];
      
      // Helper function to normalize angle
      const normalizeAngle = (angle) => {
        let normalized = angle;
        while (normalized < -180) normalized += 360;
        while (normalized > 180) normalized -= 360;
        return normalized;
      };
      
      // Get angle of a point relative to center
      const getPointAngle = (point) => {
        const dx = point.x - compassCenter.x;
        const dy = point.y - compassCenter.y;
        let angle = (Math.atan2(dy, dx) * 180 / Math.PI) - compassRotation;
        return normalizeAngle(angle);
      };
      
      // Normalize sector boundaries
      const normalizedStart = normalizeAngle(currentAngle);
      const normalizedEnd = normalizeAngle(nextAngle);
      
      // Check if angle is within sector
      const isInSector = (testAngle) => {
        const normalized = normalizeAngle(testAngle);
        if (normalizedEnd > normalizedStart) {
          return normalized >= normalizedStart && normalized <= normalizedEnd;
        } else {
          // Handles wraparound at -180/180 boundary
          return normalized >= normalizedStart || normalized <= normalizedEnd;
        }
      };
      
      // Add left intersection
      sectorPoints.push(leftIntersection);
      
      // Collect all polygon points in sector with their angles
      const pointsWithAngles = polygonPoints
        .map((point, idx) => ({
          point,
          angle: getPointAngle(point),
          index: idx
        }))
        .filter(({ angle }) => isInSector(angle));
      
      // Sort points by angle
      pointsWithAngles.sort((a, b) => {
        let angA = a.angle;
        let angB = b.angle;
        
        // Handle wraparound case
        if (normalizedEnd < normalizedStart) {
          if (angA < 0) angA += 360;
          if (angB < 0) angB += 360;
        }
        
        return angA - angB;
      });
      
      // Add sorted points to sector
      pointsWithAngles.forEach(({ point }) => sectorPoints.push(point));
      
      // Add right intersection
      sectorPoints.push(rightIntersection);

      // Calculate area using shoelace formula (in pixels)
      let areaPixels = 0;
      for (let i = 0; i < sectorPoints.length; i++) {
        const j = (i + 1) % sectorPoints.length;
        areaPixels += sectorPoints[i].x * sectorPoints[j].y;
        areaPixels -= sectorPoints[j].x * sectorPoints[i].y;
      }
      areaPixels = Math.abs(areaPixels / 2);
      
      // Convert to real units
      const areaInUnits = areaPixels / (pixelsPerUnit * pixelsPerUnit);
      
      
      return {
        direction,
        area: areaInUnits.toFixed(1),
        unit: currentUnit,
        calibrationRequired: false,
        sectorPoints
      };
    };

    // Test with single direction first
    const testDirection = all32Directions[0];
    
    const testMeasurements = calculate32DirectionArea(testDirection.area);
    
    if (!testMeasurements) {
      return null;
    }

    // Now calculate all directions
    const data = all32Directions.map(({ area }) => {
      const measurements = calculate32DirectionArea(area);
      
      const areaValue = measurements ? measurements.area : '0.00';
      
      return {
        direction: area,
        area: areaValue,
        unit: currentUnit
      };
    });
    
    // Check if we have any non-zero data
    return data;
  };
  // Calculate all 45 blocks areas for Bar chart
  const calculateBarChart45Data = () => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
      return null;
    }
    
    const data = [];
    for (let blockNum = 1; blockNum <= 45; blockNum++) {
      const blockData = calculate45AreaMeasurements(blockNum);
      if (blockData && !blockData.calibrationRequired) {
        data.push({
          blockNumber: blockNum,
          area: blockData.area,
          unit: currentUnit
        });
      }
    }
    
    return data;
  };
const calculateBarChart452Data = () => {
  if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) {
    return null;
  }

  // ?o. FIX: Modern 45 inline ring calculation ??" 25/50/75/100 boundaries
  const calcModern45Block = (blockNumber) => {
    let ringInner, ringOuter, directionIndices = [];
    if (blockNumber === 1) {
      ringInner = 0; ringOuter = 0.25;
      directionIndices = Array.from({ length: 32 }, (_, i) => i);
    } else if (blockNumber >= 2 && blockNumber <= 5) {
      ringInner = 0.25; ringOuter = 0.5;
      const quadrantMap = { 2: [24,25,26,27,28,29,30,31,0], 3: [4,5,6,7,8], 4: [12,13,14,15,16], 5: [20,21,22,23,24] };
      directionIndices = quadrantMap[blockNumber];
    } else if (blockNumber >= 6 && blockNumber <= 13) {
      ringInner = 0.5; ringOuter = 0.75;
      const skipIndices = [28, 4, 12, 20];
      let validPairs = [];
      for (let i = 0; i < 32; i++) { if (!skipIndices.includes(i)) validPairs.push(i); }
      const pairIndex = (blockNumber - 6) * 2;
      directionIndices = [validPairs[pairIndex], validPairs[pairIndex + 1]];
    } else if (blockNumber >= 14 && blockNumber <= 45) {
      ringInner = 0.75; ringOuter = 1.0;
      directionIndices = [blockNumber - 14];
    } else { return null; }

    const sectorPoints = [];
    if (ringInner === 0) sectorPoints.push(compassCenter);
    const directions = directionIndices.map(idx => all32Directions[idx]);
    const closingIndex = (directionIndices[directionIndices.length - 1] + 1) % 32;
    const allDirs = [...directions, all32Directions[closingIndex]];

    allDirs.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection && ringInner > 0) {
        sectorPoints.push({
          x: compassCenter.x + (intersection.x - compassCenter.x) * ringInner,
          y: compassCenter.y + (intersection.y - compassCenter.y) * ringInner
        });
      }
    });
    const outerPoints = [];
    allDirs.forEach(dir => {
      const intersection = findPolygonIntersection(compassCenter, dir.angle);
      if (intersection) {
        outerPoints.push({
          x: compassCenter.x + (intersection.x - compassCenter.x) * ringOuter,
          y: compassCenter.y + (intersection.y - compassCenter.y) * ringOuter
        });
      }
    });
    outerPoints.reverse().forEach(p => sectorPoints.push(p));
    if (sectorPoints.length < 3) return null;

    let areaPixels = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      areaPixels += sectorPoints[i].x * sectorPoints[j].y;
      areaPixels -= sectorPoints[j].x * sectorPoints[i].y;
    }
    areaPixels = Math.abs(areaPixels / 2);
    return { area: (areaPixels / (pixelsPerUnit * pixelsPerUnit)).toFixed(1), calibrationRequired: false };
  };

  const allData = [];
  for (let blockNum = 1; blockNum <= 45; blockNum++) {
    const blockData = calcModern45Block(blockNum);
    if (blockData && !blockData.calibrationRequired) {
      allData.push({ blockNumber: blockNum, area: blockData.area, unit: currentUnit });
    }
  }

  // ?o. Split data into 3 groups for separate pages
  return {
    page1: allData.slice(0, 4),
    page2: allData.slice(4, 12),
    page3: allData.slice(12, 44),
    combined: allData
  };
};
  
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setCanvasDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);


// Add this useEffect after your other useEffects
useEffect(() => {
  // If polygon stage is open and no polygon exists yet, auto-start drawing
  if (openStages.has('polygon') && !isDrawingPolygon && !isPolygonComplete && polygonPoints.length === 0) {
    startPolygon();
  }
}, [openStages, polygonPoints.length]);


  const calculatePolygonArea = (points) => {
    if (points.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area / 2);
  }; 
const [hoveredMeasurementId, setHoveredMeasurementId] = useState(null);
  const handleDeleteMeasurement = (measurementId) => {
  setMeasurements(prev => prev.filter(m => m.id !== measurementId));
  setShowSuccessMessage(true);
  setSuccessMessage('Measurement deleted');
  setTimeout(() => setShowSuccessMessage(false), 2000);
};
  const calculateDirectionMeasurements = (direction) => {

    if (!compassCenter) {
      return null;
    }

    if (!isPolygonComplete) {
      return null;
    }

    if (polygonPoints.length < 3) {
      return null;
    }

    const directionsArray = mainDirections;

    const directionData = directionsArray.find(d => d.area === direction);

    if (!directionData) {
      return null;
    }

    const currentIndex = directionsArray.findIndex(d => d.area === direction);
    const nextIndex = (currentIndex + 1) % directionsArray.length;

    const currentAngle = directionData.angle;
    const nextAngle = directionsArray[nextIndex].angle;

    let middleAngle = nextAngle < currentAngle
      ? (currentAngle + nextAngle + 360) / 2
      : (currentAngle + nextAngle) / 2;
    if (middleAngle >= 360) middleAngle -= 360;

    const mainIntersection = findPolygonIntersection(compassCenter, middleAngle);
    const leftIntersection = findPolygonIntersection(compassCenter, currentAngle);
    const rightIntersection = findPolygonIntersection(compassCenter, nextAngle);

    if (!mainIntersection || !leftIntersection || !rightIntersection) {
      return null;
    }

    const sectorPoints = [compassCenter];

    const normalizeAngle = (angle) => {
      let normalized = angle;
      while (normalized < -180) normalized += 360;
      while (normalized > 180) normalized -= 360;
      return normalized;
    };

    const getPointAngle = (point) => {
      const dx = point.x - compassCenter.x;
      const dy = point.y - compassCenter.y;
      let angle = (Math.atan2(dy, dx) * 180 / Math.PI) - compassRotation;
      return normalizeAngle(angle);
    };

    const normalizedStart = normalizeAngle(currentAngle);
    const normalizedEnd = normalizeAngle(nextAngle);

    const isInSector = (testAngle) => {
      const normalized = normalizeAngle(testAngle);
      if (normalizedEnd > normalizedStart) {
        return normalized >= normalizedStart && normalized <= normalizedEnd;
      } else {
        return normalized >= normalizedStart || normalized <= normalizedEnd;
      }
    };

    sectorPoints.push(leftIntersection);

    const pointsWithAngles = polygonPoints
      .map((point, idx) => ({
        point,
        angle: getPointAngle(point),
        index: idx
      }))
      .filter(({ angle }) => isInSector(angle));

    pointsWithAngles.sort((a, b) => {
      let angA = a.angle;
      let angB = b.angle;

      if (normalizedEnd < normalizedStart) {
        if (angA < 0) angA += 360;
        if (angB < 0) angB += 360;
      }

      return angA - angB;
    });

    pointsWithAngles.forEach(({ point }) => sectorPoints.push(point));

    sectorPoints.push(rightIntersection);

    let area = 0;
    for (let i = 0; i < sectorPoints.length; i++) {
      const j = (i + 1) % sectorPoints.length;
      area += sectorPoints[i].x * sectorPoints[j].y;
      area -= sectorPoints[j].x * sectorPoints[i].y;
    }
    area = Math.abs(area / 2);

    if (!isCalibrationSet || !pixelsPerUnit) {
      return {
        direction,
        angle: Math.round(middleAngle),
        area: area.toFixed(1),
        sectorPoints,
        unit: 'pixels',
        calibrationRequired: true
      };
    }

    const areaInUnits = area / (pixelsPerUnit * pixelsPerUnit);

    return {
      direction,
      angle: Math.round(middleAngle),
      area: areaInUnits.toFixed(1),
      sectorPoints,
      unit: currentUnit,
      calibrationRequired: false
    };
  };

  // Cut / Extension Zones — reuses the exact same min-line/max-line formula
  // the 16-Zone Bar Chart itself already draws as reference lines
  // (avg = total/16, maxLine = (max+avg)/2, minLine = (min+avg)/2), so a
  // direction is flagged "Cut" if its area is below minLine, "Extension" if
  // above maxLine. Zero-area (uncalibrated) directions are excluded.
  const getCutExtensionZones = (mode) => {
    if (!isPolygonComplete || !compassCenter || !isCalibrationSet || !pixelsPerUnit) return [];
    const all = mainDirections.map(d => {
      const m = calculateDirectionMeasurements(d.area);
      return { zone: d.area, area: (m && !m.calibrationRequired) ? parseFloat(m.area) : 0, unit: currentUnit };
    }).filter(item => item.area > 0);
    if (all.length === 0) return [];

    const areaValues = all.map(i => i.area);
    const minValue = Math.min(...areaValues);
    const maxValue = Math.max(...areaValues);
    const avgValue = areaValues.reduce((s, v) => s + v, 0) / areaValues.length;
    const maxLine = parseFloat(((maxValue + avgValue) / 2).toFixed(1));
    const minLine = parseFloat(((minValue + avgValue) / 2).toFixed(1));

    return all
      .filter(i => {
        const rounded = parseFloat(i.area.toFixed(1));
        return mode === 'cut' ? rounded < minLine : rounded > maxLine;
      })
      .map(i => ({ zone: i.zone, area: i.area.toFixed(1), unit: i.unit }));
  };

// ?o. ADD THIS FUNCTION AFTER calculateDirectionMeasurements
const calculate8DirectionMeasurements = (direction) => {

  if (!compassCenter) {
    return null;
  }

  if (!isPolygonComplete) {
    return null;
  }

  if (polygonPoints.length < 3) {
    return null;
  }

  const directionData = directions8.find(d => d.area === direction);

  if (!directionData) {
    return null;
  }

  const currentIndex = directions8.findIndex(d => d.area === direction);
  const nextIndex = (currentIndex + 1) % directions8.length;

  const currentAngle = directionData.angle;
  const nextAngle = directions8[nextIndex].angle;

  let middleAngle = nextAngle < currentAngle
    ? (currentAngle + nextAngle + 360) / 2
    : (currentAngle + nextAngle) / 2;
  if (middleAngle >= 360) middleAngle -= 360;

  const mainIntersection = findPolygonIntersection(compassCenter, middleAngle);
  const leftIntersection = findPolygonIntersection(compassCenter, currentAngle);
  const rightIntersection = findPolygonIntersection(compassCenter, nextAngle);

  if (!mainIntersection || !leftIntersection || !rightIntersection) {
    return null;
  }

  const sectorPoints = [compassCenter];

  const normalizeAngle = (angle) => {
    let normalized = angle;
    while (normalized < -180) normalized += 360;
    while (normalized > 180) normalized -= 360;
    return normalized;
  };

  const getPointAngle = (point) => {
    const dx = point.x - compassCenter.x;
    const dy = point.y - compassCenter.y;
    let angle = (Math.atan2(dy, dx) * 180 / Math.PI) - compassRotation;
    return normalizeAngle(angle);
  };

  const normalizedStart = normalizeAngle(currentAngle);
  const normalizedEnd = normalizeAngle(nextAngle);

  const isInSector = (testAngle) => {
    const normalized = normalizeAngle(testAngle);
    if (normalizedEnd > normalizedStart) {
      return normalized >= normalizedStart && normalized <= normalizedEnd;
    } else {
      return normalized >= normalizedStart || normalized <= normalizedEnd;
    }
  };

  sectorPoints.push(leftIntersection);

  const pointsWithAngles = polygonPoints
    .map((point, idx) => ({
      point,
      angle: getPointAngle(point),
      index: idx
    }))
    .filter(({ angle }) => isInSector(angle));

  pointsWithAngles.sort((a, b) => {
    let angA = a.angle;
    let angB = b.angle;

    if (normalizedEnd < normalizedStart) {
      if (angA < 0) angA += 360;
      if (angB < 0) angB += 360;
    }

    return angA - angB;
  });

  pointsWithAngles.forEach(({ point }) => sectorPoints.push(point));

  sectorPoints.push(rightIntersection);

  let area = 0;
  for (let i = 0; i < sectorPoints.length; i++) {
    const j = (i + 1) % sectorPoints.length;
    area += sectorPoints[i].x * sectorPoints[j].y;
    area -= sectorPoints[j].x * sectorPoints[i].y;
  }
  area = Math.abs(area / 2);

  if (!isCalibrationSet || !pixelsPerUnit) {
    return {
      direction,
      angle: Math.round(middleAngle),
      area: area.toFixed(1),
      sectorPoints,
      unit: 'pixels',
      calibrationRequired: true
    };
  }

  const areaInUnits = area / (pixelsPerUnit * pixelsPerUnit);

  return {
    direction,
    angle: Math.round(middleAngle),
    area: areaInUnits.toFixed(1),
    sectorPoints,
    unit: currentUnit,
    calibrationRequired: false
  };
};

    // Add this function after calculateDirectionMeasurements
  const getDirectionFromPoint = (point) => {
    if (!compassCenter || !isPolygonComplete) return null;
    
    const dx = point.x - compassCenter.x;
    const dy = point.y - compassCenter.y;
    let angle = (Math.atan2(dy, dx) * 180 / Math.PI) - compassRotation;
    
    // Normalize angle
    while (angle < -180) angle += 360;
    while (angle > 180) angle -= 360;
    
    const directionsArray = show32Directions ? all32Directions : mainDirections;
    
    // Find which sector this angle belongs to
    for (let i = 0; i < directionsArray.length; i++) {
      const current = directionsArray[i];
      const next = directionsArray[(i + 1) % directionsArray.length];
      
      let currentAngle = current.angle;
      let nextAngle = next.angle;
      
      // Normalize angles
      while (currentAngle < -180) currentAngle += 360;
      while (currentAngle > 180) currentAngle -= 360;
      while (nextAngle < -180) nextAngle += 360;
      while (nextAngle > 180) nextAngle -= 360;
      
      // Check if angle is in this sector
      if (nextAngle > currentAngle) {
        if (angle >= currentAngle && angle < nextAngle) {
          return current.area;
        }
      } else {
        // Wraparound case
        if (angle >= currentAngle || angle < nextAngle) {
          return current.area;
        }
      }
    }
    
    return directionsArray[0].area; // Fallback
  };
  const getDirectionsFromPolygon = (points) => {
    if (!compassCenter || !isPolygonComplete || points.length < 3) return [];
    
    const directions = new Set();
    
    // Helper: Check if a point is in which direction
    const getPointDirection = (point) => {
      const dx = point.x - compassCenter.x;
      const dy = point.y - compassCenter.y;
      let angle = (Math.atan2(dy, dx) * 180 / Math.PI) - compassRotation;
      
      while (angle < -180) angle += 360;
      while (angle > 180) angle -= 360;
      
      const directionsArray = show32Directions ? all32Directions : mainDirections;
      
      for (let i = 0; i < directionsArray.length; i++) {
        const current = directionsArray[i];
        const next = directionsArray[(i + 1) % directionsArray.length];
        
        let currentAngle = current.angle;
        let nextAngle = next.angle;
        
        while (currentAngle < -180) currentAngle += 360;
        while (currentAngle > 180) currentAngle -= 360;
        while (nextAngle < -180) nextAngle += 360;
        while (nextAngle > 180) nextAngle -= 360;
        
        if (nextAngle > currentAngle) {
          if (angle >= currentAngle && angle < nextAngle) {
            return current.area;
          }
        } else {
          if (angle >= currentAngle || angle < nextAngle) {
            return current.area;
          }
        }
      }
      
      return directionsArray[0].area;
    };
    
    // 1. Check all corner points
    points.forEach(point => {
      const dir = getPointDirection(point);
      if (dir) directions.add(dir);
    });
    
    // 2. Check polygon center
    const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
    const centerDir = getPointDirection({ x: centerX, y: centerY });
    if (centerDir) directions.add(centerDir);
    
    // 3. CHECK EDGES - Sample points along each edge
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      
      // Sample 10 points along each edge
      for (let t = 0; t <= 1; t += 0.1) {
        const edgePoint = {
          x: p1.x + (p2.x - p1.x) * t,
          y: p1.y + (p2.y - p1.y) * t
        };
        const dir = getPointDirection(edgePoint);
        if (dir) directions.add(dir);
      }
    }
    
    // 4. CHECK INTERIOR - Sample points inside polygon using grid
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    
    const gridSteps = 20; // Increase for more accuracy
    const stepX = (maxX - minX) / gridSteps;
    const stepY = (maxY - minY) / gridSteps;
    
    // Point-in-polygon check function
    const isPointInPolygon = (testPoint) => {
      let inside = false;
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const xi = points[i].x, yi = points[i].y;
        const xj = points[j].x, yj = points[j].y;
        
        const intersect = ((yi > testPoint.y) !== (yj > testPoint.y))
          && (testPoint.x < (xj - xi) * (testPoint.y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };
    
    // Sample grid points inside polygon
    for (let x = minX; x <= maxX; x += stepX) {
      for (let y = minY; y <= maxY; y += stepY) {
        const testPoint = { x, y };
        if (isPointInPolygon(testPoint)) {
          const dir = getPointDirection(testPoint);
          if (dir) directions.add(dir);
        }
      }
    }
    
    return Array.from(directions).sort();
  };
  const intersectRayWithSegment = (rayOrigin, rayDir, segmentStart, segmentEnd) => {
    const s = {
      x: segmentEnd.x - segmentStart.x,
      y: segmentEnd.y - segmentStart.y
    };
    
    const denom = rayDir.x * s.y - rayDir.y * s.x;
    
    if (Math.abs(denom) < 1e-10) {
      return null;
    }
    
    const dx = segmentStart.x - rayOrigin.x;
    const dy = segmentStart.y - rayOrigin.y;
    
    const t = (dx * s.y - dy * s.x) / denom;
    const u = (dx * rayDir.y - dy * rayDir.x) / denom;
    
    if (t >= 0 && u >= -0.001 && u <= 1.001) {
      return {
        x: rayOrigin.x + t * rayDir.x,
        y: rayOrigin.y + t * rayDir.y,
        distance: t
      };
    }
    
    return null;
  };
  const handleMouseMove = (e) => {
    if (isDrawingPolygon) {
      const stage = e.target.getStage();
      const pointerPos = stage.getPointerPosition();
      setMagnifierPosition(pointerPos);
    } else {
      setMagnifierPosition(null);
    }
  };
  const findPolygonIntersection = (center, angleDegrees) => {
    if (!isPolygonComplete || polygonPoints.length < 3) {
      return null;
    }

    const angleRad = ((angleDegrees + compassRotation) * Math.PI) / 180;
    const rayDir = {
      x: Math.cos(angleRad),
      y: Math.sin(angleRad)
    };

    let nearestIntersection = null;
    let minDistance = Infinity;

    for (let i = 0; i < polygonPoints.length; i++) {
      const j = (i + 1) % polygonPoints.length;
      const p1 = polygonPoints[i];
      const p2 = polygonPoints[j];

      const intersection = intersectRayWithSegment(center, rayDir, p1, p2);

      if (intersection && intersection.distance < minDistance && intersection.distance > 0.1) {
        minDistance = intersection.distance;
        nearestIntersection = intersection;
      }
    }

    return nearestIntersection;
  };

  const calculatePolygonCenter = useCallback(() => {
    if (polygonPoints.length < 3) return null;

    let signedArea = 0;
    let cx = 0;
    let cy = 0;
    const n = polygonPoints.length;

    for (let i = 0; i < n; i++) {
      const x0 = polygonPoints[i].x;
      const y0 = polygonPoints[i].y;
      const x1 = polygonPoints[(i + 1) % n].x;
      const y1 = polygonPoints[(i + 1) % n].y;
      
      const crossProduct = (x0 * y1) - (x1 * y0);
      
      signedArea += crossProduct;
      cx += (x0 + x1) * crossProduct;
      cy += (y0 + y1) * crossProduct;
    }

    signedArea *= 0.5;

    if (Math.abs(signedArea) < 1e-10) {
      let fallbackX = 0, fallbackY = 0;
      for (const point of polygonPoints) {
        fallbackX += point.x;
        fallbackY += point.y;
      }
      const center = { 
        x: fallbackX / polygonPoints.length, 
        y: fallbackY / polygonPoints.length 
      };
      setCompassCenter(center);
      return center;
    }

    cx /= (6 * signedArea);
    cy /= (6 * signedArea);

    const center = { x: cx, y: cy };
    setCompassCenter(center);
    return center;
  }, [polygonPoints]);

  const calculatePerpendicularIntersections = useCallback(() => {
    if (!compassCenter || polygonPoints.length < 3) return;

    const intersections = [];
    const angleStep = 1; // Check every 1 degree
    const perpendicularTolerance = 2; // ±2° tolerance for perpendicularity

    // Cast rays from 0° to 360°
    for (let rayAngle = 0; rayAngle < 360; rayAngle += angleStep) {
      // Adjust for compass rotation
      const adjustedAngle = rayAngle - compassRotation;
      
      // Ray direction
      const rayRad = (adjustedAngle * Math.PI) / 180;
      const rayDir = { x: Math.cos(rayRad), y: Math.sin(rayRad) };
      
      // Check intersection with each polygon edge
      for (let i = 0; i < polygonPoints.length; i++) {
        const j = (i + 1) % polygonPoints.length;
        const p1 = polygonPoints[i];
        const p2 = polygonPoints[j];
        
        // Ray-segment intersection
        const intersection = intersectRayWithSegment(
          compassCenter, 
          rayDir, 
          p1, 
          p2
        );
        
        if (intersection && intersection.distance > 0.1) {
          // Calculate edge vector
          const edgeVec = { x: p2.x - p1.x, y: p2.y - p1.y };
          const edgeLength = Math.sqrt(edgeVec.x * edgeVec.x + edgeVec.y * edgeVec.y);
          const edgeNorm = { x: edgeVec.x / edgeLength, y: edgeVec.y / edgeLength };
          
          // Calculate angle between ray and edge
          const dotProduct = rayDir.x * edgeNorm.x + rayDir.y * edgeNorm.y;
          const angleBetween = Math.abs(Math.acos(Math.abs(dotProduct)) * 180 / Math.PI);
          
          // Check if perpendicular (90° ±2°)
          const isPerpendicular = Math.abs(angleBetween - 90) <= perpendicularTolerance;
          
          if (isPerpendicular) {
            const existingIndex = intersections.findIndex(item => item.edgeIndex === i);
            const edgeMidX = (p1.x + p2.x) / 2;
            const edgeMidY = (p1.y + p2.y) / 2;

            if (existingIndex === -1) {
              intersections.push({
                id: `edge-${i}`,
                angle: adjustedAngle,
                intersection: intersection,
                edgeIndex: i,
                edgeMidpoint: { x: edgeMidX, y: edgeMidY },
                rayAngle: rayAngle,
                _perpErr: Math.abs(angleBetween - 90)
              });
            } else if (Math.abs(angleBetween - 90) < (intersections[existingIndex]._perpErr || Infinity)) {
              // Better (closer to true perpendicular) found for same edge
              intersections[existingIndex] = {
                ...intersections[existingIndex],
                angle: adjustedAngle,
                intersection: intersection,
                edgeMidpoint: { x: edgeMidX, y: edgeMidY },
                rayAngle: rayAngle,
                _perpErr: Math.abs(angleBetween - 90)
              };
            }
          }
        }
      }
    }
    

    // Remove vertex-hit duplicates: two adjacent edges can both match the same
    // vertex point via the u-tolerance in intersectRayWithSegment. Keep only the
    // entry closest to a true perpendicular (lowest _perpErr).
    const PROXIMITY_PX = 8;
    const deduped = intersections.filter((item, idx) => {
      for (let j = 0; j < intersections.length; j++) {
        if (j === idx) continue;
        const other = intersections[j];
        const dx = item.intersection.x - other.intersection.x;
        const dy = item.intersection.y - other.intersection.y;
        if (Math.sqrt(dx * dx + dy * dy) < PROXIMITY_PX && other._perpErr < item._perpErr) {
          return false;
        }
      }
      return true;
    });

    setCompassLines(deduped);
    setAngleInputs(deduped.map(int => ({ id: int.id, angle: int.angle })));
  }, [compassCenter, polygonPoints, compassRotation, intersectRayWithSegment]);

    useEffect(() => {
      if (isPolygonComplete && showCompass && compassCenter && polygonPoints.length >= 3) {
        calculatePerpendicularIntersections();
      }
    }, [isPolygonComplete, showCompass, compassCenter, polygonPoints, compassRotation, calculatePerpendicularIntersections]);

    useEffect(() => {
      if (showCompass && isPolygonComplete && !selectedDirection) {
        const directionsArray = show32Directions ? all32Directions : mainDirections;
        const firstDirection = calculateDirectionMeasurements(directionsArray[0].area);
        if (firstDirection) {
          setSelectedDirection(firstDirection);
        }
      }
    }, [showCompass, isPolygonComplete, show32Directions, selectedDirection, calculateDirectionMeasurements]);
      const updateCompassAngle = (id, newAngle) => {
        const numValue = parseFloat(newAngle) || 0;
        
        // Find the line being edited
        const line = compassLines.find(l => l.id === id);
        if (!line || !line.intersection) return;

        // Calculate the vector from center to intersection
        const dx = line.intersection.x - compassCenter.x;
        const dy = line.intersection.y - compassCenter.y;
        
        // Calculate actual angle from North (0°), clockwise
        // In canvas coordinates: 
        // - Y increases downward (opposite of mathematical)
        // - X increases rightward
        let actualAngleFromNorth = (Math.atan2(-dy, dx) * 180 / Math.PI + 360) % 360;
        
        // Adjust for canvas coordinate system (Y is inverted)
        actualAngleFromNorth = (90 - actualAngleFromNorth + 360) % 360;
        
        // Calculate the rotation needed to match the desired angle
        const angleDiff = numValue - actualAngleFromNorth;
        
        // Apply the rotation difference
        const newRotation = (compassRotation + angleDiff + 360) % 360;
        
        // Update the rotation
        setCompassRotation(newRotation);
        
        // Update the stored angle for this line
        setAngleInputs(prev => 
          prev.map(input => 
            input.id === id ? { ...input, angle: numValue } : input
          )
        );
      };

      // In the return statement where angles are displayed, replace the calculation with:
      const calculateDisplayedAngle = (intersection) => {
        if (!intersection) return 0;
        
        const dx = intersection.x - compassCenter.x;
        const dy = intersection.y - compassCenter.y;
        
        // Calculate angle from North (0°), clockwise
        let angleFromNorth = (Math.atan2(-dy, dx) * 180 / Math.PI + 360) % 360;
        angleFromNorth = (90 - angleFromNorth + 360) % 360;
        
        // Apply current compass rotation
        const displayedAngle = (angleFromNorth - compassRotation + 360) % 360;
        
        return Math.round(displayedAngle);
      };
  const handlePointDragStart = (index) => {
    setIsDraggingPolygonPoint(true);
    setSelectedPolygonPoint(index);
  };

  const handlePointDragMove = (index, e) => {
    const newPoints = [...polygonPoints];
    newPoints[index] = { 
      x: e.target.x(), 
      y: e.target.y(), 
      id: newPoints[index].id 
    };
    setPolygonPoints(newPoints);
  };

  const handlePointDragEnd = () => {
    setIsDraggingPolygonPoint(false);
    setSelectedPolygonPoint(null);
    if (isPolygonComplete) {
      calculatePolygonCenter();
    }
  };

  const handleStageClick = (e) => {
    if (isDraggingPolygonPoint) return;
    
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const clickedShape = e.target;
    
    // ENTRANCE MODE - Click two points
    if (isSettingEntrance) {
      const newPoint = { x: pointerPos.x, y: pointerPos.y };
      
      if (entrancePoints.length === 0) {
        setEntrancePoints([newPoint]);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 2000);
      } else if (entrancePoints.length === 1) {
        const midX = (entrancePoints[0].x + newPoint.x) / 2;
        const midY = (entrancePoints[0].y + newPoint.y) / 2;
        const midPoint = { x: midX, y: midY };
        
        // Get all directions covered by entrance line
        const allDirections = [];
        
        // Sample points along the entrance line
        for (let t = 0; t <= 1; t += 0.05) {
          const linePoint = {
            x: entrancePoints[0].x + (newPoint.x - entrancePoints[0].x) * t,
            y: entrancePoints[0].y + (newPoint.y - entrancePoints[0].y) * t
          };
          const dir = getDirectionFromPoint(linePoint);
          if (dir && !allDirections.includes(dir)) {
            allDirections.push(dir);
          }
        }
        
        const mainDirection = getDirectionFromPoint(midPoint);
        
        setEntranceData({
          points: [entrancePoints[0], newPoint],
          midPoint,
          direction: mainDirection,
          allDirections: allDirections.length > 0 ? allDirections : [mainDirection]
        });
        
        setIsSettingEntrance(false);
        setEntrancePoints([]);
        
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
      return;
    }
    
    // ROOM POLYGON DRAWING MODE
    if (currentRoomPolygon.isDrawing) {
      // Check if clicking first point to close
      if (clickedShape.getClassName() === 'Circle' && currentRoomPolygon.points.length >= 3) {
        const clickedIndex = currentRoomPolygon.points.findIndex((point) => {
          const dx = point.x - pointerPos.x;
          const dy = point.y - pointerPos.y;
          return Math.sqrt(dx * dx + dy * dy) < 10;
        });
        
        if (clickedIndex === 0) {
          // Complete room polygon
          const directions = getDirectionsFromPolygon(currentRoomPolygon.points);
          
          const completedRoom = {
            ...currentRoomPolygon,
            isDrawing: false,
            isComplete: true,
            id: Date.now(),
            directions: directions
          };
          
          setRoomLayers(prev => [...prev, completedRoom]);
          setCurrentRoomPolygon({
            points: [],
            isDrawing: false,
            isComplete: false,
            roomType: null,
            color: null
          });
          
          const roomLabel = roomTypes.find(r => r.id === completedRoom.roomType)?.label;
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 4000);
          return;
        }
      }
      
      // Add new point
      if (clickedShape.getClassName() !== 'Circle') {
        setCurrentRoomPolygon(prev => ({
          ...prev,
          points: [...prev.points, { x: pointerPos.x, y: pointerPos.y, id: Date.now() }]
        }));
      }
      return;
    }
    
    // MEASUREMENT MODE
    if (measurementMode) {
      handleMeasurementClick(e);
      return;
    }

    // MAIN POLYGON DRAWING
    if (!isDrawingPolygon) return;
    
    if (clickedShape.getClassName() === 'Circle' && polygonPoints.length >= 3) {
      const clickedIndex = polygonPoints.findIndex((point) => {
        const dx = point.x - pointerPos.x;
        const dy = point.y - pointerPos.y;
        return Math.sqrt(dx * dx + dy * dy) < 10;
      });
      
      if (clickedIndex === 0) {
        finishPolygon();
        return;
      }
      return;
    }
    
    if (clickedShape.getClassName() === 'Circle') return;
    
    setPolygonPoints([...polygonPoints, { x: pointerPos.x, y: pointerPos.y, id: Date.now() }]);
  };




// parseDistanceInput imported from ./Configure/utils/measurementHelpers

const handleMeasurementClick = (e) => {
  const stage = e.target.getStage();
  const pointerPos = stage.getPointerPosition();
  
  const newPoint = { x: pointerPos.x, y: pointerPos.y, id: Date.now() };

  if (measurementPoints.length === 0) {
    setMeasurementPoints([newPoint]);
  } else if (measurementPoints.length === 1) {
    const firstPoint = measurementPoints[0];
    const distance = Math.sqrt(
      Math.pow(pointerPos.x - firstPoint.x, 2) + 
      Math.pow(pointerPos.y - firstPoint.y, 2)
    );

    if (!isCalibrationSet) {
      const promptText = currentUnit === 'ft' 
        ? `Enter distance (e.g., 10'7, 10.5):` 
        : `Enter the real-world distance (in ${currentUnit}):`;
      
      const realDistance = prompt(promptText);
      
      if (realDistance) {
        const realDistanceValue = parseDistanceInput(realDistance, currentUnit);
        
        if (!isNaN(realDistanceValue) && realDistanceValue > 0) {
          const newPixelsPerUnit = distance / realDistanceValue;
          
          setPixelsPerUnit(newPixelsPerUnit);
          setIsCalibrationSet(true);
          
          // ?o. Format display value
          const displayValue = currentUnit === 'ft'
            ? `${Math.floor(realDistanceValue)}'${Math.round((realDistanceValue - Math.floor(realDistanceValue)) * 12)}"`
            : realDistanceValue.toFixed(1);
          
          const calibrationMeasurement = {
            id: Date.now(),
            points: [firstPoint, newPoint],
            pixelDistance: distance,
            realDistance: realDistanceValue,
            displayValue: displayValue, // ?o. Store formatted value
            unit: currentUnit,
            isCalibration: true
          };
          
          setMeasurements([calibrationMeasurement]);
          setMeasurementPoints([]);
          setMeasurementMode(false);

          // ?o. Show converted value in success message
          setShowSuccessMessage(true);
          setSuccessMessage(`?o. Calibration: ${displayValue} ${currentUnit} = ${realDistanceValue.toFixed(1)} ft`);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        } else {
          alert('?O Invalid input. Example: 10\'7 or 10.5');
          setMeasurementPoints([]);
        }
      } else {
        setMeasurementPoints([]);
      }
    } else {
      const realDistance = distance / pixelsPerUnit;
      
      // ?o. Format display value
      const displayValue = currentUnit === 'ft'
        ? `${Math.floor(realDistance)}'${Math.round((realDistance - Math.floor(realDistance)) * 12)}"`
        : realDistance.toFixed(1);
      
      const newMeasurement = {
        id: Date.now(),
        points: [firstPoint, newPoint],
        pixelDistance: distance,
        realDistance: realDistance,
        displayValue: displayValue, // ?o. Store formatted value
        unit: currentUnit,
        isCalibration: false
      };
      
      setMeasurements(prev => [...prev, newMeasurement]);
      setMeasurementPoints([]);
      setMeasurementMode(false);

      // ?o. Show converted value
      setShowSuccessMessage(true);
      setSuccessMessage(`Measurement: ${displayValue} ${currentUnit} = ${realDistance.toFixed(1)} ft`);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  }
};
const startPolygon = () => {
  setPolygonPoints([]);
  setIsDrawingPolygon(true);
  setIsPolygonComplete(false);
  setShowPolygonLayer(true);
  setShowPolygonPoints(true); // ?o. Ensure points are visible when starting
};

  const finishPolygon = () => {
    if (polygonPoints.length >= 3) {
      setIsDrawingPolygon(false);
      setIsPolygonComplete(true);
      calculatePolygonCenter();
      
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  const resetPolygon = () => {
    setPolygonPoints([]);
    setIsDrawingPolygon(false);
    setIsPolygonComplete(false);
    setSelectedPolygonPoints(new Set());
    setIsMultiSelectMode(false);
    setCompassCenter(null);
  };

  const resetCompass = () => {
    setShowCompass(false);
    setCompassRotation(0);
    setCompassOpacity(0.7);
    setCompassLineOpacity(1);
    setShowCompassLines(true);
    setShowSectorAreas(true);
    setCompassLineWeight(2);
    setCompassColor("#00A19D");
    setCompassLines([]);
    setAngleInputs([]);
  };

const startSetMeasurement = () => {
  setMeasurementMode(true);
  setMeasurementPoints([]);
  setShowMeasurementDropdown(false);
  setIsDeleteMode(false); // ?o. Turn off delete mode
};

const startGetMeasurement = () => {
  if (!isCalibrationSet) {
    alert("Please set measurement calibration first!");
    return;
  }
  setMeasurementMode(true);
  setMeasurementPoints([]);
  setShowMeasurementDropdown(false);
  setIsDeleteMode(false); // ?o. Turn off delete mode
};

  const cancelMeasurement = () => {
    setMeasurementMode(false);
    setMeasurementPoints([]);
  };

const resetMeasurement = () => {
  setMeasurements([]);
  setMeasurementPoints([]);
  setMeasurementMode(false);
  setPixelsPerUnit(null);
  setIsCalibrationSet(false);
  setIsDeleteMode(false); // ?o. Turn off delete mode
  setSelectedMeasurementForDelete(null);
  setHoveredMeasurementId(null);
};

const toggleStage = (stageId) => {
      if (stageId === 'space-analysis') {
      setOpenSpaceAnalysisModal(true);
    }
      if (stageId === 'entrance-analysis') {
      setOpenEntranceAnalysisModal(true);
    }
      if (stageId === 'vastu-devta-guide') {
      setOpenVastuDevtaGuideModal(true);
    }
      if (stageId === 'navgrah-shanti') {
      setOpenNavgrahShantiModal(true);
    }
      if (stageId === 'feng-shui') {
      setOpenFengShuiModal(true);
    }
      if (stageId === 'zone-analysis') {
      setOpenZoneAnalysisModal(true);
    }
      if (stageId === 'astro-numero') {
      setOpenBirthDateModal(true);
      return;
    }
  setOpenStages(prev => {
    const newStages = new Set();
    if (!prev.has(stageId)) {
      newStages.add(stageId);
      
      // Auto-activate compass when compass stage opens (if polygon is complete)
      if (stageId === 'compass' && isPolygonComplete) {
        setShowCompass(true);
        setShowPolygonPoints(false);
        setShowPerpendicularLines(true);
      }
      
    // Auto-activate polygon drawing when polygon stage opens
    if (stageId === 'polygon') {
      // Always show polygon points when polygon stage opens
      setShowPolygonPoints(true);
      
      // Auto-start drawing ONLY if no polygon exists yet
      if (!isDrawingPolygon && !isPolygonComplete && polygonPoints.length === 0) {
        startPolygon();
      }
    }

      
      if (stageId === 'unit' && isPolygonComplete) {
        setShowPolygonPoints(false);
      }
    } else {
      // When closing the polygon stage
      if (stageId === 'polygon') {
        setShowPolygonPoints(false);
        setSelectedPolygonPoint(null);
        setSelectedPolygonPoints(new Set());
        setIsMultiSelectMode(false);
      }
    }
    return newStages;
  });
};

  // Bottom-toolbar stage icon tap, mobile only. toggleStage() is the real
  // "open this stage" function either way — some stage ids (space-analysis,
  // entrance-analysis, astro-numero) never touch openStages at all and
  // instead pop open their own separate full-screen modal (see toggleStage
  // above); the rest (unit, polygon, compass) render inline into the
  // sidebar via renderStageContent(), which on mobile now shows inside a
  // per-stage bottom sheet instead. Calling toggleStage() for the inline
  // ones too (not just setMobileSheet) matters — openStages is what drives
  // real behaviour elsewhere (auto-starting polygon drawing, enabling the
  // compass, etc.), not just what's visually shown.
  const INLINE_STAGE_IDS = new Set(['unit', 'polygon', 'compass']);
  const handleMobileStageIconClick = (stageId) => {
    toggleStage(stageId);
    if (INLINE_STAGE_IDS.has(stageId)) {
      setMobileSheet(`stage:${stageId}`);
    }
  };

  const handleCanvasZoomIn = () => {
    setCanvasScale(prev => Math.min(Math.round((prev + 0.25) * 100) / 100, 3));
  };

  const handleCanvasZoomOut = () => {
    setCanvasScale(prev => Math.max(Math.round((prev - 0.25) * 100) / 100, 0.5));
  };

  const handleCanvasZoomReset = () => setCanvasScale(1);
  const canvasZoomed = canvasScale !== 1;

  // ── Canvas sharpness ─────────────────────────────────────────────────────────────────────────
  // Zoom is a CSS scale on the canvas element, so the browser just enlarges the canvas pixels and the
  // floor plan goes blurry. Each Konva layer is therefore drawn with MORE real pixels (a higher pixel
  // ratio) — at 100% too (2x supersampling, then the browser shrinks it smoothly) and growing with the
  // zoom level (capped at 3x so memory stays sane). Hit-testing is unaffected (hit canvas stays 1x).
  // Runs after every render so layers that mount later (e.g. compass layer) get the same treatment.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof stage.getLayers !== 'function') return;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    let target = isMobile ? dpr : Math.max(2, dpr);
    target = Math.max(target, Math.ceil(dpr * Math.max(1, canvasScale)));
    target = Math.min(target, Math.max(3, dpr));
    stage.getLayers().forEach((layer) => {
      const canvas = layer.getCanvas && layer.getCanvas();
      if (!canvas) return;
      let changed = false;
      if (Math.abs(canvas.getPixelRatio() - target) > 0.01) { canvas.setPixelRatio(target); changed = true; }
      const ctx = layer.getContext && layer.getContext() && layer.getContext()._context;
      if (ctx) { ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'; }
      if (changed) layer.batchDraw();
    });
  });

  // ── Zoom only the floor plan, never the page ─────────────────────────────────────────────────
  // Ctrl + mouse-wheel (and a touchpad pinch, which the browser reports as ctrl + wheel) used to zoom the
  // WHOLE page — left and right panels included. Over the canvas it now zooms just the canvas.
  const [mainViewportEl, setMainViewportEl] = useState(null);
  useEffect(() => {
    if (!mainViewportEl) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;      // plain wheel keeps scrolling the canvas box
      e.preventDefault();                          // stops the browser page zoom
      const d = Math.max(-25, Math.min(25, e.deltaY));
      setCanvasScale((prev) => Math.min(3, Math.max(0.5, Math.round(prev * Math.exp(-d * 0.01) * 100) / 100)));
    };
    mainViewportEl.addEventListener('wheel', onWheel, { passive: false });
    return () => mainViewportEl.removeEventListener('wheel', onWheel);
  }, [mainViewportEl]);

  useEffect(() => {
    if (isPolygonComplete) {
      calculatePolygonCenter();
    }
  }, [isPolygonComplete, calculatePolygonCenter]);

  // Slider with a teal "filled" part up to the thumb and a light grey rest (see .stage-range CSS below).
  const rangeFillStyle = (value, min, max) => {
    const p = Math.max(0, Math.min(100, ((Number(value) - Number(min)) / (Number(max) - Number(min))) * 100));
    return { width: '100%', background: `linear-gradient(to right, #00A19D 0%, #00A19D ${p}%, #DDE3EA ${p}%, #DDE3EA 100%)` };
  };

  const renderStageContent = (stageId) => {
    if (!openStages.has(stageId)) return null;

    switch (stageId) {
      case "unit":
        return (
          <div style={{ padding: '12px' }}>
            {/* mm unit commented out per request — the conversion code that handles an old project saved in mm is untouched. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
              {["ft", "m" /* , "mm" */].map((unit) => (
                <label key={unit} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '10px 6px',
                  border: '1px solid #00A19D',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: currentUnit === unit ? '#E6F6F5' : '#ffffff',
                  borderColor: currentUnit === unit ? '#00A19D' : '#D5DBE3',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="unit"
                    value={unit}
                    checked={currentUnit === unit}
                    onChange={(e) => setCurrentUnit(e.target.value)}
                    style={{ accentColor: '#00A19D', width: '12px', height: '12px' }}
                  />
                  <span style={{ color: '#00A19D', fontSize: '0.75rem', fontWeight: 500 }}>
                    {unit.toUpperCase()}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );

      case "polygon":
        return (
          <div style={{ padding: '12px' }}>
            <button
              onClick={isDrawingPolygon ? finishPolygon : startPolygon}
              style={{
                background: isDrawingPolygon ? '#00A19D' : '#00A19D',
                border: 'none',
                color: '#ffffff',
                padding: '10px 12px',
                borderRadius: '8px',
                cursor: 'pointer',
                width: '100%',
                marginBottom: '12px',
                fontSize: '0.8rem',
                fontWeight: 600,
                boxShadow: '0 3px 10px rgba(0,161,157,0.28)',
                transition: 'all 0.2s ease'
              }}
            >
              {isDrawingPolygon ? "Finish Drawing" : "Start Drawing"}
            </button>
            
            <ToggleSwitch
              checked={showPolygonLayer}
              onChange={() => setShowPolygonLayer(!showPolygonLayer)}
              label="Show Polygon Layer"
            />
            
            {showPolygonLayer && (
              <div>
                <ToggleSwitch
                  checked={showPolygonPoints}
                  onChange={() => setShowPolygonPoints(!showPolygonPoints)}
                  label="Boundary Pin Numbers"
                />
                
                <ToggleSwitch
                  checked={showCenterPoint}
                  onChange={() => setShowCenterPoint(!showCenterPoint)}
                  label="Show Center Point"
                />
                
                <div style={{ marginTop: '12px', padding: '12px 12px 4px', background: '#F6FAFA', border: '1px solid #E1EFEE', borderRadius: '10px' }}>
                  {/* Mobile: same "button hands off to a control floating
                      over the fully-visible canvas" treatment as Compass —
                      see the floating bar rendered near MobileBottomToolbar.
                      Desktop keeps every original inline slider untouched. */}
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button onClick={() => { setMobileSheet(null); setMobilePolygonControl('lineColor'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                        <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Color</span>
                        <span style={{ width: '20px', height: '20px', borderRadius: '4px', border: '1px solid #ECEEF2', background: polygonColor }} />
                      </button>
                      <button onClick={() => { setMobileSheet(null); setMobilePolygonControl('lineWeight'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                        <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Line Weight</span>
                        <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{polygonLineWeight}px</span>
                      </button>
                      <button onClick={() => { setMobileSheet(null); setMobilePolygonControl('lineOpacity'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                        <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Line Opacity</span>
                        <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{Math.round(polygonLineOpacity * 100)}%</span>
                      </button>
                      <button onClick={() => { setMobileSheet(null); setMobilePolygonControl('opacity'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                        <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Opacity</span>
                        <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{Math.round(polygonOpacity * 100)}%</span>
                      </button>
                    </div>
                  ) : (
                    <>
                  {/* Color Picker */}
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Color:</label>
                    <input
                      type="color"
                      value={polygonColor}
                      onChange={(e) => setPolygonColor(e.target.value)}
                      style={{ width: '36px', height: '28px', padding: '2px', border: '1px solid #CFE3E2', borderRadius: '8px', cursor: 'pointer', background: '#ffffff' }}
                    />
                  </div>

                  {/* Line Weight */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Line Weight:</label>
                      <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                        {polygonLineWeight}px
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={polygonLineWeight}
                      onChange={(e) => setPolygonLineWeight(Number(e.target.value))}
                      className="stage-range" style={rangeFillStyle(polygonLineWeight, 1, 10)}
                    />
                  </div>

                  {/* Line Opacity */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Line Opacity:</label>
                      <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                        {Math.round(polygonLineOpacity * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.05"
                      max="1"
                      step="0.05"
                      value={polygonLineOpacity}
                      onChange={(e) => setPolygonLineOpacity(Number(e.target.value))}
                      className="stage-range" style={rangeFillStyle(polygonLineOpacity, 0.05, 1)}
                    />
                  </div>

                  {/* Opacity */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Opacity:</label>
                      <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                        {Math.round(polygonOpacity * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={polygonOpacity}
                      onChange={(e) => setPolygonOpacity(Number(e.target.value))}
                      className="stage-range" style={rangeFillStyle(polygonOpacity, 0.1, 1)}
                    />
                  </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "compass":
        return (
          <div style={{ padding: '12px' }}>
            <ToggleSwitch
              checked={showCompass}
              onChange={() => setShowCompass(!showCompass)}
              label="Show Compass"
            />
            
            {showCompass && (
              <div style={{ marginTop: '12px', padding: '12px 12px 4px', background: '#F6FAFA', border: '1px solid #E1EFEE', borderRadius: '10px' }}>
                {/* Mobile: every one of these 5 controls used to be a
                    slider/color-picker dragged right here inside the sheet —
                    but even backdrop-less the sheet still sits over the
                    bottom ~40% of the canvas, so you couldn't see the
                    compass rose actually react while dragging. Each is now a
                    button that hands off to that ONE control floating
                    directly over the (now fully visible) canvas instead —
                    see the floating bar rendered near MobileBottomToolbar.
                    Desktop keeps every original inline slider untouched. */}
                {isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                    <button onClick={() => { setMobileSheet(null); setMobileCompassControl('rotation'); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                      <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Rotation</span>
                    </button>
                    <button onClick={() => { setMobileSheet(null); setMobileCompassControl('lineColor'); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                      <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Line Color</span>
                      <span style={{ width: '20px', height: '20px', borderRadius: '4px', border: '1px solid #ECEEF2', background: compassColor }} />
                    </button>
                    <button onClick={() => { setMobileSheet(null); setMobileCompassControl('lineWeight'); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                      <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Line Weight</span>
                      <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{compassLineWeight}px</span>
                    </button>
                    <button onClick={() => { setMobileSheet(null); setMobileCompassControl('lineOpacity'); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                      <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Line Opacity</span>
                      <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{Math.round(compassLineOpacity * 100)}%</span>
                    </button>
                    <button onClick={() => { setMobileSheet(null); setMobileCompassControl('opacity'); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDFC', border: '1px solid #00A19D', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}>
                      <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 600 }}>Change Text Opacity</span>
                      <span style={{ color: '#00A19D', fontSize: '0.78rem', fontWeight: 700 }}>{Math.round(compassOpacity * 100)}%</span>
                    </button>
                  </div>
                ) : (
                  <>
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Rotation:</label>
                    {/* Angle value hidden per request — slider below still fully works */}
                    {/* <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                      {Math.round(compassRotation)}°
                    </span> */}
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={compassRotation}
                    onChange={(e) => {
                      setCompassRotation(Number(e.target.value));
                    }}
                    className="stage-range" style={rangeFillStyle(compassRotation, 0, 360)}
                  />
                </div>

                {/* Line Color */}
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Line Color:</label>
                  <input
                    type="color"
                    value={compassColor}
                    onChange={(e) => setCompassColor(e.target.value)}
                    style={{ width: '36px', height: '28px', padding: '2px', border: '1px solid #CFE3E2', borderRadius: '8px', cursor: 'pointer', background: '#ffffff' }}
                  />
                </div>

                {/* Line Weight */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Line Weight:</label>
                    <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                      {compassLineWeight}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={compassLineWeight}
                    onChange={(e) => setCompassLineWeight(Number(e.target.value))}
                    className="stage-range" style={rangeFillStyle(compassLineWeight, 1, 10)}
                  />
                </div>

                {/* Line Opacity */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Line Opacity:</label>
                    <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                      {Math.round(compassLineOpacity * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.05"
                    max="1"
                    step="0.05"
                    value={compassLineOpacity}
                    onChange={(e) => setCompassLineOpacity(Number(e.target.value))}
                    className="stage-range" style={rangeFillStyle(compassLineOpacity, 0.05, 1)}
                  />
                </div>

                {/* Opacity */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ color: '#3A4257', fontSize: '0.78rem', fontWeight: 600, textAlign: 'left' }}>Text Opacity:</label>
                    <span style={{ color: '#00857E', background: '#E6F6F5', fontSize: '0.72rem', fontWeight: 700, minWidth: '38px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px' }}>
                      {Math.round(compassOpacity * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={compassOpacity}
                    onChange={(e) => setCompassOpacity(Number(e.target.value))}
                    className="stage-range" style={rangeFillStyle(compassOpacity, 0.1, 1)}
                  />
                </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
        case "space-analysis":
          return (
            <div style={{ padding: '12px' }}>
              <div style={{
                border: '1px solid #ECEEF2',
                borderRadius: '8px',
                padding: '0px',
                background: '#00A19D',
                minHeight: '0px'
              }}>
                {/* <SpaceAnalysis> itself is now mounted independently, further
                    below (search "always mounted so it can open from
                    anywhere") — same fix already applied to VastuDevtaGuide /
                    NavgrahShanti / FengShui / ZoneAnalysis. It used to live
                    right here, which was the ONLY place it ever got mounted;
                    on mobile the whole "Layout Stages" sidebar this switch
                    feeds is wrapped in a MobileSheetWrapper that only opens
                    for stageId in INLINE_STAGE_IDS ('unit'/'polygon'/
                    'compass') — space-analysis isn't in that set, so its
                    sheet never opened, this case never rendered, and
                    <SpaceAnalysis> never mounted at all on mobile: tapping
                    the toolbar icon (or the Reports-sheet row) flipped
                    openSpaceAnalysisModal to true with no component left in
                    the tree to read it — silently doing nothing. */}
                <button
                  onClick={() => setOpenSpaceAnalysisModal(true)}  // ?o. ADD THIS
                  style={{
                    background: '#00857E',
                    color: '#ffffff',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    width: '100%',
                    transition: 'background 0.3s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#00857E'}
                  onMouseLeave={(e) => e.target.style.background = '#00857E'}
                >
                  <DoorOpen size={16} /> Space Analysis
                </button>
              </div>
            </div>
          );

        case "entrance-analysis":
          return (
            <div style={{ padding: '12px' }}>
              <div style={{
                border: '1px solid #ECEEF2',
                borderRadius: '8px',
                padding: '0px',
                background: '#00A19D',
                minHeight: '0px'
              }}>
                {/* <EntranceAnalysis> itself is now mounted independently,
                    further below — see the identical note in the
                    "space-analysis" case just above for why. */}
                <button
                  onClick={() => setOpenEntranceAnalysisModal(true)}  // ?o. ADD THIS
                  style={{
                    background: '#00857E',
                    color: '#ffffff',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    width: '100%',
                    transition: 'background 0.3s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#00857E'}
                  onMouseLeave={(e) => e.target.style.background = '#00857E'}
                >
                  <DoorOpen size={16} /> Entrance Analysis
                </button>
              </div>
            </div>
          );

        case "navgrah-shanti":
          return (
            <div style={{ padding: '12px' }}>
              <button onClick={() => setOpenNavgrahShantiModal(true)}
                style={{ background: '#00A19D', color: '#ffffff', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', width: '100%' }}>
                <Globe size={13} style={{ flexShrink:0 }} /> Open Navgrah Shanti Editor
              </button>
            </div>
          );

        case "feng-shui":
          return (
            <div style={{ padding: '12px' }}>
              <button onClick={() => setOpenFengShuiModal(true)}
                style={{ background: '#00A19D', color: '#ffffff', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', width: '100%' }}>
                <Wind size={13} style={{ flexShrink:0 }} /> Open Feng Shui Editor
              </button>
            </div>
          );

        case "zone-analysis":
          return (
            <div style={{ padding: '12px' }}>
              <button onClick={() => setOpenZoneAnalysisModal(true)}
                style={{ background: '#00A19D', color: '#ffffff', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', width: '100%' }}>
                <LayoutGrid size={13} style={{ flexShrink:0 }} /> Open Zone Analysis Editor
              </button>
            </div>
          );

        case "astro-numero":
          return null;

        default:
          return null;
    }
  };

  return (
    <>
    {/* Saving overlay is capped at 3s via showSavingOverlay (see state
        above) — the plain loading overlay (project loads, not saves) is
        untouched and still follows project.loading directly. */}
    {((project.loading && !isSavingProject) || showSavingOverlay) && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(255,255,255,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}>
        <style>{`
          /* ── Project Loading Animation ── */
          .pll-wrap{display:flex;flex-direction:column;align-items:center;padding:32px 16px;gap:24px;}
          .pll-folder{width:60px;height:48px;position:relative;margin-bottom:8px;}
          .pll-folder-back{position:absolute;bottom:0;left:0;width:60px;height:38px;background:#00A19D;border-radius:0 6px 6px 6px;}
          .pll-folder-tab{position:absolute;top:0;left:0;width:28px;height:12px;background:#00A19D;border-radius:4px 4px 0 0;}
          .pll-folder-shine{position:absolute;bottom:6px;left:8px;width:44px;height:5px;background:rgba(255,255,255,0.18);border-radius:3px;}
          .pll-layers-stack{display:flex;flex-direction:column;gap:10px;width:220px;}
          .pll-layer{width:100%;height:36px;border-radius:8px;border:1.5px solid #B5D4F4;background:#fff;box-shadow:0 2px 10px rgba(0,161,157,0.10);display:flex;align-items:center;padding:0 14px;gap:10px;opacity:0;transform:translateY(18px);animation:pllLayerIn 2.4s ease-in-out infinite;}
          .pll-layer:nth-child(1){animation-delay:0s;}
          .pll-layer:nth-child(2){animation-delay:0.3s;}
          .pll-layer:nth-child(3){animation-delay:0.6s;}
          .pll-layer:nth-child(4){animation-delay:0.9s;}
          .pll-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}
          .pll-bar{flex:1;height:8px;background:#E1F5EE;border-radius:4px;}
          .pll-bar-s{width:55%;height:8px;background:#E1F5EE;border-radius:4px;}
          /* ── Project Saving Animation ── */
          .psl-wrap{display:flex;flex-direction:column;align-items:center;padding:32px 16px;gap:24px;}
          .psl-stage{position:relative;width:220px;height:220px;display:flex;align-items:center;justify-content:center;}
          .psl-doc{width:140px;height:170px;background:#fff;border-radius:10px;border:1.5px solid #B5D4F4;box-shadow:0 4px 24px rgba(0,161,157,0.13);overflow:hidden;position:relative;}
          .psl-doc-header{background:#00A19D;height:36px;display:flex;align-items:center;padding:0 12px;gap:8px;}
          .psl-doc-icon{width:16px;height:20px;background:#fff;border-radius:2px;display:flex;align-items:center;justify-content:center;}
          .psl-doc-icon span{color:#00A19D;font-size:6px;font-weight:700;font-family:monospace;}
          .psl-doc-label{color:#fff;font-family:'Inter',sans-serif;font-size:12px;font-weight:500;letter-spacing:1px;}
          .psl-doc-lines{padding:14px 14px 0;}
          .psl-doc-line{height:8px;background:#E1F5EE;border-radius:4px;margin-bottom:9px;}
          .psl-w100{width:100%;}.psl-w75{width:75%;}.psl-w88{width:88%;}.psl-w60{width:60%;}
          .psl-doc-block{margin:10px 14px 0;height:44px;background:#E1F5EE;border-radius:5px;}
          .psl-save-fill{position:absolute;left:0;bottom:0;width:100%;height:0;background:linear-gradient(180deg,rgba(0,161,157,0.08),rgba(0,161,157,0.18));animation:pslFillUp 2s ease-in-out infinite;z-index:2;}
          .psl-arrow{position:absolute;right:-28px;top:50%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:4px;animation:pslArrow 2s ease-in-out infinite;}
          .psl-arrow-shaft{width:3px;height:36px;background:linear-gradient(180deg,#00A19D,#16B5D9);border-radius:2px;}
          .psl-arrow-head{width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-bottom:10px solid #00A19D;}
          .psl-dots{display:flex;gap:6px;position:absolute;right:-36px;top:calc(50% + 30px);}
          .psl-dot-b{width:6px;height:6px;border-radius:50%;background:#00A19D;animation:pslDot 1.2s ease-in-out infinite;}
          .psl-dot-b:nth-child(2){animation-delay:0.2s;}.psl-dot-b:nth-child(3){animation-delay:0.4s;}
          /* ── Shared ── */
          .pxl-status-row{display:flex;align-items:center;gap:10px;}
          .pxl-status-dot{width:9px;height:9px;border-radius:50%;background:#00A19D;animation:pxlBlink 1s ease-in-out infinite;}
          .pxl-status-text{font-family:'Inter',sans-serif;font-size:13px;color:#8A94A6;}
          .pxl-progress-wrap{width:220px;height:6px;background:#ECEEF2;border-radius:6px;overflow:hidden;}
          .pxl-progress-bar{height:100%;border-radius:6px;background:linear-gradient(90deg,#00A19D,#16B5D9,#2B81FF);animation:pxlProgress 2s linear infinite;}
          @keyframes pllLayerIn{0%{opacity:0;transform:translateY(18px);}30%{opacity:1;transform:translateY(0);}80%{opacity:1;transform:translateY(0);}100%{opacity:0;transform:translateY(-8px);}}
          @keyframes pslFillUp{0%{height:0;}85%{height:100%;}100%{height:100%;}}
          @keyframes pslArrow{0%,100%{opacity:0.4;transform:translateY(-50%) translateY(4px);}50%{opacity:1;transform:translateY(-50%) translateY(-4px);}}
          @keyframes pslDot{0%,100%{opacity:0.3;transform:scale(0.8);}50%{opacity:1;transform:scale(1.2);}}
          @keyframes pxlBlink{0%,100%{opacity:1;}50%{opacity:0.3;}}
          @keyframes pxlProgress{0%{width:0%;}85%{width:100%;}100%{width:100%;}}
        `}</style>

        {(isSavingProject || showSavingOverlay) ? (
          /* ── SAVING ANIMATION ── */
          <div className="psl-wrap">
            <div className="psl-stage">
              <div className="psl-doc">
                <div className="psl-doc-header">
                  <div className="psl-doc-icon"><span>DOC</span></div>
                  <span className="psl-doc-label">saving...</span>
                </div>
                <div className="psl-doc-lines">
                  <div className="psl-doc-line psl-w100"></div>
                  <div className="psl-doc-line psl-w75"></div>
                  <div className="psl-doc-line psl-w88"></div>
                  <div className="psl-doc-line psl-w60"></div>
                </div>
                <div className="psl-doc-block"></div>
                <div className="psl-save-fill"></div>
              </div>
              <div className="psl-arrow">
                <div className="psl-arrow-head"></div>
                <div className="psl-arrow-shaft"></div>
              </div>
              <div className="psl-dots">
                <div className="psl-dot-b"></div>
                <div className="psl-dot-b"></div>
                <div className="psl-dot-b"></div>
              </div>
            </div>
            <div className="pxl-status-row">
              <div className="pxl-status-dot"></div>
              <span className="pxl-status-text">Saving project…</span>
            </div>
            <div className="pxl-progress-wrap">
              <div className="pxl-progress-bar"></div>
            </div>
          </div>
        ) : (
          /* ── LOADING ANIMATION ── */
          <div className="pll-wrap">
            <div className="pll-folder">
              <div className="pll-folder-tab"></div>
              <div className="pll-folder-back"></div>
              <div className="pll-folder-shine"></div>
            </div>
            <div className="pll-layers-stack">
              <div className="pll-layer">
                <div className="pll-dot" style={{background:'#00A19D'}}></div>
                <div className="pll-bar"></div>
                <div className="pll-bar-s"></div>
              </div>
              <div className="pll-layer">
                <div className="pll-dot" style={{background:'#16B5D9'}}></div>
                <div className="pll-bar"></div>
                <div className="pll-bar-s"></div>
              </div>
              <div className="pll-layer">
                <div className="pll-dot" style={{background:'#2B81FF'}}></div>
                <div className="pll-bar"></div>
                <div className="pll-bar-s"></div>
              </div>
              <div className="pll-layer">
                <div className="pll-dot" style={{background:'#00A19D'}}></div>
                <div className="pll-bar"></div>
                <div className="pll-bar-s"></div>
              </div>
            </div>
            <div className="pxl-status-row">
              <div className="pxl-status-dot"></div>
              <span className="pxl-status-text">Loading project data…</span>
            </div>
            <div className="pxl-progress-wrap">
              <div className="pxl-progress-bar"></div>
            </div>
          </div>
        )}
      </div>
    )}
    <SaveProjectModal
      open={showSaveModal}
      mode={saveModalMode}
      initial={saveModalMode === 'edit'
        ? { name: project.currentProject?.name, description: project.currentProject?.description }
        : { name: initialProjectName || '', description: initialProjectDescription || '' }}
      loading={project.loading}
      onClose={() => setShowSaveModal(false)}
      onSubmit={handleSaveSubmit}
    />

    {project.layerImagesSyncProgress && (
      <div style={{
        position: 'fixed',
        top: '12px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        maxWidth: 'calc(100vw - 24px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '8px 16px',
        background: '#1A1F36',
        color: '#fff',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12.5px',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
      }}>
        <span>
          Syncing report images… {project.layerImagesSyncProgress.done}/{project.layerImagesSyncProgress.total} — please don't close or reload this tab yet
        </span>
        <div style={{ width: '160px', height: '5px', background: 'rgba(255,255,255,0.25)', borderRadius: '5px', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{
            width: `${(project.layerImagesSyncProgress.done / project.layerImagesSyncProgress.total) * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg,#00A19D,#16B5D9,#2B81FF)',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>
    )}

    {editingLayer && (
      <LayerEditor
        compassColor={compassColor}
        polygonColor={polygonColor}
        compassOpacity={compassOpacity}
        compassLineOpacity={compassLineOpacity}
        coverBackgrounds={{ 'cover-page': layerImages['cover-page-bg'], 'cover-back-page': layerImages['cover-back-page-bg'] }}
        compassLineWeight={compassLineWeight}
        polygonLineWeight={polygonLineWeight}
        polygonLineOpacity={polygonLineOpacity}
        key={`${editingLayer}-${layerImages[editingLayer] ? 'edited' : 'new'}`}
        layerId={editingLayer}
        layerName={getLayerName(editingLayer)}
        language={selectedLanguage}
        projectClientDetails={clientDetails}
        coverFrontConfig={savedLayerConfigs['cover-page']}
        baseImage={baseImage}
        uploadedImage={uploadedImages[editingLayer]}
        canvasDimensions={canvasDimensions}
        currentConfig={tempLayerConfig}
        onSave={handleSaveLayerEdit}
        onCancel={handleCancelLayerEdit}
        onRequestReportsAfterSave={() => setPendingReportsOpen(true)}
        polygonPoints={polygonPoints}
        isPolygonComplete={isPolygonComplete}
        compassCenter={compassCenter}
        compassRotation={compassRotation}
        measurements={measurements}
        isCalibrationSet={isCalibrationSet}
        pixelsPerUnit={pixelsPerUnit}
        currentUnit={currentUnit}
        mainDirections={mainDirections}
        all32Directions={all32Directions}
        directions8={directions8}
        directions24={directions24}
        findPolygonIntersection={findPolygonIntersection}
        calculateDirectionMeasurements={calculateDirectionMeasurements}
        calculate8DirectionMeasurements={calculate8DirectionMeasurements}
        calculate45AreaMeasurements={calculate45AreaMeasurements}
        exportLayers={exportLayers}
        getLayerName={getLayerName}
        entranceReportImage={layerImages['entrance-analysis-report']}
        spaceReportImage={layerImages['space-analysis-report']}
        entranceReportData={entranceReportData}
        spaceReportData={spaceReportData}
        autoSave={!!autoCaptureLayer && autoCaptureLayer === editingLayer}
      />
    )}
    {autoCaptureLayer && editingLayer && (
      <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10600, background: '#1A1F36', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
        Preparing report...
      </div>
    )}
    <style>{`
    @media print {
      @page {
        size: A4;
        margin: 20mm;
      }
      
      body {
        width: 210mm;
        height: 297mm;
        margin: 0;
        padding: 0;
      }
      
      .no-print {
        display: none !important;
      }
    }
     @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
      
      /* Custom dark crosshair cursor */
      .custom-crosshair-cursor {
        cursor: none !important;
      }
       @keyframes pulse-delete {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        .delete-icon-hover {
          animation: pulse-delete 0.3s ease;
        }
      .custom-crosshair-cursor::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        border: 2px solid #000;
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
      }
    `}</style>
    <div style={{ display: 'flex', height: 'calc(100vh)', background: '#ffffff', color: '#fff', overflow: 'hidden' }}>
      {/* Left SideBar ??" image-style clean card list. On phone widths this
          renders as a bottom sheet instead (see MobileSheetWrapper) — the
          div and everything inside it is completely unchanged either way. */}
      <MobileSheetWrapper isMobile={isMobile} open={!!activeMobileStage}
        onClose={() => {
          // Mirrors desktop: collapsing the row again also calls
          // toggleStage(), which is what actually turns off e.g. polygon
          // point dragging / compass auto-show — closing just the sheet's
          // visual chrome without this would leave that stage "open"
          // (in openStages) even though its sheet is gone.
          if (activeMobileStageId) toggleStage(activeMobileStageId);
          setMobileSheet(null);
        }}
        title={activeMobileStage ? activeMobileStage.label.replace(/\*$/, '') : 'Layout Stages'}
        // Compass is the one stage where the user needs to WATCH the canvas
        // react live while dragging (the rotation slider) — the shared full
        // dark backdrop + tall sheet used for unit/polygon hid the compass
        // rose, degree markings and floor plan entirely, so there was no way
        // to tell how far it had rotated. No backdrop + a short sheet keeps
        // the canvas visible and tappable above it.
        backdrop={activeMobileStageId !== 'compass'}
        maxHeight={activeMobileStageId === 'compass' ? '38vh' : '78vh'}>
      <div style={{
        width: isMobile ? '100%' : '280px',
        minWidth: isMobile ? 'auto' : '280px',
        background: '#FBFCFE',
        borderRight: isMobile ? 'none' : '1px solid #ECEEF2',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        height: isMobile ? 'auto' : '100%',
        fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif"
      }}>
        {/* Header — skipped on mobile, MobileSheetWrapper already shows its
            own title bar (the specific stage's name) + close button, so
            this would just be a redundant second "Layout Stages" heading. */}
        {!isMobile && (
        <div style={{
          padding: '18px 20px 14px',
          background: '#FFFFFF',
          borderBottom: '1px solid #ECEEF2',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <h6 style={{
            color: '#1A1F36',
            margin: 0,
            fontSize: '0.95rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: 999,
              background: '#00A19D', boxShadow: '0 0 0 3px rgba(0,161,157,0.18)'
            }} />
            Layout Stages
          </h6>
        </div>
        )}

        {/* Stages List ??" image-style menu. On mobile, filtered down to just
            the one stage whose sheet is open (each stage now gets its own
            dedicated sheet, not one shared sheet listing all 6 like
            desktop's accordion) — isOpen is forced true for it, since
            there's nothing else in the list to accordion against. */}
        <div style={{ flex: 1, padding: '10px 10px 16px' }}>
          {(isMobile ? stages.filter(s => s.id === activeMobileStageId) : stages).map((stage) => {
            const isOpen = isMobile ? true : openStages.has(stage.id);
            return (
              <div
                key={stage.id}
                style={{
                  marginBottom: '8px',
                  borderRadius: '10px',
                  background: 'transparent',
                  transition: 'background 180ms ease'
                }}
              >
                {/* Stage Header ??" clean row like image's nav items */}
                <div
                  onClick={() => toggleStage(stage.id)}
                  onMouseEnter={(e) => {
                    if (!isOpen) {
                      e.currentTarget.style.background = '#F4FBFB';
                      e.currentTarget.style.borderColor = '#B8E3E1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isOpen) {
                      e.currentTarget.style.background = '#FFFFFF';
                      e.currentTarget.style.borderColor = '#ECEEF2';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    gap: '12px',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    borderRadius: '10px',
                    background: isOpen ? 'rgba(0, 161, 157, 0.08)' : '#FFFFFF',
                    border: '1px solid ' + (isOpen ? '#00A19D' : '#ECEEF2'),
                    boxShadow: isOpen ? '0 2px 8px rgba(0,161,157,0.10)' : '0 1px 2px rgba(20,30,60,0.03)',
                    transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                    userSelect: 'none'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    background: isOpen ? '#00A19D' : '#FFFFFF',
                    color: isOpen ? '#FFFFFF' : '#5A6478',
                    border: isOpen ? '1px solid #00A19D' : '1px solid #ECEEF2',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    flexShrink: 0,
                    boxShadow: isOpen ? '0 2px 6px rgba(0,161,157,0.30)' : 'none',
                    transition: 'background 200ms ease, color 200ms ease, box-shadow 200ms ease'
                  }}>
                    {stage.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h6 style={{
                      color: isOpen ? '#00A19D' : '#1A1F36',
                      margin: 0,
                      textAlign: 'left',
                      fontSize: '0.88rem',
                      fontWeight: 600,
                      letterSpacing: '-0.005em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      transition: 'color 160ms ease'
                    }}>
                      {stage.label}
                    </h6>
                  </div>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={isOpen ? '#00A19D' : '#A3ABB8'}
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{
                      flexShrink: 0,
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 240ms cubic-bezier(0.4, 0, 0.2, 1), stroke 160ms ease'
                    }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>

                {/* Stage Content ??" fades + slides */}
                <div style={{
                  maxHeight: isOpen ? '1400px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), opacity 240ms ease, margin-top 200ms ease',
                  opacity: isOpen ? 1 : 0,
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  marginTop: isOpen ? '8px' : '0',
                  border: isOpen ? '1px solid #DCE0E8' : '1px solid transparent',
                  borderLeft: isOpen ? '3px solid #00A19D' : '1px solid transparent',
                  boxShadow: isOpen ? '0 4px 14px rgba(20,30,60,0.07)' : 'none'
                }}>
                  <div style={{ padding: '14px 14px 16px' }}>
                    {renderStageContent(stage.id)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </MobileSheetWrapper>

      <style>{`
        /* Stage-panel sliders: teal fill (inline gradient) + round white thumb with teal ring */
        .stage-range { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 999px; outline: none; cursor: pointer; }
        .stage-range::-webkit-slider-runnable-track { height: 6px; border-radius: 999px; background: transparent; }
        .stage-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; margin-top: -5px; border-radius: 50%; background: #ffffff; border: 3px solid #00A19D; box-shadow: 0 1px 4px rgba(0,0,0,0.25); }
        .stage-range::-moz-range-track { height: 6px; border-radius: 999px; background: transparent; }
        .stage-range::-moz-range-thumb { width: 10px; height: 10px; border-radius: 50%; background: #ffffff; border: 3px solid #00A19D; box-shadow: 0 1px 4px rgba(0,0,0,0.25); }
        /* Custom ScrollBar */
        div::-webkit-scrollBar {
          width: 6px;
        }
        
        div::-webkit-scrollBar-track {
          background: #ffffff;
        }
        
        div::-webkit-scrollBar-thumb {
          background: #333;
          border-radius: 3px;
        }
        
        div::-webkit-scrollBar-thumb:hover {
          background: #ffffff;
        }
      `}</style>

      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        position: 'relative',
        minWidth: 0,
        height: '100vh',
        overflow: 'hidden',
        // Clearance for the floating bottom toolbar on phone widths, so it
        // never covers canvas content.
        paddingBottom: isMobile ? '64px' : 0
      }}>
        {/* Canvas Toolbar - merged header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          background: '#FFFFFF',
          borderBottom: '1px solid #ECEEF2',
          minHeight: '56px',
          flexWrap: 'wrap',
          gap: '12px',
          flexShrink: 0,
          position: 'relative',
          fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif"
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              {/* Trigger label — hidden on mobile, where it's redundant
                  with the bottom toolbar's own "Measure" button (see
                  MobileBottomToolbar). Split out as its own element
                  (instead of one div that was both the trigger AND the
                  dropdown-content's parent) specifically so hiding it here
                  can't also hide the dropdown content below, which still
                  needs to render (as a bottom sheet) when opened from that
                  bottom toolbar button instead. */}
              {!isMobile && (
              <div
                onClick={() => setShowMeasurementDropdown(!showMeasurementDropdown)}
                style={{
                  background: showMeasurementDropdown ? '#DFF5F4' : '#FFFFFF',
                  border: '1px solid ' + (showMeasurementDropdown ? '#00A19D' : '#DCE0E8'),
                  color: showMeasurementDropdown ? '#00A19D' : '#1A1F36',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  letterSpacing: '0',
                  minWidth: '140px',
                  transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease',
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  height: '36px',
                  boxSizing: 'border-box'
                }}
              >
                <span>Measurement</span>
                <span style={{
                  fontSize: '0.65rem',
                  color: '#00A19D',
                  transform: showMeasurementDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms ease'
                }}>▼</span>
              </div>
              )}

                {/* On phone widths this same content also opens as a bottom
                    sheet from the new floating toolbar's "Unit" button (see
                    MobileBottomToolbar/mobileSheet) — MobileSheetWrapper is a
                    no-op passthrough on desktop, so the popover below is
                    completely unchanged there. Now a sibling of the trigger
                    label above (was its child), so hiding that label on
                    mobile can't hide this along with it. */}
                {(showMeasurementDropdown || (isMobile && mobileSheet === 'measurement')) && (
                  <MobileSheetWrapper isMobile={isMobile} open={mobileSheet === 'measurement'}
                    onClose={() => { setShowMeasurementDropdown(false); setMobileSheet(null); }} title="Measurement">
                  <div style={{
                    position: isMobile ? 'static' : 'absolute',
                    top: '100%',
                    left: 0,
                    background: '#FFFFFF',
                    border: isMobile ? 'none' : '1px solid #DCE0E8',
                    borderRadius: '10px',
                    boxShadow: isMobile ? 'none' : '0 10px 30px rgba(20, 30, 60, 0.12)',
                    zIndex: 10000,
                    minWidth: isMobile ? 'auto' : '200px',
                    marginTop: isMobile ? 0 : '6px',
                    overflow: 'hidden',
                    padding: isMobile ? '10px' : '6px'
                  }}>
                    {/* Set Measurement - Disabled after calibration */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isCalibrationSet) {
                          startSetMeasurement();
                          setShowMeasurementDropdown(false);
                        }
                      }}
                      style={{
                        padding: '9px 12px',
                        cursor: isCalibrationSet ? 'not-allowed' : 'pointer',
                        borderRadius: '6px',
                        fontSize: '0.78rem',
                        color: '#1A1F36',
                        background: 'transparent',
                        transition: 'background 140ms ease, color 140ms ease',
                        fontWeight: 500,
                        opacity: isCalibrationSet ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isCalibrationSet) {
                          e.target.style.background = '#DFF5F4'; e.target.style.color = '#00A19D';
                        }
                      }}
                      onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#1A1F36'; }}
                    >
                      {isCalibrationSet ? '1. Calibration Set' : '1. Set Measurement'}
                    </div>

                    {/* Get Measurement */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCalibrationSet) {
                          startGetMeasurement();
                          setShowMeasurementDropdown(false);
                        } else {
                          alert('Please set measurement calibration first!');
                        }
                      }}
                      style={{
                        padding: '9px 12px',
                        cursor: isCalibrationSet ? 'pointer' : 'not-allowed',
                        borderRadius: '6px',
                        fontSize: '0.78rem',
                        color: '#1A1F36',
                        background: 'transparent',
                        transition: 'background 140ms ease, color 140ms ease',
                        fontWeight: 500,
                        opacity: isCalibrationSet ? 1 : 0.5
                      }}
                      onMouseEnter={(e) => {
                        if (isCalibrationSet) {
                          e.target.style.background = '#DFF5F4'; e.target.style.color = '#00A19D';
                        }
                      }}
                      onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#1A1F36'; }}
                    >
                      2. Get Measurement
                    </div>

                    {/* Reset All */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (measurements.length > 0 || isCalibrationSet) {
                          setMeasurements([]);
                          setIsCalibrationSet(false);
                          setPixelsPerUnit(null);
                          setMeasurementMode(false);
                          setMeasurementPoints([]);
                          setShowMeasurementDropdown(false);
                          setShowSuccessMessage(true);
                          setSuccessMessage('All measurements reset!');
                          setTimeout(() => setShowSuccessMessage(false), 2000);
                        } else {
                          alert('No measurements to reset!');
                        }
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: (measurements.length > 0 || isCalibrationSet) ? 'pointer' : 'not-allowed',
                        fontSize: '0.75rem',
                        color: '#ffffff',
                        background: '#00A19D',
                        transition: 'all 0.2s ease',
                        fontWeight: 500,
                        opacity: (measurements.length > 0 || isCalibrationSet) ? 1 : 0.5
                      }}
                      onMouseEnter={(e) => {
                        if (measurements.length > 0 || isCalibrationSet) {
                          e.target.style.background = '#c82333';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = (measurements.length > 0 || isCalibrationSet) ? '#00A19D' : '#00A19D';
                      }}
                    >
                      3. Reset All
                    </div>
                  </div>
                  </MobileSheetWrapper>
                )}
            </div>

            {/* Back to Projects — mobile only, placed right before the
                download icon per explicit request. The bottom toolbar has
                its own "Projects" button too (see MobileBottomToolbar); this
                is a second, closer-to-hand copy in the top row, not a
                replacement for it. */}
            {isMobile && onBackToProjects && (
              <button
                onClick={onBackToProjects}
                title="Back to Projects"
                style={mobileToolbarBtnStyle(true, true)}
              >
                <ArrowLeft size={15} />
              </button>
            )}

            {showCompass && compassCenter && isPolygonComplete && (
              <button
                onClick={handleDownloadCanvas}
                style={isMobile ? mobileToolbarBtnStyle(true, true) : {
                  width: '36px',
                  height: '36px',
                  background: '#FFFFFF',
                  border: '1px solid #CFE3E2',
                  color: '#00A19D',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s ease, border-color 0.15s ease',
                  flexShrink: 0
                }}
                title="Download Canvas"
                onMouseEnter={(e) => {
                  if (isMobile) return;
                  e.currentTarget.style.background = '#E6F6F5';
                  e.currentTarget.style.borderColor = '#00A19D';
                }}
                onMouseLeave={(e) => {
                  if (isMobile) return;
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#CFE3E2';
                }}
              >
                <Download size={16} />
              </button>
            )}

            {/* Select Language — report/remedies language, right next to
                Download Canvas per explicit request. Fixed 8-option list;
                onChange is UI-only for now (see handleLanguageChange
                above) — wiring it to actually translate remedies content
                is a separate, upcoming task. */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowLanguageDropdown(v => !v)}
                title="Select Language"
                style={isMobile ? mobileToolbarBtnStyle(true, true) : {
                  height: '36px',
                  padding: '0 12px',
                  background: showLanguageDropdown ? '#E6F6F5' : '#FFFFFF',
                  border: '1px solid ' + (showLanguageDropdown ? '#00A19D' : '#CFE3E2'),
                  color: '#00857E',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  transition: 'background 0.15s ease, border-color 0.15s ease',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                <Globe size={14} />
                {!isMobile && (
                  <span style={{ fontFamily: LANGUAGE_NATIVE_FONT }}>
                    {LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.native}
                  </span>
                )}
                <span style={{ fontSize: '0.6rem', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}>▼</span>
              </button>

              <MobileSheetWrapper isMobile={isMobile} open={showLanguageDropdown} onClose={() => setShowLanguageDropdown(false)} title="Select Language">
                {showLanguageDropdown && (
                  <div style={isMobile ? { padding: '8px 6px 16px' } : {
                    position: 'absolute', top: '42px', left: 0, zIndex: 500,
                    background: '#ffffff', border: '1px solid #DCE0E8', borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.14)', minWidth: '175px', overflow: 'hidden',
                    padding: '4px',
                  }}>
                    {LANGUAGE_OPTIONS.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                          gap: '10px', padding: isMobile ? '12px 14px' : '8px 10px', border: 'none',
                          borderRadius: '6px', cursor: 'pointer', textAlign: 'left',
                          background: selectedLanguage === lang.code ? '#DFF5F4' : 'transparent',
                          color: selectedLanguage === lang.code ? '#00817D' : '#1A1F36',
                        }}
                      >
                        <span style={{ fontFamily: LANGUAGE_NATIVE_FONT, fontSize: isMobile ? '0.95rem' : '0.85rem', fontWeight: 600 }}>
                          {lang.native}
                        </span>
                        <span style={{ fontSize: isMobile ? '0.72rem' : '0.65rem', color: '#8A94A6' }}>
                          {lang.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </MobileSheetWrapper>
            </div>

            {/* Video Guide — two buttons (Desktop + Mobile), each opens a popup with only its video.
                Centred at the top of the toolbar on desktop widths; phones keep them in the normal
                toolbar flow (display: contents makes this wrapper layout-neutral there). */}
            {isMobile && (
            <div style={{ display: 'contents' }}>
            <button
              onClick={() => { setVideoGuideKind('desktop'); setVideoGuideOpen(true); }}
              title="Desktop Video Guide"
              style={isMobile ? mobileToolbarBtnStyle(true) : {
                height: '28px',
                padding: '0 10px',
                background: '#00A19D',
                border: '1px solid #ECEEF2',
                color: '#ffffff',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '0.72rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                flexShrink: 0,
                marginRight: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              Desktop:
              <VideoGuideIcon size={14} />
              Video Guide
            </button>

            <button
              onClick={() => { setVideoGuideKind('mobile'); setVideoGuideOpen(true); }}
              title="Mobile Video Guide"
              style={isMobile ? mobileToolbarBtnStyle(true) : {
                height: '28px',
                padding: '0 10px',
                background: '#00A19D',
                border: '1px solid #ECEEF2',
                color: '#ffffff',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '0.72rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                flexShrink: 0,
                marginRight: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              Mobile:
              <VideoGuideIcon size={14} />
              Video Guide
            </button>
            </div>
            )}

            {/* Floor Plan Maker — opens the software in a new tab (desktop; sits where the video buttons used to). */}
            {!isMobile && (
            <button
              onClick={() => window.open(FLOOR_PLAN_MAKER_URL, '_blank', 'noopener,noreferrer')}
              title="Open Floor Plan Maker"
              style={{
                height: '36px',
                padding: '0 16px',
                background: '#00A19D',
                border: 'none',
                color: '#ffffff',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.78rem',
                fontWeight: 600,
                boxShadow: '0 3px 10px rgba(0,161,157,0.25)',
                transition: 'background 0.15s ease',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#00857E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#00A19D'; }}
            >
              Floor Plan Maker
            </button>
            )}

            {/* <button
              onClick={handleCanvasZoomIn}
              style={{ 
                width: '28px', 
                height: '28px', 
                background: '#00A19D', 
                border: '1px solid #ECEEF2', 
                color: '#ffffff', 
                borderRadius: '4px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              title="Zoom In"
              onMouseEnter={(e) => {
                e.target.style.background = '#00A19D';
                e.target.style.borderColor = '#00A19D';
                e.target.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#00A19D';
                e.target.style.borderColor = '#ffffff';
                e.target.style.color = '#ffffff';
              }}
            >
              <ZoomIn size={16} />
            </button>

            <button 
              onClick={handleCanvasZoomOut}
              style={{ 
                width: '28px', 
                height: '28px', 
                background: '#00A19D', 
                border: '1px solid #ffffff', 
                color: '#ffffff', 
                borderRadius: '4px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              title="Zoom Out"
              onMouseEnter={(e) => {
                e.target.style.background = '#00A19D';
                e.target.style.borderColor = '#00A19D';
                e.target.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#00A19D';
                e.target.style.borderColor = '#ffffff';
                e.target.style.color = '#ffffff';
              }}
            >
              <ZoomOut size={16} />
            </button> */}
            {/* <span style={{ 
              color: '#00A19D', 
              fontSize: '0.7rem', 
              marginLeft: '6px',
              whiteSpace: 'nowrap',
              fontWeight: 500
            }}>
              {Math.round(canvasScale * 100)}%
            </span> */}
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center', 
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            flex: 1
          }}>
            {currentRoomPolygon.isDrawing && currentRoomPolygon.points.length >= 3 && (
              <button
                onClick={() => {
                  const directions = getDirectionsFromPolygon(currentRoomPolygon.points);
                  const completedRoom = {
                    ...currentRoomPolygon,
                    isDrawing: false,
                    isComplete: true,
                    id: Date.now(),
                    directions: directions
                  };
                  setRoomLayers(prev => [...prev, completedRoom]);
                  setCurrentRoomPolygon({
                    points: [],
                    isDrawing: false,
                    isComplete: false,
                    roomType: null,
                    color: null
                  });
                  setShowSuccessMessage(true);
                  setTimeout(() => setShowSuccessMessage(false), 4000);
                }}
                style={isMobile ? mobileToolbarBtnStyle(true) : {
                  padding: '5px 10px',
                  background: '#28a745',
                  border: '1px solid #28a745',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <CheckCircle size={12} />
                Finish {roomTypes.find(r => r.id === currentRoomPolygon.roomType)?.label}
              </button>
            )}

            {currentRoomPolygon.isDrawing && (
              <button
                onClick={() => {
                  setCurrentRoomPolygon({
                    points: [],
                    isDrawing: false,
                    isComplete: false,
                    roomType: null,
                    color: null
                  });
                  setShowSuccessMessage(true);
                  setTimeout(() => setShowSuccessMessage(false), 2000);
                }}
                style={isMobile ? mobileToolbarBtnStyle(true) : {
                  padding: '5px 10px',
                  background: '#dc3545',
                  border: '1px solid #dc3545',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <X size={12} />
                Cancel
              </button>
            )}

            {measurements.length > 0 && (
              <button
                style={isMobile ? mobileToolbarBtnStyle(true, true) : {
                  background: isDeleteMode ? '#dc3545' : '#28a745',
                  color: '#fff',
                  height: '36px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                  whiteSpace: 'nowrap',
                  fontWeight: 600
                }}
                onClick={() => {
                  const newDeleteMode = !isDeleteMode;
                  setIsDeleteMode(newDeleteMode);
                  
                  // If activating delete mode, turn off measurement mode
                  if (newDeleteMode) {
                    setMeasurementMode(false);
                    setMeasurementPoints([]);
                  }
                  
                  setSelectedMeasurementForDelete(null);
                  setSelectedPointForDelete(null);
                }}
              >
                <Eraser size={15} />
                {!isMobile && <span>{isDeleteMode ? 'Eraser Active' : 'Eraser'}</span>}
              </button>
            )}

            {isDrawingPolygon && polygonPoints.length >= 3 && (
              <div style={isMobile ? mobileToolbarChipStyle() : {
                background: '#007bff',
                color: '#fff',
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                fontSize: '0.76rem',
                fontWeight: 600,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}>
                <Info size={12} />
                <span>Tap first point to close</span>
              </div>
            )}

            {currentRoomPolygon.isDrawing && currentRoomPolygon.points.length > 0 && currentRoomPolygon.points.length < 3 && (
              <div style={isMobile ? mobileToolbarChipStyle() : {
                background: '#ff9800',
                color: '#fff',
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                fontSize: '0.76rem',
                fontWeight: 600,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}>
                <Info size={12} />
                Drawing {roomTypes.find(r => r.id === currentRoomPolygon.roomType)?.label}... ({currentRoomPolygon.points.length})
              </div>
            )}

            {measurementMode && !isDeleteMode && (
              <div style={isMobile ? { ...mobileToolbarChipStyle(), padding: '0 8px', cursor: 'pointer' } : {
                background: '#007bff',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '0.7rem',
                border: '1px solid #007bff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer'
              }}>
                <span onClick={cancelMeasurement}>
                  <Ruler size={16} /> <X size={10} />
                </span>
              </div>
            )}

            <button 
              onClick={() => {
                const openStageId = Array.from(openStages)[0];
                if (openStageId === 'polygon') {
                  resetPolygon();
                } else if (openStageId === 'compass') {
                  resetCompass();
                }
                
                // ?o. Also reset measurements if any exist
                if (measurements.length > 0) {
                  resetMeasurement();
                }
                
                setOpenStages(new Set());
              }}
              disabled={openStages.size === 0}
              style={isMobile ? mobileToolbarBtnStyle(openStages.size > 0, true) : {
                height: '36px',
                padding: '0 14px',
                background: '#dc3545',
                border: 'none',
                color: '#ffffff',
                borderRadius: '8px',
                cursor: openStages.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.78rem',
                fontWeight: 600,
                opacity: openStages.size > 0 ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap'
              }}
              title="Reset current stage"
            >
              <RotateCcw size={14} />
              {!isMobile && 'Reset'}
            </button>

            <button
              onClick={() => {
                const openStageId = Array.from(openStages)[0];
                const stageOrder = ['unit', 'polygon', 'compass'];
                const currentIndex = stageOrder.indexOf(openStageId);
                setShowPolygonPoints(false);
                setOpenStages(new Set());
                if (currentIndex >= 0 && currentIndex < stageOrder.length - 1) {
                  const nextStageId = stageOrder[currentIndex + 1];
                  setTimeout(() => {
                    setOpenStages(new Set([nextStageId]));
                    if (nextStageId === 'compass' && isPolygonComplete) {
                      setShowCompass(true);
                    }
                  }, 100);
                }
              }}
              disabled={openStages.size === 0}
              style={isMobile ? mobileToolbarBtnStyle(openStages.size > 0) : {
                height: '36px',
                padding: '0 16px',
                background: '#28a745',
                border: 'none',
                color: '#ffffff',
                borderRadius: '8px',
                cursor: openStages.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.78rem',
                fontWeight: 600,
                opacity: openStages.size > 0 ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: openStages.size > 0 ? '0 3px 10px rgba(40,167,69,0.28)' : 'none',
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap'
              }}
              title="Complete and move to next stage"
            >
              <CheckCircle size={14} />
              Next
            </button>

            {/* Save — mobile only, placed right after Next per explicit
                request (moved up from the bottom toolbar, same as the Back
                to Projects button above). The bottom MobileBottomToolbar no
                longer renders its own Save button. */}
            {isMobile && (
              <button
                onClick={triggerSave}
                disabled={project.loading}
                style={mobileToolbarBtnStyle(!project.loading)}
                title="Save"
              >
                <SaveIcon size={12} />
                {project.loading ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>

        {/* Main Canvas Container - WITH SCROLLBAR */}
        <div ref={containerRef} style={{ 
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'stretch',   // inner box gets the real visible height, so the zoom viewport's max-height and scrollbars stay on screen
          background: '#ffffff',
          minHeight: 0,
          scrollBarWidth: 'thin',
          scrollBarColor: '#ffffff'
        }}>

          <div style={{
  position: 'absolute', top: 10, right: 18, zIndex: 30, display: 'flex', alignItems: 'center', gap: 4,
  background: 'rgba(255,255,255,0.94)', border: '1px solid #ECEEF2', borderRadius: 8, padding: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
}} data-zoom-controls="main">
            <button type="button" onClick={handleCanvasZoomOut} disabled={canvasScale <= 0.5} title="Zoom Out" style={{
  width: 28, height: 28, background: '#00A19D', border: '1px solid #00A19D', color: '#ffffff',
  borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: canvasScale <= 0.5 ? 0.5 : 1
}}><ZoomOut size={16} /></button>
            <button type="button" onClick={handleCanvasZoomReset} title="Reset zoom to 100%" style={{
  minWidth: 52, height: 28, background: '#ffffff', border: '1px solid #00A19D', color: '#00A19D',
  borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, padding: '0 6px'
}}>{Math.round(canvasScale * 100)}%</button>
            <button type="button" onClick={handleCanvasZoomIn} disabled={canvasScale >= 3} title="Zoom In" style={{
  width: 28, height: 28, background: '#00A19D', border: '1px solid #00A19D', color: '#ffffff',
  borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: canvasScale >= 3 ? 0.5 : 1
}}><ZoomIn size={16} /></button>
          </div>

          <div ref={containerRef} style={{
            position: 'relative',
            overflow: 'auto',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            flex: '1 1 0%',
            contain: 'layout paint',   // zoomed content may scroll inside this box but can never widen the panel
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#ffffff',
            minHeight: 0,
            scrollBarWidth: 'thin',
            scrollBarColor: '#00A19D #00A19D',
          }}>
            {/* Canvas wrapper - Centered with max-width. On mobile, this
                outer box is the "viewport" — capped to the already-computed
                post-shrink pixel size (mobileStageWidth/Height) with
                overflow:hidden, so nothing can render wider than the phone
                screen. '210mm' (~794px, same as A4_WIDTH) was never actually
                a mobile-safe constraint on its own. */}
            {/* Zoom viewport: a fixed box the size of the normal (100%) canvas. Zooming happens INSIDE it and its own
                right/bottom scrollbars appear here, so a zoomed canvas can never push the panel or the export section. */}
            <div data-zoom-viewport="main" ref={setMainViewportEl} style={{
              width: `min(100%, ${isMobile ? mobileStageWidth : A4_WIDTH}px)`,
              aspectRatio: `${A4_WIDTH} / ${A4_HEIGHT}`,
              maxHeight: 'calc(100% - 20px)',
              flexShrink: 0,
              overflow: 'auto',
              margin: isMobile ? '10px 0' : '10px 0',
              background: '#ffffff',
              scrollBarWidth: 'thin',
              scrollBarColor: '#00A19D #ffffff',
            }}>
            <div style={{
              width: canvasZoomed ? `${Math.round((isMobile ? mobileStageWidth : A4_WIDTH) * canvasScale)}px` : '100%',
              maxWidth: canvasZoomed ? 'none' : (isMobile ? `${mobileStageWidth}px` : '210mm'),
              flexShrink: canvasZoomed ? 0 : undefined,
              aspectRatio: `${A4_WIDTH} / ${A4_HEIGHT}`,
              margin: '0 auto',
              background: '#ffffff',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Full-size (A4_WIDTH x A4_HEIGHT) content, shrunk to fit the
                  smaller viewport box above via a pure CSS transform on
                  mobile — the Stage inside keeps its EXACT desktop props
                  (width/height/scaleX/scaleY all unchanged), so
                  Konva's own pointer-position math (which is derived from
                  those props, not from any outer CSS) stays byte-for-byte
                  identical between mobile and desktop. Konva computes
                  clicks/taps correctly through a CSS transform like this by
                  design — it compares its content element's post-transform
                  getBoundingClientRect() against its pre-transform
                  clientWidth to work out the right ratio on its own — so
                  this is the one change that actually needed to happen for
                  the canvas to be both responsive AND click-accurate, in
                  place of the width/height/scale prop changes tried before,
                  which fought Konva's own coordinate system instead of
                  working with it. */}
              <div style={{
                width: `${A4_WIDTH}px`,
                height: `${A4_HEIGHT}px`,
                transform: (isMobile || canvasZoomed) ? `scale(${(isMobile ? mobileFitScale : 1) * canvasScale})` : 'none',
                transformOrigin: 'top left',
                position: 'relative',
              }}>
              <div style={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}>
                <Stage
                  ref={stageRef}
                  width={A4_WIDTH}
                  height={A4_HEIGHT}
                  onClick={handleStageClick}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setMagnifierPosition(null)}
                  x={0}
                  y={0}
                  style={{
                    background: '#ffffff',
                    // Deliberately identical on mobile and desktop — see the
                    // comment on the transform wrapper just above this Stage.
                    // Changing these per-device is what caused the click/tap
                    // offset bug: Konva derives its pointer-position math
                    // from these exact props, so they need to stay exactly
                    // what shapes are actually authored against (0..A4_WIDTH)
                    // on every device; the phone-sized *visual* shrink is
                    // handled entirely by the ancestor's CSS transform.
                    width: '100%',
                    height: '100%',
                    cursor: isDrawingPolygon || measurementMode || currentRoomPolygon.isDrawing || isSettingEntrance ? 'crosshair' : 'default'
                  }}
                >
                  {/* FloorPlanImage */}
                  <Layer>
                    <FloorPlanImage 
                      src={baseImage} 
                      canvasWidth={A4_WIDTH}
                      canvasHeight={A4_HEIGHT}
                    />
                  </Layer>

                  {/* Polygon Layer */}
                  {showPolygonLayer && (
                    <Layer>
                      {isPolygonComplete && showPolygonFill && polygonPoints.length >= 3 && (
                        <Line
                          points={polygonPoints.flatMap(p => [p.x, p.y])}
                          closed
                          fill={polygonColor}
                          opacity={polygonOpacity * 0.3}
                        />
                      )}
                      
                      {polygonPoints.length >= 2 && showPolygonLines && (
                        <Line
                          points={polygonPoints.flatMap(p => [p.x, p.y])}
                          stroke={polygonColor}
                          strokeWidth={polygonLineWeight}
                          closed={isPolygonComplete}
                          dash={isDrawingPolygon ? [5, 5] : []}
                          opacity={polygonLineOpacity}
                        />
                      )}
                      
                      {showPolygonPoints && polygonPoints.map((point, index) => (
                        <Group key={index}>
                          <Circle
                            x={point.x}
                            y={point.y}
                            radius={8}
                            fill={
                              index === 0 && isDrawingPolygon && polygonPoints.length >= 3 
                                ? "#ff6b6b" 
                                : selectedPolygonPoints.has(index)
                                ? "#00A19D"
                                : selectedPolygonPoint === index
                                ? "#4ADE80"
                                : "#6366F1"
                            }
                            stroke="#ffffff"
                            strokeWidth={2}
                            draggable={openStages.has('polygon') && isPolygonComplete && !measurementMode && !isMultiSelectMode}
                            onDragStart={() => handlePointDragStart(index)}
                            onDragMove={(e) => handlePointDragMove(index, e)}
                            onDragEnd={handlePointDragEnd}
                            onClick={(e) => {
                              e.cancelBubble = true;
                              if (!openStages.has('polygon')) {
                                return;
                              }
                              if (isDrawingPolygon && polygonPoints.length >= 3 && index === 0) {
                                finishPolygon();
                                return;
                              }
                              if (isMultiSelectMode) {
                                setSelectedPolygonPoints(prev => {
                                  const newSelection = new Set(prev);
                                  if (newSelection.has(index)) {
                                    newSelection.delete(index);
                                  } else {
                                    newSelection.add(index);
                                  }
                                  return newSelection;
                                });
                              }
                            }}
                            onMouseEnter={(e) => {
                              const container = e.target.getStage().container();
                              if (isPolygonComplete && !measurementMode && !isMultiSelectMode) {
                                container.style.cursor = 'grab';
                              } else if (isDrawingPolygon && index === 0 && polygonPoints.length >= 3) {
                                container.style.cursor = 'pointer';
                              }
                            }}
                            onMouseLeave={(e) => {
                              const container = e.target.getStage().container();
                              container.style.cursor = isDrawingPolygon || measurementMode ? 'crosshair' : 'default';
                            }}
                          />
                          <Text
                            x={point.x - 5}
                            y={point.y - 5}
                            text={String(index + 1)}
                            fontSize={10}
                            fill="#ffffff"
                            fontStyle="bold"
                            listening={false}
                          />
                        </Group>
                      ))}

                      {showCenterPoint && compassCenter && isPolygonComplete && (
                        <Circle
                          x={compassCenter.x}
                          y={compassCenter.y}
                          radius={3}
                          fill={polygonColor}  // ??? Use dynamic polygon color
                          stroke="#ffffff"     // ??? Add white border for visibility
                          strokeWidth={1.2}
                        />
                      )}
                    </Layer>
                  )}


                  {/* Compass Layer */}
                  {showCompass && compassCenter && isPolygonComplete && (
                    <Layer>
                      {mainDirections.map(({ angle: startAngleDeg, area }, index) => {
                        const nextIndex = (index + 1) % mainDirections.length;
                        const endAngleDeg = mainDirections[nextIndex].angle;

                        // ?o. Ab array milega
                        const clippedRings = clipSectorWithBoundary(
                          compassCenter,
                          0,
                          startAngleDeg + (compassRotation || 0),
                          endAngleDeg   + (compassRotation || 0),
                          polygonPoints
                        );

                        if (!clippedRings || clippedRings.length === 0) return null;

                        const zoneColors = getZoneColors();
                        const zoneColor = zoneColors[area] || '#cccccc';

                        return clippedRings.map((ringPoints, ringIndex) => {
                          if (!ringPoints || ringPoints.length < 3) return null;
                          const flatPoints = ringPoints.flatMap(p => [p.x, p.y]);

                          return (
                            <Line
                              key={`sector-${area}-ring-${ringIndex}`}
                              points={flatPoints}
                              closed
                              fill={hoveredDirection === area ? directionHoverColor : 'transparent'}
                              opacity={hoveredDirection === area ? directionHoverOpacity : compassOpacity * 1}
                              stroke={compassColor} 
                              strokeWidth={2}
                              onMouseEnter={() => setHoveredDirection(area)}
                              onMouseLeave={() => setHoveredDirection(null)}
                              onClick={() => {
                                const directionData = calculateDirectionMeasurements(area);
                                if (directionData) setSelectedDirection(directionData);
                              }}
                            />
                          );
                        });
                      })}
                      {showCompassLines && (
                        <>
                          {mainDirections.map(({ angle, area }) => {
                            const intersection = findPolygonIntersection(compassCenter, angle);
                            if (!intersection) return null;

                            // ?o. Extended point - boundary ke baad bhi line jaaye
                            const angleRad = ((angle + compassRotation) * Math.PI) / 180;
                            const extendLength = 0; // boundary ke baad kitna extend karna hai
                            const extendedX = intersection.x + Math.cos(angleRad) * extendLength;
                            const extendedY = intersection.y + Math.sin(angleRad) * extendLength;

                            return (
                              <React.Fragment key={`line-group-${angle}`}>
                                {/* ?o. Solid line - center se boundary tak */}
                                <Line
                                  points={[compassCenter.x, compassCenter.y, intersection.x, intersection.y]}
                                  stroke={compassColor}
                                  strokeWidth={compassLineWeight}
                                  opacity={compassLineOpacity}
                                  listening={false}
                                />
                                {/* ?o. Dashed line - boundary ke baad extended */}
                                <Line
                                  points={[intersection.x, intersection.y, extendedX, extendedY]}
                                  stroke={compassColor}
                                  strokeWidth={compassLineWeight * 0.7}
                                  opacity={compassLineOpacity * 0.5}
                                  // dash={[4, 4]}
                                  listening={false}
                                />
                              </React.Fragment>
                            );
                          })}

                          {/* Black points with angle numbers */}
                          {compassLines.map(({ id, intersection }) => {
                            if (!intersection) return null;

                            const calculateDisplayedAngle = () => {
                              const dx = intersection.x - compassCenter.x;
                              const dy = intersection.y - compassCenter.y;
                              const rawAngle = Math.atan2(dy, dx);
                              const angleFromNorth = (rawAngle * 180 / Math.PI + 90 + 360) % 360;
                              let angle = Math.round((angleFromNorth - compassRotation + 360) % 360);
                              if (compassRotation > 90) angle = (angle - 1 + 360) % 360;
                              return angle;
                            };

                            const displayedAngle = calculateDisplayedAngle();

                            // Push the angle box outward (away from the compass center,
                            // past the boundary point) instead of always straight up —
                            // otherwise it sits on top of the boundary line at most angles.
                            const dx = intersection.x - compassCenter.x;
                            const dy = intersection.y - compassCenter.y;
                            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                            const pushOut = 42;
                            const boxX = intersection.x + (dx / dist) * pushOut;
                            const boxY = intersection.y + (dy / dist) * pushOut;

                            return (
                              <Group key={`point-group-${id}`}>
                                {/* Angle box — click to edit; editing back-solves
                                    compassRotation so every box updates together. */}
                                <Group
                                  x={boxX}
                                  y={boxY}
                                  onClick={() => setEditingAngleBox({ id, value: String(displayedAngle) })}
                                  onTap={() => setEditingAngleBox({ id, value: String(displayedAngle) })}
                                  onMouseEnter={(e) => { const c = e.target.getStage().container(); c.style.cursor = 'pointer'; }}
                                  onMouseLeave={(e) => { const c = e.target.getStage().container(); c.style.cursor = 'default'; }}
                                >
                                  <Rect
                                    width={52.5}
                                    height={30}
                                    fill="rgba(255, 107, 92, 0.15)"
                                    stroke="#00A19D"
                                    strokeWidth={3}
                                    cornerRadius={4.5}
                                    offsetX={26.25}
                                    offsetY={15}
                                  />
                                  <Text
                                    text={`${displayedAngle}°`}
                                    fontSize={13.5}
                                    fontStyle="bold"
                                    fill="#000000"
                                    align="center"
                                    verticalAlign="middle"
                                    offsetX={displayedAngle.toString().length * 3.75}
                                    offsetY={7.5}
                                    listening={false}
                                  />
                                </Group>

                                {/* Black point */}
                                <Circle
                                  x={intersection.x}
                                  y={intersection.y}
                                  radius={4}
                                  fill="#000000"
                                  stroke="#ffffff"
                                  strokeWidth={2.5}
                                  listening={false}
                                />
                              </Group>
                            );
                          })}
                        </>
                      )}
                      {showSectorAreas && (() => {
                        const directionsToShow = show32Directions ? all32Directions : mainDirections;
                        return directionsToShow.map(({ angle, area }, index) => {
                          const nextIndex = (index + 1) % directionsToShow.length;
                          const nextAngle = directionsToShow[nextIndex].angle;
                          let middleAngle = nextAngle < angle ? (angle + nextAngle + 360) / 2 : (angle + nextAngle) / 2;
                          if (middleAngle >= 360) middleAngle -= 360;
                          
                          const intersection = findPolygonIntersection(compassCenter, middleAngle);
                          if (!intersection) return null;
                          
                          const dirX = intersection.x - compassCenter.x;
                          const dirY = intersection.y - compassCenter.y;
                          const distance = Math.sqrt(dirX * dirX + dirY * dirY);
                          if (distance < 5) return null;
                          
                          // ?o. Calculate outside position
                          const outsideLabelX = compassCenter.x + (dirX / distance) * (distance * 1.1);
                          const outsideLabelY = compassCenter.y + (dirY / distance) * (distance * 1.1);
                          
                          // ?o. Check if label fits outside (using A4 dimensions)
                          const margin = 30;
                          const fitsOutside = (
                            outsideLabelX >= margin && 
                            outsideLabelX <= A4_WIDTH - margin &&
                            outsideLabelY >= margin && 
                            outsideLabelY <= A4_HEIGHT - margin
                          );
                          
                          // ?o. Choose position based on available space
                          let labelX, labelY;
                          if (fitsOutside) {
                            // Enough space - place outside
                            labelX = outsideLabelX;
                            labelY = outsideLabelY;
                          } else {
                            // Near edge - place inside at 70% distance
                            labelX = compassCenter.x + (dirX / distance) * (distance * 0.7);
                            labelY = compassCenter.y + (dirY / distance) * (distance * 0.7);
                          }
                          
                          return (
                            <Text
                              key={`sector-${area}-${index}`}
                              x={labelX}
                              y={labelY}
                              text={area}
                              fontSize={15}
                              fontStyle="bold"
                              fill="#fffff"
                              align="center"
                              verticalAlign="middle"
                              offsetX={18}
                              offsetY={9}
                              padding={4}
                              opacity={compassOpacity}
                              listening={false}
                            />
                          );
                        });
                      })()}
                      <Circle
                        x={compassCenter.x}
                        y={compassCenter.y}
                        radius={3}
                        fill="#6366F1"
                        stroke="#ffffff"
                        strokeWidth={1.8}
                        draggable
                        onDragMove={(e) => {
                          setCompassCenter({ x: e.target.x(), y: e.target.y() });
                        }}
                      />

                      <Text
                        x={compassCenter.x}
                        y={compassCenter.y - 30}
                        text=""
                        fontSize={12}
                        fontStyle="bold"
                        fill="#ffffff"
                        offsetX={20}
                        listening={false}
                      />
                    </Layer>
                  )}

                                    {/* Measurements Layer */}
                  {(measurementMode || measurements.length > 0) && (
                    <Layer>
                      {measurementPoints.map((point, index) => (
                        <Circle
                          key={`measure-point-${index}`}
                          x={point.x}
                          y={point.y}
                          radius={6}
                          fill="#dc3545"
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      ))}
                      {measurements.map((measurement) => {
                        if (measurement.isCalibration) return null;
                        const [p1, p2] = measurement.points;
                        const midX = (p1.x + p2.x) / 2;
                        const midY = (p1.y + p2.y) / 2;
                        const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                        const angleDeg = (angleRad * 180) / Math.PI;
                        const labelOffset = 12;
                        const labelX = midX + Math.cos(angleRad + Math.PI/2) * labelOffset;
                        const labelY = midY + Math.sin(angleRad + Math.PI/2) * labelOffset;
                        let readableAngle = angleDeg;
                        if (readableAngle > 90) readableAngle -= 180;
                        if (readableAngle < -90) readableAngle += 180;
                        const isHovered = hoveredMeasurementId === measurement.id;
                        return (
                          <Group key={measurement.id}>
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke={isHovered && isDeleteMode ? '#ff0000' : isHovered ? '#ff6b6b' : measurementLineColor}
                              strokeWidth={isHovered ? measurementLineWeight + 2 : measurementLineWeight}
                              opacity={isHovered && isDeleteMode ? 0.5 : isHovered ? 1 : 0.8}
                              hitStrokeWidth={isDeleteMode ? 30 : 20}
                              onClick={(e) => {
                                if (isDeleteMode) {
                                  e.cancelBubble = true;
                                  handleDeleteMeasurement(measurement.id);
                                  setShowSuccessMessage(true);
                                  setSuccessMessage('Measurement deleted!');
                                  setTimeout(() => setShowSuccessMessage(false), 2000);
                                }
                              }}
                              onMouseEnter={(e) => {
                                const stage = e.target.getStage();
                                if (stage) {
                                  stage.container().style.cursor = isDeleteMode ? 'pointer' : 'default';
                                }
                                if (isDeleteMode) {
                                  setHoveredMeasurementId(measurement.id);
                                }
                              }}
                              onMouseLeave={(e) => {
                                const stage = e.target.getStage();
                                if (stage) {
                                  stage.container().style.cursor = measurementMode ? 'crosshair' : 'default';
                                }
                                setHoveredMeasurementId(null);
                              }}
                            />
                            <Group x={labelX} y={labelY} rotation={readableAngle}>
                              <Text
                                text={`${measurement.realDistance.toFixed(1)} ${measurement.unit}`}
                                fontSize={10}
                                fontStyle="bold"
                                fill={measurementLabelColor}
                                padding={2}
                                background="#ffffff"
                                align="center"
                                offsetX={5}
                                offsetY={4}
                              />
                            </Group>
                          </Group>
                        );
                      })}
                    </Layer>
                  )}

                </Stage>

                {/* Magnifier */}
                {isDrawingPolygon && magnifierPosition && stageRef.current && (
                  <div style={{
                    position: 'absolute',
                    left: Math.min(magnifierPosition.x + 40, canvasDimensions.width - 140),
                    top: Math.max(magnifierPosition.y - 80, 20),
                    width: 'clamp(100px, 15vw, 120px)',
                    height: 'clamp(100px, 15vw, 120px)',
                    borderRadius: '50%',
                    border: '3px solid #00A19D',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    background: '#fff'
                  }}>
                    <canvas
                      ref={(canvas) => {
                        if (canvas && stageRef.current) {
                          const ctx = canvas.getContext('2d');
                          const stage = stageRef.current;
                          const stageCanvas = stage.toCanvas();
                          const zoomFactor = 2;
                          const sourceSize = 120 / zoomFactor;
                          const sourceX = magnifierPosition.x - sourceSize / 2;
                          const sourceY = magnifierPosition.y - sourceSize / 2;
                          ctx.clearRect(0, 0, 120, 120);
                          ctx.save();
                          ctx.beginPath();
                          ctx.arc(60, 60, 60, 0, Math.PI * 2);
                          ctx.clip();
                          ctx.drawImage(stageCanvas, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 120, 120);
                          ctx.restore();
                          ctx.strokeStyle = '#ff0000';
                          ctx.lineWidth = 2;
                          ctx.shadowColor = '#000';
                          ctx.shadowBlur = 2;
                          ctx.beginPath();
                          ctx.moveTo(50, 60);
                          ctx.lineTo(70, 60);
                          ctx.stroke();
                          ctx.beginPath();
                          ctx.moveTo(60, 50);
                          ctx.lineTo(60, 70);
                          ctx.stroke();
                          ctx.fillStyle = '#ff0000';
                          ctx.beginPath();
                          ctx.arc(60, 60, 2, 0, Math.PI * 2);
                          ctx.fill();
                        }
                      }}
                      width={120}
                      height={120}
                      style={{
                        width: '100%',
                        height: '100%',
                        imageRendering: 'auto'
                      }}
                    />
                  </div>
                )}
              </div>
              </div>
            </div>
            </div>
          </div>

          {/* Add CSS for Webkit browsers (Chrome, Safari, Edge) */}
          <style>{`
            div[ref="containerRef"]::-webkit-scrollBar {
              width: 12px;
              height: 12px;
            }
            div[ref="containerRef"]::-webkit-scrollBar-track {
              background: #333;
              border-radius: 6px;
            }
            div[ref="containerRef"]::-webkit-scrollBar-thumb {
              background: #00A19D;
              border-radius: 6px;
              border: 2px solid #333;
            }
            div[ref="containerRef"]::-webkit-scrollBar-thumb:hover {
              background: #FF8B7E;
            }
            div[ref="containerRef"]::-webkit-scrollBar-corner {
              background: #333;
            }
          `}</style>
        </div>
      </div>
    {/* Right SideBar — "Export & Reports". On phone widths this renders as
        a bottom sheet instead (see MobileSheetWrapper); the div and
        everything inside it is completely unchanged either way. */}
    <MobileSheetWrapper isMobile={isMobile} open={mobileSheet === 'reports'} onClose={() => setMobileSheet(null)} title="Report Section"
      headerAction={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={handleExportPDF} disabled={isGeneratingPDF} aria-label="Export PDF" title="Export PDF"
          style={{
            width: 34, height: 34, borderRadius: '50%', border: 'none',
            background: isGeneratingPDF ? '#FFB5AC' : '#FF6B5C', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
          }}>
          <Download size={16} />
        </button>
        <button onClick={handleExportWord} disabled={isGeneratingPDF} aria-label="Export Word" title="Export Word (.docx)"
          style={{
            width: 34, height: 34, borderRadius: '50%', border: 'none',
            background: isGeneratingPDF ? '#9DB6DC' : '#2B579A', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: isGeneratingPDF ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 800,
          }}>
          W
        </button>
        </div>
      )}
    >
    <div style={{
      width: isMobile ? '100%' : '300px',
      minWidth: isMobile ? 'auto' : '300px',
      background: '#FBFCFE',
      borderLeft: isMobile ? 'none' : '1px solid #ECEEF2',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      height: isMobile ? 'auto' : '100%',
      fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        padding: '18px 20px 14px',
        background: '#FFFFFF',
        borderBottom: '1px solid #ECEEF2',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <h5 style={{
          color: '#1A1F36',
          margin: 0,
          fontSize: '0.95rem',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            display: 'inline-block', width: 6, height: 6, borderRadius: 999,
            background: '#FF6B5C', boxShadow: '0 0 0 3px rgba(255,107,92,0.18)'
          }} />
          Export &amp; Reports
        </h5>
      </div>

      <div style={{ flex: 1, padding: '14px 14px 18px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '10px' }}>
            <input
              type="hidden"
              placeholder="Enter floor plan name..."
              defaultValue={imageData?.name?.split(".")[0] || "Floor Plan"}
              style={{
                background: '#FFFFFF',
                border: '1px solid #ECEEF2',
                color: '#1A1F36',
                padding: '10px 12px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                width: '100%',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 160ms ease, box-shadow 160ms ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#00A19D';
                e.target.style.boxShadow = '0 0 0 3px rgba(0,161,157,0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ECEEF2';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Report footer — the line printed at the bottom of every report page (profile details unless edited) */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '10px 12px',
            background: '#FFFFFF', border: '1px solid #DCE0E8', borderRadius: 12, boxShadow: '0 2px 6px rgba(20,30,60,0.05)',
          }}>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A94A6' }}>Report Footer</div>
              <div style={{ fontSize: '0.8rem', color: '#1A1F36', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                   title={(reportFooterText || '').trim() || profileFooterDefault || ''}>
                {(reportFooterText || '').trim() || profileFooterDefault || 'May Vastu bring peace, happiness, prosperity always'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFooterModalOpen(true)}
              title="Edit the footer text of the report"
              style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #00A19D', background: '#FFFFFF', color: '#00857E', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Edit size={13} /> Edit
            </button>
          </div>

          {/* Export Layers List ??" clean card */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #DCE0E8',
            boxShadow: '0 2px 6px rgba(20,30,60,0.05)',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
              <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>

                {/* ?o. COVER PAGE LAYER - ADD THIS FIRST */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: '8px',
                  padding: '12px 14px',
                  borderBottom: '1px solid #ECEEF2',
                  alignItems: 'center',
                  background: exportLayers['cover-page']?.enabled
                    ? 'linear-gradient(90deg, rgba(0,161,157,0.10) 0%, rgba(0,161,157,0.04) 100%)'
                    : '#FFFFFF'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22, height: 22,
                      borderRadius: 6,
                      background: '#00A19D',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700
                    }}>1</span>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.85rem', fontWeight: 600 }}>Cover Front</span>
                  </div>

                  <button
                    onClick={() => handleEditLayer('cover-page')}
                    style={{
                      background: layerImages['cover-page'] ? '#00A19D' : '#00A19D',
                      border: '1px solid ' + (layerImages['cover-page'] ? '#00A19D' : '#555'),
                      color: layerImages['cover-page'] ? '#000' : '#fff',
                      width: '48px',
                      height: '32px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Edit Cover Front"
                  >
                    <Edit size={14} />
                    <Eye size={14} />
                  </button>

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <label style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '44px',
                      height: '24px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={exportLayers['cover-page']?.enabled || false}
                        onChange={(e) => setExportLayers(prev => ({
                          ...prev,
                          'cover-page': { ...prev['cover-page'], enabled: e.target.checked }
                        }))}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: exportLayers['cover-page']?.enabled ? '#00A19D' : '#333',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease'
                      }} />
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        left: exportLayers['cover-page']?.enabled ? 'calc(100% - 22px)' : '2px',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </label>
                  </div>
                </div>

                {/* COVER INDEX LAYER — the Modern/Vedic/Medical/Others/Planning index
                    pages that used to sit under Cover Front in its edit mode. */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: '8px',
                  padding: '12px 14px',
                  borderBottom: '1px solid #ECEEF2',
                  alignItems: 'center',
                  background: ((exportLayers['cover-index']?.enabled ?? exportLayers['cover-page']?.enabled) || false)
                    ? 'linear-gradient(90deg, rgba(0,161,157,0.10) 0%, rgba(0,161,157,0.04) 100%)'
                    : '#FFFFFF'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22, height: 22,
                      borderRadius: 6,
                      background: '#00A19D',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700
                    }}>2</span>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.85rem', fontWeight: 600 }}>Cover Index</span>
                  </div>

                  <button
                    onClick={() => handleEditLayer('cover-index')}
                    style={{
                      background: '#00A19D',
                      border: '1px solid #00A19D',
                      color: '#fff',
                      width: '48px',
                      height: '32px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Edit Cover Index"
                  >
                    <Edit size={14} />
                    <Eye size={14} />
                  </button>

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <label style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '44px',
                      height: '24px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={((exportLayers['cover-index']?.enabled ?? exportLayers['cover-page']?.enabled) || false)}
                        onChange={(e) => setExportLayers(prev => ({
                          ...prev,
                          'cover-index': { ...prev['cover-index'], enabled: e.target.checked }
                        }))}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: ((exportLayers['cover-index']?.enabled ?? exportLayers['cover-page']?.enabled) || false) ? '#00A19D' : '#333',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease'
                      }} />
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        left: ((exportLayers['cover-index']?.enabled ?? exportLayers['cover-page']?.enabled) || false) ? 'calc(100% - 22px)' : '2px',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </label>
                  </div>
                </div>

                {/* COVER BACK LAYER — independent toggle/edit from Cover Front,
                    though its content is auto-populated from the same
                    client/consultant data captured there. */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: '8px',
                  padding: '12px 14px',
                  borderBottom: '1px solid #ECEEF2',
                  alignItems: 'center',
                  background: exportLayers['cover-back-page']?.enabled
                    ? 'linear-gradient(90deg, rgba(0,161,157,0.10) 0%, rgba(0,161,157,0.04) 100%)'
                    : '#FFFFFF'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22, height: 22,
                      borderRadius: 6,
                      background: '#00A19D',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700
                    }}>3</span>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.85rem', fontWeight: 600 }}>Cover Back</span>
                  </div>

                  <button
                    onClick={() => handleEditLayer('cover-back-page')}
                    style={{
                      background: '#00A19D',
                      border: '1px solid #00A19D',
                      color: '#fff',
                      width: '48px',
                      height: '32px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Edit Cover Back"
                  >
                    <Edit size={14} />
                    <Eye size={14} />
                  </button>

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <label style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '44px',
                      height: '24px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={exportLayers['cover-back-page']?.enabled || false}
                        onChange={(e) => setExportLayers(prev => ({
                          ...prev,
                          'cover-back-page': { ...prev['cover-back-page'], enabled: e.target.checked }
                        }))}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: exportLayers['cover-back-page']?.enabled ? '#00A19D' : '#333',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease'
                      }} />
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        left: exportLayers['cover-back-page']?.enabled ? 'calc(100% - 22px)' : '2px',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </label>
                  </div>
                </div>

                {/* Modern Vastu Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setModernVastuOpen(!modernVastuOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Modern Vastu</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {modernVastuOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {modernVastuOpen && (
                    <div>
                      {/* Floor plan */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Floor plan</span>
                        </div>
                        <button onClick={() => handleEditLayer('floor-plan')} disabled={false} style={{ background: layerImages['floor-plan'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['floor-plan'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['floor-plan']?.enabled || false} onChange={(e) => handleToggleLayer('floor-plan', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['floor-plan']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['floor-plan']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Floor plan with grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Floor plan with grid</span></div>
                        <button onClick={() => handleEditLayer('grid-layout')} style={{ background: layerImages['grid-layout'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['grid-layout'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5 }} disabled={false} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['grid-layout']?.enabled || false} onChange={(e) => handleToggleLayer('grid-layout', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['grid-layout']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['grid-layout']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Center analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Center analysis</span></div>
                        <button onClick={() => handleEditLayer('center-analysis')} disabled={false} style={{ background: layerImages['center-analysis'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['center-analysis'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['center-analysis']?.enabled || false} onChange={(e) => handleToggleLayer('center-analysis', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['center-analysis']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['center-analysis']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Mahavastu Chakra */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Mahavastu Chakra</span></div>
                        <button onClick={() => handleEditLayer('mahavastu-chakra')} disabled={false} style={{ background: layerImages['mahavastu-chakra'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['mahavastu-chakra'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['mahavastu-chakra']?.enabled || false} onChange={(e) => handleToggleLayer('mahavastu-chakra', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['mahavastu-chakra']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['mahavastu-chakra']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Shakti Chakra */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Shakti Chakra</span></div>
                        <button onClick={() => handleEditLayer('shakti-chakra')} disabled={false} style={{ background: layerImages['shakti-chakra'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['shakti-chakra'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['shakti-chakra']?.enabled || false} onChange={(e) => handleToggleLayer('shakti-chakra', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['shakti-chakra']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['shakti-chakra']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Floor plan with VPM */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Floor plan with VPM</span></div>
                        <button onClick={() => handleEditLayer('floor-plan-vpm')} disabled={false} style={{ background: layerImages['floor-plan-vpm'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['floor-plan-vpm'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['floor-plan-vpm']?.enabled || false} onChange={(e) => handleToggleLayer('floor-plan-vpm', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['floor-plan-vpm']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['floor-plan-vpm']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 8 Zones Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>8 Zones Analysis</span></div>
                        <button onClick={() => handleEditLayer('compass-8')} style={{ background: layerImages['compass-8'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-8'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter)} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-8']?.enabled || false} onChange={(e) => handleToggleLayer('compass-8', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-8']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-8']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 8 Zones Bar Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>8 Zones Bar Chart</span></div>
                        <button onClick={() => handleEditLayer('Bar-chart-8')} style={{ background: layerImages['Bar-chart-8'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Bar-chart-8'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Bar-chart-8']?.enabled || false} onChange={(e) => handleToggleLayer('Bar-chart-8', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Bar-chart-8']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Bar-chart-8']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 16 Zones Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>16 Zones Analysis</span></div>
                        <button onClick={() => handleEditLayer('compass-16')} style={{ background: layerImages['compass-16'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-16'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter)} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-16']?.enabled || false} onChange={(e) => handleToggleLayer('compass-16', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-16']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-16']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 16 Zones Bar Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>16 Zones Bar Chart</span></div>
                        <button onClick={() => handleEditLayer('Bar-chart-16')} style={{ background: layerImages['Bar-chart-16'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Bar-chart-16'] ? '#ffffff' : '#00A19D', width: '48px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Bar-chart-16']?.enabled || false} onChange={(e) => handleToggleLayer('Bar-chart-16', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Bar-chart-16']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Bar-chart-16']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 24 Zones Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>24 Zones Analysis</span></div>
                        <button onClick={() => handleEditLayer('compass-24')} style={{ background: layerImages['compass-24'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-24'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter)} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-24']?.enabled || false} onChange={(e) => handleToggleLayer('compass-24', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-24']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-24']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 32 Mahavastu Zones */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>32 Mahavastu Zones</span></div>
                        <button onClick={() => handleEditLayer('compass-32')} style={{ background: layerImages['compass-32'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-32'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter)} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-32']?.enabled || false} onChange={(e) => handleToggleLayer('compass-32', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-32']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-32']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 32 Mahavastu Bar Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>32 Mahavastu Bar Chart</span></div>
                        <button onClick={() => handleEditLayer('Bar-chart-32')} style={{ background: layerImages['Bar-chart-32'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Bar-chart-32'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Bar-chart-32']?.enabled || false} onChange={(e) => handleToggleLayer('Bar-chart-32', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Bar-chart-32']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Bar-chart-32']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern 45 Devata (with name) */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern 45 Devata (with name)</span></div>
                        <button onClick={() => handleEditLayer('compass-45-2')} style={{ background: layerImages['compass-45-2'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45-2'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter)} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45-2']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45-2', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45-2']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45-2']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern 45 Devata Table */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern 45 Devata Table</span></div>
                        <button onClick={() => handleEditLayer('compass-45-2-table')} style={{ background: layerImages['compass-45-2-table'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45-2-table'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45-2-table']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45-2-table', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45-2-table']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45-2-table']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern 45 Devata Bar Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern 45 Devata Bar Chart</span></div>
                        <button onClick={() => handleEditLayer('compass-45-2-chart')} style={{ background: layerImages['compass-45-2-chart'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45-2-chart'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45-2-chart']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45-2-chart', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45-2-chart']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45-2-chart']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Coloured Zone */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Coloured Zone</span></div>
                        <button onClick={() => handleEditLayer('coloured-zone')} disabled={false} style={{ background: layerImages['coloured-zone'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['coloured-zone'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['coloured-zone']?.enabled || false} onChange={(e) => handleToggleLayer('coloured-zone', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['coloured-zone']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['coloured-zone']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern 5 Elemental Zone */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern 5 Elemental Zone</span></div>
                        <button onClick={() => handleEditLayer('elemental-5-zone')} disabled={false} style={{ background: layerImages['elemental-5-zone'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['elemental-5-zone'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['elemental-5-zone']?.enabled || false} onChange={(e) => handleToggleLayer('elemental-5-zone', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['elemental-5-zone']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['elemental-5-zone']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern 5 Elemental Pie Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern 5 Elemental Pie Chart</span></div>
                        <button onClick={() => handleEditLayer('elemental-5-chart')} disabled={false} style={{ background: layerImages['elemental-5-chart'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['elemental-5-chart'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['elemental-5-chart']?.enabled || false} onChange={(e) => handleToggleLayer('elemental-5-chart', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['elemental-5-chart']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['elemental-5-chart']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Marmsthan Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Marmsthan (Shanmahanti) Analysis</span></div>
                        <button onClick={() => handleEditLayer('marmsthan-analysis')} disabled={false} style={{ background: layerImages['marmsthan-analysis'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['marmsthan-analysis'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['marmsthan-analysis']?.enabled || false} onChange={(e) => handleToggleLayer('marmsthan-analysis', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['marmsthan-analysis']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['marmsthan-analysis']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Advance Marmsthan Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Advance Marmsthan Analysis</span></div>
                        <button onClick={() => handleEditLayer('marmsthan-advance')} disabled={false} style={{ background: layerImages['marmsthan-advance'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['marmsthan-advance'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['marmsthan-advance']?.enabled || false} onChange={(e) => handleToggleLayer('marmsthan-advance', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['marmsthan-advance']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['marmsthan-advance']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern Vastu Numbers with Devata 81 */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern Vastu Numbers with Devata 81</span></div>
                        <button onClick={() => handleEditLayer('vastu-numbers-81-numbers')} disabled={false} style={{ background: layerImages['vastu-numbers-81-numbers'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['vastu-numbers-81-numbers'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['vastu-numbers-81-numbers']?.enabled || false} onChange={(e) => handleToggleLayer('vastu-numbers-81-numbers', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['vastu-numbers-81-numbers']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['vastu-numbers-81-numbers']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern Vastu Numbers 64 */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Modern Vastu Numbers 64</span></div>
                        <button onClick={() => handleEditLayer('vastu-numbers-64')} disabled={false} style={{ background: layerImages['vastu-numbers-64'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['vastu-numbers-64'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['vastu-numbers-64']?.enabled || false} onChange={(e) => handleToggleLayer('vastu-numbers-64', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['vastu-numbers-64']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['vastu-numbers-64']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 3X3 Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>3X3 Grid</span></div>
                        <button onClick={() => handleEditLayer('grid-3x3')} disabled={false} style={{ background: layerImages['grid-3x3'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['grid-3x3'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['grid-3x3']?.enabled || false} onChange={(e) => handleToggleLayer('grid-3x3', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['grid-3x3']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['grid-3x3']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 32 Entrance Analysis Compass */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>32 Entrance Analysis Compass</span></div>
                        <button onClick={() => handleEditLayer('32-Entrance-Analysis-Compass')} disabled={false} style={{ background: layerImages['32-Entrance-Analysis-Compass'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['32-Entrance-Analysis-Compass'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['32-Entrance-Analysis-Compass']?.enabled || false} onChange={(e) => handleToggleLayer('32-Entrance-Analysis-Compass', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['32-Entrance-Analysis-Compass']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['32-Entrance-Analysis-Compass']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Entrance Analysis Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Entrance Analysis Report</span></div>
                        <button onClick={() => { setOpenStages(new Set(['entrance-analysis'])); setOpenEntranceAnalysisModal(true) }} style={{ background: localStorage.getItem(entranceAnalysisStorageKey) ? '#28a745' : '#ffffff', border: '1px solid ' + (localStorage.getItem(entranceAnalysisStorageKey) ? '#28a745' : '#00A19D'), color: localStorage.getItem(entranceAnalysisStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title="View/Edit Entrance Analysis"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}><input type="checkbox" checked={exportLayers['entrance-analysis-report']?.enabled || false} onChange={(e) => { if (e.target.checked && !localStorage.getItem(entranceAnalysisStorageKey)) { const auto = buildDefaultEntranceReport(entranceAnalysisStorageKey.replace('entrance_analysis_report_data_', 'entrance_analysis_selections_'), all32Directions, compassRotation); if (!auto.length) { showAlert('Entrance Direction Needed', 'Open the Entrance Analysis report, pick the direction(s) of your entrance(s), then enable it.', 'warning'); return; } try { localStorage.setItem(entranceAnalysisStorageKey, JSON.stringify(auto)); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } } setExportLayers(prev => ({ ...prev, 'entrance-analysis-report': { ...prev['entrance-analysis-report'], enabled: e.target.checked } })); }} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['entrance-analysis-report']?.enabled ? '#28a745' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['entrance-analysis-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Space Analysis Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Space Analysis Report</span></div>
                        <button onClick={() => { setOpenStages(new Set(['space-analysis'])); setOpenSpaceAnalysisModal(true) }} style={{ background: localStorage.getItem(spaceAnalysisStorageKey) ? '#28a745' : '#ffffff', border: '1px solid ' + (localStorage.getItem(spaceAnalysisStorageKey) ? '#28a745' : '#00A19D'), color: localStorage.getItem(spaceAnalysisStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title="View/Edit Space Analysis"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}><input type="checkbox" checked={exportLayers['space-analysis-report']?.enabled || false} onChange={(e) => { if (e.target.checked && !localStorage.getItem(spaceAnalysisStorageKey)) { const auto = buildDefaultSpaceReport(spaceAnalysisStorageKey.replace('space_analysis_report_data_', 'space_analysis_selections_'), compassRotation, selectedLanguage); if (!auto.length) { showAlert('Spaces Needed', 'Open the Space Analysis report, pick at least one space and its direction(s), then enable it.', 'warning'); return; } try { localStorage.setItem(spaceAnalysisStorageKey, JSON.stringify(auto)); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } } setExportLayers(prev => ({ ...prev, 'space-analysis-report': { ...prev['space-analysis-report'], enabled: e.target.checked } })); }} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['space-analysis-report']?.enabled ? '#28a745' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['space-analysis-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                    </div>
                  )}
                </div>

                {/* Vedic Vastu Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setVedicVastuOpen(!VedicVastuOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Vedic Vastu</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {VedicVastuOpen ? '▼' : '▶'}
                    </span>
                  </div>

                  {VedicVastuOpen && (
                    <div>
                      {/* Image - 1 */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Image - 1</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('png-image-1')} disabled={false} style={{ background: layerImages['png-image-1'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['png-image-1'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['png-image-1']?.enabled || false} onChange={(e) => handleToggleLayer('png-image-1', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['png-image-1']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['png-image-1']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* Image - 2 */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Image - 2</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('png-image-2')} disabled={false} style={{ background: layerImages['png-image-2'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['png-image-2'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['png-image-2']?.enabled || false} onChange={(e) => handleToggleLayer('png-image-2', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['png-image-2']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['png-image-2']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* Vedic Shakti Chakra (was "Vedic Mahavastu Chakra") */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic Shakti Chakra</span></div>
                        <button onClick={() => handleEditLayer('Vedic-Mahavastu-Chakra')} disabled={false} style={{ background: layerImages['Vedic-Mahavastu-Chakra'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-Mahavastu-Chakra'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['Vedic-Mahavastu-Chakra']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-Mahavastu-Chakra', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-Mahavastu-Chakra']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-Mahavastu-Chakra']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* 16 Directional Domestic Vastu */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>16 Directional Domestic Vastu</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('16-Directional-Domestic-Vastu')} disabled={false} style={{ background: layerImages['16-Directional-Domestic-Vastu'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['16-Directional-Domestic-Vastu'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['16-Directional-Domestic-Vastu']?.enabled || false} onChange={(e) => handleToggleLayer('16-Directional-Domestic-Vastu', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['16-Directional-Domestic-Vastu']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['16-Directional-Domestic-Vastu']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Khanija Dhatu */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Khanija Dhatu</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Khanija-Dhatu')} disabled={false} style={{ background: layerImages['Khanija-Dhatu'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Khanija-Dhatu'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Khanija-Dhatu']?.enabled || false} onChange={(e) => handleToggleLayer('Khanija-Dhatu', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Khanija-Dhatu']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Khanija-Dhatu']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Vedic devata Numbers with Names 81 */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic devata Numbers with Names 81</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Vedic-devata-Numbers-with-Names-81')} disabled={false} style={{ background: layerImages['Vedic-devata-Numbers-with-Names-81'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-devata-Numbers-with-Names-81'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Vedic-devata-Numbers-with-Names-81']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-devata-Numbers-with-Names-81', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-devata-Numbers-with-Names-81']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-devata-Numbers-with-Names-81']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Modern Vastu Numbers with Devata 81 - Vedic */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic Vastu Numbers with Devata 81</span></div>
                        <button onClick={() => handleEditLayer('vastu-numbers-81-numbers-Vedic')} disabled={false} style={{ background: layerImages['vastu-numbers-81-numbers-Vedic'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['vastu-numbers-81-numbers-Vedic'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['vastu-numbers-81-numbers-Vedic']?.enabled || false} onChange={(e) => handleToggleLayer('vastu-numbers-81-numbers-Vedic', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['vastu-numbers-81-numbers-Vedic']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['vastu-numbers-81-numbers-Vedic']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Direction Deity Planet */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Direction Deity Planet</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Direction-Diety-Planet')} disabled={false} style={{ background: layerImages['Direction-Diety-Planet'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Direction-Diety-Planet'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Direction-Diety-Planet']?.enabled || false} onChange={(e) => handleToggleLayer('Direction-Diety-Planet', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Direction-Diety-Planet']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Direction-Diety-Planet']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Khanij */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Khanij</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-Khanij')} disabled={false} style={{ background: layerImages['Devata-Khanij'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-Khanij'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-Khanij']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-Khanij', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-Khanij']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-Khanij']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Bhojan */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Bhojan</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-Bhojan')} disabled={false} style={{ background: layerImages['Devata-Bhojan'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-Bhojan'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-Bhojan']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-Bhojan', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-Bhojan']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-Bhojan']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Bhojan Aadi */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Bhojan Aadi</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-Bhojan-aadi')} disabled={false} style={{ background: layerImages['Devata-Bhojan-aadi'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-Bhojan-aadi'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-Bhojan-aadi']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-Bhojan-aadi', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-Bhojan-aadi']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-Bhojan-aadi']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Chinha */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Chinha</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-chinha')} disabled={false} style={{ background: layerImages['Devata-chinha'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-chinha'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-chinha']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-chinha', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-chinha']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-chinha']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Chinha Aadi */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Chinha Aadi</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-chinha-aadi')} disabled={false} style={{ background: layerImages['Devata-chinha-aadi'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-chinha-aadi'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-chinha-aadi']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-chinha-aadi', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-chinha-aadi']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-chinha-aadi']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Muhurat Vichar */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Muhurat Vichar</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Muhurat-vichar')} disabled={false} style={{ background: layerImages['Muhurat-vichar'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Muhurat-vichar'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Muhurat-vichar']?.enabled || false} onChange={(e) => handleToggleLayer('Muhurat-vichar', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Muhurat-vichar']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Muhurat-vichar']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Seharumkh Achintya Vibhav */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Seharumkh Achintya Vibhav</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Seharumkh-achintya-vibhav')} disabled={false} style={{ background: layerImages['Seharumkh-achintya-vibhav'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Seharumkh-achintya-vibhav'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Seharumkh-achintya-vibhav']?.enabled || false} onChange={(e) => handleToggleLayer('Seharumkh-achintya-vibhav', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Seharumkh-achintya-vibhav']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Seharumkh-achintya-vibhav']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Disha Gandha */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Disha Gandha</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Disha-Gandha')} disabled={false} style={{ background: layerImages['Disha-Gandha'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Disha-Gandha'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Disha-Gandha']?.enabled || false} onChange={(e) => handleToggleLayer('Disha-Gandha', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Disha-Gandha']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Disha-Gandha']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Devata Nighat */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devata Nighat</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Devata-Nighat')} disabled={false} style={{ background: layerImages['Devata-Nighat'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-Nighat'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Devata-Nighat']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-Nighat', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-Nighat']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-Nighat']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Nighantu */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Nighantu + Artha</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Nighantu')} disabled={false} style={{ background: layerImages['Nighantu'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Nighantu'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: true ? 'pointer' : 'not-allowed', opacity: true ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={true ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: true ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Nighantu']?.enabled || false} onChange={(e) => handleToggleLayer('Nighantu', e.target.checked)} disabled={false} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Nighantu']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Nighantu']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Vedic 45 Devata (with name) */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic 45 Devata (with name)</span>
                        </div>
                        <button onClick={() => handleEditLayer('compass-45')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['compass-45'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Vedic 45 Devata Table */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic 45 Devata Table</span>
                        </div>
                        <button onClick={() => handleEditLayer('compass-45-table')} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ background: layerImages['compass-45-table'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45-table'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45-table']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45-table', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45-table']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45-table']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Vedic 45 Devata Bar Chart */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic 45 Devata Bar Chart</span>
                        </div>
                        <button onClick={() => handleEditLayer('compass-45-chart')} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ background: layerImages['compass-45-chart'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['compass-45-chart'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['compass-45-chart']?.enabled || false} onChange={(e) => handleToggleLayer('compass-45-chart', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['compass-45-chart']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['compass-45-chart']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                      {/*                       
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: '12px 16px', borderBottom: '1px solid #222', alignItems: 'center', background: exportLayers['4-states-image']?.enabled ? '#00A19D' : '#00A19D' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#fff', fontSize: '0.85rem' }}>4 States Image</span></div>
                        <button onClick={() => handleEditLayer('4-states-image')} disabled={false} style={{ background: layerImages['4-states-image'] ? '#00A19D' : '#00A19D', border: '1px solid ' + (layerImages['4-states-image'] ? '#00A19D' : '#555'), color: layerImages['4-states-image'] ? '#000' : '#fff', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease' }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['4-states-image']?.enabled || false} onChange={(e) => handleToggleLayer('4-states-image', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['4-states-image']?.enabled ? '#00A19D' : '#333', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['4-states-image']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* 32 Zones Vedic */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>32 Zones Vedic</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Vedic-zones-32')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['Vedic-zones-32'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-zones-32'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Vedic-zones-32']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-zones-32', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-zones-32']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-zones-32']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* Vedic Numbers 64 */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic Numbers 64</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Vedic-numbers-64')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['Vedic-numbers-64'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-numbers-64'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Vedic-numbers-64']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-numbers-64', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-numbers-64']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-numbers-64']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* Vedic Elemental zone */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic Elemental zone</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Vedic-elemental-zone')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['Vedic-elemental-zone'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-elemental-zone'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Vedic-elemental-zone']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-elemental-zone', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-elemental-zone']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-elemental-zone']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}

                      {/* Vedic Planet Zoning */}
                      {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vedic Planet Zoning</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('Vedic-planet-zoning')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['Vedic-planet-zoning'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Vedic-planet-zoning'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['Vedic-planet-zoning']?.enabled || false} onChange={(e) => handleToggleLayer('Vedic-planet-zoning', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Vedic-planet-zoning']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Vedic-planet-zoning']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div> */}
                      {/* Vedic Planet Zoning */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>4 State Analysis</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('4-state-image')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['4-state-image'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['4-state-image'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['4-state-image']?.enabled || false} onChange={(e) => handleToggleLayer('4-state-image', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['4-state-image']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['4-state-image']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>



                      {/* Surya Chandra Bhag */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Surya Chandra Bhag</span>
                          
                        </div>
                        <button onClick={() => handleEditLayer('surya-chandra-bhag')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['surya-chandra-bhag'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['surya-chandra-bhag'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['surya-chandra-bhag']?.enabled || false} onChange={(e) => handleToggleLayer('surya-chandra-bhag', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['surya-chandra-bhag']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['surya-chandra-bhag']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Vedic Generative Zones (computed diagrams, not static images) — same list, no separate sub-heading */}
                      {[
                        ['Vedic-zones-32', '32 Zones Vedic'],
                        ['Vedic-numbers-64', '64 Grid'],
                        ['Vedic-64-padvinyash', '81 Padvinyash Analysis'],
                        ['Vedic-81-padvinyash', '64 Padvinyash Analysis'],
                        ['Vedic-elemental-zone', 'Elemental Zone (Vedic)'],
                        ['Vedic-planet-zoning', 'Vedic Planet Zoning'],
                      ].map(([id, label]) => {
                        const locked = !(isPolygonComplete && compassCenter); // boundary + compass only, no Set Measurement
                        return (
                        <div key={id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>{label}</span>
                          </div>
                          <button onClick={() => handleEditLayer(id)} disabled={locked} style={{ background: layerImages[id] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages[id] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={locked ? "Please complete polygon, set compass center, and calibration first!" : "Edit Layer"}><Edit size={14} /><Eye size={14} /></button>
                          <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: locked ? 0.5 : 1 }}><input type="checkbox" checked={exportLayers[id]?.enabled || false} onChange={(e) => handleToggleLayer(id, e.target.checked)} disabled={locked} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers[id]?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers[id]?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                        </div>
                        );
                      })}

                    </div>
                  )}
                </div>
                {/* Medical Vastu Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setMedicalVastuOpen(!medicalVastuOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Medical Vastu</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {medicalVastuOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {medicalVastuOpen && (
                    <div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Tri-Dosh Vedic</span>
                        </div>
                        <button onClick={() => handleEditLayer('tri-dosh-Vedic')} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ background: layerImages['tri-dosh-Vedic'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['tri-dosh-Vedic'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['tri-dosh-Vedic']?.enabled || false} onChange={(e) => handleToggleLayer('tri-dosh-Vedic', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['tri-dosh-Vedic']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['tri-dosh-Vedic']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Tri-Guna Modern</span>
                        </div>
                        <button onClick={() => handleEditLayer('tri-dosh-modern')} disabled={!(isPolygonComplete && compassCenter  && isCalibrationSet)} style={{ background: layerImages['tri-dosh-modern'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['tri-dosh-modern'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['tri-dosh-modern']?.enabled || false} onChange={(e) => handleToggleLayer('tri-dosh-modern', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['tri-dosh-modern']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['tri-dosh-modern']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>


                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Tri-Guna Vedic</span>
                        </div>
                        <button onClick={() => handleEditLayer('tri-guna-Vedic')} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ background: layerImages['tri-guna-Vedic'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['tri-guna-Vedic'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['tri-guna-Vedic']?.enabled || false} onChange={(e) => handleToggleLayer('tri-guna-Vedic', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['tri-guna-Vedic']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['tri-guna-Vedic']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Tri-Dosh Modern</span>
                        </div>
                        <button onClick={() => handleEditLayer('tri-guna-modern')} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ background: layerImages['tri-guna-modern'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['tri-guna-modern'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter &&  isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['tri-guna-modern']?.enabled || false} onChange={(e) => handleToggleLayer('tri-guna-modern', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['tri-guna-modern']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['tri-guna-modern']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Body Part Division</span>
                        </div>
                        <button onClick={() => handleEditLayer('body-part-division')} disabled={!(isPolygonComplete && compassCenter)} style={{ background: layerImages['body-part-division'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['body-part-division'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: (isPolygonComplete && compassCenter) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={(isPolygonComplete && compassCenter) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['body-part-division']?.enabled || false} onChange={(e) => handleToggleLayer('body-part-division', e.target.checked)} disabled={!(isPolygonComplete && compassCenter)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['body-part-division']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['body-part-division']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>



                    </div>
                  )}
                </div>
                {/* Others Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setOthersOpen(!othersOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Others</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {othersOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {othersOpen && (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Mandal Image</span></div>
                        <button onClick={() => handleEditLayer('MANDAL')} disabled={false} style={{ background: layerImages['MANDAL'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['MANDAL'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['MANDAL']?.enabled || false} onChange={(e) => handleToggleLayer('MANDAL', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['MANDAL']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['MANDAL']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>RATNA STHAPNA</span></div>
                        <button onClick={() => handleEditLayer('RATNA-STHAPNA')} disabled={false} style={{ background: layerImages['RATNA-STHAPNA'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['RATNA-STHAPNA'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['RATNA-STHAPNA']?.enabled || false} onChange={(e) => handleToggleLayer('RATNA-STHAPNA', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['RATNA-STHAPNA']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['RATNA-STHAPNA']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Nakshatra Vastu</span></div>
                        <button onClick={() => handleEditLayer('nakshatra-vastu')} disabled={false} style={{ background: layerImages['nakshatra-vastu'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['nakshatra-vastu'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['nakshatra-vastu']?.enabled || false} onChange={(e) => handleToggleLayer('nakshatra-vastu', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['nakshatra-vastu']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['nakshatra-vastu']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Devta Nakshatra Vastu</span></div>
                        <button onClick={() => handleEditLayer('Devata-Nakshatra-Vastu')} disabled={false} style={{ background: layerImages['Devata-Nakshatra-Vastu'] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages['Devata-Nakshatra-Vastu'] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'all 0.2s ease', fontWeight: 500 }} title="Edit Layer"><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}><input type="checkbox" checked={exportLayers['Devata-Nakshatra-Vastu']?.enabled || false} onChange={(e) => handleToggleLayer('Devata-Nakshatra-Vastu', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['Devata-Nakshatra-Vastu']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['Devata-Nakshatra-Vastu']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* VASTU Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setVastuOpen(!vastuOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Remedial Vastu</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {vastuOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {vastuOpen && (
                    <div>
                      {/* Vastu Devta Guide Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Vastu Devta Guide</span>
                        </div>
                        <button
                          onClick={() => { setOpenStages(new Set(['vastu-devta-guide'])); setOpenVastuDevtaGuideModal(true); }}
                          style={{
                            // Green once this report has been saved (has data) — the client
                            // explicitly wants this as the "saved" signal on the Edit button
                            // itself; the separate toggle switch still independently tracks
                            // whether it's enabled for export.
                            background: localStorage.getItem(vastuDevtaGuideStorageKey) ? '#00A19D' : '#ffffff',
                            border: '1px solid #00A19D',
                            color: localStorage.getItem(vastuDevtaGuideStorageKey) ? '#fff' : '#00A19D',
                            width: '48px', gap: '4px', height: '32px', borderRadius: '4px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s ease', fontWeight: 500
                          }}
                          title="View/Edit Vastu Devta Guide"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}>
                            <input
                              type="checkbox"
                              checked={exportLayers['vastu-devta-guide-report']?.enabled || false}
                              onChange={(e) => {
                                if (e.target.checked && !localStorage.getItem(vastuDevtaGuideStorageKey)) { try { saveDefaultReport(vastuDevtaGuideStorageKey, VASTU_DEVTA_GUIDE_DATA); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } }
                                setExportLayers(prev => ({ ...prev, 'vastu-devta-guide-report': { ...prev['vastu-devta-guide-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['vastu-devta-guide-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['vastu-devta-guide-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>
                      {/* Navgrah Shanti Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Navgrah Shanti</span>
                        </div>
                        <button
                          onClick={() => { setOpenStages(new Set(['navgrah-shanti'])); setOpenNavgrahShantiModal(true); }}
                          style={{ background: localStorage.getItem(navgrahShantiStorageKey) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(navgrahShantiStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="View/Edit Navgrah Shanti"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}>
                            <input type="checkbox" checked={exportLayers['navgrah-shanti-report']?.enabled || false}
                              onChange={(e) => {
                                if (e.target.checked && !localStorage.getItem(navgrahShantiStorageKey)) { try { saveDefaultReport(navgrahShantiStorageKey, NAVGRAH_SHANTI_DATA); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } }
                                setExportLayers(prev => ({ ...prev, 'navgrah-shanti-report': { ...prev['navgrah-shanti-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['navgrah-shanti-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['navgrah-shanti-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>

                      {/* Feng Shui Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Feng Shui</span>
                        </div>
                        <button
                          onClick={() => { setOpenStages(new Set(['feng-shui'])); setOpenFengShuiModal(true); }}
                          style={{ background: localStorage.getItem(fengShuiStorageKey) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(fengShuiStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="View/Edit Feng Shui"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}>
                            <input type="checkbox" checked={exportLayers['feng-shui-report']?.enabled || false}
                              onChange={(e) => {
                                if (e.target.checked && !localStorage.getItem(fengShuiStorageKey)) { try { saveDefaultReport(fengShuiStorageKey, FENG_SHUI_DATA); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } }
                                setExportLayers(prev => ({ ...prev, 'feng-shui-report': { ...prev['feng-shui-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['feng-shui-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['feng-shui-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>
                      {/* Zone Analysis Report */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Zone Analysis</span>
                        </div>
                        <button
                          onClick={() => { setOpenStages(new Set(['zone-analysis'])); setOpenZoneAnalysisModal(true); }}
                          style={{ background: localStorage.getItem(zoneAnalysisStorageKey) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(zoneAnalysisStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="View/Edit Zone Analysis"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}>
                            <input type="checkbox" checked={exportLayers['zone-analysis-report']?.enabled || false}
                              onChange={(e) => {
                                if (e.target.checked && !localStorage.getItem(zoneAnalysisStorageKey)) { try { saveDefaultReport(zoneAnalysisStorageKey, ZONE_ANALYSIS_ITEMS); } catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; } }
                                setExportLayers(prev => ({ ...prev, 'zone-analysis-report': { ...prev['zone-analysis-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['zone-analysis-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['zone-analysis-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>
                      {/* Helix (moved here from its own parent — it belongs to Remedial Vastu) */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Helix, Spring and Stud</span>
                        </div>
                        <button
                          onClick={() => setOpenHelixModal(true)}
                          style={{ background: localStorage.getItem(helixStorageKey) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(helixStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="View/Edit Helix, Spring and Stud"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: 1 }}>
                            <input type="checkbox" checked={exportLayers['helix-report']?.enabled || false}
                              onChange={(e) => {
                                // Never opened in Edit? Save the default report (every item ON, default text — same slim
                                // shape the editor saves) so enabling + exporting works without visiting Edit first.
                                if (e.target.checked && !localStorage.getItem(helixStorageKey)) {
                                  try { localStorage.setItem(helixStorageKey, JSON.stringify(HELIX_DATA.map(h => ({ id: h.id, enabled: true })))); }
                                  catch (err) { showAlert('Could Not Save', describeSaveError(err), 'warning'); return; }
                                }
                                setExportLayers(prev => ({ ...prev, 'helix-report': { ...prev['helix-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['helix-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['helix-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* /VASTU Parent */}


                {/* Astro and Numero Vastu Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setAstroNumeroOpen(!astroNumeroOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Astro and Numero Vastu</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {astroNumeroOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {astroNumeroOpen && (
                    <div>
                      {/* Numero Vastu Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Numero Vastu</span>
                        </div>
                        <button
                          onClick={() => setOpenNumeroReport(true)}
                          style={{ background: (localStorage.getItem(numeroVastuStorageKey) || localStorage.getItem(vastuPersonsStorageKey)) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: (localStorage.getItem(numeroVastuStorageKey) || localStorage.getItem(vastuPersonsStorageKey)) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="Open Numero Vastu Report"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={exportLayers['numero-vastu-report']?.enabled || false}
                              onChange={(e) => {
                                // This one toggle gates BOTH the Numero Vastu (Lo Shu Grid) pages
                                // AND the Kua Number Analysis pages in the combined export — so
                                // saved Kua-only data (no Lo Shu persons yet) must also unlock it,
                                // or Kua Number Analysis silently never makes it into the PDF.
                                if (!localStorage.getItem(numeroVastuStorageKey) && !localStorage.getItem(vastuPersonsStorageKey) && !localStorage.getItem(numeroVastuKuaStorageKey)) { showAlert('Person Details Needed', 'Open the Numero Vastu report, add at least one person (name and date of birth) and save, then enable it.', 'warning'); return; }
                                setExportLayers(prev => ({ ...prev, 'numero-vastu-report': { ...prev['numero-vastu-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['numero-vastu-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['numero-vastu-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>

                      {/* Astro Vastu Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          
                          <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Astro Vastu</span>
                        </div>
                        <button
                          onClick={() => { setVastuGatewayView('astro'); setOpenVastuGatewayModal(true); }}
                          style={{ background: localStorage.getItem(astroVastuStorageKey) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(astroVastuStorageKey) ? '#fff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }}
                          title="Open Astro Vastu"
                        >
                          <Edit size={14} /><Eye size={14} />
                        </button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={exportLayers['astro-vastu-report']?.enabled || false}
                              onChange={(e) => {
                                if (!localStorage.getItem(astroVastuStorageKey)) { showAlert('Astro Chart Needed', 'Open the Astro Vastu report, add the person details (date, time and place of birth), generate the chart and save, then enable it.', 'warning'); return; }
                                setExportLayers(prev => ({ ...prev, 'astro-vastu-report': { ...prev['astro-vastu-report'], enabled: e.target.checked } }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['astro-vastu-report']?.enabled ? '#00A19D' : '#ccc', borderRadius: '12px', transition: 'all 0.2s ease' }} />
                            <span style={{ position: 'absolute', top: '2px', left: exportLayers['astro-vastu-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cut & Extension Parent — kept LAST; grouped from two loose rows that used to sit inside Modern Vastu */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setCutExtensionOpen(!cutExtensionOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Cut &amp; Extension</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {cutExtensionOpen ? '▼' : '▶'}
                    </span>
                  </div>

                  {cutExtensionOpen && (
                    <div>
                      {/* Cut Zones — auto-detected from 16 Zone Bar Chart's own min-line */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Cut Zones</span></div>
                        <button onClick={() => { setCutExtensionMode('cut'); setOpenCutExtensionModal(true); }} style={{ background: localStorage.getItem(`cut_zone_data_${activeProjectIdForStorage || 'new'}`) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(`cut_zone_data_${activeProjectIdForStorage || 'new'}`) ? '#ffffff' : '#00A19D', width: '48px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['cut-zone-report']?.enabled || false} onChange={(e) => handleToggleLayer('cut-zone-report', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['cut-zone-report']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['cut-zone-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>

                      {/* Extension Zones — auto-detected from 16 Zone Bar Chart's own max-line */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>Extension Zones</span></div>
                        <button onClick={() => { setCutExtensionMode('extension'); setOpenCutExtensionModal(true); }} style={{ background: localStorage.getItem(`extension_zone_data_${activeProjectIdForStorage || 'new'}`) ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: localStorage.getItem(`extension_zone_data_${activeProjectIdForStorage || 'new'}`) ? '#ffffff' : '#00A19D', width: '48px', height: '32px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'all 0.2s ease', cursor: (isPolygonComplete && compassCenter && isCalibrationSet) ? 'pointer' : 'not-allowed', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} title={(isPolygonComplete && compassCenter && isCalibrationSet) ? "Edit Layer" : "Please complete polygon, set compass center, and calibration first!"}><Edit size={14} /><Eye size={14} /></button>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: (isPolygonComplete && compassCenter && isCalibrationSet) ? 1 : 0.5 }}><input type="checkbox" checked={exportLayers['extension-zone-report']?.enabled || false} onChange={(e) => handleToggleLayer('extension-zone-report', e.target.checked)} disabled={!(isPolygonComplete && compassCenter && isCalibrationSet)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers['extension-zone-report']?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers['extension-zone-report']?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Planning Guide Parent */}
                <div style={{ borderBottom: '1px solid #ECEEF2' }}>
                  <div style={{
                    padding: '11px 14px',
                    background: '#F5F7FA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 160ms ease'
                  }}
                  onClick={() => setHousePlanningOpen(!housePlanningOpen)}>
                    <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em' }}>Planning Guide</span>
                    <span style={{ color: '#00A19D', fontSize: '0.72rem' }}>
                      {housePlanningOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {housePlanningOpen && (
                    <div>
                      {[
                        ['house-bank', 'Bank'],
                        ['house-clinic', 'Clinic'],
                        ['house-electronic-shop', 'Electronic Shop'],
                        ['house-factory', 'Factory'],
                        ['house-hotel', 'Hotel'],
                        ['house-marriage-hall', 'Marriage Hall'],
                        ['house-multiplex', 'Multiplex'],
                        ['house-office-cabin', 'Office Cabin'],
                        ['house-petrol-pump', 'Petrol Pump'],
                        ['house-retail-outlets', 'Retail Outlets'],
                        ['house-school-college', 'School & College'],
                        ['house-space-analysis-compass', 'Residential Analysis Compass'],
                        ['house-temple', 'Temple'],
                      ].map(([id, label]) => {
                        const locked = false; // Planning Guide images: no boundary / measurement needed
                        return (
                        <div key={id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: isMobile ? '14px' : '11px 14px', borderTop: isMobile ? 'none' : '1px solid #ECEEF2', borderRadius: isMobile ? 10 : 0, marginBottom: isMobile ? 8 : 0, boxShadow: isMobile ? '0 1px 3px rgba(15,23,42,0.06)' : 'none', border: isMobile ? '1px solid #ECEEF2' : 'none', alignItems: 'center', background: '#ffffff', transition: 'all 0.2s ease' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.82rem', fontWeight: 500 }}>{label}</span>
                          </div>
                          <button onClick={() => handleEditLayer(id)} disabled={locked} style={{ background: layerImages[id] ? '#00A19D' : '#ffffff', border: '1px solid #00A19D', color: layerImages[id] ? '#ffffff' : '#00A19D', width: '48px', gap: '4px', height: '32px', borderRadius: '4px', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', fontWeight: 500 }} title={locked ? "Please complete polygon, set compass center, and calibration first!" : "Edit Layer"}><Edit size={14} /><Eye size={14} /></button>
                          <div style={{ display: 'flex', justifyContent: 'center' }}><label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', opacity: locked ? 0.5 : 1 }}><input type="checkbox" checked={exportLayers[id]?.enabled || false} onChange={(e) => handleToggleLayer(id, e.target.checked)} disabled={locked} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: exportLayers[id]?.enabled ? '#00A19D' : '#DCE0E8', borderRadius: '12px', transition: 'all 0.2s ease' }} /><span style={{ position: 'absolute', top: '2px', left: exportLayers[id]?.enabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: '#fff', borderRadius: '50%', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></label></div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

            </div>
          </div>

          {/* PDF / Word export moved to the Editor top bar (Export button next to Save). */}

          {/* Add spinning animation - Place this style tag anywhere in your component */}
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>

    </div>
    </MobileSheetWrapper>

    </div>

    {/* "Finish Polygon" — phone widths only, floating just above the bottom
        toolbar. Precisely tapping the tiny first-point circle (the desktop
        way to close the boundary — see the "Tap first point to close" badge
        in the toolbar above) is hard to hit with a finger, so this calls
        the exact same finishPolygon() a real tap on that point would, from
        a big, easy-to-hit button instead. Same visibility condition as that
        badge (isDrawingPolygon + at least 3 points placed, i.e. enough to
        actually close a shape). */}
    {isMobile && isDrawingPolygon && polygonPoints.length >= 3 && (
      <button onClick={finishPolygon}
        style={{
          position: 'fixed', left: '50%', transform: 'translateX(-50%)',
          bottom: 'calc(78px + env(safe-area-inset-bottom, 0px))', zIndex: 10100,
          display: 'flex', alignItems: 'center', gap: '7px',
          background: '#00A19D', color: '#fff', border: 'none', borderRadius: '999px',
          padding: '11px 20px', fontSize: '13px', fontWeight: 700,
          boxShadow: '0 8px 20px rgba(0,161,157,0.35)', cursor: 'pointer',
        }}>
        <CheckCircle size={16} />
        Finish Polygon
      </button>
    )}

    {/* Floating bottom toolbar — phone widths only. Replaces the desktop's
        fixed left/right sidebars AND the desktop-only floating
        account-settings button with one row of icon+label buttons that
        open the same content as a bottom sheet instead — see
        MobileSheetWrapper above and MobileBottomToolbar. Back to Projects
        and Save now live in the top row instead (see the buttons next to
        Download / after Next above) — no longer duplicated here. */}
    {isMobile && !mobileCompassControl && !mobilePolygonControl && (
      <MobileBottomToolbar
        stages={stages}
        activeSheet={mobileSheet}
        onOpenStage={handleMobileStageIconClick}
        onOpenMeasurement={() => setMobileSheet('measurement')}
        onOpenReports={() => setMobileSheet('reports')}
        onOpenAccount={() => setMobileSheet('account')}
      />
    )}

    {/* Compass "Change <X>" floating control (mobile only) — replaces the
        bottom toolbar in this spot instead of stacking above it, so the
        compass/floor-plan stays fully visible on the canvas above while
        dragging. No backdrop at all (unlike MobileSheetWrapper) — canvas
        stays tappable too. Tap the checkmark to go back to the normal
        toolbar. Rotation deliberately shows no numeric value (per explicit
        request) — the other four still do, matching their sheet buttons. */}
    {isMobile && mobileCompassControl && (() => {
      const CONTROL_META = {
        rotation:    { label: 'Rotation' },
        lineColor:   { label: 'Line Color' },
        lineWeight:  { label: 'Line Weight', value: `${compassLineWeight}px` },
        lineOpacity: { label: 'Line Opacity', value: `${Math.round(compassLineOpacity * 100)}%` },
        opacity:     { label: 'Text Opacity', value: `${Math.round(compassOpacity * 100)}%` },
      };
      const meta = CONTROL_META[mobileCompassControl];
      return (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10100,
          padding: '10px 14px calc(10px + env(safe-area-inset-bottom, 0px))',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)',
            borderRadius: '16px', boxShadow: '0 8px 24px rgba(15,23,42,0.16)', border: '1px solid #EEF1F5',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 700 }}>{meta.label}</span>
              {meta.value && <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.85rem', fontWeight: 700 }}>{meta.value}</span>}
              <button onClick={() => setMobileCompassControl(null)} aria-label="Done"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#00A19D', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}>
                <Check size={16} />
              </button>
            </div>

            {mobileCompassControl === 'rotation' && (
              <input type="range" min="0" max="360" value={compassRotation}
                onChange={(e) => setCompassRotation(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
            {mobileCompassControl === 'lineColor' && (
              <input type="color" value={compassColor} onChange={(e) => setCompassColor(e.target.value)}
                style={{ width: '100%', height: '38px', border: '1px solid #ECEEF2', borderRadius: '6px', cursor: 'pointer', background: 'none', padding: '2px' }} />
            )}
            {mobileCompassControl === 'lineWeight' && (
              <input type="range" min="1" max="10" value={compassLineWeight}
                onChange={(e) => setCompassLineWeight(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
            {mobileCompassControl === 'lineOpacity' && (
              <input type="range" min="0.05" max="1" step="0.05" value={compassLineOpacity}
                onChange={(e) => setCompassLineOpacity(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
            {mobileCompassControl === 'opacity' && (
              <input type="range" min="0.1" max="1" step="0.1" value={compassOpacity}
                onChange={(e) => setCompassOpacity(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
          </div>
        </div>
      );
    })()}

    {/* Polygon "Change <X>" floating control (mobile only) — identical
        pattern to the Compass one above, for the Polygon sheet's own Color/
        Line Weight/Line Opacity/Opacity controls. */}
    {isMobile && mobilePolygonControl && (() => {
      const CONTROL_META = {
        lineColor:   { label: 'Color' },
        lineWeight:  { label: 'Line Weight', value: `${polygonLineWeight}px` },
        lineOpacity: { label: 'Line Opacity', value: `${Math.round(polygonLineOpacity * 100)}%` },
        opacity:     { label: 'Opacity', value: `${Math.round(polygonOpacity * 100)}%` },
      };
      const meta = CONTROL_META[mobilePolygonControl];
      return (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10100,
          padding: '10px 14px calc(10px + env(safe-area-inset-bottom, 0px))',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)',
            borderRadius: '16px', boxShadow: '0 8px 24px rgba(15,23,42,0.16)', border: '1px solid #EEF1F5',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#00857E', fontSize: '0.8rem', fontWeight: 700 }}>{meta.label}</span>
              {meta.value && <span style={{ textAlign: 'left', color: '#1A1F36', fontSize: '0.85rem', fontWeight: 700 }}>{meta.value}</span>}
              <button onClick={() => setMobilePolygonControl(null)} aria-label="Done"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#00A19D', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}>
                <Check size={16} />
              </button>
            </div>

            {mobilePolygonControl === 'lineColor' && (
              <input type="color" value={polygonColor} onChange={(e) => setPolygonColor(e.target.value)}
                style={{ width: '100%', height: '38px', border: '1px solid #ECEEF2', borderRadius: '6px', cursor: 'pointer', background: 'none', padding: '2px' }} />
            )}
            {mobilePolygonControl === 'lineWeight' && (
              <input type="range" min="1" max="10" value={polygonLineWeight}
                onChange={(e) => setPolygonLineWeight(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
            {mobilePolygonControl === 'lineOpacity' && (
              <input type="range" min="0.05" max="1" step="0.05" value={polygonLineOpacity}
                onChange={(e) => setPolygonLineOpacity(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
            {mobilePolygonControl === 'opacity' && (
              <input type="range" min="0.1" max="1" step="0.1" value={polygonOpacity}
                onChange={(e) => setPolygonOpacity(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#00A19D', height: '4px' }} />
            )}
          </div>
        </div>
      );
    })()}

    {/* Account details — same content as desktop's floating gear-icon
        popover (AccountDetailsPanel, shared with UserProfilePopover.js),
        opened as a full bottom sheet on mobile instead, per explicit
        client request (a sheet like every other stage, not a small
        popover). Desktop already has its own separate <UserProfilePopover>
        (in Editor.js) for this, so — unlike the stage sidebars above,
        which are meant to always render on desktop — this one is only
        ever mounted on mobile at all; MobileSheetWrapper's desktop
        passthrough (always renders children) isn't what's wanted here. */}
    {isMobile && (
      <MobileSheetWrapper isMobile={isMobile} open={mobileSheet === 'account'} onClose={() => setMobileSheet(null)} title="Account">
        <div style={{ padding: '4px 4px 16px' }}>
          <AccountDetailsWithProfile />
        </div>
      </MobileSheetWrapper>
    )}

    {/* ── Vastu Devta Guide Modal — always mounted so it can open from anywhere ──
         (moved out of the left-sidebar STAGES-driven renderStageContent switch —
         'vastu-devta-guide' was removed from the STAGES array so it wouldn't
         show as a numbered left-sidebar step, but that switch was ALSO the only
         place this component ever got mounted, so removing it there orphaned
         the modal entirely: the right-sidebar row's setOpenVastuDevtaGuideModal(true)
         had no mounted <VastuDevtaGuide> left to open. Mounting it here, same as
         every other report modal below, fixes that.) */}
    <VastuDevtaGuide
      key={`vdg-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      language={selectedLanguage}
      openModal={openVastuDevtaGuideModal}
      onModalClose={() => {
        setOpenVastuDevtaGuideModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('vastu-devta-guide'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'vastu-devta-guide-report': rawData.length > 0 ? 'data-ready' : null, 'vastu-devta-guide-report-data': rawData }));
        setExportLayers(prev => ({ ...prev, 'vastu-devta-guide-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Vastu Devta Guide saved successfully!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Navgrah Shanti Modal — always mounted so it can open from anywhere ── */}
    <NavgrahShanti
      key={`ngs-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      language={selectedLanguage}
      openModal={openNavgrahShantiModal}
      onModalClose={() => {
        setOpenNavgrahShantiModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('navgrah-shanti'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'navgrah-shanti-report': rawData.length > 0 ? 'data-ready' : null }));
        setExportLayers(prev => ({ ...prev, 'navgrah-shanti-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Navgrah Shanti report saved!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Feng Shui Modal — always mounted so it can open from anywhere ── */}
    <FengShui
      key={`fsg-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      language={selectedLanguage}
      openModal={openFengShuiModal}
      onModalClose={() => {
        setOpenFengShuiModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('feng-shui'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'feng-shui-report': rawData.length > 0 ? 'data-ready' : null }));
        setExportLayers(prev => ({ ...prev, 'feng-shui-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Feng Shui report saved!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Helix Modal — always mounted so it can open from anywhere ── */}
    <Helix
      key={`hlx-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      openModal={openHelixModal}
      onModalClose={() => setOpenHelixModal(false)}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'helix-report': rawData.length > 0 ? 'data-ready' : null }));
        setExportLayers(prev => ({ ...prev, 'helix-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Helix, Spring and Stud report saved!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Zone Analysis Modal — always mounted so it can open from anywhere ── */}
    <ZoneAnalysis
      key={`zan-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      language={selectedLanguage}
      openModal={openZoneAnalysisModal}
      onModalClose={() => {
        setOpenZoneAnalysisModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('zone-analysis'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'zone-analysis-report': rawData.length > 0 ? 'data-ready' : null }));
        setExportLayers(prev => ({ ...prev, 'zone-analysis-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Zone Analysis report saved!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Space Analysis Modal — always mounted so it can open from anywhere ──
         (moved out of the left-sidebar STAGES-driven renderStageContent
         switch, same fix as VastuDevtaGuide/NavgrahShanti/FengShui/
         ZoneAnalysis above — that switch was the ONLY place this ever got
         mounted, and on mobile its host sidebar only opens for stageId in
         INLINE_STAGE_IDS, which space-analysis isn't, so the button silently
         did nothing on phones.) */}
    <SpaceAnalysis
      key={`spa-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      projectName={project.currentProject?.name || null}
      language={selectedLanguage}
      openModal={openSpaceAnalysisModal}
      all32Directions={mainDirections}
      compassCenter={compassCenter}
      compassRotation={compassRotation}
      polygonPoints={polygonPoints}
      baseImage={baseImage}
      isPolygonComplete={isPolygonComplete}
      canvasWidth={A4_WIDTH}
      canvasHeight={A4_HEIGHT}
      onModalClose={() => {
        setOpenSpaceAnalysisModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('space-analysis'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'space-analysis-report': rawData.length > 0 ? 'data-ready' : null, 'space-analysis-report-data': rawData }));
        setExportLayers(prev => ({ ...prev, 'space-analysis-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Space Analysis Report saved successfully!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Entrance Analysis Modal — always mounted so it can open from anywhere — same fix, same reason as Space Analysis just above. ── */}
    <EntranceAnalysis
      key={`ena-${project.currentProject?.id || projectId || 'new'}`}
      projectId={project.currentProject?.id || projectId}
      projectName={project.currentProject?.name || null}
      language={selectedLanguage}
      openModal={openEntranceAnalysisModal}
      all32Directions={all32Directions}
      compassCenter={compassCenter}
      compassRotation={compassRotation}
      findPolygonIntersection={findPolygonIntersection}
      polygonPoints={polygonPoints}
      baseImage={baseImage}
      isPolygonComplete={isPolygonComplete}
      canvasWidth={A4_WIDTH}
      canvasHeight={A4_HEIGHT}
      onModalClose={() => {
        setOpenEntranceAnalysisModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('entrance-analysis'); return n; });
      }}
      onReportGenerated={(reportData) => {
        const rawData = reportData.reportData || [];
        setLayerImages(prev => ({ ...prev, 'entrance-analysis-report': rawData.length > 0 ? 'data-ready' : null, 'entrance-analysis-report-data': rawData }));
        setExportLayers(prev => ({ ...prev, 'entrance-analysis-report': { enabled: rawData.length > 0 } }));
        setShowSuccessMessage(true); setSuccessMessage('Entrance Analysis Report saved successfully!');
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }}
    />

    {/* ── Add Birth Date Modal ── */}
    <BirthDateModal open={openBirthDateModal} onClose={() => setOpenBirthDateModal(false)} projectId={activeProjectIdForStorage} />

    {/* ── Edit-angle popup for the on-canvas compass angle boxes ──
        Saving here back-solves compassRotation from the box's current vs
        entered value, so every other angle box (all reading the same
        compassRotation state) recalculates together — same effect as
        dragging the rotation slider, just driven from one specific box. */}
    {editingAngleBox && (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 20000,
          background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}
        onClick={() => setEditingAngleBox(null)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ffffff', borderRadius: '14px', width: '240px',
            boxShadow: '0 12px 32px rgba(15,23,42,0.28)', overflow: 'hidden',
            fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', background: '#00A19D',
          }}>
            <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>Edit Angle</span>
            <button
              onClick={() => setEditingAngleBox(null)}
              aria-label="Close"
              style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.25)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
          <div style={{ padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="number"
                value={editingAngleBox.value}
                autoFocus
                onChange={(e) => setEditingAngleBox(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEditingAngleBox(); }}
                style={{
                  flex: 1, fontSize: '1rem', fontWeight: 700, color: '#1A1F36',
                  border: '1px solid #ECEEF2', borderRadius: '8px', padding: '8px 10px',
                  outline: 'none', width: '100%',
                }}
              />
              <span style={{ color: '#697386', fontSize: '0.9rem', fontWeight: 600 }}>°</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button
                onClick={() => setEditingAngleBox(null)}
                style={{ flex: 1, padding: '9px 0', borderRadius: '8px', border: '1px solid #ECEEF2', background: '#fff', color: '#475467', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={saveEditingAngleBox}
                style={{ flex: 1, padding: '9px 0', borderRadius: '8px', border: 'none', background: '#00A19D', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
              >
                <Check size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Numero Vastu Report (inline page) ── */}
    <NumeroVastuReport
      open={openNumeroReport}
      onClose={() => setOpenNumeroReport(false)}
      projectId={activeProjectIdForStorage}
      language={selectedLanguage}
      remedyData={numeroRemedyData}
      onRemedyChange={setNumeroRemedyData}
      onSaved={() => setExportLayers(prev => ({ ...prev, 'numero-vastu-report': { ...prev['numero-vastu-report'], enabled: true } }))}
    />

    {/* ── Astro & Numero Vastu Gateway Modal ── */}
    <VastuPersonsGateway
      key={`vpg-${project.currentProject?.id || projectId || 'new'}-${vastuGatewayView}`}
      projectId={project.currentProject?.id || projectId}
      projectName={project.currentProject?.name || null}
      language={selectedLanguage}
      openModal={openVastuGatewayModal}
      initialView={vastuGatewayView}
      onModalClose={() => {
        setOpenVastuGatewayModal(false);
        setOpenStages(prev => { const n = new Set(prev); n.delete('astro-numero'); return n; });
      }}
      onAstroSaved={() => setExportLayers(prev => ({ ...prev, 'astro-vastu-report': { ...prev['astro-vastu-report'], enabled: true } }))}
      // Numero Vastu and Kua Number Analysis share this same export toggle —
      // saving either one now auto-enables it, so a Kua-only save no longer
      // needs the user to separately find and flip the "Numero Vastu Report"
      // switch for Kua Number Analysis to actually appear in the exported PDF.
      onNumeroSaved={() => setExportLayers(prev => ({ ...prev, 'numero-vastu-report': { ...prev['numero-vastu-report'], enabled: true } }))}
      onKuaSaved={() => setExportLayers(prev => ({ ...prev, 'numero-vastu-report': { ...prev['numero-vastu-report'], enabled: true } }))}
    />

    {/* ── Cut / Extension Zone Guide Modal ── */}
    <CutExtensionGuide
      key={`ceg-${activeProjectIdForStorage || 'new'}`}
      projectId={activeProjectIdForStorage}
      language={selectedLanguage}
      mode={cutExtensionMode}
      zones={openCutExtensionModal ? getCutExtensionZones(cutExtensionMode) : []}
      openModal={openCutExtensionModal}
      onModalClose={() => setOpenCutExtensionModal(false)}
      onReportGenerated={({ mode: savedMode, reportData }) => {
        const layerKey = savedMode === 'cut' ? 'cut-zone-report' : 'extension-zone-report';
        const hasEnabled = (reportData || []).some(z => z.enabled !== false);
        setExportLayers(prev => ({ ...prev, [layerKey]: { enabled: hasEnabled } }));
      }}
    />

    <ReportFooterModal
      open={footerModalOpen}
      value={reportFooterText}
      profileDefault={profileFooterDefault}
      onSave={(t) => { setReportFooterText(t); setFooterModalOpen(false); }}
      onClose={() => setFooterModalOpen(false)}
    />
    <VideoGuideModal open={videoGuideOpen} url={videoGuideKind === 'mobile' ? VIDEO_GUIDE_MOBILE_URL : videoGuideKind === 'action-required' ? ACTION_REQUIRED_VIDEO_URL : videoGuideKind === 'floor-plan-maker' ? FLOOR_PLAN_MAKER_TUTORIAL_URL : VIDEO_GUIDE_DESKTOP_URL} onClose={() => setVideoGuideOpen(false)} />

    <CustomSweetAlert
        show={alertState.show}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onConfirm={hideAlert}
        confirmText="OK"
      />

    {/* PDF generation full-screen overlay */}
    {isGeneratingPDF && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <style>{`
          .psc-wrap{display:flex;flex-direction:column;align-items:center;padding:32px 16px;gap:24px;}
          .psc-scanner-stage{position:relative;width:220px;height:290px;}
          .psc-pdf-doc{position:absolute;left:0;top:0;width:220px;height:290px;background:#fff;border-radius:10px;border:1.5px solid #B5D4F4;box-shadow:0 4px 24px rgba(0,161,157,0.13);overflow:hidden;}
          .psc-pdf-header{background:#00A19D;height:38px;display:flex;align-items:center;padding:0 14px;gap:8px;}
          .psc-pdf-label{color:#fff;font-family:'Inter',sans-serif;font-size:13px;font-weight:500;letter-spacing:1px;}
          .psc-pdf-icon{width:18px;height:22px;background:#fff;border-radius:2px;display:flex;align-items:center;justify-content:center;}
          .psc-pdf-icon span{color:#00A19D;font-size:7px;font-weight:700;font-family:monospace;}
          .psc-pdf-lines{padding:18px 18px 0;}
          .psc-pdf-line{height:9px;background:#E1F5EE;border-radius:4px;margin-bottom:10px;}
          .psc-w100{width:100%;}.psc-w80{width:80%;}.psc-w90{width:90%;}.psc-w60{width:60%;}.psc-w70{width:70%;}.psc-w85{width:85%;}
          .psc-pdf-block{margin:14px 18px 0;height:60px;background:#E1F5EE;border-radius:6px;}
          .psc-scan-beam{position:absolute;left:0;top:0;width:100%;height:3px;border-radius:2px;background:linear-gradient(90deg,transparent 0%,#00E9B3 30%,#16B5D9 60%,#2B81FF 85%,transparent 100%);animation:pscScanDown 2s linear infinite;z-index:10;}
          .psc-scan-glow{position:absolute;left:0;top:-10px;width:100%;height:24px;background:linear-gradient(180deg,transparent,rgba(0,233,179,0.18) 40%,rgba(22,181,217,0.22) 60%,transparent);animation:pscScanDown 2s linear infinite;z-index:9;}
          .psc-scanned-overlay{position:absolute;left:0;top:0;width:100%;height:0;background:linear-gradient(180deg,rgba(0,161,157,0.1),rgba(230,241,251,0.35));animation:pscFillDown 2s linear infinite;z-index:8;border-radius:10px 10px 0 0;}
          .psc-corner{position:absolute;width:18px;height:18px;border-color:#00A19D;border-style:solid;border-width:0;}
          .psc-tl{top:-2px;left:-2px;border-top-width:3px;border-left-width:3px;border-radius:4px 0 0 0;}
          .psc-tr{top:-2px;right:-2px;border-top-width:3px;border-right-width:3px;border-radius:0 4px 0 0;}
          .psc-bl{bottom:-2px;left:-2px;border-bottom-width:3px;border-left-width:3px;border-radius:0 0 0 4px;}
          .psc-br{bottom:-2px;right:-2px;border-bottom-width:3px;border-right-width:3px;border-radius:0 0 4px 0;}
          .psc-status-row{display:flex;align-items:center;gap:10px;}
          .psc-status-dot{width:9px;height:9px;border-radius:50%;background:#00A19D;animation:pscBlink 1s ease-in-out infinite;}
          .psc-status-text{font-family:'Inter',sans-serif;font-size:13px;color:#8A94A6;}
          .psc-progress-wrap{width:220px;height:6px;background:#ECEEF2;border-radius:6px;overflow:hidden;}
          .psc-progress-bar{height:100%;border-radius:6px;background:linear-gradient(90deg,#00A19D,#16B5D9,#2B81FF);animation:pscProgressFill 2s linear infinite;}
          @keyframes pscScanDown{0%{transform:translateY(0);}100%{transform:translateY(290px);}}
          @keyframes pscFillDown{0%{height:0;}100%{height:290px;}}
          @keyframes pscBlink{0%,100%{opacity:1;}50%{opacity:0.3;}}
          @keyframes pscProgressFill{0%{width:0%;}90%{width:100%;}100%{width:100%;}}
        `}</style>
        <div className="psc-wrap">
          <div className="psc-scanner-stage">
            <div className="psc-pdf-doc">
              <div className="psc-pdf-header">
                <div className="psc-pdf-icon"><span>{exportKind === 'word' ? 'DOC' : 'PDF'}</span></div>
                <span className="psc-pdf-label">{exportKind === 'word' ? 'document.docx' : 'document.pdf'}</span>
              </div>
              <div className="psc-pdf-lines">
                <div className="psc-pdf-line psc-w100"></div>
                <div className="psc-pdf-line psc-w80"></div>
                <div className="psc-pdf-line psc-w90"></div>
                <div className="psc-pdf-line psc-w60"></div>
              </div>
              <div className="psc-pdf-block"></div>
              <div className="psc-pdf-lines" style={{ paddingTop: '14px' }}>
                <div className="psc-pdf-line psc-w85"></div>
                <div className="psc-pdf-line psc-w70"></div>
                <div className="psc-pdf-line psc-w90"></div>
              </div>
            </div>
            <div className="psc-scanned-overlay"></div>
            <div className="psc-scan-glow"></div>
            <div className="psc-scan-beam"></div>
            <div className="psc-corner psc-tl"></div>
            <div className="psc-corner psc-tr"></div>
            <div className="psc-corner psc-bl"></div>
            <div className="psc-corner psc-br"></div>
          </div>
          <div className="psc-status-row">
            <div className="psc-status-dot"></div>
            <span className="psc-status-text">{exportKind === 'word' ? 'Preparing your Word file…' : 'Generating your report…'}</span>
          </div>
          <div className="psc-progress-wrap">
            <div className="psc-progress-bar"></div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Configure;
